import { hash, List, Map as Map$1 } from 'immutable';

function truncate(s, max) {
  if (s.length <= max) {
    return s;
  }
  if (max <= 8) {
    return s.slice(0, max);
  }
  return s.slice(0, max - 1) + '…';
}

// This is in its own file instead of in ./msg.ts to avoid a circular
// dependency.

// Supports easy simple message formatting. It works very similarly to Markdown
// as far as whitespace rules. Whitespace is normalized to a single space. An
// empty line can be used to break up paragraphs. \n can be used to force a new
// line.
function msg(strings, ...args) {
  const out = [];
  // We capture raw strings so that we can allow forced new lines with \n.
  for (let i = 0; i < strings.raw.length; i++) {
    let string = strings.raw[i];
    if (i === 0) {
      string = string.replace(/^\s+/, '');
    }
    if (i === strings.length - 1) {
      string = string.replace(/\s+$/, '');
    }
    string = string.replace(/\s+/g, w => /\n\n/.test(w) ? '\\n\\n' : ' ');
    // Whitespace following a forced new line wtih \n should be ignored.
    string = string.replace(/\\n\s+/g, '\\n');
    // Process the raw strings to convert escapes sequences.
    string = JSON.parse('"' + string.replace(/((?:^|[^\\])(?:\\{2})*)"/g, '$1\\"') + '"');
    out.push(string);
    if (i < args.length) {
      out.push(String(args[i]));
    }
  }
  return out.join('');
}

const MAX_VAR_LENGTH = 128;

// Supports easy error message formatting. Arguments will be converted into
// strings intelligently based on type using `toString`.
function emsg(strings, ...args) {
  // By returning a function, we allow skipping running `toString` when the
  // error message won't be needed.
  return () => tmsg(strings, ...args);
}

// Supports an 'immediate call' case for emsg, first use was for tests thus 't'
function tmsg(strings, ...args) {
  return msg(strings, ...args.map(u => toString(u)));
}
function toString(value) {
  return truncate(toStringInner(value), MAX_VAR_LENGTH);
}
function toStringInner(value) {
  if (value === null) {
    return '(null)';
  }
  switch (typeof value) {
    case 'boolean':
    case 'number':
      return String(value);
    case 'string':
      return smartString(value);
    case 'object':
      {
        const name = value.constructor?.name;
        return (name ? toStringInner(name) + ' ' : '') + stringifyObject(value);
      }
    case 'undefined':
      return '(undefined)';
    case 'bigint':
      return `${String(value)}n`;
    case 'function':
      return special('function', value.name);
    case 'symbol':
      return special('symbol', value.description);
  }
  return special('unknown');
}
function stringifyObject(value) {
  if (value instanceof RegExp) {
    return String(value);
  }
  return JSON.stringify(value);
}

// Similar to JSON.stringify but drops surrounding quotes when possible.
function smartString(value) {
  const stringified = JSON.stringify(value);
  if (value !== '' && stringified === `"${value}"` && !/\s/.test(value)) {
    return value;
  }
  return stringified;
}
function special(type, name) {
  if (name) {
    return `<${type} ${name}>`;
  }
  return `<${type}>`;
}

'use strict';

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol ? "symbol" : typeof obj; };

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var RANGE_FROM = 'tempo::range:from';
var RANGE_TO = 'tempo::range:to';
var RANGE_STEP = 'tempo::range:step';
var RANGE_INCLUSIVE = 'tempo::range:inclusive';

function __TEMPO_GET_DEF__(name, value) {
  return value;
}

var Range = function () {
  function Range(from, to, step, inclusive) {
    _classCallCheck(this, Range);

    this[RANGE_FROM] = +from;
    this[RANGE_TO] = +to;
    this[RANGE_STEP] = toStep(step);
    this[RANGE_INCLUSIVE] = !!inclusive;
  }

  _createClass(Range, [{
    key: Symbol.iterator,
    value: function* () {
      if (runtime.checkAsserts()) {
        runtime.ensure(isRange(this), 'Range object expected.');
      }
      const from = this[RANGE_FROM];
      const to = this[RANGE_TO];
      const step = this[RANGE_STEP];
      const inclusive = this[RANGE_INCLUSIVE];

      for (let i = from;
           step == 0 ? i == to
           : isPositive(step)
               ? inclusive ? i <= to : i < to
               : inclusive ? i >= to : i > to;
           i += step) {
        yield i;
      }
    },
  }, {
    key: 'has',
    value: function has(value) {
      var from = this[RANGE_FROM];
      var to = this[RANGE_TO];
      var inclusive = this[RANGE_INCLUSIVE];
      var step = this[RANGE_STEP];
      var lower = isPositive(step) ? from : to;
      var inclusiveLower = isPositive(step) ? true : inclusive;
      var upper = isPositive(step) ? to : from;
      var inclusiveUpper = isPositive(step) ? inclusive : true;
      if (!(value > lower && value < upper || inclusiveLower && value == lower || inclusiveUpper && value == upper)) {
        return false;
      }
      // Handle the trivial case where `from` is an integer and `step` is 1 or
      // -1, which means to just ensure `value` is an integer.
      if (Number.isInteger(from) && (step == 1 || step == -1)) {
        return Number.isInteger(value);
      }
      // TODO(nathan): Come up with a faster solution for the general case.
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = this[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var v = _step.value;

          if (value == v) {
            return true;
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      return false;
    }
  }]);

  return Range;
}();

function toStep(value) {
  var V = +value;
  // NaN is treated like 0 (infinite loop unless the from == to).
  if (V != V) {
    return 0;
  }
  return V;
}

var runtime = {
  checkAsserts: function checkAsserts() {
    return runtime.getDef('CHECK_ASSERTS', true);
  },
  getDef: function getDef(name, value) {
    return __TEMPO_GET_DEF__(name, value);
  },
  ensure: function ensure(test, getMessage) {
    if (!test) {
      runtime.failedValidation(getMessage);
    }
    return test;
  },
  failedValidation: function failedValidation(getMessage) {
    throw new Error(getMessage ? getMessage() : 'Validation failed.');
  },
  range: function range(from, to, step, inclusive) {
    return new Range(from, to, step, inclusive);
  },
  by: function by(range, step) {
    if (!isRange(range)) {
      throw new Error('Range object expected.');
    }
    var from = range[RANGE_FROM];
    var to = range[RANGE_TO];
    var inclusive = range[RANGE_INCLUSIVE];
    return new Range(from, to, step, inclusive);
  },
  getComputed: function getComputed(object, property) {
    var p = computeProperty(object, property);
    return object[p];
  },
  setComputed: function setComputed(object, property, value) {
    var p = computeProperty(object, property);
    return object[p] = value;
  },
  deleteComputed: function deleteComputed(object, property) {
    var p = computeProperty(object, property);
    return delete object[p];
  },
  callComputed: function callComputed(object, property, args) {
    var p = computeProperty(object, property);
    return object[p].apply(object, _toConsumableArray(args));
  },
  defineConditionally: function defineConditionally(object, props) {
    var descriptors = Object.create(null);
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = props[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var prop = _step2.value;

        if (prop.condition) {
          descriptors[prop.key] = prop.descriptor;
        }
      }
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    return Object.defineProperties(object, descriptors);
  }
};

function async(iter) {
  return new Promise(function (resolve) {
    resolve(asyncNext(iter, undefined, false));
  });
};

function asyncNext(iter, send, shouldThrow) {
  try {
    var _value = void 0,
        done = void 0;
    if (shouldThrow) {
      var _iter$throw = iter.throw(send);

      _value = _iter$throw.value;
      done = _iter$throw.done;
    } else {
      var _iter$next = iter.next(send);

      _value = _iter$next.value;
      done = _iter$next.done;
    }
    if (done) {
      return _value;
    }
    return Promise.resolve(_value).then(function (v) {
      return asyncNext(iter, v, false);
    }, function (e) {
      return asyncNext(iter, e, true);
    });
  } catch (e) {
    return Promise.reject(e);
  }
}

function array(iter) {
  // TODO(nathan): Would only freezing in devmode but not in prod have any
  // effect on performance?
  return Object.freeze([].concat(_toConsumableArray(iter)));
};

function string(iter) {
  if (typeof iter == 'string') {
    var _ret = function () {
      var delim = iter;
      return {
        v: function joiner(iter) {
          return [].concat(_toConsumableArray(iter)).join(delim);
        }
      };
    }();

    if ((typeof _ret === 'undefined' ? 'undefined' : _typeof(_ret)) === "object") return _ret.v;
  }
  return [].concat(_toConsumableArray(iter)).join('');
};

function sum(iter) {
  var total = 0;
  var _iteratorNormalCompletion3 = true;
  var _didIteratorError3 = false;
  var _iteratorError3 = undefined;

  try {
    for (var _iterator3 = iter[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
      var u = _step3.value;

      if (runtime.checkAsserts() && typeof u != 'number') {
        throw new TypeError('Expected type "number" but found "' + (typeof u === 'undefined' ? 'undefined' : _typeof(u)) + '".');
      }
      total += u;
    }
  } catch (err) {
    _didIteratorError3 = true;
    _iteratorError3 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion3 && _iterator3.return) {
        _iterator3.return();
      }
    } finally {
      if (_didIteratorError3) {
        throw _iteratorError3;
      }
    }
  }

  return total;
};

function isPositive(num) {
  if (num == 0) {
    return 1 / 0 == Infinity;
  }
  return num > 0;
}

function computeProperty(object, property) {
  // Allow negative indices for strings and arrays.
  if (typeof property == 'number' && property < 0 && (typeof object == 'string' || object instanceof Array)) {
    // Prevent use of this feature so we can switch to regular JS.
    if (runtime.checkAsserts()) {
      runtime.failedValidation(function() {
        return 'Uses of negative indexes in object properties should be '
            + 'removed';
      });
    }
    return object.length + property;
  }
  return property;
}

function isRange(object) {
  if (object == null || (typeof object === 'undefined' ? 'undefined' : _typeof(object)) != 'object') {
    return false;
  }
  return RANGE_FROM in object;
}

const DEV_MODE = runtime.getDef('DEV_MODE', true);
const CHECK_ASSERTS = runtime.getDef('CHECK_ASSERTS', DEV_MODE);

function _defineProperty$2l(obj, key, value) { key = _toPropertyKey$2l(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2l(arg) { var key = _toPrimitive$2l(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2l(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
const METHODS = defineMethods();
function assertThrow(message, _ignoreFromStack) {
  if (CHECK_ASSERTS) {
    ensureThrow(message, _ignoreFromStack || assertThrow);
  }
}
function ensureThrow(message, _ignoreFromStack) {
  throw new AssertionError(undefined, message, _ignoreFromStack || ensureThrow);
}
function assertTruthy(test, message, _ignoreFromStack) {
  if (CHECK_ASSERTS) {
    ensureTruthy(test, message, _ignoreFromStack || assertTruthy);
  }
}
function ensureTruthy(test, message, _ignoreFromStack) {
  if (!test) {
    throw new AssertionError(test, message, _ignoreFromStack || ensureTruthy);
  }
}
const assert = addValidateMethods(assertTruthy);
const ensure = addValidateMethods(ensureTruthy);
const test = addTestMethods(function test(test) {
  return !!test;
});
const assertionErrorTag = Symbol('AssertionError');
class AssertionError extends Error {
  static isInstance(value) {
    return typeof value === 'object' && value != null && assertionErrorTag in value;
  }
  constructor(value, message = 'Assertion failed', _ignoreFromStack) {
    super(getMessage(message, value));
    _defineProperty$2l(this, assertionErrorTag, true);
    if (_ignoreFromStack && Error.captureStackTrace) {
      Error.captureStackTrace(this, _ignoreFromStack);
    }
  }
}
function defineMethods() {
  return {
    fail: () => result('Assertion failed', false),
    // Unfortunately equality in EcmaScript comes in unlimited forms. This tests
    // SameValueZero[1] with an exception that `null` and `undefined` are
    // considered equal.
    //
    // This is similar to `===` with the following exceptions:
    // - same(null, undefined) => true
    // - same(NaN, NaN) => true
    //
    // [1] https://tc39.es/ecma262/#sec-samevaluezero
    same: (a, b) => result(emsg`Expected values to be equal: ${a} and ${b}`, Object.is(a, b) || a == null && b == null || a === 0 && b === 0),
    nullish: value => result(emsg`Expected null or undefined: ${value}`, value == null),
    notNullish: value => result(emsg`Unexpected null or undefined: ${value}`, value != null),
    boolean: value => result(emsg`Expected boolean: ${value}`, typeof value === 'boolean'),
    number: value => result(emsg`Expected number: ${value}`, typeof value === 'number'),
    string: value => result(emsg`Expected string: ${value}`, typeof value === 'string'),
    safeInteger: value => result(emsg`Expected safe integer: ${value}`, Number.isSafeInteger(value)),
    array: value => result(emsg`Expected array: ${value}`, Array.isArray(value)),
    iterable: value => result(emsg`Expected iterable: ${value}`, !!value && typeof value[Symbol.iterator] === 'function'),
    // A "simple object" is an object with tag[1] of "Object".
    //
    // https://tc39.es/ecma262/#sec-object.prototype.tostring
    simpleObject: value => result(emsg`Expected simple object: ${value}`, getStringTag(value) === 'Object'),
    hasOwn: (value, key) => result(emsg`Expected object with key: ${key}`, typeof value === 'object' && value != null && (typeof key === 'string' || typeof key === 'number' || typeof key === 'symbol') && hasOwn$1(value, key)),
    instanceOf: (value, constructor) => result(emsg`
        Expected instance of ${constructor?.name || '[Unknown]'} but found
        ${value}`, value instanceof constructor),
    nodeError: value => result(emsg`Expected Node error ${value}`, value instanceof Error && !!value.code)
  };
}
function createValidateMethods(validate) {
  return {
    fail: wrapValidate(validate, METHODS.fail),
    same: wrapValidate(validate, METHODS.same),
    nullish: wrapValidate(validate, METHODS.nullish),
    notNullish: wrapValidate(validate, METHODS.notNullish),
    boolean: wrapValidate(validate, METHODS.boolean),
    number: wrapValidate(validate, METHODS.number),
    safeInteger: wrapValidate(validate, METHODS.safeInteger),
    string: wrapValidate(validate, METHODS.string),
    array: wrapValidate(validate, METHODS.array),
    iterable: wrapValidate(validate, METHODS.iterable),
    simpleObject: wrapValidate(validate, METHODS.simpleObject),
    hasOwn: wrapValidate(validate, METHODS.hasOwn),
    instanceOf: wrapValidate(validate, METHODS.instanceOf),
    nodeError: wrapValidate(validate, METHODS.nodeError)
  };
}
function createTestMethods() {
  return {
    fail: wrapTest(METHODS.fail),
    same: wrapTest(METHODS.same),
    nullish: wrapTest(METHODS.nullish),
    notNullish: wrapTest(METHODS.notNullish),
    boolean: wrapTest(METHODS.boolean),
    number: wrapTest(METHODS.number),
    safeInteger: wrapTest(METHODS.safeInteger),
    string: wrapTest(METHODS.string),
    array: wrapTest(METHODS.array),
    iterable: wrapTest(METHODS.iterable),
    simpleObject: wrapTest(METHODS.simpleObject),
    hasOwn: wrapTest(METHODS.hasOwn),
    instanceOf: wrapTest(METHODS.instanceOf),
    nodeError: wrapTest(METHODS.nodeError)
  };
}

// The T in Result<T> is used by wrapValidate, but eslint can't understand that.
// eslint-disable-next-line @typescript-eslint/no-unused-vars

function result(error, pass) {
  return [error, pass];
}
function addValidateMethods(validate) {
  return Object.assign(function wrapper(test, message) {
    validate(!!test, applyToMessage(message, test), wrapper);
    return test;
  }, createValidateMethods(validate));
}
function addTestMethods(test) {
  return Object.assign(test, createTestMethods());
}
function wrapValidate(validate, test) {
  return function wrapper(...args) {
    const testArgs = args.slice(0, test.length);
    const customError = test.length === args.length ? undefined : args[args.length - 1];
    const [error, pass] = test(...testArgs);
    const value = args[0];
    validate(pass, applyToMessage(customError ?? error, value), wrapper);
    return value;
  };
}
function wrapTest(test) {
  return (value, ...args) => {
    const [, pass] = test(value, ...args);
    return pass;
  };
}
function getMessage(message, value) {
  if (typeof message === 'string') {
    return message;
  }
  const msg = message(value);
  if (typeof msg === 'string') {
    return msg;
  }
  return msg(value);
}

// Apply the `test` value as an argument to `message` if `message` is a
// function. Do this two levels deep if `message` is a function that
// returns a function. This is to make the types work out.
function applyToMessage(message, value) {
  return typeof message === 'string' || message === undefined ? message : () => {
    const unwrapped = message(value);
    return typeof unwrapped === 'string' || unwrapped === undefined ? unwrapped : unwrapped(value);
  };
}
function getStringTag(value) {
  const result = Object.prototype.toString.call(value);
  assert(/^\[object /.test(result));
  assert(/\]$/.test(result));
  return result.slice(8, -1);
}
const hasOwn$1 = Function.prototype.call.bind(Object.prototype.hasOwnProperty);

function* linkIterables(iterables) {
  const iters = iterables.map(u => u[Symbol.iterator]());
  const done = {};
  while (true) {
    let allDone = true;
    const results = [];
    for (const [i, iter] of iters.entries()) {
      if (done[i]) {
        results.push(undefined);
        continue;
      }
      const result = iter.next();
      if (result.done) {
        done[i] = true;
      } else {
        allDone = false;
      }
      results.push(result.value);
    }
    if (allDone) {
      return results;
    }
    yield results;
  }
}

// Many more types of itrables may be added here. Feel free to add whatever is
// needed, as long as the iterable can be both sized with `size` or `length`
// properties and it can be retraversed by calling `Symbol.iterator` unlimited
// times.

// Note that the process of comparing iterables is necessarily destructive for
// ephemeral iterables and cannot work for infinite iterables. That is why this
// function is typed for specific kinds of iterables and will throw if it cannot
// determine the size of the iterable through `length` or `size` properties.
function iterablesEqual(a, b, compare = test.same) {
  // Check for equal sizes as a shortcut to determine a class of unequal
  // iterables.
  const aSize = getSizeOf(a);
  const bSize = getSizeOf(b);
  ensure(aSize !== -1 && bSize !== -1, 'Must be able to determine size of iterables when comparing');
  if (aSize !== bSize) {
    return false;
  }

  // Even though this check is quicker, we still check size first to keep the
  // constraint that the iterables passed in must provide their size.
  if (a === b) {
    return true;
  }
  for (const [aItem, bItem] of linkIterables([a, b])) {
    if (!compare(aItem, bItem)) {
      return false;
    }
  }
  return true;
}

// Check size of iterables that offer the information like arrays and sets
// without expending iterables that don't offer size information.
function getSizeOf(a) {
  // `size` is checked before `length` so that Immutable.js types won't
  // complain (they warn if the `length` property is accessed).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const size = a.size;
  if (isValidSize(size)) {
    return size;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const length = a.length;
  if (isValidSize(length)) {
    return length;
  }
  return -1;
}
function isValidSize(value) {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0;
  }
  return false;
}

const hasOwn = Function.prototype.call.bind(Object.prototype.hasOwnProperty);

function _defineProperty$2k(obj, key, value) { key = _toPropertyKey$2k(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2k(arg) { var key = _toPrimitive$2k(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2k(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

// A more accuracte description of RawData would be
// {[id: number]: RawValue}|RawValue[]

const MAX_PROTOTYPE_CHAIN = 16;
const defaults = new WeakMap();
// `typeof Message` and `Message` are both included in the type because this
// stores both classes and prototypes.
const canonicalTypes = new WeakMap();
const protoName = Symbol('protoName');

// This setting has no effect in prod but can be changed in dev.
let _useCompactJson = !DEV_MODE;
function configUseCompactJson(value) {
  _useCompactJson = value;
}
class Message {
  static new_(fromObject, prototypeChainSize, data, cache) {
    return Message.createMessageFromData(prototypeChainSize, Object.getPrototypeOf(fromObject), data, cache);
  }

  // DEPRECATED. Unfortunately returning a subclass instance from a getter isn't
  // currently supported by TypeScript[1]. Use `getDefault()` instead.
  //
  // Note: This is marked as `private` to prevent its use in TypeScript because
  // using it could create subtle type bugs where it works until the message is
  // extended later.
  //
  // [1] https://github.com/microsoft/TypeScript/issues/35986
  static get default() {
    return this.getDefault();
  }
  static getDefault() {
    const type = getCanonical(this);
    if (defaults.has(type)) {
      return defaults.get(type);
    }
    const defaultValue = type.createEmpty_();
    defaults.set(type, defaultValue);
    return defaultValue;
  }
  static get useCompactJson() {
    return _useCompactJson;
  }

  // This method can be overridden in a proto extension to support advanced
  // conversions from data to the message. However, for most cases it is
  // recommended that the helper methods fromString, fromNumber, and
  // fromBoolean be overridden instead.
  static from(data) {
    return Message.from_(this, this[protoName], data, false /* strictJson */);
  }

  // If strictJson is true, data can be either:
  // - a raw, numerically-keyed data object or array
  // - an object mapping from field names
  //
  // If strictJson is false, in addition to one of the types above, data can
  // also be an instance of the current Message subclass or a string, number,
  // boolean if the message has a corresponding method for fromString,
  // fromNumber, or fromBoolean.
  //
  // It is also allowed to override the `from` method if greater control is
  // allowed. Just keep in mind if `from` is overridden methods `fromJson`,
  // `fromString`, `fromNumber`, and `fromBoolean` will not be automatically
  // called.
  //
  // If data is null, an empty message is created.
  static from_(Msg, messageName, data, strictJson) {
    if (data == null) {
      return Msg.getDefault();
    }
    if (!strictJson) {
      switch (typeof data) {
        case 'string':
          return Msg.fromString(data);
        case 'number':
          return Msg.fromNumber(data);
        case 'boolean':
          return Msg.fromBoolean(data);
        case 'object':
          // Allow passing in a message instead of just raw JSON data.
          if (data instanceof Msg) {
            return data;
          }
          // Otherwise, continue to code that handles JSON data.
          break;
        default:
          assert.fail(emsg`
            Cannot convert message ${messageName} from type ${typeof data}
          `);
      }
    }
    assert(test.simpleObject(data) || Array.isArray(data), emsg`
      Invalid value for creating message ${messageName} from JSON
      Found type ${typeof data}: ${data},
    `);
    const strictData = data;
    const rawData = Message.maybeConvertObjectMappingToData(Msg, messageName, strictData, strictJson);

    // TODO(nathan): Currently no validation is performed to ensure that
    // only one value in a oneof is set. This is something which we should
    // at least be doing in dev mode.
    // TODO(nathan): Validate types.
    return Message.createMessageFromData(1, Msg.prototype, rawData, Object.create(null) // cache
    );
  }

  // It is not recommended that fromJson be overridden at this time due to
  // the difficulty of getting it right. fromJson is often used for
  // deserializing messages, and making sure a custom overridden fromJson
  // won't interfere with the standard deserialization isn't easy. In the
  // future perhaps we could add a fromObject that is called only in
  // non-strictJson mode to support overriding if it becomes a desired
  // feature, but more thought needs to go into that to make sure it makes
  // sense and is done well.
  static fromJson(data) {
    return Message.from_(this, this[protoName], data, true /* strictJson */);
  }

  // This function will automatically be called if a string is passed to
  // the from method (unless from is overridden). It can be overridden in a
  // proto extension to support converting to a message from a string.
  static fromString(v) {
    assert.fail(emsg`Cannot convert message ${this[protoName]} from a string: ${v}`);
    return this.getDefault();
  }

  // This function will automatically be called if a number is passed to
  // the from method (unless from is overridden). It can be overridden in a
  // proto extension to support converting to a message from a number.
  static fromNumber(v) {
    assert.fail(emsg`Cannot convert message ${this[protoName]} from a number: ${v}`);
    return this.getDefault();
  }

  // This function will automatically be called if a boolean is passed to
  // the from method (unless from is overridden). It can be overridden in a
  // proto extension to support converting to a message from a boolean.
  static fromBoolean(v) {
    assert.fail(emsg`Cannot convert message ${this[protoName]} from a boolean: ${v}`);
    return this.getDefault();
  }
  static createMessageFromData(prototypeChainSize, prototype, data, cache) {
    return createMessageFromTrustedData(prototypeChainSize, prototype,
    // Copy (and flatten) the data because it may have come from an
    // outside source and we want to ensure it is not tampered with.
    Object.freeze(assignProtos(Object.create(null), data)), cache);
  }
  static maybeConvertObjectMappingToData(Msg, messageName, data, strictJson) {
    if (Array.isArray(data)) {
      return data;
    }
    const keys = Object.keys(data);
    if (keys.length === 0) {
      return data;
    }

    // The keys should either be all numerical or all start with a letter
    // or underscore.
    const firstNumerical = isNumerical(keys[0]);
    assert(keys.every(u => isNumerical(u) === firstNumerical));
    if (firstNumerical) {
      return data;
    }
    return Message.mapFields_(Msg, messageName, data, strictJson, Msg => Msg.idsByName_);
  }

  // This function handles both conversions from field names to field IDs and
  // from field IDs to field names, depending on which `getFieldMap` is passed
  // in.
  //
  // If `data` is an object mapping from field names, convert it to
  // a raw data object mapping from field numbers.
  //
  // If `data` is an object mapping from ids, convert it to an object mapping
  // from field names. Keep in mind `data` may be an array, but it will still
  // be mapping from ids (array indices) and can be converted in the same way
  // as an object.
  static mapFields_(Msg, messageName, data, strictJson, getFieldMap) {
    const map = assert(getFieldMap(Msg));
    const newData = Object.create(null);
    for (const key of Object.keys(data)) {
      // If `data` is an array, there will be a key "0" that doesn't map to a
      // field that should be skipped.
      if (key === '0') {
        continue;
      }
      const newKey = assert(isNumerical(key) ? map[+key] : map[key], `Unknown field ${JSON.stringify(key)} ` + `(message ${JSON.stringify(messageName)}). ` + 'Expected one of: ' + Object.getOwnPropertyNames(map).map(u => JSON.stringify(u)).join(', '));
      const value = isNumerical(key) ? data[+key] : data[key];
      let newValue;
      if (value == null) {
        newValue = value;
      } else {
        const fieldId = isNumerical(key) ? +key : +newKey;
        const convert = Msg.getFieldMapper_(fieldId);
        newValue = convert ? convert(value, strictJson, getFieldMap) : value;
      }
      // This odd little dance is to make TypeScript happy.
      if (typeof newKey === 'string') {
        newData[newKey] = newValue;
      } else {
        newData[newKey] = newValue;
      }
    }
    return newData;
  }
  static unwrapData(value) {
    if (Array.isArray(value)) {
      return value.map(u => Message.unwrapData(u));
    }
    if (value instanceof Message) {
      return value.data_;
    }
    // Convert boolean values to a 0 or 1 for storage.
    if (typeof value === 'boolean') {
      return +value;
    }
    return value ?? null;
  }
  static createEmpty_() {
    const value = Object.create(this.prototype);
    value.prototypeChainSize = 1;
    // TODO(nathan): Would it improve performance to only freeze the objects
    // in dev mode but remove that in production?
    value.data_ = Object.freeze(Object.create(null));
    value.cache_ = Object.create(null);
    return value;
  }

  // `value` can be a `Message` or a primitive. Otherwise, getting a value ID is
  // impossible.
  static getValueId(value) {
    if (typeof value === 'object') {
      return assert.instanceOf(value, Message).valueId();
    }
    assert(typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string', emsg`
        Expected type boolean, number, string, or Message, but found ${value}`);
    return JSON.stringify(value);
  }
  constructor() {
    _defineProperty$2k(this, "prototypeChainSize", void 0);
    _defineProperty$2k(this, "data_", void 0);
    _defineProperty$2k(this, "cache_", void 0);
    return this.constructor.getDefault();
  }
  toJson() {
    return prepForJson(this.data_);
  }

  // This method is required in order for `JSON.stringify` to work correctly.
  // Use `toJson` internally to get an object, since it follows our conventions.
  // To get a string, use `JSON.stringify(message)`.
  'toJSON'() {
    return this.toJson();
  }

  // The purpose of `valueId` is to take a Message and turn it into a primitive
  // value (currently a string) in such a way that producing a new Message in a
  // different way containing the same data would also produce the same value
  // ID. The purpose is to test equality, so two Messages with different data
  // should always produce a different value ID.
  //
  // For example:
  //
  //     valueId(new Foo().setBar(1)) === valueId(new Foo().setBar(2).setBar(1))
  //
  // TODO(nathan): Come up with a safer/faster implementation?
  //
  valueId() {
    // We're writing out some manual caching logic instead of using an
    // abstraction because we need this method to be defined on the prototype
    // rather than the instance (because Messages make several optimizations
    // which assume methods are defined on the prototype).
    if (!hasOwn(this.cache_, 'valueId')) {
      this.cache_.valueId = JSON.stringify(this);
    }
    return assert.string(this.cache_.valueId);
  }
  hashCode() {
    return hash(this.valueId());
  }

  // Important: If `equals` is overridden in an extension file, it needs to
  // make sure the value is an instance of the correct proto message class.
  // It doesn't necessarily need to check the `valueId()` (in fact, it likely
  // shouldn't, otherwise there's no point in overriding `equals`).
  equals(test) {
    return !!test && test instanceof this.constructor && this.valueId() === test.valueId();
  }
  isDefault() {
    return this.equals(this.constructor.getDefault());
  }
  getFieldById(id, defaultValue) {
    if (id in this.prototypes_) {
      return this.getObjectById(id, assert.instanceOf(defaultValue, Message));
    }
    const value = this.data_[id] ?? defaultValue;
    // Convert boolean values from `0` or `1` back to `true` or `false`.
    return defaultValue === false ? !!value : value;
  }
  getObjectById(id, defaultValue) {
    return this.cache_[id] ?? this.createObjectForId(id, defaultValue);
  }
  createObjectForId(id, defaultValue) {
    const data = this.data_[id];
    const prototype = assert.instanceOf(this.prototypes_[id], Message);
    if (data == null) {
      return defaultValue;
    }
    const object = wrapData(prototype, data);
    this.cache_[id] = object;
    return object;
  }

  // Provides a way to conditionally set a value without breaking the flow of
  // object construction or alteration.
  set(callback) {
    return callback(this);
  }
  setValueFieldById(id, value, oneof) {
    return this.setFieldById(id, value, null /* object */, oneof);
  }
  setFieldById(id, value, object, oneof) {
    const unwrappedValue = Message.unwrapData(value);
    // If it's not a oneof, and it's being set to the default value, then set it
    // to `null` instead. This allows us to skip serializing it, which not only
    // saves space, but also gives us a way to compare whether two messages are
    // the same.
    const V = oneof ? unwrappedValue : defaultToNull(unwrappedValue);
    if (this.data_[id] === V) {
      return this;
    }
    const prototypeChainSize = this.prototypeChainSize + 1;
    let newInstance;
    if (prototypeChainSize >= MAX_PROTOTYPE_CHAIN) {
      newInstance = Message.new_(this, 1, Object.freeze(Object.assign(assignProtos(Object.create(null), this.data_), {
        [id]: V
      })),
      // We don't want the `valueId` property defined directly on the cache
      // (note how it uses a `hasOwn` check to determine if it's still
      // valid).
      Object.create(Object.assign(assignProtos(Object.create(null), this.cache_), {
        [id]: object
      })));
    } else {
      newInstance = Message.new_(this, prototypeChainSize,
      // We need to mark the property enumerable so that it's picked up by
      // `JSON.stringify` and `Object.assign`.
      Object.freeze(Object.create(this.data_, {
        [id]: {
          value: V,
          enumerable: true
        }
      })), Object.create(this.cache_, {
        [id]: {
          value: object,
          enumerable: true,
          writable: true,
          configurable: true
        }
      }));
    }
    Object.freeze(newInstance);
    if (oneof == null || value == null) {
      return newInstance;
    }
    return newInstance.clearAlternates(oneof, id);
  }
  clearAlternates(oneof, id) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let instance = this;
    const alternates = assert(this.oneofs_[oneof], emsg`Expected oneof not found ${oneof}.`);
    for (const fieldId of alternates) {
      if (fieldId !== id) {
        instance = instance.setFieldById(fieldId, null /* value */,
        // We pass a null value for oneof this time to avoid an infinite
        // recursion.
        null /* object */, null /* oneof */);
      }
    }

    return instance;
  }
  getRepeatedFieldById(id) {
    const cached = this.cache_[id];
    return cached ? assertImmutableList(cached) : this.createRepeatedFieldForId(id);
  }
  createRepeatedFieldForId(id) {
    const data = this.data_[id];
    if (data == null) {
      const list = List();
      this.cache_[id] = list;
      return list;
    }
    assert.array(data);
    const prototype = assertMaybeMessage(this.prototypes_[id]);
    const list = List(function* () {
      for (const item of data) {
        yield wrapData(prototype, item);
      }
    }());
    this.cache_[id] = list;
    return list;
  }
  addToRepeatedFieldById(id, value) {
    assert(value != null, emsg`Field ${id} in ${getConstructorProtoName(this)} cannot be null.`);
    // We use an array for `data` instead of an ImmutableList because we want
    // `data` to be quickly serializable through `JSON.stringify` and
    // deserializable through `JSON.parse`.
    const data = assert.array(this.data_[id] ?? []);
    const list = this.cache_[id] ?? null;
    return this.setFieldById(id, [...data, Message.unwrapData(value)], list ? assertImmutableList(list).push(value) : null, null // oneof
    );
  }

  setRepeatedFieldById(id, index, value) {
    const data = assert.array(this.data_[id] ?? []);
    const list = this.cache_[id] ?? null;
    return this.setFieldById(id, [...data.slice(0, index), Message.unwrapData(value), ...data.slice(index + 1)], list ? assertImmutableList(list).set(index, value) : null, null // oneof
    );
  }

  overwriteRepeatedFieldById(id, values) {
    const valueArr = [...values];
    if (!iterablesEqual(this.getRepeatedFieldById(id), valueArr)) {
      return this.setFieldById(id, valueArr.map(value => Message.unwrapData(value)), List(valueArr), null // oneof
      );
    }

    return this;
  }
  repeatedFieldByIdHas(id, value) {
    const data = this.data_[id] ?? null;
    if (!data) {
      return false;
    }
    if (typeof value === 'object') {
      // Objects (arrays and messages) are considered equal if they have the
      // same data store. In order to compare them as quickly as possible they
      // are unwrapped and stringified.
      // TODO(nathan): Does enough normalization of data during unwrapping occur
      // in order to ensure there's only one valid stringification for a
      // particular set of data, no matter where the data came from?
      const stringifiedValue = JSON.stringify(value);
      for (const d of assert.array(data)) {
        if (JSON.stringify(Message.unwrapData(d)) === stringifiedValue) {
          return true;
        }
      }
    } else {
      // Primitives can take a faster route which doesn't require unwrapping
      // and stringifying data.
      for (const d of assert.array(data)) {
        if (d === value) {
          return true;
        }
      }
    }
    return false;
  }
  removeFromRepeatedFieldById(id, index) {
    const data = assert.array(this.data_[id] ?? []);
    const list = this.cache_[id] ?? null;
    return this.setFieldById(id, [...data.slice(0, index), ...data.slice(index + 1)], list ? assertImmutableList(list).delete(index) : null, null // oneof
    );
  }

  clearRepeatedFieldById(id) {
    return this.setFieldById(id, null,
    // value
    null,
    // object
    null // oneof
    );
  }

  getMapById(id) {
    return assertImmutableMap(this.cache_[id] ?? this.createMapForId(id));
  }
  getMapSizeById(id) {
    return this.getMapById(id).size;
  }
  hasMapKeyById(id, key) {
    return this.getMapById(id).has(key);
  }
  getMapValueById(id, key) {
    return this.getMapById(id).get(key) ?? null;
  }
  getMapIteratorById(id) {
    return {
      [Symbol.iterator]: () => {
        return this.getMapById(id).keys();
      }
    };
  }
  getMapPrototypes(id) {
    const prototypes = this.prototypes_[id];
    if (!prototypes) {
      return undefined;
    }

    // Map prototypes are stored as key value pairs.
    assert.same(assert.array(prototypes).length, 2);
    const pair = prototypes;
    assertMaybeMessage(pair[0]);
    assertMaybeMessage(pair[1]);
    return pair;
  }
  getMapKeyPrototype(id) {
    const prototypes = this.getMapPrototypes(id);
    return prototypes && prototypes[0];
  }
  getMapValuePrototype(id) {
    const prototypes = this.getMapPrototypes(id);
    return prototypes && prototypes[1];
  }
  getMapData(id) {
    const data = this.data_[id];
    if (!data) {
      return [];
    }
    // Maps are stored as an array of arrays.
    for (const item of assert.array(data)) {
      assert.same(assert.array(item).length, 2);
    }
    return data;
  }
  createMapForId(id) {
    const data = this.getMapData(id);
    // The protobuf spec only allows primitive map keys.
    assert.nullish(this.getMapKeyPrototype(id));
    const valuePrototype = this.getMapValuePrototype(id);
    const map = Map$1(function* () {
      for (const [key, value] of data) {
        yield [key, wrapData(valuePrototype, value)];
      }
    }());
    this.cache_[id] = map;
    return map;
  }

  // TODO(nathan): If the same key is set twice, are there any problems? The
  // data array is just concatenating the new key and value each time. So for
  // multiples of the same key, when the data is sent over the wire, are all
  // values sent? If so, we should at least remove those extra values before
  // serializing. We should perhaps remove them from memory as well.
  setMapValue(id, key, value) {
    const data = this.getMapData(id);
    const map = this.cache_[id] ?? null;
    return this.setFieldById(id, [...data, [Message.unwrapData(key), Message.unwrapData(value)]], map ? assertImmutableMap(map).set(key, value) : null, null // oneof
    );
  }

  // TODO(nathan): This is O(n), but it can be improved to O(1).
  removeFromMapById(id, key) {
    const data = this.getMapData(id);
    const map = this.cache_[id] ?? null;
    const valueId = Message.getValueId(key);
    const filteredData = data.filter(([k]) => Message.getValueId(k) !== valueId);
    return this.setFieldById(id, filteredData, map ? assertImmutableMap(map).delete(key) : null, null // oneof
    );
  }

  clearMap(id) {
    return this.setFieldById(id, [], null /* object */, null /* oneof */);
  }

  getCaseFromIds(ids) {
    for (const id of ids) {
      if (this.data_[id] != null) {
        return id;
      }
    }
    return 0;
  }
}
_defineProperty$2k(Message, protoName, '[Unknown]');
function createMessageFromTrustedData(prototypeChainSize, prototype, data, cache) {
  const message = Object.create(getCanonical(prototype), {
    prototypeChainSize: {
      value: prototypeChainSize
    },
    data_: {
      value: data
    },
    cache_: {
      value: cache
    }
  });
  return message;
}
function wrapData(prototype, data) {
  if (prototype == null) {
    return assertPrimitive(data);
  }
  assert(test.simpleObject(data) || test.array(data), emsg`
      Expected simple object or array but found type ${typeof data}: ${data}`);
  const rawData = data;
  return createMessageFromTrustedData(countPrototypeChainSize(rawData), prototype, rawData, Object.create(null) // cache
  );
}

// An empty object should be converted to `null`, but this should not be done
// recursively! For example, `[]` should convert to `null`, but `[0, []]` should
// not. In the case an object contains an empty object, it is preserving a oneof
// case.
function defaultToNull(value) {
  if (value) {
    if (typeof value !== 'object') {
      return value;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        return value;
      }
    } else if (!isEmptyObjectOrNull(value)) {
      return value;
    }
  }
  return null;
}

// See not above on `defaultToNull` noting that this should not check for
// recursively empty objects, only truly empty objects.
function isEmptyObjectOrNull(value) {
  if (typeof value !== 'object') {
    return false;
  }
  if (value == null) {
    return true;
  }
  const flattened = assignProtos(Object.create(null), value);
  const keys = getSortedNumericKeys(flattened);
  if (keys.length === 0) {
    return true;
  }
  return false;
}
function countPrototypeChainSize(object) {
  if (object == null) {
    return 0;
  }
  return countPrototypeChainSize(Object.getPrototypeOf(object)) + 1;
}

// Assigns all properties on `copyFrom` to `copyTo`, including properties on
// `copyFrom`'s prototype chain.
function assignProtos(copyTo, copyFrom) {
  if (copyFrom == null || copyFrom === Object.prototype) {
    return copyTo;
  }
  return Object.assign(assignProtos(copyTo, Object.getPrototypeOf(copyFrom)), copyFrom);
}

// Deep clones the object, flattening its prototype chain so that it can be used
// with `JSON.stringify`, also checking to see if replacing it with an array
// will be beneficial.
function prepForJson(value) {
  if (value != null && typeof value === 'object') {
    if (value instanceof Array) {
      return value.map(prepForJson);
    }
    return flattenToObjectOrArray(value);
  }
  return value;
}
function flattenToObjectOrArray(copyFrom) {
  const flattened = assignProtos(Object.create(null), copyFrom);
  const keys = getSortedNumericKeys(flattened);
  if (keys.length === 0) {
    return [];
  }
  // Try to serialize as an array if it's easily apparent that we can save space
  // that way.
  const highest = keys[keys.length - 1];
  if (highest === keys.length) {
    const array = [];
    // The first item in the array will be ignored, since message properties
    // start at 1.
    array.push(0);
    const filler = [];
    for (const key of keys) {
      const value = prepForJson(flattened[key]);
      if (value != null) {
        array.push(...filler.splice(0, filler.length), value);
      } else {
        filler.push(value);
      }
    }
    return array;
  }
  let copyTo = null;
  for (const key of keys) {
    // A `0` key may end up on the data object if it originated from a
    // serialized form of the message, where an array was used in the
    // serialization with a leading `0` indexed property for padding. Later this
    // array may be used as the prototype for a new data object which then
    // becomes sparse and so is serialized as an object rather than an array.
    // In such a case, we should not include the `0` indexed property.
    if (key !== 0) {
      const value = prepForJson(flattened[key]);
      if (value != null) {
        if (!copyTo) {
          copyTo = Object.create(null);
        }
        copyTo[key] = value;
      }
    }
  }
  return copyTo;
}
function getSortedNumericKeys(object) {
  let highest = 0;
  let sorted = true;
  const result = [];
  for (const key of Object.keys(object)) {
    const k = +key;
    // Exclude 0 if it's present and any non-numeric keys.
    // Also exclude null values, since these are ones which have been deleted.
    if (k > 0 && object[k] != null) {
      if (k > highest) {
        highest = k;
      } else {
        sorted = false;
      }
      result.push(k);
    }
  }
  // We can skip the O(n log n) sorting step if it's already sorted.
  // NOTE: ES6 compliant JavaScript engines will already have these keys in the
  // correct order.
  if (sorted) {
    return result;
  }
  return result.sort((a, b) => a - b);
}

// `extension` should be a class defining properties to add to message. They can
// be static or instance properties.
function extendMessage(name, getExtension) {
  return message => {
    if (!getExtension) {
      return message;
    }
    const extension = getExtension(message);
    assert(typeof extension === 'function');
    assert(typeof extension.prototype === 'object' && extension.prototype != null);
    if (!extension.name) {
      Object.defineProperty(extension, 'name', {
        'value': `${name}Ext`
      });
    }
    return extension;
  };
}
function defineEnumProperties(en, ids) {
  return {
    ...en,
    [Symbol.iterator]() {
      return ids[Symbol.iterator]();
    },
    has(value) {
      return ids.indexOf(value) !== -1;
    }
  };
}

// `extension` should be an object defining properties to add to en.
function extendEnum(enumName, values, getExtension) {
  return en => {
    const extension = getExtension ? getExtension(en) : Object.create(null);
    const nameToId = Object.create(null);
    const idToName = Object.create(null);
    for (const [name, id] of values) {
      nameToId[name] = id;
      idToName[id] = name;
    }
    function fromString(valueName) {
      const id = nameToId[valueName];
      assert(id != null, emsg`
          Invalid value ${valueName} for enum ${enumName}.
          Expected one of ${Object.keys(nameToId)}`);
      return id;
    }
    function fromNumber(valueId) {
      assert(idToName[valueId], emsg`
          Invalid value (${valueId}) for enum "${enumName}".
          Expected one of ${Object.keys(idToName)}`);
      return valueId;
    }
    function fromNumberToString(valueId) {
      return assert(idToName[valueId], emsg`
          Invalid value (${valueId}) for enum "${enumName}".
          Expected one of ${Object.keys(idToName)}`);
    }
    function fromStrictJson(value, getFieldMap) {
      // If we're converting from IDs to name, we want to honor that so that
      // obfuscated IDs become readable names for e.g. debugging. However, if
      // we're converting from names to IDs we want to also support users
      // specifying with IDs (while leaving it as the ID during the conversion).
      //
      //
      // This allows us to accept both forms {'field_a': 'VALUE_X'} and
      // {'field_a': FieldA.VALUE_X} for a message.
      const convertToId = getFieldMap({
        idsByName_: true,
        namesById_: false
      });
      switch (typeof value) {
        case 'string':
          return fromString(value);
        case 'number':
          return convertToId ? fromNumber(value) : fromNumberToString(value);
      }
      assert.fail(emsg`
          Expected type "number" or "string" as value for enum ${enumName}
          but found ${typeof value}.`);
      return 0;
    }
    const nameRegexp = new RegExp(['^(', values.map(([name]) => name).join('|'), ')$'].join(''));

    // `from`, `fromString`, and `fromNumber` may be overridden.
    if (!extension.from) {
      // `from` needs to reference `wrapper` so that overridden `fromNumber` and
      // `fromString` methods will be used, while `fromStrictJson` shouldn't.
      // That's why both functions are needed even though they're nearly
      // identical.
      extension.from = function from(data) {
        switch (typeof data) {
          case 'string':
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return wrapper.fromString(data);
          case 'number':
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return wrapper.fromNumber(data);
        }
        assert.fail(emsg`
            Cannot convert enum ${enumName} from type ${typeof data}`);
        return 0;
      };
    }
    if (!extension.fromString) {
      extension.fromString = fromString;
    }
    if (!extension.fromNumber) {
      extension.fromNumber = fromNumber;
    }
    // `fromStrictJson_` shouldn't be overridden.
    extension.fromStrictJson_ = fromStrictJson;
    if (!extension.nameRegexp) {
      extension.nameRegexp = nameRegexp;
    }
    if (!extension.toString) {
      extension.toString = fromNumberToString;
    }
    const wrapper = {
      ...en,
      ...extension
    };
    return wrapper;
  };
}

// This is needed to convert base messages and enums to the correct extended
// values.
function getCanonical
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(type) {
  return canonicalTypes.get(type) ?? type;
}
function registerCanonical(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
base,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
extension) {
  canonicalTypes.set(base, extension);
  if (typeof base === 'function' && base.prototype) {
    canonicalTypes.set(base.prototype, extension.prototype);
  }
}
function getConstructorProtoName(value) {
  return value && value.constructor && value.constructor[protoName] || '[Unknown]';
}
function isNumerical(u) {
  return /^\d+$/.test(u);
}
function assertPrimitive(value) {
  const type = typeof value;
  assert(type === 'string' || type === 'number' || type === 'boolean');
  return value;
}
function assertMaybeMessage(value) {
  assert(value == null || value instanceof Message);
  return value;
}
function assertImmutableList(value) {
  return assert.instanceOf(value, List);
}
function assertImmutableMap(value) {
  return assert.instanceOf(value, Map$1);
}

// tslint:disable-next-line:no-unused-variable
var AcquisitionStatus__;
(function (AcquisitionStatus__) {
  AcquisitionStatus__[AcquisitionStatus__["UNSET"] = 0] = "UNSET";
  AcquisitionStatus__[AcquisitionStatus__["PROSPECTIVE"] = 1] = "PROSPECTIVE";
  AcquisitionStatus__[AcquisitionStatus__["UNDER_CONTRACT"] = 2] = "UNDER_CONTRACT";
  AcquisitionStatus__[AcquisitionStatus__["OWNED"] = 3] = "OWNED";
})(AcquisitionStatus__ || (AcquisitionStatus__ = {}));
const AcquisitionStatus_ = defineEnumProperties(AcquisitionStatus__, [0, 1, 2, 3]);
const AcquisitionStatus$1 = extendEnum('AcquisitionStatus', [["UNSET", 0], ["PROSPECTIVE", 1], ["UNDER_CONTRACT", 2], ["OWNED", 3]])(AcquisitionStatus_);

const AcquisitionStatus = {
  ...AcquisitionStatus$1,
  format(value) {
    switch (value) {
      case AcquisitionStatus.UNSET:
        return '';
      case AcquisitionStatus.PROSPECTIVE:
        return 'Prospective';
      case AcquisitionStatus.UNDER_CONTRACT:
        return 'Under Contract';
      case AcquisitionStatus.OWNED:
        return 'Owned';
    }
    assert.fail(emsg`Unknown acquisition status: ${value}`);
    return '';
  }
};

registerCanonical(AcquisitionStatus$1, AcquisitionStatus);

function toTitleCase(s) {
  const parts = s.match(/[a-zA-Z0-9]+/g);
  const out = [];
  if (parts) {
    for (const part of parts) {
      out.push(part[0].toUpperCase(), part.slice(1).toLowerCase());
    }
  }
  return out.join('');
}
function fromTitleCase(s, delimiter) {
  const parts = s.match(/([A-Z][a-z0-9]*)|([0-9]+[a-z0-9]*|[a-z0-9]+)/g);
  if (!parts || parts.length === 0) {
    return '';
  }
  const out = [];
  for (const part of parts) {
    assert(!/^[a-z]/.test(part), emsg`Invalid title case string ${s}`);
    out.push(part.toLowerCase());
  }
  return out.join(delimiter);
}

// tslint:disable-next-line:no-unused-variable
var AssetType__;
(function (AssetType__) {
  AssetType__[AssetType__["OTHER"] = 0] = "OTHER";
  AssetType__[AssetType__["APARTMENTS"] = 3] = "APARTMENTS";
  AssetType__[AssetType__["CONDOMINIUMS"] = 6] = "CONDOMINIUMS";
  AssetType__[AssetType__["SENIOR_APARTMENTS"] = 7] = "SENIOR_APARTMENTS";
  AssetType__[AssetType__["STUDENT_HOUSING"] = 8] = "STUDENT_HOUSING";
  AssetType__[AssetType__["ASSISTED_LIVING"] = 9] = "ASSISTED_LIVING";
  AssetType__[AssetType__["RENTAL_PORTFOLIO"] = 10] = "RENTAL_PORTFOLIO";
  AssetType__[AssetType__["MOBILE_HOME_PARK"] = 11] = "MOBILE_HOME_PARK";
  AssetType__[AssetType__["CO_LIVING"] = 12] = "CO_LIVING";
  AssetType__[AssetType__["GENERAL_OFFICE"] = 4] = "GENERAL_OFFICE";
  AssetType__[AssetType__["MEDICAL_OFFICE"] = 13] = "MEDICAL_OFFICE";
  AssetType__[AssetType__["MANUFACTURING"] = 14] = "MANUFACTURING";
  AssetType__[AssetType__["LIGHT_INDUSTRIAL"] = 2] = "LIGHT_INDUSTRIAL";
  AssetType__[AssetType__["CANNABIS_PRODUCTION"] = 15] = "CANNABIS_PRODUCTION";
  AssetType__[AssetType__["DATA_CENTER"] = 21] = "DATA_CENTER";
  AssetType__[AssetType__["HOTEL"] = 1] = "HOTEL";
  AssetType__[AssetType__["SHORT_TERM_HOUSING"] = 20] = "SHORT_TERM_HOUSING";
  AssetType__[AssetType__["RETAIL"] = 5] = "RETAIL";
  AssetType__[AssetType__["LAND"] = 16] = "LAND";
  AssetType__[AssetType__["SELF_STORAGE"] = 17] = "SELF_STORAGE";
  AssetType__[AssetType__["RELIGIOUS"] = 18] = "RELIGIOUS";
  AssetType__[AssetType__["HOSPITAL"] = 19] = "HOSPITAL";
})(AssetType__ || (AssetType__ = {}));
const AssetType_ = defineEnumProperties(AssetType__, [0, 3, 6, 7, 8, 9, 10, 11, 12, 4, 13, 14, 2, 15, 21, 1, 20, 5, 16, 17, 18, 19]);
const AssetType$1 = extendEnum('AssetType', [["OTHER", 0], ["APARTMENTS", 3], ["CONDOMINIUMS", 6], ["SENIOR_APARTMENTS", 7], ["STUDENT_HOUSING", 8], ["ASSISTED_LIVING", 9], ["RENTAL_PORTFOLIO", 10], ["MOBILE_HOME_PARK", 11], ["CO_LIVING", 12], ["GENERAL_OFFICE", 4], ["MEDICAL_OFFICE", 13], ["MANUFACTURING", 14], ["LIGHT_INDUSTRIAL", 2], ["CANNABIS_PRODUCTION", 15], ["DATA_CENTER", 21], ["HOTEL", 1], ["SHORT_TERM_HOUSING", 20], ["RETAIL", 5], ["LAND", 16], ["SELF_STORAGE", 17], ["RELIGIOUS", 18], ["HOSPITAL", 19]])(AssetType_);

const AssetType = {
  ...AssetType$1,
  format(value, other = 'Other') {
    switch (value) {
      case AssetType.OTHER:
        return other;
      case AssetType.APARTMENTS:
        return 'Apartments';
      case AssetType.CONDOMINIUMS:
        return 'Condominiums';
      case AssetType.SENIOR_APARTMENTS:
        return 'Senior Apartments';
      case AssetType.STUDENT_HOUSING:
        return 'Student Housing';
      case AssetType.ASSISTED_LIVING:
        return 'Assisted Living';
      case AssetType.RENTAL_PORTFOLIO:
        return 'Rental Portfolio';
      case AssetType.MOBILE_HOME_PARK:
        return 'Mobile Home Park';
      case AssetType.CO_LIVING:
        return 'Co-Living';
      case AssetType.GENERAL_OFFICE:
        return 'Office';
      case AssetType.MEDICAL_OFFICE:
        return 'Medical Office';
      case AssetType.MANUFACTURING:
        return 'Manufacturing';
      case AssetType.LIGHT_INDUSTRIAL:
        return 'Light Industrial';
      case AssetType.CANNABIS_PRODUCTION:
        return 'Cannabis Production';
      case AssetType.DATA_CENTER:
        return 'Data Center';
      case AssetType.RETAIL:
        return 'Retail';
      case AssetType.HOTEL:
        return 'Hotel';
      case AssetType.SHORT_TERM_HOUSING:
        return 'Short Term Housing';
      case AssetType.LAND:
        return 'Land';
      case AssetType.SELF_STORAGE:
        return 'Self Storage';
      case AssetType.RELIGIOUS:
        return 'Religious';
      case AssetType.HOSPITAL:
        return 'Hospital';
    }
    assert.fail(`Unknown asset type: ${value}`);
    return '';
  },
  isMultiFamily(value) {
    return value === AssetType.APARTMENTS || value === AssetType.CONDOMINIUMS || value === AssetType.SENIOR_APARTMENTS || value === AssetType.STUDENT_HOUSING || value === AssetType.ASSISTED_LIVING || value === AssetType.RENTAL_PORTFOLIO || value === AssetType.MOBILE_HOME_PARK || value === AssetType.CO_LIVING;
  },
  isOffice(value) {
    return value === AssetType.GENERAL_OFFICE || value === AssetType.MEDICAL_OFFICE;
  },
  isIndustrial(value) {
    return value === AssetType.MANUFACTURING || value === AssetType.LIGHT_INDUSTRIAL || value === AssetType.CANNABIS_PRODUCTION || value === AssetType.DATA_CENTER;
  },
  isHospitality(value) {
    return value === AssetType.HOTEL || value === AssetType.SHORT_TERM_HOUSING;
  },
  getUnitLabel(value, count = 1) {
    let label;
    switch (value) {
      case AssetType.HOTEL:
        label = 'key';
        break;
      case AssetType.RENTAL_PORTFOLIO:
        label = 'door';
        break;
      case AssetType.MOBILE_HOME_PARK:
        label = 'space';
        break;
      case AssetType.CO_LIVING:
        label = 'room';
        break;
      default:
        label = 'unit';
        break;
    }
    if (count !== 1) {
      label += 's';
    }
    return label;
  }
};

registerCanonical(AssetType$1, AssetType);

function assetTypeToCapitalString(assetType, other) {
  if (other === undefined) other = 'Other';
  return AssetType.format(assetType, other);
}
function isMultiFamily(assetType) {
  return AssetType.isMultiFamily(assetType);
}
function isOffice(assetType) {
  return AssetType.isOffice(assetType);
}
function isIndustrial(assetType) {
  return AssetType.isIndustrial(assetType);
}
function isHospitality(assetType) {
  return AssetType.isHospitality(assetType);
}
function hasDwellingUnits(assetType) {
  return isHospitality(assetType) || isMultiFamily(assetType);
}
function getUnitLabelTitleCase(assetType, count) {
  if (count === undefined) count = 5;
  return toTitleCase(getUnitLabelLowerCase(assetType, count));
}
function getUnitLabelLowerCase(assetType, count) {
  if (count === undefined) count = 5;
  return AssetType.getUnitLabel(assetType, count);
}

// tslint:disable-next-line:no-unused-variable
var AttachmentType__;
(function (AttachmentType__) {
  AttachmentType__[AttachmentType__["UNSET"] = 0] = "UNSET";
  AttachmentType__[AttachmentType__["PRO_FORMA"] = 19] = "PRO_FORMA";
  AttachmentType__[AttachmentType__["FINANCIAL_STATEMENTS"] = 1] = "FINANCIAL_STATEMENTS";
  AttachmentType__[AttachmentType__["PAST_APPRAISAL"] = 2] = "PAST_APPRAISAL";
  AttachmentType__[AttachmentType__["RENT_ROLL"] = 3] = "RENT_ROLL";
  AttachmentType__[AttachmentType__["TENANT_LEASE"] = 4] = "TENANT_LEASE";
  AttachmentType__[AttachmentType__["TENANT_INFORMATION"] = 21] = "TENANT_INFORMATION";
  AttachmentType__[AttachmentType__["ENVIRONMENTAL_REPORT"] = 5] = "ENVIRONMENTAL_REPORT";
  AttachmentType__[AttachmentType__["MARKET_INFORMATION"] = 6] = "MARKET_INFORMATION";
  AttachmentType__[AttachmentType__["SALES_CONTRACT"] = 7] = "SALES_CONTRACT";
  AttachmentType__[AttachmentType__["TAX_INFORMATION"] = 8] = "TAX_INFORMATION";
  AttachmentType__[AttachmentType__["SALES_COMPS"] = 9] = "SALES_COMPS";
  AttachmentType__[AttachmentType__["LEASE_COMPS"] = 10] = "LEASE_COMPS";
  AttachmentType__[AttachmentType__["SPONSOR_FINANCIALS"] = 11] = "SPONSOR_FINANCIALS";
  AttachmentType__[AttachmentType__["SCHEDULE_OF_REAL_ESTATE_OWNED"] = 12] = "SCHEDULE_OF_REAL_ESTATE_OWNED";
  AttachmentType__[AttachmentType__["SOURCES_AND_USES"] = 13] = "SOURCES_AND_USES";
  AttachmentType__[AttachmentType__["OFFERING_MEMORANDUM"] = 14] = "OFFERING_MEMORANDUM";
  AttachmentType__[AttachmentType__["CONSTRUCTION_BUDGET"] = 15] = "CONSTRUCTION_BUDGET";
  AttachmentType__[AttachmentType__["DESIGN_AND_ENGINEERING"] = 16] = "DESIGN_AND_ENGINEERING";
  AttachmentType__[AttachmentType__["SPONSOR_BIO"] = 17] = "SPONSOR_BIO";
  AttachmentType__[AttachmentType__["ZONING_AND_ENTITLEMENTS"] = 18] = "ZONING_AND_ENTITLEMENTS";
  AttachmentType__[AttachmentType__["HOTEL_FRANCHISE_AGREEMENT"] = 20] = "HOTEL_FRANCHISE_AGREEMENT";
})(AttachmentType__ || (AttachmentType__ = {}));
const AttachmentType_ = defineEnumProperties(AttachmentType__, [0, 19, 1, 2, 3, 4, 21, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 20]);
const AttachmentType$1 = extendEnum('AttachmentType', [["UNSET", 0], ["PRO_FORMA", 19], ["FINANCIAL_STATEMENTS", 1], ["PAST_APPRAISAL", 2], ["RENT_ROLL", 3], ["TENANT_LEASE", 4], ["TENANT_INFORMATION", 21], ["ENVIRONMENTAL_REPORT", 5], ["MARKET_INFORMATION", 6], ["SALES_CONTRACT", 7], ["TAX_INFORMATION", 8], ["SALES_COMPS", 9], ["LEASE_COMPS", 10], ["SPONSOR_FINANCIALS", 11], ["SCHEDULE_OF_REAL_ESTATE_OWNED", 12], ["SOURCES_AND_USES", 13], ["OFFERING_MEMORANDUM", 14], ["CONSTRUCTION_BUDGET", 15], ["DESIGN_AND_ENGINEERING", 16], ["SPONSOR_BIO", 17], ["ZONING_AND_ENTITLEMENTS", 18], ["HOTEL_FRANCHISE_AGREEMENT", 20]])(AttachmentType_);

const AttachmentType = {
  ...AttachmentType$1,
  format(type) {
    switch (type) {
      case AttachmentType.UNSET:
        return '';
      case AttachmentType.PRO_FORMA:
        return 'Pro Forma';
      case AttachmentType.FINANCIAL_STATEMENTS:
        return 'Financial Statements';
      case AttachmentType.PAST_APPRAISAL:
        return 'Past Appraisal';
      case AttachmentType.RENT_ROLL:
        return 'Rent Roll';
      case AttachmentType.TENANT_LEASE:
        return 'Tenant Lease';
      case AttachmentType.TENANT_INFORMATION:
        return 'Tenant Information';
      case AttachmentType.ENVIRONMENTAL_REPORT:
        return 'Environmental Report';
      case AttachmentType.MARKET_INFORMATION:
        return 'Market Information';
      case AttachmentType.SALES_CONTRACT:
        return 'Sales Contract';
      case AttachmentType.TAX_INFORMATION:
        return 'Tax Information';
      case AttachmentType.SALES_COMPS:
        return 'Sales Comps';
      case AttachmentType.LEASE_COMPS:
        return 'Lease Comps';
      case AttachmentType.SPONSOR_FINANCIALS:
        return 'Sponsor Financials';
      case AttachmentType.SCHEDULE_OF_REAL_ESTATE_OWNED:
        return 'Schedule of Real Estate Owned';
      case AttachmentType.SOURCES_AND_USES:
        return 'Sources & Uses';
      case AttachmentType.OFFERING_MEMORANDUM:
        return 'Offering Memorandum';
      case AttachmentType.CONSTRUCTION_BUDGET:
        return 'Construction Budget';
      case AttachmentType.DESIGN_AND_ENGINEERING:
        return 'Design & Engineering';
      case AttachmentType.SPONSOR_BIO:
        return 'Sponsor Bio';
      case AttachmentType.ZONING_AND_ENTITLEMENTS:
        return 'Zoning & Entitlements';
      case AttachmentType.HOTEL_FRANCHISE_AGREEMENT:
        return 'Hotel Franchise Agreement';
    }
    assert.fail(`Unknown attachment type: ${type}`);
    return '';
  }
};

registerCanonical(AttachmentType$1, AttachmentType);

const identity = arg => arg;

function throwableToStack(thrown) {
  if (typeof thrown === 'string') {
    return thrown;
  }
  if (!(thrown instanceof Error)) {
    return JSON.stringify(thrown);
  }
  const error = thrown;
  const {
    cause,
    message = '',
    name = 'UnknownError',
    stack = ''
  } = error;
  let causeString = '';
  if (cause) {
    if (cause instanceof CustomError) {
      causeString = cause.stack;
    } else {
      causeString = throwableToStack(cause);
    }
    causeString = `Caused by: ${causeString}`;
  }
  return [stack ? stack : `${name}: ${message}`, causeString].filter(identity).join('\n');
}
// Defining this way rather than as a declaration so that "CustomError" doesn't
// show up in the error message.
const CustomError = (() => class extends Error {
  constructor(msg, cause) {
    // Why ignore these errors? It seems that the Error type does not allow a
    //   second parameter where you can specify the cause in TypeScript even
    //   though that is the documented structure of Error on MDN. Having this
    //   CustomError base type for new errors we want to construct with a cause
    //   means that we only have to ignore this bad behavior in one location.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    super(typeof msg === 'function' ? msg() : msg, {
      cause
    });
    this.name = this.constructor.name;
    this.stack = throwableToStack(this);
  }
})();

function _defineProperty$2j(obj, key, value) { key = _toPropertyKey$2j(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2j(arg) { var key = _toPrimitive$2j(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2j(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class DEPRECATED_Date_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 3
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message DEPRECATED_Date field from_epoch.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(DEPRECATED_Date$1, 'DEPRECATED_Date', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message DEPRECATED_Date to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get fromEpoch() {
    return this.getFieldById(1, 0);
  }
  setFromEpoch(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteFromEpoch() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$2j(DEPRECATED_Date_, protoName, DEV_MODE ? 'DEPRECATED_Date' : '[Unknown]');
let DEPRECATED_Date$1 = DEPRECATED_Date_;
(function (_DEPRECATED_Date) {})(DEPRECATED_Date$1 || (DEPRECATED_Date$1 = {}));
Object.defineProperties(DEPRECATED_Date$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "from_epoch": {
        value: 1
      },
      "fromEpoch": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "from_epoch"
      }
    }))
  }
});
Object.defineProperties(DEPRECATED_Date$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(DEPRECATED_Date$1);
  Object.freeze(DEPRECATED_Date$1.prototype);
}

function _defineProperty$2i(obj, key, value) { key = _toPropertyKey$2i(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2i(arg) { var key = _toPrimitive$2i(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2i(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class SimpleDate_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message SimpleDate field since_greg.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SimpleDate$1, 'SimpleDate', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SimpleDate to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sinceGreg() {
    return this.getFieldById(1, 0);
  }
  setSinceGreg(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSinceGreg() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$2i(SimpleDate_, protoName, DEV_MODE ? 'SimpleDate' : '[Unknown]');
let SimpleDate$1 = SimpleDate_;
(function (_SimpleDate) {})(SimpleDate$1 || (SimpleDate$1 = {}));
Object.defineProperties(SimpleDate$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "since_greg": {
        value: 1
      },
      "sinceGreg": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "since_greg"
      }
    }))
  }
});
Object.defineProperties(SimpleDate$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SimpleDate$1);
  Object.freeze(SimpleDate$1.prototype);
}

// Prevent showing the same warning over and over.
const seenWarnings = new Set();
const MAX_SEEN_WARNINGS = 4196;
function warn(message) {
  if (DEV_MODE && typeof console === 'object' && console != null && typeof console.warn === 'function') {
    if (!seenWarnings.has(message)) {
      if (seenWarnings.size < MAX_SEEN_WARNINGS) {
        seenWarnings.add(message);
      }
      console.warn(message);
    }
  }
}
function deprecated(oldItem, newItem, result) {
  warn(`DEPRECATED: ${oldItem}; use ${newItem} instead.`);
  return result;
}
function deprecatedMember(type, oldMember, newMember, result) {
  return deprecated(`<${type}>.${oldMember}`, `<${type}>.${newMember}`, result);
}

const _MONTH_DAYS = new Map([[1, 31], [2, 28], [3, 31], [4, 30], [5, 31], [6, 30], [7, 31], [8, 31], [9, 30], [10, 31], [11, 30], [12, 31]]);
const monthAndDayOfMonth = Symbol('monthAndDayOfMonth');
const formatNumeric = Symbol('formatNumeric');
let today = null;
class SimpleDate extends SimpleDate_ {
  static get today() {
    const t = SimpleDate.fromUnixEpoch(Date.now());
    // As a convenience & optimization, use a cached version of today if it has
    // already been calculated.
    if (today === null || !today.equals(t)) {
      today = t;
    }
    return today;
  }

  /**
   * Creates a SimpleDate from a year, month, and day of the month. January is
   * month 1.
   */

  static from(yearOrData, month = -1, dayOfMonth = -1) {
    // Support standard single-parameter uses of `from` from Message.
    if (month === -1) {
      return super.from(yearOrData);
    }
    const year = assert.number(yearOrData);
    assert(year > 1582 || year === 1582 && month > 10 || year === 1582 && month === 10 && dayOfMonth > 14, emsg`
        SimpleDate cannot refer to dates before Oct 15, 1582
        (${year}, ${month}, ${dayOfMonth}).`);
    assert(year <= 9999, emsg`SimpleDate does not support years after 9999: ${year}.`);
    assert(month >= 1 && month <= 12, emsg`Invalid month: ${month}`);
    assert(dayOfMonth >= 1 && dayOfMonth <= monthDays(year, month), emsg`Invalid day (${dayOfMonth}) for month (${month}) of year (${year})`);
    if (year === 1582 && month === 10) {
      assert(dayOfMonth > 14);
      return new SimpleDate().setSinceGreg(dayOfMonth - 14);
    }
    const startMonth = year === 1582 ? 11 : 1;
    let days = year === 1582 ? 17 : yearToDaysSinceGreg(year);
    for (let i = startMonth; i < month; i++) {
      days += monthDays(year, i);
    }
    days += dayOfMonth;
    return new SimpleDate().setSinceGreg(days);
  }
  static fromString(s) {
    if (!s) {
      return SimpleDate.getDefault();
    }
    return SimpleDate.fromIso(s);
  }
  static fromIso(s) {
    const m = assert(/^(\d{4})-(\d{2})-(\d{2})$/.exec(s), emsg`
        Invalid SimpleDate string ${s}. SimpleDate only supports
        the YYYY-MM-DD format.`);
    const year = +m[1];
    const month = +m[2];
    const dayOfMonth = +m[3];
    return SimpleDate.from(year, month, dayOfMonth);
  }

  /**
   * Converts a Unix epoch timestamp (in milliseconds) to a SimpleDate. Note
   * that this could result in a different date if run simultaneously in
   * different time zones. This tells you what the current machine evaluates
   * for the date relative to the Unix epoch.
   */
  static fromUnixEpoch(epoch) {
    // We use the empty value if epoch is 0 for backwards compatibility with
    // DEPRECATED_Date. Unfortunately, this means the date January 1, 1970 will
    // not be settable using `fromUnixEpoch`. In the future when DEPRECATED_Date
    // has been removed, we can probably remove this case and support the date
    // January 1, 1970.
    return epoch === 0 ? SimpleDate.getDefault() : SimpleDate.fromJsDate(new Date(epoch));
  }
  static fromJsDate(date) {
    return SimpleDate.from(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }
  static isValid(year, month, dayOfMonth) {
    return (year > 1582 || year === 1582 && month > 10 || year === 1582 && month === 10 && dayOfMonth > 14) && year <= 9999 && month >= 1 && month <= 12 && dayOfMonth >= 1 && dayOfMonth <= monthDays(year, month);
  }
  setSinceGreg(value) {
    assert(value >= 0, emsg`Invalid SimpleDate value: ${value}.`);
    assert.same(value >>> 0, value, emsg`Invalid SimpleDate value (${value}). Value must be an integer.`);
    assert(value <= 3074324, emsg`
        Invalid SimpleDate value (${value}). SimpleDate only supports dates up
        to Dec 31, 9999 (sinceGreg value of 3074324).`);
    return super.setSinceGreg(value);
  }
  isEmpty() {
    return this.sinceGreg === 0;
  }
  equals(value) {
    return !!value && value instanceof SimpleDate && this.sinceGreg === value.sinceGreg;
  }
  get year() {
    if (this.sinceGreg === 0) {
      return 0;
    }
    const days = this.sinceGreg;
    let lowerBound = 1583;
    // We support finding years above 3000. This is just a decent initial guess
    // for common bounds.
    let upperBound = 3000;
    // Find the actual upper bound in case it is above 3000.
    while (yearToDaysSinceGreg(upperBound + 1) < days) {
      upperBound += upperBound - lowerBound;
    }
    // Binary search between the bounds.
    while (lowerBound < upperBound) {
      const guess = Math.floor((upperBound + lowerBound) / 2);
      const daysToYear = yearToDaysSinceGreg(guess);
      if (daysToYear >= days) {
        upperBound = guess - 1;
        continue;
      }
      if (days - daysToYear <= daysInYear(guess)) {
        return guess;
      }
      lowerBound = guess + 1;
    }
    assert.same(lowerBound, upperBound);
    assert(days - yearToDaysSinceGreg(lowerBound) <= daysInYear(lowerBound));
    return lowerBound;
  }
  getYear() {
    return deprecatedMember('SimpleDate', 'getYear', 'year', this.year);
  }
  get [monthAndDayOfMonth]() {
    let days = this.sinceGreg;
    const year = this.year;
    const daysToYear = yearToDaysSinceGreg(year);
    assert(days > daysToYear);
    days -= daysToYear;
    for (let month = 1; month <= 12; month++) {
      const daysInMonth = monthDays(year, month);
      if (days <= daysInMonth) {
        return [month, days];
      }
      days -= daysInMonth;
    }
    assert.fail(emsg`Could not find month for SimpleDate: ${this.sinceGreg}`);
    return [1, 1];
  }
  getMonth() {
    return deprecatedMember('SimpleDate', 'getMonth', 'month', this.month);
  }
  get month() {
    return this[monthAndDayOfMonth][0];
  }
  getDayOfMonth() {
    return deprecatedMember('SimpleDate', 'getDayOfMonth', 'dayOfMonth', this.dayOfMonth);
  }
  get dayOfMonth() {
    return this[monthAndDayOfMonth][1];
  }
  formatMonth() {
    switch (this.month) {
      case 1:
        return 'January';
      case 2:
        return 'February';
      case 3:
        return 'March';
      case 4:
        return 'April';
      case 5:
        return 'May';
      case 6:
        return 'June';
      case 7:
        return 'July';
      case 8:
        return 'August';
      case 9:
        return 'September';
      case 10:
        return 'October';
      case 11:
        return 'November';
      case 12:
        return 'December';
    }
    assert.fail(emsg`Invalid month: ${this.month}`);
    return '';
  }
  formatMonthAbbr() {
    return this.formatMonth().slice(0, 3);
  }
  formatRelative() {
    const today = SimpleDate.today;
    const diff = today.sinceGreg - this.sinceGreg;
    if (diff < 0) {
      assert.fail(emsg`Cannot format relative date in the future: ${this.sinceGreg}`);
      return '-';
    }
    if (diff === 0) {
      return 'today';
    }
    if (diff < 2) {
      return 'a day ago';
    }
    if (diff < 27) {
      return `${diff} days ago`;
    }
    const months = Math.round(diff / 30);
    if (months < 2) {
      return 'a month ago';
    }
    if (months < 12) {
      return `${months} months ago`;
    }
    if (months < 19) {
      return 'a year ago';
    }
    return `${Math.round(diff / 365.2425)} years ago`;
  }
  [formatNumeric](delim, a, b, c) {
    return [pad2$1(a), pad2$1(b), pad2$1(c)].join(delim);
  }
  formatIso() {
    return this.isEmpty() ? '' : this[formatNumeric]('-', this.year, this.month, this.dayOfMonth);
  }
  formatAmerican(delim = '/') {
    return this.isEmpty() ? '' : this[formatNumeric](delim, this.month, this.dayOfMonth, this.year);
  }
  formatHumanAbbr() {
    if (this.isEmpty()) {
      return '';
    }
    const out = [this.formatMonthAbbr(), ' ', this.dayOfMonth];
    if (this.year !== SimpleDate.today.year) {
      out.push(', ', this.year);
    }
    return out.join('');
  }
  formatHumanAbbreviated() {
    return deprecatedMember('SimpleDate', 'formatHumanAbbreviated', 'formatHumanAbbr', this.formatHumanAbbr());
  }
  get daysSince() {
    return SimpleDate.today.sinceGreg - this.sinceGreg;
  }
  compare(value) {
    return this.sinceGreg - value.sinceGreg;
  }
  lessThan(value) {
    return this.compare(value) < 0;
  }
  greaterThan(value) {
    return this.compare(value) > 0;
  }
}
function isLeapYear(y) {
  return y % 400 === 0 || y % 4 === 0 && y % 100 !== 0;
}

// Returns the number of days since Oct 14, 1582 to (but not including) Jan 1 of
// the specified year.
//
// For example if y = 1583, the result would be 78 (17 days for Oct, 30 days for
// Nov, and 31 days for Dec). This gets you right up to the end of the previous
// year such that you can then add the number of days into the desired year.
function yearToDaysSinceGreg(y) {
  assert(y > 1582, emsg`
      SimpleDate cannot refer to dates before Oct 15, 1582. Year (${y}) cannot
      be converted to a number of days since Greg.`);
  let days = 78;
  if (y > 1583) {
    const L = leapYearsSinceGreg(y);
    days += L * 366 + (y - 1583 - L) * 365;
  }
  return days;
}
function daysInYear(y) {
  assert(y > 1582, emsg`
      SimpleDate cannot refer to dates before Oct 15, 1582. Year (${y}) cannot
      be converted to a number of days.`);
  return y === 1582 ? 78 : isLeapYear(y) ? 366 : 365;
}

// Calculate the number of leap years between 1582 and another year. 1584 is the
// first leap year in the Gregorian Calendar. The year `y` is not included in
// the count, so for `y` = 1584 the result is still 0, but for 1585 the result
// is 1.
function leapYearsSinceGreg(y) {
  let start = 1584;
  let n = 0;
  // To save some iterations for modern dates, we just skip to 2020 if the year
  // is after that.
  if (y >= 2020) {
    start = 2020;
    n = 106;
  }
  for (let i = start; i < y; i++) {
    if (isLeapYear(i)) {
      n++;
    }
  }
  return n;
}
function monthDays(year, month) {
  return month === 2 && isLeapYear(year) ? 29 : _MONTH_DAYS.get(month);
}
function pad2$1(n) {
  const s = String(n);
  if (s.length < 2) {
    return '0'.repeat(2 - s.length) + s;
  }
  return s;
}

registerCanonical(SimpleDate_, SimpleDate);

const MONTHS_ABBREVIATED = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const update = Symbol('update');
class DateProto extends DEPRECATED_Date_ {
  static getCurrent() {
    return new DateProto().setFromEpoch(Date.now());
  }
  static from(yearOrData, month = -1, dayOfMonth = -1) {
    // Support standard single-parameter uses of `from` from Message.
    if (month === -1) {
      return super.from(yearOrData);
    }
    const year = assert.number(yearOrData);
    return DateProto.fromJsDate(new Date(year, month - 1, dayOfMonth));
  }

  // Support ISO date format for compatibility for `SimpleDate`.
  static fromString(s) {
    const m = assert(/^(\d{4})-(\d{2})-(\d{2})$/.exec(s), emsg`
        Invalid date string ${s}. DEPRECATED_Date only supports
        the YYYY-MM-DD format.`);
    const year = +m[1];
    const month = +m[2];
    const dayOfMonth = +m[3];
    return DEPRECATED_Date.from(year, month, dayOfMonth);
  }
  static fromJsDate(date) {
    return new DateProto().setFromEpoch(+date);
  }
  equals(value) {
    return !!value && value instanceof DateProto && this.fromEpoch === value.fromEpoch;
  }
  isEmpty() {
    return this.fromEpoch === 0;
  }
  toJsDate() {
    return new Date(this.fromEpoch);
  }
  toSimpleDate() {
    return SimpleDate.fromUnixEpoch(this.fromEpoch);
  }
  formatRelative(
  // Number in milliseconds when exact dates should begin being used.
  maxRelative = Infinity) {
    const diff = Date.now() - this.fromEpoch;
    if (diff > maxRelative) {
      return this.formatHumanAbbreviated();
    }
    if (Math.abs(diff) < 10000) {
      return 'just now';
    }
    if (diff < 0) {
      return 'not yet';
    }
    if (diff < 45000) {
      return 'a few seconds ago';
    }
    if (diff < 105000) {
      return 'a minute ago';
    }
    if (diff < 50 * 60 * 1000) {
      return `${Math.round(diff / 60 / 1000)} minutes ago`;
    }
    if (diff < 1.5 * 60 * 60 * 1000) {
      return 'an hour ago';
    }
    if (diff < 20 * 60 * 60 * 1000) {
      return `${Math.round(diff / 60 / 60 / 1000)} hours ago`;
    }
    if (diff < 1.5 * 24 * 60 * 60 * 1000) {
      return 'a day ago';
    }
    if (diff < 27 * 24 * 60 * 60 * 1000) {
      return `${Math.round(diff / 24 / 60 / 60 / 1000)} days ago`;
    }
    if (diff < 1.6 * 30 * 24 * 60 * 60 * 1000) {
      return 'a month ago';
    }
    if (diff < 11.3 * 30 * 24 * 60 * 60 * 1000) {
      const months = Math.round(diff / 30 / 24 / 60 / 60 / 1000);
      if (months < 12) {
        return `${months} months ago`;
      }
    }
    if (diff < 1.6 * 365.2422 * 24 * 60 * 60 * 1000) {
      return 'a year ago';
    }
    return `${Math.round(diff / 365.2422 / 24 / 60 / 60 / 1000)} years ago`;
  }
  formatIso() {
    return this.fromEpoch === 0 ? '' : [this.getYear(), pad2(this.getMonth()), pad2(this.getDayOfMonth())].join('-');
  }
  formatAmerican(delim = '/') {
    return this.fromEpoch === 0 ? '' : [pad2(this.getMonth()), pad2(this.getDayOfMonth()), this.getYear()].join(delim);
  }
  formatHumanAbbreviated() {
    return this.formatHumanAbbr();
  }
  formatHumanAbbr() {
    if (this.fromEpoch === 0) {
      return '';
    }
    const d = this.toJsDate();
    let s = MONTHS_ABBREVIATED[d.getMonth()] + ' ' + d.getDate();
    if (this.getYear() !== new Date().getFullYear()) {
      s += ', ' + d.getFullYear();
    }
    return s;
  }
  getYear() {
    return this.toJsDate().getFullYear();
  }
  getMonth() {
    return this.toJsDate().getMonth() + 1;
  }
  getDayOfMonth() {
    return this.toJsDate().getDate();
  }
  setYear(year) {
    return this[update](v => v.setFullYear(year));
  }
  setMonth(month) {
    return this[update](v => v.setMonth(month - 1));
  }
  setDayOfMonth(dayOfMonth) {
    return this[update](v => v.setDate(dayOfMonth));
  }
  setHour(hour) {
    return this[update](v => v.setHours(hour));
  }
  setMinute(minute) {
    return this[update](v => v.setMinutes(minute));
  }
  setSecond(second) {
    return this[update](v => v.setSeconds(second));
  }
  setMillisecond(millisecond) {
    return this[update](v => v.setMilliseconds(millisecond));
  }
  incrementDate(n = 1) {
    return this[update](v => v.setDate(v.getDate() + n));
  }
  daysSince() {
    return (Date.now() - this.fromEpoch) / (24 * 60 * 60 * 1000);
  }
  compare(value) {
    this.fromEpoch - value.fromEpoch;
  }
  [update](callback) {
    const d = new Date(this.fromEpoch);
    callback(d);
    return new DateProto().setFromEpoch(+d);
  }
}
const DEPRECATED_Date = DateProto;
function pad2(n) {
  const s = String(n);
  if (s.length < 2) {
    return '0'.repeat(2 - s.length) + s;
  }
  return s;
}

registerCanonical(DEPRECATED_Date_, DEPRECATED_Date);

function _defineProperty$2h(obj, key, value) { key = _toPropertyKey$2h(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2h(arg) { var key = _toPrimitive$2h(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2h(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Upload_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      4: 2,
      1: 2,
      2: 2,
      3: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Upload field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Upload field key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Upload field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Upload field creation_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Upload, 'Upload', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Upload to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(4, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(4, null, null);
  }
  get key() {
    return this.getFieldById(1, "");
  }
  setKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteKey() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get creationTime() {
    return this.getFieldById(3, DEPRECATED_Date.getDefault());
  }
  setCreationTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteCreationTime() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$2h(Upload_, protoName, DEV_MODE ? 'Upload' : '[Unknown]');
let Upload = Upload_;
(function (_Upload) {})(Upload || (Upload = {}));
Object.defineProperties(Upload, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 4
      },
      "key": {
        value: 1
      },
      "name": {
        value: 2
      },
      "creation_time": {
        value: 3
      },
      "creationTime": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '4': {
        value: "sym"
      },
      '1': {
        value: "key"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "creation_time"
      }
    }))
  }
});
Object.defineProperties(Upload.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Upload);
  Object.freeze(Upload.prototype);
}

function _defineProperty$2g(obj, key, value) { key = _toPropertyKey$2g(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2g(arg) { var key = _toPrimitive$2g(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2g(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Attachment_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Upload.shape,
      2: 2,
      3: DEPRECATED_Date.shape,
      4: 0,
      5: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Upload);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Attachment field upload.
                Expected type Upload.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Upload', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Attachment field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Attachment field added_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Attachment field primary_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AttachmentType.fromStrictJson_(v, getFieldMap);
          }
          return AttachmentType.from(value);
        };
      case 5:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Attachment field sub_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return AttachmentType.fromStrictJson_(v, getFieldMap);
            }
            return AttachmentType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Attachment$1, 'Attachment', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Attachment to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get upload() {
    return this.getFieldById(1, Upload.getDefault());
  }
  setUpload(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUpload() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get addedTime() {
    return this.getFieldById(3, DEPRECATED_Date.getDefault());
  }
  setAddedTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteAddedTime() {
    return this.setValueFieldById(3, null, null);
  }
  get primaryType() {
    return this.getFieldById(4, 0);
  }
  setPrimaryType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deletePrimaryType() {
    return this.setValueFieldById(4, null, null);
  }
  get subTypeList() {
    return this.getRepeatedFieldById(5);
  }
  hasSubType(value) {
    return this.repeatedFieldByIdHas(5, value);
  }
  addSubType(value) {
    return this.addToRepeatedFieldById(5, value);
  }
  addEachSubType(values) {
    let T = this;
    for (const value of values) {
      T = T.addSubType(value);
    }
    return T;
  }
  setSubType(index, value) {
    return this.setRepeatedFieldById(5, index, value);
  }
  removeSubType(index) {
    return this.removeFromRepeatedFieldById(5, index);
  }
  clearSubTypeList() {
    return this.clearRepeatedFieldById(5);
  }
  setSubTypeList(values) {
    return this.overwriteRepeatedFieldById(5, values);
  }
}
_defineProperty$2g(Attachment_, protoName, DEV_MODE ? 'Attachment' : '[Unknown]');
let Attachment$1 = Attachment_;
(function (_Attachment) {})(Attachment$1 || (Attachment$1 = {}));
Object.defineProperties(Attachment$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "upload": {
        value: 1
      },
      "name": {
        value: 2
      },
      "added_time": {
        value: 3
      },
      "addedTime": {
        value: 3
      },
      "primary_type": {
        value: 4
      },
      "primaryType": {
        value: 4
      },
      "sub_type": {
        value: 5
      },
      "subType": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "upload"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "added_time"
      },
      '4': {
        value: "primary_type"
      },
      '5': {
        value: "sub_type"
      }
    }))
  }
});
Object.defineProperties(Attachment$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Upload.prototype,
      3: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Attachment$1);
  Object.freeze(Attachment$1.prototype);
}

function _defineProperty$2f(obj, key, value) { key = _toPropertyKey$2f(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2f(arg) { var key = _toPrimitive$2f(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2f(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Configuration {
  // If restrictKeys is not specified, any keys are allowed.
  constructor(restrictKeys) {
    _defineProperty$2f(this, "data", void 0);
    _defineProperty$2f(this, "restrictKeys", void 0);
    if (restrictKeys) {
      this.restrictKeys = new Set(restrictKeys);
    }
    this.data = Object.create(null);
  }
  get(key) {
    assert(key in this.data, emsg`Unconfigured: ${key}`);
    return this.data[key];
  }
  set(key, value) {
    assert(!(key in this.data), emsg`Cannot overwrite configuration: ${key}`);
    if (this.restrictKeys) {
      assert(this.restrictKeys.has(key), emsg`Unknown configuration: ${key}`);
    }
    this.data[key] = value;
  }
}

class EnvConfiguration extends Configuration {
  constructor(vars) {
    super(Object.keys(vars));
    if (typeof process === 'object' && process && typeof process.env === 'object' && process.env) {
      for (const [configKey, envVar] of Object.entries(vars)) {
        const key = configKey;
        if (typeof process !== 'undefined' && envVar && process.env[envVar]) {
          this.set(key, process.env[envVar]);
        }
      }
    }
  }
}

const config$2 = new EnvConfiguration({
  'app-root': 'APP_ROOT',
  'user-content-root': 'BLOOMLAKE_USER_CONTENT_ROOT'
});
function setAppRoot(value) {
  config$2.set('app-root', value);
}
function getAppRoot() {
  return config$2.get('app-root');
}
function setUserContentRoot(value) {
  config$2.set('user-content-root', value);
}

// TODO(andrew.bowers): Other 'USER_CONTENT_ROOT' usages should probably use
// this method to fetch that value going forward
function getUserContentRoot() {
  return config$2.get('user-content-root');
}

function resolveUserContent(userContentRoot, key) {
  // For now, all keys must begin with "0:". In the future we may use different
  // numbers in order to expand the scheme.
  assert.same(key.slice(0, 2), '0:', emsg`Invalid user content key: ${key}`);
  return `${userContentRoot}/${key.slice(2).replace(/ /g, '%20')}`;
}

// We allow for cases where `userContentRoot` is empty because it is unknown and
// simply return `false`.
function isUserContent(userContentRoot, url) {
  return !!userContentRoot && url.indexOf(`${userContentRoot}/`) === 0;
}
function getUserContentKey(userContentRoot, url) {
  assert(userContentRoot, 'User content root was not provided.');
  assert(url.indexOf(`${userContentRoot}/`) === 0);
  return `0:${url.slice(userContentRoot.length + 1)}`;
}

function resourceKeyToUrl(key) {
  if (key === '') {
    return '';
  }
  return resolveUserContent(getUserContentRoot(), key);
}
function resourceSymToUrl(sym) {
  assert(sym);
  return `${getAppRoot()}/resource:${sym}/download`;
}

// Using `super.name` didn't work after deployment because the `this` was lost
// in the way it compiled. So we need to be careful to keep the correct `this`,
// at least as long as we're compiling down to ES5 with the same tools.
const getName = assert(Object.getOwnPropertyDescriptor(Attachment_.prototype, 'name')).get;
class Attachment extends Attachment_ {
  get typeList() {
    return this.subTypeList.unshift(this.primaryType);
  }

  // DEPRECATED. This URL is based on the key and user content root rather than
  // the sym and app root. It is going to be
  get url() {
    return resourceSymToUrl(this.upload.sym);
  }

  // Note(nathan): This is a hack to allow Buble to start using a `sym` property
  // on the attachment to refer to it. This will allow an easy transition when
  // we update the back end. It is returning the key, which is a string, but not
  // proper sym format.
  get sym() {
    return this.key;
  }
  formatTypes() {
    return deprecatedMember('Attachment', 'formatType', '(formatPrimaryType and formatSubTypes)', this.typeList.map(type => AttachmentType.format(type)));
  }
  formatPrimaryType() {
    return AttachmentType.format(this.primaryType);
  }
  formatSubTypes() {
    return this.subTypeList.map(type => AttachmentType.format(type));
  }

  // Deprecated fields moved to Upload
  get key() {
    return this.upload.key;
  }
  get name() {
    return getName.call(this) || this.upload.name;
  }
  get creationTime() {
    return this.upload.creationTime;
  }
}

registerCanonical(Attachment_, Attachment);

function getAttachmentTypeLabel(type) {
  return AttachmentType.format(type);
}
const ATTACHMENT_TYPE_ORDER = [AttachmentType.SPONSOR_FINANCIALS, AttachmentType.FINANCIAL_STATEMENTS, AttachmentType.PRO_FORMA, AttachmentType.RENT_ROLL, AttachmentType.SCHEDULE_OF_REAL_ESTATE_OWNED, AttachmentType.TENANT_LEASE, AttachmentType.TENANT_INFORMATION, AttachmentType.OFFERING_MEMORANDUM, AttachmentType.MARKET_INFORMATION, AttachmentType.SALES_CONTRACT, AttachmentType.SPONSOR_BIO, AttachmentType.LEASE_COMPS, AttachmentType.DESIGN_AND_ENGINEERING, AttachmentType.CONSTRUCTION_BUDGET, AttachmentType.TAX_INFORMATION, AttachmentType.SALES_COMPS, AttachmentType.SOURCES_AND_USES, AttachmentType.PAST_APPRAISAL, AttachmentType.ZONING_AND_ENTITLEMENTS, AttachmentType.ENVIRONMENTAL_REPORT, AttachmentType.HOTEL_FRANCHISE_AGREEMENT];

// tslint:disable-next-line:no-unused-variable
var CurrencyUnit__;
(function (CurrencyUnit__) {
  CurrencyUnit__[CurrencyUnit__["USD"] = 0] = "USD";
})(CurrencyUnit__ || (CurrencyUnit__ = {}));
const CurrencyUnit_ = defineEnumProperties(CurrencyUnit__, [0]);
const CurrencyUnit = extendEnum('CurrencyUnit', [["USD", 0]])(CurrencyUnit_);

function _defineProperty$2e(obj, key, value) { key = _toPropertyKey$2e(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2e(arg) { var key = _toPrimitive$2e(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2e(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CurrencyValue_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message CurrencyValue field unit.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return CurrencyUnit.fromStrictJson_(v, getFieldMap);
          }
          return CurrencyUnit.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CurrencyValue field value.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CurrencyValue$1, 'CurrencyValue', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CurrencyValue to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get unit() {
    return this.getFieldById(1, 0);
  }
  setUnit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUnit() {
    return this.setValueFieldById(1, null, null);
  }
  get value() {
    return this.getFieldById(2, "");
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$2e(CurrencyValue_, protoName, DEV_MODE ? 'CurrencyValue' : '[Unknown]');
let CurrencyValue$1 = CurrencyValue_;
(function (_CurrencyValue) {})(CurrencyValue$1 || (CurrencyValue$1 = {}));
Object.defineProperties(CurrencyValue$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "unit": {
        value: 1
      },
      "value": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "unit"
      },
      '2': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(CurrencyValue$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CurrencyValue$1);
  Object.freeze(CurrencyValue$1.prototype);
}
class RepeatedCurrencyValue_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CurrencyValue$1.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CurrencyValue$1);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedCurrencyValue field value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedCurrencyValue, 'RepeatedCurrencyValue', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedCurrencyValue to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$2e(RepeatedCurrencyValue_, protoName, DEV_MODE ? 'RepeatedCurrencyValue' : '[Unknown]');
let RepeatedCurrencyValue = RepeatedCurrencyValue_;
(function (_RepeatedCurrencyValue) {})(RepeatedCurrencyValue || (RepeatedCurrencyValue = {}));
Object.defineProperties(RepeatedCurrencyValue, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedCurrencyValue.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CurrencyValue$1.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedCurrencyValue);
  Object.freeze(RepeatedCurrencyValue.prototype);
}

function _defineProperty$2d(obj, key, value) { key = _toPropertyKey$2d(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2d(arg) { var key = _toPrimitive$2d(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2d(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
const tag$1 = Symbol('CalculationError');
class CalculationError extends Error {
  static isOfType(value) {
    return !!value && tag$1 in value;
  }
  constructor(message = 'Calculation error') {
    super(message);
    _defineProperty$2d(this, tag$1, void 0);
    this[tag$1] = true;
  }
}

function _defineProperty$2c(obj, key, value) { key = _toPropertyKey$2c(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2c(arg) { var key = _toPrimitive$2c(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2c(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
const tag = Symbol('CalculationOutOfBoundsError');
class CalculationOutOfBoundsError extends CalculationError {
  static isOfType(value) {
    return !!value && tag in value;
  }
  constructor(result) {
    const msg = 'Calculation produced value out of range';
    super(msg + (result === undefined ? '' : ` (${result})`));
    _defineProperty$2c(this, tag, void 0);
    this[tag] = true;
  }
}

function formatCommaSeparated(num) {
  return string(function* () {
    let $3539, $3540;
    const s = String(num);
    if (s == '') {
      return '';
    }
    runtime.ensure(!/[^\-\d\.]/.test(s), function () {
      return `Unsupported number format "${s}".`;
    });
    const isNegative = runtime.getComputed(s, 0) == '-';
    const positive = isNegative ? s.slice(1) : s;
    const [characteristic, mantissa] = positive.split('.');
    const offset = ($3539 = characteristic.length, $3540 = typeof $3539, (runtime.checkAsserts() ? runtime.ensure(typeof $3539 == 'number', function () {
      return `Expected type "number" but found "${$3540}" for (${$3539}).`;
    }) : undefined, $3539)) % 3 || 3;
    if (isNegative) {
      yield '-';
    }
    yield characteristic.slice(0, offset);
    for (const i of runtime.by(runtime.range(offset, characteristic.length, 1, false), 3)) {
      let $3537, $3538;
      yield ',';
      yield characteristic.slice(i, ($3537 = i, $3538 = typeof $3537, (runtime.checkAsserts() ? runtime.ensure(typeof $3537 == 'number', function () {
        return `Expected type "number" but found "${$3538}" for (${$3537}).`;
      }) : undefined, $3537)) + 3);
    }
    if (mantissa) {
      yield '.';
      yield mantissa;
    }
  }());
}
function formatCommaSeparatedZeroEmpty(n) {
  return String(n) == '0' ? '' : formatCommaSeparated(n);
}

// Notably, this returns true for -0.
function isNegative(n) {
  return (n || 1 / n) < 0;
}

function _defineProperty$2b(obj, key, value) { key = _toPropertyKey$2b(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2b(arg) { var key = _toPrimitive$2b(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2b(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class SimpleDecimalTools {
  constructor({
    shiftPlaces,
    formatMinDecimals,
    lenientMinDecimals
  }) {
    _defineProperty$2b(this, "shiftPlaces", void 0);
    _defineProperty$2b(this, "formatMinDecimals", void 0);
    _defineProperty$2b(this, "lenientMinDecimals", void 0);
    _defineProperty$2b(this, "shiftValue", void 0);
    _defineProperty$2b(this, "validator", void 0);
    assert(Number.isSafeInteger(shiftPlaces) && shiftPlaces >= 0 && shiftPlaces <= 15, emsg`Out of range: ${shiftPlaces}`);
    assert(Number.isSafeInteger(formatMinDecimals) && formatMinDecimals >= 0 && formatMinDecimals <= 15, emsg`Out of range: ${formatMinDecimals}`);
    this.shiftPlaces = shiftPlaces;
    this.formatMinDecimals = formatMinDecimals;
    this.lenientMinDecimals = !!lenientMinDecimals;
    this.shiftValue = 10 ** shiftPlaces;
    if (CHECK_ASSERTS) {
      this.validator = this.createValidator();
    }
  }
  normalize(value, shiftExtra = 0, maxRightPlaces = Infinity) {
    return this.serialize(this.toInteger(value, maxRightPlaces - shiftExtra), shiftExtra);
  }
  fromNumber(value) {
    return this.forData(value.toFixed(this.shiftPlaces));
  }
  toNumber(value) {
    return +this.normalize(value);
  }

  // Note that '0' is normalized to '' and '0.1' is normalized to '.1'. This
  // makes comparison easier between "empty" and "zero" protos, though it
  // shouldn't be relied upon.
  forData(value) {
    const result = this.normalize(value);
    if (/^0/.test(result)) {
      return result.slice(1);
    }
    return result;
  }
  toInteger(value, maxRightPlaces) {
    // The value should not be NaN.
    return assert.safeInteger(this.toIntegerInner(value, maxRightPlaces));
  }
  toIntegerInner(value, maxRightPlaces) {
    // It's important that the argument actually be a string, so we'll double
    // enforce it at runtime (especially for non-TypeScript users of this
    // function).
    assert.string(value);
    if (this.validator) {
      assert(this.validator.test(value), emsg`
          Invalid decimal number format ${value} for validator
          ${this.validator}`);
    }
    const parts = value.split('.');
    if (parts.length > 1) {
      assert.same(parts.length, 2);
      assert(parts[1].length <= this.shiftPlaces);
      const [c, mUnrounded] = parts;
      const m = mUnrounded.slice(0, maxRightPlaces);
      const roundAdjust = +mUnrounded[maxRightPlaces] >= 5 ? 1 : 0;
      const mantissaShift = 10 ** (this.shiftPlaces - m.length);
      const characteristic = +c * this.shiftValue;
      const mantissa = (+m + roundAdjust) * mantissaShift;
      if (isNegative(characteristic)) {
        return characteristic - mantissa;
      }
      assert(characteristic >= 0);
      return characteristic + mantissa;
    }
    assert.same(parts.length, 1);
    return +parts[0] * this.shiftValue;
  }
  serialize(valueAsInteger, shiftExtra) {
    const shift = this.shiftPlaces + shiftExtra;
    const shiftValue = 10 ** shift;
    let s = (valueAsInteger / shiftValue).toFixed(shift);
    if (/\./.test(s)) {
      s = s.replace(/0+$/, '').replace(/\.$/, '');
    }
    const m = /\.(\d+)$/.exec(s);
    const places = m ? m[1].length : 0;
    const formatMinDecimals = places === 0 && this.lenientMinDecimals ? 0 : this.formatMinDecimals;
    if (places < formatMinDecimals) {
      const trailing0s = '0'.repeat(formatMinDecimals - places);
      const end = places === 0 ? '.' + trailing0s : trailing0s;
      return s + end;
    }
    return s;
  }
  createValidator() {
    const neg = '\\-?';
    const characteristic = `\\d{0,${15 - this.shiftPlaces}}`;
    const mantissa = `\\d{1,${this.shiftPlaces}}`;
    // When the characteristic is present, the mantissa is not required.
    const withCharacteristic = `^${neg}${characteristic}(\\.${mantissa})?$`;
    // When the characteristic is present, the mantissa is required.
    const withoutCharacteristic = `^${neg}\\.${mantissa}$`;
    return new RegExp(`${withCharacteristic}|${withoutCharacteristic}`);
  }
}

function _defineProperty$2a(obj, key, value) { key = _toPropertyKey$2a(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2a(arg) { var key = _toPrimitive$2a(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2a(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Percent_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Percent field value.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Percent$1, 'Percent', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Percent to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get value() {
    return this.getFieldById(1, "");
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$2a(Percent_, protoName, DEV_MODE ? 'Percent' : '[Unknown]');
let Percent$1 = Percent_;
(function (_Percent) {})(Percent$1 || (Percent$1 = {}));
Object.defineProperties(Percent$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(Percent$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Percent$1);
  Object.freeze(Percent$1.prototype);
}

// This `Percent` class currently enforces a maximum of 7 digits to the left
const MAX$1 = 10 ** 7 - 1;
const MIN$1 = -MAX$1;
const tools$1 = new SimpleDecimalTools({
  shiftPlaces: 8,
  formatMinDecimals: 0
});
class Percent extends Percent_ {
  // Supports the format "5%" or "1.2x"
  static fromString(s) {
    const m = assert(/^((\d*)(?:\.(\d+))?)(%|x)$/.exec(s), emsg`
        Cannot convert to percent from string ${s}. Invalid string format.
        Expected format "1.23%" or "1.2x".`);
    assert(!m[2] || m[2].length <= 9, emsg`
        Cannot convert to percent from string ${s}. Percents do not support more
        than 9 digits to the left of the decimal place.`);
    assert(!m[3] || m[3].length <= 6, emsg`
        Cannot convert to percent from string ${JSON.stringify(s)}.
        Percents do not support more than 6 digits to the right of the
        decimal place.`);
    const n = +m[1];
    return Percent.fromNumber(m[4] === '%' ? n / 100 : n);
  }
  static fromNumber(value, errorResult) {
    if (!Percent.inBounds(value)) {
      if (errorResult !== undefined) {
        return errorResult;
      }
      throw new CalculationOutOfBoundsError(value);
    }
    return new Percent().setValue(tools$1.fromNumber(value));
  }
  static max(...values) {
    assert(values.length > 0, 'Expected at least one percent.');
    let max = values[0];
    for (const value of values.slice(1)) {
      if (value.greaterThan(max)) {
        max = value;
      }
    }
    return max;
  }
  static min(...values) {
    assert(values.length > 0, 'Expected at least one percent.');
    let min = values[0];
    for (const value of values.slice(1)) {
      if (value.lessThan(min)) {
        min = value;
      }
    }
    return min;
  }

  // Note this still returns zero percent if all values are zero.
  static minExceptZero(...values) {
    return Percent.min(...function* () {
      let found = false;
      for (const value of values) {
        if (!value.isZero()) {
          yield value;
          found = true;
        }
      }
      if (!found && values.length > 0) {
        yield values[0];
      }
    }());
  }
  static inBounds(n) {
    return n >= MIN$1 && n <= MAX$1;
  }
  format(includeSymbol = true) {
    return this.formatMaxDecimalPlaces(Infinity, includeSymbol);
  }
  formatMaxDecimalPlaces(places, includeSymbol = true) {
    return formatCommaSeparated(tools$1.normalize(this.value, -2, places)) + (includeSymbol ? '%' : '');
  }
  formatAsNumber(maxRightPlaces = Infinity) {
    return formatCommaSeparated(tools$1.normalize(this.value, 0, maxRightPlaces));
  }
  formatAsMultiple(maxRightPlaces = Infinity) {
    return this.formatAsNumber(maxRightPlaces) + 'x';
  }
  toNumber() {
    return tools$1.toNumber(this.value);
  }
  isZero() {
    return tools$1.toNumber(this.value) === 0;
  }
  divide(value) {
    return this.divideByNumber(value.toNumber());
  }
  divideByNumber(amount) {
    // fromNumber deals with division by zero
    return Percent.fromNumber(this.toNumber() / amount);
  }
  compare(value) {
    return this.subtract(value).toNumber();
  }
  equals(value) {
    return !!value && value instanceof Percent && this.compare(value) === 0;
  }
  lessThan(value) {
    return this.compare(value) < 0;
  }
  greaterThan(value) {
    return this.compare(value) > 0;
  }
  lessThanOrEqualTo(value) {
    return this.compare(value) <= 0;
  }
  greaterThanOrEqualTo(value) {
    return this.compare(value) >= 0;
  }
  add(...values) {
    if (values.length === 0) {
      return this;
    }
    let sum = this.toNumber();
    for (const v of values) {
      sum += v.toNumber();
    }
    return Percent.fromNumber(sum);
  }
  subtract(...values) {
    const [second, ...rest] = values;
    if (!second) {
      return this;
    }
    const difference = this.toNumber() - second.add(...rest).toNumber();
    return Percent.fromNumber(difference);
  }
  pow(num) {
    return Percent.fromNumber(Math.pow(this.toNumber(), num));
  }
  isNegative() {
    return this.lessThan(Percent.fromNumber(0));
  }
}

registerCanonical(Percent_, Percent);

const formatPercent = (value, includeSymbol = true) => value.format(includeSymbol);
const formatMaxRightDecimalPlaces = (value, places, includeSymbol = true) => value.formatMaxDecimalPlaces(places, includeSymbol);
const formatAsDecimal = (value, places) => value.formatAsNumber(places);
const formatAsMultiple = (value, places) => value.formatAsMultiple(places);
const percentToNumber = value => value.toNumber();
const percentIsZero = value => value.isZero();

// DEPRECATED. Use `percentToNumber` instead.
const toNumber$1 = percentToNumber;
const percentFromNumber = value => Percent.fromNumber(value);
const dividePercent = (value, amount) => value.divideByNumber(amount);
const percentCompare = (a, b) => a.compare(b);
const percentsEqual = (a, b) => a.equals(b);
const percentLessThan = (a, b) => a.lessThan(b);
const percentGreaterThan = (a, b) => a.greaterThan(b);
const percentLessThanOrEqualTo = (a, b) => a.lessThanOrEqualTo(b);
const maxPercent = (...values) => Percent.max(...values);
const minPercent = (...values) => Percent.min(...values);

// Note this still returns zero percent if all values are zero.
const minPercentFilterZero = (...values) => Percent.minExceptZero(...values);
function addPercents(...values) {
  if (values.length === 0) {
    return Percent.getDefault();
  }
  if (values.length === 1) {
    return values[0];
  }
  return values[0].add(...values.slice(1));
}
const subtractPercents = (a, b) => a.subtract(b);
const percentIsNegative = v => v.isNegative();

// This currency class currently enforces a maximum of 11 digits to the left of
const THOUSAND = 1000;
const MILLION = THOUSAND * THOUSAND;
const TEN_MILLION = MILLION * 10;
const BILLION = MILLION * THOUSAND;
const MAX = 10 ** 11 - 1;
const MIN = -MAX;
const tools = new SimpleDecimalTools({
  shiftPlaces: 4,
  formatMinDecimals: 2,
  lenientMinDecimals: true
});
const DENOMINATION_THOUSAND = {
  abbr: 'K',
  full: 'Thousand'
};
const DENOMINATION_MILLION = {
  abbr: 'M',
  full: 'Million'
};
const DENOMINATION_BILLION = {
  abbr: 'B',
  full: 'Billion'
};
class CurrencyValue extends CurrencyValue_ {
  // Supported format: 'USD 123.45'
  static fromString(s) {
    const m = assert(/^USD\s+((\d*)(?:\.(\d*))?)$/.exec(s), emsg`
        Cannot convert to currency value from string ${JSON.stringify(s)}.
        Invalid string format. Expected format "USD 123.45".`);
    assert(!m[2] || m[2].length <= 11, emsg`
        Cannot convert to currency value from string ${JSON.stringify(s)}.
        Currency values do not support more than 11 digits to the left of the
        decimal place.`);
    assert(!m[3] || m[3].length <= 4, emsg`
        Cannot convert to currency value from string ${JSON.stringify(s)}.
        Currency values do not support more than 4 digits to the right of the
        decimal place.`);
    const n = +m[1];
    return CurrencyValue.fromNumber(CurrencyUnit.USD, n);
  }

  // Unfortunately the type annotations are a little wonky up here. The unit and
  // value parameters are both required, and unit must be a `CurrencyUnit`.
  static fromNumber(unit, value) {
    value = assert.notNullish(value, 'Invalid call. CurrencyValue.fromNumber requires 2 arguments');
    assert(CurrencyUnit.has(unit));
    if (!CurrencyValue.inBounds(value)) {
      throw new CalculationOutOfBoundsError(value);
    }
    return new CurrencyValue().setUnit(unit).setValue(tools.fromNumber(value));
  }
  static min(...values) {
    assert(values.length > 0, 'Expected at least one currency value.');
    let min = values[0];
    for (const value of values.slice(1)) {
      if (value.lessThan(min)) {
        min = value;
      }
    }
    return min;
  }
  static max(...values) {
    assert(values.length > 0, 'Expected at least one currency value.');
    let max = values[0];
    for (const value of values.slice(1)) {
      if (value.greaterThan(max)) {
        max = value;
      }
    }
    return max;
  }
  static inBounds(n) {
    return n >= MIN && n <= MAX;
  }
  toNumber() {
    return tools.toNumber(this.value);
  }
  isZero() {
    return tools.toNumber(this.value) === 0;
  }
  format() {
    const formatted = formatCommaSeparated(tools.normalize(this.value, 0, 2));
    if (formatted[0] === '-') {
      return `-$${formatted.slice(1)}`;
    }
    return '$' + formatted;
  }
  roundedFormat() {
    const n = this.toNumber();
    const value = Math.abs(n);
    let amount = value;
    let denomination = DENOMINATION_THOUSAND;
    if (value < MILLION) {
      amount = Math.round(value / THOUSAND);
      denomination = DENOMINATION_THOUSAND;
    } else if (value < TEN_MILLION) {
      amount = Math.round(value / MILLION * 10) / 10;
      denomination = DENOMINATION_MILLION;
    } else if (value < BILLION) {
      amount = Math.round(value / MILLION);
      denomination = DENOMINATION_MILLION;
    } else {
      amount = Math.round(value / BILLION * 100) / 100;
      denomination = DENOMINATION_BILLION;
    }
    return {
      amount: `${n < 0 ? '-' : ''}$${amount}`,
      denomination
    };
  }
  formatAbbr() {
    const {
      amount,
      denomination
    } = this.roundedFormat();
    return `${amount}${denomination.abbr}`;
  }
  formatNoDecimal() {
    return this.round().format();
  }
  formatSiginificant(digits, maxRightDec = 4) {
    const formatted = formatCommaSeparated(this.roundSignificant(digits, maxRightDec).value);
    if (formatted[0] === '-') {
      return `-$${formatted.slice(1)}`;
    }
    return '$' + formatted;
  }
  compare(value) {
    return this.subtract(value).toNumber();
  }
  lessThan(value) {
    return this.compare(value) < 0;
  }
  greaterThan(value) {
    return this.compare(value) > 0;
  }
  equals(value) {
    return !!value && value instanceof CurrencyValue && this.unit === value.unit && this.toNumber() === value.toNumber();
  }
  round() {
    return CurrencyValue.fromNumber(this.unit, Math.round(this.toNumber()));
  }
  roundSignificant(digits, maxRightDec = 4) {
    // This currency class currently enforces a maximum of 11 digits to the left
    // of the decimal point and 4 to the right, for a total of 15 digits.
    assert(maxRightDec <= 4 && maxRightDec >= 0, 'maxRightDec must be 0-4');

    // Seperate out sign
    const sign = this.value[0] === '-' ? '-' : '';
    const unsigned = !sign ? this.value : this.value.slice(1);
    let significant = '';

    // Get Significant Numbers
    let i = 0;
    let d = digits;
    let r = -9999;
    while (d > 0 && i < unsigned.length && r < maxRightDec) {
      if (unsigned[i] === '.') {
        if (maxRightDec === 0) {
          break;
        }
        significant = significant + unsigned[i];
        i++;
        r = 0;
        continue;
      }
      significant = significant + unsigned[i];
      i++;
      d--;
      r++;
    }

    // Round Significant digits
    if (unsigned[i] && (unsigned[i] === '.' ? +unsigned[i + 1] : +unsigned[i]) > 4) {
      significant = roundStringValue(significant);
    }

    // Fill with needed zeros
    while (i < unsigned.length) {
      if (unsigned[i] === '.') {
        break;
      }
      significant = significant + '0';
      i++;
    }

    // Remove all trailing 0s
    significant = cleanZeros(significant);

    // Add back any needed 0s
    let reqLength = significant.indexOf('.') === -1 ? digits : digits + 1;
    let right = significant.indexOf('.') === -1 ? -1 : significant.length - significant.indexOf('.');
    while (significant.length < reqLength && right <= maxRightDec && maxRightDec > 0) {
      if (right === -1) {
        significant = significant + '.';
        reqLength++;
        right = 1;
      }
      significant = significant + '0';
      right++;
    }
    return new CurrencyValue().setUnit(this.unit).setValue(sign + significant);
  }
  floor() {
    return CurrencyValue.fromNumber(this.unit, Math.floor(this.toNumber()));
  }
  add(...values) {
    return this.addAll(values);
  }
  addAll(values) {
    if (values.length === 0) {
      return this;
    }
    const [first, ...rest] = validateSameUnits([this, ...values]);
    let sum = first.toNumber();
    for (const v of rest) {
      sum += v.toNumber();
    }
    return CurrencyValue.fromNumber(first.unit, sum);
  }
  subtract(...values) {
    const [first, second, ...rest] = validateSameUnits([this, ...values]);
    if (!second) {
      return first;
    }
    const difference = first.toNumber() - second.add(...rest).toNumber();
    return CurrencyValue.fromNumber(first.unit, difference);
  }
  divide(value, errorResult) {
    assert.same(this.unit, value.unit, 'Expected units to match');
    // fromNumber deals with division by zero
    return Percent.fromNumber(this.toNumber() / value.toNumber(), errorResult);
  }
  divideByNumber(amount) {
    // fromNumber deals with division by zero
    return CurrencyValue.fromNumber(this.unit, this.toNumber() / amount);
  }
  multiplyByNumber(amount) {
    return CurrencyValue.fromNumber(this.unit, this.toNumber() * amount);
  }
  multiplyByPercent(percent) {
    return CurrencyValue.fromNumber(this.unit, percent.toNumber() * this.toNumber());
  }
  divideByPercent(percent) {
    const percentNumber = percent.toNumber();
    // fromNumber deals with division by zero
    return CurrencyValue.fromNumber(this.unit, this.toNumber() / percentNumber);
  }
  unitToCapitalString() {
    assert(this.unit === CurrencyUnit.USD);
    return 'USD';
  }
}
function* validateSameUnits(values, expectedUnit) {
  let unit = expectedUnit;
  for (const v of values) {
    if (unit === undefined) {
      unit = v.unit;
    }
    assert.same(unit, v.unit);
    yield v;
  }
}
function roundStringValue(significant) {
  if (significant.indexOf('.') === -1) {
    return significant = (+significant + 1).toString();
  }
  let roundVal = +significant[significant.length - 1] + 1;
  let round = roundVal === 10;
  let roundStr = round ? '0' : roundVal.toString();
  while (round) {
    significant = significant.slice(0, -1);
    const v = significant[significant.length - 1];
    if (v === '.') {
      roundStr = v + roundStr;
      significant = (+significant + 1).toString();
      return significant + roundStr;
    }
    roundVal = +v + 1;
    round = roundVal === 10;
    roundStr = round ? '0' + roundStr : roundVal.toString() + roundStr;
  }
  significant = significant.slice(0, -1) + roundStr;
  return significant;
}
function cleanZeros(num) {
  if (num.indexOf('.') === -1) {
    return num;
  }
  while (num[num.length - 1] === '0') {
    num = num.slice(0, -1);
  }
  if (num[num.length - 1] === '.') {
    num = num.slice(0, -1);
  }
  return num;
}

registerCanonical(CurrencyValue_, CurrencyValue);
registerCanonical(RepeatedCurrencyValue_, RepeatedCurrencyValue_);

const currencyValueToNumber = value => value.toNumber();

// Alias
const currencyToNumber = currencyValueToNumber;

// DEPRECATED. Use currencyToNumber.
const toNumber = currencyValueToNumber;
const currencyIsZero = value => value.isZero();
const currencyFromNumber = (unit, value) => CurrencyValue.fromNumber(unit, value);
const formatCurrency = value => value.format();
const formatCurrencyAbbr = value => value.formatAbbr();
const formatCurrencyNoDecimal = value => value.formatNoDecimal();
const currencyValueCompare = (a, b) => a.compare(b);
const currencyValueLessThan = (a, b) => a.lessThan(b);
const currencyValueGreaterThan = (a, b) => a.greaterThan(b);

// DEPRECATED. Use `formatCurrency` instead.
const format = formatCurrency;

// DEPRECATED. Use `formatCurrencyAbbr` instead.
const formatAbbr = formatCurrencyAbbr;
const currenciesEqual = (a, b) => a.equals(b);
const minCurrency = values => CurrencyValue.min(...values);
const roundCurrency = value => value.round();
const floorCurrency = value => value.floor();
function addCurrencies(...values) {
  if (values.length === 0) {
    return CurrencyValue.getDefault();
  }
  if (values.length === 1) {
    return values[0];
  }
  return values[0].add(...values.slice(1));
}
function subtractCurrencies(...values) {
  if (values.length === 0) {
    return CurrencyValue.getDefault();
  }
  if (values.length === 1) {
    return values[0];
  }
  return values[0].subtract(...values.slice(1));
}
function divideCurrencies(a, b, errorResult) {
  return a.divide(b, errorResult);
}
const divideCurrency = (value, amount) => value.divideByNumber(amount);
const multiplyCurrency = (value, amount) => value.multiplyByNumber(amount);
const percentOfCurrencyValue = (percent, value) => value.multiplyByPercent(percent);

// Alias.
const percentOfCurrency = percentOfCurrencyValue;
const currencyDividedByPercent = (value, percent) => value.divideByPercent(percent);

// Alias.
const compareCurrencies = currencyValueCompare;
function maxCurrency(...values) {
  return CurrencyValue.max(...values);
}

function getCurrentDate() {
  return DEPRECATED_Date.getCurrent();
}
function datesEqual(a, b) {
  return a.equals(b);
}
function isEmptyDate(value) {
  return value.isEmpty();
}
function toJsDate(value) {
  return value.toJsDate();
}
function formatRelative(value,
// Number in milliseconds when exact dates should begin being used.
maxRelative = Infinity) {
  return value.formatRelative(maxRelative);
}
function formatDateIso(value) {
  return value.formatIso();
}
function formatDateAmerican(value, delim = '/') {
  return value.formatAmerican(delim);
}
function formatDateHumanAbbreviated(value) {
  return value.formatHumanAbbreviated();
}
function getYear(value) {
  return value.getYear();
}
function getMonth(value) {
  return value.getMonth();
}
function getDayOfMonth(value) {
  return value.getDayOfMonth();
}
function setYear(value, year) {
  return value.setYear(year);
}
function setMonth(value, month) {
  return value.setMonth(month);
}
function setDayOfMonth(value, dayOfMonth) {
  return value.setDayOfMonth(dayOfMonth);
}
function setHour(value, hour) {
  return value.setHour(hour);
}
function setMinute(value, minute) {
  return value.setMinute(minute);
}
function setSecond(value, second) {
  return value.setSecond(second);
}
function setMillisecond(value, millisecond) {
  return value.setMillisecond(millisecond);
}
function incrementDate(value, n = 1) {
  return value.incrementDate(n);
}
function daysSince(value) {
  return value.daysSince();
}
function compareDates(a, b) {
  return a.compare(b);
}

// tslint:disable-next-line:no-unused-variable
var CapStackItemType__;
(function (CapStackItemType__) {
  CapStackItemType__[CapStackItemType__["OTHER"] = 0] = "OTHER";
  CapStackItemType__[CapStackItemType__["COMMON_EQUITY"] = 1] = "COMMON_EQUITY";
  CapStackItemType__[CapStackItemType__["PREFERRED_EQUITY"] = 2] = "PREFERRED_EQUITY";
  CapStackItemType__[CapStackItemType__["MEZZANINE_DEBT"] = 3] = "MEZZANINE_DEBT";
  CapStackItemType__[CapStackItemType__["SENIOR_DEBT"] = 4] = "SENIOR_DEBT";
  CapStackItemType__[CapStackItemType__["LAND_VALUE"] = 6] = "LAND_VALUE";
  CapStackItemType__[CapStackItemType__["PACE_FINANCING"] = 7] = "PACE_FINANCING";
  CapStackItemType__[CapStackItemType__["TAX_CREDITS"] = 9] = "TAX_CREDITS";
  CapStackItemType__[CapStackItemType__["OPEN"] = 5] = "OPEN";
  CapStackItemType__[CapStackItemType__["GROUND_LEASE"] = 8] = "GROUND_LEASE";
})(CapStackItemType__ || (CapStackItemType__ = {}));
const CapStackItemType_ = defineEnumProperties(CapStackItemType__, [0, 1, 2, 3, 4, 6, 7, 5, 8]);
const CapStackItemType$1 = extendEnum('CapStackItemType', [["OTHER", 0], ["COMMON_EQUITY", 1], ["PREFERRED_EQUITY", 2], ["MEZZANINE_DEBT", 3], ["SENIOR_DEBT", 4], ["LAND_VALUE", 6], ["PACE_FINANCING", 7], ["TAX_CREDITS", 9], ["OPEN", 5], ["GROUND_LEASE", 8]])(CapStackItemType_);

const CapStackItemType = {
  ...CapStackItemType$1,
  format(value) {
    switch (value) {
      case CapStackItemType.OTHER:
        return 'Other';
      case CapStackItemType.COMMON_EQUITY:
        return 'Common Equity';
      case CapStackItemType.PREFERRED_EQUITY:
        return 'Preferred Equity';
      case CapStackItemType.MEZZANINE_DEBT:
        return 'Mezzanine Debt';
      case CapStackItemType.SENIOR_DEBT:
        return 'Senior Debt';
      case CapStackItemType.LAND_VALUE:
        return 'Land Value';
      case CapStackItemType.PACE_FINANCING:
        return 'Pace Financing';
      case CapStackItemType.OPEN:
        return 'Open';
      case CapStackItemType.GROUND_LEASE:
        return 'Ground Lease';
      case CapStackItemType.TAX_CREDITS:
        return 'Tax Credits';
    }
    assert.fail(emsg`Unknown cap stack item type: ${value}`);
    return '';
  }
};

registerCanonical(CapStackItemType$1, CapStackItemType);

function _defineProperty$29(obj, key, value) { key = _toPropertyKey$29(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$29(arg) { var key = _toPrimitive$29(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$29(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CapStackItem_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: CurrencyValue.shape,
      3: 2,
      4: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message CapStackItem field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return CapStackItemType.fromStrictJson_(v, getFieldMap);
          }
          return CapStackItemType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CapStackItem field principal.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CapStackItem field entity.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CapStackItem field maturity_date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CapStackItem$1, 'CapStackItem', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CapStackItem to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get principal() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setPrincipal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deletePrincipal() {
    return this.setValueFieldById(2, null, null);
  }
  get entity() {
    return this.getFieldById(3, "");
  }
  setEntity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteEntity() {
    return this.setValueFieldById(3, null, null);
  }
  get maturityDate() {
    return this.getFieldById(4, DEPRECATED_Date.getDefault());
  }
  setMaturityDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteMaturityDate() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$29(CapStackItem_, protoName, DEV_MODE ? 'CapStackItem' : '[Unknown]');
let CapStackItem$1 = CapStackItem_;
(function (_CapStackItem) {})(CapStackItem$1 || (CapStackItem$1 = {}));
Object.defineProperties(CapStackItem$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "principal": {
        value: 2
      },
      "entity": {
        value: 3
      },
      "maturity_date": {
        value: 4
      },
      "maturityDate": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "principal"
      },
      '3': {
        value: "entity"
      },
      '4': {
        value: "maturity_date"
      }
    }))
  }
});
Object.defineProperties(CapStackItem$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: CurrencyValue.prototype,
      4: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CapStackItem$1);
  Object.freeze(CapStackItem$1.prototype);
}
class RepeatedCapStackItem_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CapStackItem$1.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackItem$1);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedCapStackItem field value.
                Expected type CapStackItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedCapStackItem$1, 'RepeatedCapStackItem', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedCapStackItem to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$29(RepeatedCapStackItem_, protoName, DEV_MODE ? 'RepeatedCapStackItem' : '[Unknown]');
let RepeatedCapStackItem$1 = RepeatedCapStackItem_;
(function (_RepeatedCapStackItem) {})(RepeatedCapStackItem$1 || (RepeatedCapStackItem$1 = {}));
Object.defineProperties(RepeatedCapStackItem$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedCapStackItem$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CapStackItem$1.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedCapStackItem$1);
  Object.freeze(RepeatedCapStackItem$1.prototype);
}

const RepeatedCapStackItem = RepeatedCapStackItem_;
class CapStackItem extends CapStackItem_ {
  formatType() {
    return CapStackItemType.format(this.type);
  }
}

registerCanonical(CapStackItem_, CapStackItem);
registerCanonical(RepeatedCapStackItem_, RepeatedCapStackItem);

const CapStackItemTypeLabel = {
  [CapStackItemType.COMMON_EQUITY]: 'Common Equity',
  [CapStackItemType.PREFERRED_EQUITY]: 'Preferred Equity',
  [CapStackItemType.MEZZANINE_DEBT]: 'Mezzanine Debt',
  [CapStackItemType.SENIOR_DEBT]: 'Senior Debt',
  [CapStackItemType.LAND_VALUE]: 'Land Value',
  [CapStackItemType.GROUND_LEASE]: 'Ground Lease',
  [CapStackItemType.TAX_CREDITS]: 'Tax Credits',
  [CapStackItemType.PACE_FINANCING]: 'PACE Financing',
  [CapStackItemType.OPEN]: 'Open',
  [CapStackItemType.OTHER]: 'Other'
};
function capStackItemsEqual(a, b) {
  return a.type === b.type && currenciesEqual(a.principal, b.principal) && a.entity === b.entity && datesEqual(a.maturityDate, b.maturityDate);
}
function capStacksEqual(a, b) {
  return iterablesEqual(a, b, capStackItemsEqual);
}

function _defineProperty$28(obj, key, value) { key = _toPropertyKey$28(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$28(arg) { var key = _toPrimitive$28(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$28(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CapStackTarget_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CapStackTarget field use.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CapStackTarget field principal.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CapStackTarget, 'CapStackTarget', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CapStackTarget to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get use() {
    return this.getFieldById(1, "");
  }
  setUse(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUse() {
    return this.setValueFieldById(1, null, null);
  }
  get principal() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setPrincipal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deletePrincipal() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$28(CapStackTarget_, protoName, DEV_MODE ? 'CapStackTarget' : '[Unknown]');
let CapStackTarget = CapStackTarget_;
(function (_CapStackTarget) {})(CapStackTarget || (CapStackTarget = {}));
Object.defineProperties(CapStackTarget, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "use": {
        value: 1
      },
      "principal": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "use"
      },
      '2': {
        value: "principal"
      }
    }))
  }
});
Object.defineProperties(CapStackTarget.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CapStackTarget);
  Object.freeze(CapStackTarget.prototype);
}
class RepeatedCapStackTarget_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CapStackTarget.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackTarget);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedCapStackTarget field value.
                Expected type CapStackTarget.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackTarget', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedCapStackTarget, 'RepeatedCapStackTarget', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedCapStackTarget to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$28(RepeatedCapStackTarget_, protoName, DEV_MODE ? 'RepeatedCapStackTarget' : '[Unknown]');
let RepeatedCapStackTarget = RepeatedCapStackTarget_;
(function (_RepeatedCapStackTarget) {})(RepeatedCapStackTarget || (RepeatedCapStackTarget = {}));
Object.defineProperties(RepeatedCapStackTarget, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedCapStackTarget.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CapStackTarget.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedCapStackTarget);
  Object.freeze(RepeatedCapStackTarget.prototype);
}

function capStackTargetsEqual(a, b) {
  return a.use === b.use && a.principal.equals(b.principal);
}
function capStackTargetListsEqual(a, b) {
  return iterablesEqual(a, b, capStackTargetsEqual);
}
function capStackTargetListHasCashOut(targets) {
  return targets.some(u => u.use.toLowerCase().replace(/\W/g, '') === 'cashout' && !u.principal.isZero());
}

// tslint:disable-next-line:no-unused-variable
var ClosingCostTiming__;
(function (ClosingCostTiming__) {
  ClosingCostTiming__[ClosingCostTiming__["CLOSING"] = 0] = "CLOSING";
  ClosingCostTiming__[ClosingCostTiming__["APPLICATION"] = 1] = "APPLICATION";
  ClosingCostTiming__[ClosingCostTiming__["LOAN_MATURITY"] = 2] = "LOAN_MATURITY";
})(ClosingCostTiming__ || (ClosingCostTiming__ = {}));
const ClosingCostTiming_ = defineEnumProperties(ClosingCostTiming__, [0, 1, 2]);
const ClosingCostTiming$1 = extendEnum('ClosingCostTiming', [["CLOSING", 0], ["APPLICATION", 1], ["LOAN_MATURITY", 2]])(ClosingCostTiming_);

const ClosingCostTiming = {
  ...ClosingCostTiming$1,
  format(value) {
    switch (value) {
      case ClosingCostTiming.CLOSING:
        return 'Closing';
      case ClosingCostTiming.APPLICATION:
        return 'Application';
      case ClosingCostTiming.LOAN_MATURITY:
        return 'Loan Maturity';
    }
    assert.fail(`Unknown closing cost timing: (${value})`);
    return '';
  }
};

registerCanonical(ClosingCostTiming$1, ClosingCostTiming);

function _defineProperty$27(obj, key, value) { key = _toPropertyKey$27(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$27(arg) { var key = _toPrimitive$27(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$27(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _CurrencyOrPercent_Value_;
(function (_CurrencyOrPercent_Value_) {
  _CurrencyOrPercent_Value_[_CurrencyOrPercent_Value_["UNSET"] = 0] = "UNSET";
  _CurrencyOrPercent_Value_[_CurrencyOrPercent_Value_["CURRENCY"] = 1] = "CURRENCY";
  _CurrencyOrPercent_Value_[_CurrencyOrPercent_Value_["PERCENT"] = 2] = "PERCENT";
})(_CurrencyOrPercent_Value_ || (_CurrencyOrPercent_Value_ = {}));
const _CurrencyOrPercent_Value = defineEnumProperties(_CurrencyOrPercent_Value_, [1, 2]);
class CurrencyOrPercent_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CurrencyValue.shape,
      2: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CurrencyOrPercent field currency.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CurrencyOrPercent field percent.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CurrencyOrPercent$1, 'CurrencyOrPercent', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CurrencyOrPercent to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ValueCase() {
    return _CurrencyOrPercent_Value;
  }
  get valueCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get currency() {
    return this.getFieldById(1, CurrencyValue.getDefault());
  }
  setCurrency(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "value");
  }
  deleteCurrency() {
    return this.setValueFieldById(1, null, "value");
  }
  get percent() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setPercent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "value");
  }
  deletePercent() {
    return this.setValueFieldById(2, null, "value");
  }
}
_defineProperty$27(CurrencyOrPercent_, protoName, DEV_MODE ? 'CurrencyOrPercent' : '[Unknown]');
let CurrencyOrPercent$1 = CurrencyOrPercent_;
(function (_CurrencyOrPercent) {})(CurrencyOrPercent$1 || (CurrencyOrPercent$1 = {}));
Object.defineProperties(CurrencyOrPercent$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "currency": {
        value: 1
      },
      "percent": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "currency"
      },
      '2': {
        value: "percent"
      }
    }))
  }
});
Object.defineProperties(CurrencyOrPercent$1.prototype, {
  oneofs_: {
    value: {
      "value": [1, 2]
    }
  },
  prototypes_: {
    value: {
      1: CurrencyValue.prototype,
      2: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CurrencyOrPercent$1);
  Object.freeze(CurrencyOrPercent$1.prototype);
}

class CurrencyOrPercent extends CurrencyOrPercent_ {
  // Supports the following formats:
  // - 'USD 100000'
  // - '5%'
  static fromString(s) {
    if (/^USD /.test(s)) {
      return new CurrencyOrPercent().setCurrency(CurrencyValue.fromString(s));
    }
    // TODO(nathan): Display a more helpful error message if the user enters
    // something wrong, such as 'EUR 1000'. Currently it will tell them they
    // have entered an invalid percent, but it makes no mention of the ability
    // to enter a currency.
    return new CurrencyOrPercent().setPercent(Percent.fromString(s));
  }
  format() {
    switch (this.valueCase) {
      case CurrencyOrPercent.ValueCase.UNSET:
        return '';
      case CurrencyOrPercent.ValueCase.CURRENCY:
        return this.currency.format();
      case CurrencyOrPercent.ValueCase.PERCENT:
        return this.percent.format();
    }
    assert.fail(emsg`Unknown currency or percent value case: ${this.valueCase}`);
    return '';
  }
}

registerCanonical(CurrencyOrPercent_, CurrencyOrPercent);

function currencyOrPercentsEqual(a, b) {
  if (a.valueCase === CurrencyOrPercent.ValueCase.CURRENCY && b.valueCase === CurrencyOrPercent.ValueCase.CURRENCY) {
    return currenciesEqual(a.currency, b.currency);
  }
  if (a.valueCase === CurrencyOrPercent.ValueCase.PERCENT && b.valueCase === CurrencyOrPercent.ValueCase.PERCENT) {
    return percentsEqual(a.percent, b.percent);
  }
  return false;
}

// tslint:disable-next-line:no-unused-variable
var ClosingCostType__;
(function (ClosingCostType__) {
  ClosingCostType__[ClosingCostType__["ORIGINATION_FEE"] = 0] = "ORIGINATION_FEE";
  ClosingCostType__[ClosingCostType__["APPLICATION_FEE"] = 1] = "APPLICATION_FEE";
  ClosingCostType__[ClosingCostType__["APPRAISAL_FEE"] = 2] = "APPRAISAL_FEE";
  ClosingCostType__[ClosingCostType__["LEGAL_FEE"] = 3] = "LEGAL_FEE";
  ClosingCostType__[ClosingCostType__["TITLE_INSURANCE"] = 4] = "TITLE_INSURANCE";
  ClosingCostType__[ClosingCostType__["ENVIRONMENTAL_FEE"] = 5] = "ENVIRONMENTAL_FEE";
  ClosingCostType__[ClosingCostType__["CREDIT_CHECK"] = 6] = "CREDIT_CHECK";
  ClosingCostType__[ClosingCostType__["MORTGAGE_INSURANCE_PREMIUM"] = 9] = "MORTGAGE_INSURANCE_PREMIUM";
  ClosingCostType__[ClosingCostType__["OTHER"] = 7] = "OTHER";
  ClosingCostType__[ClosingCostType__["STACK_SOURCE_FEE"] = 8] = "STACK_SOURCE_FEE";
})(ClosingCostType__ || (ClosingCostType__ = {}));
const ClosingCostType_ = defineEnumProperties(ClosingCostType__, [0, 1, 2, 3, 4, 5, 6, 9, 7, 8]);
const ClosingCostType$1 = extendEnum('ClosingCostType', [["ORIGINATION_FEE", 0], ["APPLICATION_FEE", 1], ["APPRAISAL_FEE", 2], ["LEGAL_FEE", 3], ["TITLE_INSURANCE", 4], ["ENVIRONMENTAL_FEE", 5], ["CREDIT_CHECK", 6], ["MORTGAGE_INSURANCE_PREMIUM", 9], ["OTHER", 7], ["STACK_SOURCE_FEE", 8]])(ClosingCostType_);

const ClosingCostType = {
  ...ClosingCostType$1,
  format(value) {
    switch (value) {
      case ClosingCostType.ORIGINATION_FEE:
        return 'Origination Fee';
      case ClosingCostType.APPLICATION_FEE:
        return 'Application Fee';
      case ClosingCostType.APPRAISAL_FEE:
        return 'Appraisal Fee';
      case ClosingCostType.LEGAL_FEE:
        return 'Legal Fee';
      case ClosingCostType.TITLE_INSURANCE:
        return 'Title Insurance';
      case ClosingCostType.ENVIRONMENTAL_FEE:
        return 'Environmental Fee';
      case ClosingCostType.CREDIT_CHECK:
        return 'Credit Check';
      case ClosingCostType.MORTGAGE_INSURANCE_PREMIUM:
        return 'Mortgage Insurance Premium';
      case ClosingCostType.OTHER:
        return '';
      case ClosingCostType.STACK_SOURCE_FEE:
        return 'StackSource Fee';
    }
    assert.fail(`Unknown closing cost timing: (${value})`);
    return '';
  }
};

registerCanonical(ClosingCostType$1, ClosingCostType);

function _defineProperty$26(obj, key, value) { key = _toPropertyKey$26(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$26(arg) { var key = _toPrimitive$26(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$26(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ClosingCost_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: CurrencyOrPercent.shape,
      3: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ClosingCost field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ClosingCostType.fromStrictJson_(v, getFieldMap);
          }
          return ClosingCostType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyOrPercent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ClosingCost field value.
                Expected type CurrencyOrPercent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyOrPercent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ClosingCost field timing.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ClosingCostTiming.fromStrictJson_(v, getFieldMap);
          }
          return ClosingCostTiming.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ClosingCost$1, 'ClosingCost', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ClosingCost to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get value() {
    return this.getFieldById(2, CurrencyOrPercent.getDefault());
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(2, null, null);
  }
  get timing() {
    return this.getFieldById(3, 0);
  }
  setTiming(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteTiming() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$26(ClosingCost_, protoName, DEV_MODE ? 'ClosingCost' : '[Unknown]');
let ClosingCost$1 = ClosingCost_;
(function (_ClosingCost) {})(ClosingCost$1 || (ClosingCost$1 = {}));
Object.defineProperties(ClosingCost$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "value": {
        value: 2
      },
      "timing": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "value"
      },
      '3': {
        value: "timing"
      }
    }))
  }
});
Object.defineProperties(ClosingCost$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: CurrencyOrPercent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ClosingCost$1);
  Object.freeze(ClosingCost$1.prototype);
}

class ClosingCost extends ClosingCost_ {
  formatTiming() {
    return ClosingCostTiming.format(this.timing);
  }
  formatType() {
    return ClosingCostType.format(this.type);
  }
}

registerCanonical(ClosingCost_, ClosingCost);

const ZERO_CURRENCY_VALUE = new CurrencyValue().setUnit(CurrencyUnit.USD).setValue('0');
function closingCostsEqual(a, b) {
  return a.type === b.type && currencyOrPercentsEqual(a.value, b.value);
}
function calculateTotalClosingCostsCurrency(closingCosts, principal) {
  return addCurrencies(...[...closingCosts].map(c => calculateClosingCostCurrency(c.value, principal)));
}
function calculateClosingCostCurrency(cost, principal) {
  if (cost.valueCase === CurrencyOrPercent.ValueCase.UNSET) {
    return ZERO_CURRENCY_VALUE;
  }
  if (cost.valueCase === CurrencyOrPercent.ValueCase.CURRENCY) {
    return cost.currency;
  }
  if (cost.valueCase === CurrencyOrPercent.ValueCase.PERCENT) {
    const v = percentToNumber(cost.percent) * toNumber(principal.total);
    const unit = principal.total.unit;
    return currencyFromNumber(unit, v);
  }
  assert.fail(emsg`Unexpected ClosingCost value ${cost}`);
  return ZERO_CURRENCY_VALUE;
}

// tslint:disable-next-line:no-unused-variable
var CreditScore__;
(function (CreditScore__) {
  CreditScore__[CreditScore__["UNSET"] = 0] = "UNSET";
  CreditScore__[CreditScore__["BAD"] = 1] = "BAD";
  CreditScore__[CreditScore__["POOR"] = 2] = "POOR";
  CreditScore__[CreditScore__["OKAY"] = 3] = "OKAY";
  CreditScore__[CreditScore__["GOOD"] = 4] = "GOOD";
  CreditScore__[CreditScore__["GREAT"] = 5] = "GREAT";
  CreditScore__[CreditScore__["EXCELLENT"] = 6] = "EXCELLENT";
})(CreditScore__ || (CreditScore__ = {}));
const CreditScore_ = defineEnumProperties(CreditScore__, [0, 1, 2, 3, 4, 5, 6]);
const CreditScore$1 = extendEnum('CreditScore', [["UNSET", 0], ["BAD", 1], ["POOR", 2], ["OKAY", 3], ["GOOD", 4], ["GREAT", 5], ["EXCELLENT", 6]])(CreditScore_);

const CreditScore = {
  ...CreditScore$1,
  format(creditScore) {
    switch (creditScore) {
      case CreditScore.UNSET:
        return '';
      case CreditScore.BAD:
        return 'Bad (499 or below)';
      case CreditScore.POOR:
        return 'Poor (500 - 599)';
      case CreditScore.OKAY:
        return 'Okay (600 - 649)';
      case CreditScore.GOOD:
        return 'Good (650 - 679)';
      case CreditScore.GREAT:
        return 'Great (680 - 719)';
      case CreditScore.EXCELLENT:
        return 'Excellent (720 or above)';
    }
    assert.fail(emsg`Unknown credit score: ${creditScore}`);
    return '';
  }
};

registerCanonical(CreditScore$1, CreditScore);

const creditScoreToString = creditScore => CreditScore.format(creditScore);

function _defineProperty$25(obj, key, value) { key = _toPropertyKey$25(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$25(arg) { var key = _toPrimitive$25(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$25(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CurrencyValueRange_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CurrencyValue.shape,
      2: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CurrencyValueRange field lower_bound.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CurrencyValueRange field upper_bound.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CurrencyValueRange$1, 'CurrencyValueRange', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CurrencyValueRange to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get lowerBound() {
    return this.getFieldById(1, CurrencyValue.getDefault());
  }
  setLowerBound(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteLowerBound() {
    return this.setValueFieldById(1, null, null);
  }
  get upperBound() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setUpperBound(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUpperBound() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$25(CurrencyValueRange_, protoName, DEV_MODE ? 'CurrencyValueRange' : '[Unknown]');
let CurrencyValueRange$1 = CurrencyValueRange_;
(function (_CurrencyValueRange) {})(CurrencyValueRange$1 || (CurrencyValueRange$1 = {}));
Object.defineProperties(CurrencyValueRange$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "lower_bound": {
        value: 1
      },
      "lowerBound": {
        value: 1
      },
      "upper_bound": {
        value: 2
      },
      "upperBound": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "lower_bound"
      },
      '2': {
        value: "upper_bound"
      }
    }))
  }
});
Object.defineProperties(CurrencyValueRange$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CurrencyValue.prototype,
      2: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CurrencyValueRange$1);
  Object.freeze(CurrencyValueRange$1.prototype);
}

class CurrencyValueRange extends CurrencyValueRange_ {
  // Supports the format "USD 123.45 to USD 567.89" or simply "USD 123.45"
  static fromString(s) {
    const [lower, upper] = s.split(/\s+to\s+/);
    return new CurrencyValueRange().setLowerBound(CurrencyValue.fromString(lower)).setUpperBound(CurrencyValue.fromString(upper || lower));
  }
  static createUsd(minValue, maxValue) {
    return new CurrencyValueRange().setLowerBound(CurrencyValue.fromNumber(CurrencyUnit.USD, minValue)).setUpperBound(CurrencyValue.fromNumber(CurrencyUnit.USD, maxValue));
  }
  equals(value) {
    return !!value && value instanceof CurrencyValueRange && this.lowerBound.equals(value.lowerBound) && this.upperBound.equals(value.upperBound);
  }
  format() {
    const lower = this.lowerBound.format();
    const upper = this.upperBound.format();
    if (upper === lower) {
      return lower;
    }
    return `${lower} to ${upper}`;
  }
  formatAbbr() {
    const lower = this.lowerBound.formatAbbr();
    const upper = this.upperBound.formatAbbr();
    if (lower === upper) {
      return lower;
    }
    return `${lower} to ${upper}`;
  }
  overlaps(value) {
    assert.same(this.lowerBound.unit, CurrencyUnit.USD);
    assert.same(this.upperBound.unit, CurrencyUnit.USD);
    assert.same(value.lowerBound.unit, CurrencyUnit.USD);
    assert.same(value.upperBound.unit, CurrencyUnit.USD);
    return (this.upperBound.greaterThan(value.lowerBound) || this.upperBound.equals(value.lowerBound)) && (value.upperBound.greaterThan(this.lowerBound) || value.upperBound.equals(this.lowerBound));
  }
  containsCurrencyValue(value) {
    assert.same(this.lowerBound.unit, this.upperBound.unit);
    assert.same(this.upperBound.unit, value.unit);
    return (value.greaterThan(this.lowerBound) || value.equals(this.lowerBound)) && (this.upperBound.greaterThan(value) || this.upperBound.equals(value));
  }
}

registerCanonical(CurrencyValueRange_, CurrencyValueRange);

const currencyRangesEqual = (a, b) => a.equals(b);
const formatCurrencyRange = range => range.format();
const formatCurrencyRangeAbbr = range => range.formatAbbr();
const currencyValueRangesOverlap = (range1, range2) => range1.overlaps(range2);
const currencyValueInRange = (value, range) => range.containsCurrencyValue(value);
const createUsdCurrencyValueRange = (minValue, maxValue) => CurrencyValueRange.createUsd(minValue, maxValue);
const currencyValueToRange = value => new CurrencyValueRange().setLowerBound(value).setUpperBound(value);

function _defineProperty$24(obj, key, value) { key = _toPropertyKey$24(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$24(arg) { var key = _toPrimitive$24(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$24(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CapStackDetails_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CapStackItem.shape,
      2: CapStackItem.shape,
      3: CapStackTarget.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message CapStackDetails field existing_item.
                Expected type CapStackItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message CapStackDetails field source.
                Expected type CapStackItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackTarget);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message CapStackDetails field use.
                Expected type CapStackTarget.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackTarget', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CapStackDetails, 'CapStackDetails', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CapStackDetails to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get existingItemList() {
    return this.getRepeatedFieldById(1);
  }
  hasExistingItem(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addExistingItem(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachExistingItem(values) {
    let T = this;
    for (const value of values) {
      T = T.addExistingItem(value);
    }
    return T;
  }
  setExistingItem(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeExistingItem(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearExistingItemList() {
    return this.clearRepeatedFieldById(1);
  }
  setExistingItemList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get sourceList() {
    return this.getRepeatedFieldById(2);
  }
  hasSource(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addSource(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachSource(values) {
    let T = this;
    for (const value of values) {
      T = T.addSource(value);
    }
    return T;
  }
  setSource(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeSource(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearSourceList() {
    return this.clearRepeatedFieldById(2);
  }
  setSourceList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
  get useList() {
    return this.getRepeatedFieldById(3);
  }
  hasUse(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addUse(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachUse(values) {
    let T = this;
    for (const value of values) {
      T = T.addUse(value);
    }
    return T;
  }
  setUse(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeUse(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearUseList() {
    return this.clearRepeatedFieldById(3);
  }
  setUseList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
}
_defineProperty$24(CapStackDetails_, protoName, DEV_MODE ? 'CapStackDetails' : '[Unknown]');
let CapStackDetails = CapStackDetails_;
(function (_CapStackDetails) {})(CapStackDetails || (CapStackDetails = {}));
Object.defineProperties(CapStackDetails, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "existing_item": {
        value: 1
      },
      "existingItem": {
        value: 1
      },
      "source": {
        value: 2
      },
      "use": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "existing_item"
      },
      '2': {
        value: "source"
      },
      '3': {
        value: "use"
      }
    }))
  }
});
Object.defineProperties(CapStackDetails.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CapStackItem.prototype,
      2: CapStackItem.prototype,
      3: CapStackTarget.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CapStackDetails);
  Object.freeze(CapStackDetails.prototype);
}

function _defineProperty$23(obj, key, value) { key = _toPropertyKey$23(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$23(arg) { var key = _toPrimitive$23(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$23(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _HotelFlag_Specification_$1;
(function (_HotelFlag_Specification_) {
  _HotelFlag_Specification_[_HotelFlag_Specification_["UNSET"] = 0] = "UNSET";
  _HotelFlag_Specification_[_HotelFlag_Specification_["ORGANIZATION"] = 1] = "ORGANIZATION";
  _HotelFlag_Specification_[_HotelFlag_Specification_["NAME"] = 2] = "NAME";
})(_HotelFlag_Specification_$1 || (_HotelFlag_Specification_$1 = {}));
const _HotelFlag_Specification$1 = defineEnumProperties(_HotelFlag_Specification_$1, [1, 2]);
class HotelFlag_$1 extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: SimpleDate.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message HotelFlag field organization.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message HotelFlag field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message HotelFlag field expiration_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(HotelFlag$1, 'HotelFlag', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message HotelFlag to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get SpecificationCase() {
    return _HotelFlag_Specification$1;
  }
  get specificationCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get organization() {
    return this.getFieldById(1, "");
  }
  setOrganization(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "specification");
  }
  deleteOrganization() {
    return this.setValueFieldById(1, null, "specification");
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "specification");
  }
  deleteName() {
    return this.setValueFieldById(2, null, "specification");
  }
  get expirationDate() {
    return this.getFieldById(3, SimpleDate.getDefault());
  }
  setExpirationDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteExpirationDate() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$23(HotelFlag_$1, protoName, DEV_MODE ? 'HotelFlag' : '[Unknown]');
let HotelFlag$1 = HotelFlag_$1;
(function (_HotelFlag) {})(HotelFlag$1 || (HotelFlag$1 = {}));
Object.defineProperties(HotelFlag$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "organization": {
        value: 1
      },
      "name": {
        value: 2
      },
      "expiration_date": {
        value: 3
      },
      "expirationDate": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "organization"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "expiration_date"
      }
    }))
  }
});
Object.defineProperties(HotelFlag$1.prototype, {
  oneofs_: {
    value: {
      "specification": [1, 2]
    }
  },
  prototypes_: {
    value: {
      3: SimpleDate.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(HotelFlag$1);
  Object.freeze(HotelFlag$1.prototype);
}

// tslint:disable-next-line:no-unused-variable
var AffordabilityType__;
(function (AffordabilityType__) {
  AffordabilityType__[AffordabilityType__["UNSET"] = 0] = "UNSET";
  AffordabilityType__[AffordabilityType__["MARKET_RATE"] = 1] = "MARKET_RATE";
  AffordabilityType__[AffordabilityType__["AFFORDABLE"] = 2] = "AFFORDABLE";
  AffordabilityType__[AffordabilityType__["SUBSIDIZED"] = 3] = "SUBSIDIZED";
})(AffordabilityType__ || (AffordabilityType__ = {}));
const AffordabilityType_ = defineEnumProperties(AffordabilityType__, [0, 1, 2, 3]);
const AffordabilityType = extendEnum('AffordabilityType', [["UNSET", 0], ["MARKET_RATE", 1], ["AFFORDABLE", 2], ["SUBSIDIZED", 3]])(AffordabilityType_);

function _defineProperty$22(obj, key, value) { key = _toPropertyKey$22(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$22(arg) { var key = _toPrimitive$22(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$22(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class DealImage_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message DealImage field key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(DealImage$1, 'DealImage', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message DealImage to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get key() {
    return this.getFieldById(1, "");
  }
  setKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteKey() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$22(DealImage_, protoName, DEV_MODE ? 'DealImage' : '[Unknown]');
let DealImage$1 = DealImage_;
(function (_DealImage) {})(DealImage$1 || (DealImage$1 = {}));
Object.defineProperties(DealImage$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "key": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "key"
      }
    }))
  }
});
Object.defineProperties(DealImage$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(DealImage$1);
  Object.freeze(DealImage$1.prototype);
}

// NOTE(nathan): These numbers cannot be changed without producing thumbnails
// of the new size for all existing user images.
const PROJECT_THUMBNAIL_HEIGHT = 180;
const PROJECT_THUMBNAIL_WIDTH = 180;
const PROJECT_TILE_IMAGE_WIDTH = 720;
const PROJECT_TILE_IMAGE_HEIGHT = 350;
const USER_PROFILE_HEIGHT = 700;
const USER_PROFILE_WIDTH = 700;
const USER_PROFILE_THUMBNAIL_HEIGHT = 120;
const USER_PROFILE_THUMBNAIL_WIDTH = 120;
const USER_PROFILE_TINY_THUMBNAIL_HEIGHT = 40;
const USER_PROFILE_TINY_THUMBNAIL_WIDTH = 40;
const ORGANIZATION_LOGO_HEIGHT = 400;
const ORGANIZATION_LOGO_WIDTH = 400;
const ORGANIZATION_LOGO_THUMBNAIL_HEIGHT = 120;
const ORGANIZATION_LOGO_THUMBNAIL_WIDTH = 120;
const ORGANIZATION_LOGO_TINY_THUMBNAIL_HEIGHT = 50;
const ORGANIZATION_LOGO_TINY_THUMBNAIL_WIDTH = 50;

// This list must be kept in sync with types defined in
// //bloomlake/common/image_util.
// We have a little bit of support for TIFF, but we don't support all TIFF
// images. See https://github.com/StackSource/stacksource/issues/514
const SUPPORTED_IMAGE_TYPES = new Set(['image/bmp', 'image/gif', 'image/jpeg', 'image/pjpeg', 'image/png', 'image/svg+xml', 'image/webp', 'image/x-windows-bmp']);
const isSupportedImageType = type => SUPPORTED_IMAGE_TYPES.has(type);
const isSvg = url => /^data:image\/svg\+xml;|\.svg$/.test(url);
function imagePathToProjectThumbnail(image) {
  assert(/\.\w+$/.test(image));
  return image.replace(/\.\w+$/, `_${PROJECT_THUMBNAIL_WIDTH}x${PROJECT_THUMBNAIL_HEIGHT}$&`);
}
function imagePathToProjectTileImage(image) {
  assert(/\.\w+$/.test(image));
  return image.replace(/\.\w+$/, `_${PROJECT_TILE_IMAGE_WIDTH}x${PROJECT_TILE_IMAGE_HEIGHT}$&`);
}
function imagePathToUserProfileThumbnail(image) {
  assert(/\.\w+$/.test(image));
  return image.replace(/\.\w+$/, ['_', USER_PROFILE_THUMBNAIL_WIDTH, 'x', USER_PROFILE_THUMBNAIL_HEIGHT, '$&'].join(''));
}
function imagePathToUserProfileTinyThumbnail(image) {
  assert(/\.\w+$/.test(image));
  return image.replace(/\.\w+$/, ['_', USER_PROFILE_TINY_THUMBNAIL_WIDTH, 'x', USER_PROFILE_TINY_THUMBNAIL_HEIGHT, '$&'].join(''));
}
function imagePathToOrganizationLogoThumbnail(image) {
  assert(/\.\w+$/.test(image));
  if (/\.svg$/.test(image)) {
    return image;
  }
  return image.replace(/\.\w+$/, ['_', ORGANIZATION_LOGO_THUMBNAIL_WIDTH, 'x', ORGANIZATION_LOGO_THUMBNAIL_HEIGHT, '$&'].join(''));
}
function imagePathToOrganizationLogoTinyThumbnail(image) {
  assert(/\.\w+$/.test(image));
  if (/\.svg$/.test(image)) {
    return image;
  }
  return image.replace(/\.\w+$/, ['_', ORGANIZATION_LOGO_TINY_THUMBNAIL_WIDTH, 'x', ORGANIZATION_LOGO_TINY_THUMBNAIL_HEIGHT, '$&'].join(''));
}

class DealImage extends DealImage_ {
  // Note(nathan): This is a hack to allow Buble to start using a `sym` property
  // on the image to refer to it. This will allow an easy transition when
  // we update the back end. It is returning the key, which is a string, but not
  // proper sym format.
  get sym() {
    return this.key;
  }
  get url() {
    return resourceKeyToUrl(this.key);
  }
  getUrl() {
    return deprecatedMember('ProjectImage', 'getUrl', 'url', this.url);
  }
  get tileUrl() {
    const url = this.url;
    if (!url) {
      return '';
    }
    return imagePathToProjectTileImage(url);
  }
  getTileUrl() {
    return deprecatedMember('ProjectImage', 'getTileUrl', 'tileUrl', this.tileUrl);
  }
  get thumbnailUrl() {
    const url = this.url;
    if (!url) {
      return '';
    }
    return imagePathToProjectThumbnail(url);
  }
  getThumbnailUrl() {
    return deprecatedMember('ProjectImage', 'getThumbnailUrl', 'thumbnailUrl', this.thumbnailUrl);
  }
}

registerCanonical(DealImage_, DealImage);

function _defineProperty$21(obj, key, value) { key = _toPropertyKey$21(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$21(arg) { var key = _toPrimitive$21(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$21(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class SymOrString_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SymOrString field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SymOrString field string.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SymOrString, 'SymOrString', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SymOrString to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get string() {
    return this.getFieldById(2, "");
  }
  setString(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteString() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$21(SymOrString_, protoName, DEV_MODE ? 'SymOrString' : '[Unknown]');
let SymOrString = SymOrString_;
(function (_SymOrString) {})(SymOrString || (SymOrString = {}));
Object.defineProperties(SymOrString, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "string": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "string"
      }
    }))
  }
});
Object.defineProperties(SymOrString.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SymOrString);
  Object.freeze(SymOrString.prototype);
}

function _defineProperty$20(obj, key, value) { key = _toPropertyKey$20(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$20(arg) { var key = _toPrimitive$20(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$20(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var DealTenancyType__;
(function (DealTenancyType__) {
  DealTenancyType__[DealTenancyType__["UNKNOWN"] = 0] = "UNKNOWN";
  DealTenancyType__[DealTenancyType__["SINGLE"] = 1] = "SINGLE";
  DealTenancyType__[DealTenancyType__["MULTI"] = 2] = "MULTI";
})(DealTenancyType__ || (DealTenancyType__ = {}));
const DealTenancyType_ = defineEnumProperties(DealTenancyType__, [0, 1, 2]);
const DealTenancyType = extendEnum('DealTenancyType', [["UNKNOWN", 0], ["SINGLE", 1], ["MULTI", 2]])(DealTenancyType_);
class DealTenancy_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: SymOrString.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message DealTenancy field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DealTenancyType.fromStrictJson_(v, getFieldMap);
          }
          return DealTenancyType.from(value);
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(SymOrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message DealTenancy field tenant.
                Expected type SymOrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'SymOrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(DealTenancy, 'DealTenancy', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message DealTenancy to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get tenantList() {
    return this.getRepeatedFieldById(2);
  }
  hasTenant(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addTenant(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachTenant(values) {
    let T = this;
    for (const value of values) {
      T = T.addTenant(value);
    }
    return T;
  }
  setTenant(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeTenant(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearTenantList() {
    return this.clearRepeatedFieldById(2);
  }
  setTenantList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
}
_defineProperty$20(DealTenancy_, protoName, DEV_MODE ? 'DealTenancy' : '[Unknown]');
let DealTenancy = DealTenancy_;
(function (_DealTenancy) {})(DealTenancy || (DealTenancy = {}));
Object.defineProperties(DealTenancy, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "tenant": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "tenant"
      }
    }))
  }
});
Object.defineProperties(DealTenancy.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: SymOrString.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(DealTenancy);
  Object.freeze(DealTenancy.prototype);
}

function _defineProperty$1$(obj, key, value) { key = _toPropertyKey$1$(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1$(arg) { var key = _toPrimitive$1$(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1$(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class FinancialInstruments_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 1,
      2: 1,
      3: 1,
      4: 1,
      5: 1,
      6: 1,
      7: 1,
      8: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field senior_debt.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field mezzanine_debt.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field preferred_equity.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field lp_equity.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field cogp_equity.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field line_of_credit.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field pace.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 8:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message FinancialInstruments field ground_lease.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FinancialInstruments, 'FinancialInstruments', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FinancialInstruments to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get seniorDebt() {
    return this.getFieldById(1, false);
  }
  setSeniorDebt(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSeniorDebt() {
    return this.setValueFieldById(1, null, null);
  }
  get mezzanineDebt() {
    return this.getFieldById(2, false);
  }
  setMezzanineDebt(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteMezzanineDebt() {
    return this.setValueFieldById(2, null, null);
  }
  get preferredEquity() {
    return this.getFieldById(3, false);
  }
  setPreferredEquity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deletePreferredEquity() {
    return this.setValueFieldById(3, null, null);
  }
  get lpEquity() {
    return this.getFieldById(4, false);
  }
  setLpEquity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteLpEquity() {
    return this.setValueFieldById(4, null, null);
  }
  get cogpEquity() {
    return this.getFieldById(5, false);
  }
  setCogpEquity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteCogpEquity() {
    return this.setValueFieldById(5, null, null);
  }
  get lineOfCredit() {
    return this.getFieldById(6, false);
  }
  setLineOfCredit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteLineOfCredit() {
    return this.setValueFieldById(6, null, null);
  }
  get pace() {
    return this.getFieldById(7, false);
  }
  setPace(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deletePace() {
    return this.setValueFieldById(7, null, null);
  }
  get groundLease() {
    return this.getFieldById(8, false);
  }
  setGroundLease(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteGroundLease() {
    return this.setValueFieldById(8, null, null);
  }
}
_defineProperty$1$(FinancialInstruments_, protoName, DEV_MODE ? 'FinancialInstruments' : '[Unknown]');
let FinancialInstruments = FinancialInstruments_;
(function (_FinancialInstruments) {})(FinancialInstruments || (FinancialInstruments = {}));
Object.defineProperties(FinancialInstruments, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "senior_debt": {
        value: 1
      },
      "seniorDebt": {
        value: 1
      },
      "mezzanine_debt": {
        value: 2
      },
      "mezzanineDebt": {
        value: 2
      },
      "preferred_equity": {
        value: 3
      },
      "preferredEquity": {
        value: 3
      },
      "lp_equity": {
        value: 4
      },
      "lpEquity": {
        value: 4
      },
      "cogp_equity": {
        value: 5
      },
      "cogpEquity": {
        value: 5
      },
      "line_of_credit": {
        value: 6
      },
      "lineOfCredit": {
        value: 6
      },
      "pace": {
        value: 7
      },
      "ground_lease": {
        value: 8
      },
      "groundLease": {
        value: 8
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "senior_debt"
      },
      '2': {
        value: "mezzanine_debt"
      },
      '3': {
        value: "preferred_equity"
      },
      '4': {
        value: "lp_equity"
      },
      '5': {
        value: "cogp_equity"
      },
      '6': {
        value: "line_of_credit"
      },
      '7': {
        value: "pace"
      },
      '8': {
        value: "ground_lease"
      }
    }))
  }
});
Object.defineProperties(FinancialInstruments.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FinancialInstruments);
  Object.freeze(FinancialInstruments.prototype);
}

// tslint:disable-next-line:no-unused-variable
var FinancingPurpose__;
(function (FinancingPurpose__) {
  FinancingPurpose__[FinancingPurpose__["ACQUISITION"] = 0] = "ACQUISITION";
  FinancingPurpose__[FinancingPurpose__["NEW_DEVELOPMENT"] = 1] = "NEW_DEVELOPMENT";
  FinancingPurpose__[FinancingPurpose__["REDEVELOPMENT"] = 2] = "REDEVELOPMENT";
  FinancingPurpose__[FinancingPurpose__["REFINANCE"] = 3] = "REFINANCE";
})(FinancingPurpose__ || (FinancingPurpose__ = {}));
const FinancingPurpose_ = defineEnumProperties(FinancingPurpose__, [0, 1, 2, 3]);
const FinancingPurpose$1 = extendEnum('FinancingPurpose', [["ACQUISITION", 0], ["NEW_DEVELOPMENT", 1], ["REDEVELOPMENT", 2], ["REFINANCE", 3]])(FinancingPurpose_);

const FinancingPurpose = {
  ...FinancingPurpose$1,
  format(value) {
    switch (value) {
      case FinancingPurpose.ACQUISITION:
        return 'Acquisition';
      case FinancingPurpose.NEW_DEVELOPMENT:
        return 'New Development';
      case FinancingPurpose.REDEVELOPMENT:
        return 'Redevelopment';
      case FinancingPurpose.REFINANCE:
        return 'Refinance';
    }
    assert.fail(emsg`Unknown financing purpose ${value}`);
    return '';
  },
  isDevelopment(value) {
    return value === FinancingPurpose.NEW_DEVELOPMENT || value === FinancingPurpose.REDEVELOPMENT;
  }
};

registerCanonical(FinancingPurpose$1, FinancingPurpose);

// tslint:disable-next-line:no-unused-variable
var InvestmentCategory__;
(function (InvestmentCategory__) {
  InvestmentCategory__[InvestmentCategory__["UNSET"] = 0] = "UNSET";
  InvestmentCategory__[InvestmentCategory__["STABILIZED"] = 1] = "STABILIZED";
  InvestmentCategory__[InvestmentCategory__["VALUE_ADD"] = 2] = "VALUE_ADD";
  InvestmentCategory__[InvestmentCategory__["VACANT"] = 3] = "VACANT";
})(InvestmentCategory__ || (InvestmentCategory__ = {}));
const InvestmentCategory_ = defineEnumProperties(InvestmentCategory__, [0, 1, 2, 3]);
const InvestmentCategory$1 = extendEnum('InvestmentCategory', [["UNSET", 0], ["STABILIZED", 1], ["VALUE_ADD", 2], ["VACANT", 3]])(InvestmentCategory_);

const InvestmentCategory = {
  ...InvestmentCategory$1,
  format(value) {
    switch (value) {
      case InvestmentCategory.UNSET:
        return '';
      case InvestmentCategory.STABILIZED:
        return 'Stabilized';
      case InvestmentCategory.VALUE_ADD:
        return 'Value Add';
      case InvestmentCategory.VACANT:
        return 'Vacant';
    }
    assert.fail(emsg`Unknown investment category: ${value}`);
    return '';
  }
};

registerCanonical(InvestmentCategory$1, InvestmentCategory);

function _defineProperty$1_(obj, key, value) { key = _toPropertyKey$1_(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1_(arg) { var key = _toPrimitive$1_(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1_(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _Occupancy_Type_;
(function (_Occupancy_Type_) {
  _Occupancy_Type_[_Occupancy_Type_["UNSET"] = 0] = "UNSET";
  _Occupancy_Type_[_Occupancy_Type_["UNITS"] = 1] = "UNITS";
  _Occupancy_Type_[_Occupancy_Type_["PERCENT"] = 2] = "PERCENT";
})(_Occupancy_Type_ || (_Occupancy_Type_ = {}));
const _Occupancy_Type = defineEnumProperties(_Occupancy_Type_, [1, 2]);
class Occupancy_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Occupancy field units.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Occupancy field percent.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Occupancy$1, 'Occupancy', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Occupancy to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _Occupancy_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get units() {
    return this.getFieldById(1, 0);
  }
  setUnits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteUnits() {
    return this.setValueFieldById(1, null, "type");
  }
  get percent() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setPercent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deletePercent() {
    return this.setValueFieldById(2, null, "type");
  }
}
_defineProperty$1_(Occupancy_, protoName, DEV_MODE ? 'Occupancy' : '[Unknown]');
let Occupancy$1 = Occupancy_;
(function (_Occupancy) {})(Occupancy$1 || (Occupancy$1 = {}));
Object.defineProperties(Occupancy$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "units": {
        value: 1
      },
      "percent": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "units"
      },
      '2': {
        value: "percent"
      }
    }))
  }
});
Object.defineProperties(Occupancy$1.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2]
    }
  },
  prototypes_: {
    value: {
      2: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Occupancy$1);
  Object.freeze(Occupancy$1.prototype);
}

class Occupancy extends Occupancy_ {
  isZero() {
    return this.units === 0 && this.percent.isZero();
  }
  formatForNumberOfUnits(numberOfUnits, places = Infinity) {
    if (this.isZero()) {
      return '';
    }
    switch (this.typeCase) {
      case Occupancy.TypeCase.UNITS:
        if (numberOfUnits >= this.units) {
          return Percent.fromNumber(this.units / numberOfUnits).formatMaxDecimalPlaces(places);
        }
        return `${numberOfUnits} units`;
      case Occupancy.TypeCase.PERCENT:
        return this.percent.formatMaxDecimalPlaces(places);
    }
    assert.fail(emsg`Unknown occupancy type: ${this.typeCase}`);
    return '';
  }
}

registerCanonical(Occupancy_, Occupancy);

// tslint:disable-next-line:no-unused-variable
var ExpectedInterestRate__;
(function (ExpectedInterestRate__) {
  ExpectedInterestRate__[ExpectedInterestRate__["UNSET"] = 0] = "UNSET";
  ExpectedInterestRate__[ExpectedInterestRate__["FIXED"] = 1] = "FIXED";
  ExpectedInterestRate__[ExpectedInterestRate__["FLOATING"] = 2] = "FLOATING";
})(ExpectedInterestRate__ || (ExpectedInterestRate__ = {}));
const ExpectedInterestRate_ = defineEnumProperties(ExpectedInterestRate__, [0, 1, 2]);
const ExpectedInterestRate$1 = extendEnum('ExpectedInterestRate', [["UNSET", 0], ["FIXED", 1], ["FLOATING", 2]])(ExpectedInterestRate_);

const ExpectedInterestRate = {
  ...ExpectedInterestRate$1,
  format(value) {
    switch (value) {
      case ExpectedInterestRate$1.UNSET:
        return '';
      case ExpectedInterestRate$1.FIXED:
        return 'Fixed';
      case ExpectedInterestRate$1.FLOATING:
        return 'Floating';
    }
    assert.fail(emsg`Unknown expected interest rate: ${value}`);
    return '';
  }
};

registerCanonical(ExpectedInterestRate$1, ExpectedInterestRate);

// tslint:disable-next-line:no-unused-variable
var ExpectedRecourse__;
(function (ExpectedRecourse__) {
  ExpectedRecourse__[ExpectedRecourse__["UNSET"] = 0] = "UNSET";
  ExpectedRecourse__[ExpectedRecourse__["NO_PREFERENCE"] = 2] = "NO_PREFERENCE";
  ExpectedRecourse__[ExpectedRecourse__["PREFER_NON_RECOURSE"] = 3] = "PREFER_NON_RECOURSE";
  ExpectedRecourse__[ExpectedRecourse__["REQUIRE_NON_RECOURSE"] = 1] = "REQUIRE_NON_RECOURSE";
})(ExpectedRecourse__ || (ExpectedRecourse__ = {}));
const ExpectedRecourse_ = defineEnumProperties(ExpectedRecourse__, [0, 2, 3, 1]);
const ExpectedRecourse$1 = extendEnum('ExpectedRecourse', [["UNSET", 0], ["NO_PREFERENCE", 2], ["PREFER_NON_RECOURSE", 3], ["REQUIRE_NON_RECOURSE", 1]])(ExpectedRecourse_);

function _defineProperty$1Z(obj, key, value) { key = _toPropertyKey$1Z(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1Z(arg) { var key = _toPrimitive$1Z(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1Z(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var RecourseType__;
(function (RecourseType__) {
  RecourseType__[RecourseType__["NO_RECOURSE"] = 0] = "NO_RECOURSE";
  RecourseType__[RecourseType__["FULL"] = 1] = "FULL";
  RecourseType__[RecourseType__["LIMITED"] = 2] = "LIMITED";
})(RecourseType__ || (RecourseType__ = {}));
const RecourseType_ = defineEnumProperties(RecourseType__, [0, 1, 2]);
const RecourseType = extendEnum('RecourseType', [["NO_RECOURSE", 0], ["FULL", 1], ["LIMITED", 2]])(RecourseType_);
class Recourse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Recourse field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RecourseType.fromStrictJson_(v, getFieldMap);
          }
          return RecourseType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Recourse field recourse_limit.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Recourse, 'Recourse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Recourse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get recourseLimit() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setRecourseLimit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteRecourseLimit() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1Z(Recourse_, protoName, DEV_MODE ? 'Recourse' : '[Unknown]');
let Recourse = Recourse_;
(function (_Recourse) {})(Recourse || (Recourse = {}));
Object.defineProperties(Recourse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "recourse_limit": {
        value: 2
      },
      "recourseLimit": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "recourse_limit"
      }
    }))
  }
});
Object.defineProperties(Recourse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Recourse);
  Object.freeze(Recourse.prototype);
}

function formatRecourse(value) {
  return function () {
    const array$151 = [];
    array$151.push(formatRecourseType(value.type));
    if (value.type == RecourseType.LIMITED) {
      array$151.push(' (');
      array$151.push(formatPercent(value.recourseLimit));
      array$151.push(')');
    }
    return array$151;
  }().join('');
}
function formatRecourseType(recourseType) {
  switch (recourseType) {
    case RecourseType.FULL:
      return 'Full';
      break;
    case RecourseType.LIMITED:
      return 'Limited';
      break;
    case RecourseType.NO_RECOURSE:
      return 'No Recourse';
      break;
  }
  runtime.checkAsserts() ? runtime.ensure(false, function () {
    return `Unknown recourse type (${recourseType}).`;
  }) : undefined;
}

const ExpectedRecourse = {
  ...ExpectedRecourse$1,
  format(value) {
    switch (value) {
      case ExpectedRecourse.UNSET:
        return '';
      case ExpectedRecourse.NO_PREFERENCE:
        return 'No preference';
      case ExpectedRecourse.PREFER_NON_RECOURSE:
        return 'Prefer non-recourse';
      case ExpectedRecourse.REQUIRE_NON_RECOURSE:
        return 'Require non-recourse';
    }
    assert.fail(`Unknown expected recourse: ${value}`);
    return '';
  },
  toRecourseType(value) {
    switch (value) {
      case ExpectedRecourse.UNSET:
        assert.fail('Cannot convert unset expected recourse to recourse value.');
        break;
      case ExpectedRecourse.NO_PREFERENCE:
        return RecourseType.FULL;
      case ExpectedRecourse.PREFER_NON_RECOURSE:
        return RecourseType.LIMITED;
      case ExpectedRecourse.REQUIRE_NON_RECOURSE:
        return RecourseType.NO_RECOURSE;
    }
    assert.fail(`Unknown expected recourse: ${value}`);
    return RecourseType.NO_RECOURSE;
  }
};

registerCanonical(ExpectedRecourse$1, ExpectedRecourse);

// tslint:disable-next-line:no-unused-variable
var ExpectedRepaymentSchedule__;
(function (ExpectedRepaymentSchedule__) {
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["UNSET"] = 0] = "UNSET";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["SELF_LIQUIDATING"] = 2] = "SELF_LIQUIDATING";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["FULL_TERM_IO"] = 3] = "FULL_TERM_IO";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["PARTIAL_TERM_IO"] = 4] = "PARTIAL_TERM_IO";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["FIFTEEN_YEARS"] = 5] = "FIFTEEN_YEARS";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["TWENTY_YEARS"] = 6] = "TWENTY_YEARS";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["TWENTY_FIVE_YEARS"] = 7] = "TWENTY_FIVE_YEARS";
  ExpectedRepaymentSchedule__[ExpectedRepaymentSchedule__["THIRTY_PLUS_YEARS"] = 8] = "THIRTY_PLUS_YEARS";
})(ExpectedRepaymentSchedule__ || (ExpectedRepaymentSchedule__ = {}));
const ExpectedRepaymentSchedule_ = defineEnumProperties(ExpectedRepaymentSchedule__, [0, 2, 3, 4, 5, 6, 7, 8]);
const ExpectedRepaymentSchedule$1 = extendEnum('ExpectedRepaymentSchedule', [["UNSET", 0], ["SELF_LIQUIDATING", 2], ["FULL_TERM_IO", 3], ["PARTIAL_TERM_IO", 4], ["FIFTEEN_YEARS", 5], ["TWENTY_YEARS", 6], ["TWENTY_FIVE_YEARS", 7], ["THIRTY_PLUS_YEARS", 8]])(ExpectedRepaymentSchedule_);

const Base = ExpectedRepaymentSchedule$1;
const ExpectedRepaymentSchedule = {
  ...Base,
  format(value) {
    switch (value) {
      case Base.UNSET:
        return '';
      case Base.SELF_LIQUIDATING:
        return 'Self liquidating';
      case Base.FULL_TERM_IO:
        return 'Full term I/O';
      case Base.PARTIAL_TERM_IO:
        return 'Partial term I/O';
      case Base.FIFTEEN_YEARS:
        return '15 years';
      case Base.TWENTY_YEARS:
        return '20 years';
      case Base.TWENTY_FIVE_YEARS:
        return '25 years';
      case Base.THIRTY_PLUS_YEARS:
        return '30+ years';
    }
    assert.fail(emsg`Unknown expected repayment schedule: ${value}`);
    return '';
  }
};

registerCanonical(ExpectedRepaymentSchedule$1, ExpectedRepaymentSchedule);

// tslint:disable-next-line:no-unused-variable
var TermUnit__;
(function (TermUnit__) {
  TermUnit__[TermUnit__["MONTH"] = 0] = "MONTH";
})(TermUnit__ || (TermUnit__ = {}));
const TermUnit_ = defineEnumProperties(TermUnit__, [0]);
const TermUnit = extendEnum('TermUnit', [["MONTH", 0]])(TermUnit_);

function _defineProperty$1Y(obj, key, value) { key = _toPropertyKey$1Y(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1Y(arg) { var key = _toPrimitive$1Y(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1Y(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Term_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Term field unit.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return TermUnit.fromStrictJson_(v, getFieldMap);
          }
          return TermUnit.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Term field length.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Term$1, 'Term', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Term to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get unit() {
    return this.getFieldById(1, 0);
  }
  setUnit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUnit() {
    return this.setValueFieldById(1, null, null);
  }
  get length() {
    return this.getFieldById(2, 0);
  }
  setLength(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLength() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1Y(Term_, protoName, DEV_MODE ? 'Term' : '[Unknown]');
let Term$1 = Term_;
(function (_Term) {})(Term$1 || (Term$1 = {}));
Object.defineProperties(Term$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "unit": {
        value: 1
      },
      "length": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "unit"
      },
      '2': {
        value: "length"
      }
    }))
  }
});
Object.defineProperties(Term$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Term$1);
  Object.freeze(Term$1.prototype);
}

class Term extends Term_ {
  static fromString(s) {
    const m = assert(/^(\d{1,7})\s+(month|year)s?$/.exec(s), emsg`
        Cannot convert to term from string ${s}.
        Invalid string format. Expected format examples:
        "6 months", "2 years", "1 month", "1 months".`);
    return m[2] === 'month' ? Term.ofMonths(+m[1]) : Term.ofYears(+m[1]);
  }
  static ofMonths(n) {
    assert(Number.isSafeInteger(n));
    return new Term().setUnit(TermUnit.MONTH).setLength(n);
  }
  static ofYears(n) {
    return Term.ofMonths(n * 12);
  }
  static sum(...terms) {
    let total = 0;
    for (const term of terms) {
      assert.same(term.unit, TermUnit.MONTH);
      total += term.length;
    }
    return new Term().setUnit(TermUnit.MONTH).setLength(total);
  }
  isZero() {
    return this.length === 0;
  }
  equals(value) {
    return !!value && value instanceof Term && this.unit === value.unit && this.length === value.length;
  }
  lessThan(value) {
    return this.toMonths() < value.toMonths();
  }
  greaterThan(value) {
    return this.toMonths() > value.toMonths();
  }
  greaterThanOrEqual(value) {
    return this.toMonths() >= value.toMonths();
  }
  toMonths() {
    assert.same(this.unit, TermUnit.MONTH);
    return this.length;
  }
  toYears() {
    return this.toMonths() / 12;
  }
  format() {
    switch (this.unit) {
      case TermUnit.MONTH:
        return formatMonthTermLong(this);
      default:
        assert.fail(emsg`Unknown term unit (${this.unit}).`);
    }
    return '';
  }
  formatAbbr() {
    switch (this.unit) {
      case TermUnit.MONTH:
        return formatMonthTermShort(this);
      default:
        assert.fail(emsg`Unknown term unit (${this.unit}).`);
    }
    return '';
  }
}
function formatMonthTermShort(term) {
  return formatMonthTermGeneric$1(term, 'mo', 'mo', 'yr', 'yrs');
}
function formatMonthTermLong(term) {
  return formatMonthTermGeneric$1(term, 'month', 'months', 'year', 'years');
}
function formatMonthTermGeneric$1(term, monthSingularLabel, monthPluralLabel, yearSingularLabel, yearPluralLabel) {
  const out = [];
  if (shouldDisplayInYears$1(term)) {
    const years = term.length / 12;
    out.push(years, ' ');
    if (years === 1) {
      out.push(yearSingularLabel);
    } else {
      out.push(yearPluralLabel);
    }
  } else {
    out.push(term.length, ' ');
    if (term.length === 1) {
      out.push(monthSingularLabel);
    } else {
      out.push(monthPluralLabel);
    }
  }
  return out.join('');
}
function shouldDisplayInYears$1(term) {
  return term.length >= 12 && term.length % 6 === 0;
}

registerCanonical(Term_, Term);

function _defineProperty$1X(obj, key, value) { key = _toPropertyKey$1X(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1X(arg) { var key = _toPrimitive$1X(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1X(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class TermRange_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Term.shape,
      2: Term.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Term);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message TermRange field lower_bound.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Term);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message TermRange field upper_bound.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(TermRange$1, 'TermRange', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message TermRange to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get lowerBound() {
    return this.getFieldById(1, Term.getDefault());
  }
  setLowerBound(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteLowerBound() {
    return this.setValueFieldById(1, null, null);
  }
  get upperBound() {
    return this.getFieldById(2, Term.getDefault());
  }
  setUpperBound(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUpperBound() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1X(TermRange_, protoName, DEV_MODE ? 'TermRange' : '[Unknown]');
let TermRange$1 = TermRange_;
(function (_TermRange) {})(TermRange$1 || (TermRange$1 = {}));
Object.defineProperties(TermRange$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "lower_bound": {
        value: 1
      },
      "lowerBound": {
        value: 1
      },
      "upper_bound": {
        value: 2
      },
      "upperBound": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "lower_bound"
      },
      '2': {
        value: "upper_bound"
      }
    }))
  }
});
Object.defineProperties(TermRange$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Term.prototype,
      2: Term.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(TermRange$1);
  Object.freeze(TermRange$1.prototype);
}

function formatTermsWithExtensions(terms) {
  return string(function* () {
    yield formatTerm(terms.first());
    if (terms.size > 1) {
      const formatInYears = shouldDisplayInYears(terms.first());
      for (const extension of terms.rest()) {
        runtime.checkAsserts() ? runtime.ensure(extension.unit == TermUnit.MONTH, null) : undefined;
        if (formatInYears) {
          yield ` +${formatMonthTermInYears(extension)}`;
        } else {
          yield ` +${extension.length}`;
        }
      }
    }
  }());
}
function formatTermLong(term) {
  return term.format();
}
function formatTermShort(term) {
  return term.formatAbbr();
}
function formatTerm(term) {
  switch (term.unit) {
    case TermUnit.MONTH:
      return formatMonthTerm(term);
      break;
    default:
      runtime.checkAsserts() ? runtime.ensure(false, function () {
        return `Unknown term unit (${term.unit}).`;
      }) : undefined;
      break;
  }
}
function formatMonthTerm(term) {
  return formatMonthTermGeneric(term, 'mo', 'mo', 'year', 'years');
}
function formatMonthTermGeneric(term, monthSingularLabel, monthPluralLabel, yearSingularLabel, yearPluralLabel) {
  return string(function* () {
    if (shouldDisplayInYears(term)) {
      let $152, $153;
      const years = ($152 = term.length, $153 = typeof $152, (runtime.checkAsserts() ? runtime.ensure(typeof $152 == 'number', function () {
        return `Expected type "number" but found "${$153}" for (${$152}).`;
      }) : undefined, $152)) / 12;
      yield years;
      yield ' ';
      yield years == 1 ? yearSingularLabel : yearPluralLabel;
    } else {
      yield term.length;
      yield ' ';
      yield term.length == 1 ? monthSingularLabel : monthPluralLabel;
    }
  }());
}
function shouldDisplayInYears(term) {
  let $156, $157;
  return term.length >= 12 && ($156 = term.length, $157 = typeof $156, (runtime.checkAsserts() ? runtime.ensure(typeof $156 == 'number', function () {
    return `Expected type "number" but found "${$157}" for (${$156}).`;
  }) : undefined, $156)) % 6 == 0;
}
function formatMonthTermInYears(term) {
  let $154, $155;
  switch (term.unit) {
    case TermUnit.MONTH:
      const years = (($154 = term.length, $155 = typeof $154, (runtime.checkAsserts() ? runtime.ensure(typeof $154 == 'number', function () {
        return `Expected type "number" but found "${$155}" for (${$154}).`;
      }) : undefined, $154)) / 12).toFixed(2);
      return years.replace(/\.*0*$/, '');
      break;
    default:
      runtime.checkAsserts() ? runtime.ensure(false, function () {
        return `Unknown term unit (${term.unit}).`;
      }) : undefined;
      break;
  }
}
;

function termOfMonths(n) {
  return Term.ofMonths(n);
}
function termOfYears(n) {
  return Term.ofYears(n);
}
function termToMonths(value) {
  return value.toMonths();
}
function termsEqual(a, b) {
  return a.equals(b);
}
function sumTerms(...terms) {
  return Term.sum(...terms);
}
function calculateLoanTermsYears(loanTerms) {
  let $166, $167, $168, $174, $175, $169;
  let years = 0;
  let remainder = 0;
  for (const term of loanTerms) {
    let $158, $159, $160, $161, $162, $163, $164, $165;
    const {
      loanYears,
      loanRemainder
    } = calculateLoanTermYears(term);
    years = ($158 = years, $159 = typeof $158, (runtime.checkAsserts() ? runtime.ensure(typeof $158 == 'number', function () {
      return `Expected type "number" but found "${$159}" for (${$158}).`;
    }) : undefined, $158)) + ($160 = loanYears, $161 = typeof $160, (runtime.checkAsserts() ? runtime.ensure(typeof $160 == 'number', function () {
      return `Expected type "number" but found "${$161}" for (${$160}).`;
    }) : undefined, $160));
    remainder = ($162 = remainder, $163 = typeof $162, (runtime.checkAsserts() ? runtime.ensure(typeof $162 == 'number', function () {
      return `Expected type "number" but found "${$163}" for (${$162}).`;
    }) : undefined, $162)) + ($164 = loanRemainder, $165 = typeof $164, (runtime.checkAsserts() ? runtime.ensure(typeof $164 == 'number', function () {
      return `Expected type "number" but found "${$165}" for (${$164}).`;
    }) : undefined, $164));
  }
  return ($166 = years, $167 = typeof $166, (runtime.checkAsserts() ? runtime.ensure(typeof $166 == 'number', function () {
    return `Expected type "number" but found "${$167}" for (${$166}).`;
  }) : undefined, $166)) + ($168 = ($174 = remainder, $175 = typeof $174, (runtime.checkAsserts() ? runtime.ensure(typeof $174 == 'number', function () {
    return `Expected type "number" but found "${$175}" for (${$174}).`;
  }) : undefined, $174)) / 12, $169 = typeof $168, (runtime.checkAsserts() ? runtime.ensure(typeof $168 == 'number', function () {
    return `Expected type "number" but found "${$169}" for (${$168}).`;
  }) : undefined, $168));
}
function calculateLoanTermYears(loanTerm) {
  let $178, $179, $176, $170, $171, $172, $173, $177;
  runtime.checkAsserts() ? runtime.ensure(loanTerm.unit == TermUnit.MONTH, null) : undefined;
  const loanRemainder = ($178 = loanTerm.length, $179 = typeof $178, (runtime.checkAsserts() ? runtime.ensure(typeof $178 == 'number', function () {
    return `Expected type "number" but found "${$179}" for (${$178}).`;
  }) : undefined, $178)) % 12;
  return {
    loanYears: ($176 = ($170 = loanTerm.length, $171 = typeof $170, (runtime.checkAsserts() ? runtime.ensure(typeof $170 == 'number', function () {
      return `Expected type "number" but found "${$171}" for (${$170}).`;
    }) : undefined, $170)) - ($172 = loanRemainder, $173 = typeof $172, (runtime.checkAsserts() ? runtime.ensure(typeof $172 == 'number', function () {
      return `Expected type "number" but found "${$173}" for (${$172}).`;
    }) : undefined, $172)), $177 = typeof $176, (runtime.checkAsserts() ? runtime.ensure(typeof $176 == 'number', function () {
      return `Expected type "number" but found "${$177}" for (${$176}).`;
    }) : undefined, $176)) / 12,
    loanRemainder
  };
}
function calculateLoanTermMonths(loanTerm) {
  runtime.checkAsserts() ? runtime.ensure(loanTerm.unit == TermUnit.MONTH, null) : undefined;
  return loanTerm.length;
}

class TermRange extends TermRange_ {
  static ofMonths(lowerBound, upperBound) {
    assert(Number.isSafeInteger(lowerBound) && lowerBound >= 0);
    assert(Number.isSafeInteger(upperBound) && upperBound >= 0);
    return new TermRange().setLowerBound(Term.ofMonths(lowerBound)).setUpperBound(Term.ofMonths(upperBound));
  }

  // 0 for `upperBoundYears` represents an unlimited upper bound.
  static ofYears(lowerBoundYears = 0, upperBoundYears = 0) {
    return TermRange.ofMonths(lowerBoundYears * 12, upperBoundYears * 12);
  }
  equals(value) {
    return !!value && value instanceof TermRange && this.lowerBound.equals(value.lowerBound) && this.upperBound.equals(value.upperBound);
  }
  format() {
    assert.same(this.lowerBound.unit, TermUnit.MONTH);
    assert.same(this.upperBound.unit, TermUnit.MONTH);
    if (this.lowerBound.length === 0 && this.upperBound.length === 0) {
      return '';
    }

    // Try to display the value in years.
    if (this.lowerBound.length % 12 === 0 && this.upperBound.length % 12 === 0) {
      return termRangeToStringWithLabel(this, 12, 'year');
    }

    // Otherwise, display in months.
    return termRangeToStringWithLabel(this, 1, 'month');
  }
  overlaps(value) {
    assert.same(this.lowerBound.unit, TermUnit.MONTH);
    assert.same(this.upperBound.unit, TermUnit.MONTH);
    assert.same(value.lowerBound.unit, TermUnit.MONTH);
    assert.same(value.upperBound.unit, TermUnit.MONTH);
    return (this.upperBound.length >= value.lowerBound.length || this.upperBound.length === 0) && (value.upperBound.length >= this.lowerBound.length || value.upperBound.length === 0);
  }
  contains(term) {
    assert.same(this.lowerBound.unit, term.unit);
    assert.same(this.upperBound.unit, term.unit);
    return this.lowerBound.length <= term.length && (this.upperBound.length >= term.length || this.upperBound.length === 0);
  }
}
function termRangeToStringWithLabel(value, divisor, label) {
  assert.same(value.lowerBound.length % divisor, 0);
  assert.same(value.upperBound.length % divisor, 0);
  const out = [];
  let pluralLabel = true;
  out.push(value.lowerBound.length / divisor);
  if (value.upperBound.length === 0) {
    out.push('+');
  } else {
    if (value.upperBound.length === value.lowerBound.length) {
      pluralLabel = value.lowerBound.length !== 1;
    } else {
      out.push('-');
      out.push(value.upperBound.length / divisor);
    }
  }
  out.push(' ');
  out.push(label);
  if (pluralLabel) {
    out.push('s');
  }
  return out.join('');
}

registerCanonical(TermRange_, TermRange);

// tslint:disable-next-line:no-unused-variable
var RichTextType__;
(function (RichTextType__) {
  RichTextType__[RichTextType__["HTML"] = 0] = "HTML";
  RichTextType__[RichTextType__["PLAIN"] = 2] = "PLAIN";
})(RichTextType__ || (RichTextType__ = {}));
const RichTextType_ = defineEnumProperties(RichTextType__, [0, 2]);
const RichTextType = extendEnum('RichTextType', [["HTML", 0], ["PLAIN", 2]])(RichTextType_);

function _defineProperty$1W(obj, key, value) { key = _toPropertyKey$1W(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1W(arg) { var key = _toPrimitive$1W(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1W(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class RichText_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RichText field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RichTextType.fromStrictJson_(v, getFieldMap);
          }
          return RichTextType.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message RichText field content.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RichText$1, 'RichText', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RichText to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get content() {
    return this.getFieldById(2, "");
  }
  setContent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteContent() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1W(RichText_, protoName, DEV_MODE ? 'RichText' : '[Unknown]');
let RichText$1 = RichText_;
(function (_RichText) {})(RichText$1 || (RichText$1 = {}));
Object.defineProperties(RichText$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "content": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "content"
      }
    }))
  }
});
Object.defineProperties(RichText$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RichText$1);
  Object.freeze(RichText$1.prototype);
}

function escapeHtmlEntities(s) {
  return s.replace(/[<>&]/g, replaceHtmlSpecialChar);
}
function stringToHtml(s) {
  return s.replace(/[<>&\n]/g, replaceHtmlSpecialChar);
}
function replaceHtmlSpecialChar(c) {
  switch (c) {
    case '<':
      return '&lt;';
    case '>':
      return '&gt;';
    case '&':
      return '&amp;';
    case '"':
      return '&quot;';
    case "'":
      return '&apos;';
    case '\n':
      return '<br />';
  }
  throw new Error(`Unexpected HTML special char "${c}".`);
}

// Note: This is far from perfect and may miss some advanced things, like CDATA
// tags. This is intended to be quick and to the point. Tags are stripped, <br>
// tags are converted to new lines, and <p> tags are converted to double new
// lines.
function htmlToPlainText(html) {
  const out = [];
  let inTag = false;
  let currentTag = '';
  let inWhiteSpace = false;
  let textSeen = false;
  let inAttribute = false;
  let attributeOpen = '';
  let inEntity = false;
  for (let i = 0; i < html.length; i++) {
    const c = html[i];
    switch (c) {
      case '<':
        if (!inTag) {
          inTag = true;
          const m = assert(/^[\w-]*/.exec(html.slice(i + 1)));
          currentTag = m[0].toLowerCase();
          if (currentTag === 'p' && textSeen) {
            out.push('\n\n');
          }
        }
        continue;
      case '>':
        if (inTag) {
          if (currentTag === 'br') {
            out.push('\n');
          }
          inTag = false;
          currentTag = '';
        }
        continue;
      case '"':
      case "'":
        if (inTag) {
          if (!inAttribute) {
            inAttribute = true;
            attributeOpen = c;
          } else if (attributeOpen === c) {
            inAttribute = false;
            attributeOpen = '';
          }
          continue;
        }
    }
    if (inTag) {
      continue;
    }
    switch (c) {
      case '&':
        {
          if (inWhiteSpace) {
            out.push(' ');
            inWhiteSpace = false;
          }
          // TODO(nathan): Support more HTML entities and escaped unicode
          // characters.
          const m = /^&(\w+);/.exec(html.slice(i));
          if (m) {
            inEntity = true;
            out.push(entityToChar(m[1]));
          } else {
            out.push('\ufffd');
          }
          textSeen = true;
          continue;
        }
      case ';':
        if (inEntity) {
          inEntity = false;
          continue;
        }
    }
    if (inEntity) {
      continue;
    }
    if (/\s/.test(c)) {
      if (textSeen) {
        inWhiteSpace = true;
      }
    } else {
      textSeen = true;
      if (inWhiteSpace) {
        out.push(' ');
        inWhiteSpace = false;
      }
      out.push(c);
    }
  }
  return out.join('');
}
function entityToChar(entity) {
  switch (entity) {
    case 'lt':
      return '<';
    case 'gt':
      return '>';
    case 'amp':
      return '&';
    case 'quot':
      return '"';
    case 'apos':
      return "'";
  }
  return '\ufffd';
}

class RichText extends RichText_ {
  isEmpty() {
    switch (this.type) {
      case RichTextType.HTML:
        return isEmptyHtml(this.content);
      case RichTextType.PLAIN:
        return !/\S/.test(this.content);
    }
    assert.fail(emsg`Unknown rich text type: ${this.type}`);
    return false;
  }
  formatAsPlainText() {
    switch (this.type) {
      case RichTextType.HTML:
        return htmlToPlainText(this.content);
      case RichTextType.PLAIN:
        return this.content;
    }
    assert.fail(emsg`Unknown rich text type: ${this.type}`);
    return '';
  }
  formatAsHtml() {
    switch (this.type) {
      case RichTextType.HTML:
        return this.content;
      case RichTextType.PLAIN:
        return stringToHtml(this.content);
    }
    assert.fail(emsg`Unknown rich text type: ${this.type}`);
    return '';
  }
}
function isEmptyHtml(content) {
  return !/\S/.test(htmlToPlainText(content));
}

registerCanonical(RichText_, RichText);

function _defineProperty$1V(obj, key, value) { key = _toPropertyKey$1V(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1V(arg) { var key = _toPrimitive$1V(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1V(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OfferExpectations_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: RichText.shape,
      2: TermRange.shape,
      3: 0,
      4: 0,
      5: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferExpectations field notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TermRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferExpectations field loan_term_range.
                Expected type TermRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TermRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferExpectations field repayment_schedule.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ExpectedRepaymentSchedule.fromStrictJson_(v, getFieldMap);
          }
          return ExpectedRepaymentSchedule.from(value);
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferExpectations field recourse.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ExpectedRecourse.fromStrictJson_(v, getFieldMap);
          }
          return ExpectedRecourse.from(value);
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferExpectations field interest_rate.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ExpectedInterestRate.fromStrictJson_(v, getFieldMap);
          }
          return ExpectedInterestRate.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferExpectations$1, 'OfferExpectations', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferExpectations to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get notes() {
    return this.getFieldById(1, RichText.getDefault());
  }
  setNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteNotes() {
    return this.setValueFieldById(1, null, null);
  }
  get loanTermRange() {
    return this.getFieldById(2, TermRange.getDefault());
  }
  setLoanTermRange(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLoanTermRange() {
    return this.setValueFieldById(2, null, null);
  }
  get repaymentSchedule() {
    return this.getFieldById(3, 0);
  }
  setRepaymentSchedule(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteRepaymentSchedule() {
    return this.setValueFieldById(3, null, null);
  }
  get recourse() {
    return this.getFieldById(4, 0);
  }
  setRecourse(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteRecourse() {
    return this.setValueFieldById(4, null, null);
  }
  get interestRate() {
    return this.getFieldById(5, 0);
  }
  setInterestRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteInterestRate() {
    return this.setValueFieldById(5, null, null);
  }
}
_defineProperty$1V(OfferExpectations_, protoName, DEV_MODE ? 'OfferExpectations' : '[Unknown]');
let OfferExpectations$1 = OfferExpectations_;
(function (_OfferExpectations) {})(OfferExpectations$1 || (OfferExpectations$1 = {}));
Object.defineProperties(OfferExpectations$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "notes": {
        value: 1
      },
      "loan_term_range": {
        value: 2
      },
      "loanTermRange": {
        value: 2
      },
      "repayment_schedule": {
        value: 3
      },
      "repaymentSchedule": {
        value: 3
      },
      "recourse": {
        value: 4
      },
      "interest_rate": {
        value: 5
      },
      "interestRate": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "notes"
      },
      '2': {
        value: "loan_term_range"
      },
      '3': {
        value: "repayment_schedule"
      },
      '4': {
        value: "recourse"
      },
      '5': {
        value: "interest_rate"
      }
    }))
  }
});
Object.defineProperties(OfferExpectations$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: RichText.prototype,
      2: TermRange.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferExpectations$1);
  Object.freeze(OfferExpectations$1.prototype);
}

function expectedInterestRateToString(value) {
  return ExpectedInterestRate.format(value);
}

function expectedRecourseToString(value) {
  return ExpectedRecourse.format(value);
}
function expectedRecourseToRecourse(value) {
  return ExpectedRecourse.toRecourseType(value);
}

function expectedRepaymentScheduleToString(value) {
  return ExpectedRepaymentSchedule.format(value);
}
function expectedRepaymentScheduleToTerm(value) {
  switch (value) {
    case ExpectedRepaymentSchedule.FIFTEEN_YEARS:
      return Term.ofYears(15);
    case ExpectedRepaymentSchedule.TWENTY_YEARS:
      return Term.ofYears(20);
    case ExpectedRepaymentSchedule.TWENTY_FIVE_YEARS:
      return Term.ofYears(25);
    case ExpectedRepaymentSchedule.THIRTY_PLUS_YEARS:
      return Term.ofYears(30);
  }
  assert.fail(emsg`No way to convert repayment schedule (${value}) to term`);
  return Term.getDefault();
}

class OfferExpectations extends OfferExpectations_ {
  formatInterestRate() {
    return ExpectedInterestRate.format(this.interestRate);
  }
  formatRepaymentSchedule() {
    return ExpectedRepaymentSchedule.format(this.repaymentSchedule);
  }
  formatRecourse() {
    return ExpectedRecourse.format(this.recourse);
  }
}

registerCanonical(OfferExpectations_, OfferExpectations);

function _defineProperty$1U(obj, key, value) { key = _toPropertyKey$1U(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1U(arg) { var key = _toPrimitive$1U(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1U(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectFee_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Percent.shape,
      2: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectFee field rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectFee field minimum.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectFee, 'ProjectFee', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectFee to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get rate() {
    return this.getFieldById(1, Percent.getDefault());
  }
  setRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteRate() {
    return this.setValueFieldById(1, null, null);
  }
  get minimum() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setMinimum(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteMinimum() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1U(ProjectFee_, protoName, DEV_MODE ? 'ProjectFee' : '[Unknown]');
let ProjectFee = ProjectFee_;
(function (_ProjectFee) {})(ProjectFee || (ProjectFee = {}));
Object.defineProperties(ProjectFee, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "rate": {
        value: 1
      },
      "minimum": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "rate"
      },
      '2': {
        value: "minimum"
      }
    }))
  }
});
Object.defineProperties(ProjectFee.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Percent.prototype,
      2: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectFee);
  Object.freeze(ProjectFee.prototype);
}

function _defineProperty$1T(obj, key, value) { key = _toPropertyKey$1T(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1T(arg) { var key = _toPrimitive$1T(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1T(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectLostReason_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 1,
      3: 1,
      4: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectLostReason field reason.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectLostReason field funded.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectLostReason field visible_to_sponsor.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectLostReason field choosable_by_sponsor.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectLostReason, 'ProjectLostReason', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectLostReason to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get reason() {
    return this.getFieldById(1, "");
  }
  setReason(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteReason() {
    return this.setValueFieldById(1, null, null);
  }
  get funded() {
    return this.getFieldById(2, false);
  }
  setFunded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteFunded() {
    return this.setValueFieldById(2, null, null);
  }
  get visibleToSponsor() {
    return this.getFieldById(3, false);
  }
  setVisibleToSponsor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteVisibleToSponsor() {
    return this.setValueFieldById(3, null, null);
  }
  get choosableBySponsor() {
    return this.getFieldById(4, false);
  }
  setChoosableBySponsor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteChoosableBySponsor() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$1T(ProjectLostReason_, protoName, DEV_MODE ? 'ProjectLostReason' : '[Unknown]');
let ProjectLostReason = ProjectLostReason_;
(function (_ProjectLostReason) {})(ProjectLostReason || (ProjectLostReason = {}));
Object.defineProperties(ProjectLostReason, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "reason": {
        value: 1
      },
      "funded": {
        value: 2
      },
      "visible_to_sponsor": {
        value: 3
      },
      "visibleToSponsor": {
        value: 3
      },
      "choosable_by_sponsor": {
        value: 4
      },
      "choosableBySponsor": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "reason"
      },
      '2': {
        value: "funded"
      },
      '3': {
        value: "visible_to_sponsor"
      },
      '4': {
        value: "choosable_by_sponsor"
      }
    }))
  }
});
Object.defineProperties(ProjectLostReason.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectLostReason);
  Object.freeze(ProjectLostReason.prototype);
}

// tslint:disable-next-line:no-unused-variable
var ProjectStage__;
(function (ProjectStage__) {
  ProjectStage__[ProjectStage__["DRAFT"] = 0] = "DRAFT";
  ProjectStage__[ProjectStage__["ACCEPTING_OFFERS"] = 1] = "ACCEPTING_OFFERS";
  ProjectStage__[ProjectStage__["DUE_DILIGENCE"] = 2] = "DUE_DILIGENCE";
})(ProjectStage__ || (ProjectStage__ = {}));
const ProjectStage_ = defineEnumProperties(ProjectStage__, [0, 1, 2]);
const ProjectStage = extendEnum('ProjectStage', [["DRAFT", 0], ["ACCEPTING_OFFERS", 1], ["DUE_DILIGENCE", 2]])(ProjectStage_);

// tslint:disable-next-line:no-unused-variable
var ProjectStatus__;
(function (ProjectStatus__) {
  ProjectStatus__[ProjectStatus__["OPEN"] = 0] = "OPEN";
  ProjectStatus__[ProjectStatus__["WON"] = 1] = "WON";
  ProjectStatus__[ProjectStatus__["LOST"] = 2] = "LOST";
})(ProjectStatus__ || (ProjectStatus__ = {}));
const ProjectStatus_ = defineEnumProperties(ProjectStatus__, [0, 1, 2]);
const ProjectStatus = extendEnum('ProjectStatus', [["OPEN", 0], ["WON", 1], ["LOST", 2]])(ProjectStatus_);

function _defineProperty$1S(obj, key, value) { key = _toPropertyKey$1S(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1S(arg) { var key = _toPrimitive$1S(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1S(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _PropertyDemographics_StudentsOccupancy_;
(function (_PropertyDemographics_StudentsOccupancy_) {
  _PropertyDemographics_StudentsOccupancy_[_PropertyDemographics_StudentsOccupancy_["UNSET"] = 0] = "UNSET";
  _PropertyDemographics_StudentsOccupancy_[_PropertyDemographics_StudentsOccupancy_["STUDENTS"] = 1] = "STUDENTS";
})(_PropertyDemographics_StudentsOccupancy_ || (_PropertyDemographics_StudentsOccupancy_ = {}));
const _PropertyDemographics_StudentsOccupancy = defineEnumProperties(_PropertyDemographics_StudentsOccupancy_, [1]);
var _PropertyDemographics_MilitaryOccupancy_;
(function (_PropertyDemographics_MilitaryOccupancy_) {
  _PropertyDemographics_MilitaryOccupancy_[_PropertyDemographics_MilitaryOccupancy_["UNSET"] = 0] = "UNSET";
  _PropertyDemographics_MilitaryOccupancy_[_PropertyDemographics_MilitaryOccupancy_["MILITARY"] = 2] = "MILITARY";
})(_PropertyDemographics_MilitaryOccupancy_ || (_PropertyDemographics_MilitaryOccupancy_ = {}));
const _PropertyDemographics_MilitaryOccupancy = defineEnumProperties(_PropertyDemographics_MilitaryOccupancy_, [2]);
var _PropertyDemographics_MajorityEmployerName_;
(function (_PropertyDemographics_MajorityEmployerName_) {
  _PropertyDemographics_MajorityEmployerName_[_PropertyDemographics_MajorityEmployerName_["UNSET"] = 0] = "UNSET";
  _PropertyDemographics_MajorityEmployerName_[_PropertyDemographics_MajorityEmployerName_["MAJORITY_EMPLOYER"] = 3] = "MAJORITY_EMPLOYER";
})(_PropertyDemographics_MajorityEmployerName_ || (_PropertyDemographics_MajorityEmployerName_ = {}));
const _PropertyDemographics_MajorityEmployerName = defineEnumProperties(_PropertyDemographics_MajorityEmployerName_, [3]);
class PropertyDemographics_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Occupancy.shape,
      2: Occupancy.shape,
      3: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Occupancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PropertyDemographics field students.
                Expected type Occupancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Occupancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Occupancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PropertyDemographics field military.
                Expected type Occupancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Occupancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message PropertyDemographics field majority_employer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PropertyDemographics, 'PropertyDemographics', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PropertyDemographics to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get StudentsOccupancyCase() {
    return _PropertyDemographics_StudentsOccupancy;
  }
  get studentsOccupancyCase() {
    return this.getCaseFromIds([1]);
  }
  static get MilitaryOccupancyCase() {
    return _PropertyDemographics_MilitaryOccupancy;
  }
  get militaryOccupancyCase() {
    return this.getCaseFromIds([2]);
  }
  static get MajorityEmployerNameCase() {
    return _PropertyDemographics_MajorityEmployerName;
  }
  get majorityEmployerNameCase() {
    return this.getCaseFromIds([3]);
  }
  get students() {
    return this.getFieldById(1, Occupancy.getDefault());
  }
  setStudents(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "students_occupancy");
  }
  deleteStudents() {
    return this.setValueFieldById(1, null, "students_occupancy");
  }
  get military() {
    return this.getFieldById(2, Occupancy.getDefault());
  }
  setMilitary(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "military_occupancy");
  }
  deleteMilitary() {
    return this.setValueFieldById(2, null, "military_occupancy");
  }
  get majorityEmployer() {
    return this.getFieldById(3, "");
  }
  setMajorityEmployer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "majority_employer_name");
  }
  deleteMajorityEmployer() {
    return this.setValueFieldById(3, null, "majority_employer_name");
  }
}
_defineProperty$1S(PropertyDemographics_, protoName, DEV_MODE ? 'PropertyDemographics' : '[Unknown]');
let PropertyDemographics = PropertyDemographics_;
(function (_PropertyDemographics) {})(PropertyDemographics || (PropertyDemographics = {}));
Object.defineProperties(PropertyDemographics, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "students": {
        value: 1
      },
      "military": {
        value: 2
      },
      "majority_employer": {
        value: 3
      },
      "majorityEmployer": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "students"
      },
      '2': {
        value: "military"
      },
      '3': {
        value: "majority_employer"
      }
    }))
  }
});
Object.defineProperties(PropertyDemographics.prototype, {
  oneofs_: {
    value: {
      "students_occupancy": [1],
      "military_occupancy": [2],
      "majority_employer_name": [3]
    }
  },
  prototypes_: {
    value: {
      1: Occupancy.prototype,
      2: Occupancy.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PropertyDemographics);
  Object.freeze(PropertyDemographics.prototype);
}

// tslint:disable-next-line:no-unused-variable
var DisclaimerItem__;
(function (DisclaimerItem__) {
  DisclaimerItem__[DisclaimerItem__["UNSET"] = 0] = "UNSET";
  DisclaimerItem__[DisclaimerItem__["YES"] = 1] = "YES";
  DisclaimerItem__[DisclaimerItem__["NO"] = 2] = "NO";
})(DisclaimerItem__ || (DisclaimerItem__ = {}));
const DisclaimerItem_ = defineEnumProperties(DisclaimerItem__, [0, 1, 2]);
const DisclaimerItem = extendEnum('DisclaimerItem', [["UNSET", 0], ["YES", 1], ["NO", 2]])(DisclaimerItem_);

function _defineProperty$1R(obj, key, value) { key = _toPropertyKey$1R(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1R(arg) { var key = _toPrimitive$1R(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1R(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class PropertyDisclaimer_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 0,
      10: 0,
      11: 0,
      12: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field major_crimes.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field lesser_crimes.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field taxes_delinquent.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field payments_delinquent.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field environmental_hazards.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field affordability_issues.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field flood_zone.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field seismic_zone.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field partial_units.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field sponsor_lawsuits.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field sponsor_felony.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DisclaimerItem.fromStrictJson_(v, getFieldMap);
          }
          return DisclaimerItem.from(value);
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PropertyDisclaimer field sponsor_credit_score.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return CreditScore.fromStrictJson_(v, getFieldMap);
          }
          return CreditScore.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PropertyDisclaimer, 'PropertyDisclaimer', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PropertyDisclaimer to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get majorCrimes() {
    return this.getFieldById(1, 0);
  }
  setMajorCrimes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteMajorCrimes() {
    return this.setValueFieldById(1, null, null);
  }
  get lesserCrimes() {
    return this.getFieldById(2, 0);
  }
  setLesserCrimes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLesserCrimes() {
    return this.setValueFieldById(2, null, null);
  }
  get taxesDelinquent() {
    return this.getFieldById(3, 0);
  }
  setTaxesDelinquent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteTaxesDelinquent() {
    return this.setValueFieldById(3, null, null);
  }
  get paymentsDelinquent() {
    return this.getFieldById(4, 0);
  }
  setPaymentsDelinquent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deletePaymentsDelinquent() {
    return this.setValueFieldById(4, null, null);
  }
  get environmentalHazards() {
    return this.getFieldById(5, 0);
  }
  setEnvironmentalHazards(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteEnvironmentalHazards() {
    return this.setValueFieldById(5, null, null);
  }
  get affordabilityIssues() {
    return this.getFieldById(6, 0);
  }
  setAffordabilityIssues(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteAffordabilityIssues() {
    return this.setValueFieldById(6, null, null);
  }
  get floodZone() {
    return this.getFieldById(7, 0);
  }
  setFloodZone(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteFloodZone() {
    return this.setValueFieldById(7, null, null);
  }
  get seismicZone() {
    return this.getFieldById(8, 0);
  }
  setSeismicZone(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteSeismicZone() {
    return this.setValueFieldById(8, null, null);
  }
  get partialUnits() {
    return this.getFieldById(9, 0);
  }
  setPartialUnits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deletePartialUnits() {
    return this.setValueFieldById(9, null, null);
  }
  get sponsorLawsuits() {
    return this.getFieldById(10, 0);
  }
  setSponsorLawsuits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteSponsorLawsuits() {
    return this.setValueFieldById(10, null, null);
  }
  get sponsorFelony() {
    return this.getFieldById(11, 0);
  }
  setSponsorFelony(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteSponsorFelony() {
    return this.setValueFieldById(11, null, null);
  }
  get sponsorCreditScore() {
    return this.getFieldById(12, 0);
  }
  setSponsorCreditScore(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteSponsorCreditScore() {
    return this.setValueFieldById(12, null, null);
  }
}
_defineProperty$1R(PropertyDisclaimer_, protoName, DEV_MODE ? 'PropertyDisclaimer' : '[Unknown]');
let PropertyDisclaimer = PropertyDisclaimer_;
(function (_PropertyDisclaimer) {})(PropertyDisclaimer || (PropertyDisclaimer = {}));
Object.defineProperties(PropertyDisclaimer, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "major_crimes": {
        value: 1
      },
      "majorCrimes": {
        value: 1
      },
      "lesser_crimes": {
        value: 2
      },
      "lesserCrimes": {
        value: 2
      },
      "taxes_delinquent": {
        value: 3
      },
      "taxesDelinquent": {
        value: 3
      },
      "payments_delinquent": {
        value: 4
      },
      "paymentsDelinquent": {
        value: 4
      },
      "environmental_hazards": {
        value: 5
      },
      "environmentalHazards": {
        value: 5
      },
      "affordability_issues": {
        value: 6
      },
      "affordabilityIssues": {
        value: 6
      },
      "flood_zone": {
        value: 7
      },
      "floodZone": {
        value: 7
      },
      "seismic_zone": {
        value: 8
      },
      "seismicZone": {
        value: 8
      },
      "partial_units": {
        value: 9
      },
      "partialUnits": {
        value: 9
      },
      "sponsor_lawsuits": {
        value: 10
      },
      "sponsorLawsuits": {
        value: 10
      },
      "sponsor_felony": {
        value: 11
      },
      "sponsorFelony": {
        value: 11
      },
      "sponsor_credit_score": {
        value: 12
      },
      "sponsorCreditScore": {
        value: 12
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "major_crimes"
      },
      '2': {
        value: "lesser_crimes"
      },
      '3': {
        value: "taxes_delinquent"
      },
      '4': {
        value: "payments_delinquent"
      },
      '5': {
        value: "environmental_hazards"
      },
      '6': {
        value: "affordability_issues"
      },
      '7': {
        value: "flood_zone"
      },
      '8': {
        value: "seismic_zone"
      },
      '9': {
        value: "partial_units"
      },
      '10': {
        value: "sponsor_lawsuits"
      },
      '11': {
        value: "sponsor_felony"
      },
      '12': {
        value: "sponsor_credit_score"
      }
    }))
  }
});
Object.defineProperties(PropertyDisclaimer.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PropertyDisclaimer);
  Object.freeze(PropertyDisclaimer.prototype);
}

// tslint:disable-next-line:no-unused-variable
var LegalEntityType__;
(function (LegalEntityType__) {
  LegalEntityType__[LegalEntityType__["INDIVIDUAL"] = 0] = "INDIVIDUAL";
  LegalEntityType__[LegalEntityType__["LLC"] = 1] = "LLC";
  LegalEntityType__[LegalEntityType__["CORPORATION"] = 2] = "CORPORATION";
})(LegalEntityType__ || (LegalEntityType__ = {}));
const LegalEntityType_ = defineEnumProperties(LegalEntityType__, [0, 1, 2]);
const LegalEntityType = extendEnum('LegalEntityType', [["INDIVIDUAL", 0], ["LLC", 1], ["CORPORATION", 2]])(LegalEntityType_);

function _defineProperty$1Q(obj, key, value) { key = _toPropertyKey$1Q(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1Q(arg) { var key = _toPrimitive$1Q(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1Q(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class StandardUsAddress_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: 2,
      4: 2,
      5: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message StandardUsAddress field line1.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message StandardUsAddress field line2.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message StandardUsAddress field city.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message StandardUsAddress field state.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message StandardUsAddress field zip.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(StandardUsAddress$1, 'StandardUsAddress', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message StandardUsAddress to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get line1() {
    return this.getFieldById(1, "");
  }
  setLine1(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteLine1() {
    return this.setValueFieldById(1, null, null);
  }
  get line2() {
    return this.getFieldById(2, "");
  }
  setLine2(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLine2() {
    return this.setValueFieldById(2, null, null);
  }
  get city() {
    return this.getFieldById(3, "");
  }
  setCity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteCity() {
    return this.setValueFieldById(3, null, null);
  }
  get state() {
    return this.getFieldById(4, "");
  }
  setState(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteState() {
    return this.setValueFieldById(4, null, null);
  }
  get zip() {
    return this.getFieldById(5, "");
  }
  setZip(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteZip() {
    return this.setValueFieldById(5, null, null);
  }
}
_defineProperty$1Q(StandardUsAddress_, protoName, DEV_MODE ? 'StandardUsAddress' : '[Unknown]');
let StandardUsAddress$1 = StandardUsAddress_;
(function (_StandardUsAddress) {})(StandardUsAddress$1 || (StandardUsAddress$1 = {}));
Object.defineProperties(StandardUsAddress$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "line1": {
        value: 1
      },
      "line2": {
        value: 2
      },
      "city": {
        value: 3
      },
      "state": {
        value: 4
      },
      "zip": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "line1"
      },
      '2': {
        value: "line2"
      },
      '3': {
        value: "city"
      },
      '4': {
        value: "state"
      },
      '5': {
        value: "zip"
      }
    }))
  }
});
Object.defineProperties(StandardUsAddress$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(StandardUsAddress$1);
  Object.freeze(StandardUsAddress$1.prototype);
}

class StandardUsAddress extends StandardUsAddress_ {
  // An address is considered empty if it has a `line2` but no other
  // information. (This really shouldn't happen. We should try to guard against
  // it.)
  isEmpty() {
    return !(this.line1 || this.city || this.state || this.zip);
  }
  formatLines() {
    const out = [];
    const line1 = clean(this.line1);
    if (line1) {
      out.push(line1);
    }
    const line2 = clean(this.line2);
    if (line2) {
      out.push(line2);
    }
    const line3 = [];
    const cityAndState = this.formatCityAndState();
    if (cityAndState) {
      line3.push(cityAndState);
    }
    const zip = this.zip.replace(/\D/g, '');
    if (zip) {
      line3.push(zip);
    }
    if (line3.length > 0) {
      out.push(line3.join(' '));
    }
    return out;
  }
  formatCityAndState() {
    const out = [];
    if (this.city) {
      out.push(this.city);
    }
    if (this.state) {
      out.push(this.state);
    }
    return clean(out.join(', '));
  }
  formatOneLine() {
    return this.formatLines().join(', ');
  }
}
function clean(s) {
  return s.replace(/\s+/, ' ').trim();
}

registerCanonical(StandardUsAddress_, StandardUsAddress);

// tslint:disable-next-line:no-unused-variable
var UsCitizenshipStatus__;
(function (UsCitizenshipStatus__) {
  UsCitizenshipStatus__[UsCitizenshipStatus__["OTHER"] = 0] = "OTHER";
  UsCitizenshipStatus__[UsCitizenshipStatus__["US_CITIZEN"] = 1] = "US_CITIZEN";
  UsCitizenshipStatus__[UsCitizenshipStatus__["IMMIGRANT_LEGAL"] = 2] = "IMMIGRANT_LEGAL";
  UsCitizenshipStatus__[UsCitizenshipStatus__["NON_IMMIGRANT_VISA"] = 3] = "NON_IMMIGRANT_VISA";
})(UsCitizenshipStatus__ || (UsCitizenshipStatus__ = {}));
const UsCitizenshipStatus_ = defineEnumProperties(UsCitizenshipStatus__, [0, 1, 2, 3]);
const UsCitizenshipStatus = extendEnum('UsCitizenshipStatus', [["OTHER", 0], ["US_CITIZEN", 1], ["IMMIGRANT_LEGAL", 2], ["NON_IMMIGRANT_VISA", 3]])(UsCitizenshipStatus_);

function _defineProperty$1P(obj, key, value) { key = _toPropertyKey$1P(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1P(arg) { var key = _toPrimitive$1P(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1P(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectGuarantor_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 5,
      2: 2,
      3: 0,
      5: 0,
      6: StandardUsAddress.shape,
      7: CurrencyValue.shape,
      9: CurrencyValue.shape,
      8: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectGuarantor field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectGuarantor field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectGuarantor field entity_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LegalEntityType.fromStrictJson_(v, getFieldMap);
          }
          return LegalEntityType.from(value);
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectGuarantor field us_citizenship.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return UsCitizenshipStatus.fromStrictJson_(v, getFieldMap);
          }
          return UsCitizenshipStatus.from(value);
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectGuarantor field address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectGuarantor field net_worth.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectGuarantor field liquidity.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectGuarantor field credit_score.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return CreditScore.fromStrictJson_(v, getFieldMap);
          }
          return CreditScore.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectGuarantor, 'ProjectGuarantor', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectGuarantor to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get entityType() {
    return this.getFieldById(3, 0);
  }
  setEntityType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteEntityType() {
    return this.setValueFieldById(3, null, null);
  }
  get usCitizenship() {
    return this.getFieldById(5, 0);
  }
  setUsCitizenship(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteUsCitizenship() {
    return this.setValueFieldById(5, null, null);
  }
  get address() {
    return this.getFieldById(6, StandardUsAddress.getDefault());
  }
  setAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteAddress() {
    return this.setValueFieldById(6, null, null);
  }
  get netWorth() {
    return this.getFieldById(7, CurrencyValue.getDefault());
  }
  setNetWorth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteNetWorth() {
    return this.setValueFieldById(7, null, null);
  }
  get liquidity() {
    return this.getFieldById(9, CurrencyValue.getDefault());
  }
  setLiquidity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteLiquidity() {
    return this.setValueFieldById(9, null, null);
  }
  get creditScore() {
    return this.getFieldById(8, 0);
  }
  setCreditScore(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteCreditScore() {
    return this.setValueFieldById(8, null, null);
  }
}
_defineProperty$1P(ProjectGuarantor_, protoName, DEV_MODE ? 'ProjectGuarantor' : '[Unknown]');
let ProjectGuarantor = ProjectGuarantor_;
(function (_ProjectGuarantor) {})(ProjectGuarantor || (ProjectGuarantor = {}));
Object.defineProperties(ProjectGuarantor, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "name": {
        value: 2
      },
      "entity_type": {
        value: 3
      },
      "entityType": {
        value: 3
      },
      "us_citizenship": {
        value: 5
      },
      "usCitizenship": {
        value: 5
      },
      "address": {
        value: 6
      },
      "net_worth": {
        value: 7
      },
      "netWorth": {
        value: 7
      },
      "liquidity": {
        value: 9
      },
      "credit_score": {
        value: 8
      },
      "creditScore": {
        value: 8
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "entity_type"
      },
      '5': {
        value: "us_citizenship"
      },
      '6': {
        value: "address"
      },
      '7': {
        value: "net_worth"
      },
      '9': {
        value: "liquidity"
      },
      '8': {
        value: "credit_score"
      }
    }))
  }
});
Object.defineProperties(ProjectGuarantor.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      6: StandardUsAddress.prototype,
      7: CurrencyValue.prototype,
      9: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectGuarantor);
  Object.freeze(ProjectGuarantor.prototype);
}
class RepeatedProjectGuarantor_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: ProjectGuarantor.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectGuarantor);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedProjectGuarantor field value.
                Expected type ProjectGuarantor.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectGuarantor', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedProjectGuarantor, 'RepeatedProjectGuarantor', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedProjectGuarantor to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1P(RepeatedProjectGuarantor_, protoName, DEV_MODE ? 'RepeatedProjectGuarantor' : '[Unknown]');
let RepeatedProjectGuarantor = RepeatedProjectGuarantor_;
(function (_RepeatedProjectGuarantor) {})(RepeatedProjectGuarantor || (RepeatedProjectGuarantor = {}));
Object.defineProperties(RepeatedProjectGuarantor, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedProjectGuarantor.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: ProjectGuarantor.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedProjectGuarantor);
  Object.freeze(RepeatedProjectGuarantor.prototype);
}

// tslint:disable-next-line:no-unused-variable
var PropertyTitle__;
(function (PropertyTitle__) {
  PropertyTitle__[PropertyTitle__["UNSET"] = 0] = "UNSET";
  PropertyTitle__[PropertyTitle__["FEE_SIMPLE"] = 1] = "FEE_SIMPLE";
  PropertyTitle__[PropertyTitle__["LEASEHOLD"] = 2] = "LEASEHOLD";
})(PropertyTitle__ || (PropertyTitle__ = {}));
const PropertyTitle_ = defineEnumProperties(PropertyTitle__, [0, 1, 2]);
const PropertyTitle$1 = extendEnum('PropertyTitle', [["UNSET", 0], ["FEE_SIMPLE", 1], ["LEASEHOLD", 2]])(PropertyTitle_);

const PropertyTitle = {
  ...PropertyTitle$1,
  format(value) {
    switch (value) {
      case PropertyTitle.UNSET:
        return '';
      case PropertyTitle.FEE_SIMPLE:
        return 'Fee Simple';
      case PropertyTitle.LEASEHOLD:
        return 'Leasehold';
    }
    assert.fail(emsg`Unknown property title: ${value}`);
    return '';
  }
};

registerCanonical(PropertyTitle$1, PropertyTitle);

// tslint:disable-next-line:no-unused-variable
var SponsorType__;
(function (SponsorType__) {
  SponsorType__[SponsorType__["COMPANY"] = 0] = "COMPANY";
  SponsorType__[SponsorType__["INDIVIDUAL"] = 1] = "INDIVIDUAL";
})(SponsorType__ || (SponsorType__ = {}));
const SponsorType_ = defineEnumProperties(SponsorType__, [0, 1]);
const SponsorType$1 = extendEnum('SponsorType', [["COMPANY", 0], ["INDIVIDUAL", 1]])(SponsorType_);

const SponsorType = {
  ...SponsorType$1,
  format(value) {
    switch (value) {
      case SponsorType.COMPANY:
        return 'Company';
      case SponsorType.INDIVIDUAL:
        return 'Individual';
    }
    assert.fail(emsg`Unknown sponsor type: ${value}`);
    return '';
  }
};

registerCanonical(SponsorType$1, SponsorType);

function _defineProperty$1O(obj, key, value) { key = _toPropertyKey$1O(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1O(arg) { var key = _toPrimitive$1O(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1O(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _SponsorProfile_YearFoundedOrYearsOfExperience_;
(function (_SponsorProfile_YearFoundedOrYearsOfExperience_) {
  _SponsorProfile_YearFoundedOrYearsOfExperience_[_SponsorProfile_YearFoundedOrYearsOfExperience_["UNSET"] = 0] = "UNSET";
  _SponsorProfile_YearFoundedOrYearsOfExperience_[_SponsorProfile_YearFoundedOrYearsOfExperience_["YEAR_FOUNDED"] = 3] = "YEAR_FOUNDED";
  _SponsorProfile_YearFoundedOrYearsOfExperience_[_SponsorProfile_YearFoundedOrYearsOfExperience_["YEARS_OF_EXPERIENCE"] = 8] = "YEARS_OF_EXPERIENCE";
})(_SponsorProfile_YearFoundedOrYearsOfExperience_ || (_SponsorProfile_YearFoundedOrYearsOfExperience_ = {}));
const _SponsorProfile_YearFoundedOrYearsOfExperience = defineEnumProperties(_SponsorProfile_YearFoundedOrYearsOfExperience_, [3, 8]);
class SponsorProfile_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: 7,
      8: 7,
      4: StandardUsAddress.shape,
      5: CurrencyValue.shape,
      6: RichText.shape,
      7: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SponsorProfile field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SponsorProfile field website.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message SponsorProfile field year_founded.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message SponsorProfile field years_of_experience.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message SponsorProfile field address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message SponsorProfile field assets_under_ownership.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message SponsorProfile field description.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message SponsorProfile field sponsor_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return SponsorType.fromStrictJson_(v, getFieldMap);
          }
          return SponsorType.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SponsorProfile$1, 'SponsorProfile', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SponsorProfile to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get YearFoundedOrYearsOfExperienceCase() {
    return _SponsorProfile_YearFoundedOrYearsOfExperience;
  }
  get yearFoundedOrYearsOfExperienceCase() {
    return this.getCaseFromIds([3, 8]);
  }
  get name() {
    return this.getFieldById(1, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteName() {
    return this.setValueFieldById(1, null, null);
  }
  get website() {
    return this.getFieldById(2, "");
  }
  setWebsite(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteWebsite() {
    return this.setValueFieldById(2, null, null);
  }
  get yearFounded() {
    return this.getFieldById(3, 0);
  }
  setYearFounded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "year_founded_or_years_of_experience");
  }
  deleteYearFounded() {
    return this.setValueFieldById(3, null, "year_founded_or_years_of_experience");
  }
  get yearsOfExperience() {
    return this.getFieldById(8, 0);
  }
  setYearsOfExperience(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, "year_founded_or_years_of_experience");
  }
  deleteYearsOfExperience() {
    return this.setValueFieldById(8, null, "year_founded_or_years_of_experience");
  }
  get address() {
    return this.getFieldById(4, StandardUsAddress.getDefault());
  }
  setAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteAddress() {
    return this.setValueFieldById(4, null, null);
  }
  get assetsUnderOwnership() {
    return this.getFieldById(5, CurrencyValue.getDefault());
  }
  setAssetsUnderOwnership(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteAssetsUnderOwnership() {
    return this.setValueFieldById(5, null, null);
  }
  get description() {
    return this.getFieldById(6, RichText.getDefault());
  }
  setDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteDescription() {
    return this.setValueFieldById(6, null, null);
  }
  get sponsorType() {
    return this.getFieldById(7, 0);
  }
  setSponsorType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteSponsorType() {
    return this.setValueFieldById(7, null, null);
  }
}
_defineProperty$1O(SponsorProfile_, protoName, DEV_MODE ? 'SponsorProfile' : '[Unknown]');
let SponsorProfile$1 = SponsorProfile_;
(function (_SponsorProfile) {})(SponsorProfile$1 || (SponsorProfile$1 = {}));
Object.defineProperties(SponsorProfile$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "name": {
        value: 1
      },
      "website": {
        value: 2
      },
      "year_founded": {
        value: 3
      },
      "yearFounded": {
        value: 3
      },
      "years_of_experience": {
        value: 8
      },
      "yearsOfExperience": {
        value: 8
      },
      "address": {
        value: 4
      },
      "assets_under_ownership": {
        value: 5
      },
      "assetsUnderOwnership": {
        value: 5
      },
      "description": {
        value: 6
      },
      "sponsor_type": {
        value: 7
      },
      "sponsorType": {
        value: 7
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "name"
      },
      '2': {
        value: "website"
      },
      '3': {
        value: "year_founded"
      },
      '8': {
        value: "years_of_experience"
      },
      '4': {
        value: "address"
      },
      '5': {
        value: "assets_under_ownership"
      },
      '6': {
        value: "description"
      },
      '7': {
        value: "sponsor_type"
      }
    }))
  }
});
Object.defineProperties(SponsorProfile$1.prototype, {
  oneofs_: {
    value: {
      "year_founded_or_years_of_experience": [3, 8]
    }
  },
  prototypes_: {
    value: {
      4: StandardUsAddress.prototype,
      5: CurrencyValue.prototype,
      6: RichText.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SponsorProfile$1);
  Object.freeze(SponsorProfile$1.prototype);
}

function sponsorTypeToCapitalString(sponsorType) {
  switch (sponsorType) {
    case SponsorType.COMPANY:
      return 'Company';
    case SponsorType.INDIVIDUAL:
      return 'Individual';
  }
  assert.fail(emsg`Unknown sponsor type: ${sponsorType}`);
  return '';
}

class SponsorProfile extends SponsorProfile_ {
  formatSponsorType() {
    return SponsorType.format(this.sponsorType);
  }
}

registerCanonical(SponsorProfile_, SponsorProfile);

// tslint:disable-next-line:no-unused-variable
var SponsorRole__;
(function (SponsorRole__) {
  SponsorRole__[SponsorRole__["INVESTOR"] = 0] = "INVESTOR";
  SponsorRole__[SponsorRole__["OWNER_OCCUPIER"] = 1] = "OWNER_OCCUPIER";
})(SponsorRole__ || (SponsorRole__ = {}));
const SponsorRole_ = defineEnumProperties(SponsorRole__, [0, 1]);
const SponsorRole = extendEnum('SponsorRole', [["INVESTOR", 0], ["OWNER_OCCUPIER", 1]])(SponsorRole_);

function _defineProperty$1N(obj, key, value) { key = _toPropertyKey$1N(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1N(arg) { var key = _toPrimitive$1N(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1N(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var UnderwritingApprovalRequirement__;
(function (UnderwritingApprovalRequirement__) {
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["VERIFY_SPONSOR_EMAIL"] = 0] = "VERIFY_SPONSOR_EMAIL";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["SPONSOR_BIO"] = 1] = "SPONSOR_BIO";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["ASSET_PICTURES"] = 2] = "ASSET_PICTURES";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["STACKSOURCE_AGREEMENT"] = 3] = "STACKSOURCE_AGREEMENT";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["PRO_FORMA"] = 4] = "PRO_FORMA";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["FINANCIAL_STATEMENTS"] = 5] = "FINANCIAL_STATEMENTS";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["RENT_ROLL"] = 6] = "RENT_ROLL";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["TENANT_LEASE"] = 7] = "TENANT_LEASE";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["SALES_CONTRACT"] = 8] = "SALES_CONTRACT";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["SALES_COMPS"] = 9] = "SALES_COMPS";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["LEASE_COMPS"] = 10] = "LEASE_COMPS";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["SPONSOR_FINANCIALS"] = 11] = "SPONSOR_FINANCIALS";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["SCHEDULE_OF_REAL_ESTATE_OWNED"] = 12] = "SCHEDULE_OF_REAL_ESTATE_OWNED";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["CONSTRUCTION_BUDGET"] = 13] = "CONSTRUCTION_BUDGET";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["SOURCES_AND_USES"] = 14] = "SOURCES_AND_USES";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["ENVIRONMENTAL_REPORT"] = 15] = "ENVIRONMENTAL_REPORT";
  UnderwritingApprovalRequirement__[UnderwritingApprovalRequirement__["HOTEL_FRANCHISE_AGREEMENT"] = 16] = "HOTEL_FRANCHISE_AGREEMENT";
})(UnderwritingApprovalRequirement__ || (UnderwritingApprovalRequirement__ = {}));
const UnderwritingApprovalRequirement_ = defineEnumProperties(UnderwritingApprovalRequirement__, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]);
const UnderwritingApprovalRequirement = extendEnum('UnderwritingApprovalRequirement', [["VERIFY_SPONSOR_EMAIL", 0], ["SPONSOR_BIO", 1], ["ASSET_PICTURES", 2], ["STACKSOURCE_AGREEMENT", 3], ["PRO_FORMA", 4], ["FINANCIAL_STATEMENTS", 5], ["RENT_ROLL", 6], ["TENANT_LEASE", 7], ["SALES_CONTRACT", 8], ["SALES_COMPS", 9], ["LEASE_COMPS", 10], ["SPONSOR_FINANCIALS", 11], ["SCHEDULE_OF_REAL_ESTATE_OWNED", 12], ["CONSTRUCTION_BUDGET", 13], ["SOURCES_AND_USES", 14], ["ENVIRONMENTAL_REPORT", 15], ["HOTEL_FRANCHISE_AGREEMENT", 16]])(UnderwritingApprovalRequirement_);
class RepeatedUnderwritingApprovalRequirement_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RepeatedUnderwritingApprovalRequirement field value.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UnderwritingApprovalRequirement.fromStrictJson_(v, getFieldMap);
            }
            return UnderwritingApprovalRequirement.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedUnderwritingApprovalRequirement, 'RepeatedUnderwritingApprovalRequirement', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedUnderwritingApprovalRequirement to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1N(RepeatedUnderwritingApprovalRequirement_, protoName, DEV_MODE ? 'RepeatedUnderwritingApprovalRequirement' : '[Unknown]');
let RepeatedUnderwritingApprovalRequirement = RepeatedUnderwritingApprovalRequirement_;
(function (_RepeatedUnderwritingApprovalRequirement) {})(RepeatedUnderwritingApprovalRequirement || (RepeatedUnderwritingApprovalRequirement = {}));
Object.defineProperties(RepeatedUnderwritingApprovalRequirement, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedUnderwritingApprovalRequirement.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedUnderwritingApprovalRequirement);
  Object.freeze(RepeatedUnderwritingApprovalRequirement.prototype);
}

function _defineProperty$1M(obj, key, value) { key = _toPropertyKey$1M(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1M(arg) { var key = _toPrimitive$1M(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1M(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class UnitInformation_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 7,
      3: 7,
      4: 7,
      5: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UnitInformation field studio.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UnitInformation field one_bedroom.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UnitInformation field two_bedroom.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UnitInformation field three_bedroom.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UnitInformation field four_plus_bedroom.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UnitInformation, 'UnitInformation', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UnitInformation to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get studio() {
    return this.getFieldById(1, 0);
  }
  setStudio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteStudio() {
    return this.setValueFieldById(1, null, null);
  }
  get oneBedroom() {
    return this.getFieldById(2, 0);
  }
  setOneBedroom(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteOneBedroom() {
    return this.setValueFieldById(2, null, null);
  }
  get twoBedroom() {
    return this.getFieldById(3, 0);
  }
  setTwoBedroom(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteTwoBedroom() {
    return this.setValueFieldById(3, null, null);
  }
  get threeBedroom() {
    return this.getFieldById(4, 0);
  }
  setThreeBedroom(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteThreeBedroom() {
    return this.setValueFieldById(4, null, null);
  }
  get fourPlusBedroom() {
    return this.getFieldById(5, 0);
  }
  setFourPlusBedroom(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteFourPlusBedroom() {
    return this.setValueFieldById(5, null, null);
  }
}
_defineProperty$1M(UnitInformation_, protoName, DEV_MODE ? 'UnitInformation' : '[Unknown]');
let UnitInformation = UnitInformation_;
(function (_UnitInformation) {})(UnitInformation || (UnitInformation = {}));
Object.defineProperties(UnitInformation, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "studio": {
        value: 1
      },
      "one_bedroom": {
        value: 2
      },
      "oneBedroom": {
        value: 2
      },
      "two_bedroom": {
        value: 3
      },
      "twoBedroom": {
        value: 3
      },
      "three_bedroom": {
        value: 4
      },
      "threeBedroom": {
        value: 4
      },
      "four_plus_bedroom": {
        value: 5
      },
      "fourPlusBedroom": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "studio"
      },
      '2': {
        value: "one_bedroom"
      },
      '3': {
        value: "two_bedroom"
      },
      '4': {
        value: "three_bedroom"
      },
      '5': {
        value: "four_plus_bedroom"
      }
    }))
  }
});
Object.defineProperties(UnitInformation.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UnitInformation);
  Object.freeze(UnitInformation.prototype);
}

// tslint:disable-next-line:no-unused-variable
var AreaUnit__;
(function (AreaUnit__) {
  AreaUnit__[AreaUnit__["SQUARE_FEET"] = 0] = "SQUARE_FEET";
  AreaUnit__[AreaUnit__["MILLIACRES"] = 1] = "MILLIACRES";
})(AreaUnit__ || (AreaUnit__ = {}));
const AreaUnit_ = defineEnumProperties(AreaUnit__, [0, 1]);
const AreaUnit$1 = extendEnum('AreaUnit', [["SQUARE_FEET", 0], ["MILLIACRES", 1]])(AreaUnit_);

const AreaUnit = {
  ...AreaUnit$1,
  format(unit) {
    switch (unit) {
      case AreaUnit.SQUARE_FEET:
        return 'sqft';
      case AreaUnit.MILLIACRES:
        return 'acres';
    }
    assert.fail(`Unknown area unit: ${unit}`);
    return '';
  }
};

registerCanonical(AreaUnit$1, AreaUnit);

function _defineProperty$1L(obj, key, value) { key = _toPropertyKey$1L(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1L(arg) { var key = _toPrimitive$1L(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1L(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Area_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 8
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Area field unit.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AreaUnit.fromStrictJson_(v, getFieldMap);
          }
          return AreaUnit.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Area field amount.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Area$1, 'Area', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Area to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get unit() {
    return this.getFieldById(1, 0);
  }
  setUnit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUnit() {
    return this.setValueFieldById(1, null, null);
  }
  get amount() {
    return this.getFieldById(2, 0);
  }
  setAmount(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteAmount() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1L(Area_, protoName, DEV_MODE ? 'Area' : '[Unknown]');
let Area$1 = Area_;
(function (_Area) {})(Area$1 || (Area$1 = {}));
Object.defineProperties(Area$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "unit": {
        value: 1
      },
      "amount": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "unit"
      },
      '2': {
        value: "amount"
      }
    }))
  }
});
Object.defineProperties(Area$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Area$1);
  Object.freeze(Area$1.prototype);
}

class Area extends Area_ {
  isZero() {
    return this.amount === 0;
  }

  // DEPRECATED
  isEmpty() {
    return deprecatedMember('Area', 'isEmpty', 'isZero', this.isZero());
  }
  format() {
    return `${this.formatAmount()} ${this.formatUnit()}`;
  }
  formatAmount() {
    return formatCommaSeparated(this.toNumber());
  }

  // Converts to acres if value is stored in milliacres. For feet, leaves value
  // in feet.
  toNumber() {
    if (this.unit === AreaUnit.MILLIACRES) {
      return milliacresToAcres(this.amount);
    }
    assert.same(this.unit, AreaUnit.SQUARE_FEET);
    return this.amount;
  }
  formatUnit() {
    return AreaUnit.format(this.unit);
  }
}
function milliacresToAcres(amount) {
  return Math.round(amount / 10) / 100;
}

registerCanonical(Area_, Area);

function _defineProperty$1K(obj, key, value) { key = _toPropertyKey$1K(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1K(arg) { var key = _toPrimitive$1K(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1K(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Deal_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: StandardUsAddress.shape,
      4: 2,
      5: SymOrString.shape,
      80: SymOrString.shape,
      6: 0,
      7: ProjectLostReason.shape,
      8: 0,
      9: 0,
      10: 0,
      11: HotelFlag$1.shape,
      12: CurrencyValue.shape,
      13: Percent.shape,
      14: DealTenancy.shape,
      15: CurrencyValue.shape,
      16: 7,
      17: RichText.shape,
      18: Area.shape,
      19: Area.shape,
      20: Area.shape,
      21: 2,
      22: 2,
      23: 2,
      24: 2,
      25: 2,
      26: 2,
      27: 7,
      28: 7,
      29: 7,
      30: 7,
      84: 7,
      31: RichText.shape,
      32: 0,
      33: RichText.shape,
      34: CurrencyValue.shape,
      35: SimpleDate.shape,
      83: SimpleDate.shape,
      36: CurrencyValue.shape,
      37: CurrencyValue.shape,
      38: Percent.shape,
      39: Percent.shape,
      40: RichText.shape,
      41: CurrencyValue.shape,
      42: SimpleDate.shape,
      43: 0,
      44: CurrencyValueRange.shape,
      45: 7,
      46: FinancialInstruments.shape,
      47: 0,
      48: 1,
      49: 7,
      50: UnitInformation.shape,
      51: Percent.shape,
      52: OfferExpectations.shape,
      53: CapStackDetails.shape,
      54: DealImage.shape,
      55: Attachment.shape,
      56: SponsorProfile.shape,
      57: ProjectGuarantor.shape,
      58: SymOrString.shape,
      59: 2,
      60: 2,
      61: 2,
      62: 2,
      63: DEPRECATED_Date.shape,
      64: 7,
      65: 0,
      66: Occupancy.shape,
      67: PropertyDisclaimer.shape,
      68: PropertyDemographics.shape,
      69: 0,
      71: 1,
      72: ProjectFee.shape,
      73: 2,
      74: 1,
      75: 0,
      76: 1,
      81: 1,
      82: 2,
      77: 1,
      78: 1,
      79: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(SymOrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Deal field sponsor_user.
                Expected type SymOrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'SymOrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 80:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(SymOrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Deal field broker_user.
                Expected type SymOrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'SymOrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectStatus.fromStrictJson_(v, getFieldMap);
          }
          return ProjectStatus.from(value);
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectLostReason);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field lost_reason.
                Expected type ProjectLostReason.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectLostReason', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field financing_purpose.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FinancingPurpose.fromStrictJson_(v, getFieldMap);
          }
          return FinancingPurpose.from(value);
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field asset_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AssetType.fromStrictJson_(v, getFieldMap);
          }
          return AssetType.from(value);
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field sponsor_role.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return SponsorRole.fromStrictJson_(v, getFieldMap);
          }
          return SponsorRole.from(value);
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(HotelFlag$1);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field hotel_flag.
                Expected type HotelFlag.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'HotelFlag', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field hotel_average_daily_rate.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field hotel_average_occupancy_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DealTenancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field tenancy.
                Expected type DealTenancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DealTenancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 15:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field deprecated_total_project_budget.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 16:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field number_of_units.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 17:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field project_summary.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 18:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field total_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 19:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field footprint_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 20:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field lot_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 21:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field architect.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 22:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field structural_engineer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 23:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field construction_manager.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 24:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field general_contractor.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 25:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field leasing_representative.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 26:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field property_manager.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 27:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field completed_year.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 28:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field renovated_year.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 29:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field floors_above_ground.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 30:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field floors_below_ground.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 84:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field parking_spots.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 31:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field design_info.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 32:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field property_title.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return PropertyTitle.fromStrictJson_(v, getFieldMap);
          }
          return PropertyTitle.from(value);
        };
      case 33:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field asset_notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 34:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field latest_appraisal_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 35:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field latest_appraisal_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 83:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field target_close_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 36:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field current_net_operating_income.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 37:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field pro_forma_net_operating_income.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 38:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 39:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field pro_forma_cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 40:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field financial_notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 41:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field acquisition_price.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 42:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field acquisition_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 43:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field acquisition_status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AcquisitionStatus.fromStrictJson_(v, getFieldMap);
          }
          return AcquisitionStatus.from(value);
        };
      case 44:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValueRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field principal_needed.
                Expected type CurrencyValueRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValueRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 45:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field lien_position.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 46:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FinancialInstruments);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field financial_instruments.
                Expected type FinancialInstruments.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FinancialInstruments', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 47:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field affordability.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AffordabilityType.fromStrictJson_(v, getFieldMap);
          }
          return AffordabilityType.from(value);
        };
      case 48:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field has_elevator.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 49:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field number_of_floors.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 50:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UnitInformation);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field unit_information.
                Expected type UnitInformation.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UnitInformation', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 51:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field lihtc_ratio.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 52:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferExpectations);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field offer_expectations.
                Expected type OfferExpectations.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferExpectations', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 53:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CapStackDetails);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field cap_stack.
                Expected type CapStackDetails.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CapStackDetails', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 54:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(DealImage);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Deal field image.
                Expected type DealImage.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'DealImage', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 55:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Attachment);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Deal field attachment.
                Expected type Attachment.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Attachment', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 56:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SponsorProfile);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field sponsor_profile.
                Expected type SponsorProfile.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SponsorProfile', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 57:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectGuarantor);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Deal field guarantor.
                Expected type ProjectGuarantor.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectGuarantor', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 58:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(SymOrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Deal field solicited_lender.
                Expected type SymOrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'SymOrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 59:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field share.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 60:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field offer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 61:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field leading_offer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 62:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field accepted_offer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 63:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field offer_accepted_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 64:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Deal field offer_underwriting_requested.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 65:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field project_stage.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectStage.fromStrictJson_(v, getFieldMap);
          }
          return ProjectStage.from(value);
        };
      case 66:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Occupancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field occupancy.
                Expected type Occupancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Occupancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 67:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PropertyDisclaimer);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field disclaimer.
                Expected type PropertyDisclaimer.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PropertyDisclaimer', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 68:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PropertyDemographics);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field demographics.
                Expected type PropertyDemographics.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PropertyDemographics', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 69:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field investment_category.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return InvestmentCategory.fromStrictJson_(v, getFieldMap);
          }
          return InvestmentCategory.from(value);
        };
      case 71:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field show_concealed_shares.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 72:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectFee);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field project_fee.
                Expected type ProjectFee.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectFee', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 73:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field capital_advisor.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 74:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field underwriting_approval_checklist_enabled.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 75:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Deal field underwriting_approval_requirement_exception.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UnderwritingApprovalRequirement.fromStrictJson_(v, getFieldMap);
            }
            return UnderwritingApprovalRequirement.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 76:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field approved.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 81:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field approval_requested.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 82:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Deal field approval_requested_by.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 77:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field nda_required.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 78:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Deal field stacksource_agreement_signed.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 79:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Deal field creation_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Deal$1, 'Deal', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Deal to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get address() {
    return this.getFieldById(3, StandardUsAddress.getDefault());
  }
  setAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteAddress() {
    return this.setValueFieldById(3, null, null);
  }
  get label() {
    return this.getFieldById(4, "");
  }
  setLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteLabel() {
    return this.setValueFieldById(4, null, null);
  }
  get sponsorUserList() {
    return this.getRepeatedFieldById(5);
  }
  hasSponsorUser(value) {
    return this.repeatedFieldByIdHas(5, value);
  }
  addSponsorUser(value) {
    return this.addToRepeatedFieldById(5, value);
  }
  addEachSponsorUser(values) {
    let T = this;
    for (const value of values) {
      T = T.addSponsorUser(value);
    }
    return T;
  }
  setSponsorUser(index, value) {
    return this.setRepeatedFieldById(5, index, value);
  }
  removeSponsorUser(index) {
    return this.removeFromRepeatedFieldById(5, index);
  }
  clearSponsorUserList() {
    return this.clearRepeatedFieldById(5);
  }
  setSponsorUserList(values) {
    return this.overwriteRepeatedFieldById(5, values);
  }
  get brokerUserList() {
    return this.getRepeatedFieldById(80);
  }
  hasBrokerUser(value) {
    return this.repeatedFieldByIdHas(80, value);
  }
  addBrokerUser(value) {
    return this.addToRepeatedFieldById(80, value);
  }
  addEachBrokerUser(values) {
    let T = this;
    for (const value of values) {
      T = T.addBrokerUser(value);
    }
    return T;
  }
  setBrokerUser(index, value) {
    return this.setRepeatedFieldById(80, index, value);
  }
  removeBrokerUser(index) {
    return this.removeFromRepeatedFieldById(80, index);
  }
  clearBrokerUserList() {
    return this.clearRepeatedFieldById(80);
  }
  setBrokerUserList(values) {
    return this.overwriteRepeatedFieldById(80, values);
  }
  get status() {
    return this.getFieldById(6, 0);
  }
  setStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteStatus() {
    return this.setValueFieldById(6, null, null);
  }
  get lostReason() {
    return this.getFieldById(7, ProjectLostReason.getDefault());
  }
  setLostReason(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteLostReason() {
    return this.setValueFieldById(7, null, null);
  }
  get financingPurpose() {
    return this.getFieldById(8, 0);
  }
  setFinancingPurpose(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteFinancingPurpose() {
    return this.setValueFieldById(8, null, null);
  }
  get assetType() {
    return this.getFieldById(9, 0);
  }
  setAssetType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteAssetType() {
    return this.setValueFieldById(9, null, null);
  }
  get sponsorRole() {
    return this.getFieldById(10, 0);
  }
  setSponsorRole(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteSponsorRole() {
    return this.setValueFieldById(10, null, null);
  }
  get hotelFlag() {
    return this.getFieldById(11, HotelFlag$1.getDefault());
  }
  setHotelFlag(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteHotelFlag() {
    return this.setValueFieldById(11, null, null);
  }
  get hotelAverageDailyRate() {
    return this.getFieldById(12, CurrencyValue.getDefault());
  }
  setHotelAverageDailyRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteHotelAverageDailyRate() {
    return this.setValueFieldById(12, null, null);
  }
  get hotelAverageOccupancyRate() {
    return this.getFieldById(13, Percent.getDefault());
  }
  setHotelAverageOccupancyRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteHotelAverageOccupancyRate() {
    return this.setValueFieldById(13, null, null);
  }
  get tenancy() {
    return this.getFieldById(14, DealTenancy.getDefault());
  }
  setTenancy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteTenancy() {
    return this.setValueFieldById(14, null, null);
  }
  get deprecatedTotalProjectBudget() {
    return this.getFieldById(15, CurrencyValue.getDefault());
  }
  setDeprecatedTotalProjectBudget(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteDeprecatedTotalProjectBudget() {
    return this.setValueFieldById(15, null, null);
  }
  get numberOfUnits() {
    return this.getFieldById(16, 0);
  }
  setNumberOfUnits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deleteNumberOfUnits() {
    return this.setValueFieldById(16, null, null);
  }
  get projectSummary() {
    return this.getFieldById(17, RichText.getDefault());
  }
  setProjectSummary(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteProjectSummary() {
    return this.setValueFieldById(17, null, null);
  }
  get totalSize() {
    return this.getFieldById(18, Area.getDefault());
  }
  setTotalSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(18, value, null);
  }
  deleteTotalSize() {
    return this.setValueFieldById(18, null, null);
  }
  get footprintSize() {
    return this.getFieldById(19, Area.getDefault());
  }
  setFootprintSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(19, value, null);
  }
  deleteFootprintSize() {
    return this.setValueFieldById(19, null, null);
  }
  get lotSize() {
    return this.getFieldById(20, Area.getDefault());
  }
  setLotSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, null);
  }
  deleteLotSize() {
    return this.setValueFieldById(20, null, null);
  }
  get architect() {
    return this.getFieldById(21, "");
  }
  setArchitect(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, null);
  }
  deleteArchitect() {
    return this.setValueFieldById(21, null, null);
  }
  get structuralEngineer() {
    return this.getFieldById(22, "");
  }
  setStructuralEngineer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteStructuralEngineer() {
    return this.setValueFieldById(22, null, null);
  }
  get constructionManager() {
    return this.getFieldById(23, "");
  }
  setConstructionManager(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(23, value, null);
  }
  deleteConstructionManager() {
    return this.setValueFieldById(23, null, null);
  }
  get generalContractor() {
    return this.getFieldById(24, "");
  }
  setGeneralContractor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(24, value, null);
  }
  deleteGeneralContractor() {
    return this.setValueFieldById(24, null, null);
  }
  get leasingRepresentative() {
    return this.getFieldById(25, "");
  }
  setLeasingRepresentative(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(25, value, null);
  }
  deleteLeasingRepresentative() {
    return this.setValueFieldById(25, null, null);
  }
  get propertyManager() {
    return this.getFieldById(26, "");
  }
  setPropertyManager(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(26, value, null);
  }
  deletePropertyManager() {
    return this.setValueFieldById(26, null, null);
  }
  get completedYear() {
    return this.getFieldById(27, 0);
  }
  setCompletedYear(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(27, value, null);
  }
  deleteCompletedYear() {
    return this.setValueFieldById(27, null, null);
  }
  get renovatedYear() {
    return this.getFieldById(28, 0);
  }
  setRenovatedYear(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(28, value, null);
  }
  deleteRenovatedYear() {
    return this.setValueFieldById(28, null, null);
  }
  get floorsAboveGround() {
    return this.getFieldById(29, 0);
  }
  setFloorsAboveGround(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(29, value, null);
  }
  deleteFloorsAboveGround() {
    return this.setValueFieldById(29, null, null);
  }
  get floorsBelowGround() {
    return this.getFieldById(30, 0);
  }
  setFloorsBelowGround(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(30, value, null);
  }
  deleteFloorsBelowGround() {
    return this.setValueFieldById(30, null, null);
  }
  get parkingSpots() {
    return this.getFieldById(84, 0);
  }
  setParkingSpots(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(84, value, null);
  }
  deleteParkingSpots() {
    return this.setValueFieldById(84, null, null);
  }
  get designInfo() {
    return this.getFieldById(31, RichText.getDefault());
  }
  setDesignInfo(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(31, value, null);
  }
  deleteDesignInfo() {
    return this.setValueFieldById(31, null, null);
  }
  get propertyTitle() {
    return this.getFieldById(32, 0);
  }
  setPropertyTitle(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(32, value, null);
  }
  deletePropertyTitle() {
    return this.setValueFieldById(32, null, null);
  }
  get assetNotes() {
    return this.getFieldById(33, RichText.getDefault());
  }
  setAssetNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(33, value, null);
  }
  deleteAssetNotes() {
    return this.setValueFieldById(33, null, null);
  }
  get latestAppraisalValue() {
    return this.getFieldById(34, CurrencyValue.getDefault());
  }
  setLatestAppraisalValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(34, value, null);
  }
  deleteLatestAppraisalValue() {
    return this.setValueFieldById(34, null, null);
  }
  get latestAppraisalDate() {
    return this.getFieldById(35, SimpleDate.getDefault());
  }
  setLatestAppraisalDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(35, value, null);
  }
  deleteLatestAppraisalDate() {
    return this.setValueFieldById(35, null, null);
  }
  get targetCloseDate() {
    return this.getFieldById(83, SimpleDate.getDefault());
  }
  setTargetCloseDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(83, value, null);
  }
  deleteTargetCloseDate() {
    return this.setValueFieldById(83, null, null);
  }
  get currentNetOperatingIncome() {
    return this.getFieldById(36, CurrencyValue.getDefault());
  }
  setCurrentNetOperatingIncome(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(36, value, null);
  }
  deleteCurrentNetOperatingIncome() {
    return this.setValueFieldById(36, null, null);
  }
  get proFormaNetOperatingIncome() {
    return this.getFieldById(37, CurrencyValue.getDefault());
  }
  setProFormaNetOperatingIncome(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(37, value, null);
  }
  deleteProFormaNetOperatingIncome() {
    return this.setValueFieldById(37, null, null);
  }
  get capRate() {
    return this.getFieldById(38, Percent.getDefault());
  }
  setCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(38, value, null);
  }
  deleteCapRate() {
    return this.setValueFieldById(38, null, null);
  }
  get proFormaCapRate() {
    return this.getFieldById(39, Percent.getDefault());
  }
  setProFormaCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(39, value, null);
  }
  deleteProFormaCapRate() {
    return this.setValueFieldById(39, null, null);
  }
  get financialNotes() {
    return this.getFieldById(40, RichText.getDefault());
  }
  setFinancialNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(40, value, null);
  }
  deleteFinancialNotes() {
    return this.setValueFieldById(40, null, null);
  }
  get acquisitionPrice() {
    return this.getFieldById(41, CurrencyValue.getDefault());
  }
  setAcquisitionPrice(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(41, value, null);
  }
  deleteAcquisitionPrice() {
    return this.setValueFieldById(41, null, null);
  }
  get acquisitionDate() {
    return this.getFieldById(42, SimpleDate.getDefault());
  }
  setAcquisitionDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(42, value, null);
  }
  deleteAcquisitionDate() {
    return this.setValueFieldById(42, null, null);
  }
  get acquisitionStatus() {
    return this.getFieldById(43, 0);
  }
  setAcquisitionStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(43, value, null);
  }
  deleteAcquisitionStatus() {
    return this.setValueFieldById(43, null, null);
  }
  get principalNeeded() {
    return this.getFieldById(44, CurrencyValueRange.getDefault());
  }
  setPrincipalNeeded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(44, value, null);
  }
  deletePrincipalNeeded() {
    return this.setValueFieldById(44, null, null);
  }
  get lienPosition() {
    return this.getFieldById(45, 0);
  }
  setLienPosition(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(45, value, null);
  }
  deleteLienPosition() {
    return this.setValueFieldById(45, null, null);
  }
  get financialInstruments() {
    return this.getFieldById(46, FinancialInstruments.getDefault());
  }
  setFinancialInstruments(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(46, value, null);
  }
  deleteFinancialInstruments() {
    return this.setValueFieldById(46, null, null);
  }
  get affordability() {
    return this.getFieldById(47, 0);
  }
  setAffordability(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(47, value, null);
  }
  deleteAffordability() {
    return this.setValueFieldById(47, null, null);
  }
  get hasElevator() {
    return this.getFieldById(48, false);
  }
  setHasElevator(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(48, value, null);
  }
  deleteHasElevator() {
    return this.setValueFieldById(48, null, null);
  }
  get numberOfFloors() {
    return this.getFieldById(49, 0);
  }
  setNumberOfFloors(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(49, value, null);
  }
  deleteNumberOfFloors() {
    return this.setValueFieldById(49, null, null);
  }
  get unitInformation() {
    return this.getFieldById(50, UnitInformation.getDefault());
  }
  setUnitInformation(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(50, value, null);
  }
  deleteUnitInformation() {
    return this.setValueFieldById(50, null, null);
  }
  get lihtcRatio() {
    return this.getFieldById(51, Percent.getDefault());
  }
  setLihtcRatio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(51, value, null);
  }
  deleteLihtcRatio() {
    return this.setValueFieldById(51, null, null);
  }
  get offerExpectations() {
    return this.getFieldById(52, OfferExpectations.getDefault());
  }
  setOfferExpectations(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(52, value, null);
  }
  deleteOfferExpectations() {
    return this.setValueFieldById(52, null, null);
  }
  get capStack() {
    return this.getFieldById(53, CapStackDetails.getDefault());
  }
  setCapStack(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(53, value, null);
  }
  deleteCapStack() {
    return this.setValueFieldById(53, null, null);
  }
  get imageList() {
    return this.getRepeatedFieldById(54);
  }
  hasImage(value) {
    return this.repeatedFieldByIdHas(54, value);
  }
  addImage(value) {
    return this.addToRepeatedFieldById(54, value);
  }
  addEachImage(values) {
    let T = this;
    for (const value of values) {
      T = T.addImage(value);
    }
    return T;
  }
  setImage(index, value) {
    return this.setRepeatedFieldById(54, index, value);
  }
  removeImage(index) {
    return this.removeFromRepeatedFieldById(54, index);
  }
  clearImageList() {
    return this.clearRepeatedFieldById(54);
  }
  setImageList(values) {
    return this.overwriteRepeatedFieldById(54, values);
  }
  get attachmentList() {
    return this.getRepeatedFieldById(55);
  }
  hasAttachment(value) {
    return this.repeatedFieldByIdHas(55, value);
  }
  addAttachment(value) {
    return this.addToRepeatedFieldById(55, value);
  }
  addEachAttachment(values) {
    let T = this;
    for (const value of values) {
      T = T.addAttachment(value);
    }
    return T;
  }
  setAttachment(index, value) {
    return this.setRepeatedFieldById(55, index, value);
  }
  removeAttachment(index) {
    return this.removeFromRepeatedFieldById(55, index);
  }
  clearAttachmentList() {
    return this.clearRepeatedFieldById(55);
  }
  setAttachmentList(values) {
    return this.overwriteRepeatedFieldById(55, values);
  }
  get sponsorProfile() {
    return this.getFieldById(56, SponsorProfile.getDefault());
  }
  setSponsorProfile(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(56, value, null);
  }
  deleteSponsorProfile() {
    return this.setValueFieldById(56, null, null);
  }
  get guarantorList() {
    return this.getRepeatedFieldById(57);
  }
  hasGuarantor(value) {
    return this.repeatedFieldByIdHas(57, value);
  }
  addGuarantor(value) {
    return this.addToRepeatedFieldById(57, value);
  }
  addEachGuarantor(values) {
    let T = this;
    for (const value of values) {
      T = T.addGuarantor(value);
    }
    return T;
  }
  setGuarantor(index, value) {
    return this.setRepeatedFieldById(57, index, value);
  }
  removeGuarantor(index) {
    return this.removeFromRepeatedFieldById(57, index);
  }
  clearGuarantorList() {
    return this.clearRepeatedFieldById(57);
  }
  setGuarantorList(values) {
    return this.overwriteRepeatedFieldById(57, values);
  }
  get solicitedLenderList() {
    return this.getRepeatedFieldById(58);
  }
  hasSolicitedLender(value) {
    return this.repeatedFieldByIdHas(58, value);
  }
  addSolicitedLender(value) {
    return this.addToRepeatedFieldById(58, value);
  }
  addEachSolicitedLender(values) {
    let T = this;
    for (const value of values) {
      T = T.addSolicitedLender(value);
    }
    return T;
  }
  setSolicitedLender(index, value) {
    return this.setRepeatedFieldById(58, index, value);
  }
  removeSolicitedLender(index) {
    return this.removeFromRepeatedFieldById(58, index);
  }
  clearSolicitedLenderList() {
    return this.clearRepeatedFieldById(58);
  }
  setSolicitedLenderList(values) {
    return this.overwriteRepeatedFieldById(58, values);
  }
  get shareList() {
    return this.getRepeatedFieldById(59);
  }
  hasShare(value) {
    return this.repeatedFieldByIdHas(59, value);
  }
  addShare(value) {
    return this.addToRepeatedFieldById(59, value);
  }
  addEachShare(values) {
    let T = this;
    for (const value of values) {
      T = T.addShare(value);
    }
    return T;
  }
  setShare(index, value) {
    return this.setRepeatedFieldById(59, index, value);
  }
  removeShare(index) {
    return this.removeFromRepeatedFieldById(59, index);
  }
  clearShareList() {
    return this.clearRepeatedFieldById(59);
  }
  setShareList(values) {
    return this.overwriteRepeatedFieldById(59, values);
  }
  get offerList() {
    return this.getRepeatedFieldById(60);
  }
  hasOffer(value) {
    return this.repeatedFieldByIdHas(60, value);
  }
  addOffer(value) {
    return this.addToRepeatedFieldById(60, value);
  }
  addEachOffer(values) {
    let T = this;
    for (const value of values) {
      T = T.addOffer(value);
    }
    return T;
  }
  setOffer(index, value) {
    return this.setRepeatedFieldById(60, index, value);
  }
  removeOffer(index) {
    return this.removeFromRepeatedFieldById(60, index);
  }
  clearOfferList() {
    return this.clearRepeatedFieldById(60);
  }
  setOfferList(values) {
    return this.overwriteRepeatedFieldById(60, values);
  }
  get leadingOffer() {
    return this.getFieldById(61, "");
  }
  setLeadingOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(61, value, null);
  }
  deleteLeadingOffer() {
    return this.setValueFieldById(61, null, null);
  }
  get acceptedOffer() {
    return this.getFieldById(62, "");
  }
  setAcceptedOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(62, value, null);
  }
  deleteAcceptedOffer() {
    return this.setValueFieldById(62, null, null);
  }
  get offerAcceptedTime() {
    return this.getFieldById(63, DEPRECATED_Date.getDefault());
  }
  setOfferAcceptedTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(63, value, null);
  }
  deleteOfferAcceptedTime() {
    return this.setValueFieldById(63, null, null);
  }
  get offerUnderwritingRequested() {
    return this.getFieldById(64, 0);
  }
  setOfferUnderwritingRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(64, value, null);
  }
  deleteOfferUnderwritingRequested() {
    return this.setValueFieldById(64, null, null);
  }
  get projectStage() {
    return this.getFieldById(65, 0);
  }
  setProjectStage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(65, value, null);
  }
  deleteProjectStage() {
    return this.setValueFieldById(65, null, null);
  }
  get occupancy() {
    return this.getFieldById(66, Occupancy.getDefault());
  }
  setOccupancy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(66, value, null);
  }
  deleteOccupancy() {
    return this.setValueFieldById(66, null, null);
  }
  get disclaimer() {
    return this.getFieldById(67, PropertyDisclaimer.getDefault());
  }
  setDisclaimer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(67, value, null);
  }
  deleteDisclaimer() {
    return this.setValueFieldById(67, null, null);
  }
  get demographics() {
    return this.getFieldById(68, PropertyDemographics.getDefault());
  }
  setDemographics(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(68, value, null);
  }
  deleteDemographics() {
    return this.setValueFieldById(68, null, null);
  }
  get investmentCategory() {
    return this.getFieldById(69, 0);
  }
  setInvestmentCategory(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(69, value, null);
  }
  deleteInvestmentCategory() {
    return this.setValueFieldById(69, null, null);
  }
  get showConcealedShares() {
    return this.getFieldById(71, false);
  }
  setShowConcealedShares(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(71, value, null);
  }
  deleteShowConcealedShares() {
    return this.setValueFieldById(71, null, null);
  }
  get projectFee() {
    return this.getFieldById(72, ProjectFee.getDefault());
  }
  setProjectFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(72, value, null);
  }
  deleteProjectFee() {
    return this.setValueFieldById(72, null, null);
  }
  get capitalAdvisorList() {
    return this.getRepeatedFieldById(73);
  }
  hasCapitalAdvisor(value) {
    return this.repeatedFieldByIdHas(73, value);
  }
  addCapitalAdvisor(value) {
    return this.addToRepeatedFieldById(73, value);
  }
  addEachCapitalAdvisor(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapitalAdvisor(value);
    }
    return T;
  }
  setCapitalAdvisor(index, value) {
    return this.setRepeatedFieldById(73, index, value);
  }
  removeCapitalAdvisor(index) {
    return this.removeFromRepeatedFieldById(73, index);
  }
  clearCapitalAdvisorList() {
    return this.clearRepeatedFieldById(73);
  }
  setCapitalAdvisorList(values) {
    return this.overwriteRepeatedFieldById(73, values);
  }
  get underwritingApprovalChecklistEnabled() {
    return this.getFieldById(74, false);
  }
  setUnderwritingApprovalChecklistEnabled(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(74, value, null);
  }
  deleteUnderwritingApprovalChecklistEnabled() {
    return this.setValueFieldById(74, null, null);
  }
  get underwritingApprovalRequirementExceptionList() {
    return this.getRepeatedFieldById(75);
  }
  hasUnderwritingApprovalRequirementException(value) {
    return this.repeatedFieldByIdHas(75, value);
  }
  addUnderwritingApprovalRequirementException(value) {
    return this.addToRepeatedFieldById(75, value);
  }
  addEachUnderwritingApprovalRequirementException(values) {
    let T = this;
    for (const value of values) {
      T = T.addUnderwritingApprovalRequirementException(value);
    }
    return T;
  }
  setUnderwritingApprovalRequirementException(index, value) {
    return this.setRepeatedFieldById(75, index, value);
  }
  removeUnderwritingApprovalRequirementException(index) {
    return this.removeFromRepeatedFieldById(75, index);
  }
  clearUnderwritingApprovalRequirementExceptionList() {
    return this.clearRepeatedFieldById(75);
  }
  setUnderwritingApprovalRequirementExceptionList(values) {
    return this.overwriteRepeatedFieldById(75, values);
  }
  get approved() {
    return this.getFieldById(76, false);
  }
  setApproved(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(76, value, null);
  }
  deleteApproved() {
    return this.setValueFieldById(76, null, null);
  }
  get approvalRequested() {
    return this.getFieldById(81, false);
  }
  setApprovalRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(81, value, null);
  }
  deleteApprovalRequested() {
    return this.setValueFieldById(81, null, null);
  }
  get approvalRequestedBy() {
    return this.getFieldById(82, "");
  }
  setApprovalRequestedBy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(82, value, null);
  }
  deleteApprovalRequestedBy() {
    return this.setValueFieldById(82, null, null);
  }
  get ndaRequired() {
    return this.getFieldById(77, false);
  }
  setNdaRequired(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(77, value, null);
  }
  deleteNdaRequired() {
    return this.setValueFieldById(77, null, null);
  }
  get stacksourceAgreementSigned() {
    return this.getFieldById(78, false);
  }
  setStacksourceAgreementSigned(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(78, value, null);
  }
  deleteStacksourceAgreementSigned() {
    return this.setValueFieldById(78, null, null);
  }
  get creationTime() {
    return this.getFieldById(79, DEPRECATED_Date.getDefault());
  }
  setCreationTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(79, value, null);
  }
  deleteCreationTime() {
    return this.setValueFieldById(79, null, null);
  }
}
_defineProperty$1K(Deal_, protoName, DEV_MODE ? 'Deal' : '[Unknown]');
let Deal$1 = Deal_;
(function (_Deal) {})(Deal$1 || (Deal$1 = {}));
Object.defineProperties(Deal$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "name": {
        value: 2
      },
      "address": {
        value: 3
      },
      "label": {
        value: 4
      },
      "sponsor_user": {
        value: 5
      },
      "sponsorUser": {
        value: 5
      },
      "broker_user": {
        value: 80
      },
      "brokerUser": {
        value: 80
      },
      "status": {
        value: 6
      },
      "lost_reason": {
        value: 7
      },
      "lostReason": {
        value: 7
      },
      "financing_purpose": {
        value: 8
      },
      "financingPurpose": {
        value: 8
      },
      "asset_type": {
        value: 9
      },
      "assetType": {
        value: 9
      },
      "sponsor_role": {
        value: 10
      },
      "sponsorRole": {
        value: 10
      },
      "hotel_flag": {
        value: 11
      },
      "hotelFlag": {
        value: 11
      },
      "hotel_average_daily_rate": {
        value: 12
      },
      "hotelAverageDailyRate": {
        value: 12
      },
      "hotel_average_occupancy_rate": {
        value: 13
      },
      "hotelAverageOccupancyRate": {
        value: 13
      },
      "tenancy": {
        value: 14
      },
      "deprecated_total_project_budget": {
        value: 15
      },
      "deprecatedTotalProjectBudget": {
        value: 15
      },
      "number_of_units": {
        value: 16
      },
      "numberOfUnits": {
        value: 16
      },
      "project_summary": {
        value: 17
      },
      "projectSummary": {
        value: 17
      },
      "total_size": {
        value: 18
      },
      "totalSize": {
        value: 18
      },
      "footprint_size": {
        value: 19
      },
      "footprintSize": {
        value: 19
      },
      "lot_size": {
        value: 20
      },
      "lotSize": {
        value: 20
      },
      "architect": {
        value: 21
      },
      "structural_engineer": {
        value: 22
      },
      "structuralEngineer": {
        value: 22
      },
      "construction_manager": {
        value: 23
      },
      "constructionManager": {
        value: 23
      },
      "general_contractor": {
        value: 24
      },
      "generalContractor": {
        value: 24
      },
      "leasing_representative": {
        value: 25
      },
      "leasingRepresentative": {
        value: 25
      },
      "property_manager": {
        value: 26
      },
      "propertyManager": {
        value: 26
      },
      "completed_year": {
        value: 27
      },
      "completedYear": {
        value: 27
      },
      "renovated_year": {
        value: 28
      },
      "renovatedYear": {
        value: 28
      },
      "floors_above_ground": {
        value: 29
      },
      "floorsAboveGround": {
        value: 29
      },
      "floors_below_ground": {
        value: 30
      },
      "floorsBelowGround": {
        value: 30
      },
      "parking_spots": {
        value: 84
      },
      "parkingSpots": {
        value: 84
      },
      "design_info": {
        value: 31
      },
      "designInfo": {
        value: 31
      },
      "property_title": {
        value: 32
      },
      "propertyTitle": {
        value: 32
      },
      "asset_notes": {
        value: 33
      },
      "assetNotes": {
        value: 33
      },
      "latest_appraisal_value": {
        value: 34
      },
      "latestAppraisalValue": {
        value: 34
      },
      "latest_appraisal_date": {
        value: 35
      },
      "latestAppraisalDate": {
        value: 35
      },
      "target_close_date": {
        value: 83
      },
      "targetCloseDate": {
        value: 83
      },
      "current_net_operating_income": {
        value: 36
      },
      "currentNetOperatingIncome": {
        value: 36
      },
      "pro_forma_net_operating_income": {
        value: 37
      },
      "proFormaNetOperatingIncome": {
        value: 37
      },
      "cap_rate": {
        value: 38
      },
      "capRate": {
        value: 38
      },
      "pro_forma_cap_rate": {
        value: 39
      },
      "proFormaCapRate": {
        value: 39
      },
      "financial_notes": {
        value: 40
      },
      "financialNotes": {
        value: 40
      },
      "acquisition_price": {
        value: 41
      },
      "acquisitionPrice": {
        value: 41
      },
      "acquisition_date": {
        value: 42
      },
      "acquisitionDate": {
        value: 42
      },
      "acquisition_status": {
        value: 43
      },
      "acquisitionStatus": {
        value: 43
      },
      "principal_needed": {
        value: 44
      },
      "principalNeeded": {
        value: 44
      },
      "lien_position": {
        value: 45
      },
      "lienPosition": {
        value: 45
      },
      "financial_instruments": {
        value: 46
      },
      "financialInstruments": {
        value: 46
      },
      "affordability": {
        value: 47
      },
      "has_elevator": {
        value: 48
      },
      "hasElevator": {
        value: 48
      },
      "number_of_floors": {
        value: 49
      },
      "numberOfFloors": {
        value: 49
      },
      "unit_information": {
        value: 50
      },
      "unitInformation": {
        value: 50
      },
      "lihtc_ratio": {
        value: 51
      },
      "lihtcRatio": {
        value: 51
      },
      "offer_expectations": {
        value: 52
      },
      "offerExpectations": {
        value: 52
      },
      "cap_stack": {
        value: 53
      },
      "capStack": {
        value: 53
      },
      "image": {
        value: 54
      },
      "attachment": {
        value: 55
      },
      "sponsor_profile": {
        value: 56
      },
      "sponsorProfile": {
        value: 56
      },
      "guarantor": {
        value: 57
      },
      "solicited_lender": {
        value: 58
      },
      "solicitedLender": {
        value: 58
      },
      "share": {
        value: 59
      },
      "offer": {
        value: 60
      },
      "leading_offer": {
        value: 61
      },
      "leadingOffer": {
        value: 61
      },
      "accepted_offer": {
        value: 62
      },
      "acceptedOffer": {
        value: 62
      },
      "offer_accepted_time": {
        value: 63
      },
      "offerAcceptedTime": {
        value: 63
      },
      "offer_underwriting_requested": {
        value: 64
      },
      "offerUnderwritingRequested": {
        value: 64
      },
      "project_stage": {
        value: 65
      },
      "projectStage": {
        value: 65
      },
      "occupancy": {
        value: 66
      },
      "disclaimer": {
        value: 67
      },
      "demographics": {
        value: 68
      },
      "investment_category": {
        value: 69
      },
      "investmentCategory": {
        value: 69
      },
      "show_concealed_shares": {
        value: 71
      },
      "showConcealedShares": {
        value: 71
      },
      "project_fee": {
        value: 72
      },
      "projectFee": {
        value: 72
      },
      "capital_advisor": {
        value: 73
      },
      "capitalAdvisor": {
        value: 73
      },
      "underwriting_approval_checklist_enabled": {
        value: 74
      },
      "underwritingApprovalChecklistEnabled": {
        value: 74
      },
      "underwriting_approval_requirement_exception": {
        value: 75
      },
      "underwritingApprovalRequirementException": {
        value: 75
      },
      "approved": {
        value: 76
      },
      "approval_requested": {
        value: 81
      },
      "approvalRequested": {
        value: 81
      },
      "approval_requested_by": {
        value: 82
      },
      "approvalRequestedBy": {
        value: 82
      },
      "nda_required": {
        value: 77
      },
      "ndaRequired": {
        value: 77
      },
      "stacksource_agreement_signed": {
        value: 78
      },
      "stacksourceAgreementSigned": {
        value: 78
      },
      "creation_time": {
        value: 79
      },
      "creationTime": {
        value: 79
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "address"
      },
      '4': {
        value: "label"
      },
      '5': {
        value: "sponsor_user"
      },
      '80': {
        value: "broker_user"
      },
      '6': {
        value: "status"
      },
      '7': {
        value: "lost_reason"
      },
      '8': {
        value: "financing_purpose"
      },
      '9': {
        value: "asset_type"
      },
      '10': {
        value: "sponsor_role"
      },
      '11': {
        value: "hotel_flag"
      },
      '12': {
        value: "hotel_average_daily_rate"
      },
      '13': {
        value: "hotel_average_occupancy_rate"
      },
      '14': {
        value: "tenancy"
      },
      '15': {
        value: "deprecated_total_project_budget"
      },
      '16': {
        value: "number_of_units"
      },
      '17': {
        value: "project_summary"
      },
      '18': {
        value: "total_size"
      },
      '19': {
        value: "footprint_size"
      },
      '20': {
        value: "lot_size"
      },
      '21': {
        value: "architect"
      },
      '22': {
        value: "structural_engineer"
      },
      '23': {
        value: "construction_manager"
      },
      '24': {
        value: "general_contractor"
      },
      '25': {
        value: "leasing_representative"
      },
      '26': {
        value: "property_manager"
      },
      '27': {
        value: "completed_year"
      },
      '28': {
        value: "renovated_year"
      },
      '29': {
        value: "floors_above_ground"
      },
      '30': {
        value: "floors_below_ground"
      },
      '84': {
        value: "parking_spots"
      },
      '31': {
        value: "design_info"
      },
      '32': {
        value: "property_title"
      },
      '33': {
        value: "asset_notes"
      },
      '34': {
        value: "latest_appraisal_value"
      },
      '35': {
        value: "latest_appraisal_date"
      },
      '83': {
        value: "target_close_date"
      },
      '36': {
        value: "current_net_operating_income"
      },
      '37': {
        value: "pro_forma_net_operating_income"
      },
      '38': {
        value: "cap_rate"
      },
      '39': {
        value: "pro_forma_cap_rate"
      },
      '40': {
        value: "financial_notes"
      },
      '41': {
        value: "acquisition_price"
      },
      '42': {
        value: "acquisition_date"
      },
      '43': {
        value: "acquisition_status"
      },
      '44': {
        value: "principal_needed"
      },
      '45': {
        value: "lien_position"
      },
      '46': {
        value: "financial_instruments"
      },
      '47': {
        value: "affordability"
      },
      '48': {
        value: "has_elevator"
      },
      '49': {
        value: "number_of_floors"
      },
      '50': {
        value: "unit_information"
      },
      '51': {
        value: "lihtc_ratio"
      },
      '52': {
        value: "offer_expectations"
      },
      '53': {
        value: "cap_stack"
      },
      '54': {
        value: "image"
      },
      '55': {
        value: "attachment"
      },
      '56': {
        value: "sponsor_profile"
      },
      '57': {
        value: "guarantor"
      },
      '58': {
        value: "solicited_lender"
      },
      '59': {
        value: "share"
      },
      '60': {
        value: "offer"
      },
      '61': {
        value: "leading_offer"
      },
      '62': {
        value: "accepted_offer"
      },
      '63': {
        value: "offer_accepted_time"
      },
      '64': {
        value: "offer_underwriting_requested"
      },
      '65': {
        value: "project_stage"
      },
      '66': {
        value: "occupancy"
      },
      '67': {
        value: "disclaimer"
      },
      '68': {
        value: "demographics"
      },
      '69': {
        value: "investment_category"
      },
      '71': {
        value: "show_concealed_shares"
      },
      '72': {
        value: "project_fee"
      },
      '73': {
        value: "capital_advisor"
      },
      '74': {
        value: "underwriting_approval_checklist_enabled"
      },
      '75': {
        value: "underwriting_approval_requirement_exception"
      },
      '76': {
        value: "approved"
      },
      '81': {
        value: "approval_requested"
      },
      '82': {
        value: "approval_requested_by"
      },
      '77': {
        value: "nda_required"
      },
      '78': {
        value: "stacksource_agreement_signed"
      },
      '79': {
        value: "creation_time"
      }
    }))
  }
});
Object.defineProperties(Deal$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: StandardUsAddress.prototype,
      5: SymOrString.prototype,
      80: SymOrString.prototype,
      7: ProjectLostReason.prototype,
      11: HotelFlag$1.prototype,
      12: CurrencyValue.prototype,
      13: Percent.prototype,
      14: DealTenancy.prototype,
      15: CurrencyValue.prototype,
      17: RichText.prototype,
      18: Area.prototype,
      19: Area.prototype,
      20: Area.prototype,
      31: RichText.prototype,
      33: RichText.prototype,
      34: CurrencyValue.prototype,
      35: SimpleDate.prototype,
      83: SimpleDate.prototype,
      36: CurrencyValue.prototype,
      37: CurrencyValue.prototype,
      38: Percent.prototype,
      39: Percent.prototype,
      40: RichText.prototype,
      41: CurrencyValue.prototype,
      42: SimpleDate.prototype,
      44: CurrencyValueRange.prototype,
      46: FinancialInstruments.prototype,
      50: UnitInformation.prototype,
      51: Percent.prototype,
      52: OfferExpectations.prototype,
      53: CapStackDetails.prototype,
      54: DealImage.prototype,
      55: Attachment.prototype,
      56: SponsorProfile.prototype,
      57: ProjectGuarantor.prototype,
      58: SymOrString.prototype,
      63: DEPRECATED_Date.prototype,
      66: Occupancy.prototype,
      67: PropertyDisclaimer.prototype,
      68: PropertyDemographics.prototype,
      72: ProjectFee.prototype,
      79: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Deal$1);
  Object.freeze(Deal$1.prototype);
}

function calculateCapRateValuation(currentNetOperatingIncome, capRate) {
  const currentNetOperatingIncomeNumber = currentNetOperatingIncome.toNumber();
  // capRate must have a value > 0
  const capRateNumber = assert(capRate.toNumber());
  return CurrencyValue.fromNumber(currentNetOperatingIncome.unit, currentNetOperatingIncomeNumber / capRateNumber);
}

function _defineProperty$1J(obj, key, value) { key = _toPropertyKey$1J(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1J(arg) { var key = _toPrimitive$1J(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1J(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class PercentRange_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Percent.shape,
      2: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PercentRange field lower_bound.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PercentRange field upper_bound.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PercentRange$1, 'PercentRange', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PercentRange to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get lowerBound() {
    return this.getFieldById(1, Percent.getDefault());
  }
  setLowerBound(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteLowerBound() {
    return this.setValueFieldById(1, null, null);
  }
  get upperBound() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setUpperBound(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUpperBound() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1J(PercentRange_, protoName, DEV_MODE ? 'PercentRange' : '[Unknown]');
let PercentRange$1 = PercentRange_;
(function (_PercentRange) {})(PercentRange$1 || (PercentRange$1 = {}));
Object.defineProperties(PercentRange$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "lower_bound": {
        value: 1
      },
      "lowerBound": {
        value: 1
      },
      "upper_bound": {
        value: 2
      },
      "upperBound": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "lower_bound"
      },
      '2': {
        value: "upper_bound"
      }
    }))
  }
});
Object.defineProperties(PercentRange$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Percent.prototype,
      2: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PercentRange$1);
  Object.freeze(PercentRange$1.prototype);
}

const toArray = generator => Array.from(typeof generator === 'function' ? generator() : generator);
function joinString(separatorOrGenerator, generator) {
  if (typeof separatorOrGenerator === 'string') {
    const separator = separatorOrGenerator;
    const gen = assert(generator);
    return toArray(gen).join(separator);
  }
  return toArray(separatorOrGenerator).join('');
}
const joinValues = (gen, joiner = 'or') => joinString(function* () {
  const values = toArray(gen);
  for (let i = 0; i < values.length - 1; ++i) {
    yield values[i];
    if (i === values.length - 2) {
      if (i === 0) {
        yield ` ${joiner} `;
      } else {
        yield `, ${joiner} `;
      }
    } else {
      yield ', ';
    }
  }
  yield values[values.length - 1];
});

const DEFAULT_BRIDGE = ' to ';
class PercentRange extends PercentRange_ {
  isZero() {
    return this.lowerBound.isZero() && this.upperBound.isZero();
  }
  format(includeSymbol = true, bridge = DEFAULT_BRIDGE) {
    return formatUsing(this, Percent.prototype.format, [includeSymbol], bridge);
  }
  formatMaxDecimalPlaces(places, includeSymbol = true, bridge = DEFAULT_BRIDGE) {
    return formatUsing(this, Percent.prototype.formatMaxDecimalPlaces, [places, includeSymbol], bridge);
  }
  formatAsNumber(decimalPlaces = Infinity) {
    return formatUsing(this, Percent.prototype.formatAsNumber, [decimalPlaces]);
  }
  formatAsMultiple(places = Infinity) {
    return formatUsing(this, Percent.prototype.formatAsMultiple, [places]);
  }
}
function formatUsing(range, callback, args = [], bridge = DEFAULT_BRIDGE) {
  return joinString(bridge, function* () {
    const lower = callback.apply(range.lowerBound, args);
    const upper = callback.apply(range.upperBound, args);
    yield lower;
    if (upper !== lower) {
      yield upper;
    }
  });
}

registerCanonical(PercentRange_, PercentRange);

function calculateDebtYield(currentNetOperatingIncome, principalNeeded) {
  return currentNetOperatingIncome.divide(principalNeeded);
}
function calculateDebtYieldRange(currentNetOperatingIncome, principalNeeded) {
  let lowerBound = currentNetOperatingIncome.divide(principalNeeded.upperBound, Percent.getDefault());
  let upperBound = currentNetOperatingIncome.divide(principalNeeded.lowerBound, Percent.getDefault());
  if (lowerBound.isZero()) {
    lowerBound = upperBound;
  } else if (upperBound.isZero()) {
    upperBound = lowerBound;
  }
  return new PercentRange().setLowerBound(lowerBound).setUpperBound(upperBound);
}

function showOccupancyUnits(assetType) {
  return isMultiFamily(assetType) || assetType === AssetType.SELF_STORAGE;
}
function showOccupancyPercent(assetType) {
  return assetType === AssetType.RETAIL || isIndustrial(assetType) || isOffice(assetType) || assetType === AssetType.OTHER;
}
function getOccupancyLabel(assetType) {
  if (showOccupancyUnits(assetType)) {
    return `Number of Occupied ${getUnitLabelTitleCase(assetType)}`;
  }
  if (showOccupancyPercent(assetType)) {
    return 'Occupancy';
  }
  assert.fail(`Occupancy should not display for asset type ${assetType}`);
}

const financingPurposeToCapitalString = financingPurpose => FinancingPurpose.format(financingPurpose);

function buildingSizeIsActive(financingPurpose, assetType) {
  return assetType !== AssetType.LAND;
}
function footprintSizeIsActive(financingPurpose, assetType) {
  return assetType !== AssetType.LAND;
}
function capRateIsActive(financingPurpose) {
  return financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REFINANCE;
}
function proFormaCapRateIsActive(financingPurpose, investmentCategory) {
  return proFormaFieldsAreActive(financingPurpose, investmentCategory);
}
function acquisitionFieldsAreActive(unused_financingPurpose) {
  return true;
}
function acquisitionStatusIsActive(financingPurpose) {
  return financingPurpose === FinancingPurpose.ACQUISITION;
}
function ltvIsActive(financingPurpose) {
  return financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REDEVELOPMENT || financingPurpose === FinancingPurpose.REFINANCE;
}
function ltcIsActive(unused_financingPurpose) {
  return true;
}
function totalProjectBudgetIsActive(unused_financingPurpose) {
  return true;
}
function completedYearIsActive(financingPurpose, assetType) {
  return (financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REDEVELOPMENT || financingPurpose === FinancingPurpose.REFINANCE) && assetType !== AssetType.LAND;
}
function currentNetOperatingIncomeIsActive(financingPurpose) {
  return financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REDEVELOPMENT || financingPurpose === FinancingPurpose.REFINANCE;
}
function proFormaNetOperatingIncomeIsActive(financingPurpose, investmentCategory) {
  return proFormaFieldsAreActive(financingPurpose, investmentCategory);
}
function renovatedYearIsActive(financingPurpose, assetType) {
  return assetType !== AssetType.LAND && (financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REFINANCE);
}
function occupancyIsActive(financingPurpose, assetType) {
  return (financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REFINANCE) && (showOccupancyUnits(assetType) || showOccupancyPercent(assetType));
}
function investmentCategoryFieldIsActive(financingPurpose) {
  return financingPurpose === FinancingPurpose.ACQUISITION || financingPurpose === FinancingPurpose.REFINANCE;
}
function proFormaFieldsAreActive(financingPurpose, investmentCategory) {
  return financingPurpose === FinancingPurpose.NEW_DEVELOPMENT || financingPurpose === FinancingPurpose.REDEVELOPMENT || investmentCategory === 'Value Add' || investmentCategory === 'Vacant';
}

function _defineProperty$1I(obj, key, value) { key = _toPropertyKey$1I(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1I(arg) { var key = _toPrimitive$1I(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1I(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var EstimatedValuationType__;
(function (EstimatedValuationType__) {
  EstimatedValuationType__[EstimatedValuationType__["UNSET"] = 0] = "UNSET";
  EstimatedValuationType__[EstimatedValuationType__["CAP_RATE_VALUATION"] = 1] = "CAP_RATE_VALUATION";
  EstimatedValuationType__[EstimatedValuationType__["ACQUISITION_PRICE"] = 2] = "ACQUISITION_PRICE";
  EstimatedValuationType__[EstimatedValuationType__["LATEST_APPRAISAL_VALUE"] = 3] = "LATEST_APPRAISAL_VALUE";
})(EstimatedValuationType__ || (EstimatedValuationType__ = {}));
const EstimatedValuationType_ = defineEnumProperties(EstimatedValuationType__, [0, 1, 2, 3]);
const EstimatedValuationType = extendEnum('EstimatedValuationType', [["UNSET", 0], ["CAP_RATE_VALUATION", 1], ["ACQUISITION_PRICE", 2], ["LATEST_APPRAISAL_VALUE", 3]])(EstimatedValuationType_);
class EstimatedValuation_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message EstimatedValuation field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return EstimatedValuationType.fromStrictJson_(v, getFieldMap);
          }
          return EstimatedValuationType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message EstimatedValuation field value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(EstimatedValuation, 'EstimatedValuation', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message EstimatedValuation to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get value() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1I(EstimatedValuation_, protoName, DEV_MODE ? 'EstimatedValuation' : '[Unknown]');
let EstimatedValuation = EstimatedValuation_;
(function (_EstimatedValuation) {})(EstimatedValuation || (EstimatedValuation = {}));
Object.defineProperties(EstimatedValuation, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "value": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(EstimatedValuation.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(EstimatedValuation);
  Object.freeze(EstimatedValuation.prototype);
}

const Type = EstimatedValuationType;

// Grandfathered in param count 7
// eslint-disable-next-line max-params
function calculateEstimatedValuation(financingPurpose, acquisitionPrice, acquisitionDate, currentNetOperatingIncome, capRate, latestAppraisalValue, latestAppraisalDate) {
  const capRateValuation = _calculateCapRateValuation$1(currentNetOperatingIncome, capRate);

  // For refinance, prefer calculation based on cap rate.
  // https://github.com/StackSource/stacksource/issues/1119
  const capRateFirst = financingPurpose === FinancingPurpose.REFINANCE;
  if (capRateFirst && capRateIsActive(financingPurpose) && !capRateValuation.isZero()) {
    return ev(Type.CAP_RATE_VALUATION, capRateValuation);
  }
  if (acquisitionFieldsAreActive(financingPurpose) && (financingPurpose === FinancingPurpose.ACQUISITION || latestAppraisalValue.isZero() || latestAppraisalDate.lessThan(acquisitionDate)) && acquisitionDate.daysSince < 90 && !acquisitionPrice.isZero()) {
    return ev(Type.ACQUISITION_PRICE, acquisitionPrice);
  }
  if (latestAppraisalDate.daysSince < 90 && !latestAppraisalValue.isZero()) {
    return ev(Type.LATEST_APPRAISAL_VALUE, latestAppraisalValue);
  }
  if (capRateIsActive(financingPurpose) && !capRateValuation.isZero()) {
    return ev(Type.CAP_RATE_VALUATION, capRateValuation);
  }
  if (!acquisitionPrice.isZero()) {
    return ev(Type.ACQUISITION_PRICE, acquisitionPrice);
  }
  if (!latestAppraisalValue.isZero()) {
    return ev(Type.LATEST_APPRAISAL_VALUE, latestAppraisalValue);
  }
  // TODO(nathan): Should this even be here if cap rate isn't active?
  if (!capRateValuation.isZero()) {
    return ev(Type.CAP_RATE_VALUATION, capRateValuation);
  }
  return EstimatedValuation.getDefault();
}
function ev(type, value) {
  return new EstimatedValuation().setType(type).setValue(value);
}
function _calculateCapRateValuation$1(currentNetOperatingIncome, capRate) {
  if (capRate.toNumber()) {
    return calculateCapRateValuation(currentNetOperatingIncome, capRate);
  }
  return CurrencyValue.fromNumber(currentNetOperatingIncome.unit, 0);
}

const calculateLtv = (financingPurpose, acquisitionPrice, acquisitionDate, currentNetOperatingIncome, capRate, principalNeeded, latestAppraisalValue, latestAppraisalDate
// eslint-disable-next-line max-params
) => calculateLtvNew({
  acquisitionDate,
  acquisitionPrice,
  capRate,
  currentNetOperatingIncome,
  financingPurpose,
  latestAppraisalDate,
  latestAppraisalValue,
  principalNeeded
});
const calculateLtvNew = params => upperBound(calculateLtvRangeNew(params));
const calculateLtvRange = (financingPurpose, acquisitionPrice, acquisitionDate, currentNetOperatingIncome, capRate, principalNeeded, latestAppraisalValue, latestAppraisalDate
// eslint-disable-next-line max-params
) => calculateLtvRangeNew({
  acquisitionDate,
  acquisitionPrice,
  capRate,
  currentNetOperatingIncome,
  financingPurpose,
  latestAppraisalDate,
  latestAppraisalValue,
  principalNeeded
});
function calculateLtvRangeNew(params) {
  if (!ltvIsActive(params.financingPurpose)) {
    return null;
  }
  try {
    const denominator = getLtvDenominatorNew(params).value;
    return calculateLtvOrLtcRange(params.principalNeeded, denominator);
  } catch (x) {
    if (UnitMismatchError.isOfType(x) || LtvOrLtcOutOfBoundsError.isOfType(x)) {
      return null;
    }
    throw x;
  }
}
const calculateAsStabilizedLtv = (financingPurpose, proFormaCurrentNetOperatingIncome, proFormaCapRate, principalNeeded) => calculateAsStabilizedLtvNew({
  financingPurpose,
  principalNeeded,
  proFormaCapRate,
  proFormaCurrentNetOperatingIncome
});
const calculateAsStabilizedLtvNew = params => calculateLtvNew({
  financingPurpose: params.financingPurpose,
  acquisitionPrice: CurrencyValue.getDefault(),
  acquisitionDate: SimpleDate.getDefault(),
  currentNetOperatingIncome: params.proFormaCurrentNetOperatingIncome,
  capRate: params.proFormaCapRate,
  principalNeeded: params.principalNeeded,
  latestAppraisalValue: CurrencyValue.getDefault(),
  latestAppraisalDate: SimpleDate.getDefault()
});
const getLtvDenominator = (financingPurpose, acquisitionPrice, acquisitionDate, currentNetOperatingIncome, capRate, latestAppraisalValue, latestAppraisalDate
// eslint-disable-next-line max-params
) => getLtvDenominatorNew({
  acquisitionDate,
  acquisitionPrice,
  capRate,
  currentNetOperatingIncome,
  financingPurpose,
  latestAppraisalDate,
  latestAppraisalValue
});
function getLtvDenominatorNew(params) {
  if (params.propertyValue != null) {
    // Note: It might be a good idea to define a new EstimatedValuationType
    //   specifically for this case.
    return new EstimatedValuation().setType(EstimatedValuationType.LATEST_APPRAISAL_VALUE).setValue(params.propertyValue);
  }
  const value = calculateEstimatedValuation(params.financingPurpose, params.acquisitionPrice, params.acquisitionDate, params.currentNetOperatingIncome, params.capRate, params.latestAppraisalValue, params.latestAppraisalDate);
  if (value.type === EstimatedValuationType.UNSET || value.value.isZero()) {
    throw new LtvOrLtcOutOfBoundsError(NaN);
  }
  return value;
}
const calculateLtc = (financingPurpose, principalNeeded, totalProjectBudget) => upperBound(calculateLtcRange(financingPurpose, principalNeeded, totalProjectBudget));
const calculateLtcRange = (financingPurpose, principalNeeded, totalProjectBudget) => !ltcIsActive(financingPurpose) ? null : calculateLtvOrLtcRange(principalNeeded, totalProjectBudget);
function calculateLtvOrLtcRange(principalNeeded, denominator) {
  let lowerBound = calculateLtvOrLtcRangePart(principalNeeded.lowerBound, denominator);
  let upperBound = calculateLtvOrLtcRangePart(principalNeeded.upperBound, denominator);
  if (!lowerBound) {
    if (!upperBound) {
      return null;
    }
    lowerBound = upperBound;
  }
  if (!upperBound) {
    upperBound = lowerBound;
  }
  return new PercentRange().setLowerBound(lowerBound).setUpperBound(upperBound);
}
function calculateLtvOrLtcRangePart(principalNeededBound, denominator) {
  try {
    return calculateLtvOrLtc(principalNeededBound, [denominator]);
  } catch (x) {
    if (!(UnitMismatchError.isOfType(x) || LtvOrLtcOutOfBoundsError.isOfType(x))) {
      throw x;
    }
  }
  return null;
}
const upperBound = range => range != null ? range.upperBound : null;
function calculateLtvOrLtc(principalNeeded, potentialValues) {
  for (const u of potentialValues) {
    if (!u.isZero()) {
      if (principalNeeded.unit !== u.unit) {
        throw new UnitMismatchError(`Units must match; found "${principalNeeded.unit}" and "${u.unit}".`);
      }
      const value = +principalNeeded.value / +u.value;
      if (value < 0 || value > 1) {
        throw new LtvOrLtcOutOfBoundsError(value);
      }
      return percentFromNumber(value);
    }
  }
  throw new LtvOrLtcOutOfBoundsError(NaN);
}
const unitMismatchErrorTag = Symbol('UnitMismatchError');
class UnitMismatchError extends Error {
  // Due to limitations of ES5, extending `Error` isn't fully supported. There
  // is a workaround[1], but it relies on the use of `__proto__` which comes
  // with a significant and far-reaching performance penalty[2].
  //
  // Therefore, these error classes use alternate means to `instanceof` to
  // determine their kind.
  //
  // [1] http://stackoverflow.com/a/33877501
  // [2] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/proto
  //
  // TODO(nathan): One day we'll drop ES5 support and only compile to ES2015, in
  // which case we can start using `instanceof` instead of this workaround.
  static isOfType(value) {
    return !!value && unitMismatchErrorTag in value;
  }
  constructor(message) {
    super(message);
  }
  [unitMismatchErrorTag]() {
    return true;
  }
}
const ltvOrLtcOutOfBoundsErrorTag = Symbol('LtvOrLtcOutOfBoundsError');
class LtvOrLtcOutOfBoundsError extends Error {
  static isOfType(value) {
    return !!value && ltvOrLtcOutOfBoundsErrorTag in value;
  }
  constructor(value) {
    super(`LTV or LTC out of bounds (${value}).`);
  }
  [ltvOrLtcOutOfBoundsErrorTag]() {
    return true;
  }
}

// tslint:disable-next-line:no-unused-variable
var ValuationUnit__;
(function (ValuationUnit__) {
  ValuationUnit__[ValuationUnit__["SQUARE_FOOT"] = 0] = "SQUARE_FOOT";
  ValuationUnit__[ValuationUnit__["BUILDING_UNIT"] = 1] = "BUILDING_UNIT";
  ValuationUnit__[ValuationUnit__["KEY"] = 2] = "KEY";
})(ValuationUnit__ || (ValuationUnit__ = {}));
const ValuationUnit_ = defineEnumProperties(ValuationUnit__, [0, 1, 2]);
const ValuationUnit = extendEnum('ValuationUnit', [["SQUARE_FOOT", 0], ["BUILDING_UNIT", 1], ["KEY", 2]])(ValuationUnit_);

function _defineProperty$1H(obj, key, value) { key = _toPropertyKey$1H(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1H(arg) { var key = _toPrimitive$1H(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1H(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ValuationPerUnit_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ValuationPerUnit field unit.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ValuationUnit.fromStrictJson_(v, getFieldMap);
          }
          return ValuationUnit.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ValuationPerUnit field value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ValuationPerUnit$1, 'ValuationPerUnit', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ValuationPerUnit to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get unit() {
    return this.getFieldById(1, 0);
  }
  setUnit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUnit() {
    return this.setValueFieldById(1, null, null);
  }
  get value() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1H(ValuationPerUnit_, protoName, DEV_MODE ? 'ValuationPerUnit' : '[Unknown]');
let ValuationPerUnit$1 = ValuationPerUnit_;
(function (_ValuationPerUnit) {})(ValuationPerUnit$1 || (ValuationPerUnit$1 = {}));
Object.defineProperties(ValuationPerUnit$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "unit": {
        value: 1
      },
      "value": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "unit"
      },
      '2': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(ValuationPerUnit$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ValuationPerUnit$1);
  Object.freeze(ValuationPerUnit$1.prototype);
}

function formatValuationUnit(unit) {
  switch (unit) {
    case ValuationUnit.SQUARE_FOOT:
      return 'per sqft';
    case ValuationUnit.BUILDING_UNIT:
      return 'per unit';
    case ValuationUnit.KEY:
      return 'per key';
  }
  assert.fail(`Unexpected ValuationUnit: ${unit}`);
  return '';
}

class ValuationPerUnit extends ValuationPerUnit_ {
  formatUnit() {
    return formatValuationUnit(this.unit);
  }
}

registerCanonical(ValuationPerUnit_, ValuationPerUnit);

const getValuationUnitString = formatValuationUnit;
// Alias
const renderValuationUnit = formatValuationUnit;

function _defineProperty$1G(obj, key, value) { key = _toPropertyKey$1G(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1G(arg) { var key = _toPrimitive$1G(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1G(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function calculateValuationPerUnit(values, assetType, numberOfUnits, totalSize, suppressException = false) {
  assert(AssetType.has(assetType));
  let value = CurrencyValue.getDefault();
  for (const v of values) {
    if (!v.isZero()) {
      value = v;
      break;
    }
  }
  let unit = ValuationUnit.BUILDING_UNIT;
  let denominator = 0;
  if (!value.isZero()) {
    if (isMultiFamily(assetType)) {
      if (numberOfUnits) {
        denominator = numberOfUnits;
        unit = ValuationUnit.BUILDING_UNIT;
      }
    } else if (assetType === AssetType.HOTEL) {
      if (numberOfUnits) {
        denominator = numberOfUnits;
        unit = ValuationUnit.KEY;
      }
    } else {
      if (totalSize.amount) {
        denominator = totalSize.amount;
        unit = ValuationUnit.SQUARE_FOOT;
      }
    }
    if (denominator) {
      const calculatedValue = CurrencyValue.fromNumber(value.unit, value.toNumber() / denominator);
      return new ValuationPerUnit().setUnit(unit).setValue(calculatedValue);
    }
  }
  if (suppressException) {
    return ValuationPerUnit.getDefault();
  }
  throw new ValuationPerUnitError('Invalid input to calculateValuationPerUnit');
}
const valuationPerUnitErrorTag = Symbol('ValuationPerUnitError');
class ValuationPerUnitError extends Error {
  constructor(...args) {
    super(...args);
    _defineProperty$1G(this, valuationPerUnitErrorTag, true);
  }
  static isOfType(value) {
    return !!value && valuationPerUnitErrorTag in value;
  }
}

const PROTOTYPE_KEYS = new Set(Reflect.ownKeys(class {}.prototype));

// Note: All getters in this class are memoized. See the call to
// `memoizeGetters`.
class Deal extends Deal_ {
  formatAssetType() {
    return AssetType.format(this.assetType);
  }
  formatFinancingPurpose() {
    return FinancingPurpose.format(this.financingPurpose);
  }
  formatPropertyTitle() {
    return PropertyTitle.format(this.propertyTitle);
  }
  formatAcquisitionStatus() {
    return AcquisitionStatus.format(this.acquisitionStatus);
  }
  formatOccupancy(places = Infinity) {
    return this.occupancy.formatForNumberOfUnits(this.numberOfUnits, places);
  }
  formatInvestmentCategory() {
    return InvestmentCategory.format(this.investmentCategory);
  }
  get totalProjectBudget() {
    if (!this.deprecatedTotalProjectBudget.isZero()) {
      return this.deprecatedTotalProjectBudget;
    }
    return addCurrencies(...this.capStack.useList.map(u => u.principal));
  }
  get estimatedValuation() {
    return calculateEstimatedValuation(this.financingPurpose, this.acquisitionPrice, this.acquisitionDate, this.currentNetOperatingIncome, this.capRate, this.latestAppraisalValue, this.latestAppraisalDate);
  }
  get stabilizedEstimatedValuation() {
    return calculateEstimatedValuation(this.financingPurpose, CurrencyValue.getDefault(),
    // acquisitionPrice
    SimpleDate.getDefault(),
    // acquisitionDate
    this.proFormaNetOperatingIncome, this.proFormaCapRate, CurrencyValue.getDefault(),
    // latestAppraisalValue
    SimpleDate.getDefault() // LatestAppraisalDate
    );
  }

  get ltv() {
    return calculated(PercentRange.getDefault(), calculateLtvRange(this.financingPurpose, this.acquisitionPrice, this.acquisitionDate, this.currentNetOperatingIncome, this.capRate, this.principalNeeded, this.latestAppraisalValue, this.latestAppraisalDate));
  }
  get stabilizedLtv() {
    return calculated(PercentRange.getDefault(), calculateLtvRange(this.financingPurpose, CurrencyValue.getDefault(),
    // acquisitionPrice
    SimpleDate.getDefault(),
    // acquisitionDate
    this.proFormaNetOperatingIncome, this.proFormaCapRate, this.principalNeeded, CurrencyValue.getDefault(),
    // latestAppraisalValue
    SimpleDate.getDefault() // LatestAppraisalDate
    ));
  }

  get ltc() {
    return calculated(PercentRange.getDefault(), calculateLtcRange(this.financingPurpose, this.principalNeeded, this.totalProjectBudget));
  }
  get capRateValuation() {
    return _calculateCapRateValuation(this.currentNetOperatingIncome, this.capRate);
  }
  get stabilizedCapRateValuation() {
    return _calculateCapRateValuation(this.proFormaNetOperatingIncome, this.proFormaCapRate);
  }
  get debtYield() {
    return calculateDebtYieldRange(this.currentNetOperatingIncome, this.principalNeeded);
  }
  get stabilizedDebtYield() {
    return calculateDebtYieldRange(this.proFormaNetOperatingIncome, this.principalNeeded);
  }
  get valuationPerUnit() {
    return calculateValuationPerUnit([this.estimatedValuation.value], this.assetType, this.numberOfUnits, this.totalSize, true // suppressException
    );
  }
}

memoizeGetters(Deal);
function _calculateCapRateValuation(netOperatingIncome, capRate) {
  if (capRate.isZero()) {
    return CurrencyValue.getDefault();
  }
  return calculateCapRateValuation(netOperatingIncome, capRate);
}
function memoizeGetters(Class) {
  const newDescriptors = Object.create(null);
  let hasNewDescriptor = false;
  for (const [key, desc] of descriptorEntries(PROTOTYPE_KEYS, Class.prototype)) {
    if (desc.get) {
      const get = desc.get;
      const cache = new WeakMap();
      desc.get = function memoizedGetter() {
        if (cache.has(this)) {
          return cache.get(this);
        }
        const value = get.apply(this);
        cache.set(this, value);
        return value;
      };
      newDescriptors[key] = desc;
      hasNewDescriptor = true;
    }
  }
  if (hasNewDescriptor) {
    Object.defineProperties(Class.prototype, newDescriptors);
  }
}
function* descriptorEntries(filterSet, object) {
  const descriptors = Object.getOwnPropertyDescriptors(object);
  for (const key of Reflect.ownKeys(descriptors)) {
    if (typeof key === 'string' && !filterSet.has(key)) {
      yield [key, descriptors[key]];
    }
  }
}
function calculated(defaultValue, value) {
  if (value) {
    return value;
  }
  return defaultValue;
}

registerCanonical(Deal_, Deal);

function _defineProperty$1F(obj, key, value) { key = _toPropertyKey$1F(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1F(arg) { var key = _toPrimitive$1F(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1F(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _HotelFlag_Specification_;
(function (_HotelFlag_Specification_) {
  _HotelFlag_Specification_[_HotelFlag_Specification_["UNSET"] = 0] = "UNSET";
  _HotelFlag_Specification_[_HotelFlag_Specification_["ORGANIZATION_ID"] = 1] = "ORGANIZATION_ID";
  _HotelFlag_Specification_[_HotelFlag_Specification_["NAME"] = 2] = "NAME";
})(_HotelFlag_Specification_ || (_HotelFlag_Specification_ = {}));
const _HotelFlag_Specification = defineEnumProperties(_HotelFlag_Specification_, [1, 2]);
class HotelFlag_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 2,
      3: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message HotelFlag field organization_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message HotelFlag field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message HotelFlag field expiration_date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(HotelFlag, 'HotelFlag', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message HotelFlag to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get SpecificationCase() {
    return _HotelFlag_Specification;
  }
  get specificationCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get organizationId() {
    return this.getFieldById(1, 0);
  }
  setOrganizationId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "specification");
  }
  deleteOrganizationId() {
    return this.setValueFieldById(1, null, "specification");
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "specification");
  }
  deleteName() {
    return this.setValueFieldById(2, null, "specification");
  }
  get expirationDate() {
    return this.getFieldById(3, DEPRECATED_Date.getDefault());
  }
  setExpirationDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteExpirationDate() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$1F(HotelFlag_, protoName, DEV_MODE ? 'HotelFlag' : '[Unknown]');
let HotelFlag = HotelFlag_;
(function (_HotelFlag) {})(HotelFlag || (HotelFlag = {}));
Object.defineProperties(HotelFlag, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "organization_id": {
        value: 1
      },
      "organizationId": {
        value: 1
      },
      "name": {
        value: 2
      },
      "expiration_date": {
        value: 3
      },
      "expirationDate": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "organization_id"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "expiration_date"
      }
    }))
  }
});
Object.defineProperties(HotelFlag.prototype, {
  oneofs_: {
    value: {
      "specification": [1, 2]
    }
  },
  prototypes_: {
    value: {
      3: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(HotelFlag);
  Object.freeze(HotelFlag.prototype);
}

function _defineProperty$1E(obj, key, value) { key = _toPropertyKey$1E(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1E(arg) { var key = _toPrimitive$1E(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1E(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _Int32OrString_Type_;
(function (_Int32OrString_Type_) {
  _Int32OrString_Type_[_Int32OrString_Type_["UNSET"] = 0] = "UNSET";
  _Int32OrString_Type_[_Int32OrString_Type_["INT32_VALUE"] = 1] = "INT32_VALUE";
  _Int32OrString_Type_[_Int32OrString_Type_["STRING_VALUE"] = 2] = "STRING_VALUE";
})(_Int32OrString_Type_ || (_Int32OrString_Type_ = {}));
const _Int32OrString_Type = defineEnumProperties(_Int32OrString_Type_, [1, 2]);
class Int32OrString_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 5,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Int32OrString field int32_value.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Int32OrString field string_value.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Int32OrString, 'Int32OrString', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Int32OrString to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _Int32OrString_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get int32Value() {
    return this.getFieldById(1, 0);
  }
  setInt32Value(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteInt32Value() {
    return this.setValueFieldById(1, null, "type");
  }
  get stringValue() {
    return this.getFieldById(2, "");
  }
  setStringValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deleteStringValue() {
    return this.setValueFieldById(2, null, "type");
  }
}
_defineProperty$1E(Int32OrString_, protoName, DEV_MODE ? 'Int32OrString' : '[Unknown]');
let Int32OrString = Int32OrString_;
(function (_Int32OrString) {})(Int32OrString || (Int32OrString = {}));
Object.defineProperties(Int32OrString, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "int32_value": {
        value: 1
      },
      "int32Value": {
        value: 1
      },
      "string_value": {
        value: 2
      },
      "stringValue": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "int32_value"
      },
      '2': {
        value: "string_value"
      }
    }))
  }
});
Object.defineProperties(Int32OrString.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2]
    }
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Int32OrString);
  Object.freeze(Int32OrString.prototype);
}
class RepeatedInt32OrString_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Int32OrString.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedInt32OrString field value.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedInt32OrString, 'RepeatedInt32OrString', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedInt32OrString to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1E(RepeatedInt32OrString_, protoName, DEV_MODE ? 'RepeatedInt32OrString' : '[Unknown]');
let RepeatedInt32OrString = RepeatedInt32OrString_;
(function (_RepeatedInt32OrString) {})(RepeatedInt32OrString || (RepeatedInt32OrString = {}));
Object.defineProperties(RepeatedInt32OrString, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedInt32OrString.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Int32OrString.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedInt32OrString);
  Object.freeze(RepeatedInt32OrString.prototype);
}

function _defineProperty$1D(obj, key, value) { key = _toPropertyKey$1D(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1D(arg) { var key = _toPrimitive$1D(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1D(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _OptionalCurrencyValue_Exists_;
(function (_OptionalCurrencyValue_Exists_) {
  _OptionalCurrencyValue_Exists_[_OptionalCurrencyValue_Exists_["UNSET"] = 0] = "UNSET";
  _OptionalCurrencyValue_Exists_[_OptionalCurrencyValue_Exists_["VALUE"] = 1] = "VALUE";
})(_OptionalCurrencyValue_Exists_ || (_OptionalCurrencyValue_Exists_ = {}));
const _OptionalCurrencyValue_Exists = defineEnumProperties(_OptionalCurrencyValue_Exists_, [1]);
class OptionalCurrencyValue_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OptionalCurrencyValue field value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OptionalCurrencyValue, 'OptionalCurrencyValue', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OptionalCurrencyValue to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ExistsCase() {
    return _OptionalCurrencyValue_Exists;
  }
  get existsCase() {
    return this.getCaseFromIds([1]);
  }
  get value() {
    return this.getFieldById(1, CurrencyValue.getDefault());
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "exists");
  }
  deleteValue() {
    return this.setValueFieldById(1, null, "exists");
  }
}
_defineProperty$1D(OptionalCurrencyValue_, protoName, DEV_MODE ? 'OptionalCurrencyValue' : '[Unknown]');
let OptionalCurrencyValue = OptionalCurrencyValue_;
(function (_OptionalCurrencyValue) {})(OptionalCurrencyValue || (OptionalCurrencyValue = {}));
Object.defineProperties(OptionalCurrencyValue, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(OptionalCurrencyValue.prototype, {
  oneofs_: {
    value: {
      "exists": [1]
    }
  },
  prototypes_: {
    value: {
      1: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OptionalCurrencyValue);
  Object.freeze(OptionalCurrencyValue.prototype);
}
class RepeatedOptionalCurrencyValue_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: OptionalCurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(OptionalCurrencyValue);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedOptionalCurrencyValue field value.
                Expected type OptionalCurrencyValue.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'OptionalCurrencyValue', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedOptionalCurrencyValue, 'RepeatedOptionalCurrencyValue', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedOptionalCurrencyValue to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1D(RepeatedOptionalCurrencyValue_, protoName, DEV_MODE ? 'RepeatedOptionalCurrencyValue' : '[Unknown]');
let RepeatedOptionalCurrencyValue = RepeatedOptionalCurrencyValue_;
(function (_RepeatedOptionalCurrencyValue) {})(RepeatedOptionalCurrencyValue || (RepeatedOptionalCurrencyValue = {}));
Object.defineProperties(RepeatedOptionalCurrencyValue, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedOptionalCurrencyValue.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: OptionalCurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedOptionalCurrencyValue);
  Object.freeze(RepeatedOptionalCurrencyValue.prototype);
}

// tslint:disable-next-line:no-unused-variable
var ProjectExitScenario__;
(function (ProjectExitScenario__) {
  ProjectExitScenario__[ProjectExitScenario__["UNSET"] = 0] = "UNSET";
  ProjectExitScenario__[ProjectExitScenario__["SALE"] = 1] = "SALE";
  ProjectExitScenario__[ProjectExitScenario__["REFINANCE"] = 2] = "REFINANCE";
})(ProjectExitScenario__ || (ProjectExitScenario__ = {}));
const ProjectExitScenario_ = defineEnumProperties(ProjectExitScenario__, [0, 1, 2]);
const ProjectExitScenario = extendEnum('ProjectExitScenario', [["UNSET", 0], ["SALE", 1], ["REFINANCE", 2]])(ProjectExitScenario_);

function _defineProperty$1C(obj, key, value) { key = _toPropertyKey$1C(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1C(arg) { var key = _toPrimitive$1C(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1C(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var LoanTenor__;
(function (LoanTenor__) {
  LoanTenor__[LoanTenor__["NONE"] = 0] = "NONE";
  LoanTenor__[LoanTenor__["ONE_MONTH"] = 1] = "ONE_MONTH";
  LoanTenor__[LoanTenor__["THREE_MONTHS"] = 2] = "THREE_MONTHS";
  LoanTenor__[LoanTenor__["SIX_MONTHS"] = 3] = "SIX_MONTHS";
  LoanTenor__[LoanTenor__["ONE_YEAR"] = 4] = "ONE_YEAR";
  LoanTenor__[LoanTenor__["TWO_YEARS"] = 5] = "TWO_YEARS";
  LoanTenor__[LoanTenor__["THREE_YEARS"] = 6] = "THREE_YEARS";
  LoanTenor__[LoanTenor__["FIVE_YEARS"] = 7] = "FIVE_YEARS";
  LoanTenor__[LoanTenor__["SEVEN_YEARS"] = 8] = "SEVEN_YEARS";
  LoanTenor__[LoanTenor__["TEN_YEARS"] = 9] = "TEN_YEARS";
  LoanTenor__[LoanTenor__["TWENTY_YEARS"] = 10] = "TWENTY_YEARS";
  LoanTenor__[LoanTenor__["THIRTY_YEARS"] = 11] = "THIRTY_YEARS";
})(LoanTenor__ || (LoanTenor__ = {}));
const LoanTenor_ = defineEnumProperties(LoanTenor__, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
const LoanTenor = extendEnum('LoanTenor', [["NONE", 0], ["ONE_MONTH", 1], ["THREE_MONTHS", 2], ["SIX_MONTHS", 3], ["ONE_YEAR", 4], ["TWO_YEARS", 5], ["THREE_YEARS", 6], ["FIVE_YEARS", 7], ["SEVEN_YEARS", 8], ["TEN_YEARS", 9], ["TWENTY_YEARS", 10], ["THIRTY_YEARS", 11]])(LoanTenor_);
var RateIndexType__;
(function (RateIndexType__) {
  RateIndexType__[RateIndexType__["LIBOR"] = 0] = "LIBOR";
  RateIndexType__[RateIndexType__["LIBOR_SWAPS"] = 4] = "LIBOR_SWAPS";
  RateIndexType__[RateIndexType__["PRIME"] = 2] = "PRIME";
  RateIndexType__[RateIndexType__["TREASURIES"] = 1] = "TREASURIES";
  RateIndexType__[RateIndexType__["TREASURY_SWAPS"] = 3] = "TREASURY_SWAPS";
  RateIndexType__[RateIndexType__["SOFR"] = 5] = "SOFR";
  RateIndexType__[RateIndexType__["SOFR_SWAP"] = 7] = "SOFR_SWAP";
  RateIndexType__[RateIndexType__["CME_TERM_SOFR"] = 6] = "CME_TERM_SOFR";
})(RateIndexType__ || (RateIndexType__ = {}));
const RateIndexType_ = defineEnumProperties(RateIndexType__, [0, 4, 2, 1, 3, 5, 7, 6]);
const RateIndexType = extendEnum('RateIndexType', [["LIBOR", 0], ["LIBOR_SWAPS", 4], ["PRIME", 2], ["TREASURIES", 1], ["TREASURY_SWAPS", 3], ["SOFR", 5], ["SOFR_SWAP", 7], ["CME_TERM_SOFR", 6]])(RateIndexType_);
class RateIndex_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: DEPRECATED_Date.shape,
      3: 0,
      4: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RateIndex field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RateIndexType.fromStrictJson_(v, getFieldMap);
          }
          return RateIndexType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message RateIndex field valid_date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RateIndex field tenor.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanTenor.fromStrictJson_(v, getFieldMap);
          }
          return LoanTenor.from(value);
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message RateIndex field rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RateIndex, 'RateIndex', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RateIndex to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get validDate() {
    return this.getFieldById(2, DEPRECATED_Date.getDefault());
  }
  setValidDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteValidDate() {
    return this.setValueFieldById(2, null, null);
  }
  get tenor() {
    return this.getFieldById(3, 0);
  }
  setTenor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteTenor() {
    return this.setValueFieldById(3, null, null);
  }
  get rate() {
    return this.getFieldById(4, Percent.getDefault());
  }
  setRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteRate() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$1C(RateIndex_, protoName, DEV_MODE ? 'RateIndex' : '[Unknown]');
let RateIndex = RateIndex_;
(function (_RateIndex) {})(RateIndex || (RateIndex = {}));
Object.defineProperties(RateIndex, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "valid_date": {
        value: 2
      },
      "validDate": {
        value: 2
      },
      "tenor": {
        value: 3
      },
      "rate": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "valid_date"
      },
      '3': {
        value: "tenor"
      },
      '4': {
        value: "rate"
      }
    }))
  }
});
Object.defineProperties(RateIndex.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: DEPRECATED_Date.prototype,
      4: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RateIndex);
  Object.freeze(RateIndex.prototype);
}
class RateIndexTypeAndTenor_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RateIndexTypeAndTenor field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RateIndexType.fromStrictJson_(v, getFieldMap);
          }
          return RateIndexType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RateIndexTypeAndTenor field tenor.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanTenor.fromStrictJson_(v, getFieldMap);
          }
          return LoanTenor.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RateIndexTypeAndTenor, 'RateIndexTypeAndTenor', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RateIndexTypeAndTenor to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get tenor() {
    return this.getFieldById(2, 0);
  }
  setTenor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteTenor() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1C(RateIndexTypeAndTenor_, protoName, DEV_MODE ? 'RateIndexTypeAndTenor' : '[Unknown]');
let RateIndexTypeAndTenor = RateIndexTypeAndTenor_;
(function (_RateIndexTypeAndTenor) {})(RateIndexTypeAndTenor || (RateIndexTypeAndTenor = {}));
Object.defineProperties(RateIndexTypeAndTenor, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "tenor": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "tenor"
      }
    }))
  }
});
Object.defineProperties(RateIndexTypeAndTenor.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RateIndexTypeAndTenor);
  Object.freeze(RateIndexTypeAndTenor.prototype);
}

function _defineProperty$1B(obj, key, value) { key = _toPropertyKey$1B(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1B(arg) { var key = _toPrimitive$1B(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1B(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class RateIndexProjections_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 0,
      3: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RateIndexProjections field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RateIndexType.fromStrictJson_(v, getFieldMap);
          }
          return RateIndexType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message RateIndexProjections field tenor.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanTenor.fromStrictJson_(v, getFieldMap);
          }
          return LoanTenor.from(value);
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Percent);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RateIndexProjections field yearly_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RateIndexProjections, 'RateIndexProjections', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RateIndexProjections to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get tenor() {
    return this.getFieldById(2, 0);
  }
  setTenor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteTenor() {
    return this.setValueFieldById(2, null, null);
  }
  get yearlyRateList() {
    return this.getRepeatedFieldById(3);
  }
  hasYearlyRate(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addYearlyRate(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachYearlyRate(values) {
    let T = this;
    for (const value of values) {
      T = T.addYearlyRate(value);
    }
    return T;
  }
  setYearlyRate(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeYearlyRate(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearYearlyRateList() {
    return this.clearRepeatedFieldById(3);
  }
  setYearlyRateList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
}
_defineProperty$1B(RateIndexProjections_, protoName, DEV_MODE ? 'RateIndexProjections' : '[Unknown]');
let RateIndexProjections = RateIndexProjections_;
(function (_RateIndexProjections) {})(RateIndexProjections || (RateIndexProjections = {}));
Object.defineProperties(RateIndexProjections, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "tenor": {
        value: 2
      },
      "yearly_rate": {
        value: 3
      },
      "yearlyRate": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "tenor"
      },
      '3': {
        value: "yearly_rate"
      }
    }))
  }
});
Object.defineProperties(RateIndexProjections.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RateIndexProjections);
  Object.freeze(RateIndexProjections.prototype);
}
class RepeatedRateIndexProjections_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: RateIndexProjections.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RateIndexProjections);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedRateIndexProjections field value.
                Expected type RateIndexProjections.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RateIndexProjections', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedRateIndexProjections, 'RepeatedRateIndexProjections', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedRateIndexProjections to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1B(RepeatedRateIndexProjections_, protoName, DEV_MODE ? 'RepeatedRateIndexProjections' : '[Unknown]');
let RepeatedRateIndexProjections = RepeatedRateIndexProjections_;
(function (_RepeatedRateIndexProjections) {})(RepeatedRateIndexProjections || (RepeatedRateIndexProjections = {}));
Object.defineProperties(RepeatedRateIndexProjections, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedRateIndexProjections.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: RateIndexProjections.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedRateIndexProjections);
  Object.freeze(RepeatedRateIndexProjections.prototype);
}

function _defineProperty$1A(obj, key, value) { key = _toPropertyKey$1A(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1A(arg) { var key = _toPrimitive$1A(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1A(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectProjections_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 7,
      3: OptionalCurrencyValue.shape,
      4: RateIndexProjections.shape,
      5: CurrencyValue.shape,
      6: 0,
      7: CurrencyOrPercent.shape,
      8: CurrencyOrPercent.shape,
      9: Percent.shape,
      10: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectProjections field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectProjections field project_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(OptionalCurrencyValue);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field net_operating_income.
                Expected type OptionalCurrencyValue.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'OptionalCurrencyValue', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 4:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RateIndexProjections);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field rate_indexes.
                Expected type RateIndexProjections.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RateIndexProjections', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 5:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CurrencyValue);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field yearly_reserves.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectProjections field exit_scenario.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectExitScenario.fromStrictJson_(v, getFieldMap);
          }
          return ProjectExitScenario.from(value);
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyOrPercent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field selling_cost.
                Expected type CurrencyOrPercent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyOrPercent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyOrPercent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field refinance_cost.
                Expected type CurrencyOrPercent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyOrPercent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field exit_cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectProjections field refinance_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectProjections, 'ProjectProjections', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectProjections to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get projectId() {
    return this.getFieldById(2, 0);
  }
  setProjectId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteProjectId() {
    return this.setValueFieldById(2, null, null);
  }
  get netOperatingIncomeList() {
    return this.getRepeatedFieldById(3);
  }
  hasNetOperatingIncome(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addNetOperatingIncome(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachNetOperatingIncome(values) {
    let T = this;
    for (const value of values) {
      T = T.addNetOperatingIncome(value);
    }
    return T;
  }
  setNetOperatingIncome(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeNetOperatingIncome(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearNetOperatingIncomeList() {
    return this.clearRepeatedFieldById(3);
  }
  setNetOperatingIncomeList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
  get rateIndexesList() {
    return this.getRepeatedFieldById(4);
  }
  hasRateIndexes(value) {
    return this.repeatedFieldByIdHas(4, value);
  }
  addRateIndexes(value) {
    return this.addToRepeatedFieldById(4, value);
  }
  addEachRateIndexes(values) {
    let T = this;
    for (const value of values) {
      T = T.addRateIndexes(value);
    }
    return T;
  }
  setRateIndexes(index, value) {
    return this.setRepeatedFieldById(4, index, value);
  }
  removeRateIndexes(index) {
    return this.removeFromRepeatedFieldById(4, index);
  }
  clearRateIndexesList() {
    return this.clearRepeatedFieldById(4);
  }
  setRateIndexesList(values) {
    return this.overwriteRepeatedFieldById(4, values);
  }
  get yearlyReservesList() {
    return this.getRepeatedFieldById(5);
  }
  hasYearlyReserves(value) {
    return this.repeatedFieldByIdHas(5, value);
  }
  addYearlyReserves(value) {
    return this.addToRepeatedFieldById(5, value);
  }
  addEachYearlyReserves(values) {
    let T = this;
    for (const value of values) {
      T = T.addYearlyReserves(value);
    }
    return T;
  }
  setYearlyReserves(index, value) {
    return this.setRepeatedFieldById(5, index, value);
  }
  removeYearlyReserves(index) {
    return this.removeFromRepeatedFieldById(5, index);
  }
  clearYearlyReservesList() {
    return this.clearRepeatedFieldById(5);
  }
  setYearlyReservesList(values) {
    return this.overwriteRepeatedFieldById(5, values);
  }
  get exitScenario() {
    return this.getFieldById(6, 0);
  }
  setExitScenario(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteExitScenario() {
    return this.setValueFieldById(6, null, null);
  }
  get sellingCost() {
    return this.getFieldById(7, CurrencyOrPercent.getDefault());
  }
  setSellingCost(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteSellingCost() {
    return this.setValueFieldById(7, null, null);
  }
  get refinanceCost() {
    return this.getFieldById(8, CurrencyOrPercent.getDefault());
  }
  setRefinanceCost(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteRefinanceCost() {
    return this.setValueFieldById(8, null, null);
  }
  get exitCapRate() {
    return this.getFieldById(9, Percent.getDefault());
  }
  setExitCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteExitCapRate() {
    return this.setValueFieldById(9, null, null);
  }
  get refinanceLtv() {
    return this.getFieldById(10, Percent.getDefault());
  }
  setRefinanceLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteRefinanceLtv() {
    return this.setValueFieldById(10, null, null);
  }
}
_defineProperty$1A(ProjectProjections_, protoName, DEV_MODE ? 'ProjectProjections' : '[Unknown]');
let ProjectProjections = ProjectProjections_;
(function (_ProjectProjections) {})(ProjectProjections || (ProjectProjections = {}));
Object.defineProperties(ProjectProjections, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "project_id": {
        value: 2
      },
      "projectId": {
        value: 2
      },
      "net_operating_income": {
        value: 3
      },
      "netOperatingIncome": {
        value: 3
      },
      "rate_indexes": {
        value: 4
      },
      "rateIndexes": {
        value: 4
      },
      "yearly_reserves": {
        value: 5
      },
      "yearlyReserves": {
        value: 5
      },
      "exit_scenario": {
        value: 6
      },
      "exitScenario": {
        value: 6
      },
      "selling_cost": {
        value: 7
      },
      "sellingCost": {
        value: 7
      },
      "refinance_cost": {
        value: 8
      },
      "refinanceCost": {
        value: 8
      },
      "exit_cap_rate": {
        value: 9
      },
      "exitCapRate": {
        value: 9
      },
      "refinance_ltv": {
        value: 10
      },
      "refinanceLtv": {
        value: 10
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "project_id"
      },
      '3': {
        value: "net_operating_income"
      },
      '4': {
        value: "rate_indexes"
      },
      '5': {
        value: "yearly_reserves"
      },
      '6': {
        value: "exit_scenario"
      },
      '7': {
        value: "selling_cost"
      },
      '8': {
        value: "refinance_cost"
      },
      '9': {
        value: "exit_cap_rate"
      },
      '10': {
        value: "refinance_ltv"
      }
    }))
  }
});
Object.defineProperties(ProjectProjections.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: OptionalCurrencyValue.prototype,
      4: RateIndexProjections.prototype,
      5: CurrencyValue.prototype,
      7: CurrencyOrPercent.prototype,
      8: CurrencyOrPercent.prototype,
      9: Percent.prototype,
      10: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectProjections);
  Object.freeze(ProjectProjections.prototype);
}
class RepeatedProjectProjections_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: ProjectProjections.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectProjections);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedProjectProjections field value.
                Expected type ProjectProjections.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectProjections', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedProjectProjections, 'RepeatedProjectProjections', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedProjectProjections to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1A(RepeatedProjectProjections_, protoName, DEV_MODE ? 'RepeatedProjectProjections' : '[Unknown]');
let RepeatedProjectProjections = RepeatedProjectProjections_;
(function (_RepeatedProjectProjections) {})(RepeatedProjectProjections || (RepeatedProjectProjections = {}));
Object.defineProperties(RepeatedProjectProjections, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedProjectProjections.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: ProjectProjections.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedProjectProjections);
  Object.freeze(RepeatedProjectProjections.prototype);
}

function _defineProperty$1z(obj, key, value) { key = _toPropertyKey$1z(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1z(arg) { var key = _toPrimitive$1z(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1z(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectTenancy_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: Int32OrString.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectTenancy field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DealTenancyType.fromStrictJson_(v, getFieldMap);
          }
          return DealTenancyType.from(value);
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectTenancy field tenant.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectTenancy, 'ProjectTenancy', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectTenancy to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get tenantList() {
    return this.getRepeatedFieldById(2);
  }
  hasTenant(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addTenant(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachTenant(values) {
    let T = this;
    for (const value of values) {
      T = T.addTenant(value);
    }
    return T;
  }
  setTenant(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeTenant(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearTenantList() {
    return this.clearRepeatedFieldById(2);
  }
  setTenantList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
}
_defineProperty$1z(ProjectTenancy_, protoName, DEV_MODE ? 'ProjectTenancy' : '[Unknown]');
let ProjectTenancy = ProjectTenancy_;
(function (_ProjectTenancy) {})(ProjectTenancy || (ProjectTenancy = {}));
Object.defineProperties(ProjectTenancy, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "tenant": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "tenant"
      }
    }))
  }
});
Object.defineProperties(ProjectTenancy.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: Int32OrString.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectTenancy);
  Object.freeze(ProjectTenancy.prototype);
}

function _defineProperty$1y(obj, key, value) { key = _toPropertyKey$1y(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1y(arg) { var key = _toPrimitive$1y(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1y(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var FreddieSblDealType__;
(function (FreddieSblDealType__) {
  FreddieSblDealType__[FreddieSblDealType__["UNSET"] = 0] = "UNSET";
  FreddieSblDealType__[FreddieSblDealType__["ACQUISITION"] = 1] = "ACQUISITION";
  FreddieSblDealType__[FreddieSblDealType__["REFINANCE"] = 2] = "REFINANCE";
})(FreddieSblDealType__ || (FreddieSblDealType__ = {}));
const FreddieSblDealType_ = defineEnumProperties(FreddieSblDealType__, [0, 1, 2]);
const FreddieSblDealType = extendEnum('FreddieSblDealType', [["UNSET", 0], ["ACQUISITION", 1], ["REFINANCE", 2]])(FreddieSblDealType_);
var FreddieSblInterestOnlyTerm__;
(function (FreddieSblInterestOnlyTerm__) {
  FreddieSblInterestOnlyTerm__[FreddieSblInterestOnlyTerm__["NONE"] = 0] = "NONE";
  FreddieSblInterestOnlyTerm__[FreddieSblInterestOnlyTerm__["ONE_YEAR"] = 1] = "ONE_YEAR";
  FreddieSblInterestOnlyTerm__[FreddieSblInterestOnlyTerm__["TWO_YEARS"] = 2] = "TWO_YEARS";
  FreddieSblInterestOnlyTerm__[FreddieSblInterestOnlyTerm__["THREE_YEARS"] = 3] = "THREE_YEARS";
  FreddieSblInterestOnlyTerm__[FreddieSblInterestOnlyTerm__["FULL_TERM"] = 4] = "FULL_TERM";
})(FreddieSblInterestOnlyTerm__ || (FreddieSblInterestOnlyTerm__ = {}));
const FreddieSblInterestOnlyTerm_ = defineEnumProperties(FreddieSblInterestOnlyTerm__, [0, 1, 2, 3, 4]);
const FreddieSblInterestOnlyTerm = extendEnum('FreddieSblInterestOnlyTerm', [["NONE", 0], ["ONE_YEAR", 1], ["TWO_YEARS", 2], ["THREE_YEARS", 3], ["FULL_TERM", 4]])(FreddieSblInterestOnlyTerm_);
var FreddieSblPrepaymentPenalty__;
(function (FreddieSblPrepaymentPenalty__) {
  FreddieSblPrepaymentPenalty__[FreddieSblPrepaymentPenalty__["STANDARD"] = 0] = "STANDARD";
  FreddieSblPrepaymentPenalty__[FreddieSblPrepaymentPenalty__["YIELD_MAINTENANCE"] = 1] = "YIELD_MAINTENANCE";
  FreddieSblPrepaymentPenalty__[FreddieSblPrepaymentPenalty__["SOFT_STEP_DOWN"] = 2] = "SOFT_STEP_DOWN";
  FreddieSblPrepaymentPenalty__[FreddieSblPrepaymentPenalty__["MODIFIED_STEP_DOWN"] = 3] = "MODIFIED_STEP_DOWN";
})(FreddieSblPrepaymentPenalty__ || (FreddieSblPrepaymentPenalty__ = {}));
const FreddieSblPrepaymentPenalty_ = defineEnumProperties(FreddieSblPrepaymentPenalty__, [0, 1, 2, 3]);
const FreddieSblPrepaymentPenalty = extendEnum('FreddieSblPrepaymentPenalty', [["STANDARD", 0], ["YIELD_MAINTENANCE", 1], ["SOFT_STEP_DOWN", 2], ["MODIFIED_STEP_DOWN", 3]])(FreddieSblPrepaymentPenalty_);
class FreddieSblInputs_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Percent.shape,
      2: Percent.shape,
      3: CurrencyValue.shape,
      4: Percent.shape,
      5: CurrencyValue.shape,
      6: CurrencyValue.shape,
      7: 7,
      8: 0,
      9: 0,
      10: 0,
      11: Percent.shape,
      12: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field selected_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field selected_dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field noi.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field acquisition_price.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field property_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message FreddieSblInputs field zip.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message FreddieSblInputs field deal_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblDealType.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblDealType.from(value);
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message FreddieSblInputs field interest_only_term.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblInterestOnlyTerm.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblInterestOnlyTerm.from(value);
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message FreddieSblInputs field prepayment_penalty.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblPrepaymentPenalty.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblPrepaymentPenalty.from(value);
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field qualified_uncapped.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FreddieSblInputs field qualified_vli.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FreddieSblInputs, 'FreddieSblInputs', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FreddieSblInputs to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get selectedLtv() {
    return this.getFieldById(1, Percent.getDefault());
  }
  setSelectedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSelectedLtv() {
    return this.setValueFieldById(1, null, null);
  }
  get selectedDscr() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setSelectedDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSelectedDscr() {
    return this.setValueFieldById(2, null, null);
  }
  get noi() {
    return this.getFieldById(3, CurrencyValue.getDefault());
  }
  setNoi(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteNoi() {
    return this.setValueFieldById(3, null, null);
  }
  get capRate() {
    return this.getFieldById(4, Percent.getDefault());
  }
  setCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteCapRate() {
    return this.setValueFieldById(4, null, null);
  }
  get acquisitionPrice() {
    return this.getFieldById(5, CurrencyValue.getDefault());
  }
  setAcquisitionPrice(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteAcquisitionPrice() {
    return this.setValueFieldById(5, null, null);
  }
  get propertyValue() {
    return this.getFieldById(6, CurrencyValue.getDefault());
  }
  setPropertyValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deletePropertyValue() {
    return this.setValueFieldById(6, null, null);
  }
  get zip() {
    return this.getFieldById(7, 0);
  }
  setZip(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteZip() {
    return this.setValueFieldById(7, null, null);
  }
  get dealType() {
    return this.getFieldById(8, 0);
  }
  setDealType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteDealType() {
    return this.setValueFieldById(8, null, null);
  }
  get interestOnlyTerm() {
    return this.getFieldById(9, 0);
  }
  setInterestOnlyTerm(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteInterestOnlyTerm() {
    return this.setValueFieldById(9, null, null);
  }
  get prepaymentPenalty() {
    return this.getFieldById(10, 0);
  }
  setPrepaymentPenalty(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deletePrepaymentPenalty() {
    return this.setValueFieldById(10, null, null);
  }
  get qualifiedUncapped() {
    return this.getFieldById(11, Percent.getDefault());
  }
  setQualifiedUncapped(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteQualifiedUncapped() {
    return this.setValueFieldById(11, null, null);
  }
  get qualifiedVli() {
    return this.getFieldById(12, Percent.getDefault());
  }
  setQualifiedVli(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteQualifiedVli() {
    return this.setValueFieldById(12, null, null);
  }
}
_defineProperty$1y(FreddieSblInputs_, protoName, DEV_MODE ? 'FreddieSblInputs' : '[Unknown]');
let FreddieSblInputs = FreddieSblInputs_;
(function (_FreddieSblInputs) {})(FreddieSblInputs || (FreddieSblInputs = {}));
Object.defineProperties(FreddieSblInputs, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "selected_ltv": {
        value: 1
      },
      "selectedLtv": {
        value: 1
      },
      "selected_dscr": {
        value: 2
      },
      "selectedDscr": {
        value: 2
      },
      "noi": {
        value: 3
      },
      "cap_rate": {
        value: 4
      },
      "capRate": {
        value: 4
      },
      "acquisition_price": {
        value: 5
      },
      "acquisitionPrice": {
        value: 5
      },
      "property_value": {
        value: 6
      },
      "propertyValue": {
        value: 6
      },
      "zip": {
        value: 7
      },
      "deal_type": {
        value: 8
      },
      "dealType": {
        value: 8
      },
      "interest_only_term": {
        value: 9
      },
      "interestOnlyTerm": {
        value: 9
      },
      "prepayment_penalty": {
        value: 10
      },
      "prepaymentPenalty": {
        value: 10
      },
      "qualified_uncapped": {
        value: 11
      },
      "qualifiedUncapped": {
        value: 11
      },
      "qualified_vli": {
        value: 12
      },
      "qualifiedVli": {
        value: 12
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "selected_ltv"
      },
      '2': {
        value: "selected_dscr"
      },
      '3': {
        value: "noi"
      },
      '4': {
        value: "cap_rate"
      },
      '5': {
        value: "acquisition_price"
      },
      '6': {
        value: "property_value"
      },
      '7': {
        value: "zip"
      },
      '8': {
        value: "deal_type"
      },
      '9': {
        value: "interest_only_term"
      },
      '10': {
        value: "prepayment_penalty"
      },
      '11': {
        value: "qualified_uncapped"
      },
      '12': {
        value: "qualified_vli"
      }
    }))
  }
});
Object.defineProperties(FreddieSblInputs.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Percent.prototype,
      2: Percent.prototype,
      3: CurrencyValue.prototype,
      4: Percent.prototype,
      5: CurrencyValue.prototype,
      6: CurrencyValue.prototype,
      11: Percent.prototype,
      12: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FreddieSblInputs);
  Object.freeze(FreddieSblInputs.prototype);
}

function _defineProperty$1x(obj, key, value) { key = _toPropertyKey$1x(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1x(arg) { var key = _toPrimitive$1x(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1x(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Project_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      100: 2,
      57: 1,
      95: 1,
      96: 7,
      58: 1,
      66: 1,
      2: 1,
      3: 7,
      65: 1,
      64: Int32OrString.shape,
      94: Int32OrString.shape,
      4: 1,
      71: 0,
      72: ProjectLostReason.shape,
      5: 0,
      6: 0,
      49: 0,
      91: HotelFlag.shape,
      92: CurrencyValue.shape,
      93: Percent.shape,
      63: ProjectTenancy.shape,
      7: 2,
      73: 2,
      8: StandardUsAddress.shape,
      9: CurrencyValue.shape,
      10: 7,
      11: RichText.shape,
      12: Area.shape,
      13: Area.shape,
      14: Area.shape,
      15: 2,
      42: 2,
      43: 2,
      37: 2,
      44: 2,
      45: 2,
      16: 7,
      51: 7,
      17: 7,
      18: 7,
      98: 7,
      19: RichText.shape,
      20: 0,
      21: RichText.shape,
      22: CurrencyValue.shape,
      38: SimpleDate.shape,
      97: SimpleDate.shape,
      39: CurrencyValue.shape,
      77: CurrencyValue.shape,
      40: Percent.shape,
      78: Percent.shape,
      23: RichText.shape,
      24: CurrencyValue.shape,
      25: SimpleDate.shape,
      48: 0,
      26: CurrencyValueRange.shape,
      27: 7,
      28: FinancialInstruments.shape,
      79: 0,
      80: 1,
      81: 7,
      82: UnitInformation.shape,
      83: Percent.shape,
      84: Percent.shape,
      85: Percent.shape,
      86: Percent.shape,
      87: 0,
      88: 0,
      46: OfferExpectations.shape,
      30: CapStackItem.shape,
      75: CapStackTarget.shape,
      31: 2,
      32: Attachment.shape,
      33: SponsorProfile.shape,
      74: ProjectGuarantor.shape,
      90: Int32OrString.shape,
      41: RichText.shape,
      34: 7,
      101: 2,
      35: 7,
      36: 7,
      69: DEPRECATED_Date.shape,
      89: 7,
      70: 0,
      47: 7,
      50: DEPRECATED_Date.shape,
      52: Occupancy.shape,
      53: CapStackItem.shape,
      54: PropertyDisclaimer.shape,
      55: PropertyDemographics.shape,
      76: 2,
      56: ProjectProjections.shape,
      59: 1,
      61: ProjectFee.shape,
      62: 7,
      68: 1,
      67: 0,
      99: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 100:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 57:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field approved.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 95:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field approval_requested.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 96:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field approval_requested_by.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 58:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field nda_required.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 66:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field stacksource_agreement_signed.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field user_has_write_permission.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field owner.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 65:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field sponsor_email_verified.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 64:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field sponsor_user.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 94:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field broker_user.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field draft.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 71:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectStatus.fromStrictJson_(v, getFieldMap);
          }
          return ProjectStatus.from(value);
        };
      case 72:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectLostReason);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field lost_reason.
                Expected type ProjectLostReason.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectLostReason', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field financing_purpose.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FinancingPurpose.fromStrictJson_(v, getFieldMap);
          }
          return FinancingPurpose.from(value);
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field asset_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AssetType.fromStrictJson_(v, getFieldMap);
          }
          return AssetType.from(value);
        };
      case 49:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field sponsor_role.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return SponsorRole.fromStrictJson_(v, getFieldMap);
          }
          return SponsorRole.from(value);
        };
      case 91:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(HotelFlag);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field hotel_flag.
                Expected type HotelFlag.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'HotelFlag', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 92:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field hotel_average_daily_rate.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 93:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field hotel_average_occupancy_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 63:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectTenancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field tenancy.
                Expected type ProjectTenancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectTenancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 73:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field deprecated_total_project_budget.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field number_of_units.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field project_summary.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field total_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field footprint_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field lot_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field architect.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 42:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field structural_engineer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 43:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field construction_manager.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 37:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field general_contractor.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 44:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field leasing_representative.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 45:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field property_manager.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 16:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field completed_year.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 51:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field renovated_year.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 17:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field floors_above_ground.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 18:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field floors_below_ground.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 98:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field parking_spots.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 19:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field design_info.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 20:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field property_title.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return PropertyTitle.fromStrictJson_(v, getFieldMap);
          }
          return PropertyTitle.from(value);
        };
      case 21:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field asset_notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 22:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field latest_appraisal_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 38:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field latest_appraisal_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 97:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field target_close_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 39:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field current_net_operating_income.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 77:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field pro_forma_net_operating_income.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 40:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 78:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field pro_forma_cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 23:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field financial_notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 24:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field acquisition_price.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 25:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field acquisition_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 48:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field acquisition_status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AcquisitionStatus.fromStrictJson_(v, getFieldMap);
          }
          return AcquisitionStatus.from(value);
        };
      case 26:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValueRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field principal_needed.
                Expected type CurrencyValueRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValueRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 27:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field lien_position.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 28:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FinancialInstruments);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field financial_instruments.
                Expected type FinancialInstruments.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FinancialInstruments', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 79:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field affordability.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AffordabilityType.fromStrictJson_(v, getFieldMap);
          }
          return AffordabilityType.from(value);
        };
      case 80:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field has_elevator.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 81:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field number_of_floors.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 82:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UnitInformation);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field unit_information.
                Expected type UnitInformation.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UnitInformation', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 83:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field lihtc_ratio.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 84:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field hud_selected_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 85:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field freddie_sbl_selected_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 86:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field freddie_sbl_selected_dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 87:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field freddie_sbl_interest_only_term.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblInterestOnlyTerm.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblInterestOnlyTerm.from(value);
        };
      case 88:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field freddie_sbl_prepayment_penalty.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblPrepaymentPenalty.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblPrepaymentPenalty.from(value);
        };
      case 46:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferExpectations);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field offer_expectations.
                Expected type OfferExpectations.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferExpectations', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 30:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field cap_stack_item.
                Expected type CapStackItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 75:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackTarget);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field cap_stack_target.
                Expected type CapStackTarget.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackTarget', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 31:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field image.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 32:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Attachment);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field attachment.
                Expected type Attachment.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Attachment', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 33:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SponsorProfile);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field sponsor_profile.
                Expected type SponsorProfile.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SponsorProfile', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 74:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectGuarantor);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field guarantor.
                Expected type ProjectGuarantor.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectGuarantor', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 90:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field solicited_lender.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 41:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field note.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 34:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field share_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 101:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field share_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 35:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 36:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field accepted_offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 69:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field offer_accepted_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 89:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field offer_underwriting_requested.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 70:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field project_stage.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectStage.fromStrictJson_(v, getFieldMap);
          }
          return ProjectStage.from(value);
        };
      case 47:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field leading_offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 50:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field creation_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 52:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Occupancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field occupancy.
                Expected type Occupancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Occupancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 53:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(CapStackItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Project field use.
                Expected type CapStackItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'CapStackItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 54:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PropertyDisclaimer);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field disclaimer.
                Expected type PropertyDisclaimer.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PropertyDisclaimer', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 55:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PropertyDemographics);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field demographics.
                Expected type PropertyDemographics.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PropertyDemographics', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 76:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Project field investment_category.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 56:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectProjections);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field sponsor_projections.
                Expected type ProjectProjections.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectProjections', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 59:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field show_concealed_shares.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 61:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectFee);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field project_fee.
                Expected type ProjectFee.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectFee', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 62:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Project field capital_advisor.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 68:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Project field underwriting_approval_checklist_enabled.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 67:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Project field underwriting_approval_requirement_exception.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UnderwritingApprovalRequirement.fromStrictJson_(v, getFieldMap);
            }
            return UnderwritingApprovalRequirement.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 99:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Project field property_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Project$1, 'Project', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Project to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(100, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(100, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(100, null, null);
  }
  get approved() {
    return this.getFieldById(57, false);
  }
  setApproved(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(57, value, null);
  }
  deleteApproved() {
    return this.setValueFieldById(57, null, null);
  }
  get approvalRequested() {
    return this.getFieldById(95, false);
  }
  setApprovalRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(95, value, null);
  }
  deleteApprovalRequested() {
    return this.setValueFieldById(95, null, null);
  }
  get approvalRequestedBy() {
    return this.getFieldById(96, 0);
  }
  setApprovalRequestedBy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(96, value, null);
  }
  deleteApprovalRequestedBy() {
    return this.setValueFieldById(96, null, null);
  }
  get ndaRequired() {
    return this.getFieldById(58, false);
  }
  setNdaRequired(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(58, value, null);
  }
  deleteNdaRequired() {
    return this.setValueFieldById(58, null, null);
  }
  get stacksourceAgreementSigned() {
    return this.getFieldById(66, false);
  }
  setStacksourceAgreementSigned(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(66, value, null);
  }
  deleteStacksourceAgreementSigned() {
    return this.setValueFieldById(66, null, null);
  }
  get userHasWritePermission() {
    return this.getFieldById(2, false);
  }
  setUserHasWritePermission(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUserHasWritePermission() {
    return this.setValueFieldById(2, null, null);
  }
  get owner() {
    return this.getFieldById(3, 0);
  }
  setOwner(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteOwner() {
    return this.setValueFieldById(3, null, null);
  }
  get sponsorEmailVerified() {
    return this.getFieldById(65, false);
  }
  setSponsorEmailVerified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(65, value, null);
  }
  deleteSponsorEmailVerified() {
    return this.setValueFieldById(65, null, null);
  }
  get sponsorUserList() {
    return this.getRepeatedFieldById(64);
  }
  hasSponsorUser(value) {
    return this.repeatedFieldByIdHas(64, value);
  }
  addSponsorUser(value) {
    return this.addToRepeatedFieldById(64, value);
  }
  addEachSponsorUser(values) {
    let T = this;
    for (const value of values) {
      T = T.addSponsorUser(value);
    }
    return T;
  }
  setSponsorUser(index, value) {
    return this.setRepeatedFieldById(64, index, value);
  }
  removeSponsorUser(index) {
    return this.removeFromRepeatedFieldById(64, index);
  }
  clearSponsorUserList() {
    return this.clearRepeatedFieldById(64);
  }
  setSponsorUserList(values) {
    return this.overwriteRepeatedFieldById(64, values);
  }
  get brokerUserList() {
    return this.getRepeatedFieldById(94);
  }
  hasBrokerUser(value) {
    return this.repeatedFieldByIdHas(94, value);
  }
  addBrokerUser(value) {
    return this.addToRepeatedFieldById(94, value);
  }
  addEachBrokerUser(values) {
    let T = this;
    for (const value of values) {
      T = T.addBrokerUser(value);
    }
    return T;
  }
  setBrokerUser(index, value) {
    return this.setRepeatedFieldById(94, index, value);
  }
  removeBrokerUser(index) {
    return this.removeFromRepeatedFieldById(94, index);
  }
  clearBrokerUserList() {
    return this.clearRepeatedFieldById(94);
  }
  setBrokerUserList(values) {
    return this.overwriteRepeatedFieldById(94, values);
  }
  get draft() {
    return this.getFieldById(4, false);
  }
  setDraft(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteDraft() {
    return this.setValueFieldById(4, null, null);
  }
  get status() {
    return this.getFieldById(71, 0);
  }
  setStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(71, value, null);
  }
  deleteStatus() {
    return this.setValueFieldById(71, null, null);
  }
  get lostReason() {
    return this.getFieldById(72, ProjectLostReason.getDefault());
  }
  setLostReason(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(72, value, null);
  }
  deleteLostReason() {
    return this.setValueFieldById(72, null, null);
  }
  get financingPurpose() {
    return this.getFieldById(5, 0);
  }
  setFinancingPurpose(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteFinancingPurpose() {
    return this.setValueFieldById(5, null, null);
  }
  get assetType() {
    return this.getFieldById(6, 0);
  }
  setAssetType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteAssetType() {
    return this.setValueFieldById(6, null, null);
  }
  get sponsorRole() {
    return this.getFieldById(49, 0);
  }
  setSponsorRole(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(49, value, null);
  }
  deleteSponsorRole() {
    return this.setValueFieldById(49, null, null);
  }
  get hotelFlag() {
    return this.getFieldById(91, HotelFlag.getDefault());
  }
  setHotelFlag(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(91, value, null);
  }
  deleteHotelFlag() {
    return this.setValueFieldById(91, null, null);
  }
  get hotelAverageDailyRate() {
    return this.getFieldById(92, CurrencyValue.getDefault());
  }
  setHotelAverageDailyRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(92, value, null);
  }
  deleteHotelAverageDailyRate() {
    return this.setValueFieldById(92, null, null);
  }
  get hotelAverageOccupancyRate() {
    return this.getFieldById(93, Percent.getDefault());
  }
  setHotelAverageOccupancyRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(93, value, null);
  }
  deleteHotelAverageOccupancyRate() {
    return this.setValueFieldById(93, null, null);
  }
  get tenancy() {
    return this.getFieldById(63, ProjectTenancy.getDefault());
  }
  setTenancy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(63, value, null);
  }
  deleteTenancy() {
    return this.setValueFieldById(63, null, null);
  }
  get name() {
    return this.getFieldById(7, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteName() {
    return this.setValueFieldById(7, null, null);
  }
  get label() {
    return this.getFieldById(73, "");
  }
  setLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(73, value, null);
  }
  deleteLabel() {
    return this.setValueFieldById(73, null, null);
  }
  get address() {
    return this.getFieldById(8, StandardUsAddress.getDefault());
  }
  setAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteAddress() {
    return this.setValueFieldById(8, null, null);
  }
  get deprecatedTotalProjectBudget() {
    return this.getFieldById(9, CurrencyValue.getDefault());
  }
  setDeprecatedTotalProjectBudget(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteDeprecatedTotalProjectBudget() {
    return this.setValueFieldById(9, null, null);
  }
  get numberOfUnits() {
    return this.getFieldById(10, 0);
  }
  setNumberOfUnits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteNumberOfUnits() {
    return this.setValueFieldById(10, null, null);
  }
  get projectSummary() {
    return this.getFieldById(11, RichText.getDefault());
  }
  setProjectSummary(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteProjectSummary() {
    return this.setValueFieldById(11, null, null);
  }
  get totalSize() {
    return this.getFieldById(12, Area.getDefault());
  }
  setTotalSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteTotalSize() {
    return this.setValueFieldById(12, null, null);
  }
  get footprintSize() {
    return this.getFieldById(13, Area.getDefault());
  }
  setFootprintSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteFootprintSize() {
    return this.setValueFieldById(13, null, null);
  }
  get lotSize() {
    return this.getFieldById(14, Area.getDefault());
  }
  setLotSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteLotSize() {
    return this.setValueFieldById(14, null, null);
  }
  get architect() {
    return this.getFieldById(15, "");
  }
  setArchitect(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteArchitect() {
    return this.setValueFieldById(15, null, null);
  }
  get structuralEngineer() {
    return this.getFieldById(42, "");
  }
  setStructuralEngineer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(42, value, null);
  }
  deleteStructuralEngineer() {
    return this.setValueFieldById(42, null, null);
  }
  get constructionManager() {
    return this.getFieldById(43, "");
  }
  setConstructionManager(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(43, value, null);
  }
  deleteConstructionManager() {
    return this.setValueFieldById(43, null, null);
  }
  get generalContractor() {
    return this.getFieldById(37, "");
  }
  setGeneralContractor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(37, value, null);
  }
  deleteGeneralContractor() {
    return this.setValueFieldById(37, null, null);
  }
  get leasingRepresentative() {
    return this.getFieldById(44, "");
  }
  setLeasingRepresentative(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(44, value, null);
  }
  deleteLeasingRepresentative() {
    return this.setValueFieldById(44, null, null);
  }
  get propertyManager() {
    return this.getFieldById(45, "");
  }
  setPropertyManager(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(45, value, null);
  }
  deletePropertyManager() {
    return this.setValueFieldById(45, null, null);
  }
  get completedYear() {
    return this.getFieldById(16, 0);
  }
  setCompletedYear(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deleteCompletedYear() {
    return this.setValueFieldById(16, null, null);
  }
  get renovatedYear() {
    return this.getFieldById(51, 0);
  }
  setRenovatedYear(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(51, value, null);
  }
  deleteRenovatedYear() {
    return this.setValueFieldById(51, null, null);
  }
  get floorsAboveGround() {
    return this.getFieldById(17, 0);
  }
  setFloorsAboveGround(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteFloorsAboveGround() {
    return this.setValueFieldById(17, null, null);
  }
  get floorsBelowGround() {
    return this.getFieldById(18, 0);
  }
  setFloorsBelowGround(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(18, value, null);
  }
  deleteFloorsBelowGround() {
    return this.setValueFieldById(18, null, null);
  }
  get parkingSpots() {
    return this.getFieldById(98, 0);
  }
  setParkingSpots(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(98, value, null);
  }
  deleteParkingSpots() {
    return this.setValueFieldById(98, null, null);
  }
  get designInfo() {
    return this.getFieldById(19, RichText.getDefault());
  }
  setDesignInfo(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(19, value, null);
  }
  deleteDesignInfo() {
    return this.setValueFieldById(19, null, null);
  }
  get propertyTitle() {
    return this.getFieldById(20, 0);
  }
  setPropertyTitle(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, null);
  }
  deletePropertyTitle() {
    return this.setValueFieldById(20, null, null);
  }
  get assetNotes() {
    return this.getFieldById(21, RichText.getDefault());
  }
  setAssetNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, null);
  }
  deleteAssetNotes() {
    return this.setValueFieldById(21, null, null);
  }
  get latestAppraisalValue() {
    return this.getFieldById(22, CurrencyValue.getDefault());
  }
  setLatestAppraisalValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteLatestAppraisalValue() {
    return this.setValueFieldById(22, null, null);
  }
  get latestAppraisalDate() {
    return this.getFieldById(38, SimpleDate.getDefault());
  }
  setLatestAppraisalDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(38, value, null);
  }
  deleteLatestAppraisalDate() {
    return this.setValueFieldById(38, null, null);
  }
  get targetCloseDate() {
    return this.getFieldById(97, SimpleDate.getDefault());
  }
  setTargetCloseDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(97, value, null);
  }
  deleteTargetCloseDate() {
    return this.setValueFieldById(97, null, null);
  }
  get currentNetOperatingIncome() {
    return this.getFieldById(39, CurrencyValue.getDefault());
  }
  setCurrentNetOperatingIncome(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(39, value, null);
  }
  deleteCurrentNetOperatingIncome() {
    return this.setValueFieldById(39, null, null);
  }
  get proFormaNetOperatingIncome() {
    return this.getFieldById(77, CurrencyValue.getDefault());
  }
  setProFormaNetOperatingIncome(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(77, value, null);
  }
  deleteProFormaNetOperatingIncome() {
    return this.setValueFieldById(77, null, null);
  }
  get capRate() {
    return this.getFieldById(40, Percent.getDefault());
  }
  setCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(40, value, null);
  }
  deleteCapRate() {
    return this.setValueFieldById(40, null, null);
  }
  get proFormaCapRate() {
    return this.getFieldById(78, Percent.getDefault());
  }
  setProFormaCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(78, value, null);
  }
  deleteProFormaCapRate() {
    return this.setValueFieldById(78, null, null);
  }
  get financialNotes() {
    return this.getFieldById(23, RichText.getDefault());
  }
  setFinancialNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(23, value, null);
  }
  deleteFinancialNotes() {
    return this.setValueFieldById(23, null, null);
  }
  get acquisitionPrice() {
    return this.getFieldById(24, CurrencyValue.getDefault());
  }
  setAcquisitionPrice(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(24, value, null);
  }
  deleteAcquisitionPrice() {
    return this.setValueFieldById(24, null, null);
  }
  get acquisitionDate() {
    return this.getFieldById(25, SimpleDate.getDefault());
  }
  setAcquisitionDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(25, value, null);
  }
  deleteAcquisitionDate() {
    return this.setValueFieldById(25, null, null);
  }
  get acquisitionStatus() {
    return this.getFieldById(48, 0);
  }
  setAcquisitionStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(48, value, null);
  }
  deleteAcquisitionStatus() {
    return this.setValueFieldById(48, null, null);
  }
  get principalNeeded() {
    return this.getFieldById(26, CurrencyValueRange.getDefault());
  }
  setPrincipalNeeded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(26, value, null);
  }
  deletePrincipalNeeded() {
    return this.setValueFieldById(26, null, null);
  }
  get lienPosition() {
    return this.getFieldById(27, 0);
  }
  setLienPosition(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(27, value, null);
  }
  deleteLienPosition() {
    return this.setValueFieldById(27, null, null);
  }
  get financialInstruments() {
    return this.getFieldById(28, FinancialInstruments.getDefault());
  }
  setFinancialInstruments(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(28, value, null);
  }
  deleteFinancialInstruments() {
    return this.setValueFieldById(28, null, null);
  }
  get affordability() {
    return this.getFieldById(79, 0);
  }
  setAffordability(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(79, value, null);
  }
  deleteAffordability() {
    return this.setValueFieldById(79, null, null);
  }
  get hasElevator() {
    return this.getFieldById(80, false);
  }
  setHasElevator(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(80, value, null);
  }
  deleteHasElevator() {
    return this.setValueFieldById(80, null, null);
  }
  get numberOfFloors() {
    return this.getFieldById(81, 0);
  }
  setNumberOfFloors(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(81, value, null);
  }
  deleteNumberOfFloors() {
    return this.setValueFieldById(81, null, null);
  }
  get unitInformation() {
    return this.getFieldById(82, UnitInformation.getDefault());
  }
  setUnitInformation(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(82, value, null);
  }
  deleteUnitInformation() {
    return this.setValueFieldById(82, null, null);
  }
  get lihtcRatio() {
    return this.getFieldById(83, Percent.getDefault());
  }
  setLihtcRatio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(83, value, null);
  }
  deleteLihtcRatio() {
    return this.setValueFieldById(83, null, null);
  }
  get hudSelectedLtv() {
    return this.getFieldById(84, Percent.getDefault());
  }
  setHudSelectedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(84, value, null);
  }
  deleteHudSelectedLtv() {
    return this.setValueFieldById(84, null, null);
  }
  get freddieSblSelectedLtv() {
    return this.getFieldById(85, Percent.getDefault());
  }
  setFreddieSblSelectedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(85, value, null);
  }
  deleteFreddieSblSelectedLtv() {
    return this.setValueFieldById(85, null, null);
  }
  get freddieSblSelectedDscr() {
    return this.getFieldById(86, Percent.getDefault());
  }
  setFreddieSblSelectedDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(86, value, null);
  }
  deleteFreddieSblSelectedDscr() {
    return this.setValueFieldById(86, null, null);
  }
  get freddieSblInterestOnlyTerm() {
    return this.getFieldById(87, 0);
  }
  setFreddieSblInterestOnlyTerm(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(87, value, null);
  }
  deleteFreddieSblInterestOnlyTerm() {
    return this.setValueFieldById(87, null, null);
  }
  get freddieSblPrepaymentPenalty() {
    return this.getFieldById(88, 0);
  }
  setFreddieSblPrepaymentPenalty(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(88, value, null);
  }
  deleteFreddieSblPrepaymentPenalty() {
    return this.setValueFieldById(88, null, null);
  }
  get offerExpectations() {
    return this.getFieldById(46, OfferExpectations.getDefault());
  }
  setOfferExpectations(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(46, value, null);
  }
  deleteOfferExpectations() {
    return this.setValueFieldById(46, null, null);
  }
  get capStackItemList() {
    return this.getRepeatedFieldById(30);
  }
  hasCapStackItem(value) {
    return this.repeatedFieldByIdHas(30, value);
  }
  addCapStackItem(value) {
    return this.addToRepeatedFieldById(30, value);
  }
  addEachCapStackItem(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapStackItem(value);
    }
    return T;
  }
  setCapStackItem(index, value) {
    return this.setRepeatedFieldById(30, index, value);
  }
  removeCapStackItem(index) {
    return this.removeFromRepeatedFieldById(30, index);
  }
  clearCapStackItemList() {
    return this.clearRepeatedFieldById(30);
  }
  setCapStackItemList(values) {
    return this.overwriteRepeatedFieldById(30, values);
  }
  get capStackTargetList() {
    return this.getRepeatedFieldById(75);
  }
  hasCapStackTarget(value) {
    return this.repeatedFieldByIdHas(75, value);
  }
  addCapStackTarget(value) {
    return this.addToRepeatedFieldById(75, value);
  }
  addEachCapStackTarget(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapStackTarget(value);
    }
    return T;
  }
  setCapStackTarget(index, value) {
    return this.setRepeatedFieldById(75, index, value);
  }
  removeCapStackTarget(index) {
    return this.removeFromRepeatedFieldById(75, index);
  }
  clearCapStackTargetList() {
    return this.clearRepeatedFieldById(75);
  }
  setCapStackTargetList(values) {
    return this.overwriteRepeatedFieldById(75, values);
  }
  get imageList() {
    return this.getRepeatedFieldById(31);
  }
  hasImage(value) {
    return this.repeatedFieldByIdHas(31, value);
  }
  addImage(value) {
    return this.addToRepeatedFieldById(31, value);
  }
  addEachImage(values) {
    let T = this;
    for (const value of values) {
      T = T.addImage(value);
    }
    return T;
  }
  setImage(index, value) {
    return this.setRepeatedFieldById(31, index, value);
  }
  removeImage(index) {
    return this.removeFromRepeatedFieldById(31, index);
  }
  clearImageList() {
    return this.clearRepeatedFieldById(31);
  }
  setImageList(values) {
    return this.overwriteRepeatedFieldById(31, values);
  }
  get attachmentList() {
    return this.getRepeatedFieldById(32);
  }
  hasAttachment(value) {
    return this.repeatedFieldByIdHas(32, value);
  }
  addAttachment(value) {
    return this.addToRepeatedFieldById(32, value);
  }
  addEachAttachment(values) {
    let T = this;
    for (const value of values) {
      T = T.addAttachment(value);
    }
    return T;
  }
  setAttachment(index, value) {
    return this.setRepeatedFieldById(32, index, value);
  }
  removeAttachment(index) {
    return this.removeFromRepeatedFieldById(32, index);
  }
  clearAttachmentList() {
    return this.clearRepeatedFieldById(32);
  }
  setAttachmentList(values) {
    return this.overwriteRepeatedFieldById(32, values);
  }
  get sponsorProfile() {
    return this.getFieldById(33, SponsorProfile.getDefault());
  }
  setSponsorProfile(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(33, value, null);
  }
  deleteSponsorProfile() {
    return this.setValueFieldById(33, null, null);
  }
  get guarantorList() {
    return this.getRepeatedFieldById(74);
  }
  hasGuarantor(value) {
    return this.repeatedFieldByIdHas(74, value);
  }
  addGuarantor(value) {
    return this.addToRepeatedFieldById(74, value);
  }
  addEachGuarantor(values) {
    let T = this;
    for (const value of values) {
      T = T.addGuarantor(value);
    }
    return T;
  }
  setGuarantor(index, value) {
    return this.setRepeatedFieldById(74, index, value);
  }
  removeGuarantor(index) {
    return this.removeFromRepeatedFieldById(74, index);
  }
  clearGuarantorList() {
    return this.clearRepeatedFieldById(74);
  }
  setGuarantorList(values) {
    return this.overwriteRepeatedFieldById(74, values);
  }
  get solicitedLenderList() {
    return this.getRepeatedFieldById(90);
  }
  hasSolicitedLender(value) {
    return this.repeatedFieldByIdHas(90, value);
  }
  addSolicitedLender(value) {
    return this.addToRepeatedFieldById(90, value);
  }
  addEachSolicitedLender(values) {
    let T = this;
    for (const value of values) {
      T = T.addSolicitedLender(value);
    }
    return T;
  }
  setSolicitedLender(index, value) {
    return this.setRepeatedFieldById(90, index, value);
  }
  removeSolicitedLender(index) {
    return this.removeFromRepeatedFieldById(90, index);
  }
  clearSolicitedLenderList() {
    return this.clearRepeatedFieldById(90);
  }
  setSolicitedLenderList(values) {
    return this.overwriteRepeatedFieldById(90, values);
  }
  get note() {
    return this.getFieldById(41, RichText.getDefault());
  }
  setNote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(41, value, null);
  }
  deleteNote() {
    return this.setValueFieldById(41, null, null);
  }
  get shareIdList() {
    return this.getRepeatedFieldById(34);
  }
  hasShareId(value) {
    return this.repeatedFieldByIdHas(34, value);
  }
  addShareId(value) {
    return this.addToRepeatedFieldById(34, value);
  }
  addEachShareId(values) {
    let T = this;
    for (const value of values) {
      T = T.addShareId(value);
    }
    return T;
  }
  setShareId(index, value) {
    return this.setRepeatedFieldById(34, index, value);
  }
  removeShareId(index) {
    return this.removeFromRepeatedFieldById(34, index);
  }
  clearShareIdList() {
    return this.clearRepeatedFieldById(34);
  }
  setShareIdList(values) {
    return this.overwriteRepeatedFieldById(34, values);
  }
  get shareSymList() {
    return this.getRepeatedFieldById(101);
  }
  hasShareSym(value) {
    return this.repeatedFieldByIdHas(101, value);
  }
  addShareSym(value) {
    return this.addToRepeatedFieldById(101, value);
  }
  addEachShareSym(values) {
    let T = this;
    for (const value of values) {
      T = T.addShareSym(value);
    }
    return T;
  }
  setShareSym(index, value) {
    return this.setRepeatedFieldById(101, index, value);
  }
  removeShareSym(index) {
    return this.removeFromRepeatedFieldById(101, index);
  }
  clearShareSymList() {
    return this.clearRepeatedFieldById(101);
  }
  setShareSymList(values) {
    return this.overwriteRepeatedFieldById(101, values);
  }
  get offerList() {
    return this.getRepeatedFieldById(35);
  }
  hasOffer(value) {
    return this.repeatedFieldByIdHas(35, value);
  }
  addOffer(value) {
    return this.addToRepeatedFieldById(35, value);
  }
  addEachOffer(values) {
    let T = this;
    for (const value of values) {
      T = T.addOffer(value);
    }
    return T;
  }
  setOffer(index, value) {
    return this.setRepeatedFieldById(35, index, value);
  }
  removeOffer(index) {
    return this.removeFromRepeatedFieldById(35, index);
  }
  clearOfferList() {
    return this.clearRepeatedFieldById(35);
  }
  setOfferList(values) {
    return this.overwriteRepeatedFieldById(35, values);
  }
  get acceptedOffer() {
    return this.getFieldById(36, 0);
  }
  setAcceptedOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(36, value, null);
  }
  deleteAcceptedOffer() {
    return this.setValueFieldById(36, null, null);
  }
  get offerAcceptedTime() {
    return this.getFieldById(69, DEPRECATED_Date.getDefault());
  }
  setOfferAcceptedTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(69, value, null);
  }
  deleteOfferAcceptedTime() {
    return this.setValueFieldById(69, null, null);
  }
  get offerUnderwritingRequested() {
    return this.getFieldById(89, 0);
  }
  setOfferUnderwritingRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(89, value, null);
  }
  deleteOfferUnderwritingRequested() {
    return this.setValueFieldById(89, null, null);
  }
  get projectStage() {
    return this.getFieldById(70, 0);
  }
  setProjectStage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(70, value, null);
  }
  deleteProjectStage() {
    return this.setValueFieldById(70, null, null);
  }
  get leadingOffer() {
    return this.getFieldById(47, 0);
  }
  setLeadingOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(47, value, null);
  }
  deleteLeadingOffer() {
    return this.setValueFieldById(47, null, null);
  }
  get creationTime() {
    return this.getFieldById(50, DEPRECATED_Date.getDefault());
  }
  setCreationTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(50, value, null);
  }
  deleteCreationTime() {
    return this.setValueFieldById(50, null, null);
  }
  get occupancy() {
    return this.getFieldById(52, Occupancy.getDefault());
  }
  setOccupancy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(52, value, null);
  }
  deleteOccupancy() {
    return this.setValueFieldById(52, null, null);
  }
  get useList() {
    return this.getRepeatedFieldById(53);
  }
  hasUse(value) {
    return this.repeatedFieldByIdHas(53, value);
  }
  addUse(value) {
    return this.addToRepeatedFieldById(53, value);
  }
  addEachUse(values) {
    let T = this;
    for (const value of values) {
      T = T.addUse(value);
    }
    return T;
  }
  setUse(index, value) {
    return this.setRepeatedFieldById(53, index, value);
  }
  removeUse(index) {
    return this.removeFromRepeatedFieldById(53, index);
  }
  clearUseList() {
    return this.clearRepeatedFieldById(53);
  }
  setUseList(values) {
    return this.overwriteRepeatedFieldById(53, values);
  }
  get disclaimer() {
    return this.getFieldById(54, PropertyDisclaimer.getDefault());
  }
  setDisclaimer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(54, value, null);
  }
  deleteDisclaimer() {
    return this.setValueFieldById(54, null, null);
  }
  get demographics() {
    return this.getFieldById(55, PropertyDemographics.getDefault());
  }
  setDemographics(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(55, value, null);
  }
  deleteDemographics() {
    return this.setValueFieldById(55, null, null);
  }
  get investmentCategory() {
    return this.getFieldById(76, "");
  }
  setInvestmentCategory(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(76, value, null);
  }
  deleteInvestmentCategory() {
    return this.setValueFieldById(76, null, null);
  }
  get sponsorProjections() {
    return this.getFieldById(56, ProjectProjections.getDefault());
  }
  setSponsorProjections(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(56, value, null);
  }
  deleteSponsorProjections() {
    return this.setValueFieldById(56, null, null);
  }
  get showConcealedShares() {
    return this.getFieldById(59, false);
  }
  setShowConcealedShares(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(59, value, null);
  }
  deleteShowConcealedShares() {
    return this.setValueFieldById(59, null, null);
  }
  get projectFee() {
    return this.getFieldById(61, ProjectFee.getDefault());
  }
  setProjectFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(61, value, null);
  }
  deleteProjectFee() {
    return this.setValueFieldById(61, null, null);
  }
  get capitalAdvisorList() {
    return this.getRepeatedFieldById(62);
  }
  hasCapitalAdvisor(value) {
    return this.repeatedFieldByIdHas(62, value);
  }
  addCapitalAdvisor(value) {
    return this.addToRepeatedFieldById(62, value);
  }
  addEachCapitalAdvisor(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapitalAdvisor(value);
    }
    return T;
  }
  setCapitalAdvisor(index, value) {
    return this.setRepeatedFieldById(62, index, value);
  }
  removeCapitalAdvisor(index) {
    return this.removeFromRepeatedFieldById(62, index);
  }
  clearCapitalAdvisorList() {
    return this.clearRepeatedFieldById(62);
  }
  setCapitalAdvisorList(values) {
    return this.overwriteRepeatedFieldById(62, values);
  }
  get underwritingApprovalChecklistEnabled() {
    return this.getFieldById(68, false);
  }
  setUnderwritingApprovalChecklistEnabled(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(68, value, null);
  }
  deleteUnderwritingApprovalChecklistEnabled() {
    return this.setValueFieldById(68, null, null);
  }
  get underwritingApprovalRequirementExceptionList() {
    return this.getRepeatedFieldById(67);
  }
  hasUnderwritingApprovalRequirementException(value) {
    return this.repeatedFieldByIdHas(67, value);
  }
  addUnderwritingApprovalRequirementException(value) {
    return this.addToRepeatedFieldById(67, value);
  }
  addEachUnderwritingApprovalRequirementException(values) {
    let T = this;
    for (const value of values) {
      T = T.addUnderwritingApprovalRequirementException(value);
    }
    return T;
  }
  setUnderwritingApprovalRequirementException(index, value) {
    return this.setRepeatedFieldById(67, index, value);
  }
  removeUnderwritingApprovalRequirementException(index) {
    return this.removeFromRepeatedFieldById(67, index);
  }
  clearUnderwritingApprovalRequirementExceptionList() {
    return this.clearRepeatedFieldById(67);
  }
  setUnderwritingApprovalRequirementExceptionList(values) {
    return this.overwriteRepeatedFieldById(67, values);
  }
  get propertyValue() {
    return this.getFieldById(99, CurrencyValue.getDefault());
  }
  setPropertyValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(99, value, null);
  }
  deletePropertyValue() {
    return this.setValueFieldById(99, null, null);
  }
}
_defineProperty$1x(Project_, protoName, DEV_MODE ? 'Project' : '[Unknown]');
let Project$1 = Project_;
(function (_Project) {})(Project$1 || (Project$1 = {}));
Object.defineProperties(Project$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 100
      },
      "approved": {
        value: 57
      },
      "approval_requested": {
        value: 95
      },
      "approvalRequested": {
        value: 95
      },
      "approval_requested_by": {
        value: 96
      },
      "approvalRequestedBy": {
        value: 96
      },
      "nda_required": {
        value: 58
      },
      "ndaRequired": {
        value: 58
      },
      "stacksource_agreement_signed": {
        value: 66
      },
      "stacksourceAgreementSigned": {
        value: 66
      },
      "user_has_write_permission": {
        value: 2
      },
      "userHasWritePermission": {
        value: 2
      },
      "owner": {
        value: 3
      },
      "sponsor_email_verified": {
        value: 65
      },
      "sponsorEmailVerified": {
        value: 65
      },
      "sponsor_user": {
        value: 64
      },
      "sponsorUser": {
        value: 64
      },
      "broker_user": {
        value: 94
      },
      "brokerUser": {
        value: 94
      },
      "draft": {
        value: 4
      },
      "status": {
        value: 71
      },
      "lost_reason": {
        value: 72
      },
      "lostReason": {
        value: 72
      },
      "financing_purpose": {
        value: 5
      },
      "financingPurpose": {
        value: 5
      },
      "asset_type": {
        value: 6
      },
      "assetType": {
        value: 6
      },
      "sponsor_role": {
        value: 49
      },
      "sponsorRole": {
        value: 49
      },
      "hotel_flag": {
        value: 91
      },
      "hotelFlag": {
        value: 91
      },
      "hotel_average_daily_rate": {
        value: 92
      },
      "hotelAverageDailyRate": {
        value: 92
      },
      "hotel_average_occupancy_rate": {
        value: 93
      },
      "hotelAverageOccupancyRate": {
        value: 93
      },
      "tenancy": {
        value: 63
      },
      "name": {
        value: 7
      },
      "label": {
        value: 73
      },
      "address": {
        value: 8
      },
      "deprecated_total_project_budget": {
        value: 9
      },
      "deprecatedTotalProjectBudget": {
        value: 9
      },
      "number_of_units": {
        value: 10
      },
      "numberOfUnits": {
        value: 10
      },
      "project_summary": {
        value: 11
      },
      "projectSummary": {
        value: 11
      },
      "total_size": {
        value: 12
      },
      "totalSize": {
        value: 12
      },
      "footprint_size": {
        value: 13
      },
      "footprintSize": {
        value: 13
      },
      "lot_size": {
        value: 14
      },
      "lotSize": {
        value: 14
      },
      "architect": {
        value: 15
      },
      "structural_engineer": {
        value: 42
      },
      "structuralEngineer": {
        value: 42
      },
      "construction_manager": {
        value: 43
      },
      "constructionManager": {
        value: 43
      },
      "general_contractor": {
        value: 37
      },
      "generalContractor": {
        value: 37
      },
      "leasing_representative": {
        value: 44
      },
      "leasingRepresentative": {
        value: 44
      },
      "property_manager": {
        value: 45
      },
      "propertyManager": {
        value: 45
      },
      "completed_year": {
        value: 16
      },
      "completedYear": {
        value: 16
      },
      "renovated_year": {
        value: 51
      },
      "renovatedYear": {
        value: 51
      },
      "floors_above_ground": {
        value: 17
      },
      "floorsAboveGround": {
        value: 17
      },
      "floors_below_ground": {
        value: 18
      },
      "floorsBelowGround": {
        value: 18
      },
      "parking_spots": {
        value: 98
      },
      "parkingSpots": {
        value: 98
      },
      "design_info": {
        value: 19
      },
      "designInfo": {
        value: 19
      },
      "property_title": {
        value: 20
      },
      "propertyTitle": {
        value: 20
      },
      "asset_notes": {
        value: 21
      },
      "assetNotes": {
        value: 21
      },
      "latest_appraisal_value": {
        value: 22
      },
      "latestAppraisalValue": {
        value: 22
      },
      "latest_appraisal_date": {
        value: 38
      },
      "latestAppraisalDate": {
        value: 38
      },
      "target_close_date": {
        value: 97
      },
      "targetCloseDate": {
        value: 97
      },
      "current_net_operating_income": {
        value: 39
      },
      "currentNetOperatingIncome": {
        value: 39
      },
      "pro_forma_net_operating_income": {
        value: 77
      },
      "proFormaNetOperatingIncome": {
        value: 77
      },
      "cap_rate": {
        value: 40
      },
      "capRate": {
        value: 40
      },
      "pro_forma_cap_rate": {
        value: 78
      },
      "proFormaCapRate": {
        value: 78
      },
      "financial_notes": {
        value: 23
      },
      "financialNotes": {
        value: 23
      },
      "acquisition_price": {
        value: 24
      },
      "acquisitionPrice": {
        value: 24
      },
      "acquisition_date": {
        value: 25
      },
      "acquisitionDate": {
        value: 25
      },
      "acquisition_status": {
        value: 48
      },
      "acquisitionStatus": {
        value: 48
      },
      "principal_needed": {
        value: 26
      },
      "principalNeeded": {
        value: 26
      },
      "lien_position": {
        value: 27
      },
      "lienPosition": {
        value: 27
      },
      "financial_instruments": {
        value: 28
      },
      "financialInstruments": {
        value: 28
      },
      "affordability": {
        value: 79
      },
      "has_elevator": {
        value: 80
      },
      "hasElevator": {
        value: 80
      },
      "number_of_floors": {
        value: 81
      },
      "numberOfFloors": {
        value: 81
      },
      "unit_information": {
        value: 82
      },
      "unitInformation": {
        value: 82
      },
      "lihtc_ratio": {
        value: 83
      },
      "lihtcRatio": {
        value: 83
      },
      "hud_selected_ltv": {
        value: 84
      },
      "hudSelectedLtv": {
        value: 84
      },
      "freddie_sbl_selected_ltv": {
        value: 85
      },
      "freddieSblSelectedLtv": {
        value: 85
      },
      "freddie_sbl_selected_dscr": {
        value: 86
      },
      "freddieSblSelectedDscr": {
        value: 86
      },
      "freddie_sbl_interest_only_term": {
        value: 87
      },
      "freddieSblInterestOnlyTerm": {
        value: 87
      },
      "freddie_sbl_prepayment_penalty": {
        value: 88
      },
      "freddieSblPrepaymentPenalty": {
        value: 88
      },
      "offer_expectations": {
        value: 46
      },
      "offerExpectations": {
        value: 46
      },
      "cap_stack_item": {
        value: 30
      },
      "capStackItem": {
        value: 30
      },
      "cap_stack_target": {
        value: 75
      },
      "capStackTarget": {
        value: 75
      },
      "image": {
        value: 31
      },
      "attachment": {
        value: 32
      },
      "sponsor_profile": {
        value: 33
      },
      "sponsorProfile": {
        value: 33
      },
      "guarantor": {
        value: 74
      },
      "solicited_lender": {
        value: 90
      },
      "solicitedLender": {
        value: 90
      },
      "note": {
        value: 41
      },
      "share_id": {
        value: 34
      },
      "shareId": {
        value: 34
      },
      "share_sym": {
        value: 101
      },
      "shareSym": {
        value: 101
      },
      "offer": {
        value: 35
      },
      "accepted_offer": {
        value: 36
      },
      "acceptedOffer": {
        value: 36
      },
      "offer_accepted_time": {
        value: 69
      },
      "offerAcceptedTime": {
        value: 69
      },
      "offer_underwriting_requested": {
        value: 89
      },
      "offerUnderwritingRequested": {
        value: 89
      },
      "project_stage": {
        value: 70
      },
      "projectStage": {
        value: 70
      },
      "leading_offer": {
        value: 47
      },
      "leadingOffer": {
        value: 47
      },
      "creation_time": {
        value: 50
      },
      "creationTime": {
        value: 50
      },
      "occupancy": {
        value: 52
      },
      "use": {
        value: 53
      },
      "disclaimer": {
        value: 54
      },
      "demographics": {
        value: 55
      },
      "investment_category": {
        value: 76
      },
      "investmentCategory": {
        value: 76
      },
      "sponsor_projections": {
        value: 56
      },
      "sponsorProjections": {
        value: 56
      },
      "show_concealed_shares": {
        value: 59
      },
      "showConcealedShares": {
        value: 59
      },
      "project_fee": {
        value: 61
      },
      "projectFee": {
        value: 61
      },
      "capital_advisor": {
        value: 62
      },
      "capitalAdvisor": {
        value: 62
      },
      "underwriting_approval_checklist_enabled": {
        value: 68
      },
      "underwritingApprovalChecklistEnabled": {
        value: 68
      },
      "underwriting_approval_requirement_exception": {
        value: 67
      },
      "underwritingApprovalRequirementException": {
        value: 67
      },
      "property_value": {
        value: 99
      },
      "propertyValue": {
        value: 99
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '100': {
        value: "sym"
      },
      '57': {
        value: "approved"
      },
      '95': {
        value: "approval_requested"
      },
      '96': {
        value: "approval_requested_by"
      },
      '58': {
        value: "nda_required"
      },
      '66': {
        value: "stacksource_agreement_signed"
      },
      '2': {
        value: "user_has_write_permission"
      },
      '3': {
        value: "owner"
      },
      '65': {
        value: "sponsor_email_verified"
      },
      '64': {
        value: "sponsor_user"
      },
      '94': {
        value: "broker_user"
      },
      '4': {
        value: "draft"
      },
      '71': {
        value: "status"
      },
      '72': {
        value: "lost_reason"
      },
      '5': {
        value: "financing_purpose"
      },
      '6': {
        value: "asset_type"
      },
      '49': {
        value: "sponsor_role"
      },
      '91': {
        value: "hotel_flag"
      },
      '92': {
        value: "hotel_average_daily_rate"
      },
      '93': {
        value: "hotel_average_occupancy_rate"
      },
      '63': {
        value: "tenancy"
      },
      '7': {
        value: "name"
      },
      '73': {
        value: "label"
      },
      '8': {
        value: "address"
      },
      '9': {
        value: "deprecated_total_project_budget"
      },
      '10': {
        value: "number_of_units"
      },
      '11': {
        value: "project_summary"
      },
      '12': {
        value: "total_size"
      },
      '13': {
        value: "footprint_size"
      },
      '14': {
        value: "lot_size"
      },
      '15': {
        value: "architect"
      },
      '42': {
        value: "structural_engineer"
      },
      '43': {
        value: "construction_manager"
      },
      '37': {
        value: "general_contractor"
      },
      '44': {
        value: "leasing_representative"
      },
      '45': {
        value: "property_manager"
      },
      '16': {
        value: "completed_year"
      },
      '51': {
        value: "renovated_year"
      },
      '17': {
        value: "floors_above_ground"
      },
      '18': {
        value: "floors_below_ground"
      },
      '98': {
        value: "parking_spots"
      },
      '19': {
        value: "design_info"
      },
      '20': {
        value: "property_title"
      },
      '21': {
        value: "asset_notes"
      },
      '22': {
        value: "latest_appraisal_value"
      },
      '38': {
        value: "latest_appraisal_date"
      },
      '97': {
        value: "target_close_date"
      },
      '39': {
        value: "current_net_operating_income"
      },
      '77': {
        value: "pro_forma_net_operating_income"
      },
      '40': {
        value: "cap_rate"
      },
      '78': {
        value: "pro_forma_cap_rate"
      },
      '23': {
        value: "financial_notes"
      },
      '24': {
        value: "acquisition_price"
      },
      '25': {
        value: "acquisition_date"
      },
      '48': {
        value: "acquisition_status"
      },
      '26': {
        value: "principal_needed"
      },
      '27': {
        value: "lien_position"
      },
      '28': {
        value: "financial_instruments"
      },
      '79': {
        value: "affordability"
      },
      '80': {
        value: "has_elevator"
      },
      '81': {
        value: "number_of_floors"
      },
      '82': {
        value: "unit_information"
      },
      '83': {
        value: "lihtc_ratio"
      },
      '84': {
        value: "hud_selected_ltv"
      },
      '85': {
        value: "freddie_sbl_selected_ltv"
      },
      '86': {
        value: "freddie_sbl_selected_dscr"
      },
      '87': {
        value: "freddie_sbl_interest_only_term"
      },
      '88': {
        value: "freddie_sbl_prepayment_penalty"
      },
      '46': {
        value: "offer_expectations"
      },
      '30': {
        value: "cap_stack_item"
      },
      '75': {
        value: "cap_stack_target"
      },
      '31': {
        value: "image"
      },
      '32': {
        value: "attachment"
      },
      '33': {
        value: "sponsor_profile"
      },
      '74': {
        value: "guarantor"
      },
      '90': {
        value: "solicited_lender"
      },
      '41': {
        value: "note"
      },
      '34': {
        value: "share_id"
      },
      '101': {
        value: "share_sym"
      },
      '35': {
        value: "offer"
      },
      '36': {
        value: "accepted_offer"
      },
      '69': {
        value: "offer_accepted_time"
      },
      '89': {
        value: "offer_underwriting_requested"
      },
      '70': {
        value: "project_stage"
      },
      '47': {
        value: "leading_offer"
      },
      '50': {
        value: "creation_time"
      },
      '52': {
        value: "occupancy"
      },
      '53': {
        value: "use"
      },
      '54': {
        value: "disclaimer"
      },
      '55': {
        value: "demographics"
      },
      '76': {
        value: "investment_category"
      },
      '56': {
        value: "sponsor_projections"
      },
      '59': {
        value: "show_concealed_shares"
      },
      '61': {
        value: "project_fee"
      },
      '62': {
        value: "capital_advisor"
      },
      '68': {
        value: "underwriting_approval_checklist_enabled"
      },
      '67': {
        value: "underwriting_approval_requirement_exception"
      },
      '99': {
        value: "property_value"
      }
    }))
  }
});
Object.defineProperties(Project$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      64: Int32OrString.prototype,
      94: Int32OrString.prototype,
      72: ProjectLostReason.prototype,
      91: HotelFlag.prototype,
      92: CurrencyValue.prototype,
      93: Percent.prototype,
      63: ProjectTenancy.prototype,
      8: StandardUsAddress.prototype,
      9: CurrencyValue.prototype,
      11: RichText.prototype,
      12: Area.prototype,
      13: Area.prototype,
      14: Area.prototype,
      19: RichText.prototype,
      21: RichText.prototype,
      22: CurrencyValue.prototype,
      38: SimpleDate.prototype,
      97: SimpleDate.prototype,
      39: CurrencyValue.prototype,
      77: CurrencyValue.prototype,
      40: Percent.prototype,
      78: Percent.prototype,
      23: RichText.prototype,
      24: CurrencyValue.prototype,
      25: SimpleDate.prototype,
      26: CurrencyValueRange.prototype,
      28: FinancialInstruments.prototype,
      82: UnitInformation.prototype,
      83: Percent.prototype,
      84: Percent.prototype,
      85: Percent.prototype,
      86: Percent.prototype,
      46: OfferExpectations.prototype,
      30: CapStackItem.prototype,
      75: CapStackTarget.prototype,
      32: Attachment.prototype,
      33: SponsorProfile.prototype,
      74: ProjectGuarantor.prototype,
      90: Int32OrString.prototype,
      41: RichText.prototype,
      69: DEPRECATED_Date.prototype,
      50: DEPRECATED_Date.prototype,
      52: Occupancy.prototype,
      53: CapStackItem.prototype,
      54: PropertyDisclaimer.prototype,
      55: PropertyDemographics.prototype,
      56: ProjectProjections.prototype,
      61: ProjectFee.prototype,
      99: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Project$1);
  Object.freeze(Project$1.prototype);
}

class Project extends Project_ {
  get totalProjectBudget() {
    if (!this.deprecatedTotalProjectBudget.isZero()) {
      return this.deprecatedTotalProjectBudget;
    }
    return addCurrencies(...this.capStackTargetList.map(u => u.principal));
  }
}

registerCanonical(Project_, Project);

function _defineProperty$1w(obj, key, value) { key = _toPropertyKey$1w(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1w(arg) { var key = _toPrimitive$1w(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1w(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var LoanSizeConstraint__;
(function (LoanSizeConstraint__) {
  LoanSizeConstraint__[LoanSizeConstraint__["UNKNOWN"] = 0] = "UNKNOWN";
  LoanSizeConstraint__[LoanSizeConstraint__["LTV"] = 1] = "LTV";
  LoanSizeConstraint__[LoanSizeConstraint__["LTC"] = 2] = "LTC";
  LoanSizeConstraint__[LoanSizeConstraint__["DSCR"] = 3] = "DSCR";
  LoanSizeConstraint__[LoanSizeConstraint__["DEBT_YIELD"] = 4] = "DEBT_YIELD";
})(LoanSizeConstraint__ || (LoanSizeConstraint__ = {}));
const LoanSizeConstraint_ = defineEnumProperties(LoanSizeConstraint__, [0, 1, 2, 3, 4]);
const LoanSizeConstraint = extendEnum('LoanSizeConstraint', [["UNKNOWN", 0], ["LTV", 1], ["LTC", 2], ["DSCR", 3], ["DEBT_YIELD", 4]])(LoanSizeConstraint_);
class LoanSizeConstraints_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Percent.shape,
      2: Percent.shape,
      3: Percent.shape,
      4: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSizeConstraints field ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSizeConstraints field ltc.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSizeConstraints field dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSizeConstraints field debt_yield.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LoanSizeConstraints, 'LoanSizeConstraints', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LoanSizeConstraints to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get ltv() {
    return this.getFieldById(1, Percent.getDefault());
  }
  setLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteLtv() {
    return this.setValueFieldById(1, null, null);
  }
  get ltc() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setLtc(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLtc() {
    return this.setValueFieldById(2, null, null);
  }
  get dscr() {
    return this.getFieldById(3, Percent.getDefault());
  }
  setDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteDscr() {
    return this.setValueFieldById(3, null, null);
  }
  get debtYield() {
    return this.getFieldById(4, Percent.getDefault());
  }
  setDebtYield(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteDebtYield() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$1w(LoanSizeConstraints_, protoName, DEV_MODE ? 'LoanSizeConstraints' : '[Unknown]');
let LoanSizeConstraints = LoanSizeConstraints_;
(function (_LoanSizeConstraints) {})(LoanSizeConstraints || (LoanSizeConstraints = {}));
Object.defineProperties(LoanSizeConstraints, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "ltv": {
        value: 1
      },
      "ltc": {
        value: 2
      },
      "dscr": {
        value: 3
      },
      "debt_yield": {
        value: 4
      },
      "debtYield": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "ltv"
      },
      '2': {
        value: "ltc"
      },
      '3': {
        value: "dscr"
      },
      '4': {
        value: "debt_yield"
      }
    }))
  }
});
Object.defineProperties(LoanSizeConstraints.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Percent.prototype,
      2: Percent.prototype,
      3: Percent.prototype,
      4: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LoanSizeConstraints);
  Object.freeze(LoanSizeConstraints.prototype);
}

function _defineProperty$1v(obj, key, value) { key = _toPropertyKey$1v(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1v(arg) { var key = _toPrimitive$1v(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1v(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class LoanSize_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CurrencyValue.shape,
      2: CurrencyValue.shape,
      3: CurrencyValue.shape,
      4: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSize field by_ltv.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSize field by_ltc.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSize field by_dscr.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanSize field by_debt_yield.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LoanSize$1, 'LoanSize', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LoanSize to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get byLtv() {
    return this.getFieldById(1, CurrencyValue.getDefault());
  }
  setByLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteByLtv() {
    return this.setValueFieldById(1, null, null);
  }
  get byLtc() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setByLtc(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteByLtc() {
    return this.setValueFieldById(2, null, null);
  }
  get byDscr() {
    return this.getFieldById(3, CurrencyValue.getDefault());
  }
  setByDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteByDscr() {
    return this.setValueFieldById(3, null, null);
  }
  get byDebtYield() {
    return this.getFieldById(4, CurrencyValue.getDefault());
  }
  setByDebtYield(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteByDebtYield() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$1v(LoanSize_, protoName, DEV_MODE ? 'LoanSize' : '[Unknown]');
let LoanSize$1 = LoanSize_;
(function (_LoanSize) {})(LoanSize$1 || (LoanSize$1 = {}));
Object.defineProperties(LoanSize$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "by_ltv": {
        value: 1
      },
      "byLtv": {
        value: 1
      },
      "by_ltc": {
        value: 2
      },
      "byLtc": {
        value: 2
      },
      "by_dscr": {
        value: 3
      },
      "byDscr": {
        value: 3
      },
      "by_debt_yield": {
        value: 4
      },
      "byDebtYield": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "by_ltv"
      },
      '2': {
        value: "by_ltc"
      },
      '3': {
        value: "by_dscr"
      },
      '4': {
        value: "by_debt_yield"
      }
    }))
  }
});
Object.defineProperties(LoanSize$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CurrencyValue.prototype,
      2: CurrencyValue.prototype,
      3: CurrencyValue.prototype,
      4: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LoanSize$1);
  Object.freeze(LoanSize$1.prototype);
}

function pv(rate, numberOfPeriods, payment) {
  const r = rate.toNumber();
  const n = numberOfPeriods;
  const p = payment.toNumber();
  const d = r === 0 ? n : (1 - Math.pow(1 + r, -n)) / r;
  return CurrencyValue.fromNumber(payment.unit, -p * d);
}

const accessors = new Map([[LoanSizeConstraint.LTV, {
  getValue: u => u.byLtv
}], [LoanSizeConstraint.LTC, {
  getValue: u => u.byLtc
}], [LoanSizeConstraint.DSCR, {
  getValue: u => u.byDscr
}], [LoanSizeConstraint.DEBT_YIELD, {
  getValue: u => u.byDebtYield
}]]);
class LoanSize extends LoanSize_ {
  static calculate(project, constraints, interestRate, amortization) {
    return new LoanSize().setByLtv(estimatedValuation(project).multiplyByPercent(constraints.ltv)).setByLtc(project.totalProjectBudget.multiplyByPercent(constraints.ltc)).setByDscr(loanSizeFromDscr(constraints.dscr, project.currentNetOperatingIncome, interestRate, amortization)).setByDebtYield(constraints.debtYield.isZero() ? new CurrencyValue().setUnit(project.currentNetOperatingIncome.unit) : project.currentNetOperatingIncome.divideByPercent(constraints.debtYield));
  }
  get value() {
    return this._reduce()[1];
  }
  get constraint() {
    return this._reduce()[0];
  }
  formatConstraint() {
    switch (this.constraint) {
      case LoanSizeConstraint.UNKNOWN:
        return '';
      case LoanSizeConstraint.LTV:
        return 'LTV';
      case LoanSizeConstraint.LTC:
        return 'LTC';
      case LoanSizeConstraint.DSCR:
        return 'DSCR';
      case LoanSizeConstraint.DEBT_YIELD:
        return 'Debt Yield';
    }
  }
  _reduce() {
    let lowestConstraint = LoanSizeConstraint.UNKNOWN;
    let lowestValue = CurrencyValue.fromNumber(this.byLtc.unit, 0);
    for (const [constraint, accessor] of accessors) {
      const value = accessor.getValue(this);
      if (!value.isZero() && (lowestValue.isZero() || value.lessThan(lowestValue))) {
        lowestConstraint = constraint;
        lowestValue = value;
      }
    }
    return [lowestConstraint, lowestValue];
  }
}
function estimatedValuation(project) {
  return !project.propertyValue.isZero() ? project.propertyValue : calculateEstimatedValuation(project.financingPurpose, project.acquisitionPrice, project.acquisitionDate, project.currentNetOperatingIncome, project.capRate, project.latestAppraisalValue, project.latestAppraisalDate).value;
}
function loanSizeFromDscr(dscr, noi, interestRate, amortization) {
  return dscr.isZero() || interestRate.isZero() || amortization.isZero() ? CurrencyValue.fromNumber(noi.unit, 0)
  // TODO(nathan): Check on the 1 here. It came from Tim's spreadsheet for
  // HUD. Make sure it's correct.
  : CurrencyValue.fromNumber(noi.unit, 1).subtract(pv(interestRate.divideByNumber(12), amortization.toMonths(), calculatePaymentAmount(noi, dscr)));
}
function calculatePaymentAmount(noi, dscr) {
  if (dscr.isZero()) {
    return CurrencyValue.fromNumber(noi.unit, 0);
  }
  return noi.divideByNumber(12).divideByPercent(dscr);
}

registerCanonical(LoanSize_, LoanSize);

function calculateLoanSize(project, constraints, interestRate, amortization) {
  return LoanSize.calculate(project, constraints, interestRate, amortization);
}

function occupancyIsZero(occupancy) {
  return occupancy.isZero();
}

// tslint:disable-next-line:no-unused-variable
var InterestRateIndex__;
(function (InterestRateIndex__) {
  InterestRateIndex__[InterestRateIndex__["FIXED"] = 0] = "FIXED";
  InterestRateIndex__[InterestRateIndex__["PRIME"] = 3] = "PRIME";
  InterestRateIndex__[InterestRateIndex__["SOFR"] = 7] = "SOFR";
  InterestRateIndex__[InterestRateIndex__["LIBOR"] = 1] = "LIBOR";
  InterestRateIndex__[InterestRateIndex__["LIBOR_SWAP"] = 2] = "LIBOR_SWAP";
  InterestRateIndex__[InterestRateIndex__["US_TREASURY"] = 4] = "US_TREASURY";
  InterestRateIndex__[InterestRateIndex__["US_TREASURY_SWAP"] = 5] = "US_TREASURY_SWAP";
  InterestRateIndex__[InterestRateIndex__["CME_TERM_SOFR"] = 8] = "CME_TERM_SOFR";
  InterestRateIndex__[InterestRateIndex__["SOFR_SWAP"] = 9] = "SOFR_SWAP";
})(InterestRateIndex__ || (InterestRateIndex__ = {}));
const InterestRateIndex_ = defineEnumProperties(InterestRateIndex__, [0, 3, 7, 1, 2, 4, 5, 8, 9]);
const InterestRateIndex = extendEnum('InterestRateIndex', [["FIXED", 0], ["PRIME", 3], ["SOFR", 7], ["LIBOR", 1], ["LIBOR_SWAP", 2], ["US_TREASURY", 4], ["US_TREASURY_SWAP", 5], ["CME_TERM_SOFR", 8], ["SOFR_SWAP", 9]])(InterestRateIndex_);

function _defineProperty$1u(obj, key, value) { key = _toPropertyKey$1u(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1u(arg) { var key = _toPrimitive$1u(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1u(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class InterestRateSpread_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Percent.shape,
      2: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message InterestRateSpread field value.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message InterestRateSpread field floor.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(InterestRateSpread, 'InterestRateSpread', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message InterestRateSpread to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get value() {
    return this.getFieldById(1, Percent.getDefault());
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(1, null, null);
  }
  get floor() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setFloor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteFloor() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1u(InterestRateSpread_, protoName, DEV_MODE ? 'InterestRateSpread' : '[Unknown]');
let InterestRateSpread = InterestRateSpread_;
(function (_InterestRateSpread) {})(InterestRateSpread || (InterestRateSpread = {}));
Object.defineProperties(InterestRateSpread, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      },
      "floor": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      },
      '2': {
        value: "floor"
      }
    }))
  }
});
Object.defineProperties(InterestRateSpread.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Percent.prototype,
      2: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(InterestRateSpread);
  Object.freeze(InterestRateSpread.prototype);
}
var InterestRateLockEvent__;
(function (InterestRateLockEvent__) {
  InterestRateLockEvent__[InterestRateLockEvent__["NO_LOCK"] = 0] = "NO_LOCK";
  InterestRateLockEvent__[InterestRateLockEvent__["SIGNED_TERM_SHEET"] = 1] = "SIGNED_TERM_SHEET";
  InterestRateLockEvent__[InterestRateLockEvent__["COMMITMENT_LETTER"] = 2] = "COMMITMENT_LETTER";
  InterestRateLockEvent__[InterestRateLockEvent__["DAY_OF_CLOSE"] = 3] = "DAY_OF_CLOSE";
})(InterestRateLockEvent__ || (InterestRateLockEvent__ = {}));
const InterestRateLockEvent_ = defineEnumProperties(InterestRateLockEvent__, [0, 1, 2, 3]);
const InterestRateLockEvent = extendEnum('InterestRateLockEvent', [["NO_LOCK", 0], ["SIGNED_TERM_SHEET", 1], ["COMMITMENT_LETTER", 2], ["DAY_OF_CLOSE", 3]])(InterestRateLockEvent_);
class Interest_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: InterestRateSpread.shape,
      3: 0,
      4: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Interest field index.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return InterestRateIndex.fromStrictJson_(v, getFieldMap);
          }
          return InterestRateIndex.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(InterestRateSpread);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Interest field spread.
                Expected type InterestRateSpread.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'InterestRateSpread', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Interest field lock_event_code.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return InterestRateLockEvent.fromStrictJson_(v, getFieldMap);
          }
          return InterestRateLockEvent.from(value);
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Interest field tenor_code.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Interest$1, 'Interest', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Interest to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get index() {
    return this.getFieldById(1, 0);
  }
  setIndex(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteIndex() {
    return this.setValueFieldById(1, null, null);
  }
  get spread() {
    return this.getFieldById(2, InterestRateSpread.getDefault());
  }
  setSpread(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSpread() {
    return this.setValueFieldById(2, null, null);
  }
  get lockEventCode() {
    return this.getFieldById(3, 0);
  }
  setLockEventCode(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLockEventCode() {
    return this.setValueFieldById(3, null, null);
  }
  get tenorCode() {
    return this.getFieldById(4, 0);
  }
  setTenorCode(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteTenorCode() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$1u(Interest_, protoName, DEV_MODE ? 'Interest' : '[Unknown]');
let Interest$1 = Interest_;
(function (_Interest) {})(Interest$1 || (Interest$1 = {}));
Object.defineProperties(Interest$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "index": {
        value: 1
      },
      "spread": {
        value: 2
      },
      "lock_event_code": {
        value: 3
      },
      "lockEventCode": {
        value: 3
      },
      "tenor_code": {
        value: 4
      },
      "tenorCode": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "index"
      },
      '2': {
        value: "spread"
      },
      '3': {
        value: "lock_event_code"
      },
      '4': {
        value: "tenor_code"
      }
    }))
  }
});
Object.defineProperties(Interest$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: InterestRateSpread.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Interest$1);
  Object.freeze(Interest$1.prototype);
}
class InterestInterval_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Interest$1.shape,
      3: Term.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Interest$1);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message InterestInterval field value.
                Expected type Interest.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Interest', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Term);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message InterestInterval field length.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(InterestInterval$1, 'InterestInterval', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message InterestInterval to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get value() {
    return this.getFieldById(1, Interest$1.getDefault());
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(1, null, null);
  }
  get length() {
    return this.getFieldById(3, Term.getDefault());
  }
  setLength(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLength() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$1u(InterestInterval_, protoName, DEV_MODE ? 'InterestInterval' : '[Unknown]');
let InterestInterval$1 = InterestInterval_;
(function (_InterestInterval) {})(InterestInterval$1 || (InterestInterval$1 = {}));
Object.defineProperties(InterestInterval$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      },
      "length": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      },
      '3': {
        value: "length"
      }
    }))
  }
});
Object.defineProperties(InterestInterval$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Interest$1.prototype,
      3: Term.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(InterestInterval$1);
  Object.freeze(InterestInterval$1.prototype);
}

function rateIndexTypeAndTenorToString(type, tenor) {
  return string(' ')(function* () {
    switch (type) {
      case RateIndexType.LIBOR:
        yield 'LIBOR';
        break;
      case RateIndexType.LIBOR_SWAPS:
        yield 'LIBOR Swaps';
        break;
      case RateIndexType.TREASURIES:
        yield 'US Treasuries';
        break;
      case RateIndexType.TREASURY_SWAPS:
        yield 'US Treasury Swaps';
        break;
      case RateIndexType.PRIME:
        yield 'PRIME';
        break;
      case RateIndexType.SOFR:
        yield 'SOFR';
        break;
      case RateIndexType.SOFR_SWAP:
        yield 'SOFR Swap';
        break;
      case RateIndexType.CME_TERM_SOFR:
        yield 'CME Term SOFR';
        break;
      default:
        runtime.checkAsserts() ? runtime.ensure(false, function () {
          return `Unknown rate index type (${type}).`;
        }) : undefined;
        break;
    }
    switch (tenor) {
      case LoanTenor.ONE_MONTH:
        yield '(1M)';
        break;
      case LoanTenor.THREE_MONTHS:
        yield '(3M)';
        break;
      case LoanTenor.SIX_MONTHS:
        yield '(6M)';
        break;
      case LoanTenor.ONE_YEAR:
        yield '(1Y)';
        break;
      case LoanTenor.TWO_YEARS:
        yield '(2Y)';
        break;
      case LoanTenor.THREE_YEARS:
        yield '(3Y)';
        break;
      case LoanTenor.FIVE_YEARS:
        yield '(5Y)';
        break;
      case LoanTenor.SEVEN_YEARS:
        yield '(7Y)';
        break;
      case LoanTenor.TEN_YEARS:
        yield '(10Y)';
        break;
      case LoanTenor.TWENTY_YEARS:
        yield '(20Y)';
        break;
      case LoanTenor.THIRTY_YEARS:
        yield '(30Y)';
        break;
    }
  }());
}
function rateIndexTypeAndTenorToStringShort(type, tenor) {
  return string(' ')(function* () {
    switch (type) {
      case RateIndexType.LIBOR:
        yield 'L';
        break;
      case RateIndexType.LIBOR_SWAPS:
        yield 'L Swap';
        break;
      case RateIndexType.TREASURIES:
        yield 'UST';
        break;
      case RateIndexType.TREASURY_SWAPS:
        yield 'UST Swap';
        break;
      case RateIndexType.PRIME:
        yield 'P';
        break;
      case RateIndexType.SOFR:
        yield 'SOFR';
        break;
      case RateIndexType.SOFR_SWAP:
        yield 'SOFR Swap';
        break;
      case RateIndexType.CME_TERM_SOFR:
        yield 'Term SOFR';
        break;
      default:
        runtime.checkAsserts() ? runtime.ensure(false, function () {
          return `Unknown rate index type (${type}).`;
        }) : undefined;
        break;
    }
    switch (tenor) {
      case LoanTenor.ONE_MONTH:
        yield '(1M)';
        break;
      case LoanTenor.THREE_MONTHS:
        yield '(3M)';
        break;
      case LoanTenor.SIX_MONTHS:
        yield '(6M)';
        break;
      case LoanTenor.ONE_YEAR:
        yield '(1Y)';
        break;
      case LoanTenor.TWO_YEARS:
        yield '(2Y)';
        break;
      case LoanTenor.THREE_YEARS:
        yield '(3Y)';
        break;
      case LoanTenor.FIVE_YEARS:
        yield '(5Y)';
        break;
      case LoanTenor.SEVEN_YEARS:
        yield '(7Y)';
        break;
      case LoanTenor.TEN_YEARS:
        yield '(10Y)';
        break;
      case LoanTenor.TWENTY_YEARS:
        yield '(20Y)';
        break;
      case LoanTenor.THIRTY_YEARS:
        yield '(30Y)';
        break;
    }
  }());
}
function loanTenorToMonths(value) {
  switch (value) {
    case LoanTenor.NONE:
      return 0;
      break;
    case LoanTenor.ONE_MONTH:
      return 1;
      break;
    case LoanTenor.THREE_MONTHS:
      return 3;
      break;
    case LoanTenor.SIX_MONTHS:
      return 6;
      break;
    case LoanTenor.ONE_YEAR:
      return 12;
      break;
    case LoanTenor.TWO_YEARS:
      return 24;
      break;
    case LoanTenor.THREE_YEARS:
      return 36;
      break;
    case LoanTenor.FIVE_YEARS:
      return 60;
      break;
    case LoanTenor.SEVEN_YEARS:
      return 84;
      break;
    case LoanTenor.TEN_YEARS:
      return 120;
      break;
    case LoanTenor.TWENTY_YEARS:
      return 240;
      break;
    case LoanTenor.THIRTY_YEARS:
      return 360;
      break;
  }
  runtime.checkAsserts() ? runtime.ensure(false, function () {
    return `Unknown loan tenor (${value}).`;
  }) : undefined;
}
function loanTenorToTerm(value) {
  return new Term().setUnit(TermUnit.MONTH).setLength(loanTenorToMonths(value));
}

// tslint:disable-next-line:no-unused-variable
var LiborTenor__;
(function (LiborTenor__) {
  LiborTenor__[LiborTenor__["ONE_MONTH"] = 0] = "ONE_MONTH";
  LiborTenor__[LiborTenor__["THREE_MONTHS"] = 1] = "THREE_MONTHS";
  LiborTenor__[LiborTenor__["SIX_MONTHS"] = 2] = "SIX_MONTHS";
  LiborTenor__[LiborTenor__["TWELVE_MONTHS"] = 3] = "TWELVE_MONTHS";
})(LiborTenor__ || (LiborTenor__ = {}));
const LiborTenor_ = defineEnumProperties(LiborTenor__, [0, 1, 2, 3]);
const LiborTenor = extendEnum('LiborTenor', [["ONE_MONTH", 0], ["THREE_MONTHS", 1], ["SIX_MONTHS", 2], ["TWELVE_MONTHS", 3]])(LiborTenor_);
var UsTreasuryTenor__;
(function (UsTreasuryTenor__) {
  UsTreasuryTenor__[UsTreasuryTenor__["ONE_YEAR"] = 0] = "ONE_YEAR";
  UsTreasuryTenor__[UsTreasuryTenor__["THREE_YEARS"] = 1] = "THREE_YEARS";
  UsTreasuryTenor__[UsTreasuryTenor__["FIVE_YEARS"] = 2] = "FIVE_YEARS";
  UsTreasuryTenor__[UsTreasuryTenor__["SEVEN_YEARS"] = 3] = "SEVEN_YEARS";
  UsTreasuryTenor__[UsTreasuryTenor__["TEN_YEARS"] = 4] = "TEN_YEARS";
})(UsTreasuryTenor__ || (UsTreasuryTenor__ = {}));
const UsTreasuryTenor_ = defineEnumProperties(UsTreasuryTenor__, [0, 1, 2, 3, 4]);
const UsTreasuryTenor = extendEnum('UsTreasuryTenor', [["ONE_YEAR", 0], ["THREE_YEARS", 1], ["FIVE_YEARS", 2], ["SEVEN_YEARS", 3], ["TEN_YEARS", 4]])(UsTreasuryTenor_);
var SofrSwapTenor__;
(function (SofrSwapTenor__) {
  SofrSwapTenor__[SofrSwapTenor__["FIVE_YEARS"] = 0] = "FIVE_YEARS";
  SofrSwapTenor__[SofrSwapTenor__["TEN_YEARS"] = 1] = "TEN_YEARS";
})(SofrSwapTenor__ || (SofrSwapTenor__ = {}));
const SofrSwapTenor_ = defineEnumProperties(SofrSwapTenor__, [0, 1]);
const SofrSwapTenor = extendEnum('SofrSwapTenor', [["FIVE_YEARS", 0], ["TEN_YEARS", 1]])(SofrSwapTenor_);
var CmeTermSofrTenor__;
(function (CmeTermSofrTenor__) {
  CmeTermSofrTenor__[CmeTermSofrTenor__["ONE_MONTH"] = 0] = "ONE_MONTH";
})(CmeTermSofrTenor__ || (CmeTermSofrTenor__ = {}));
const CmeTermSofrTenor_ = defineEnumProperties(CmeTermSofrTenor__, [0]);
const CmeTermSofrTenor = extendEnum('CmeTermSofrTenor', [["ONE_MONTH", 0]])(CmeTermSofrTenor_);

class Interest extends Interest_ {
  // --------------- Getters ---------------

  get rate() {
    return this.spread.value;
  }
  get rateFloor() {
    if (this.index === InterestRateIndex.FIXED) {
      return null;
    }
    return this.spread.floor;
  }
  get lockEvent() {
    if (!this.hasLockEvent()) {
      return null;
    }
    return this.lockEventCode;
  }
  get tenor() {
    if (!this.hasTenor()) {
      return null;
    }
    return new Term().setUnit(TermUnit.MONTH).setLength(getTenorLength(this.index, this.tenorCode));
  }

  // --------------- Setters ---------------

  setLockEvent(code) {
    return this.setLockEventCode(code);
  }
  setTenor(t) {
    if (!this.hasTenor()) {
      return this.setTenorCode(0);
    }
    if (t instanceof Term) {
      return this.setTenorCode(termToTenorCode(this.index, t));
    }
    return this.setTenorCode(t);
  }

  // --------------- has & equals methods ---------------

  hasLockEvent() {
    return Interest.hasLockEvent(this.index);
  }
  static hasLockEvent(index) {
    switch (index) {
      case InterestRateIndex.FIXED:
        return false;
      default:
        return true;
    }
  }
  hasTenor() {
    return Interest.hasTenor(this.index);
  }
  static hasTenor(index) {
    switch (index) {
      case InterestRateIndex.FIXED:
      case InterestRateIndex.PRIME:
      case InterestRateIndex.SOFR:
        return false;
      default:
        return true;
    }
  }
  static interestsEqual(a, b) {
    return a.equals(b);
  }

  // --------------- Formatters ---------------

  // Interest Formatting

  format() {
    return formatInterestLongOrShort(this, false);
  }
  formatShort() {
    return formatInterestLongOrShort(this, true);
  }

  // Interest Index Formatting

  formatIndex() {
    return Interest.formatIndex(this.index);
  }
  formatIndexShort() {
    return Interest.formatIndexShort(this.index);
  }
  static formatIndex(index) {
    return formatIndexLongOrShort(index, false);
  }
  static formatIndexShort(index) {
    return formatIndexLongOrShort(index, true);
  }

  // Interest Lock Event Formatting

  formatLockEvent() {
    if (!this.hasLockEvent()) {
      assert.fail(emsg`Index does not support Lock Event: ${this.index}.`);
      return '';
    }
    return Interest.formatLockEvent(this.lockEvent);
  }
  static formatLockEvent(event) {
    switch (event) {
      // null case is handled because existing code relies on value.lockEvent
      // returning null for those that do not have lock events
      case null:
        return '';
      case InterestRateLockEvent.NO_LOCK:
        return '';
      case InterestRateLockEvent.SIGNED_TERM_SHEET:
        return 'Signed Term Sheet';
      case InterestRateLockEvent.COMMITMENT_LETTER:
        return 'Commitment Letter';
      case InterestRateLockEvent.DAY_OF_CLOSE:
        return 'Day of Close';
      default:
        assert.fail(emsg`Unknown Interest Lock Event ${event}.`);
        return '';
    }
  }

  // Interest Tenor Formatting

  formatTenor() {
    return Interest.formatTenor(this.index, this.tenorCode);
  }
  static formatTenor(index, pTenor) {
    if (!Interest.hasTenor(index)) {
      assert.fail(emsg`Index ${index} does have a tenor.`);
      return '';
    }
    let tenor;
    if (pTenor instanceof Term) {
      tenor = termToTenorCode(index, pTenor);
    } else {
      tenor = pTenor;
    }
    return formatTenor(index, tenor);
  }

  // --------------- Converters ---------------

  static interestIndexToRateIndexType(interest) {
    switch (interest.index) {
      case InterestRateIndex.LIBOR:
        return RateIndexType.LIBOR;
      case InterestRateIndex.LIBOR_SWAP:
        return RateIndexType.LIBOR_SWAPS;
      case InterestRateIndex.PRIME:
        return RateIndexType.PRIME;
      case InterestRateIndex.US_TREASURY:
        return RateIndexType.TREASURIES;
      case InterestRateIndex.US_TREASURY_SWAP:
        return RateIndexType.TREASURY_SWAPS;
      case InterestRateIndex.SOFR:
        return RateIndexType.SOFR;
      case InterestRateIndex.SOFR_SWAP:
        return RateIndexType.SOFR_SWAP;
      case InterestRateIndex.CME_TERM_SOFR:
        return RateIndexType.CME_TERM_SOFR;
      case InterestRateIndex.FIXED:
        assert.fail(emsg`No rate index for fixed interest type`);
    }
    assert.fail(emsg`Unknown interest index ${interest.index}`);
  }
  static interestToLoanTenor(interest) {
    switch (interest.index) {
      case InterestRateIndex.FIXED:
        return LoanTenor.NONE;
      case InterestRateIndex.LIBOR:
      case InterestRateIndex.LIBOR_SWAP:
        return liborTenorToLoanTenor(interest.tenorCode);
      case InterestRateIndex.PRIME:
        return LoanTenor.NONE;
      case InterestRateIndex.US_TREASURY:
      case InterestRateIndex.US_TREASURY_SWAP:
        return usTreasuryTenorToLoanTenor(interest.tenorCode);
      case InterestRateIndex.SOFR:
        return LoanTenor.NONE;
      case InterestRateIndex.SOFR_SWAP:
        return sofrSwapTenorToLoanTenor(interest.tenorCode);
      case InterestRateIndex.CME_TERM_SOFR:
        return cmeTermSofrTenorToLoanTenor(interest.tenorCode);
    }
    assert.fail(emsg`Unknown interest index ${interest.index}.`);
    return LoanTenor.NONE;
  }
}
class InterestInterval extends InterestInterval_ {
  // --------------- Getters ---------------

  // --------------- Setters ---------------

  // --------------- has & equals methods ---------------

  static interestIntervalsEqual(a, b) {
    return a.equals(b);
  }

  // --------------- Formatters ---------------

  // Interest Interval Formatting

  static format(interval, isFirst) {
    return formatInterestIntervalLongOrShort(interval, isFirst, false);
  }
  static formatShort(interval, isFirst) {
    return formatInterestIntervalLongOrShort(interval, isFirst, true);
  }

  // Interest Interval Length Formatting

  static formatLength(interval, isFirst) {
    return formatInterestIntervalLength(interval, isFirst, false);
  }
  static formatLengthShort(interval, isFirst) {
    return formatInterestIntervalLength(interval, isFirst, true);
  }
}

// --------------- Private Functions ---------------

// Interest Functions

function formatInterestLongOrShort(interest, short) {
  switch (interest.index) {
    case InterestRateIndex.FIXED:
      return [formatPercent(interest.spread.value), '(Fixed)'].join(' ');
    case InterestRateIndex.LIBOR:
      return [formatIndexLongOrShort(interest.index, short), short ? null : interest.formatTenor(), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].filter(u => u != null).join(' ');
    case InterestRateIndex.LIBOR_SWAP:
      return [formatIndexLongOrShort(interest.index, short), short ? null : interest.formatTenor(), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].filter(u => u != null).join(' ');
    case InterestRateIndex.PRIME:
      return [short ? 'P' : 'Prime', !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].join(' ');
    case InterestRateIndex.US_TREASURY:
      return [formatIndexLongOrShort(interest.index, short), short ? null : interest.formatTenor(), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].filter(u => u != null).join(' ');
    case InterestRateIndex.US_TREASURY_SWAP:
      return [formatIndexLongOrShort(interest.index, short), short ? null : interest.formatTenor(), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].filter(u => u != null).join(' ');
    case InterestRateIndex.SOFR:
      return [formatIndexLongOrShort(interest.index, short), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].join(' ');
    case InterestRateIndex.SOFR_SWAP:
      return [formatIndexLongOrShort(interest.index, short), short ? null : interest.formatTenor(), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].filter(u => u != null).join(' ');
    case InterestRateIndex.CME_TERM_SOFR:
      return [formatIndexLongOrShort(interest.index, short), short ? null : interest.formatTenor(), !percentIsNegative(interest.spread.value) ? '+' : '', formatPercent(interest.spread.value)].filter(u => u != null).join(' ');
    default:
      assert.fail(emsg`Unknown investment category: ${interest.index}`);
      return '';
  }
}

// Index Functions

function formatIndexLongOrShort(index, short) {
  switch (index) {
    case InterestRateIndex.FIXED:
      return '(Fixed)';
    case InterestRateIndex.LIBOR:
      return short ? 'L' : 'LIBOR';
    case InterestRateIndex.LIBOR_SWAP:
      return short ? 'L Swap' : 'LIBOR Swaps';
    case InterestRateIndex.PRIME:
      return short ? 'P' : 'Prime';
    case InterestRateIndex.US_TREASURY:
      return short ? 'UST' : 'US Treasuries';
    case InterestRateIndex.US_TREASURY_SWAP:
      return short ? 'UST Swap' : 'US Treasuries Swaps';
    case InterestRateIndex.SOFR:
      return 'SOFR';
    case InterestRateIndex.SOFR_SWAP:
      return 'SOFR Swap';
    case InterestRateIndex.CME_TERM_SOFR:
      return short ? 'Term SOFR' : 'CME Term SOFR';
    default:
      assert.fail(emsg`Unknown investment category: ${index}`);
      return '';
  }
}

// Tenor Functions

function formatTenor(index, tenor) {
  switch (index) {
    case InterestRateIndex.LIBOR:
    case InterestRateIndex.LIBOR_SWAP:
      switch (tenor) {
        case LiborTenor.ONE_MONTH:
          return '(1M)';
        case LiborTenor.THREE_MONTHS:
          return '(3M)';
        case LiborTenor.SIX_MONTHS:
          return '(6M)';
        case LiborTenor.TWELVE_MONTHS:
          return '(1Y)';
        default:
          assert.fail(emsg`Unknown LIBOR (or SWAP) tenor ${tenor}`);
      }
      break;
    case InterestRateIndex.US_TREASURY:
    case InterestRateIndex.US_TREASURY_SWAP:
      switch (tenor) {
        case UsTreasuryTenor.ONE_YEAR:
          return '(1Y)';
        case UsTreasuryTenor.THREE_YEARS:
          return '(3Y)';
        case UsTreasuryTenor.FIVE_YEARS:
          return '(5Y)';
        case UsTreasuryTenor.SEVEN_YEARS:
          return '(7Y)';
        case UsTreasuryTenor.TEN_YEARS:
          return '(10Y)';
        default:
          assert.fail(emsg`Unknown US_TREASURY (or SWAP) tenor ${tenor}`);
      }
      break;
    case InterestRateIndex.SOFR_SWAP:
      switch (tenor) {
        case SofrSwapTenor.FIVE_YEARS:
          return '(5Y)';
        case SofrSwapTenor.TEN_YEARS:
          return '(10Y)';
        default:
          assert.fail(emsg`Unknown Sofr Swap tenor ${tenor}`);
      }
      break;
    case InterestRateIndex.CME_TERM_SOFR:
      switch (tenor) {
        case CmeTermSofrTenor.ONE_MONTH:
          return '(1M)';
        default:
          assert.fail(emsg`Unknown CME Term Sofr Swap tenor ${tenor}`);
      }
      break;
    default:
      assert.fail(emsg`Unknown investment category: ${index}`);
  }
  assert.fail(emsg`Issue formatting investment category with index: ${index}`);
  return '';
}
function termToTenorCode(index, term) {
  switch (index) {
    case InterestRateIndex.LIBOR:
    case InterestRateIndex.LIBOR_SWAP:
      switch (term.length) {
        case 1:
          return LiborTenor.ONE_MONTH;
        case 3:
          return LiborTenor.THREE_MONTHS;
        case 6:
          return LiborTenor.THREE_MONTHS;
        case 12:
          return LiborTenor.TWELVE_MONTHS;
        default:
          assert.fail(emsg`Unknown LIBOR (or SWAP) term length: ${term.length}`);
          return LiborTenor.ONE_MONTH;
      }
    case InterestRateIndex.US_TREASURY:
    case InterestRateIndex.US_TREASURY_SWAP:
      switch (term.length) {
        case 1 * 12:
          return UsTreasuryTenor.ONE_YEAR;
        case 3 * 12:
          return UsTreasuryTenor.THREE_YEARS;
        case 5 * 12:
          return UsTreasuryTenor.FIVE_YEARS;
        case 7 * 12:
          return UsTreasuryTenor.SEVEN_YEARS;
        case 10 * 12:
          return UsTreasuryTenor.TEN_YEARS;
        default:
          assert.fail(emsg`Unknown US_TREASURY (or SWAP) term length: ${term.length}`);
          return UsTreasuryTenor.ONE_YEAR;
      }
    case InterestRateIndex.SOFR_SWAP:
      switch (term.length) {
        case 5 * 12:
          return SofrSwapTenor.FIVE_YEARS;
        case 10 * 12:
          return SofrSwapTenor.TEN_YEARS;
        default:
          assert.fail(emsg`Unknown SOFR_SWAP term length: ${term.length}`);
          return SofrSwapTenor.FIVE_YEARS;
      }
    case InterestRateIndex.CME_TERM_SOFR:
      switch (term.length) {
        case 1:
          return CmeTermSofrTenor.ONE_MONTH;
        default:
          assert.fail(emsg`Unknown CME_TERM_SOFR term length: ${term.length}`);
          return CmeTermSofrTenor.ONE_MONTH;
      }
    default:
      assert.fail(emsg`Unknown index type: ${index}`);
      return 0;
  }
}
function getTenorLength(index, tenor) {
  switch (index) {
    case InterestRateIndex.LIBOR:
    case InterestRateIndex.LIBOR_SWAP:
      switch (tenor) {
        case LiborTenor.ONE_MONTH:
          return 1;
        case LiborTenor.THREE_MONTHS:
          return 3;
        case LiborTenor.SIX_MONTHS:
          return 6;
        case LiborTenor.TWELVE_MONTHS:
          return 12;
        default:
          assert.fail(emsg`Unknown LIBOR (or SWAP) tenor: ${tenor}`);
          return 1;
      }
    case InterestRateIndex.US_TREASURY:
    case InterestRateIndex.US_TREASURY_SWAP:
      switch (tenor) {
        case UsTreasuryTenor.ONE_YEAR:
          return 1 * 12;
        case UsTreasuryTenor.THREE_YEARS:
          return 3 * 12;
        case UsTreasuryTenor.FIVE_YEARS:
          return 5 * 12;
        case UsTreasuryTenor.SEVEN_YEARS:
          return 7 * 12;
        case UsTreasuryTenor.TEN_YEARS:
          return 10 * 12;
        default:
          assert.fail(emsg`Unknown US_TREASURY (or SWAP) tenor: ${tenor}`);
          return 1 * 12;
      }
    case InterestRateIndex.SOFR_SWAP:
      switch (tenor) {
        case SofrSwapTenor.FIVE_YEARS:
          return 5 * 12;
        case SofrSwapTenor.TEN_YEARS:
          return 10 * 12;
        default:
          assert.fail(emsg`Unknown SOFR_SWAP tenor: ${tenor}`);
          return 5 * 12;
      }
    case InterestRateIndex.CME_TERM_SOFR:
      switch (tenor) {
        case CmeTermSofrTenor.ONE_MONTH:
          return 1;
        default:
          assert.fail(emsg`Unknown CME_TERM_SOFR tenor: ${tenor}`);
          return 1;
      }
    default:
      assert.fail(emsg`Unknown index type to set tenor: ${index}`);
      return 1;
  }
}

// Interest Interval Functions

function formatInterestIntervalLongOrShort(interval, isFirst, short) {
  return [formatInterestIntervalLength(interval, isFirst, short), short ? interval.value.formatShort() : interval.value.format()].join('');
}
function formatInterestIntervalLength(interval, isFirst, short) {
  assert.same(interval.length.unit, TermUnit.MONTH);
  if (interval.length.isZero()) {
    return isFirst ? '' : short ? 'Then: ' : 'Remainder: ';
  }
  if (interval.length.length % 12 === 0) {
    const years = Math.round(interval.length.length / 12 * 100) / 100;
    return short ? `${years}Y: ` : `${years} Year${years === 1 ? '' : 's'}: `;
  }
  const months = interval.length.length;
  return short ? `${months}M: ` : `${months} Month${months === 1 ? '' : 's'}: `;
}

// --------------------- LoanTenor ---------------------------
// TODO(jesse): Move away from LoanTenor System

function liborTenorToLoanTenor(tenor) {
  switch (tenor) {
    case LiborTenor.ONE_MONTH:
      return LoanTenor.ONE_MONTH;
    case LiborTenor.THREE_MONTHS:
      return LoanTenor.THREE_MONTHS;
    case LiborTenor.SIX_MONTHS:
      return LoanTenor.SIX_MONTHS;
    case LiborTenor.TWELVE_MONTHS:
      return LoanTenor.ONE_YEAR;
  }
  assert.fail(emsg`Unknown LIBOR tenor ${tenor}.`);
  return LoanTenor.NONE;
}
function usTreasuryTenorToLoanTenor(tenor) {
  switch (tenor) {
    case UsTreasuryTenor.ONE_YEAR:
      return LoanTenor.ONE_YEAR;
    case UsTreasuryTenor.THREE_YEARS:
      return LoanTenor.THREE_YEARS;
    case UsTreasuryTenor.FIVE_YEARS:
      return LoanTenor.FIVE_YEARS;
    case UsTreasuryTenor.SEVEN_YEARS:
      return LoanTenor.SEVEN_YEARS;
    case UsTreasuryTenor.TEN_YEARS:
      return LoanTenor.TEN_YEARS;
  }
  assert.fail(emsg`Unknown US_TREASURY tenor ${tenor}.`);
  return LoanTenor.NONE;
}
function sofrSwapTenorToLoanTenor(tenor) {
  switch (tenor) {
    case SofrSwapTenor.FIVE_YEARS:
      return LoanTenor.FIVE_YEARS;
    case SofrSwapTenor.TEN_YEARS:
      return LoanTenor.TEN_YEARS;
  }
  assert.fail(emsg`Unknown SOFR_SWAP tenor ${tenor}.`);
  return LoanTenor.NONE;
}
function cmeTermSofrTenorToLoanTenor(tenor) {
  switch (tenor) {
    case CmeTermSofrTenor.ONE_MONTH:
      return LoanTenor.ONE_MONTH;
  }
  assert.fail(emsg`Unknown CME_SOFR tenor ${tenor}.`);
  return LoanTenor.NONE;
}

registerCanonical(InterestRateSpread_, InterestRateSpread_);
registerCanonical(InterestRateLockEvent, InterestRateLockEvent);
registerCanonical(Interest_, Interest);
registerCanonical(InterestInterval_, InterestInterval);

function _defineProperty$1t(obj, key, value) { key = _toPropertyKey$1t(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1t(arg) { var key = _toPrimitive$1t(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1t(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _OfferStipulation_Value_;
(function (_OfferStipulation_Value_) {
  _OfferStipulation_Value_[_OfferStipulation_Value_["UNSET"] = 0] = "UNSET";
  _OfferStipulation_Value_[_OfferStipulation_Value_["PERCENT"] = 2] = "PERCENT";
  _OfferStipulation_Value_[_OfferStipulation_Value_["TERM"] = 3] = "TERM";
  _OfferStipulation_Value_[_OfferStipulation_Value_["NUMBER"] = 4] = "NUMBER";
})(_OfferStipulation_Value_ || (_OfferStipulation_Value_ = {}));
const _OfferStipulation_Value = defineEnumProperties(_OfferStipulation_Value_, [2, 3, 4]);
class OfferStipulation_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: Percent.shape,
      3: Term.shape,
      4: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OfferStipulation field type.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferStipulation field percent.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Term);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferStipulation field term.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message OfferStipulation field number.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferStipulation, 'OfferStipulation', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferStipulation to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ValueCase() {
    return _OfferStipulation_Value;
  }
  get valueCase() {
    return this.getCaseFromIds([2, 3, 4]);
  }
  get type() {
    return this.getFieldById(1, "");
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get percent() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setPercent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "value");
  }
  deletePercent() {
    return this.setValueFieldById(2, null, "value");
  }
  get term() {
    return this.getFieldById(3, Term.getDefault());
  }
  setTerm(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "value");
  }
  deleteTerm() {
    return this.setValueFieldById(3, null, "value");
  }
  get number() {
    return this.getFieldById(4, 0);
  }
  setNumber(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, "value");
  }
  deleteNumber() {
    return this.setValueFieldById(4, null, "value");
  }
}
_defineProperty$1t(OfferStipulation_, protoName, DEV_MODE ? 'OfferStipulation' : '[Unknown]');
let OfferStipulation = OfferStipulation_;
(function (_OfferStipulation) {})(OfferStipulation || (OfferStipulation = {}));
Object.defineProperties(OfferStipulation, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "percent": {
        value: 2
      },
      "term": {
        value: 3
      },
      "number": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "percent"
      },
      '3': {
        value: "term"
      },
      '4': {
        value: "number"
      }
    }))
  }
});
Object.defineProperties(OfferStipulation.prototype, {
  oneofs_: {
    value: {
      "value": [2, 3, 4]
    }
  },
  prototypes_: {
    value: {
      2: Percent.prototype,
      3: Term.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferStipulation);
  Object.freeze(OfferStipulation.prototype);
}

function _defineProperty$1s(obj, key, value) { key = _toPropertyKey$1s(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1s(arg) { var key = _toPrimitive$1s(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1s(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var TimeUnit__;
(function (TimeUnit__) {
  TimeUnit__[TimeUnit__["MONTH"] = 0] = "MONTH";
})(TimeUnit__ || (TimeUnit__ = {}));
const TimeUnit_ = defineEnumProperties(TimeUnit__, [0]);
const TimeUnit = extendEnum('TimeUnit', [["MONTH", 0]])(TimeUnit_);
class TimeInterval_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message TimeInterval field unit.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return TimeUnit.fromStrictJson_(v, getFieldMap);
          }
          return TimeUnit.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message TimeInterval field length.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(TimeInterval, 'TimeInterval', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message TimeInterval to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get unit() {
    return this.getFieldById(1, 0);
  }
  setUnit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUnit() {
    return this.setValueFieldById(1, null, null);
  }
  get length() {
    return this.getFieldById(2, 0);
  }
  setLength(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLength() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1s(TimeInterval_, protoName, DEV_MODE ? 'TimeInterval' : '[Unknown]');
let TimeInterval = TimeInterval_;
(function (_TimeInterval) {})(TimeInterval || (TimeInterval = {}));
Object.defineProperties(TimeInterval, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "unit": {
        value: 1
      },
      "length": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "unit"
      },
      '2': {
        value: "length"
      }
    }))
  }
});
Object.defineProperties(TimeInterval.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(TimeInterval);
  Object.freeze(TimeInterval.prototype);
}

function _defineProperty$1r(obj, key, value) { key = _toPropertyKey$1r(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1r(arg) { var key = _toPrimitive$1r(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1r(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class TimePeriod_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: TimeInterval.shape,
      2: TimeInterval.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TimeInterval);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message TimePeriod field initial.
                Expected type TimeInterval.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TimeInterval', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TimeInterval);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message TimePeriod field final.
                Expected type TimeInterval.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TimeInterval', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(TimePeriod, 'TimePeriod', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message TimePeriod to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get initial() {
    return this.getFieldById(1, TimeInterval.getDefault());
  }
  setInitial(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteInitial() {
    return this.setValueFieldById(1, null, null);
  }
  get final() {
    return this.getFieldById(2, TimeInterval.getDefault());
  }
  setFinal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteFinal() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1r(TimePeriod_, protoName, DEV_MODE ? 'TimePeriod' : '[Unknown]');
let TimePeriod = TimePeriod_;
(function (_TimePeriod) {})(TimePeriod || (TimePeriod = {}));
Object.defineProperties(TimePeriod, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "initial": {
        value: 1
      },
      "final": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "initial"
      },
      '2': {
        value: "final"
      }
    }))
  }
});
Object.defineProperties(TimePeriod.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: TimeInterval.prototype,
      2: TimeInterval.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(TimePeriod);
  Object.freeze(TimePeriod.prototype);
}

function _defineProperty$1q(obj, key, value) { key = _toPropertyKey$1q(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1q(arg) { var key = _toPrimitive$1q(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1q(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class PeriodRate_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: TimePeriod.shape,
      2: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TimePeriod);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PeriodRate field period.
                Expected type TimePeriod.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TimePeriod', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PeriodRate field rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PeriodRate, 'PeriodRate', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PeriodRate to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get period() {
    return this.getFieldById(1, TimePeriod.getDefault());
  }
  setPeriod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deletePeriod() {
    return this.setValueFieldById(1, null, null);
  }
  get rate() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteRate() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1q(PeriodRate_, protoName, DEV_MODE ? 'PeriodRate' : '[Unknown]');
let PeriodRate = PeriodRate_;
(function (_PeriodRate) {})(PeriodRate || (PeriodRate = {}));
Object.defineProperties(PeriodRate, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "period": {
        value: 1
      },
      "rate": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "period"
      },
      '2': {
        value: "rate"
      }
    }))
  }
});
Object.defineProperties(PeriodRate.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: TimePeriod.prototype,
      2: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PeriodRate);
  Object.freeze(PeriodRate.prototype);
}
class RepeatedPeriodRate_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: PeriodRate.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(PeriodRate);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedPeriodRate field period_rate.
                Expected type PeriodRate.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'PeriodRate', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedPeriodRate, 'RepeatedPeriodRate', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedPeriodRate to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get periodRateList() {
    return this.getRepeatedFieldById(1);
  }
  hasPeriodRate(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addPeriodRate(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachPeriodRate(values) {
    let T = this;
    for (const value of values) {
      T = T.addPeriodRate(value);
    }
    return T;
  }
  setPeriodRate(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removePeriodRate(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearPeriodRateList() {
    return this.clearRepeatedFieldById(1);
  }
  setPeriodRateList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$1q(RepeatedPeriodRate_, protoName, DEV_MODE ? 'RepeatedPeriodRate' : '[Unknown]');
let RepeatedPeriodRate = RepeatedPeriodRate_;
(function (_RepeatedPeriodRate) {})(RepeatedPeriodRate || (RepeatedPeriodRate = {}));
Object.defineProperties(RepeatedPeriodRate, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "period_rate": {
        value: 1
      },
      "periodRate": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "period_rate"
      }
    }))
  }
});
Object.defineProperties(RepeatedPeriodRate.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: PeriodRate.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedPeriodRate);
  Object.freeze(RepeatedPeriodRate.prototype);
}

function _defineProperty$1p(obj, key, value) { key = _toPropertyKey$1p(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1p(arg) { var key = _toPrimitive$1p(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1p(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var PrepaymentPenaltyType__;
(function (PrepaymentPenaltyType__) {
  PrepaymentPenaltyType__[PrepaymentPenaltyType__["NONE"] = 0] = "NONE";
  PrepaymentPenaltyType__[PrepaymentPenaltyType__["LOCKOUT"] = 1] = "LOCKOUT";
  PrepaymentPenaltyType__[PrepaymentPenaltyType__["DEFEASANCE"] = 2] = "DEFEASANCE";
  PrepaymentPenaltyType__[PrepaymentPenaltyType__["YIELD_MAINTENANCE"] = 3] = "YIELD_MAINTENANCE";
  PrepaymentPenaltyType__[PrepaymentPenaltyType__["DECLINING"] = 4] = "DECLINING";
})(PrepaymentPenaltyType__ || (PrepaymentPenaltyType__ = {}));
const PrepaymentPenaltyType_ = defineEnumProperties(PrepaymentPenaltyType__, [0, 1, 2, 3, 4]);
const PrepaymentPenaltyType = extendEnum('PrepaymentPenaltyType', [["NONE", 0], ["LOCKOUT", 1], ["DEFEASANCE", 2], ["YIELD_MAINTENANCE", 3], ["DECLINING", 4]])(PrepaymentPenaltyType_);
class PrepaymentPenalty_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: RepeatedPeriodRate.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message PrepaymentPenalty field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return PrepaymentPenaltyType.fromStrictJson_(v, getFieldMap);
          }
          return PrepaymentPenaltyType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedPeriodRate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message PrepaymentPenalty field declining_details.
                Expected type RepeatedPeriodRate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedPeriodRate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PrepaymentPenalty, 'PrepaymentPenalty', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PrepaymentPenalty to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get decliningDetails() {
    return this.getFieldById(2, RepeatedPeriodRate.getDefault());
  }
  setDecliningDetails(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteDecliningDetails() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1p(PrepaymentPenalty_, protoName, DEV_MODE ? 'PrepaymentPenalty' : '[Unknown]');
let PrepaymentPenalty = PrepaymentPenalty_;
(function (_PrepaymentPenalty) {})(PrepaymentPenalty || (PrepaymentPenalty = {}));
Object.defineProperties(PrepaymentPenalty, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "declining_details": {
        value: 2
      },
      "decliningDetails": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "declining_details"
      }
    }))
  }
});
Object.defineProperties(PrepaymentPenalty.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: RepeatedPeriodRate.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PrepaymentPenalty);
  Object.freeze(PrepaymentPenalty.prototype);
}

// tslint:disable-next-line:no-unused-variable
var RateCalculationMethod__;
(function (RateCalculationMethod__) {
  RateCalculationMethod__[RateCalculationMethod__["UNSET"] = 0] = "UNSET";
  RateCalculationMethod__[RateCalculationMethod__["ACTUAL_PER_THREE_SIXTY"] = 1] = "ACTUAL_PER_THREE_SIXTY";
  RateCalculationMethod__[RateCalculationMethod__["THIRTY_PER_THREE_SIXTY"] = 2] = "THIRTY_PER_THREE_SIXTY";
})(RateCalculationMethod__ || (RateCalculationMethod__ = {}));
const RateCalculationMethod_ = defineEnumProperties(RateCalculationMethod__, [0, 1, 2]);
const RateCalculationMethod = extendEnum('RateCalculationMethod', [["UNSET", 0], ["ACTUAL_PER_THREE_SIXTY", 1], ["THIRTY_PER_THREE_SIXTY", 2]])(RateCalculationMethod_);

function _defineProperty$1o(obj, key, value) { key = _toPropertyKey$1o(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1o(arg) { var key = _toPrimitive$1o(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1o(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class RepaymentSchedule_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Term.shape,
      2: Term.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Term);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message RepaymentSchedule field interest_only_period.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Term);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message RepaymentSchedule field amortization_period.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepaymentSchedule, 'RepaymentSchedule', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepaymentSchedule to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get interestOnlyPeriod() {
    return this.getFieldById(1, Term.getDefault());
  }
  setInterestOnlyPeriod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteInterestOnlyPeriod() {
    return this.setValueFieldById(1, null, null);
  }
  get amortizationPeriod() {
    return this.getFieldById(2, Term.getDefault());
  }
  setAmortizationPeriod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteAmortizationPeriod() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1o(RepaymentSchedule_, protoName, DEV_MODE ? 'RepaymentSchedule' : '[Unknown]');
let RepaymentSchedule = RepaymentSchedule_;
(function (_RepaymentSchedule) {})(RepaymentSchedule || (RepaymentSchedule = {}));
Object.defineProperties(RepaymentSchedule, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "interest_only_period": {
        value: 1
      },
      "interestOnlyPeriod": {
        value: 1
      },
      "amortization_period": {
        value: 2
      },
      "amortizationPeriod": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "interest_only_period"
      },
      '2': {
        value: "amortization_period"
      }
    }))
  }
});
Object.defineProperties(RepaymentSchedule.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Term.prototype,
      2: Term.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepaymentSchedule);
  Object.freeze(RepaymentSchedule.prototype);
}

function _defineProperty$1n(obj, key, value) { key = _toPropertyKey$1n(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1n(arg) { var key = _toPrimitive$1n(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1n(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var ReservesItemType__;
(function (ReservesItemType__) {
  ReservesItemType__[ReservesItemType__["TAXES_AND_INSURANCE"] = 0] = "TAXES_AND_INSURANCE";
  ReservesItemType__[ReservesItemType__["TAXES"] = 6] = "TAXES";
  ReservesItemType__[ReservesItemType__["INSURANCE"] = 7] = "INSURANCE";
  ReservesItemType__[ReservesItemType__["CAPEX"] = 1] = "CAPEX";
  ReservesItemType__[ReservesItemType__["TENANT_IMPROVEMENTS_AND_LEASING_COMISSION"] = 2] = "TENANT_IMPROVEMENTS_AND_LEASING_COMISSION";
  ReservesItemType__[ReservesItemType__["INTEREST_AND_OPERATING"] = 3] = "INTEREST_AND_OPERATING";
  ReservesItemType__[ReservesItemType__["FURNITURE_FIXTURES_AND_EQUIPMENT"] = 4] = "FURNITURE_FIXTURES_AND_EQUIPMENT";
  ReservesItemType__[ReservesItemType__["OTHER"] = 5] = "OTHER";
})(ReservesItemType__ || (ReservesItemType__ = {}));
const ReservesItemType_ = defineEnumProperties(ReservesItemType__, [0, 6, 7, 1, 2, 3, 4, 5]);
const ReservesItemType = extendEnum('ReservesItemType', [["TAXES_AND_INSURANCE", 0], ["TAXES", 6], ["INSURANCE", 7], ["CAPEX", 1], ["TENANT_IMPROVEMENTS_AND_LEASING_COMISSION", 2], ["INTEREST_AND_OPERATING", 3], ["FURNITURE_FIXTURES_AND_EQUIPMENT", 4], ["OTHER", 5]])(ReservesItemType_);
class ReservesItem_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: CurrencyValue.shape,
      3: CurrencyValue.shape,
      4: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ReservesItem field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ReservesItemType.fromStrictJson_(v, getFieldMap);
          }
          return ReservesItemType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ReservesItem field upfront.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ReservesItem field monthly.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ReservesItem field cap.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ReservesItem, 'ReservesItem', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ReservesItem to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get upfront() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setUpfront(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUpfront() {
    return this.setValueFieldById(2, null, null);
  }
  get monthly() {
    return this.getFieldById(3, CurrencyValue.getDefault());
  }
  setMonthly(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteMonthly() {
    return this.setValueFieldById(3, null, null);
  }
  get cap() {
    return this.getFieldById(4, CurrencyValue.getDefault());
  }
  setCap(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteCap() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$1n(ReservesItem_, protoName, DEV_MODE ? 'ReservesItem' : '[Unknown]');
let ReservesItem = ReservesItem_;
(function (_ReservesItem) {})(ReservesItem || (ReservesItem = {}));
Object.defineProperties(ReservesItem, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "upfront": {
        value: 2
      },
      "monthly": {
        value: 3
      },
      "cap": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "upfront"
      },
      '3': {
        value: "monthly"
      },
      '4': {
        value: "cap"
      }
    }))
  }
});
Object.defineProperties(ReservesItem.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: CurrencyValue.prototype,
      3: CurrencyValue.prototype,
      4: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ReservesItem);
  Object.freeze(ReservesItem.prototype);
}

function _defineProperty$1m(obj, key, value) { key = _toPropertyKey$1m(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1m(arg) { var key = _toPrimitive$1m(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1m(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _UserOrProjectShare_Value_;
(function (_UserOrProjectShare_Value_) {
  _UserOrProjectShare_Value_[_UserOrProjectShare_Value_["UNSET"] = 0] = "UNSET";
  _UserOrProjectShare_Value_[_UserOrProjectShare_Value_["USER_ID"] = 1] = "USER_ID";
  _UserOrProjectShare_Value_[_UserOrProjectShare_Value_["PROJECT_SHARE_ID"] = 2] = "PROJECT_SHARE_ID";
})(_UserOrProjectShare_Value_ || (_UserOrProjectShare_Value_ = {}));
const _UserOrProjectShare_Value = defineEnumProperties(_UserOrProjectShare_Value_, [1, 2]);
class UserOrProjectShare_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UserOrProjectShare field user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UserOrProjectShare field project_share_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UserOrProjectShare, 'UserOrProjectShare', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UserOrProjectShare to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ValueCase() {
    return _UserOrProjectShare_Value;
  }
  get valueCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get userId() {
    return this.getFieldById(1, 0);
  }
  setUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "value");
  }
  deleteUserId() {
    return this.setValueFieldById(1, null, "value");
  }
  get projectShareId() {
    return this.getFieldById(2, 0);
  }
  setProjectShareId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "value");
  }
  deleteProjectShareId() {
    return this.setValueFieldById(2, null, "value");
  }
}
_defineProperty$1m(UserOrProjectShare_, protoName, DEV_MODE ? 'UserOrProjectShare' : '[Unknown]');
let UserOrProjectShare = UserOrProjectShare_;
(function (_UserOrProjectShare) {})(UserOrProjectShare || (UserOrProjectShare = {}));
Object.defineProperties(UserOrProjectShare, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "user_id": {
        value: 1
      },
      "userId": {
        value: 1
      },
      "project_share_id": {
        value: 2
      },
      "projectShareId": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "user_id"
      },
      '2': {
        value: "project_share_id"
      }
    }))
  }
});
Object.defineProperties(UserOrProjectShare.prototype, {
  oneofs_: {
    value: {
      "value": [1, 2]
    }
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UserOrProjectShare);
  Object.freeze(UserOrProjectShare.prototype);
}

function _defineProperty$1l(obj, key, value) { key = _toPropertyKey$1l(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1l(arg) { var key = _toPrimitive$1l(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1l(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Principal_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CurrencyValue.shape,
      2: CurrencyValue.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Principal field total.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Principal field guarantee.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Principal, 'Principal', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Principal to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get total() {
    return this.getFieldById(1, CurrencyValue.getDefault());
  }
  setTotal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteTotal() {
    return this.setValueFieldById(1, null, null);
  }
  get guarantee() {
    return this.getFieldById(2, CurrencyValue.getDefault());
  }
  setGuarantee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteGuarantee() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1l(Principal_, protoName, DEV_MODE ? 'Principal' : '[Unknown]');
let Principal = Principal_;
(function (_Principal) {})(Principal || (Principal = {}));
Object.defineProperties(Principal, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "total": {
        value: 1
      },
      "guarantee": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "total"
      },
      '2': {
        value: "guarantee"
      }
    }))
  }
});
Object.defineProperties(Principal.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CurrencyValue.prototype,
      2: CurrencyValue.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Principal);
  Object.freeze(Principal.prototype);
}
var DealSide__;
(function (DealSide__) {
  DealSide__[DealSide__["CAPITAL_SOURCE"] = 0] = "CAPITAL_SOURCE";
  DealSide__[DealSide__["SPONSOR"] = 1] = "SPONSOR";
})(DealSide__ || (DealSide__ = {}));
const DealSide_ = defineEnumProperties(DealSide__, [0, 1]);
const DealSide = extendEnum('DealSide', [["CAPITAL_SOURCE", 0], ["SPONSOR", 1]])(DealSide_);
class OfferLastModified_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: DEPRECATED_Date.shape,
      2: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferLastModified field date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferLastModified field side.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DealSide.fromStrictJson_(v, getFieldMap);
          }
          return DealSide.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferLastModified, 'OfferLastModified', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferLastModified to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get date() {
    return this.getFieldById(1, DEPRECATED_Date.getDefault());
  }
  setDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteDate() {
    return this.setValueFieldById(1, null, null);
  }
  get side() {
    return this.getFieldById(2, 0);
  }
  setSide(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSide() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1l(OfferLastModified_, protoName, DEV_MODE ? 'OfferLastModified' : '[Unknown]');
let OfferLastModified = OfferLastModified_;
(function (_OfferLastModified) {})(OfferLastModified || (OfferLastModified = {}));
Object.defineProperties(OfferLastModified, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "date": {
        value: 1
      },
      "side": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "date"
      },
      '2': {
        value: "side"
      }
    }))
  }
});
Object.defineProperties(OfferLastModified.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferLastModified);
  Object.freeze(OfferLastModified.prototype);
}
var CashManagement__;
(function (CashManagement__) {
  CashManagement__[CashManagement__["NONE"] = 0] = "NONE";
  CashManagement__[CashManagement__["IMMEDIATE_HARD_LOCKBOX"] = 1] = "IMMEDIATE_HARD_LOCKBOX";
  CashManagement__[CashManagement__["IMMEDIATE_SOFT_LOCKBOX"] = 2] = "IMMEDIATE_SOFT_LOCKBOX";
  CashManagement__[CashManagement__["SPRINGING_HARD_LOCKBOX"] = 3] = "SPRINGING_HARD_LOCKBOX";
  CashManagement__[CashManagement__["SPRINGING_SOFT_LOCKBOX"] = 4] = "SPRINGING_SOFT_LOCKBOX";
})(CashManagement__ || (CashManagement__ = {}));
const CashManagement_ = defineEnumProperties(CashManagement__, [0, 1, 2, 3, 4]);
const CashManagement = extendEnum('CashManagement', [["NONE", 0], ["IMMEDIATE_HARD_LOCKBOX", 1], ["IMMEDIATE_SOFT_LOCKBOX", 2], ["SPRINGING_HARD_LOCKBOX", 3], ["SPRINGING_SOFT_LOCKBOX", 4]])(CashManagement_);
var InsuranceRequirement__;
(function (InsuranceRequirement__) {
  InsuranceRequirement__[InsuranceRequirement__["SPECIAL_PERILS"] = 0] = "SPECIAL_PERILS";
  InsuranceRequirement__[InsuranceRequirement__["FLOOD"] = 1] = "FLOOD";
  InsuranceRequirement__[InsuranceRequirement__["EARTHQUAKE"] = 2] = "EARTHQUAKE";
  InsuranceRequirement__[InsuranceRequirement__["EQUIPMENT_BREAKDOWN"] = 3] = "EQUIPMENT_BREAKDOWN";
  InsuranceRequirement__[InsuranceRequirement__["BUILDERS_RISK"] = 4] = "BUILDERS_RISK";
  InsuranceRequirement__[InsuranceRequirement__["RENT_LOSS"] = 5] = "RENT_LOSS";
  InsuranceRequirement__[InsuranceRequirement__["LIABILITY"] = 6] = "LIABILITY";
  InsuranceRequirement__[InsuranceRequirement__["STATUTORY_EMPLOYEES"] = 7] = "STATUTORY_EMPLOYEES";
  InsuranceRequirement__[InsuranceRequirement__["ENVIRONMENTAL"] = 8] = "ENVIRONMENTAL";
  InsuranceRequirement__[InsuranceRequirement__["BUILDING_ORDINANCE_AND_LAW"] = 9] = "BUILDING_ORDINANCE_AND_LAW";
  InsuranceRequirement__[InsuranceRequirement__["UTILITY_SERVICES_INTERRUPTION"] = 10] = "UTILITY_SERVICES_INTERRUPTION";
  InsuranceRequirement__[InsuranceRequirement__["INFLATION"] = 11] = "INFLATION";
})(InsuranceRequirement__ || (InsuranceRequirement__ = {}));
const InsuranceRequirement_ = defineEnumProperties(InsuranceRequirement__, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
const InsuranceRequirement = extendEnum('InsuranceRequirement', [["SPECIAL_PERILS", 0], ["FLOOD", 1], ["EARTHQUAKE", 2], ["EQUIPMENT_BREAKDOWN", 3], ["BUILDERS_RISK", 4], ["RENT_LOSS", 5], ["LIABILITY", 6], ["STATUTORY_EMPLOYEES", 7], ["ENVIRONMENTAL", 8], ["BUILDING_ORDINANCE_AND_LAW", 9], ["UTILITY_SERVICES_INTERRUPTION", 10], ["INFLATION", 11]])(InsuranceRequirement_);
var OfferReportingFrequencyRequirement__;
(function (OfferReportingFrequencyRequirement__) {
  OfferReportingFrequencyRequirement__[OfferReportingFrequencyRequirement__["NONE"] = 0] = "NONE";
  OfferReportingFrequencyRequirement__[OfferReportingFrequencyRequirement__["MONTHLY"] = 1] = "MONTHLY";
  OfferReportingFrequencyRequirement__[OfferReportingFrequencyRequirement__["QUARTERLY"] = 2] = "QUARTERLY";
  OfferReportingFrequencyRequirement__[OfferReportingFrequencyRequirement__["ANNUAL"] = 3] = "ANNUAL";
})(OfferReportingFrequencyRequirement__ || (OfferReportingFrequencyRequirement__ = {}));
const OfferReportingFrequencyRequirement_ = defineEnumProperties(OfferReportingFrequencyRequirement__, [0, 1, 2, 3]);
const OfferReportingFrequencyRequirement = extendEnum('OfferReportingFrequencyRequirement', [["NONE", 0], ["MONTHLY", 1], ["QUARTERLY", 2], ["ANNUAL", 3]])(OfferReportingFrequencyRequirement_);
class ProjectOffer_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      27: 2,
      1: 7,
      19: 7,
      2: UserOrProjectShare.shape,
      3: OfferLastModified.shape,
      4: 2,
      5: Principal.shape,
      6: InterestInterval.shape,
      7: Term.shape,
      8: ClosingCost.shape,
      10: 2,
      11: RepaymentSchedule.shape,
      12: 0,
      13: Recourse.shape,
      14: 0,
      15: PrepaymentPenalty.shape,
      16: 0,
      17: RichText.shape,
      18: Upload.shape,
      25: OfferStipulation.shape,
      21: CurrencyValue.shape,
      22: 0,
      23: ReservesItem.shape,
      24: 1,
      26: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 27:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectOffer field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectOffer field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 19:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectOffer field project_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UserOrProjectShare);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field owner.
                Expected type UserOrProjectShare.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UserOrProjectShare', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferLastModified);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field last_modified.
                Expected type OfferLastModified.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferLastModified', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectOffer field capital_source_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Principal);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field principal.
                Expected type Principal.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Principal', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(InterestInterval);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field interest_interval.
                Expected type InterestInterval.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'InterestInterval', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 7:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Term);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field loan_term.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 8:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ClosingCost);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field closing_cost.
                Expected type ClosingCost.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ClosingCost', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectOffer field collateral.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepaymentSchedule);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field repayment_schedule.
                Expected type RepaymentSchedule.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepaymentSchedule', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectOffer field insurance_requirement.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return InsuranceRequirement.fromStrictJson_(v, getFieldMap);
            }
            return InsuranceRequirement.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Recourse);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field recourse.
                Expected type Recourse.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Recourse', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectOffer field cash_management.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return CashManagement.fromStrictJson_(v, getFieldMap);
          }
          return CashManagement.from(value);
        };
      case 15:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PrepaymentPenalty);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field prepayment_penalty.
                Expected type PrepaymentPenalty.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PrepaymentPenalty', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 16:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectOffer field reporting_frequency_requirement.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return OfferReportingFrequencyRequirement.fromStrictJson_(v, getFieldMap);
          }
          return OfferReportingFrequencyRequirement.from(value);
        };
      case 17:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field other_requirements.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 18:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Upload);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field legal_term_sheet.
                Expected type Upload.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Upload', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 25:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(OfferStipulation);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field stipulation.
                Expected type OfferStipulation.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'OfferStipulation', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 21:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field property_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 22:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectOffer field interest_rate_calculation_method.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RateCalculationMethod.fromStrictJson_(v, getFieldMap);
          }
          return RateCalculationMethod.from(value);
        };
      case 23:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ReservesItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectOffer field reserves.
                Expected type ReservesItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ReservesItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 24:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectOffer field approved.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 26:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectOffer field automated_quote.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectOffer, 'ProjectOffer', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectOffer to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(27, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(27, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(27, null, null);
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get projectId() {
    return this.getFieldById(19, 0);
  }
  setProjectId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(19, value, null);
  }
  deleteProjectId() {
    return this.setValueFieldById(19, null, null);
  }
  get owner() {
    return this.getFieldById(2, UserOrProjectShare.getDefault());
  }
  setOwner(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteOwner() {
    return this.setValueFieldById(2, null, null);
  }
  get lastModified() {
    return this.getFieldById(3, OfferLastModified.getDefault());
  }
  setLastModified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLastModified() {
    return this.setValueFieldById(3, null, null);
  }
  get capitalSourceName() {
    return this.getFieldById(4, "");
  }
  setCapitalSourceName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteCapitalSourceName() {
    return this.setValueFieldById(4, null, null);
  }
  get principal() {
    return this.getFieldById(5, Principal.getDefault());
  }
  setPrincipal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deletePrincipal() {
    return this.setValueFieldById(5, null, null);
  }
  get interestIntervalList() {
    return this.getRepeatedFieldById(6);
  }
  hasInterestInterval(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addInterestInterval(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachInterestInterval(values) {
    let T = this;
    for (const value of values) {
      T = T.addInterestInterval(value);
    }
    return T;
  }
  setInterestInterval(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removeInterestInterval(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearInterestIntervalList() {
    return this.clearRepeatedFieldById(6);
  }
  setInterestIntervalList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
  get loanTermList() {
    return this.getRepeatedFieldById(7);
  }
  hasLoanTerm(value) {
    return this.repeatedFieldByIdHas(7, value);
  }
  addLoanTerm(value) {
    return this.addToRepeatedFieldById(7, value);
  }
  addEachLoanTerm(values) {
    let T = this;
    for (const value of values) {
      T = T.addLoanTerm(value);
    }
    return T;
  }
  setLoanTerm(index, value) {
    return this.setRepeatedFieldById(7, index, value);
  }
  removeLoanTerm(index) {
    return this.removeFromRepeatedFieldById(7, index);
  }
  clearLoanTermList() {
    return this.clearRepeatedFieldById(7);
  }
  setLoanTermList(values) {
    return this.overwriteRepeatedFieldById(7, values);
  }
  get closingCostList() {
    return this.getRepeatedFieldById(8);
  }
  hasClosingCost(value) {
    return this.repeatedFieldByIdHas(8, value);
  }
  addClosingCost(value) {
    return this.addToRepeatedFieldById(8, value);
  }
  addEachClosingCost(values) {
    let T = this;
    for (const value of values) {
      T = T.addClosingCost(value);
    }
    return T;
  }
  setClosingCost(index, value) {
    return this.setRepeatedFieldById(8, index, value);
  }
  removeClosingCost(index) {
    return this.removeFromRepeatedFieldById(8, index);
  }
  clearClosingCostList() {
    return this.clearRepeatedFieldById(8);
  }
  setClosingCostList(values) {
    return this.overwriteRepeatedFieldById(8, values);
  }
  get collateral() {
    return this.getFieldById(10, "");
  }
  setCollateral(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteCollateral() {
    return this.setValueFieldById(10, null, null);
  }
  get repaymentSchedule() {
    return this.getFieldById(11, RepaymentSchedule.getDefault());
  }
  setRepaymentSchedule(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteRepaymentSchedule() {
    return this.setValueFieldById(11, null, null);
  }
  get insuranceRequirementList() {
    return this.getRepeatedFieldById(12);
  }
  hasInsuranceRequirement(value) {
    return this.repeatedFieldByIdHas(12, value);
  }
  addInsuranceRequirement(value) {
    return this.addToRepeatedFieldById(12, value);
  }
  addEachInsuranceRequirement(values) {
    let T = this;
    for (const value of values) {
      T = T.addInsuranceRequirement(value);
    }
    return T;
  }
  setInsuranceRequirement(index, value) {
    return this.setRepeatedFieldById(12, index, value);
  }
  removeInsuranceRequirement(index) {
    return this.removeFromRepeatedFieldById(12, index);
  }
  clearInsuranceRequirementList() {
    return this.clearRepeatedFieldById(12);
  }
  setInsuranceRequirementList(values) {
    return this.overwriteRepeatedFieldById(12, values);
  }
  get recourse() {
    return this.getFieldById(13, Recourse.getDefault());
  }
  setRecourse(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteRecourse() {
    return this.setValueFieldById(13, null, null);
  }
  get cashManagement() {
    return this.getFieldById(14, 0);
  }
  setCashManagement(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteCashManagement() {
    return this.setValueFieldById(14, null, null);
  }
  get prepaymentPenalty() {
    return this.getFieldById(15, PrepaymentPenalty.getDefault());
  }
  setPrepaymentPenalty(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deletePrepaymentPenalty() {
    return this.setValueFieldById(15, null, null);
  }
  get reportingFrequencyRequirement() {
    return this.getFieldById(16, 0);
  }
  setReportingFrequencyRequirement(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deleteReportingFrequencyRequirement() {
    return this.setValueFieldById(16, null, null);
  }
  get otherRequirements() {
    return this.getFieldById(17, RichText.getDefault());
  }
  setOtherRequirements(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteOtherRequirements() {
    return this.setValueFieldById(17, null, null);
  }
  get legalTermSheet() {
    return this.getFieldById(18, Upload.getDefault());
  }
  setLegalTermSheet(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(18, value, null);
  }
  deleteLegalTermSheet() {
    return this.setValueFieldById(18, null, null);
  }
  get stipulationList() {
    return this.getRepeatedFieldById(25);
  }
  hasStipulation(value) {
    return this.repeatedFieldByIdHas(25, value);
  }
  addStipulation(value) {
    return this.addToRepeatedFieldById(25, value);
  }
  addEachStipulation(values) {
    let T = this;
    for (const value of values) {
      T = T.addStipulation(value);
    }
    return T;
  }
  setStipulation(index, value) {
    return this.setRepeatedFieldById(25, index, value);
  }
  removeStipulation(index) {
    return this.removeFromRepeatedFieldById(25, index);
  }
  clearStipulationList() {
    return this.clearRepeatedFieldById(25);
  }
  setStipulationList(values) {
    return this.overwriteRepeatedFieldById(25, values);
  }
  get propertyValue() {
    return this.getFieldById(21, CurrencyValue.getDefault());
  }
  setPropertyValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, null);
  }
  deletePropertyValue() {
    return this.setValueFieldById(21, null, null);
  }
  get interestRateCalculationMethod() {
    return this.getFieldById(22, 0);
  }
  setInterestRateCalculationMethod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteInterestRateCalculationMethod() {
    return this.setValueFieldById(22, null, null);
  }
  get reservesList() {
    return this.getRepeatedFieldById(23);
  }
  hasReserves(value) {
    return this.repeatedFieldByIdHas(23, value);
  }
  addReserves(value) {
    return this.addToRepeatedFieldById(23, value);
  }
  addEachReserves(values) {
    let T = this;
    for (const value of values) {
      T = T.addReserves(value);
    }
    return T;
  }
  setReserves(index, value) {
    return this.setRepeatedFieldById(23, index, value);
  }
  removeReserves(index) {
    return this.removeFromRepeatedFieldById(23, index);
  }
  clearReservesList() {
    return this.clearRepeatedFieldById(23);
  }
  setReservesList(values) {
    return this.overwriteRepeatedFieldById(23, values);
  }
  get approved() {
    return this.getFieldById(24, false);
  }
  setApproved(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(24, value, null);
  }
  deleteApproved() {
    return this.setValueFieldById(24, null, null);
  }
  get automatedQuote() {
    return this.getFieldById(26, "");
  }
  setAutomatedQuote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(26, value, null);
  }
  deleteAutomatedQuote() {
    return this.setValueFieldById(26, null, null);
  }
}
_defineProperty$1l(ProjectOffer_, protoName, DEV_MODE ? 'ProjectOffer' : '[Unknown]');
let ProjectOffer = ProjectOffer_;
(function (_ProjectOffer) {})(ProjectOffer || (ProjectOffer = {}));
Object.defineProperties(ProjectOffer, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 27
      },
      "id": {
        value: 1
      },
      "project_id": {
        value: 19
      },
      "projectId": {
        value: 19
      },
      "owner": {
        value: 2
      },
      "last_modified": {
        value: 3
      },
      "lastModified": {
        value: 3
      },
      "capital_source_name": {
        value: 4
      },
      "capitalSourceName": {
        value: 4
      },
      "principal": {
        value: 5
      },
      "interest_interval": {
        value: 6
      },
      "interestInterval": {
        value: 6
      },
      "loan_term": {
        value: 7
      },
      "loanTerm": {
        value: 7
      },
      "closing_cost": {
        value: 8
      },
      "closingCost": {
        value: 8
      },
      "collateral": {
        value: 10
      },
      "repayment_schedule": {
        value: 11
      },
      "repaymentSchedule": {
        value: 11
      },
      "insurance_requirement": {
        value: 12
      },
      "insuranceRequirement": {
        value: 12
      },
      "recourse": {
        value: 13
      },
      "cash_management": {
        value: 14
      },
      "cashManagement": {
        value: 14
      },
      "prepayment_penalty": {
        value: 15
      },
      "prepaymentPenalty": {
        value: 15
      },
      "reporting_frequency_requirement": {
        value: 16
      },
      "reportingFrequencyRequirement": {
        value: 16
      },
      "other_requirements": {
        value: 17
      },
      "otherRequirements": {
        value: 17
      },
      "legal_term_sheet": {
        value: 18
      },
      "legalTermSheet": {
        value: 18
      },
      "stipulation": {
        value: 25
      },
      "property_value": {
        value: 21
      },
      "propertyValue": {
        value: 21
      },
      "interest_rate_calculation_method": {
        value: 22
      },
      "interestRateCalculationMethod": {
        value: 22
      },
      "reserves": {
        value: 23
      },
      "approved": {
        value: 24
      },
      "automated_quote": {
        value: 26
      },
      "automatedQuote": {
        value: 26
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '27': {
        value: "sym"
      },
      '1': {
        value: "id"
      },
      '19': {
        value: "project_id"
      },
      '2': {
        value: "owner"
      },
      '3': {
        value: "last_modified"
      },
      '4': {
        value: "capital_source_name"
      },
      '5': {
        value: "principal"
      },
      '6': {
        value: "interest_interval"
      },
      '7': {
        value: "loan_term"
      },
      '8': {
        value: "closing_cost"
      },
      '10': {
        value: "collateral"
      },
      '11': {
        value: "repayment_schedule"
      },
      '12': {
        value: "insurance_requirement"
      },
      '13': {
        value: "recourse"
      },
      '14': {
        value: "cash_management"
      },
      '15': {
        value: "prepayment_penalty"
      },
      '16': {
        value: "reporting_frequency_requirement"
      },
      '17': {
        value: "other_requirements"
      },
      '18': {
        value: "legal_term_sheet"
      },
      '25': {
        value: "stipulation"
      },
      '21': {
        value: "property_value"
      },
      '22': {
        value: "interest_rate_calculation_method"
      },
      '23': {
        value: "reserves"
      },
      '24': {
        value: "approved"
      },
      '26': {
        value: "automated_quote"
      }
    }))
  }
});
Object.defineProperties(ProjectOffer.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: UserOrProjectShare.prototype,
      3: OfferLastModified.prototype,
      5: Principal.prototype,
      6: InterestInterval.prototype,
      7: Term.prototype,
      8: ClosingCost.prototype,
      11: RepaymentSchedule.prototype,
      13: Recourse.prototype,
      15: PrepaymentPenalty.prototype,
      17: RichText.prototype,
      18: Upload.prototype,
      25: OfferStipulation.prototype,
      21: CurrencyValue.prototype,
      23: ReservesItem.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectOffer);
  Object.freeze(ProjectOffer.prototype);
}

function _defineProperty$1k(obj, key, value) { key = _toPropertyKey$1k(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1k(arg) { var key = _toPrimitive$1k(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1k(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _OfferOwner_Type_;
(function (_OfferOwner_Type_) {
  _OfferOwner_Type_[_OfferOwner_Type_["UNSET"] = 0] = "UNSET";
  _OfferOwner_Type_[_OfferOwner_Type_["USER"] = 1] = "USER";
  _OfferOwner_Type_[_OfferOwner_Type_["PROJECT_SHARE"] = 2] = "PROJECT_SHARE";
})(_OfferOwner_Type_ || (_OfferOwner_Type_ = {}));
const _OfferOwner_Type = defineEnumProperties(_OfferOwner_Type_, [1, 2]);
class OfferOwner_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OfferOwner field user.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OfferOwner field project_share.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferOwner, 'OfferOwner', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferOwner to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _OfferOwner_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get user() {
    return this.getFieldById(1, "");
  }
  setUser(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteUser() {
    return this.setValueFieldById(1, null, "type");
  }
  get projectShare() {
    return this.getFieldById(2, "");
  }
  setProjectShare(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deleteProjectShare() {
    return this.setValueFieldById(2, null, "type");
  }
}
_defineProperty$1k(OfferOwner_, protoName, DEV_MODE ? 'OfferOwner' : '[Unknown]');
let OfferOwner = OfferOwner_;
(function (_OfferOwner) {})(OfferOwner || (OfferOwner = {}));
Object.defineProperties(OfferOwner, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "user": {
        value: 1
      },
      "project_share": {
        value: 2
      },
      "projectShare": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "user"
      },
      '2': {
        value: "project_share"
      }
    }))
  }
});
Object.defineProperties(OfferOwner.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2]
    }
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferOwner);
  Object.freeze(OfferOwner.prototype);
}
class Offer_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: OfferOwner.shape,
      3: CurrencyValue.shape,
      4: Term.shape,
      5: InterestInterval.shape,
      6: ClosingCost.shape,
      7: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Offer field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferOwner);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Offer field owner.
                Expected type OfferOwner.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferOwner', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Offer field principal.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Term);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Offer field term.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 5:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(InterestInterval);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Offer field interest.
                Expected type InterestInterval.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'InterestInterval', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ClosingCost);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Offer field closing_cost.
                Expected type ClosingCost.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ClosingCost', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Offer field collateral.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Offer, 'Offer', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Offer to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get owner() {
    return this.getFieldById(2, OfferOwner.getDefault());
  }
  setOwner(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteOwner() {
    return this.setValueFieldById(2, null, null);
  }
  get principal() {
    return this.getFieldById(3, CurrencyValue.getDefault());
  }
  setPrincipal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deletePrincipal() {
    return this.setValueFieldById(3, null, null);
  }
  get termList() {
    return this.getRepeatedFieldById(4);
  }
  hasTerm(value) {
    return this.repeatedFieldByIdHas(4, value);
  }
  addTerm(value) {
    return this.addToRepeatedFieldById(4, value);
  }
  addEachTerm(values) {
    let T = this;
    for (const value of values) {
      T = T.addTerm(value);
    }
    return T;
  }
  setTerm(index, value) {
    return this.setRepeatedFieldById(4, index, value);
  }
  removeTerm(index) {
    return this.removeFromRepeatedFieldById(4, index);
  }
  clearTermList() {
    return this.clearRepeatedFieldById(4);
  }
  setTermList(values) {
    return this.overwriteRepeatedFieldById(4, values);
  }
  get interestList() {
    return this.getRepeatedFieldById(5);
  }
  hasInterest(value) {
    return this.repeatedFieldByIdHas(5, value);
  }
  addInterest(value) {
    return this.addToRepeatedFieldById(5, value);
  }
  addEachInterest(values) {
    let T = this;
    for (const value of values) {
      T = T.addInterest(value);
    }
    return T;
  }
  setInterest(index, value) {
    return this.setRepeatedFieldById(5, index, value);
  }
  removeInterest(index) {
    return this.removeFromRepeatedFieldById(5, index);
  }
  clearInterestList() {
    return this.clearRepeatedFieldById(5);
  }
  setInterestList(values) {
    return this.overwriteRepeatedFieldById(5, values);
  }
  get closingCostList() {
    return this.getRepeatedFieldById(6);
  }
  hasClosingCost(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addClosingCost(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachClosingCost(values) {
    let T = this;
    for (const value of values) {
      T = T.addClosingCost(value);
    }
    return T;
  }
  setClosingCost(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removeClosingCost(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearClosingCostList() {
    return this.clearRepeatedFieldById(6);
  }
  setClosingCostList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
  get collateral() {
    return this.getFieldById(7, "");
  }
  setCollateral(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteCollateral() {
    return this.setValueFieldById(7, null, null);
  }
}
_defineProperty$1k(Offer_, protoName, DEV_MODE ? 'Offer' : '[Unknown]');
let Offer = Offer_;
(function (_Offer) {})(Offer || (Offer = {}));
Object.defineProperties(Offer, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "owner": {
        value: 2
      },
      "principal": {
        value: 3
      },
      "term": {
        value: 4
      },
      "interest": {
        value: 5
      },
      "closing_cost": {
        value: 6
      },
      "closingCost": {
        value: 6
      },
      "collateral": {
        value: 7
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "owner"
      },
      '3': {
        value: "principal"
      },
      '4': {
        value: "term"
      },
      '5': {
        value: "interest"
      },
      '6': {
        value: "closing_cost"
      },
      '7': {
        value: "collateral"
      }
    }))
  }
});
Object.defineProperties(Offer.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: OfferOwner.prototype,
      3: CurrencyValue.prototype,
      4: Term.prototype,
      5: InterestInterval.prototype,
      6: ClosingCost.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Offer);
  Object.freeze(Offer.prototype);
}

function getOfferUrlString(appRoot, offer) {
  return `${appRoot}/project:${offer.projectId}/offer-details:${offer.id}`;
}
function formatCashManagement(value) {
  switch (value) {
    case CashManagement.NONE:
      return 'No Lockbox';
      break;
    case CashManagement.IMMEDIATE_HARD_LOCKBOX:
      return 'Immediate Hard Lockbox';
      break;
    case CashManagement.IMMEDIATE_SOFT_LOCKBOX:
      return 'Immediate Soft Lockbox';
      break;
    case CashManagement.SPRINGING_HARD_LOCKBOX:
      return 'Springing Hard Lockbox';
      break;
    case CashManagement.SPRINGING_SOFT_LOCKBOX:
      return 'Springing Soft Lockbox';
      break;
    default:
      runtime.checkAsserts() ? runtime.ensure(false, function () {
        return `Unknown cash management (${value}).`;
      }) : undefined;
      break;
  }
}
function getProjectOfferLtv(offer) {
  for (const stipulation of offer.stipulationList) {
    if (stipulation.type == 'LTV') {
      return stipulation.value;
    }
  }
  return null;
}
function getProjectOfferDscr(offer) {
  for (const stipulation of offer.stipulationList) {
    if (stipulation.type == 'DSCR') {
      return stipulation.value;
    }
  }
  return null;
}

function _defineProperty$1j(obj, key, value) { key = _toPropertyKey$1j(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1j(arg) { var key = _toPrimitive$1j(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1j(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OfferDetails_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 2,
      4: 0,
      5: Recourse.shape,
      6: 0,
      7: PrepaymentPenalty.shape,
      8: 0,
      9: RichText.shape,
      10: Upload.shape,
      11: 0,
      12: ReservesItem.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message OfferDetails field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OfferDetails field collateral.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferDetails field insurance_requirement.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return InsuranceRequirement.fromStrictJson_(v, getFieldMap);
            }
            return InsuranceRequirement.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Recourse);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferDetails field recourse.
                Expected type Recourse.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Recourse', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferDetails field cash_management.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return CashManagement.fromStrictJson_(v, getFieldMap);
          }
          return CashManagement.from(value);
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PrepaymentPenalty);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferDetails field prepayment_penalty.
                Expected type PrepaymentPenalty.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PrepaymentPenalty', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferDetails field reporting_frequency_requirement.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return OfferReportingFrequencyRequirement.fromStrictJson_(v, getFieldMap);
          }
          return OfferReportingFrequencyRequirement.from(value);
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferDetails field other_requirements.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Upload);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferDetails field legal_term_sheet.
                Expected type Upload.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Upload', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message OfferDetails field interest_rate_calculation_method.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return RateCalculationMethod.fromStrictJson_(v, getFieldMap);
          }
          return RateCalculationMethod.from(value);
        };
      case 12:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ReservesItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message OfferDetails field reserves.
                Expected type ReservesItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ReservesItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferDetails, 'OfferDetails', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferDetails to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get collateral() {
    return this.getFieldById(2, "");
  }
  setCollateral(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteCollateral() {
    return this.setValueFieldById(2, null, null);
  }
  get insuranceRequirementList() {
    return this.getRepeatedFieldById(4);
  }
  hasInsuranceRequirement(value) {
    return this.repeatedFieldByIdHas(4, value);
  }
  addInsuranceRequirement(value) {
    return this.addToRepeatedFieldById(4, value);
  }
  addEachInsuranceRequirement(values) {
    let T = this;
    for (const value of values) {
      T = T.addInsuranceRequirement(value);
    }
    return T;
  }
  setInsuranceRequirement(index, value) {
    return this.setRepeatedFieldById(4, index, value);
  }
  removeInsuranceRequirement(index) {
    return this.removeFromRepeatedFieldById(4, index);
  }
  clearInsuranceRequirementList() {
    return this.clearRepeatedFieldById(4);
  }
  setInsuranceRequirementList(values) {
    return this.overwriteRepeatedFieldById(4, values);
  }
  get recourse() {
    return this.getFieldById(5, Recourse.getDefault());
  }
  setRecourse(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteRecourse() {
    return this.setValueFieldById(5, null, null);
  }
  get cashManagement() {
    return this.getFieldById(6, 0);
  }
  setCashManagement(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteCashManagement() {
    return this.setValueFieldById(6, null, null);
  }
  get prepaymentPenalty() {
    return this.getFieldById(7, PrepaymentPenalty.getDefault());
  }
  setPrepaymentPenalty(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deletePrepaymentPenalty() {
    return this.setValueFieldById(7, null, null);
  }
  get reportingFrequencyRequirement() {
    return this.getFieldById(8, 0);
  }
  setReportingFrequencyRequirement(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteReportingFrequencyRequirement() {
    return this.setValueFieldById(8, null, null);
  }
  get otherRequirements() {
    return this.getFieldById(9, RichText.getDefault());
  }
  setOtherRequirements(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteOtherRequirements() {
    return this.setValueFieldById(9, null, null);
  }
  get legalTermSheet() {
    return this.getFieldById(10, Upload.getDefault());
  }
  setLegalTermSheet(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteLegalTermSheet() {
    return this.setValueFieldById(10, null, null);
  }
  get interestRateCalculationMethod() {
    return this.getFieldById(11, 0);
  }
  setInterestRateCalculationMethod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteInterestRateCalculationMethod() {
    return this.setValueFieldById(11, null, null);
  }
  get reservesList() {
    return this.getRepeatedFieldById(12);
  }
  hasReserves(value) {
    return this.repeatedFieldByIdHas(12, value);
  }
  addReserves(value) {
    return this.addToRepeatedFieldById(12, value);
  }
  addEachReserves(values) {
    let T = this;
    for (const value of values) {
      T = T.addReserves(value);
    }
    return T;
  }
  setReserves(index, value) {
    return this.setRepeatedFieldById(12, index, value);
  }
  removeReserves(index) {
    return this.removeFromRepeatedFieldById(12, index);
  }
  clearReservesList() {
    return this.clearRepeatedFieldById(12);
  }
  setReservesList(values) {
    return this.overwriteRepeatedFieldById(12, values);
  }
}
_defineProperty$1j(OfferDetails_, protoName, DEV_MODE ? 'OfferDetails' : '[Unknown]');
let OfferDetails = OfferDetails_;
(function (_OfferDetails) {})(OfferDetails || (OfferDetails = {}));
Object.defineProperties(OfferDetails, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "collateral": {
        value: 2
      },
      "insurance_requirement": {
        value: 4
      },
      "insuranceRequirement": {
        value: 4
      },
      "recourse": {
        value: 5
      },
      "cash_management": {
        value: 6
      },
      "cashManagement": {
        value: 6
      },
      "prepayment_penalty": {
        value: 7
      },
      "prepaymentPenalty": {
        value: 7
      },
      "reporting_frequency_requirement": {
        value: 8
      },
      "reportingFrequencyRequirement": {
        value: 8
      },
      "other_requirements": {
        value: 9
      },
      "otherRequirements": {
        value: 9
      },
      "legal_term_sheet": {
        value: 10
      },
      "legalTermSheet": {
        value: 10
      },
      "interest_rate_calculation_method": {
        value: 11
      },
      "interestRateCalculationMethod": {
        value: 11
      },
      "reserves": {
        value: 12
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "collateral"
      },
      '4': {
        value: "insurance_requirement"
      },
      '5': {
        value: "recourse"
      },
      '6': {
        value: "cash_management"
      },
      '7': {
        value: "prepayment_penalty"
      },
      '8': {
        value: "reporting_frequency_requirement"
      },
      '9': {
        value: "other_requirements"
      },
      '10': {
        value: "legal_term_sheet"
      },
      '11': {
        value: "interest_rate_calculation_method"
      },
      '12': {
        value: "reserves"
      }
    }))
  }
});
Object.defineProperties(OfferDetails.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      5: Recourse.prototype,
      7: PrepaymentPenalty.prototype,
      9: RichText.prototype,
      10: Upload.prototype,
      12: ReservesItem.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferDetails);
  Object.freeze(OfferDetails.prototype);
}

function _defineProperty$1i(obj, key, value) { key = _toPropertyKey$1i(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1i(arg) { var key = _toPrimitive$1i(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1i(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OfferSummary_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: UserOrProjectShare.shape,
      3: OfferLastModified.shape,
      4: 2,
      5: Principal.shape,
      6: InterestInterval.shape,
      7: Term.shape,
      8: ClosingCost.shape,
      11: CurrencyValue.shape,
      12: RepaymentSchedule.shape,
      14: OfferStipulation.shape,
      13: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message OfferSummary field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UserOrProjectShare);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field owner.
                Expected type UserOrProjectShare.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UserOrProjectShare', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferLastModified);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field last_modified.
                Expected type OfferLastModified.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferLastModified', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OfferSummary field capital_source_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Principal);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field principal.
                Expected type Principal.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Principal', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(InterestInterval);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field interest_interval.
                Expected type InterestInterval.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'InterestInterval', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 7:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Term);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field loan_term.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 8:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ClosingCost);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field closing_cost.
                Expected type ClosingCost.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ClosingCost', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field property_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepaymentSchedule);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field repayment_schedule.
                Expected type RepaymentSchedule.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepaymentSchedule', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(OfferStipulation);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message OfferSummary field stipulation.
                Expected type OfferStipulation.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'OfferStipulation', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 13:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message OfferSummary field approved.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferSummary, 'OfferSummary', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferSummary to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get owner() {
    return this.getFieldById(2, UserOrProjectShare.getDefault());
  }
  setOwner(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteOwner() {
    return this.setValueFieldById(2, null, null);
  }
  get lastModified() {
    return this.getFieldById(3, OfferLastModified.getDefault());
  }
  setLastModified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLastModified() {
    return this.setValueFieldById(3, null, null);
  }
  get capitalSourceName() {
    return this.getFieldById(4, "");
  }
  setCapitalSourceName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteCapitalSourceName() {
    return this.setValueFieldById(4, null, null);
  }
  get principal() {
    return this.getFieldById(5, Principal.getDefault());
  }
  setPrincipal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deletePrincipal() {
    return this.setValueFieldById(5, null, null);
  }
  get interestIntervalList() {
    return this.getRepeatedFieldById(6);
  }
  hasInterestInterval(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addInterestInterval(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachInterestInterval(values) {
    let T = this;
    for (const value of values) {
      T = T.addInterestInterval(value);
    }
    return T;
  }
  setInterestInterval(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removeInterestInterval(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearInterestIntervalList() {
    return this.clearRepeatedFieldById(6);
  }
  setInterestIntervalList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
  get loanTermList() {
    return this.getRepeatedFieldById(7);
  }
  hasLoanTerm(value) {
    return this.repeatedFieldByIdHas(7, value);
  }
  addLoanTerm(value) {
    return this.addToRepeatedFieldById(7, value);
  }
  addEachLoanTerm(values) {
    let T = this;
    for (const value of values) {
      T = T.addLoanTerm(value);
    }
    return T;
  }
  setLoanTerm(index, value) {
    return this.setRepeatedFieldById(7, index, value);
  }
  removeLoanTerm(index) {
    return this.removeFromRepeatedFieldById(7, index);
  }
  clearLoanTermList() {
    return this.clearRepeatedFieldById(7);
  }
  setLoanTermList(values) {
    return this.overwriteRepeatedFieldById(7, values);
  }
  get closingCostList() {
    return this.getRepeatedFieldById(8);
  }
  hasClosingCost(value) {
    return this.repeatedFieldByIdHas(8, value);
  }
  addClosingCost(value) {
    return this.addToRepeatedFieldById(8, value);
  }
  addEachClosingCost(values) {
    let T = this;
    for (const value of values) {
      T = T.addClosingCost(value);
    }
    return T;
  }
  setClosingCost(index, value) {
    return this.setRepeatedFieldById(8, index, value);
  }
  removeClosingCost(index) {
    return this.removeFromRepeatedFieldById(8, index);
  }
  clearClosingCostList() {
    return this.clearRepeatedFieldById(8);
  }
  setClosingCostList(values) {
    return this.overwriteRepeatedFieldById(8, values);
  }
  get propertyValue() {
    return this.getFieldById(11, CurrencyValue.getDefault());
  }
  setPropertyValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deletePropertyValue() {
    return this.setValueFieldById(11, null, null);
  }
  get repaymentSchedule() {
    return this.getFieldById(12, RepaymentSchedule.getDefault());
  }
  setRepaymentSchedule(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteRepaymentSchedule() {
    return this.setValueFieldById(12, null, null);
  }
  get stipulationList() {
    return this.getRepeatedFieldById(14);
  }
  hasStipulation(value) {
    return this.repeatedFieldByIdHas(14, value);
  }
  addStipulation(value) {
    return this.addToRepeatedFieldById(14, value);
  }
  addEachStipulation(values) {
    let T = this;
    for (const value of values) {
      T = T.addStipulation(value);
    }
    return T;
  }
  setStipulation(index, value) {
    return this.setRepeatedFieldById(14, index, value);
  }
  removeStipulation(index) {
    return this.removeFromRepeatedFieldById(14, index);
  }
  clearStipulationList() {
    return this.clearRepeatedFieldById(14);
  }
  setStipulationList(values) {
    return this.overwriteRepeatedFieldById(14, values);
  }
  get approved() {
    return this.getFieldById(13, false);
  }
  setApproved(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteApproved() {
    return this.setValueFieldById(13, null, null);
  }
}
_defineProperty$1i(OfferSummary_, protoName, DEV_MODE ? 'OfferSummary' : '[Unknown]');
let OfferSummary = OfferSummary_;
(function (_OfferSummary) {})(OfferSummary || (OfferSummary = {}));
Object.defineProperties(OfferSummary, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "owner": {
        value: 2
      },
      "last_modified": {
        value: 3
      },
      "lastModified": {
        value: 3
      },
      "capital_source_name": {
        value: 4
      },
      "capitalSourceName": {
        value: 4
      },
      "principal": {
        value: 5
      },
      "interest_interval": {
        value: 6
      },
      "interestInterval": {
        value: 6
      },
      "loan_term": {
        value: 7
      },
      "loanTerm": {
        value: 7
      },
      "closing_cost": {
        value: 8
      },
      "closingCost": {
        value: 8
      },
      "property_value": {
        value: 11
      },
      "propertyValue": {
        value: 11
      },
      "repayment_schedule": {
        value: 12
      },
      "repaymentSchedule": {
        value: 12
      },
      "stipulation": {
        value: 14
      },
      "approved": {
        value: 13
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "owner"
      },
      '3': {
        value: "last_modified"
      },
      '4': {
        value: "capital_source_name"
      },
      '5': {
        value: "principal"
      },
      '6': {
        value: "interest_interval"
      },
      '7': {
        value: "loan_term"
      },
      '8': {
        value: "closing_cost"
      },
      '11': {
        value: "property_value"
      },
      '12': {
        value: "repayment_schedule"
      },
      '14': {
        value: "stipulation"
      },
      '13': {
        value: "approved"
      }
    }))
  }
});
Object.defineProperties(OfferSummary.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: UserOrProjectShare.prototype,
      3: OfferLastModified.prototype,
      5: Principal.prototype,
      6: InterestInterval.prototype,
      7: Term.prototype,
      8: ClosingCost.prototype,
      11: CurrencyValue.prototype,
      12: RepaymentSchedule.prototype,
      14: OfferStipulation.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferSummary);
  Object.freeze(OfferSummary.prototype);
}

function dealSideFromDbToProto(dealSide) {
  switch (dealSide) {
    case 'capital_source':
      return DealSide.CAPITAL_SOURCE;
    case 'sponsor':
      return DealSide.SPONSOR;
  }
  assert.fail(emsg`Unrecognized deal side: ${dealSide}`);
  return DealSide.SPONSOR;
}

function _defineProperty$1h(obj, key, value) { key = _toPropertyKey$1h(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1h(arg) { var key = _toPrimitive$1h(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1h(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class IdAndEmail_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message IdAndEmail field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message IdAndEmail field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(IdAndEmail, 'IdAndEmail', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message IdAndEmail to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get email() {
    return this.getFieldById(2, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1h(IdAndEmail_, protoName, DEV_MODE ? 'IdAndEmail' : '[Unknown]');
let IdAndEmail = IdAndEmail_;
(function (_IdAndEmail) {})(IdAndEmail || (IdAndEmail = {}));
Object.defineProperties(IdAndEmail, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "email": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "email"
      }
    }))
  }
});
Object.defineProperties(IdAndEmail.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(IdAndEmail);
  Object.freeze(IdAndEmail.prototype);
}
var _ComStreamMessageSender_Type_;
(function (_ComStreamMessageSender_Type_) {
  _ComStreamMessageSender_Type_[_ComStreamMessageSender_Type_["UNSET"] = 0] = "UNSET";
  _ComStreamMessageSender_Type_[_ComStreamMessageSender_Type_["USER_ID"] = 1] = "USER_ID";
  _ComStreamMessageSender_Type_[_ComStreamMessageSender_Type_["PROJECT_SHARE_ID_AND_EMAIL"] = 2] = "PROJECT_SHARE_ID_AND_EMAIL";
})(_ComStreamMessageSender_Type_ || (_ComStreamMessageSender_Type_ = {}));
const _ComStreamMessageSender_Type = defineEnumProperties(_ComStreamMessageSender_Type_, [1, 2]);
class ComStreamMessageSender_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 5,
      2: IdAndEmail.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ComStreamMessageSender field user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(IdAndEmail);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamMessageSender field project_share_id_and_email.
                Expected type IdAndEmail.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'IdAndEmail', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ComStreamMessageSender, 'ComStreamMessageSender', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ComStreamMessageSender to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _ComStreamMessageSender_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get userId() {
    return this.getFieldById(1, 0);
  }
  setUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteUserId() {
    return this.setValueFieldById(1, null, "type");
  }
  get projectShareIdAndEmail() {
    return this.getFieldById(2, IdAndEmail.getDefault());
  }
  setProjectShareIdAndEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deleteProjectShareIdAndEmail() {
    return this.setValueFieldById(2, null, "type");
  }
}
_defineProperty$1h(ComStreamMessageSender_, protoName, DEV_MODE ? 'ComStreamMessageSender' : '[Unknown]');
let ComStreamMessageSender = ComStreamMessageSender_;
(function (_ComStreamMessageSender) {})(ComStreamMessageSender || (ComStreamMessageSender = {}));
Object.defineProperties(ComStreamMessageSender, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "user_id": {
        value: 1
      },
      "userId": {
        value: 1
      },
      "project_share_id_and_email": {
        value: 2
      },
      "projectShareIdAndEmail": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "user_id"
      },
      '2': {
        value: "project_share_id_and_email"
      }
    }))
  }
});
Object.defineProperties(ComStreamMessageSender.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2]
    }
  },
  prototypes_: {
    value: {
      2: IdAndEmail.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ComStreamMessageSender);
  Object.freeze(ComStreamMessageSender.prototype);
}

function _defineProperty$1g(obj, key, value) { key = _toPropertyKey$1g(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1g(arg) { var key = _toPrimitive$1g(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1g(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _ComStreamMessage_Content_;
(function (_ComStreamMessage_Content_) {
  _ComStreamMessage_Content_[_ComStreamMessage_Content_["UNSET"] = 0] = "UNSET";
  _ComStreamMessage_Content_[_ComStreamMessage_Content_["BODY"] = 3] = "BODY";
  _ComStreamMessage_Content_[_ComStreamMessage_Content_["ATTACHMENT"] = 6] = "ATTACHMENT";
})(_ComStreamMessage_Content_ || (_ComStreamMessage_Content_ = {}));
const _ComStreamMessage_Content = defineEnumProperties(_ComStreamMessage_Content_, [3, 6]);
class ComStreamMessage_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: ComStreamMessageSender.shape,
      3: RichText.shape,
      6: Attachment.shape,
      4: DEPRECATED_Date.shape,
      5: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ComStreamMessage field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ComStreamMessageSender);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamMessage field sender.
                Expected type ComStreamMessageSender.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ComStreamMessageSender', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamMessage field body.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Attachment);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamMessage field attachment.
                Expected type Attachment.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Attachment', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamMessage field creation_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamMessage field last_modified.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ComStreamMessage, 'ComStreamMessage', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ComStreamMessage to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ContentCase() {
    return _ComStreamMessage_Content;
  }
  get contentCase() {
    return this.getCaseFromIds([3, 6]);
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sender() {
    return this.getFieldById(2, ComStreamMessageSender.getDefault());
  }
  setSender(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSender() {
    return this.setValueFieldById(2, null, null);
  }
  get body() {
    return this.getFieldById(3, RichText.getDefault());
  }
  setBody(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "content");
  }
  deleteBody() {
    return this.setValueFieldById(3, null, "content");
  }
  get attachment() {
    return this.getFieldById(6, Attachment.getDefault());
  }
  setAttachment(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, "content");
  }
  deleteAttachment() {
    return this.setValueFieldById(6, null, "content");
  }
  get creationTime() {
    return this.getFieldById(4, DEPRECATED_Date.getDefault());
  }
  setCreationTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteCreationTime() {
    return this.setValueFieldById(4, null, null);
  }
  get lastModified() {
    return this.getFieldById(5, DEPRECATED_Date.getDefault());
  }
  setLastModified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteLastModified() {
    return this.setValueFieldById(5, null, null);
  }
}
_defineProperty$1g(ComStreamMessage_, protoName, DEV_MODE ? 'ComStreamMessage' : '[Unknown]');
let ComStreamMessage = ComStreamMessage_;
(function (_ComStreamMessage) {})(ComStreamMessage || (ComStreamMessage = {}));
Object.defineProperties(ComStreamMessage, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sender": {
        value: 2
      },
      "body": {
        value: 3
      },
      "attachment": {
        value: 6
      },
      "creation_time": {
        value: 4
      },
      "creationTime": {
        value: 4
      },
      "last_modified": {
        value: 5
      },
      "lastModified": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "sender"
      },
      '3': {
        value: "body"
      },
      '6': {
        value: "attachment"
      },
      '4': {
        value: "creation_time"
      },
      '5': {
        value: "last_modified"
      }
    }))
  }
});
Object.defineProperties(ComStreamMessage.prototype, {
  oneofs_: {
    value: {
      "content": [3, 6]
    }
  },
  prototypes_: {
    value: {
      2: ComStreamMessageSender.prototype,
      3: RichText.prototype,
      6: Attachment.prototype,
      4: DEPRECATED_Date.prototype,
      5: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ComStreamMessage);
  Object.freeze(ComStreamMessage.prototype);
}

function _defineProperty$1f(obj, key, value) { key = _toPropertyKey$1f(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1f(arg) { var key = _toPrimitive$1f(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1f(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _ComStreamItem_Type_;
(function (_ComStreamItem_Type_) {
  _ComStreamItem_Type_[_ComStreamItem_Type_["UNSET"] = 0] = "UNSET";
  _ComStreamItem_Type_[_ComStreamItem_Type_["MESSAGE"] = 1] = "MESSAGE";
})(_ComStreamItem_Type_ || (_ComStreamItem_Type_ = {}));
const _ComStreamItem_Type = defineEnumProperties(_ComStreamItem_Type_, [1]);
class ComStreamItem_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: ComStreamMessage.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ComStreamMessage);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ComStreamItem field message.
                Expected type ComStreamMessage.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ComStreamMessage', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ComStreamItem, 'ComStreamItem', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ComStreamItem to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _ComStreamItem_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1]);
  }
  get message() {
    return this.getFieldById(1, ComStreamMessage.getDefault());
  }
  setMessage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteMessage() {
    return this.setValueFieldById(1, null, "type");
  }
}
_defineProperty$1f(ComStreamItem_, protoName, DEV_MODE ? 'ComStreamItem' : '[Unknown]');
let ComStreamItem = ComStreamItem_;
(function (_ComStreamItem) {})(ComStreamItem || (ComStreamItem = {}));
Object.defineProperties(ComStreamItem, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "message": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "message"
      }
    }))
  }
});
Object.defineProperties(ComStreamItem.prototype, {
  oneofs_: {
    value: {
      "type": [1]
    }
  },
  prototypes_: {
    value: {
      1: ComStreamMessage.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ComStreamItem);
  Object.freeze(ComStreamItem.prototype);
}
class ComStream_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: ComStreamItem.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ComStream field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ComStreamItem);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ComStream field item.
                Expected type ComStreamItem.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ComStreamItem', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ComStream, 'ComStream', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ComStream to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get itemList() {
    return this.getRepeatedFieldById(2);
  }
  hasItem(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addItem(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachItem(values) {
    let T = this;
    for (const value of values) {
      T = T.addItem(value);
    }
    return T;
  }
  setItem(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeItem(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearItemList() {
    return this.clearRepeatedFieldById(2);
  }
  setItemList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
}
_defineProperty$1f(ComStream_, protoName, DEV_MODE ? 'ComStream' : '[Unknown]');
let ComStream = ComStream_;
(function (_ComStream) {})(ComStream || (ComStream = {}));
Object.defineProperties(ComStream, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "item": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "item"
      }
    }))
  }
});
Object.defineProperties(ComStream.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: ComStreamItem.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ComStream);
  Object.freeze(ComStream.prototype);
}

function _defineProperty$1e(obj, key, value) { key = _toPropertyKey$1e(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1e(arg) { var key = _toPrimitive$1e(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1e(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OfferInfo_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: OfferSummary.shape,
      3: OfferDetails.shape,
      5: ComStream.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message OfferInfo field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferSummary);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferInfo field summary.
                Expected type OfferSummary.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferSummary', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferDetails);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferInfo field details.
                Expected type OfferDetails.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferDetails', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ComStream);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OfferInfo field stream.
                Expected type ComStream.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ComStream', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OfferInfo, 'OfferInfo', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OfferInfo to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get summary() {
    return this.getFieldById(2, OfferSummary.getDefault());
  }
  setSummary(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSummary() {
    return this.setValueFieldById(2, null, null);
  }
  get details() {
    return this.getFieldById(3, OfferDetails.getDefault());
  }
  setDetails(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteDetails() {
    return this.setValueFieldById(3, null, null);
  }
  get stream() {
    return this.getFieldById(5, ComStream.getDefault());
  }
  setStream(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteStream() {
    return this.setValueFieldById(5, null, null);
  }
}
_defineProperty$1e(OfferInfo_, protoName, DEV_MODE ? 'OfferInfo' : '[Unknown]');
let OfferInfo = OfferInfo_;
(function (_OfferInfo) {})(OfferInfo || (OfferInfo = {}));
Object.defineProperties(OfferInfo, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "summary": {
        value: 2
      },
      "details": {
        value: 3
      },
      "stream": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "summary"
      },
      '3': {
        value: "details"
      },
      '5': {
        value: "stream"
      }
    }))
  }
});
Object.defineProperties(OfferInfo.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: OfferSummary.prototype,
      3: OfferDetails.prototype,
      5: ComStream.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OfferInfo);
  Object.freeze(OfferInfo.prototype);
}

const defaultNewOffer = new OfferInfo().setDetails(new OfferDetails().setCollateral('1st Deed, Land & Building'));
function getOfferInfoLtv(offer) {
  for (const stipulation of offer.summary.stipulationList) {
    if (stipulation.type === 'LTV') {
      return stipulation.percent;
    }
  }
  return null;
}
function getOfferInfoDscr(offer) {
  for (const stipulation of offer.summary.stipulationList) {
    if (stipulation.type === 'DSCR') {
      return stipulation.percent;
    }
  }
  return null;
}
function offerInfoToProjectOffer(u) {
  return ProjectOffer.from({
    'id': u.id,
    ...summaryFields(u.summary),
    ...detailsFields(u.details)
  });
}
function projectOfferToOfferInfo(u) {
  return OfferInfo.from({
    'id': u.id,
    'summary': {
      'id': u.id,
      ...summaryFields(u)
    },
    'details': {
      'id': u.id,
      ...detailsFields(u)
    }
  });
}
function summaryFields(u) {
  return {
    'owner': u.owner,
    'last_modified': u.lastModified,
    'capital_source_name': u.capitalSourceName,
    'principal': u.principal,
    'interest_interval': u.interestIntervalList,
    'loan_term': u.loanTermList,
    'closing_cost': u.closingCostList,
    'property_value': u.propertyValue,
    'repayment_schedule': u.repaymentSchedule,
    'stipulation': u.stipulationList,
    'approved': u.approved
  };
}
function detailsFields(u) {
  return {
    'collateral': u.collateral,
    'insurance_requirement': u.insuranceRequirementList,
    'recourse': u.recourse,
    'cash_management': u.cashManagement,
    'prepayment_penalty': u.prepaymentPenalty,
    'reporting_frequency_requirement': u.reportingFrequencyRequirement,
    'other_requirements': u.otherRequirements,
    'legal_term_sheet': u.legalTermSheet,
    'interest_rate_calculation_method': u.interestRateCalculationMethod,
    'reserves': u.reservesList
  };
}

function _defineProperty$1d(obj, key, value) { key = _toPropertyKey$1d(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1d(arg) { var key = _toPrimitive$1d(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1d(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OrgImage_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OrgImage field key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OrgImage$1, 'OrgImage', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OrgImage to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get key() {
    return this.getFieldById(1, "");
  }
  setKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteKey() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$1d(OrgImage_, protoName, DEV_MODE ? 'OrgImage' : '[Unknown]');
let OrgImage$1 = OrgImage_;
(function (_OrgImage) {})(OrgImage$1 || (OrgImage$1 = {}));
Object.defineProperties(OrgImage$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "key": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "key"
      }
    }))
  }
});
Object.defineProperties(OrgImage$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OrgImage$1);
  Object.freeze(OrgImage$1.prototype);
}

class OrgImage extends OrgImage_ {
  get url() {
    if (!this.key) {
      return '';
    }
    return resourceKeyToUrl(this.key);
  }
  get thumbnailUrl() {
    if (!this.key) {
      return '';
    }
    return imagePathToOrganizationLogoThumbnail(resourceKeyToUrl(this.key));
  }
  get tinyThumbnailUrl() {
    if (!this.key) {
      return '';
    }
    return imagePathToOrganizationLogoThumbnail(resourceKeyToUrl(this.key));
  }
}

registerCanonical(OrgImage_, OrgImage);

function _defineProperty$1c(obj, key, value) { key = _toPropertyKey$1c(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1c(arg) { var key = _toPrimitive$1c(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1c(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var MoodyLongTermBondCreditRatingSymbol__;
(function (MoodyLongTermBondCreditRatingSymbol__) {
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["UNSET"] = 0] = "UNSET";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["Aaa"] = 1] = "Aaa";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["Aa"] = 2] = "Aa";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["A"] = 3] = "A";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["Baa"] = 4] = "Baa";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["Ba"] = 5] = "Ba";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["B"] = 6] = "B";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["Caa"] = 7] = "Caa";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["Ca"] = 8] = "Ca";
  MoodyLongTermBondCreditRatingSymbol__[MoodyLongTermBondCreditRatingSymbol__["C"] = 9] = "C";
})(MoodyLongTermBondCreditRatingSymbol__ || (MoodyLongTermBondCreditRatingSymbol__ = {}));
const MoodyLongTermBondCreditRatingSymbol_ = defineEnumProperties(MoodyLongTermBondCreditRatingSymbol__, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);
const MoodyLongTermBondCreditRatingSymbol = extendEnum('MoodyLongTermBondCreditRatingSymbol', [["UNSET", 0], ["Aaa", 1], ["Aa", 2], ["A", 3], ["Baa", 4], ["Ba", 5], ["B", 6], ["Caa", 7], ["Ca", 8], ["C", 9]])(MoodyLongTermBondCreditRatingSymbol_);
class MoodyLongTermBondCreditRating_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message MoodyLongTermBondCreditRating field symbol.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return MoodyLongTermBondCreditRatingSymbol.fromStrictJson_(v, getFieldMap);
          }
          return MoodyLongTermBondCreditRatingSymbol.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message MoodyLongTermBondCreditRating field modifier.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(MoodyLongTermBondCreditRating, 'MoodyLongTermBondCreditRating', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message MoodyLongTermBondCreditRating to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get symbol() {
    return this.getFieldById(1, 0);
  }
  setSymbol(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSymbol() {
    return this.setValueFieldById(1, null, null);
  }
  get modifier() {
    return this.getFieldById(2, 0);
  }
  setModifier(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteModifier() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$1c(MoodyLongTermBondCreditRating_, protoName, DEV_MODE ? 'MoodyLongTermBondCreditRating' : '[Unknown]');
let MoodyLongTermBondCreditRating = MoodyLongTermBondCreditRating_;
(function (_MoodyLongTermBondCreditRating) {})(MoodyLongTermBondCreditRating || (MoodyLongTermBondCreditRating = {}));
Object.defineProperties(MoodyLongTermBondCreditRating, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "symbol": {
        value: 1
      },
      "modifier": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "symbol"
      },
      '2': {
        value: "modifier"
      }
    }))
  }
});
Object.defineProperties(MoodyLongTermBondCreditRating.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(MoodyLongTermBondCreditRating);
  Object.freeze(MoodyLongTermBondCreditRating.prototype);
}
class MoodyBondCreditRating_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: MoodyLongTermBondCreditRating.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(MoodyLongTermBondCreditRating);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message MoodyBondCreditRating field long_term.
                Expected type MoodyLongTermBondCreditRating.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'MoodyLongTermBondCreditRating', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(MoodyBondCreditRating, 'MoodyBondCreditRating', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message MoodyBondCreditRating to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get longTerm() {
    return this.getFieldById(1, MoodyLongTermBondCreditRating.getDefault());
  }
  setLongTerm(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteLongTerm() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$1c(MoodyBondCreditRating_, protoName, DEV_MODE ? 'MoodyBondCreditRating' : '[Unknown]');
let MoodyBondCreditRating = MoodyBondCreditRating_;
(function (_MoodyBondCreditRating) {})(MoodyBondCreditRating || (MoodyBondCreditRating = {}));
Object.defineProperties(MoodyBondCreditRating, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "long_term": {
        value: 1
      },
      "longTerm": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "long_term"
      }
    }))
  }
});
Object.defineProperties(MoodyBondCreditRating.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: MoodyLongTermBondCreditRating.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(MoodyBondCreditRating);
  Object.freeze(MoodyBondCreditRating.prototype);
}
class BondCreditRating_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: MoodyBondCreditRating.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(MoodyBondCreditRating);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message BondCreditRating field moody.
                Expected type MoodyBondCreditRating.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'MoodyBondCreditRating', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(BondCreditRating, 'BondCreditRating', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message BondCreditRating to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get moody() {
    return this.getFieldById(1, MoodyBondCreditRating.getDefault());
  }
  setMoody(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteMoody() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$1c(BondCreditRating_, protoName, DEV_MODE ? 'BondCreditRating' : '[Unknown]');
let BondCreditRating = BondCreditRating_;
(function (_BondCreditRating) {})(BondCreditRating || (BondCreditRating = {}));
Object.defineProperties(BondCreditRating, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "moody": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "moody"
      }
    }))
  }
});
Object.defineProperties(BondCreditRating.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: MoodyBondCreditRating.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(BondCreditRating);
  Object.freeze(BondCreditRating.prototype);
}

// tslint:disable-next-line:no-unused-variable
var Protocol__;
(function (Protocol__) {
  Protocol__[Protocol__["HTTP"] = 0] = "HTTP";
  Protocol__[Protocol__["HTTPS"] = 1] = "HTTPS";
})(Protocol__ || (Protocol__ = {}));
const Protocol_ = defineEnumProperties(Protocol__, [0, 1]);
const Protocol = extendEnum('Protocol', [["HTTP", 0], ["HTTPS", 1]])(Protocol_);

const RepeatedParamStrategy = {
  [Symbol.iterator]: function () {
    const _this = this;
    return [0, 1][Symbol.iterator]();
  },
  validValues_: new Set([0, 1]),
  has: function (value) {
    return this.validValues_.has(value);
  },
  NAIVE: 0,
  SQUARE_INDICES: 1
};

const extensions$1 = {
  Url: function (Msg) {
    const _this = this;
    return class Url extends Msg {
      static fromString(s) {
        const _this = this;
        return _this.parse(s);
      }
      static parse(urlStr, repeatedParamsStrategy) {
        const _this = this;
        if (repeatedParamsStrategy === undefined) repeatedParamsStrategy = RepeatedParamStrategy.NAIVE;
        const {
          protocol,
          hostname,
          port,
          remaining
        } = parseProtocolAndHost(urlStr);
        runtime.checkAsserts() ? runtime.ensure(remaining == '' || runtime.getComputed(remaining, 0) != '/', null) : undefined;
        const {
          path,
          queryParams,
          fragment
        } = parsePathQueryAndFragment(remaining);
        let url = new Url().setProtocol(protocol).setHostname(hostname);
        if (port != null) {
          url = url.setPort(port);
        }
        if (path != '') {
          url = url.setPath(decodePath(path));
        }
        url = setUrlParams(url, function (url, param) {
          return url.hasQuery(param);
        }, function (url, param, value) {
          return url.setQuery(param, value);
        }, queryParams, repeatedParamsStrategy);
        url = setUrlParams(url, function (url, param) {
          return url.hasFragment(param);
        }, function (url, param, value) {
          return url.setFragment(param, value);
        }, fragment, repeatedParamsStrategy);
        return url;
      }
      toString(repeatedParamsStrategy) {
        const _this = this;
        if (repeatedParamsStrategy === undefined) repeatedParamsStrategy = RepeatedParamStrategy.NAIVE;
        return string(function* () {
          if (_this.hostname == '') {
            runtime.checkAsserts() ? runtime.ensure(_this.port == 0, null) : undefined;
            runtime.checkAsserts() ? runtime.ensure(_this.path == '', null) : undefined;
            runtime.checkAsserts() ? runtime.ensure(_this.querySize == 0, null) : undefined;
            runtime.checkAsserts() ? runtime.ensure(_this.fragmentSize == 0, null) : undefined;
            return '';
          }
          switch (_this.protocol) {
            case Protocol.HTTP:
              yield 'http://';
              break;
            case Protocol.HTTPS:
              yield 'https://';
              break;
            default:
              throw new Error(`Unknown protocol (${_this.protocol}).`);
              break;
          }
          yield encodeURIComponent(_this.hostname);
          if (_this.port != 0) {
            yield ':';
            yield _this.port;
          }
          yield '/';
          yield encodePath(_this.path);
          const queryString = urlParamsToString(_this.queryMap, repeatedParamsStrategy);
          if (queryString != '') {
            yield '?';
            yield queryString;
          }
          const fragmentString = urlParamsToString(_this.fragmentMap, repeatedParamsStrategy);
          if (fragmentString) {
            yield '#';
            yield fragmentString;
          }
        }());
      }
    };
  }
};
function parseProtocolAndHost(url) {
  const m = /^(\w+)\:\/*([\w\-\.]+)(?:\:(\d+))?(?:\/|$)/.exec(url);
  if (!m) {
    throw new Error(`Invalid url "${url}".`);
  }
  return {
    protocol: protocolFromString(decodeURIComponent(runtime.getComputed(m, 1)).toLowerCase()),
    hostname: decodeURIComponent(runtime.getComputed(m, 2).toLowerCase()),
    port: runtime.getComputed(m, 3) == null ? null : +decodeURIComponent(runtime.getComputed(m, 3)),
    remaining: url.slice(runtime.getComputed(m, 0).length).replace(/^\/+/, '')
  };
}
function parsePathQueryAndFragment(s) {
  let $3449, $3450;
  const m = /^([^\?\#]*)(?:\?([^\#]*))?(?:\#(.*))?$/.exec(s);
  runtime.checkAsserts() ? runtime.ensure(m, null) : undefined;
  return {
    path: runtime.getComputed(m, 1),
    queryParams: parseQueryOrFragmentParams(($3449 = runtime.getComputed(m, 2), $3449 == null ? '' : $3449)),
    fragment: parseQueryOrFragmentParams(($3450 = runtime.getComputed(m, 3), $3450 == null ? '' : $3450))
  };
}
function protocolFromString(s) {
  switch (s) {
    case 'http':
      return Protocol.HTTP;
      break;
    case 'https':
      return Protocol.HTTPS;
      break;
  }
  throw new Error(`Unrecognized protocol "${s}".`);
}
function parseQueryOrFragmentParams(queryString) {
  return array(function* () {
    if (queryString == '') {
      return;
    }
    for (const query of queryString.split('&')) {
      const parts = query.split('=');
      yield {
        param: decodeURIComponent(runtime.getComputed(parts, 0)),
        value: decodeURIComponent(runtime.getComputed(parts, 1))
      };
    }
  }());
}
function setUrlParams(url, has, set, params, repeatedParamsStrategy) {
  const nextParamIndices = new Map();
  let U = url;
  for (const {
    param,
    value
  } of params) {
    if (repeatedParamsStrategy == RepeatedParamStrategy.SQUARE_INDICES && has(U, param)) {
      let $3452, $3453;
      const paramIndex = nextParamIndices.get(param) || 1;
      nextParamIndices.set(param, ($3452 = paramIndex, $3453 = typeof $3452, (runtime.checkAsserts() ? runtime.ensure(typeof $3452 == 'number', function () {
        return `Expected type "number" but found "${$3453}" for (${$3452}).`;
      }) : undefined, $3452)) + 1);
      U = set(U, `${param}[${paramIndex}]`, value);
    } else {
      U = set(U, param, value);
    }
  }
  return U;
}
function urlParamsToString(params, repeatedParamsStrategy) {
  return string('&')(function* () {
    for (const [key, value] of params.entries()) {
      yield function () {
        const array$3451 = [];
        array$3451.push(encodeURIComponent(neutralizeParamName(key, repeatedParamsStrategy)));
        array$3451.push('=');
        array$3451.push(encodeURIComponent(value));
        return array$3451;
      }().join('');
    }
  }());
}
function neutralizeParamName(param, repeatedParamsStrategy) {
  if (repeatedParamsStrategy == RepeatedParamStrategy.SQUARE_INDICES) {
    const m = /^(.*)\[\d+\]$/.exec(param);
    if (m) {
      return runtime.getComputed(m, 1);
    }
  }
  return param;
}
function decodePath(path) {
  return string('/')(function* () {
    for (const part of path.split(/\//g)) {
      yield decodeURIComponent(part);
    }
  }());
}
function encodePath(path) {
  return string('/')(function* () {
    for (const part of path.split(/\//g)) {
      yield encodeURIComponent(part);
    }
  }());
}

function _defineProperty$1b(obj, key, value) { key = _toPropertyKey$1b(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1b(arg) { var key = _toPrimitive$1b(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1b(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Url_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 2,
      3: 7,
      4: 2,
      5: [2, 2],
      6: [2, 2]
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Url field protocol.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return Protocol.fromStrictJson_(v, getFieldMap);
          }
          return Protocol.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Url field hostname.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Url field port.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Url field path.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Url field query.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Url field query.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Url field fragment.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Url field fragment.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Url, 'Url', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Url to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get protocol() {
    return this.getFieldById(1, 0);
  }
  setProtocol(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteProtocol() {
    return this.setValueFieldById(1, null, null);
  }
  get hostname() {
    return this.getFieldById(2, "");
  }
  setHostname(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteHostname() {
    return this.setValueFieldById(2, null, null);
  }
  get port() {
    return this.getFieldById(3, 0);
  }
  setPort(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deletePort() {
    return this.setValueFieldById(3, null, null);
  }
  get path() {
    return this.getFieldById(4, "");
  }
  setPath(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deletePath() {
    return this.setValueFieldById(4, null, null);
  }
  get queryKeys() {
    return this.getMapIteratorById(5);
  }
  get queryValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(5)) {
        yield self.getMapValueById(5, key);
      }
    }();
  }
  get querySize() {
    return this.getMapSizeById(5);
  }
  hasQuery(key) {
    return this.hasMapKeyById(5, key);
  }
  getQuery(key) {
    return this.getMapValueById(5, key);
  }
  setQuery(key, value) {
    return this.setMapValue(5, key, value);
  }
  get queryMap() {
    return this.getMapById(5);
  }
  setQueryMap(map) {
    if (map.size === this.getMapSizeById(5)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(5, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(5);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(5, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeQuery(key) {
    return deprecatedMember('Url', 'removeQuery', 'deleteQuery', this.deleteQuery(key));
  }
  deleteQuery(key) {
    return this.removeFromMapById(5, key);
  }
  clearQueryMap() {
    return this.clearMap(5);
  }
  get fragmentKeys() {
    return this.getMapIteratorById(6);
  }
  get fragmentValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(6)) {
        yield self.getMapValueById(6, key);
      }
    }();
  }
  get fragmentSize() {
    return this.getMapSizeById(6);
  }
  hasFragment(key) {
    return this.hasMapKeyById(6, key);
  }
  getFragment(key) {
    return this.getMapValueById(6, key);
  }
  setFragment(key, value) {
    return this.setMapValue(6, key, value);
  }
  get fragmentMap() {
    return this.getMapById(6);
  }
  setFragmentMap(map) {
    if (map.size === this.getMapSizeById(6)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(6, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(6);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(6, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeFragment(key) {
    return deprecatedMember('Url', 'removeFragment', 'deleteFragment', this.deleteFragment(key));
  }
  deleteFragment(key) {
    return this.removeFromMapById(6, key);
  }
  clearFragmentMap() {
    return this.clearMap(6);
  }
}
_defineProperty$1b(Url_, protoName, DEV_MODE ? 'Url' : '[Unknown]');
let Url = extendMessage('Url', extensions$1.Url)(Url_);
(function (_Url) {})(Url || (Url = {}));
Object.defineProperties(Url, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "protocol": {
        value: 1
      },
      "hostname": {
        value: 2
      },
      "port": {
        value: 3
      },
      "path": {
        value: 4
      },
      "query": {
        value: 5
      },
      "fragment": {
        value: 6
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "protocol"
      },
      '2': {
        value: "hostname"
      },
      '3': {
        value: "port"
      },
      '4': {
        value: "path"
      },
      '5': {
        value: "query"
      },
      '6': {
        value: "fragment"
      }
    }))
  }
});
Object.defineProperties(Url.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      5: [null, null],
      6: [null, null]
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Url);
  Object.freeze(Url.prototype);
}

function _defineProperty$1a(obj, key, value) { key = _toPropertyKey$1a(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1a(arg) { var key = _toPrimitive$1a(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1a(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Quotation_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: Url.shape,
      3: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Quotation field value.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Url);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Quotation field source.
                Expected type Url.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Url', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Quotation field license.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Quotation, 'Quotation', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Quotation to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get value() {
    return this.getFieldById(1, "");
  }
  setValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteValue() {
    return this.setValueFieldById(1, null, null);
  }
  get source() {
    return this.getFieldById(2, Url.getDefault());
  }
  setSource(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSource() {
    return this.setValueFieldById(2, null, null);
  }
  get license() {
    return this.getFieldById(3, "");
  }
  setLicense(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLicense() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$1a(Quotation_, protoName, DEV_MODE ? 'Quotation' : '[Unknown]');
let Quotation = Quotation_;
(function (_Quotation) {})(Quotation || (Quotation = {}));
Object.defineProperties(Quotation, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      },
      "source": {
        value: 2
      },
      "license": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      },
      '2': {
        value: "source"
      },
      '3': {
        value: "license"
      }
    }))
  }
});
Object.defineProperties(Quotation.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: Url.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Quotation);
  Object.freeze(Quotation.prototype);
}

function _defineProperty$19(obj, key, value) { key = _toPropertyKey$19(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$19(arg) { var key = _toPrimitive$19(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$19(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OrgDescription_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Quotation.shape,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Quotation);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OrgDescription field quote.
                Expected type Quotation.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Quotation', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OrgDescription field manual.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OrgDescription$1, 'OrgDescription', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OrgDescription to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get quote() {
    return this.getFieldById(1, Quotation.getDefault());
  }
  setQuote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteQuote() {
    return this.setValueFieldById(1, null, null);
  }
  get manual() {
    return this.getFieldById(2, "");
  }
  setManual(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteManual() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$19(OrgDescription_, protoName, DEV_MODE ? 'OrgDescription' : '[Unknown]');
let OrgDescription$1 = OrgDescription_;
(function (_OrgDescription) {})(OrgDescription$1 || (OrgDescription$1 = {}));
Object.defineProperties(OrgDescription$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "quote": {
        value: 1
      },
      "manual": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "quote"
      },
      '2': {
        value: "manual"
      }
    }))
  }
});
Object.defineProperties(OrgDescription$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Quotation.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OrgDescription$1);
  Object.freeze(OrgDescription$1.prototype);
}
class Org_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: OrgImage.shape,
      4: Url.shape,
      5: BondCreditRating.shape,
      6: 2,
      7: OrgDescription$1.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Org field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Org field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OrgImage);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Org field logo.
                Expected type OrgImage.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OrgImage', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Url);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Org field website.
                Expected type Url.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Url', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(BondCreditRating);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Org field bond_credit_rating.
                Expected type BondCreditRating.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'BondCreditRating', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Org field brief_description.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OrgDescription$1);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Org field description.
                Expected type OrgDescription.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OrgDescription', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Org$1, 'Org', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Org to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get logo() {
    return this.getFieldById(3, OrgImage.getDefault());
  }
  setLogo(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLogo() {
    return this.setValueFieldById(3, null, null);
  }
  get website() {
    return this.getFieldById(4, Url.getDefault());
  }
  setWebsite(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteWebsite() {
    return this.setValueFieldById(4, null, null);
  }
  get bondCreditRating() {
    return this.getFieldById(5, BondCreditRating.getDefault());
  }
  setBondCreditRating(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteBondCreditRating() {
    return this.setValueFieldById(5, null, null);
  }
  get briefDescription() {
    return this.getFieldById(6, "");
  }
  setBriefDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteBriefDescription() {
    return this.setValueFieldById(6, null, null);
  }
  get description() {
    return this.getFieldById(7, OrgDescription$1.getDefault());
  }
  setDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteDescription() {
    return this.setValueFieldById(7, null, null);
  }
}
_defineProperty$19(Org_, protoName, DEV_MODE ? 'Org' : '[Unknown]');
let Org$1 = Org_;
(function (_Org) {})(Org$1 || (Org$1 = {}));
Object.defineProperties(Org$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "name": {
        value: 2
      },
      "logo": {
        value: 3
      },
      "website": {
        value: 4
      },
      "bond_credit_rating": {
        value: 5
      },
      "bondCreditRating": {
        value: 5
      },
      "brief_description": {
        value: 6
      },
      "briefDescription": {
        value: 6
      },
      "description": {
        value: 7
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "logo"
      },
      '4': {
        value: "website"
      },
      '5': {
        value: "bond_credit_rating"
      },
      '6': {
        value: "brief_description"
      },
      '7': {
        value: "description"
      }
    }))
  }
});
Object.defineProperties(Org$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: OrgImage.prototype,
      4: Url.prototype,
      5: BondCreditRating.prototype,
      7: OrgDescription$1.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Org$1);
  Object.freeze(Org$1.prototype);
}

function getQuotationSourceLabel(quotation) {
  switch (quotation.source.hostname) {
    case 'en.wikipedia.org':
      return 'Wikipedia';
      break;
  }
  return '';
}

class Org extends Org_ {
  getPrimaryDescription() {
    return this.description.getPrimary();
  }
}
class OrgDescription extends OrgDescription_ {
  getPrimary() {
    return this.manual ? this.fromManual() : this.fromQuote();
  }
  fromQuote() {
    return new Quotation().setSource(this.quote.source).setLicense(this.quote.license).setValue(textWithHtmlEntities(this.quote.value));
  }
  fromManual() {
    return new Quotation().setValue(textWithHtmlEntities(this.manual));
  }
}
function textWithHtmlEntities(value) {
  const out = [];
  for (const [i, part] of value.split('&').entries()) {
    const m = /^(\w+);/.exec(part);
    if (m) {
      out.push(replaceEntity(m[1]));
      out.push(part.slice(m[0].length));
    } else {
      out.push(`${i === 0 ? '' : '&'}${part}`);
    }
  }
  return out.join('');
}
function replaceEntity(entity) {
  switch (entity) {
    case 'amp':
      return '&';
  }
  return `&${entity};`;
}

registerCanonical(OrgDescription_, OrgDescription);
registerCanonical(Org_, Org);

function _defineProperty$18(obj, key, value) { key = _toPropertyKey$18(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$18(arg) { var key = _toPrimitive$18(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$18(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var ShareDeclined__;
(function (ShareDeclined__) {
  ShareDeclined__[ShareDeclined__["UNSET"] = 0] = "UNSET";
  ShareDeclined__[ShareDeclined__["MANUAL"] = 1] = "MANUAL";
  ShareDeclined__[ShareDeclined__["AUTO"] = 2] = "AUTO";
  ShareDeclined__[ShareDeclined__["EMAIL"] = 3] = "EMAIL";
})(ShareDeclined__ || (ShareDeclined__ = {}));
const ShareDeclined_ = defineEnumProperties(ShareDeclined__, [0, 1, 2, 3]);
const ShareDeclined = extendEnum('ShareDeclined', [["UNSET", 0], ["MANUAL", 1], ["AUTO", 2], ["EMAIL", 3]])(ShareDeclined_);
class Share_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: 2,
      4: 2,
      5: 2,
      6: 1,
      7: 0,
      8: 7,
      9: 1,
      10: SimpleDate.shape,
      11: 1,
      12: 2,
      13: 1,
      14: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field deal.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field org.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field location_label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Share field active.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Share field declined.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ShareDeclined.fromStrictJson_(v, getFieldMap);
          }
          return ShareDeclined.from(value);
        };
      case 8:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Share field views.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 9:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Share field invitation_sent_by_platform.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Share field token_first_created.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Share field concealed.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 12:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field provider.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 13:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Share field stacksource_sourced.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 14:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Share field offer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Share$1, 'Share', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Share to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get deal() {
    return this.getFieldById(2, "");
  }
  setDeal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteDeal() {
    return this.setValueFieldById(2, null, null);
  }
  get org() {
    return this.getFieldById(3, "");
  }
  setOrg(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteOrg() {
    return this.setValueFieldById(3, null, null);
  }
  get label() {
    return this.getFieldById(4, "");
  }
  setLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteLabel() {
    return this.setValueFieldById(4, null, null);
  }
  get locationLabel() {
    return this.getFieldById(5, "");
  }
  setLocationLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteLocationLabel() {
    return this.setValueFieldById(5, null, null);
  }
  get active() {
    return this.getFieldById(6, false);
  }
  setActive(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteActive() {
    return this.setValueFieldById(6, null, null);
  }
  get declined() {
    return this.getFieldById(7, 0);
  }
  setDeclined(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteDeclined() {
    return this.setValueFieldById(7, null, null);
  }
  get views() {
    return this.getFieldById(8, 0);
  }
  setViews(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteViews() {
    return this.setValueFieldById(8, null, null);
  }
  get invitationSentByPlatform() {
    return this.getFieldById(9, false);
  }
  setInvitationSentByPlatform(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteInvitationSentByPlatform() {
    return this.setValueFieldById(9, null, null);
  }
  get tokenFirstCreated() {
    return this.getFieldById(10, SimpleDate.getDefault());
  }
  setTokenFirstCreated(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteTokenFirstCreated() {
    return this.setValueFieldById(10, null, null);
  }
  get concealed() {
    return this.getFieldById(11, false);
  }
  setConcealed(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteConcealed() {
    return this.setValueFieldById(11, null, null);
  }
  get provider() {
    return this.getFieldById(12, "");
  }
  setProvider(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteProvider() {
    return this.setValueFieldById(12, null, null);
  }
  get stacksourceSourced() {
    return this.getFieldById(13, false);
  }
  setStacksourceSourced(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteStacksourceSourced() {
    return this.setValueFieldById(13, null, null);
  }
  get offerList() {
    return this.getRepeatedFieldById(14);
  }
  hasOffer(value) {
    return this.repeatedFieldByIdHas(14, value);
  }
  addOffer(value) {
    return this.addToRepeatedFieldById(14, value);
  }
  addEachOffer(values) {
    let T = this;
    for (const value of values) {
      T = T.addOffer(value);
    }
    return T;
  }
  setOffer(index, value) {
    return this.setRepeatedFieldById(14, index, value);
  }
  removeOffer(index) {
    return this.removeFromRepeatedFieldById(14, index);
  }
  clearOfferList() {
    return this.clearRepeatedFieldById(14);
  }
  setOfferList(values) {
    return this.overwriteRepeatedFieldById(14, values);
  }
}
_defineProperty$18(Share_, protoName, DEV_MODE ? 'Share' : '[Unknown]');
let Share$1 = Share_;
(function (_Share) {})(Share$1 || (Share$1 = {}));
Object.defineProperties(Share$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "deal": {
        value: 2
      },
      "org": {
        value: 3
      },
      "label": {
        value: 4
      },
      "location_label": {
        value: 5
      },
      "locationLabel": {
        value: 5
      },
      "active": {
        value: 6
      },
      "declined": {
        value: 7
      },
      "views": {
        value: 8
      },
      "invitation_sent_by_platform": {
        value: 9
      },
      "invitationSentByPlatform": {
        value: 9
      },
      "token_first_created": {
        value: 10
      },
      "tokenFirstCreated": {
        value: 10
      },
      "concealed": {
        value: 11
      },
      "provider": {
        value: 12
      },
      "stacksource_sourced": {
        value: 13
      },
      "stacksourceSourced": {
        value: 13
      },
      "offer": {
        value: 14
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "deal"
      },
      '3': {
        value: "org"
      },
      '4': {
        value: "label"
      },
      '5': {
        value: "location_label"
      },
      '6': {
        value: "active"
      },
      '7': {
        value: "declined"
      },
      '8': {
        value: "views"
      },
      '9': {
        value: "invitation_sent_by_platform"
      },
      '10': {
        value: "token_first_created"
      },
      '11': {
        value: "concealed"
      },
      '12': {
        value: "provider"
      },
      '13': {
        value: "stacksource_sourced"
      },
      '14': {
        value: "offer"
      }
    }))
  }
});
Object.defineProperties(Share$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      10: SimpleDate.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Share$1);
  Object.freeze(Share$1.prototype);
}

class Share extends Share_ {
  get project() {
    return deprecatedMember('Share', 'project', 'deal', this.deal);
  }
}

registerCanonical(ShareDeclined, ShareDeclined);
registerCanonical(Share_, Share);

function sponsorRoleToString(sponsorRole) {
  switch (sponsorRole) {
    case SponsorRole.INVESTOR:
      return 'Investor';
    case SponsorRole.OWNER_OCCUPIER:
      return 'Owner-Occupier';
  }
  assertThrow(emsg`Unknown sponsor role value ${sponsorRole}.`);
  return '';
}

// An address is considered empty if it has a `line2` but no other information.
// (This really shouldn't happen. We should try to guard against it.)
const isEmptyAddress = address => address.isEmpty();
const formatCityAndState = address => address.formatCityAndState();
const formatAddressOnOneLine = address => address.formatOneLine();

function termRangesEqual(a, b) {
  return a.equals(b);
}
function termRangeToString(value) {
  return value.format();
}
function termRangesOverlap(range1, range2) {
  return range1.overlaps(range2);
}
function withinTermRange(range, term) {
  return range.contains(term);
}
function createYearTermRange(lowerBoundYears, upperBoundYears) {
  if (lowerBoundYears === undefined) lowerBoundYears = 0;
  if (upperBoundYears === undefined) upperBoundYears = 0;
  return TermRange.ofYears(lowerBoundYears, upperBoundYears);
}
function createMonthTerm(length) {
  return Term.ofMonths(length);
}

// tslint:disable-next-line:no-unused-variable
var SortDirection__;
(function (SortDirection__) {
  SortDirection__[SortDirection__["ASC"] = 0] = "ASC";
  SortDirection__[SortDirection__["DESC"] = 1] = "DESC";
})(SortDirection__ || (SortDirection__ = {}));
const SortDirection_ = defineEnumProperties(SortDirection__, [0, 1]);
const SortDirection = extendEnum('SortDirection', [["ASC", 0], ["DESC", 1]])(SortDirection_);

function _defineProperty$17(obj, key, value) { key = _toPropertyKey$17(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$17(arg) { var key = _toPrimitive$17(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$17(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class SharedUser_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SharedUser field token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SharedUser field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SharedUser, 'SharedUser', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SharedUser to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get token() {
    return this.getFieldById(1, "");
  }
  setToken(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteToken() {
    return this.setValueFieldById(1, null, null);
  }
  get email() {
    return this.getFieldById(2, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$17(SharedUser_, protoName, DEV_MODE ? 'SharedUser' : '[Unknown]');
let SharedUser = SharedUser_;
(function (_SharedUser) {})(SharedUser || (SharedUser = {}));
Object.defineProperties(SharedUser, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "token": {
        value: 1
      },
      "email": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "token"
      },
      '2': {
        value: "email"
      }
    }))
  }
});
Object.defineProperties(SharedUser.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SharedUser);
  Object.freeze(SharedUser.prototype);
}

function _defineProperty$16(obj, key, value) { key = _toPropertyKey$16(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$16(arg) { var key = _toPrimitive$16(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$16(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _IpAddress_Version_;
(function (_IpAddress_Version_) {
  _IpAddress_Version_[_IpAddress_Version_["UNSET"] = 0] = "UNSET";
  _IpAddress_Version_[_IpAddress_Version_["V4"] = 4] = "V4";
})(_IpAddress_Version_ || (_IpAddress_Version_ = {}));
const _IpAddress_Version = defineEnumProperties(_IpAddress_Version_, [4]);
class IpAddress_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      4: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message IpAddress field v4.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(IpAddress$1, 'IpAddress', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message IpAddress to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get VersionCase() {
    return _IpAddress_Version;
  }
  get versionCase() {
    return this.getCaseFromIds([4]);
  }
  get v4() {
    return this.getFieldById(4, 0);
  }
  setV4(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, "version");
  }
  deleteV4() {
    return this.setValueFieldById(4, null, "version");
  }
}
_defineProperty$16(IpAddress_, protoName, DEV_MODE ? 'IpAddress' : '[Unknown]');
let IpAddress$1 = IpAddress_;
(function (_IpAddress) {})(IpAddress$1 || (IpAddress$1 = {}));
Object.defineProperties(IpAddress$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "v4": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '4': {
        value: "v4"
      }
    }))
  }
});
Object.defineProperties(IpAddress$1.prototype, {
  oneofs_: {
    value: {
      "version": [4]
    }
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(IpAddress$1);
  Object.freeze(IpAddress$1.prototype);
}

class IpAddress extends IpAddress_ {
  static fromString(s) {
    // TODO(nathan): I'm not sure what '::ffff:' at the start of an IP string
    // means, but it comes from either Node or Express. Figure out what it is and
    // why it's being added.
    const S = s.replace(/^::ffff:/, '');

    // Sometimes this has been seen to end in a port number (this port number at
    // the end didn't show up until we started deploying to Azure).
    const m = ensure(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d{1,5})?$/.exec(S), emsg`
        Currently only IPv4 addresses are supported.
        Unrecognized format: ${s}`);
    const parts = m[1].split('.');
    const values = [];
    for (const part of parts) {
      const n = +part;
      ensure(Number.isSafeInteger(n) && n >= 0 && n <= 255, emsg`Invalid IP address: ${s}`);
      values.push(n);
    }
    let value = 0;
    for (let i = 0; i < values.length; i++) {
      value += values[i] << (3 - i) * 8 >>> 0;
    }
    return new IpAddress().setV4(value);
  }
  format() {
    assert.same(this.versionCase, IpAddress.VersionCase.V4, emsg`Unexpected version case: ${this.versionCase}`);
    const value = this.v4;
    assert(value >= 0 && value < 2 ** 32, emsg`Invalid IP address value: ${value}`);
    const out = [];
    for (let i = 3; i >= 0; i -= 1) {
      out.push(255 & value >>> i * 8);
    }
    return out.join('.');
  }
}

registerCanonical(IpAddress_, IpAddress);

function _defineProperty$15(obj, key, value) { key = _toPropertyKey$15(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$15(arg) { var key = _toPrimitive$15(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$15(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var Field__;
(function (Field__) {
  Field__[Field__["ID"] = 0] = "ID";
  Field__[Field__["SYM"] = 100] = "SYM";
  Field__[Field__["APPROVED"] = 55] = "APPROVED";
  Field__[Field__["APPROVAL_REQUESTED"] = 96] = "APPROVAL_REQUESTED";
  Field__[Field__["APPROVAL_REQUESTED_BY"] = 97] = "APPROVAL_REQUESTED_BY";
  Field__[Field__["NDA_REQUIRED"] = 56] = "NDA_REQUIRED";
  Field__[Field__["STACKSOURCE_AGREEMENT_SIGNED"] = 64] = "STACKSOURCE_AGREEMENT_SIGNED";
  Field__[Field__["USER_HAS_WRITE_PERMISSION"] = 1] = "USER_HAS_WRITE_PERMISSION";
  Field__[Field__["OWNER"] = 2] = "OWNER";
  Field__[Field__["SPONSOR_USERS"] = 62] = "SPONSOR_USERS";
  Field__[Field__["SPONSOR_EMAIL_VERIFIED"] = 63] = "SPONSOR_EMAIL_VERIFIED";
  Field__[Field__["BROKER_USERS"] = 95] = "BROKER_USERS";
  Field__[Field__["DRAFT"] = 3] = "DRAFT";
  Field__[Field__["STATUS"] = 71] = "STATUS";
  Field__[Field__["LOST_REASON"] = 72] = "LOST_REASON";
  Field__[Field__["FINANCING_PURPOSE"] = 4] = "FINANCING_PURPOSE";
  Field__[Field__["ASSET_TYPE"] = 5] = "ASSET_TYPE";
  Field__[Field__["SPONSOR_ROLE"] = 48] = "SPONSOR_ROLE";
  Field__[Field__["HOTEL_FLAG"] = 92] = "HOTEL_FLAG";
  Field__[Field__["HOTEL_AVERAGE_DAILY_RATE"] = 93] = "HOTEL_AVERAGE_DAILY_RATE";
  Field__[Field__["HOTEL_AVERAGE_OCCUPANCY_RATE"] = 94] = "HOTEL_AVERAGE_OCCUPANCY_RATE";
  Field__[Field__["TENANCY"] = 61] = "TENANCY";
  Field__[Field__["NAME"] = 6] = "NAME";
  Field__[Field__["LABEL"] = 73] = "LABEL";
  Field__[Field__["ADDRESS"] = 7] = "ADDRESS";
  Field__[Field__["TOTAL_PROJECT_BUDGET"] = 8] = "TOTAL_PROJECT_BUDGET";
  Field__[Field__["NUMBER_OF_UNITS"] = 9] = "NUMBER_OF_UNITS";
  Field__[Field__["PROJECT_SUMMARY"] = 10] = "PROJECT_SUMMARY";
  Field__[Field__["TOTAL_SIZE"] = 11] = "TOTAL_SIZE";
  Field__[Field__["FOOTPRINT_SIZE"] = 12] = "FOOTPRINT_SIZE";
  Field__[Field__["LOT_SIZE"] = 13] = "LOT_SIZE";
  Field__[Field__["ARCHITECT"] = 14] = "ARCHITECT";
  Field__[Field__["STRUCTURAL_ENGINEER"] = 42] = "STRUCTURAL_ENGINEER";
  Field__[Field__["CONSTRUCTION_MANAGER"] = 43] = "CONSTRUCTION_MANAGER";
  Field__[Field__["GENERAL_CONTRACTOR"] = 36] = "GENERAL_CONTRACTOR";
  Field__[Field__["LEASING_REPRESENTATIVE"] = 44] = "LEASING_REPRESENTATIVE";
  Field__[Field__["PROPERTY_MANAGER"] = 45] = "PROPERTY_MANAGER";
  Field__[Field__["COMPLETED_YEAR"] = 15] = "COMPLETED_YEAR";
  Field__[Field__["RENOVATED_YEAR"] = 50] = "RENOVATED_YEAR";
  Field__[Field__["FLOORS_ABOVE_GROUND"] = 16] = "FLOORS_ABOVE_GROUND";
  Field__[Field__["FLOORS_BELOW_GROUND"] = 17] = "FLOORS_BELOW_GROUND";
  Field__[Field__["PARKING_SPOTS"] = 99] = "PARKING_SPOTS";
  Field__[Field__["DESIGN_INFO"] = 18] = "DESIGN_INFO";
  Field__[Field__["PROPERTY_TITLE"] = 19] = "PROPERTY_TITLE";
  Field__[Field__["ASSET_NOTES"] = 20] = "ASSET_NOTES";
  Field__[Field__["LATEST_APPRAISAL_VALUE"] = 21] = "LATEST_APPRAISAL_VALUE";
  Field__[Field__["LATEST_APPRAISAL_DATE"] = 37] = "LATEST_APPRAISAL_DATE";
  Field__[Field__["TARGET_CLOSE_DATE"] = 98] = "TARGET_CLOSE_DATE";
  Field__[Field__["INVESTMENT_CATEGORY"] = 78] = "INVESTMENT_CATEGORY";
  Field__[Field__["CURRENT_NET_OPERATING_INCOME"] = 38] = "CURRENT_NET_OPERATING_INCOME";
  Field__[Field__["PRO_FORMA_NET_OPERATING_INCOME"] = 77] = "PRO_FORMA_NET_OPERATING_INCOME";
  Field__[Field__["CAP_RATE"] = 39] = "CAP_RATE";
  Field__[Field__["PRO_FORMA_CAP_RATE"] = 79] = "PRO_FORMA_CAP_RATE";
  Field__[Field__["FINANCIAL_NOTES"] = 22] = "FINANCIAL_NOTES";
  Field__[Field__["ACQUISITION_PRICE"] = 23] = "ACQUISITION_PRICE";
  Field__[Field__["ACQUISITION_DATE"] = 24] = "ACQUISITION_DATE";
  Field__[Field__["ACQUISITION_STATUS"] = 47] = "ACQUISITION_STATUS";
  Field__[Field__["PRINCIPAL_NEEDED"] = 25] = "PRINCIPAL_NEEDED";
  Field__[Field__["LIEN_POSITION"] = 26] = "LIEN_POSITION";
  Field__[Field__["FINANCIAL_INSTRUMENTS"] = 27] = "FINANCIAL_INSTRUMENTS";
  Field__[Field__["AFFORDABILITY"] = 80] = "AFFORDABILITY";
  Field__[Field__["NUMBER_OF_FLOORS"] = 81] = "NUMBER_OF_FLOORS";
  Field__[Field__["HAS_ELEVATOR"] = 82] = "HAS_ELEVATOR";
  Field__[Field__["UNIT_INFORMATION"] = 83] = "UNIT_INFORMATION";
  Field__[Field__["LIHTC_RATIO"] = 84] = "LIHTC_RATIO";
  Field__[Field__["HUD_SELECTED_LTV"] = 85] = "HUD_SELECTED_LTV";
  Field__[Field__["FREDDIE_SBL_SELECTED_LTV"] = 86] = "FREDDIE_SBL_SELECTED_LTV";
  Field__[Field__["FREDDIE_SBL_SELECTED_DSCR"] = 87] = "FREDDIE_SBL_SELECTED_DSCR";
  Field__[Field__["FREDDIE_SBL_INTEREST_ONLY_TERM"] = 88] = "FREDDIE_SBL_INTEREST_ONLY_TERM";
  Field__[Field__["FREDDIE_SBL_PREPAYMENT_PENALTY"] = 89] = "FREDDIE_SBL_PREPAYMENT_PENALTY";
  Field__[Field__["OFFER_EXPECTATIONS"] = 28] = "OFFER_EXPECTATIONS";
  Field__[Field__["CAP_STACK"] = 29] = "CAP_STACK";
  Field__[Field__["CAP_STACK_TARGETS"] = 76] = "CAP_STACK_TARGETS";
  Field__[Field__["IMAGES"] = 30] = "IMAGES";
  Field__[Field__["ATTACHMENTS"] = 31] = "ATTACHMENTS";
  Field__[Field__["SPONSOR_PROFILE"] = 32] = "SPONSOR_PROFILE";
  Field__[Field__["GUARANTORS"] = 75] = "GUARANTORS";
  Field__[Field__["SOLICITED_LENDERS"] = 91] = "SOLICITED_LENDERS";
  Field__[Field__["NOTE"] = 40] = "NOTE";
  Field__[Field__["PROJECT_SHARE_IDS"] = 41] = "PROJECT_SHARE_IDS";
  Field__[Field__["PROJECT_SHARE_SYMS"] = 101] = "PROJECT_SHARE_SYMS";
  Field__[Field__["OFFERS"] = 34] = "OFFERS";
  Field__[Field__["OFFER_COUNT"] = 68] = "OFFER_COUNT";
  Field__[Field__["ACCEPTED_OFFER"] = 35] = "ACCEPTED_OFFER";
  Field__[Field__["OFFER_ACCEPTED_TIME"] = 67] = "OFFER_ACCEPTED_TIME";
  Field__[Field__["OFFER_UNDERWRITING_REQUESTED"] = 90] = "OFFER_UNDERWRITING_REQUESTED";
  Field__[Field__["PROJECT_STAGE"] = 69] = "PROJECT_STAGE";
  Field__[Field__["LEADING_OFFER"] = 46] = "LEADING_OFFER";
  Field__[Field__["CREATION_TIME"] = 49] = "CREATION_TIME";
  Field__[Field__["OCCUPANCY"] = 51] = "OCCUPANCY";
  Field__[Field__["USES"] = 52] = "USES";
  Field__[Field__["DISCLAIMER"] = 53] = "DISCLAIMER";
  Field__[Field__["DEMOGRAPHICS"] = 54] = "DEMOGRAPHICS";
  Field__[Field__["SHOW_CONCEALED_SHARES"] = 57] = "SHOW_CONCEALED_SHARES";
  Field__[Field__["PROJECT_FEE"] = 59] = "PROJECT_FEE";
  Field__[Field__["CAPITAL_ADVISORS"] = 60] = "CAPITAL_ADVISORS";
  Field__[Field__["UNDERWRITING_APPROVAL_CHECKLIST_ENABLED"] = 66] = "UNDERWRITING_APPROVAL_CHECKLIST_ENABLED";
  Field__[Field__["UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS"] = 65] = "UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS";
  Field__[Field__["PRINCIPAL_NEEDED_COMPARABLE"] = 70] = "PRINCIPAL_NEEDED_COMPARABLE";
  Field__[Field__["PROJECT_STAGE_AND_STATUS"] = 74] = "PROJECT_STAGE_AND_STATUS";
})(Field__ || (Field__ = {}));
const Field_ = defineEnumProperties(Field__, [0, 100, 55, 96, 97, 56, 64, 1, 2, 62, 63, 95, 3, 71, 72, 4, 5, 48, 92, 93, 94, 61, 6, 73, 7, 8, 9, 10, 11, 12, 13, 14, 42, 43, 36, 44, 45, 15, 50, 16, 17, 99, 18, 19, 20, 21, 37, 98, 78, 38, 77, 39, 79, 22, 23, 24, 47, 25, 26, 27, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 28, 29, 76, 30, 31, 32, 75, 91, 40, 41, 101, 34, 68, 35, 67, 90, 69, 46, 49, 51, 52, 53, 54, 57, 59, 60, 66, 65, 70, 74]);
const Field = extendEnum('Field', [["ID", 0], ["SYM", 100], ["APPROVED", 55], ["APPROVAL_REQUESTED", 96], ["APPROVAL_REQUESTED_BY", 97], ["NDA_REQUIRED", 56], ["STACKSOURCE_AGREEMENT_SIGNED", 64], ["USER_HAS_WRITE_PERMISSION", 1], ["OWNER", 2], ["SPONSOR_USERS", 62], ["SPONSOR_EMAIL_VERIFIED", 63], ["BROKER_USERS", 95], ["DRAFT", 3], ["STATUS", 71], ["LOST_REASON", 72], ["FINANCING_PURPOSE", 4], ["ASSET_TYPE", 5], ["SPONSOR_ROLE", 48], ["HOTEL_FLAG", 92], ["HOTEL_AVERAGE_DAILY_RATE", 93], ["HOTEL_AVERAGE_OCCUPANCY_RATE", 94], ["TENANCY", 61], ["NAME", 6], ["LABEL", 73], ["ADDRESS", 7], ["TOTAL_PROJECT_BUDGET", 8], ["NUMBER_OF_UNITS", 9], ["PROJECT_SUMMARY", 10], ["TOTAL_SIZE", 11], ["FOOTPRINT_SIZE", 12], ["LOT_SIZE", 13], ["ARCHITECT", 14], ["STRUCTURAL_ENGINEER", 42], ["CONSTRUCTION_MANAGER", 43], ["GENERAL_CONTRACTOR", 36], ["LEASING_REPRESENTATIVE", 44], ["PROPERTY_MANAGER", 45], ["COMPLETED_YEAR", 15], ["RENOVATED_YEAR", 50], ["FLOORS_ABOVE_GROUND", 16], ["FLOORS_BELOW_GROUND", 17], ["PARKING_SPOTS", 99], ["DESIGN_INFO", 18], ["PROPERTY_TITLE", 19], ["ASSET_NOTES", 20], ["LATEST_APPRAISAL_VALUE", 21], ["LATEST_APPRAISAL_DATE", 37], ["TARGET_CLOSE_DATE", 98], ["INVESTMENT_CATEGORY", 78], ["CURRENT_NET_OPERATING_INCOME", 38], ["PRO_FORMA_NET_OPERATING_INCOME", 77], ["CAP_RATE", 39], ["PRO_FORMA_CAP_RATE", 79], ["FINANCIAL_NOTES", 22], ["ACQUISITION_PRICE", 23], ["ACQUISITION_DATE", 24], ["ACQUISITION_STATUS", 47], ["PRINCIPAL_NEEDED", 25], ["LIEN_POSITION", 26], ["FINANCIAL_INSTRUMENTS", 27], ["AFFORDABILITY", 80], ["NUMBER_OF_FLOORS", 81], ["HAS_ELEVATOR", 82], ["UNIT_INFORMATION", 83], ["LIHTC_RATIO", 84], ["HUD_SELECTED_LTV", 85], ["FREDDIE_SBL_SELECTED_LTV", 86], ["FREDDIE_SBL_SELECTED_DSCR", 87], ["FREDDIE_SBL_INTEREST_ONLY_TERM", 88], ["FREDDIE_SBL_PREPAYMENT_PENALTY", 89], ["OFFER_EXPECTATIONS", 28], ["CAP_STACK", 29], ["CAP_STACK_TARGETS", 76], ["IMAGES", 30], ["ATTACHMENTS", 31], ["SPONSOR_PROFILE", 32], ["GUARANTORS", 75], ["SOLICITED_LENDERS", 91], ["NOTE", 40], ["PROJECT_SHARE_IDS", 41], ["PROJECT_SHARE_SYMS", 101], ["OFFERS", 34], ["OFFER_COUNT", 68], ["ACCEPTED_OFFER", 35], ["OFFER_ACCEPTED_TIME", 67], ["OFFER_UNDERWRITING_REQUESTED", 90], ["PROJECT_STAGE", 69], ["LEADING_OFFER", 46], ["CREATION_TIME", 49], ["OCCUPANCY", 51], ["USES", 52], ["DISCLAIMER", 53], ["DEMOGRAPHICS", 54], ["SHOW_CONCEALED_SHARES", 57], ["PROJECT_FEE", 59], ["CAPITAL_ADVISORS", 60], ["UNDERWRITING_APPROVAL_CHECKLIST_ENABLED", 66], ["UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS", 65], ["PRINCIPAL_NEEDED_COMPARABLE", 70], ["PROJECT_STAGE_AND_STATUS", 74]])(Field_);
class ProjectSort_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectSort field field.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return Field.fromStrictJson_(v, getFieldMap);
          }
          return Field.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectSort field direction.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return SortDirection.fromStrictJson_(v, getFieldMap);
          }
          return SortDirection.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectSort, 'ProjectSort', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectSort to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get field() {
    return this.getFieldById(1, 0);
  }
  setField(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteField() {
    return this.setValueFieldById(1, null, null);
  }
  get direction() {
    return this.getFieldById(2, 0);
  }
  setDirection(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteDirection() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$15(ProjectSort_, protoName, DEV_MODE ? 'ProjectSort' : '[Unknown]');
let ProjectSort = ProjectSort_;
(function (_ProjectSort) {})(ProjectSort || (ProjectSort = {}));
Object.defineProperties(ProjectSort, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "field": {
        value: 1
      },
      "direction": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "field"
      },
      '2': {
        value: "direction"
      }
    }))
  }
});
Object.defineProperties(ProjectSort.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectSort);
  Object.freeze(ProjectSort.prototype);
}
class ProjectFilters_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      7: 2,
      2: 2,
      3: 0,
      4: 0,
      5: 0,
      6: 7,
      8: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectFilters field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 7:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectFilters field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectFilters field search.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectFilters field project_status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return ProjectStatus.fromStrictJson_(v, getFieldMap);
            }
            return ProjectStatus.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 4:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectFilters field project_stage.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return ProjectStage.fromStrictJson_(v, getFieldMap);
            }
            return ProjectStage.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 5:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectFilters field financing_purpose.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return FinancingPurpose.fromStrictJson_(v, getFieldMap);
            }
            return FinancingPurpose.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectFilters field capital_advisor.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 8:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectFilters field capital_advisor_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectFilters, 'ProjectFilters', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectFilters to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get idList() {
    return this.getRepeatedFieldById(1);
  }
  hasId(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addId(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachId(values) {
    let T = this;
    for (const value of values) {
      T = T.addId(value);
    }
    return T;
  }
  setId(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeId(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearIdList() {
    return this.clearRepeatedFieldById(1);
  }
  setIdList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get symList() {
    return this.getRepeatedFieldById(7);
  }
  hasSym(value) {
    return this.repeatedFieldByIdHas(7, value);
  }
  addSym(value) {
    return this.addToRepeatedFieldById(7, value);
  }
  addEachSym(values) {
    let T = this;
    for (const value of values) {
      T = T.addSym(value);
    }
    return T;
  }
  setSym(index, value) {
    return this.setRepeatedFieldById(7, index, value);
  }
  removeSym(index) {
    return this.removeFromRepeatedFieldById(7, index);
  }
  clearSymList() {
    return this.clearRepeatedFieldById(7);
  }
  setSymList(values) {
    return this.overwriteRepeatedFieldById(7, values);
  }
  get search() {
    return this.getFieldById(2, "");
  }
  setSearch(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteSearch() {
    return this.setValueFieldById(2, null, null);
  }
  get projectStatusList() {
    return this.getRepeatedFieldById(3);
  }
  hasProjectStatus(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addProjectStatus(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachProjectStatus(values) {
    let T = this;
    for (const value of values) {
      T = T.addProjectStatus(value);
    }
    return T;
  }
  setProjectStatus(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeProjectStatus(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearProjectStatusList() {
    return this.clearRepeatedFieldById(3);
  }
  setProjectStatusList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
  get projectStageList() {
    return this.getRepeatedFieldById(4);
  }
  hasProjectStage(value) {
    return this.repeatedFieldByIdHas(4, value);
  }
  addProjectStage(value) {
    return this.addToRepeatedFieldById(4, value);
  }
  addEachProjectStage(values) {
    let T = this;
    for (const value of values) {
      T = T.addProjectStage(value);
    }
    return T;
  }
  setProjectStage(index, value) {
    return this.setRepeatedFieldById(4, index, value);
  }
  removeProjectStage(index) {
    return this.removeFromRepeatedFieldById(4, index);
  }
  clearProjectStageList() {
    return this.clearRepeatedFieldById(4);
  }
  setProjectStageList(values) {
    return this.overwriteRepeatedFieldById(4, values);
  }
  get financingPurposeList() {
    return this.getRepeatedFieldById(5);
  }
  hasFinancingPurpose(value) {
    return this.repeatedFieldByIdHas(5, value);
  }
  addFinancingPurpose(value) {
    return this.addToRepeatedFieldById(5, value);
  }
  addEachFinancingPurpose(values) {
    let T = this;
    for (const value of values) {
      T = T.addFinancingPurpose(value);
    }
    return T;
  }
  setFinancingPurpose(index, value) {
    return this.setRepeatedFieldById(5, index, value);
  }
  removeFinancingPurpose(index) {
    return this.removeFromRepeatedFieldById(5, index);
  }
  clearFinancingPurposeList() {
    return this.clearRepeatedFieldById(5);
  }
  setFinancingPurposeList(values) {
    return this.overwriteRepeatedFieldById(5, values);
  }
  get capitalAdvisorList() {
    return this.getRepeatedFieldById(6);
  }
  hasCapitalAdvisor(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addCapitalAdvisor(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachCapitalAdvisor(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapitalAdvisor(value);
    }
    return T;
  }
  setCapitalAdvisor(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removeCapitalAdvisor(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearCapitalAdvisorList() {
    return this.clearRepeatedFieldById(6);
  }
  setCapitalAdvisorList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
  get capitalAdvisorSymList() {
    return this.getRepeatedFieldById(8);
  }
  hasCapitalAdvisorSym(value) {
    return this.repeatedFieldByIdHas(8, value);
  }
  addCapitalAdvisorSym(value) {
    return this.addToRepeatedFieldById(8, value);
  }
  addEachCapitalAdvisorSym(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapitalAdvisorSym(value);
    }
    return T;
  }
  setCapitalAdvisorSym(index, value) {
    return this.setRepeatedFieldById(8, index, value);
  }
  removeCapitalAdvisorSym(index) {
    return this.removeFromRepeatedFieldById(8, index);
  }
  clearCapitalAdvisorSymList() {
    return this.clearRepeatedFieldById(8);
  }
  setCapitalAdvisorSymList(values) {
    return this.overwriteRepeatedFieldById(8, values);
  }
}
_defineProperty$15(ProjectFilters_, protoName, DEV_MODE ? 'ProjectFilters' : '[Unknown]');
let ProjectFilters = ProjectFilters_;
(function (_ProjectFilters) {})(ProjectFilters || (ProjectFilters = {}));
Object.defineProperties(ProjectFilters, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 7
      },
      "search": {
        value: 2
      },
      "project_status": {
        value: 3
      },
      "projectStatus": {
        value: 3
      },
      "project_stage": {
        value: 4
      },
      "projectStage": {
        value: 4
      },
      "financing_purpose": {
        value: 5
      },
      "financingPurpose": {
        value: 5
      },
      "capital_advisor": {
        value: 6
      },
      "capitalAdvisor": {
        value: 6
      },
      "capital_advisor_sym": {
        value: 8
      },
      "capitalAdvisorSym": {
        value: 8
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '7': {
        value: "sym"
      },
      '2': {
        value: "search"
      },
      '3': {
        value: "project_status"
      },
      '4': {
        value: "project_stage"
      },
      '5': {
        value: "financing_purpose"
      },
      '6': {
        value: "capital_advisor"
      },
      '8': {
        value: "capital_advisor_sym"
      }
    }))
  }
});
Object.defineProperties(ProjectFilters.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectFilters);
  Object.freeze(ProjectFilters.prototype);
}
class ProjectView_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectView field continuation_token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectView field limit.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectView, 'ProjectView', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectView to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get continuationToken() {
    return this.getFieldById(1, "");
  }
  setContinuationToken(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteContinuationToken() {
    return this.setValueFieldById(1, null, null);
  }
  get limit() {
    return this.getFieldById(2, 0);
  }
  setLimit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLimit() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$15(ProjectView_, protoName, DEV_MODE ? 'ProjectView' : '[Unknown]');
let ProjectView = ProjectView_;
(function (_ProjectView) {})(ProjectView || (ProjectView = {}));
Object.defineProperties(ProjectView, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "continuation_token": {
        value: 1
      },
      "continuationToken": {
        value: 1
      },
      "limit": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "continuation_token"
      },
      '2': {
        value: "limit"
      }
    }))
  }
});
Object.defineProperties(ProjectView.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectView);
  Object.freeze(ProjectView.prototype);
}
class GetProjects_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 7,
      3: SharedUser.shape,
      4: IpAddress.shape,
      5: 2,
      6: 0,
      11: ProjectView.shape,
      10: ProjectSort.shape,
      12: ProjectFilters.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message GetProjects field authentication_token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message GetProjects field user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SharedUser);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message GetProjects field shared_user.
                Expected type SharedUser.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SharedUser', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(IpAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message GetProjects field ip_address.
                Expected type IpAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'IpAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message GetProjects field user_agent.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message GetProjects field field.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return Field.fromStrictJson_(v, getFieldMap);
            }
            return Field.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectView);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message GetProjects field view.
                Expected type ProjectView.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectView', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectSort);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetProjects field sort.
                Expected type ProjectSort.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectSort', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectFilters);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message GetProjects field filters.
                Expected type ProjectFilters.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectFilters', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(GetProjects, 'GetProjects', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message GetProjects to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get authenticationToken() {
    return this.getFieldById(1, "");
  }
  setAuthenticationToken(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteAuthenticationToken() {
    return this.setValueFieldById(1, null, null);
  }
  get userId() {
    return this.getFieldById(2, 0);
  }
  setUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUserId() {
    return this.setValueFieldById(2, null, null);
  }
  get sharedUser() {
    return this.getFieldById(3, SharedUser.getDefault());
  }
  setSharedUser(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteSharedUser() {
    return this.setValueFieldById(3, null, null);
  }
  get ipAddress() {
    return this.getFieldById(4, IpAddress.getDefault());
  }
  setIpAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteIpAddress() {
    return this.setValueFieldById(4, null, null);
  }
  get userAgent() {
    return this.getFieldById(5, "");
  }
  setUserAgent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteUserAgent() {
    return this.setValueFieldById(5, null, null);
  }
  get fieldList() {
    return this.getRepeatedFieldById(6);
  }
  hasField(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addField(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachField(values) {
    let T = this;
    for (const value of values) {
      T = T.addField(value);
    }
    return T;
  }
  setField(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removeField(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearFieldList() {
    return this.clearRepeatedFieldById(6);
  }
  setFieldList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
  get view() {
    return this.getFieldById(11, ProjectView.getDefault());
  }
  setView(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteView() {
    return this.setValueFieldById(11, null, null);
  }
  get sortList() {
    return this.getRepeatedFieldById(10);
  }
  hasSort(value) {
    return this.repeatedFieldByIdHas(10, value);
  }
  addSort(value) {
    return this.addToRepeatedFieldById(10, value);
  }
  addEachSort(values) {
    let T = this;
    for (const value of values) {
      T = T.addSort(value);
    }
    return T;
  }
  setSort(index, value) {
    return this.setRepeatedFieldById(10, index, value);
  }
  removeSort(index) {
    return this.removeFromRepeatedFieldById(10, index);
  }
  clearSortList() {
    return this.clearRepeatedFieldById(10);
  }
  setSortList(values) {
    return this.overwriteRepeatedFieldById(10, values);
  }
  get filters() {
    return this.getFieldById(12, ProjectFilters.getDefault());
  }
  setFilters(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteFilters() {
    return this.setValueFieldById(12, null, null);
  }
}
_defineProperty$15(GetProjects_, protoName, DEV_MODE ? 'GetProjects' : '[Unknown]');
let GetProjects = GetProjects_;
(function (_GetProjects) {})(GetProjects || (GetProjects = {}));
Object.defineProperties(GetProjects, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "authentication_token": {
        value: 1
      },
      "authenticationToken": {
        value: 1
      },
      "user_id": {
        value: 2
      },
      "userId": {
        value: 2
      },
      "shared_user": {
        value: 3
      },
      "sharedUser": {
        value: 3
      },
      "ip_address": {
        value: 4
      },
      "ipAddress": {
        value: 4
      },
      "user_agent": {
        value: 5
      },
      "userAgent": {
        value: 5
      },
      "field": {
        value: 6
      },
      "view": {
        value: 11
      },
      "sort": {
        value: 10
      },
      "filters": {
        value: 12
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "authentication_token"
      },
      '2': {
        value: "user_id"
      },
      '3': {
        value: "shared_user"
      },
      '4': {
        value: "ip_address"
      },
      '5': {
        value: "user_agent"
      },
      '6': {
        value: "field"
      },
      '11': {
        value: "view"
      },
      '10': {
        value: "sort"
      },
      '12': {
        value: "filters"
      }
    }))
  }
});
Object.defineProperties(GetProjects.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: SharedUser.prototype,
      4: IpAddress.prototype,
      11: ProjectView.prototype,
      10: ProjectSort.prototype,
      12: ProjectFilters.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(GetProjects);
  Object.freeze(GetProjects.prototype);
}

function _defineProperty$14(obj, key, value) { key = _toPropertyKey$14(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$14(arg) { var key = _toPrimitive$14(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$14(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Projects_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 1,
      2: 7,
      3: ProjectSort.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message Projects field table_view.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Projects field capital_advisor.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectSort);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message Projects field sort.
                Expected type ProjectSort.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectSort', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Projects, 'Projects', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Projects to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get tableView() {
    return this.getFieldById(1, false);
  }
  setTableView(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteTableView() {
    return this.setValueFieldById(1, null, null);
  }
  get capitalAdvisorList() {
    return this.getRepeatedFieldById(2);
  }
  hasCapitalAdvisor(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addCapitalAdvisor(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachCapitalAdvisor(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapitalAdvisor(value);
    }
    return T;
  }
  setCapitalAdvisor(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeCapitalAdvisor(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearCapitalAdvisorList() {
    return this.clearRepeatedFieldById(2);
  }
  setCapitalAdvisorList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
  get sortList() {
    return this.getRepeatedFieldById(3);
  }
  hasSort(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addSort(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachSort(values) {
    let T = this;
    for (const value of values) {
      T = T.addSort(value);
    }
    return T;
  }
  setSort(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeSort(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearSortList() {
    return this.clearRepeatedFieldById(3);
  }
  setSortList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
}
_defineProperty$14(Projects_, protoName, DEV_MODE ? 'Projects' : '[Unknown]');
let Projects = Projects_;
(function (_Projects) {})(Projects || (Projects = {}));
Object.defineProperties(Projects, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "table_view": {
        value: 1
      },
      "tableView": {
        value: 1
      },
      "capital_advisor": {
        value: 2
      },
      "capitalAdvisor": {
        value: 2
      },
      "sort": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "table_view"
      },
      '2': {
        value: "capital_advisor"
      },
      '3': {
        value: "sort"
      }
    }))
  }
});
Object.defineProperties(Projects.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: ProjectSort.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Projects);
  Object.freeze(Projects.prototype);
}
class Pages_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Projects.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Projects);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Pages field projects.
                Expected type Projects.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Projects', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Pages, 'Pages', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Pages to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get projects() {
    return this.getFieldById(1, Projects.getDefault());
  }
  setProjects(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteProjects() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$14(Pages_, protoName, DEV_MODE ? 'Pages' : '[Unknown]');
let Pages = Pages_;
(function (_Pages) {})(Pages || (Pages = {}));
Object.defineProperties(Pages, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "projects": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "projects"
      }
    }))
  }
});
Object.defineProperties(Pages.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Projects.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Pages);
  Object.freeze(Pages.prototype);
}
class UserSettings_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      2: 7,
      1: Pages.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UserSettings field version.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Pages);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserSettings field pages.
                Expected type Pages.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Pages', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UserSettings, 'UserSettings', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UserSettings to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get version() {
    return this.getFieldById(2, 0);
  }
  setVersion(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteVersion() {
    return this.setValueFieldById(2, null, null);
  }
  get pages() {
    return this.getFieldById(1, Pages.getDefault());
  }
  setPages(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deletePages() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$14(UserSettings_, protoName, DEV_MODE ? 'UserSettings' : '[Unknown]');
let UserSettings = UserSettings_;
(function (_UserSettings) {})(UserSettings || (UserSettings = {}));
Object.defineProperties(UserSettings, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "version": {
        value: 2
      },
      "pages": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '2': {
        value: "version"
      },
      '1': {
        value: "pages"
      }
    }))
  }
});
Object.defineProperties(UserSettings.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Pages.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UserSettings);
  Object.freeze(UserSettings.prototype);
}

// tslint:disable-next-line:no-unused-variable
var UsStateShort__;
(function (UsStateShort__) {
  UsStateShort__[UsStateShort__["AK"] = 0] = "AK";
  UsStateShort__[UsStateShort__["AL"] = 1] = "AL";
  UsStateShort__[UsStateShort__["AR"] = 2] = "AR";
  UsStateShort__[UsStateShort__["AZ"] = 3] = "AZ";
  UsStateShort__[UsStateShort__["CA"] = 4] = "CA";
  UsStateShort__[UsStateShort__["CO"] = 5] = "CO";
  UsStateShort__[UsStateShort__["CT"] = 6] = "CT";
  UsStateShort__[UsStateShort__["DC"] = 7] = "DC";
  UsStateShort__[UsStateShort__["DE"] = 8] = "DE";
  UsStateShort__[UsStateShort__["FL"] = 9] = "FL";
  UsStateShort__[UsStateShort__["GA"] = 10] = "GA";
  UsStateShort__[UsStateShort__["HI"] = 11] = "HI";
  UsStateShort__[UsStateShort__["IA"] = 12] = "IA";
  UsStateShort__[UsStateShort__["ID"] = 13] = "ID";
  UsStateShort__[UsStateShort__["IL"] = 14] = "IL";
  UsStateShort__[UsStateShort__["IN"] = 15] = "IN";
  UsStateShort__[UsStateShort__["KS"] = 16] = "KS";
  UsStateShort__[UsStateShort__["KY"] = 17] = "KY";
  UsStateShort__[UsStateShort__["LA"] = 18] = "LA";
  UsStateShort__[UsStateShort__["MA"] = 19] = "MA";
  UsStateShort__[UsStateShort__["MD"] = 20] = "MD";
  UsStateShort__[UsStateShort__["ME"] = 21] = "ME";
  UsStateShort__[UsStateShort__["MI"] = 22] = "MI";
  UsStateShort__[UsStateShort__["MN"] = 23] = "MN";
  UsStateShort__[UsStateShort__["MO"] = 24] = "MO";
  UsStateShort__[UsStateShort__["MS"] = 25] = "MS";
  UsStateShort__[UsStateShort__["MT"] = 26] = "MT";
  UsStateShort__[UsStateShort__["NC"] = 27] = "NC";
  UsStateShort__[UsStateShort__["ND"] = 28] = "ND";
  UsStateShort__[UsStateShort__["NE"] = 29] = "NE";
  UsStateShort__[UsStateShort__["NH"] = 30] = "NH";
  UsStateShort__[UsStateShort__["NJ"] = 31] = "NJ";
  UsStateShort__[UsStateShort__["NM"] = 32] = "NM";
  UsStateShort__[UsStateShort__["NV"] = 33] = "NV";
  UsStateShort__[UsStateShort__["NY"] = 34] = "NY";
  UsStateShort__[UsStateShort__["OH"] = 35] = "OH";
  UsStateShort__[UsStateShort__["OK"] = 36] = "OK";
  UsStateShort__[UsStateShort__["OR"] = 37] = "OR";
  UsStateShort__[UsStateShort__["PA"] = 38] = "PA";
  UsStateShort__[UsStateShort__["RI"] = 39] = "RI";
  UsStateShort__[UsStateShort__["SC"] = 40] = "SC";
  UsStateShort__[UsStateShort__["SD"] = 41] = "SD";
  UsStateShort__[UsStateShort__["TN"] = 42] = "TN";
  UsStateShort__[UsStateShort__["TX"] = 43] = "TX";
  UsStateShort__[UsStateShort__["UT"] = 44] = "UT";
  UsStateShort__[UsStateShort__["VA"] = 45] = "VA";
  UsStateShort__[UsStateShort__["VT"] = 46] = "VT";
  UsStateShort__[UsStateShort__["WA"] = 47] = "WA";
  UsStateShort__[UsStateShort__["WI"] = 48] = "WI";
  UsStateShort__[UsStateShort__["WV"] = 49] = "WV";
  UsStateShort__[UsStateShort__["WY"] = 50] = "WY";
  UsStateShort__[UsStateShort__["PR"] = 51] = "PR";
})(UsStateShort__ || (UsStateShort__ = {}));
const UsStateShort_ = defineEnumProperties(UsStateShort__, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51]);
const UsStateShort$1 = extendEnum('UsStateShort', [["AK", 0], ["AL", 1], ["AR", 2], ["AZ", 3], ["CA", 4], ["CO", 5], ["CT", 6], ["DC", 7], ["DE", 8], ["FL", 9], ["GA", 10], ["HI", 11], ["IA", 12], ["ID", 13], ["IL", 14], ["IN", 15], ["KS", 16], ["KY", 17], ["LA", 18], ["MA", 19], ["MD", 20], ["ME", 21], ["MI", 22], ["MN", 23], ["MO", 24], ["MS", 25], ["MT", 26], ["NC", 27], ["ND", 28], ["NE", 29], ["NH", 30], ["NJ", 31], ["NM", 32], ["NV", 33], ["NY", 34], ["OH", 35], ["OK", 36], ["OR", 37], ["PA", 38], ["RI", 39], ["SC", 40], ["SD", 41], ["TN", 42], ["TX", 43], ["UT", 44], ["VA", 45], ["VT", 46], ["WA", 47], ["WI", 48], ["WV", 49], ["WY", 50], ["PR", 51]])(UsStateShort_);

function _defineProperty$13(obj, key, value) { key = _toPropertyKey$13(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$13(arg) { var key = _toPrimitive$13(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$13(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Some {
  constructor(value) {
    _defineProperty$13(this, "value", void 0);
    this.value = value;
  }
  expect() {
    return this.value;
  }
  isNone() {
    return false;
  }
  isSome() {
    return true;
  }
  match({
    some
  }) {
    if (some) {
      some(this.value);
    }
  }
  unwrap() {
    return this.value;
  }
}
class None {
  expect(message) {
    if (typeof message === 'string') {
      throw new Error(message);
    } else {
      throw message;
    }
  }
  isNone() {
    return true;
  }
  isSome() {
    return false;
  }
  match({
    none
  }) {
    if (none) {
      none();
    }
  }
  unwrap() {
    throw new Error('unwrap: Expected Some, was None');
  }
}
const NONE = new None();
const Option = {
  Some: value => new Some(value),
  None: () => NONE
};

const UsStateShort = {
  ...UsStateShort$1,
  fromZipCode(rawZipCode) {
    if (!/^\d{5}$/.test(rawZipCode)) {
      return Option.None();
    }
    const zipCode = parseInt(rawZipCode, 10);
    if (zipCode >= 35000 && zipCode <= 36999) {
      return Option.Some(UsStateShort.AL);
    } else if (zipCode >= 99500 && zipCode <= 99999) {
      return Option.Some(UsStateShort.AK);
    } else if (zipCode >= 85000 && zipCode <= 86999) {
      return Option.Some(UsStateShort.AZ);
    } else if (zipCode >= 71600 && zipCode <= 72999) {
      return Option.Some(UsStateShort.AR);
    } else if (zipCode >= 90000 && zipCode <= 96699) {
      return Option.Some(UsStateShort.CA);
    } else if (zipCode >= 80000 && zipCode <= 81999) {
      return Option.Some(UsStateShort.CO);
    } else if (zipCode >= 6000 && zipCode <= 6389 || zipCode >= 6391 && zipCode <= 6999) {
      return Option.Some(UsStateShort.CT);
    } else if (zipCode >= 19700 && zipCode <= 19999) {
      return Option.Some(UsStateShort.DE);
    } else if (zipCode >= 32000 && zipCode <= 34999) {
      return Option.Some(UsStateShort.FL);
    } else if (zipCode >= 30000 && zipCode <= 31999 || zipCode >= 39800 && zipCode <= 39999) {
      return Option.Some(UsStateShort.GA);
    } else if (zipCode >= 96700 && zipCode <= 96999) {
      return Option.Some(UsStateShort.HI);
    } else if (zipCode >= 83200 && zipCode <= 83999 && zipCode !== 83414) {
      return Option.Some(UsStateShort.ID);
    } else if (zipCode >= 60000 && zipCode <= 62999) {
      return Option.Some(UsStateShort.IL);
    } else if (zipCode >= 46000 && zipCode <= 47999) {
      return Option.Some(UsStateShort.IN);
    } else if (zipCode >= 50000 && zipCode <= 52999) {
      return Option.Some(UsStateShort.IA);
    } else if (zipCode >= 66000 && zipCode <= 67999) {
      return Option.Some(UsStateShort.KS);
    } else if (zipCode >= 40000 && zipCode <= 42999) {
      return Option.Some(UsStateShort.KY);
    } else if (zipCode >= 70000 && zipCode <= 71599) {
      return Option.Some(UsStateShort.LA);
    } else if (zipCode >= 3900 && zipCode <= 4999) {
      return Option.Some(UsStateShort.ME);
    } else if (zipCode >= 20600 && zipCode <= 21999) {
      return Option.Some(UsStateShort.MD);
    } else if (zipCode >= 1000 && zipCode <= 2799 || zipCode === 5501 || zipCode === 5544) {
      return Option.Some(UsStateShort.MA);
    } else if (zipCode >= 48000 && zipCode <= 49999) {
      return Option.Some(UsStateShort.MI);
    } else if (zipCode >= 55000 && zipCode <= 56899) {
      return Option.Some(UsStateShort.MN);
    } else if (zipCode >= 38600 && zipCode <= 39999) {
      return Option.Some(UsStateShort.MS);
    } else if (zipCode >= 63000 && zipCode <= 65999) {
      return Option.Some(UsStateShort.MO);
    } else if (zipCode >= 59000 && zipCode <= 59999) {
      return Option.Some(UsStateShort.MT);
    } else if (zipCode >= 27000 && zipCode <= 28999) {
      return Option.Some(UsStateShort.NC);
    } else if (zipCode >= 58000 && zipCode <= 58999) {
      return Option.Some(UsStateShort.ND);
    } else if (zipCode >= 68000 && zipCode <= 69999) {
      return Option.Some(UsStateShort.NE);
    } else if (zipCode >= 88900 && zipCode <= 89999) {
      return Option.Some(UsStateShort.NV);
    } else if (zipCode >= 3000 && zipCode <= 3899) {
      return Option.Some(UsStateShort.NH);
    } else if (zipCode >= 7000 && zipCode <= 8999) {
      return Option.Some(UsStateShort.NJ);
    } else if (zipCode >= 87000 && zipCode <= 88499) {
      return Option.Some(UsStateShort.NM);
    } else if (zipCode >= 10000 && zipCode <= 14999 || zipCode === 6390 || zipCode === 501 || zipCode === 544) {
      return Option.Some(UsStateShort.NY);
    } else if (zipCode >= 43000 && zipCode <= 45999) {
      return Option.Some(UsStateShort.OH);
    } else if (zipCode >= 73000 && zipCode <= 73199 || zipCode >= 73400 && zipCode <= 74999) {
      return Option.Some(UsStateShort.OK);
    } else if (zipCode >= 97000 && zipCode <= 97999) {
      return Option.Some(UsStateShort.OR);
    } else if (zipCode >= 15000 && zipCode <= 19699) {
      return Option.Some(UsStateShort.PA);
    } else if (zipCode >= 300 && zipCode <= 999) {
      return Option.Some(UsStateShort.PR);
    } else if (zipCode >= 2800 && zipCode <= 2999) {
      return Option.Some(UsStateShort.RI);
    } else if (zipCode >= 29000 && zipCode <= 29999) {
      return Option.Some(UsStateShort.SC);
    } else if (zipCode >= 57000 && zipCode <= 57999) {
      return Option.Some(UsStateShort.SD);
    } else if (zipCode >= 37000 && zipCode <= 38599) {
      return Option.Some(UsStateShort.TN);
    } else if (zipCode >= 75000 && zipCode <= 79999 || zipCode >= 73301 && zipCode <= 73399 || zipCode >= 88500 && zipCode <= 88599) {
      return Option.Some(UsStateShort.TX);
    } else if (zipCode >= 84000 && zipCode <= 84999) {
      return Option.Some(UsStateShort.UT);
    } else if (zipCode >= 5000 && zipCode <= 5999) {
      return Option.Some(UsStateShort.VT);
    } else if (zipCode >= 20100 && zipCode <= 20199 || zipCode >= 22000 && zipCode <= 24699 || zipCode === 20598) {
      return Option.Some(UsStateShort.VA);
    } else if (zipCode >= 20000 && zipCode <= 20099 || zipCode >= 20200 && zipCode <= 20599 || zipCode >= 56900 && zipCode <= 56999) {
      return Option.Some(UsStateShort.DC);
    } else if (zipCode >= 98000 && zipCode <= 99499) {
      return Option.Some(UsStateShort.WA);
    } else if (zipCode >= 24700 && zipCode <= 26999) {
      return Option.Some(UsStateShort.WV);
    } else if (zipCode >= 53000 && zipCode <= 54999) {
      return Option.Some(UsStateShort.WI);
    } else if (zipCode >= 82000 && zipCode <= 83199 || zipCode === 83414) {
      return Option.Some(UsStateShort.WY);
    }
    return Option.None();
  }
};

registerCanonical(UsStateShort$1, UsStateShort);

function _defineProperty$12(obj, key, value) { key = _toPropertyKey$12(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$12(arg) { var key = _toPrimitive$12(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$12(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var DurationUnit__;
(function (DurationUnit__) {
  DurationUnit__[DurationUnit__["YEAR"] = 0] = "YEAR";
  DurationUnit__[DurationUnit__["MONTH"] = 1] = "MONTH";
  DurationUnit__[DurationUnit__["WEEK"] = 2] = "WEEK";
  DurationUnit__[DurationUnit__["DAY"] = 3] = "DAY";
  DurationUnit__[DurationUnit__["HOUR"] = 4] = "HOUR";
  DurationUnit__[DurationUnit__["MINUTE"] = 5] = "MINUTE";
  DurationUnit__[DurationUnit__["SECOND"] = 6] = "SECOND";
  DurationUnit__[DurationUnit__["MILLISECOND"] = 7] = "MILLISECOND";
})(DurationUnit__ || (DurationUnit__ = {}));
const DurationUnit_ = defineEnumProperties(DurationUnit__, [0, 1, 2, 3, 4, 5, 6, 7]);
const DurationUnit = extendEnum('DurationUnit', [["YEAR", 0], ["MONTH", 1], ["WEEK", 2], ["DAY", 3], ["HOUR", 4], ["MINUTE", 5], ["SECOND", 6], ["MILLISECOND", 7]])(DurationUnit_);
class Duration_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message Duration field unit.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return DurationUnit.fromStrictJson_(v, getFieldMap);
          }
          return DurationUnit.from(value);
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Duration field length.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Duration$1, 'Duration', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Duration to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get unit() {
    return this.getFieldById(1, 0);
  }
  setUnit(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUnit() {
    return this.setValueFieldById(1, null, null);
  }
  get length() {
    return this.getFieldById(2, 0);
  }
  setLength(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLength() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$12(Duration_, protoName, DEV_MODE ? 'Duration' : '[Unknown]');
let Duration$1 = Duration_;
(function (_Duration) {})(Duration$1 || (Duration$1 = {}));
Object.defineProperties(Duration$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "unit": {
        value: 1
      },
      "length": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "unit"
      },
      '2': {
        value: "length"
      }
    }))
  }
});
Object.defineProperties(Duration$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Duration$1);
  Object.freeze(Duration$1.prototype);
}

const UNIT_HEIRARCHY = [DurationUnit.MILLISECOND, DurationUnit.SECOND, DurationUnit.MINUTE, DurationUnit.HOUR, DurationUnit.DAY, DurationUnit.WEEK, DurationUnit.MONTH, DurationUnit.YEAR];
const conversionKey = raw => raw.map(u => DurationUnit.toString(u)).join(', ');
const div$1 = denom => numer => Math.floor(numer / denom);
const mult = x => y => Math.floor(x * y);
const UNIT_CONVERSION = new Map([/* eslint-disable max-len */
[[DurationUnit.MILLISECOND, DurationUnit.MILLISECOND], identity], [[DurationUnit.MILLISECOND, DurationUnit.SECOND], div$1(1000)], [[DurationUnit.MILLISECOND, DurationUnit.MINUTE], div$1(1000 * 60)], [[DurationUnit.MILLISECOND, DurationUnit.HOUR], div$1(1000 * 60 * 60)], [[DurationUnit.MILLISECOND, DurationUnit.DAY], div$1(1000 * 60 * 60 * 24)], [[DurationUnit.MILLISECOND, DurationUnit.WEEK], div$1(1000 * 60 * 60 * 24 * 7)], [[DurationUnit.MILLISECOND, DurationUnit.MONTH], div$1(1000 * 60 * 60 * 24 * 30)], [[DurationUnit.MILLISECOND, DurationUnit.YEAR], div$1(1000 * 60 * 60 * 24 * 365)], [[DurationUnit.SECOND, DurationUnit.MILLISECOND], mult(1000)], [[DurationUnit.SECOND, DurationUnit.SECOND], identity], [[DurationUnit.SECOND, DurationUnit.MINUTE], div$1(60)], [[DurationUnit.SECOND, DurationUnit.HOUR], div$1(60 * 60)], [[DurationUnit.SECOND, DurationUnit.DAY], div$1(60 * 60 * 24)], [[DurationUnit.SECOND, DurationUnit.WEEK], div$1(60 * 60 * 24 * 7)], [[DurationUnit.SECOND, DurationUnit.MONTH], div$1(60 * 60 * 24 * 30)], [[DurationUnit.SECOND, DurationUnit.YEAR], div$1(60 * 60 * 24 * 365)], [[DurationUnit.MINUTE, DurationUnit.MILLISECOND], mult(1000 * 60)], [[DurationUnit.MINUTE, DurationUnit.SECOND], mult(60)], [[DurationUnit.MINUTE, DurationUnit.MINUTE], identity], [[DurationUnit.MINUTE, DurationUnit.HOUR], div$1(60)], [[DurationUnit.MINUTE, DurationUnit.DAY], div$1(60 * 24)], [[DurationUnit.MINUTE, DurationUnit.WEEK], div$1(60 * 24 * 7)], [[DurationUnit.MINUTE, DurationUnit.MONTH], div$1(60 * 24 * 30)], [[DurationUnit.MINUTE, DurationUnit.YEAR], div$1(60 * 24 * 365)], [[DurationUnit.HOUR, DurationUnit.MILLISECOND], mult(1000 * 60 * 60)], [[DurationUnit.HOUR, DurationUnit.SECOND], mult(60 * 60)], [[DurationUnit.HOUR, DurationUnit.MINUTE], mult(60)], [[DurationUnit.HOUR, DurationUnit.HOUR], identity], [[DurationUnit.HOUR, DurationUnit.DAY], div$1(24)], [[DurationUnit.HOUR, DurationUnit.WEEK], div$1(24 * 7)], [[DurationUnit.HOUR, DurationUnit.MONTH], div$1(24 * 30)], [[DurationUnit.HOUR, DurationUnit.YEAR], div$1(24 * 365)], [[DurationUnit.DAY, DurationUnit.MILLISECOND], mult(1000 * 60 * 60 * 24)], [[DurationUnit.DAY, DurationUnit.SECOND], mult(60 * 60 * 24)], [[DurationUnit.DAY, DurationUnit.MINUTE], mult(60 * 24)], [[DurationUnit.DAY, DurationUnit.HOUR], mult(24)], [[DurationUnit.DAY, DurationUnit.DAY], identity], [[DurationUnit.DAY, DurationUnit.WEEK], div$1(7)], [[DurationUnit.DAY, DurationUnit.MONTH], div$1(30)], [[DurationUnit.DAY, DurationUnit.YEAR], div$1(365)], [[DurationUnit.WEEK, DurationUnit.MILLISECOND], mult(1000 * 60 * 60 * 24 * 7)], [[DurationUnit.WEEK, DurationUnit.SECOND], mult(60 * 60 * 24 * 7)], [[DurationUnit.WEEK, DurationUnit.MINUTE], mult(60 * 24 * 7)], [[DurationUnit.WEEK, DurationUnit.HOUR], mult(24 * 7)], [[DurationUnit.WEEK, DurationUnit.DAY], mult(7)], [[DurationUnit.WEEK, DurationUnit.WEEK], identity], [[DurationUnit.WEEK, DurationUnit.MONTH], div$1(4)], [[DurationUnit.WEEK, DurationUnit.YEAR], div$1(52)], [[DurationUnit.MONTH, DurationUnit.MILLISECOND], mult(1000 * 60 * 60 * 24 * 30)], [[DurationUnit.MONTH, DurationUnit.SECOND], mult(60 * 60 * 24 * 30)], [[DurationUnit.MONTH, DurationUnit.MINUTE], mult(60 * 24 * 30)], [[DurationUnit.MONTH, DurationUnit.HOUR], mult(24 * 30)], [[DurationUnit.MONTH, DurationUnit.DAY], mult(30)], [[DurationUnit.MONTH, DurationUnit.WEEK], mult(4)], [[DurationUnit.MONTH, DurationUnit.MONTH], identity], [[DurationUnit.MONTH, DurationUnit.YEAR], div$1(12)], [[DurationUnit.YEAR, DurationUnit.MILLISECOND], mult(1000 * 60 * 60 * 24 * 365)], [[DurationUnit.YEAR, DurationUnit.SECOND], mult(60 * 60 * 24 * 365)], [[DurationUnit.YEAR, DurationUnit.MINUTE], mult(60 * 24 * 365)], [[DurationUnit.YEAR, DurationUnit.HOUR], mult(24 * 365)], [[DurationUnit.YEAR, DurationUnit.DAY], mult(365)], [[DurationUnit.YEAR, DurationUnit.WEEK], mult(52)], [[DurationUnit.YEAR, DurationUnit.MONTH], mult(12)], [[DurationUnit.YEAR, DurationUnit.YEAR], identity]
/* eslint-enable max-len */].map(([k, v]) => [conversionKey(k), v]));
class Duration extends Duration_ {
  static ofDays(days) {
    return new Duration().setUnit(DurationUnit.DAY).setLength(days);
  }
  static ofHours(hours) {
    return new Duration().setUnit(DurationUnit.HOUR).setLength(hours);
  }
  static ofMinutes(minutes) {
    return new Duration().setUnit(DurationUnit.MINUTE).setLength(minutes);
  }
  withUnit(unit) {
    const key = conversionKey([this.unit, unit]);
    if (!UNIT_CONVERSION.has(key)) {
      assert.fail(emsg`Could not convert Duration Unit ${this.unit} to ${unit}`);
      return this;
    }
    return new Duration().setUnit(unit).setLength(UNIT_CONVERSION.get(key)(this.length));
  }
  compare(other) {
    const selfIndex = UNIT_HEIRARCHY.indexOf(this.unit);
    const otherIndex = UNIT_HEIRARCHY.indexOf(other.unit);
    const selfLength = selfIndex > otherIndex ? this.withUnit(other.unit).length : this.length;
    const otherLength = selfIndex > otherIndex ? other.length : other.withUnit(this.unit).length;
    return selfLength - otherLength;
  }
  greaterThan(other) {
    return this.compare(other) > 0;
  }
  isZero() {
    return this.length === 0;
  }
}

registerCanonical(DurationUnit, DurationUnit);
registerCanonical(Duration_, Duration);

function _defineProperty$11(obj, key, value) { key = _toPropertyKey$11(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$11(arg) { var key = _toPrimitive$11(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$11(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class DurationRange_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Duration.shape,
      2: Duration.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Duration);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message DurationRange field initial.
                Expected type Duration.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Duration', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Duration);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message DurationRange field final.
                Expected type Duration.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Duration', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(DurationRange, 'DurationRange', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message DurationRange to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get initial() {
    return this.getFieldById(1, Duration.getDefault());
  }
  setInitial(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteInitial() {
    return this.setValueFieldById(1, null, null);
  }
  get final() {
    return this.getFieldById(2, Duration.getDefault());
  }
  setFinal(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteFinal() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$11(DurationRange_, protoName, DEV_MODE ? 'DurationRange' : '[Unknown]');
let DurationRange = DurationRange_;
(function (_DurationRange) {})(DurationRange || (DurationRange = {}));
Object.defineProperties(DurationRange, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "initial": {
        value: 1
      },
      "final": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "initial"
      },
      '2': {
        value: "final"
      }
    }))
  }
});
Object.defineProperties(DurationRange.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Duration.prototype,
      2: Duration.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(DurationRange);
  Object.freeze(DurationRange.prototype);
}

function _defineProperty$10(obj, key, value) { key = _toPropertyKey$10(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$10(arg) { var key = _toPrimitive$10(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$10(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Uuid_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 7,
      3: 7,
      4: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Uuid field a.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Uuid field b.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Uuid field c.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Uuid field d.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Uuid$1, 'Uuid', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Uuid to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get a() {
    return this.getFieldById(1, 0);
  }
  setA(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteA() {
    return this.setValueFieldById(1, null, null);
  }
  get b() {
    return this.getFieldById(2, 0);
  }
  setB(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteB() {
    return this.setValueFieldById(2, null, null);
  }
  get c() {
    return this.getFieldById(3, 0);
  }
  setC(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteC() {
    return this.setValueFieldById(3, null, null);
  }
  get d() {
    return this.getFieldById(4, 0);
  }
  setD(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteD() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$10(Uuid_, protoName, DEV_MODE ? 'Uuid' : '[Unknown]');
let Uuid$1 = Uuid_;
(function (_Uuid) {})(Uuid$1 || (Uuid$1 = {}));
Object.defineProperties(Uuid$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "a": {
        value: 1
      },
      "b": {
        value: 2
      },
      "c": {
        value: 3
      },
      "d": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "a"
      },
      '2': {
        value: "b"
      },
      '3': {
        value: "c"
      },
      '4': {
        value: "d"
      }
    }))
  }
});
Object.defineProperties(Uuid$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Uuid$1);
  Object.freeze(Uuid$1.prototype);
}

function reverseArray(r) {
  return r.slice().reverse();
}

function convertBase(from, to, source) {
  let acc = [1];
  const result = [];
  for (const sourceDigit of reverseArray(trimLeadingZeros(source))) {
    runtime.checkAsserts() ? runtime.ensure(Number.isInteger(sourceDigit), null) : undefined;
    runtime.checkAsserts() ? runtime.ensure(sourceDigit >= 0 && sourceDigit < from, null) : undefined;
    for (let from$3496 = 0, to$3497 = acc.length, counter$3498 = from$3496; from$3496 < to$3497 ? counter$3498 < to$3497 : counter$3498 > to$3497; from$3496 < to$3497 ? counter$3498++ : counter$3498--) {
      let $3503, $3504, $3505, $3506;
      const i = counter$3498;
      incrementDigit(result, i, ($3503 = runtime.getComputed(acc, i), $3504 = typeof $3503, (runtime.checkAsserts() ? runtime.ensure(typeof $3503 == 'number', function () {
        return `Expected type "number" but found "${$3504}" for (${$3503}).`;
      }) : undefined, $3503)) * ($3505 = sourceDigit, $3506 = typeof $3505, (runtime.checkAsserts() ? runtime.ensure(typeof $3505 == 'number', function () {
        return `Expected type "number" but found "${$3506}" for (${$3505}).`;
      }) : undefined, $3505)));
      normalize(i, to, result);
    }
    normalize(0, to, acc, from);
  }
  return result.reverse();
}
function normalize(pos, to, digits, multiplier) {
  if (multiplier === undefined) multiplier = 1;
  let rem = 0;
  let i = 0;
  while (i < digits.length || rem > 0) {
    incrementDigit(digits, i, rem, multiplier);
    if (runtime.getComputed(digits, i) >= to) {
      let res;
      [rem, res] = div(runtime.getComputed(digits, i), to);
      runtime.setComputed(digits, i, res);
    } else {
      rem = 0;
    }
    i++;
  }
}
function div(dividend, divisor) {
  let $3511, $3512, $3513, $3514, $3515, $3516, $3517, $3518;
  const quotient = Math.floor(($3511 = dividend, $3512 = typeof $3511, (runtime.checkAsserts() ? runtime.ensure(typeof $3511 == 'number', function () {
    return `Expected type "number" but found "${$3512}" for (${$3511}).`;
  }) : undefined, $3511)) / ($3513 = divisor, $3514 = typeof $3513, (runtime.checkAsserts() ? runtime.ensure(typeof $3513 == 'number', function () {
    return `Expected type "number" but found "${$3514}" for (${$3513}).`;
  }) : undefined, $3513)));
  const remainder = ($3515 = dividend, $3516 = typeof $3515, (runtime.checkAsserts() ? runtime.ensure(typeof $3515 == 'number', function () {
    return `Expected type "number" but found "${$3516}" for (${$3515}).`;
  }) : undefined, $3515)) % ($3517 = divisor, $3518 = typeof $3517, (runtime.checkAsserts() ? runtime.ensure(typeof $3517 == 'number', function () {
    return `Expected type "number" but found "${$3518}" for (${$3517}).`;
  }) : undefined, $3517));
  return [quotient, remainder];
}
function incrementDigit(digits, index, value, preMultiplier) {
  if (preMultiplier === undefined) preMultiplier = 1;
  if (index == digits.length) {
    digits.push(value);
  } else {
    let $3499, $3507, $3508, $3509, $3510, $3500, $3501, $3502;
    runtime.checkAsserts() ? runtime.ensure(index < digits.length, null) : undefined;
    runtime.setComputed(digits, index, ($3499 = ($3507 = runtime.getComputed(digits, index), $3508 = typeof $3507, (runtime.checkAsserts() ? runtime.ensure(typeof $3507 == 'number', function () {
      return `Expected type "number" but found "${$3508}" for (${$3507}).`;
    }) : undefined, $3507)) * ($3509 = preMultiplier, $3510 = typeof $3509, (runtime.checkAsserts() ? runtime.ensure(typeof $3509 == 'number', function () {
      return `Expected type "number" but found "${$3510}" for (${$3509}).`;
    }) : undefined, $3509)), $3500 = typeof $3499, (runtime.checkAsserts() ? runtime.ensure(typeof $3499 == 'number', function () {
      return `Expected type "number" but found "${$3500}" for (${$3499}).`;
    }) : undefined, $3499)) + ($3501 = value, $3502 = typeof $3501, (runtime.checkAsserts() ? runtime.ensure(typeof $3501 == 'number', function () {
      return `Expected type "number" but found "${$3502}" for (${$3501}).`;
    }) : undefined, $3501)));
  }
}
function trimLeadingZeros(source) {
  return array(function* () {
    let foundNonZero = false;
    for (const n of source) {
      if (n != 0) {
        foundNonZero = true;
      }
      if (foundNonZero) {
        yield n;
      }
    }
  }());
}

const BASE_2_32 = 2 ** 32;
const VALIDATE_UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
class Uuid extends Uuid_ {
  static fromString(s) {
    assert(VALIDATE_UUID.test(s), emsg`Invalid UUID: ${s}`);
    const hexDigits = [];
    for (const c of s) {
      if (c !== '-') {
        hexDigits.push(parseInt(c, 16));
      }
    }
    const parts = [...convertBase(16, BASE_2_32, hexDigits)];
    assert(parts.length <= 4, emsg`
      Expected 4 or less base_2^32 digits, but found ${parts.length}
    `);
    while (parts.length < 4) {
      parts.unshift(0);
    }
    return Uuid.fromJson({
      a: parts[0],
      b: parts[1],
      c: parts[2],
      d: parts[3]
    });
  }
  format() {
    const rawHexDigits = convertBase(BASE_2_32, 16, [this.a, this.b, this.c, this.d]);
    assert(rawHexDigits.length <= 32, emsg`
      Expected 32 or less hexidecimal digits but found ${rawHexDigits.length}
    `);
    const hexDigits = [];
    for (let i = rawHexDigits.length; i < 32; i++) {
      hexDigits.push(0);
    }
    hexDigits.push(...rawHexDigits);
    return joinString(function* () {
      for (let i = 0; i < hexDigits.length; i++) {
        const h = hexDigits[i];
        yield h.toString(16);
        if (i === 7 || i === 11 || i === 15 || i === 19) {
          yield '-';
        }
      }
    });
  }
  isEmpty() {
    return this.a === 0 && this.b === 0 && this.c === 0 && this.d === 0;
  }
}

registerCanonical(Uuid_, Uuid);

function _defineProperty$$(obj, key, value) { key = _toPropertyKey$$(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$$(arg) { var key = _toPrimitive$$(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$$(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class LoanProgramSingleTenancy_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message LoanProgramSingleTenancy field minimum_bond_credit_score.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LoanProgramSingleTenancy, 'LoanProgramSingleTenancy', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LoanProgramSingleTenancy to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get minimumBondCreditScore() {
    return this.getFieldById(1, 0);
  }
  setMinimumBondCreditScore(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteMinimumBondCreditScore() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$$(LoanProgramSingleTenancy_, protoName, DEV_MODE ? 'LoanProgramSingleTenancy' : '[Unknown]');
let LoanProgramSingleTenancy = LoanProgramSingleTenancy_;
(function (_LoanProgramSingleTenancy) {})(LoanProgramSingleTenancy || (LoanProgramSingleTenancy = {}));
Object.defineProperties(LoanProgramSingleTenancy, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "minimum_bond_credit_score": {
        value: 1
      },
      "minimumBondCreditScore": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "minimum_bond_credit_score"
      }
    }))
  }
});
Object.defineProperties(LoanProgramSingleTenancy.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LoanProgramSingleTenancy);
  Object.freeze(LoanProgramSingleTenancy.prototype);
}
class LoanProgramMultiTenancy_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {};
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {}
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LoanProgramMultiTenancy, 'LoanProgramMultiTenancy', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LoanProgramMultiTenancy to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
}
_defineProperty$$(LoanProgramMultiTenancy_, protoName, DEV_MODE ? 'LoanProgramMultiTenancy' : '[Unknown]');
let LoanProgramMultiTenancy = LoanProgramMultiTenancy_;
(function (_LoanProgramMultiTenancy) {})(LoanProgramMultiTenancy || (LoanProgramMultiTenancy = {}));
Object.defineProperties(LoanProgramMultiTenancy, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {}))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {}))
  }
});
Object.defineProperties(LoanProgramMultiTenancy.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LoanProgramMultiTenancy);
  Object.freeze(LoanProgramMultiTenancy.prototype);
}
var _LoanProgramTenancy_Type_;
(function (_LoanProgramTenancy_Type_) {
  _LoanProgramTenancy_Type_[_LoanProgramTenancy_Type_["UNSET"] = 0] = "UNSET";
  _LoanProgramTenancy_Type_[_LoanProgramTenancy_Type_["SINGLE"] = 1] = "SINGLE";
  _LoanProgramTenancy_Type_[_LoanProgramTenancy_Type_["MULTI"] = 2] = "MULTI";
})(_LoanProgramTenancy_Type_ || (_LoanProgramTenancy_Type_ = {}));
const _LoanProgramTenancy_Type = defineEnumProperties(_LoanProgramTenancy_Type_, [1, 2]);
class LoanProgramTenancy_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: LoanProgramSingleTenancy.shape,
      2: LoanProgramMultiTenancy.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(LoanProgramSingleTenancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgramTenancy field single.
                Expected type LoanProgramSingleTenancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'LoanProgramSingleTenancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(LoanProgramMultiTenancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgramTenancy field multi.
                Expected type LoanProgramMultiTenancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'LoanProgramMultiTenancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LoanProgramTenancy, 'LoanProgramTenancy', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LoanProgramTenancy to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _LoanProgramTenancy_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get single() {
    return this.getFieldById(1, LoanProgramSingleTenancy.getDefault());
  }
  setSingle(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteSingle() {
    return this.setValueFieldById(1, null, "type");
  }
  get multi() {
    return this.getFieldById(2, LoanProgramMultiTenancy.getDefault());
  }
  setMulti(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deleteMulti() {
    return this.setValueFieldById(2, null, "type");
  }
}
_defineProperty$$(LoanProgramTenancy_, protoName, DEV_MODE ? 'LoanProgramTenancy' : '[Unknown]');
let LoanProgramTenancy = LoanProgramTenancy_;
(function (_LoanProgramTenancy) {})(LoanProgramTenancy || (LoanProgramTenancy = {}));
Object.defineProperties(LoanProgramTenancy, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "single": {
        value: 1
      },
      "multi": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "single"
      },
      '2': {
        value: "multi"
      }
    }))
  }
});
Object.defineProperties(LoanProgramTenancy.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2]
    }
  },
  prototypes_: {
    value: {
      1: LoanProgramSingleTenancy.prototype,
      2: LoanProgramMultiTenancy.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LoanProgramTenancy);
  Object.freeze(LoanProgramTenancy.prototype);
}
var LoanProgramRecourse__;
(function (LoanProgramRecourse__) {
  LoanProgramRecourse__[LoanProgramRecourse__["SELECTIVE"] = 0] = "SELECTIVE";
  LoanProgramRecourse__[LoanProgramRecourse__["ALWAYS_FULL_RECOURSE"] = 1] = "ALWAYS_FULL_RECOURSE";
  LoanProgramRecourse__[LoanProgramRecourse__["ALWAYS_NON_RECOURSE"] = 2] = "ALWAYS_NON_RECOURSE";
  LoanProgramRecourse__[LoanProgramRecourse__["UNSET"] = 3] = "UNSET";
})(LoanProgramRecourse__ || (LoanProgramRecourse__ = {}));
const LoanProgramRecourse_ = defineEnumProperties(LoanProgramRecourse__, [0, 1, 2, 3]);
const LoanProgramRecourse = extendEnum('LoanProgramRecourse', [["SELECTIVE", 0], ["ALWAYS_FULL_RECOURSE", 1], ["ALWAYS_NON_RECOURSE", 2], ["UNSET", 3]])(LoanProgramRecourse_);
var LoanProgramType__;
(function (LoanProgramType__) {
  LoanProgramType__[LoanProgramType__["OTHER"] = 0] = "OTHER";
  LoanProgramType__[LoanProgramType__["BANK"] = 1] = "BANK";
  LoanProgramType__[LoanProgramType__["CREDIT_UNION"] = 2] = "CREDIT_UNION";
  LoanProgramType__[LoanProgramType__["AGENCY"] = 3] = "AGENCY";
  LoanProgramType__[LoanProgramType__["LIFE_CO"] = 4] = "LIFE_CO";
  LoanProgramType__[LoanProgramType__["CMBS"] = 5] = "CMBS";
  LoanProgramType__[LoanProgramType__["SBA"] = 6] = "SBA";
  LoanProgramType__[LoanProgramType__["DEBT_FUND"] = 7] = "DEBT_FUND";
  LoanProgramType__[LoanProgramType__["PRIVATE_LENDER"] = 8] = "PRIVATE_LENDER";
  LoanProgramType__[LoanProgramType__["CROWD_FUNDER"] = 9] = "CROWD_FUNDER";
  LoanProgramType__[LoanProgramType__["FAMILY_OFFICE"] = 10] = "FAMILY_OFFICE";
  LoanProgramType__[LoanProgramType__["EQUITY"] = 11] = "EQUITY";
  LoanProgramType__[LoanProgramType__["PRIVATE_EQUITY"] = 12] = "PRIVATE_EQUITY";
  LoanProgramType__[LoanProgramType__["PRIVATE_DEBT"] = 13] = "PRIVATE_DEBT";
  LoanProgramType__[LoanProgramType__["REIT"] = 14] = "REIT";
})(LoanProgramType__ || (LoanProgramType__ = {}));
const LoanProgramType_ = defineEnumProperties(LoanProgramType__, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]);
const LoanProgramType$1 = extendEnum('LoanProgramType', [["OTHER", 0], ["BANK", 1], ["CREDIT_UNION", 2], ["AGENCY", 3], ["LIFE_CO", 4], ["CMBS", 5], ["SBA", 6], ["DEBT_FUND", 7], ["PRIVATE_LENDER", 8], ["CROWD_FUNDER", 9], ["FAMILY_OFFICE", 10], ["EQUITY", 11], ["PRIVATE_EQUITY", 12], ["PRIVATE_DEBT", 13], ["REIT", 14]])(LoanProgramType_);
class TypicalAmortization_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 1,
      3: 1,
      4: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message TypicalAmortization field years.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message TypicalAmortization field interest_only.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message TypicalAmortization field partial_interest_only.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message TypicalAmortization field match_all.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(TypicalAmortization, 'TypicalAmortization', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message TypicalAmortization to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get yearsList() {
    return this.getRepeatedFieldById(1);
  }
  hasYears(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addYears(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachYears(values) {
    let T = this;
    for (const value of values) {
      T = T.addYears(value);
    }
    return T;
  }
  setYears(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeYears(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearYearsList() {
    return this.clearRepeatedFieldById(1);
  }
  setYearsList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get interestOnly() {
    return this.getFieldById(2, false);
  }
  setInterestOnly(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteInterestOnly() {
    return this.setValueFieldById(2, null, null);
  }
  get partialInterestOnly() {
    return this.getFieldById(3, false);
  }
  setPartialInterestOnly(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deletePartialInterestOnly() {
    return this.setValueFieldById(3, null, null);
  }
  get matchAll() {
    return this.getFieldById(4, false);
  }
  setMatchAll(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteMatchAll() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$$(TypicalAmortization_, protoName, DEV_MODE ? 'TypicalAmortization' : '[Unknown]');
let TypicalAmortization = TypicalAmortization_;
(function (_TypicalAmortization) {})(TypicalAmortization || (TypicalAmortization = {}));
Object.defineProperties(TypicalAmortization, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "years": {
        value: 1
      },
      "interest_only": {
        value: 2
      },
      "interestOnly": {
        value: 2
      },
      "partial_interest_only": {
        value: 3
      },
      "partialInterestOnly": {
        value: 3
      },
      "match_all": {
        value: 4
      },
      "matchAll": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "years"
      },
      '2': {
        value: "interest_only"
      },
      '3': {
        value: "partial_interest_only"
      },
      '4': {
        value: "match_all"
      }
    }))
  }
});
Object.defineProperties(TypicalAmortization.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(TypicalAmortization);
  Object.freeze(TypicalAmortization.prototype);
}
var TypicalPricingRateIndex__;
(function (TypicalPricingRateIndex__) {
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["UNKNOWN"] = 8] = "UNKNOWN";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["FIXED"] = 0] = "FIXED";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["LIBOR"] = 1] = "LIBOR";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["LIBOR_SWAP"] = 7] = "LIBOR_SWAP";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["PRIME"] = 2] = "PRIME";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["US_TREASURY"] = 3] = "US_TREASURY";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["US_TREASURY_SWAP"] = 4] = "US_TREASURY_SWAP";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["US_TREASURY_CONSTANT_MATURITY"] = 5] = "US_TREASURY_CONSTANT_MATURITY";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["ICE"] = 6] = "ICE";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["SOFR"] = 9] = "SOFR";
  TypicalPricingRateIndex__[TypicalPricingRateIndex__["CME_TERM_SOFR"] = 10] = "CME_TERM_SOFR";
})(TypicalPricingRateIndex__ || (TypicalPricingRateIndex__ = {}));
const TypicalPricingRateIndex_ = defineEnumProperties(TypicalPricingRateIndex__, [8, 0, 1, 7, 2, 3, 4, 5, 6, 9, 10]);
const TypicalPricingRateIndex = extendEnum('TypicalPricingRateIndex', [["UNKNOWN", 8], ["FIXED", 0], ["LIBOR", 1], ["LIBOR_SWAP", 7], ["PRIME", 2], ["US_TREASURY", 3], ["US_TREASURY_SWAP", 4], ["US_TREASURY_CONSTANT_MATURITY", 5], ["ICE", 6], ["SOFR", 9], ["CME_TERM_SOFR", 10]])(TypicalPricingRateIndex_);
class TypicalPricing_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: Percent.shape,
      3: Percent.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message TypicalPricing field rate_index.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return TypicalPricingRateIndex.fromStrictJson_(v, getFieldMap);
          }
          return TypicalPricingRateIndex.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message TypicalPricing field min_spread.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message TypicalPricing field max_spread.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(TypicalPricing$1, 'TypicalPricing', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message TypicalPricing to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get rateIndex() {
    return this.getFieldById(1, 0);
  }
  setRateIndex(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteRateIndex() {
    return this.setValueFieldById(1, null, null);
  }
  get minSpread() {
    return this.getFieldById(2, Percent.getDefault());
  }
  setMinSpread(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteMinSpread() {
    return this.setValueFieldById(2, null, null);
  }
  get maxSpread() {
    return this.getFieldById(3, Percent.getDefault());
  }
  setMaxSpread(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteMaxSpread() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$$(TypicalPricing_, protoName, DEV_MODE ? 'TypicalPricing' : '[Unknown]');
let TypicalPricing$1 = TypicalPricing_;
(function (_TypicalPricing) {})(TypicalPricing$1 || (TypicalPricing$1 = {}));
Object.defineProperties(TypicalPricing$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "rate_index": {
        value: 1
      },
      "rateIndex": {
        value: 1
      },
      "min_spread": {
        value: 2
      },
      "minSpread": {
        value: 2
      },
      "max_spread": {
        value: 3
      },
      "maxSpread": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "rate_index"
      },
      '2': {
        value: "min_spread"
      },
      '3': {
        value: "max_spread"
      }
    }))
  }
});
Object.defineProperties(TypicalPricing$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: Percent.prototype,
      3: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(TypicalPricing$1);
  Object.freeze(TypicalPricing$1.prototype);
}
class LoanProgram_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      48: 2,
      50: Uuid.shape,
      2: 2,
      22: 0,
      3: 0,
      4: 0,
      19: LoanProgramTenancy.shape,
      6: FinancialInstruments.shape,
      7: CurrencyValueRange.shape,
      10: 0,
      37: CurrencyValue.shape,
      38: Percent.shape,
      11: 0,
      12: 2,
      13: 0,
      20: 0,
      14: 1,
      15: Percent.shape,
      16: 1,
      18: 1,
      17: 2,
      8: 2,
      9: 7,
      49: 2,
      23: 0,
      24: TypicalAmortization.shape,
      25: 0,
      26: TypicalPricing$1.shape,
      42: PercentRange.shape,
      27: Term.shape,
      28: Percent.shape,
      29: Percent.shape,
      30: Percent.shape,
      31: Percent.shape,
      32: Percent.shape,
      33: Percent.shape,
      36: 1,
      34: 2,
      35: 1,
      21: 2,
      40: 2,
      41: DurationRange.shape,
      43: PercentRange.shape,
      44: Percent.shape,
      45: 7,
      46: CurrencyValue.shape,
      47: Percent.shape,
      51: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message LoanProgram field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 48:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 50:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Uuid);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field uuid.
                Expected type Uuid.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Uuid', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 22:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanProgramType$1.fromStrictJson_(v, getFieldMap);
          }
          return LoanProgramType$1.from(value);
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field asset_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return AssetType.fromStrictJson_(v, getFieldMap);
            }
            return AssetType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 4:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field financing_purpose.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return FinancingPurpose.fromStrictJson_(v, getFieldMap);
            }
            return FinancingPurpose.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 19:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(LoanProgramTenancy);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field tenancy.
                Expected type LoanProgramTenancy.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'LoanProgramTenancy', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FinancialInstruments);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field financial_instruments.
                Expected type FinancialInstruments.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FinancialInstruments', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValueRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field loan_size.
                Expected type CurrencyValueRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValueRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field sponsor_role.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return SponsorRole.fromStrictJson_(v, getFieldMap);
            }
            return SponsorRole.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 37:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_guarantor_net_worth.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 38:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_guarantor_net_worth_ratio.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field target_state.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UsStateShort.fromStrictJson_(v, getFieldMap);
            }
            return UsStateShort.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 12:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field target_zip_code.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 13:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field sponsor_state.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UsStateShort.fromStrictJson_(v, getFieldMap);
            }
            return UsStateShort.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 20:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field recourse.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanProgramRecourse.fromStrictJson_(v, getFieldMap);
          }
          return LoanProgramRecourse.from(value);
        };
      case 14:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message LoanProgram field preferred_lender.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 15:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field lender_fee.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 16:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message LoanProgram field auto_match_enabled.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 18:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message LoanProgram field require_offer_approval.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 17:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field notes.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field lender_email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 9:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message LoanProgram field organization_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 49:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field organization_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 23:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field required_attachment_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return AttachmentType.fromStrictJson_(v, getFieldMap);
            }
            return AttachmentType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 24:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TypicalAmortization);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field typical_amortization.
                Expected type TypicalAmortization.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TypicalAmortization', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 25:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message LoanProgram field prepayment_penalty.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return PrepaymentPenaltyType.fromStrictJson_(v, getFieldMap);
            }
            return PrepaymentPenaltyType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 26:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TypicalPricing$1);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field typical_pricing.
                Expected type TypicalPricing.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TypicalPricing', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 42:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PercentRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field typical_pricing_flat.
                Expected type PercentRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PercentRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 27:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Term);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field loan_term.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 28:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field max_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 29:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 30:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_debt_yield.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 31:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field max_ltc.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 32:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field as_stabilized_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 33:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field as_stabilized_debt_yield.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 36:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message LoanProgram field accepts_pace_financing.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 34:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field automated_quote.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 35:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message LoanProgram field include_stacksource_fee.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 21:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field exclusion_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 40:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LoanProgram field marketing_description.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 41:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DurationRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field typical_days_to_close.
                Expected type DurationRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DurationRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 43:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PercentRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field equity_stake.
                Expected type PercentRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PercentRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 44:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_equity_irr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 45:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message LoanProgram field min_guarantor_credit_score.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 46:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_guarantor_liquidity.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 47:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LoanProgram field min_guarantor_liquidity_ratio.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 51:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message LoanProgram field hotel_flag_required.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LoanProgram$1, 'LoanProgram', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LoanProgram to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(48, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(48, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(48, null, null);
  }
  get uuid() {
    return this.getFieldById(50, Uuid.getDefault());
  }
  setUuid(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(50, value, null);
  }
  deleteUuid() {
    return this.setValueFieldById(50, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get type() {
    return this.getFieldById(22, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteType() {
    return this.setValueFieldById(22, null, null);
  }
  get assetTypeList() {
    return this.getRepeatedFieldById(3);
  }
  hasAssetType(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addAssetType(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachAssetType(values) {
    let T = this;
    for (const value of values) {
      T = T.addAssetType(value);
    }
    return T;
  }
  setAssetType(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeAssetType(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearAssetTypeList() {
    return this.clearRepeatedFieldById(3);
  }
  setAssetTypeList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
  get financingPurposeList() {
    return this.getRepeatedFieldById(4);
  }
  hasFinancingPurpose(value) {
    return this.repeatedFieldByIdHas(4, value);
  }
  addFinancingPurpose(value) {
    return this.addToRepeatedFieldById(4, value);
  }
  addEachFinancingPurpose(values) {
    let T = this;
    for (const value of values) {
      T = T.addFinancingPurpose(value);
    }
    return T;
  }
  setFinancingPurpose(index, value) {
    return this.setRepeatedFieldById(4, index, value);
  }
  removeFinancingPurpose(index) {
    return this.removeFromRepeatedFieldById(4, index);
  }
  clearFinancingPurposeList() {
    return this.clearRepeatedFieldById(4);
  }
  setFinancingPurposeList(values) {
    return this.overwriteRepeatedFieldById(4, values);
  }
  get tenancyList() {
    return this.getRepeatedFieldById(19);
  }
  hasTenancy(value) {
    return this.repeatedFieldByIdHas(19, value);
  }
  addTenancy(value) {
    return this.addToRepeatedFieldById(19, value);
  }
  addEachTenancy(values) {
    let T = this;
    for (const value of values) {
      T = T.addTenancy(value);
    }
    return T;
  }
  setTenancy(index, value) {
    return this.setRepeatedFieldById(19, index, value);
  }
  removeTenancy(index) {
    return this.removeFromRepeatedFieldById(19, index);
  }
  clearTenancyList() {
    return this.clearRepeatedFieldById(19);
  }
  setTenancyList(values) {
    return this.overwriteRepeatedFieldById(19, values);
  }
  get financialInstruments() {
    return this.getFieldById(6, FinancialInstruments.getDefault());
  }
  setFinancialInstruments(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteFinancialInstruments() {
    return this.setValueFieldById(6, null, null);
  }
  get loanSize() {
    return this.getFieldById(7, CurrencyValueRange.getDefault());
  }
  setLoanSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteLoanSize() {
    return this.setValueFieldById(7, null, null);
  }
  get sponsorRoleList() {
    return this.getRepeatedFieldById(10);
  }
  hasSponsorRole(value) {
    return this.repeatedFieldByIdHas(10, value);
  }
  addSponsorRole(value) {
    return this.addToRepeatedFieldById(10, value);
  }
  addEachSponsorRole(values) {
    let T = this;
    for (const value of values) {
      T = T.addSponsorRole(value);
    }
    return T;
  }
  setSponsorRole(index, value) {
    return this.setRepeatedFieldById(10, index, value);
  }
  removeSponsorRole(index) {
    return this.removeFromRepeatedFieldById(10, index);
  }
  clearSponsorRoleList() {
    return this.clearRepeatedFieldById(10);
  }
  setSponsorRoleList(values) {
    return this.overwriteRepeatedFieldById(10, values);
  }
  get minGuarantorNetWorth() {
    return this.getFieldById(37, CurrencyValue.getDefault());
  }
  setMinGuarantorNetWorth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(37, value, null);
  }
  deleteMinGuarantorNetWorth() {
    return this.setValueFieldById(37, null, null);
  }
  get minGuarantorNetWorthRatio() {
    return this.getFieldById(38, Percent.getDefault());
  }
  setMinGuarantorNetWorthRatio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(38, value, null);
  }
  deleteMinGuarantorNetWorthRatio() {
    return this.setValueFieldById(38, null, null);
  }
  get targetStateList() {
    return this.getRepeatedFieldById(11);
  }
  hasTargetState(value) {
    return this.repeatedFieldByIdHas(11, value);
  }
  addTargetState(value) {
    return this.addToRepeatedFieldById(11, value);
  }
  addEachTargetState(values) {
    let T = this;
    for (const value of values) {
      T = T.addTargetState(value);
    }
    return T;
  }
  setTargetState(index, value) {
    return this.setRepeatedFieldById(11, index, value);
  }
  removeTargetState(index) {
    return this.removeFromRepeatedFieldById(11, index);
  }
  clearTargetStateList() {
    return this.clearRepeatedFieldById(11);
  }
  setTargetStateList(values) {
    return this.overwriteRepeatedFieldById(11, values);
  }
  get targetZipCodeList() {
    return this.getRepeatedFieldById(12);
  }
  hasTargetZipCode(value) {
    return this.repeatedFieldByIdHas(12, value);
  }
  addTargetZipCode(value) {
    return this.addToRepeatedFieldById(12, value);
  }
  addEachTargetZipCode(values) {
    let T = this;
    for (const value of values) {
      T = T.addTargetZipCode(value);
    }
    return T;
  }
  setTargetZipCode(index, value) {
    return this.setRepeatedFieldById(12, index, value);
  }
  removeTargetZipCode(index) {
    return this.removeFromRepeatedFieldById(12, index);
  }
  clearTargetZipCodeList() {
    return this.clearRepeatedFieldById(12);
  }
  setTargetZipCodeList(values) {
    return this.overwriteRepeatedFieldById(12, values);
  }
  get sponsorStateList() {
    return this.getRepeatedFieldById(13);
  }
  hasSponsorState(value) {
    return this.repeatedFieldByIdHas(13, value);
  }
  addSponsorState(value) {
    return this.addToRepeatedFieldById(13, value);
  }
  addEachSponsorState(values) {
    let T = this;
    for (const value of values) {
      T = T.addSponsorState(value);
    }
    return T;
  }
  setSponsorState(index, value) {
    return this.setRepeatedFieldById(13, index, value);
  }
  removeSponsorState(index) {
    return this.removeFromRepeatedFieldById(13, index);
  }
  clearSponsorStateList() {
    return this.clearRepeatedFieldById(13);
  }
  setSponsorStateList(values) {
    return this.overwriteRepeatedFieldById(13, values);
  }
  get recourse() {
    return this.getFieldById(20, 0);
  }
  setRecourse(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, null);
  }
  deleteRecourse() {
    return this.setValueFieldById(20, null, null);
  }
  get preferredLender() {
    return this.getFieldById(14, false);
  }
  setPreferredLender(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deletePreferredLender() {
    return this.setValueFieldById(14, null, null);
  }
  get lenderFee() {
    return this.getFieldById(15, Percent.getDefault());
  }
  setLenderFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteLenderFee() {
    return this.setValueFieldById(15, null, null);
  }
  get autoMatchEnabled() {
    return this.getFieldById(16, false);
  }
  setAutoMatchEnabled(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deleteAutoMatchEnabled() {
    return this.setValueFieldById(16, null, null);
  }
  get requireOfferApproval() {
    return this.getFieldById(18, false);
  }
  setRequireOfferApproval(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(18, value, null);
  }
  deleteRequireOfferApproval() {
    return this.setValueFieldById(18, null, null);
  }
  get notes() {
    return this.getFieldById(17, "");
  }
  setNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteNotes() {
    return this.setValueFieldById(17, null, null);
  }
  get lenderEmailList() {
    return this.getRepeatedFieldById(8);
  }
  hasLenderEmail(value) {
    return this.repeatedFieldByIdHas(8, value);
  }
  addLenderEmail(value) {
    return this.addToRepeatedFieldById(8, value);
  }
  addEachLenderEmail(values) {
    let T = this;
    for (const value of values) {
      T = T.addLenderEmail(value);
    }
    return T;
  }
  setLenderEmail(index, value) {
    return this.setRepeatedFieldById(8, index, value);
  }
  removeLenderEmail(index) {
    return this.removeFromRepeatedFieldById(8, index);
  }
  clearLenderEmailList() {
    return this.clearRepeatedFieldById(8);
  }
  setLenderEmailList(values) {
    return this.overwriteRepeatedFieldById(8, values);
  }
  get organizationId() {
    return this.getFieldById(9, 0);
  }
  setOrganizationId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteOrganizationId() {
    return this.setValueFieldById(9, null, null);
  }
  get organizationSym() {
    return this.getFieldById(49, "");
  }
  setOrganizationSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(49, value, null);
  }
  deleteOrganizationSym() {
    return this.setValueFieldById(49, null, null);
  }
  get requiredAttachmentTypeList() {
    return this.getRepeatedFieldById(23);
  }
  hasRequiredAttachmentType(value) {
    return this.repeatedFieldByIdHas(23, value);
  }
  addRequiredAttachmentType(value) {
    return this.addToRepeatedFieldById(23, value);
  }
  addEachRequiredAttachmentType(values) {
    let T = this;
    for (const value of values) {
      T = T.addRequiredAttachmentType(value);
    }
    return T;
  }
  setRequiredAttachmentType(index, value) {
    return this.setRepeatedFieldById(23, index, value);
  }
  removeRequiredAttachmentType(index) {
    return this.removeFromRepeatedFieldById(23, index);
  }
  clearRequiredAttachmentTypeList() {
    return this.clearRepeatedFieldById(23);
  }
  setRequiredAttachmentTypeList(values) {
    return this.overwriteRepeatedFieldById(23, values);
  }
  get typicalAmortization() {
    return this.getFieldById(24, TypicalAmortization.getDefault());
  }
  setTypicalAmortization(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(24, value, null);
  }
  deleteTypicalAmortization() {
    return this.setValueFieldById(24, null, null);
  }
  get prepaymentPenaltyList() {
    return this.getRepeatedFieldById(25);
  }
  hasPrepaymentPenalty(value) {
    return this.repeatedFieldByIdHas(25, value);
  }
  addPrepaymentPenalty(value) {
    return this.addToRepeatedFieldById(25, value);
  }
  addEachPrepaymentPenalty(values) {
    let T = this;
    for (const value of values) {
      T = T.addPrepaymentPenalty(value);
    }
    return T;
  }
  setPrepaymentPenalty(index, value) {
    return this.setRepeatedFieldById(25, index, value);
  }
  removePrepaymentPenalty(index) {
    return this.removeFromRepeatedFieldById(25, index);
  }
  clearPrepaymentPenaltyList() {
    return this.clearRepeatedFieldById(25);
  }
  setPrepaymentPenaltyList(values) {
    return this.overwriteRepeatedFieldById(25, values);
  }
  get typicalPricing() {
    return this.getFieldById(26, TypicalPricing$1.getDefault());
  }
  setTypicalPricing(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(26, value, null);
  }
  deleteTypicalPricing() {
    return this.setValueFieldById(26, null, null);
  }
  get typicalPricingFlat() {
    return this.getFieldById(42, PercentRange.getDefault());
  }
  setTypicalPricingFlat(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(42, value, null);
  }
  deleteTypicalPricingFlat() {
    return this.setValueFieldById(42, null, null);
  }
  get loanTermList() {
    return this.getRepeatedFieldById(27);
  }
  hasLoanTerm(value) {
    return this.repeatedFieldByIdHas(27, value);
  }
  addLoanTerm(value) {
    return this.addToRepeatedFieldById(27, value);
  }
  addEachLoanTerm(values) {
    let T = this;
    for (const value of values) {
      T = T.addLoanTerm(value);
    }
    return T;
  }
  setLoanTerm(index, value) {
    return this.setRepeatedFieldById(27, index, value);
  }
  removeLoanTerm(index) {
    return this.removeFromRepeatedFieldById(27, index);
  }
  clearLoanTermList() {
    return this.clearRepeatedFieldById(27);
  }
  setLoanTermList(values) {
    return this.overwriteRepeatedFieldById(27, values);
  }
  get maxLtv() {
    return this.getFieldById(28, Percent.getDefault());
  }
  setMaxLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(28, value, null);
  }
  deleteMaxLtv() {
    return this.setValueFieldById(28, null, null);
  }
  get minDscr() {
    return this.getFieldById(29, Percent.getDefault());
  }
  setMinDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(29, value, null);
  }
  deleteMinDscr() {
    return this.setValueFieldById(29, null, null);
  }
  get minDebtYield() {
    return this.getFieldById(30, Percent.getDefault());
  }
  setMinDebtYield(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(30, value, null);
  }
  deleteMinDebtYield() {
    return this.setValueFieldById(30, null, null);
  }
  get maxLtc() {
    return this.getFieldById(31, Percent.getDefault());
  }
  setMaxLtc(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(31, value, null);
  }
  deleteMaxLtc() {
    return this.setValueFieldById(31, null, null);
  }
  get asStabilizedLtv() {
    return this.getFieldById(32, Percent.getDefault());
  }
  setAsStabilizedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(32, value, null);
  }
  deleteAsStabilizedLtv() {
    return this.setValueFieldById(32, null, null);
  }
  get asStabilizedDebtYield() {
    return this.getFieldById(33, Percent.getDefault());
  }
  setAsStabilizedDebtYield(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(33, value, null);
  }
  deleteAsStabilizedDebtYield() {
    return this.setValueFieldById(33, null, null);
  }
  get acceptsPaceFinancing() {
    return this.getFieldById(36, false);
  }
  setAcceptsPaceFinancing(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(36, value, null);
  }
  deleteAcceptsPaceFinancing() {
    return this.setValueFieldById(36, null, null);
  }
  get automatedQuote() {
    return this.getFieldById(34, "");
  }
  setAutomatedQuote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(34, value, null);
  }
  deleteAutomatedQuote() {
    return this.setValueFieldById(34, null, null);
  }
  get includeStacksourceFee() {
    return this.getFieldById(35, false);
  }
  setIncludeStacksourceFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(35, value, null);
  }
  deleteIncludeStacksourceFee() {
    return this.setValueFieldById(35, null, null);
  }
  get exclusionId() {
    return this.getFieldById(21, "");
  }
  setExclusionId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, null);
  }
  deleteExclusionId() {
    return this.setValueFieldById(21, null, null);
  }
  get marketingDescription() {
    return this.getFieldById(40, "");
  }
  setMarketingDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(40, value, null);
  }
  deleteMarketingDescription() {
    return this.setValueFieldById(40, null, null);
  }
  get typicalDaysToClose() {
    return this.getFieldById(41, DurationRange.getDefault());
  }
  setTypicalDaysToClose(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(41, value, null);
  }
  deleteTypicalDaysToClose() {
    return this.setValueFieldById(41, null, null);
  }
  get equityStake() {
    return this.getFieldById(43, PercentRange.getDefault());
  }
  setEquityStake(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(43, value, null);
  }
  deleteEquityStake() {
    return this.setValueFieldById(43, null, null);
  }
  get minEquityIrr() {
    return this.getFieldById(44, Percent.getDefault());
  }
  setMinEquityIrr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(44, value, null);
  }
  deleteMinEquityIrr() {
    return this.setValueFieldById(44, null, null);
  }
  get minGuarantorCreditScore() {
    return this.getFieldById(45, 0);
  }
  setMinGuarantorCreditScore(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(45, value, null);
  }
  deleteMinGuarantorCreditScore() {
    return this.setValueFieldById(45, null, null);
  }
  get minGuarantorLiquidity() {
    return this.getFieldById(46, CurrencyValue.getDefault());
  }
  setMinGuarantorLiquidity(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(46, value, null);
  }
  deleteMinGuarantorLiquidity() {
    return this.setValueFieldById(46, null, null);
  }
  get minGuarantorLiquidityRatio() {
    return this.getFieldById(47, Percent.getDefault());
  }
  setMinGuarantorLiquidityRatio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(47, value, null);
  }
  deleteMinGuarantorLiquidityRatio() {
    return this.setValueFieldById(47, null, null);
  }
  get hotelFlagRequired() {
    return this.getFieldById(51, false);
  }
  setHotelFlagRequired(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(51, value, null);
  }
  deleteHotelFlagRequired() {
    return this.setValueFieldById(51, null, null);
  }
}
_defineProperty$$(LoanProgram_, protoName, DEV_MODE ? 'LoanProgram' : '[Unknown]');
let LoanProgram$1 = LoanProgram_;
(function (_LoanProgram) {})(LoanProgram$1 || (LoanProgram$1 = {}));
Object.defineProperties(LoanProgram$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 48
      },
      "uuid": {
        value: 50
      },
      "name": {
        value: 2
      },
      "type": {
        value: 22
      },
      "asset_type": {
        value: 3
      },
      "assetType": {
        value: 3
      },
      "financing_purpose": {
        value: 4
      },
      "financingPurpose": {
        value: 4
      },
      "tenancy": {
        value: 19
      },
      "financial_instruments": {
        value: 6
      },
      "financialInstruments": {
        value: 6
      },
      "loan_size": {
        value: 7
      },
      "loanSize": {
        value: 7
      },
      "sponsor_role": {
        value: 10
      },
      "sponsorRole": {
        value: 10
      },
      "min_guarantor_net_worth": {
        value: 37
      },
      "minGuarantorNetWorth": {
        value: 37
      },
      "min_guarantor_net_worth_ratio": {
        value: 38
      },
      "minGuarantorNetWorthRatio": {
        value: 38
      },
      "target_state": {
        value: 11
      },
      "targetState": {
        value: 11
      },
      "target_zip_code": {
        value: 12
      },
      "targetZipCode": {
        value: 12
      },
      "sponsor_state": {
        value: 13
      },
      "sponsorState": {
        value: 13
      },
      "recourse": {
        value: 20
      },
      "preferred_lender": {
        value: 14
      },
      "preferredLender": {
        value: 14
      },
      "lender_fee": {
        value: 15
      },
      "lenderFee": {
        value: 15
      },
      "auto_match_enabled": {
        value: 16
      },
      "autoMatchEnabled": {
        value: 16
      },
      "require_offer_approval": {
        value: 18
      },
      "requireOfferApproval": {
        value: 18
      },
      "notes": {
        value: 17
      },
      "lender_email": {
        value: 8
      },
      "lenderEmail": {
        value: 8
      },
      "organization_id": {
        value: 9
      },
      "organizationId": {
        value: 9
      },
      "organization_sym": {
        value: 49
      },
      "organizationSym": {
        value: 49
      },
      "required_attachment_type": {
        value: 23
      },
      "requiredAttachmentType": {
        value: 23
      },
      "typical_amortization": {
        value: 24
      },
      "typicalAmortization": {
        value: 24
      },
      "prepayment_penalty": {
        value: 25
      },
      "prepaymentPenalty": {
        value: 25
      },
      "typical_pricing": {
        value: 26
      },
      "typicalPricing": {
        value: 26
      },
      "typical_pricing_flat": {
        value: 42
      },
      "typicalPricingFlat": {
        value: 42
      },
      "loan_term": {
        value: 27
      },
      "loanTerm": {
        value: 27
      },
      "max_ltv": {
        value: 28
      },
      "maxLtv": {
        value: 28
      },
      "min_dscr": {
        value: 29
      },
      "minDscr": {
        value: 29
      },
      "min_debt_yield": {
        value: 30
      },
      "minDebtYield": {
        value: 30
      },
      "max_ltc": {
        value: 31
      },
      "maxLtc": {
        value: 31
      },
      "as_stabilized_ltv": {
        value: 32
      },
      "asStabilizedLtv": {
        value: 32
      },
      "as_stabilized_debt_yield": {
        value: 33
      },
      "asStabilizedDebtYield": {
        value: 33
      },
      "accepts_pace_financing": {
        value: 36
      },
      "acceptsPaceFinancing": {
        value: 36
      },
      "automated_quote": {
        value: 34
      },
      "automatedQuote": {
        value: 34
      },
      "include_stacksource_fee": {
        value: 35
      },
      "includeStacksourceFee": {
        value: 35
      },
      "exclusion_id": {
        value: 21
      },
      "exclusionId": {
        value: 21
      },
      "marketing_description": {
        value: 40
      },
      "marketingDescription": {
        value: 40
      },
      "typical_days_to_close": {
        value: 41
      },
      "typicalDaysToClose": {
        value: 41
      },
      "equity_stake": {
        value: 43
      },
      "equityStake": {
        value: 43
      },
      "min_equity_irr": {
        value: 44
      },
      "minEquityIrr": {
        value: 44
      },
      "min_guarantor_credit_score": {
        value: 45
      },
      "minGuarantorCreditScore": {
        value: 45
      },
      "min_guarantor_liquidity": {
        value: 46
      },
      "minGuarantorLiquidity": {
        value: 46
      },
      "min_guarantor_liquidity_ratio": {
        value: 47
      },
      "minGuarantorLiquidityRatio": {
        value: 47
      },
      "hotel_flag_required": {
        value: 51
      },
      "hotelFlagRequired": {
        value: 51
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '48': {
        value: "sym"
      },
      '50': {
        value: "uuid"
      },
      '2': {
        value: "name"
      },
      '22': {
        value: "type"
      },
      '3': {
        value: "asset_type"
      },
      '4': {
        value: "financing_purpose"
      },
      '19': {
        value: "tenancy"
      },
      '6': {
        value: "financial_instruments"
      },
      '7': {
        value: "loan_size"
      },
      '10': {
        value: "sponsor_role"
      },
      '37': {
        value: "min_guarantor_net_worth"
      },
      '38': {
        value: "min_guarantor_net_worth_ratio"
      },
      '11': {
        value: "target_state"
      },
      '12': {
        value: "target_zip_code"
      },
      '13': {
        value: "sponsor_state"
      },
      '20': {
        value: "recourse"
      },
      '14': {
        value: "preferred_lender"
      },
      '15': {
        value: "lender_fee"
      },
      '16': {
        value: "auto_match_enabled"
      },
      '18': {
        value: "require_offer_approval"
      },
      '17': {
        value: "notes"
      },
      '8': {
        value: "lender_email"
      },
      '9': {
        value: "organization_id"
      },
      '49': {
        value: "organization_sym"
      },
      '23': {
        value: "required_attachment_type"
      },
      '24': {
        value: "typical_amortization"
      },
      '25': {
        value: "prepayment_penalty"
      },
      '26': {
        value: "typical_pricing"
      },
      '42': {
        value: "typical_pricing_flat"
      },
      '27': {
        value: "loan_term"
      },
      '28': {
        value: "max_ltv"
      },
      '29': {
        value: "min_dscr"
      },
      '30': {
        value: "min_debt_yield"
      },
      '31': {
        value: "max_ltc"
      },
      '32': {
        value: "as_stabilized_ltv"
      },
      '33': {
        value: "as_stabilized_debt_yield"
      },
      '36': {
        value: "accepts_pace_financing"
      },
      '34': {
        value: "automated_quote"
      },
      '35': {
        value: "include_stacksource_fee"
      },
      '21': {
        value: "exclusion_id"
      },
      '40': {
        value: "marketing_description"
      },
      '41': {
        value: "typical_days_to_close"
      },
      '43': {
        value: "equity_stake"
      },
      '44': {
        value: "min_equity_irr"
      },
      '45': {
        value: "min_guarantor_credit_score"
      },
      '46': {
        value: "min_guarantor_liquidity"
      },
      '47': {
        value: "min_guarantor_liquidity_ratio"
      },
      '51': {
        value: "hotel_flag_required"
      }
    }))
  }
});
Object.defineProperties(LoanProgram$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      50: Uuid.prototype,
      19: LoanProgramTenancy.prototype,
      6: FinancialInstruments.prototype,
      7: CurrencyValueRange.prototype,
      37: CurrencyValue.prototype,
      38: Percent.prototype,
      15: Percent.prototype,
      24: TypicalAmortization.prototype,
      26: TypicalPricing$1.prototype,
      42: PercentRange.prototype,
      27: Term.prototype,
      28: Percent.prototype,
      29: Percent.prototype,
      30: Percent.prototype,
      31: Percent.prototype,
      32: Percent.prototype,
      33: Percent.prototype,
      41: DurationRange.prototype,
      43: PercentRange.prototype,
      44: Percent.prototype,
      46: CurrencyValue.prototype,
      47: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LoanProgram$1);
  Object.freeze(LoanProgram$1.prototype);
}
class RepeatedLoanProgram_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: LoanProgram$1.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(LoanProgram$1);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedLoanProgram field value.
                Expected type LoanProgram.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'LoanProgram', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedLoanProgram, 'RepeatedLoanProgram', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedLoanProgram to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$$(RepeatedLoanProgram_, protoName, DEV_MODE ? 'RepeatedLoanProgram' : '[Unknown]');
let RepeatedLoanProgram = RepeatedLoanProgram_;
(function (_RepeatedLoanProgram) {})(RepeatedLoanProgram || (RepeatedLoanProgram = {}));
Object.defineProperties(RepeatedLoanProgram, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedLoanProgram.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: LoanProgram$1.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedLoanProgram);
  Object.freeze(RepeatedLoanProgram.prototype);
}

function formatPrepaymentPenaltyType(prepaymentPenaltyType) {
  switch (prepaymentPenaltyType) {
    case PrepaymentPenaltyType.NONE:
      return 'No Penalty';
    case PrepaymentPenaltyType.LOCKOUT:
      return 'Lockout';
    case PrepaymentPenaltyType.DEFEASANCE:
      return 'Defeasance';
    case PrepaymentPenaltyType.YIELD_MAINTENANCE:
      return 'Yield Maintenance';
    case PrepaymentPenaltyType.DECLINING:
      return 'Stepdown';
    default:
      assert.fail(emsg`Unknown prepayment penalty type: ${prepaymentPenaltyType}`);
  }
  return '';
}
function formatPrepaymentPenaltyDecliningDetails(value, loanTerms) {
  const type = value.type;
  const periodRates = value.decliningDetails.periodRateList;
  if (type === PrepaymentPenaltyType.DECLINING) {
    if (periodRates.isEmpty()) {
      return '';
    }
    if (Math.ceil(calculateLoanTermsYears(loanTerms)) === periodRates.size) {
      if (periodRates.size === 1) {
        return formatPercent(periodRates.first().rate);
      }
      if (periodRates.size < 6) {
        const out = [];
        for (const periodRate of periodRates) {
          out.push(formatPercent(periodRate.rate));
        }
        return out.join(', ');
      }
      return [formatPercent(periodRates.first().rate), formatPercent(periodRates.last().rate)].join(' to ');
    }
    return formatPercent(periodRates.first().rate) + ' to 0%';
  }
  return '';
}

function usStateShortToString(state) {
  switch (state) {
    case UsStateShort.AK:
      return 'AK';
    case UsStateShort.AL:
      return 'AL';
    case UsStateShort.AR:
      return 'AR';
    case UsStateShort.AZ:
      return 'AZ';
    case UsStateShort.CA:
      return 'CA';
    case UsStateShort.CO:
      return 'CO';
    case UsStateShort.CT:
      return 'CT';
    case UsStateShort.DC:
      return 'DC';
    case UsStateShort.DE:
      return 'DE';
    case UsStateShort.FL:
      return 'FL';
    case UsStateShort.GA:
      return 'GA';
    case UsStateShort.HI:
      return 'HI';
    case UsStateShort.IA:
      return 'IA';
    case UsStateShort.ID:
      return 'ID';
    case UsStateShort.IL:
      return 'IL';
    case UsStateShort.IN:
      return 'IN';
    case UsStateShort.KS:
      return 'KS';
    case UsStateShort.KY:
      return 'KY';
    case UsStateShort.LA:
      return 'LA';
    case UsStateShort.MA:
      return 'MA';
    case UsStateShort.MD:
      return 'MD';
    case UsStateShort.ME:
      return 'ME';
    case UsStateShort.MI:
      return 'MI';
    case UsStateShort.MN:
      return 'MN';
    case UsStateShort.MO:
      return 'MO';
    case UsStateShort.MS:
      return 'MS';
    case UsStateShort.MT:
      return 'MT';
    case UsStateShort.NC:
      return 'NC';
    case UsStateShort.ND:
      return 'ND';
    case UsStateShort.NE:
      return 'NE';
    case UsStateShort.NH:
      return 'NH';
    case UsStateShort.NJ:
      return 'NJ';
    case UsStateShort.NM:
      return 'NM';
    case UsStateShort.NV:
      return 'NV';
    case UsStateShort.NY:
      return 'NY';
    case UsStateShort.OH:
      return 'OH';
    case UsStateShort.OK:
      return 'OK';
    case UsStateShort.OR:
      return 'OR';
    case UsStateShort.PA:
      return 'PA';
    case UsStateShort.RI:
      return 'RI';
    case UsStateShort.SC:
      return 'SC';
    case UsStateShort.SD:
      return 'SD';
    case UsStateShort.TN:
      return 'TN';
    case UsStateShort.TX:
      return 'TX';
    case UsStateShort.UT:
      return 'UT';
    case UsStateShort.VA:
      return 'VA';
    case UsStateShort.VT:
      return 'VT';
    case UsStateShort.WA:
      return 'WA';
    case UsStateShort.WI:
      return 'WI';
    case UsStateShort.WV:
      return 'WV';
    case UsStateShort.WY:
      return 'WY';
    case UsStateShort.PR:
      return 'PR';
  }
  assert(false, `Unknown UsStateShort value "${state}"`);
  return '';
}
function usStateShortFromString(state) {
  const usStateShort = usStateShortOrNullFromString(state);
  if (usStateShort == null) {
    throw new Error(`State not found in UsStateShort "${state}"`);
  }
  return usStateShort;
}
function usStateShortOrNullFromString(state) {
  const usStateShort = state.toUpperCase();
  switch (usStateShort) {
    case 'AK':
      return UsStateShort.AK;
    case 'AL':
      return UsStateShort.AL;
    case 'AR':
      return UsStateShort.AR;
    case 'AZ':
      return UsStateShort.AZ;
    case 'CA':
      return UsStateShort.CA;
    case 'CO':
      return UsStateShort.CO;
    case 'CT':
      return UsStateShort.CT;
    case 'DC':
      return UsStateShort.DC;
    case 'DE':
      return UsStateShort.DE;
    case 'FL':
      return UsStateShort.FL;
    case 'GA':
      return UsStateShort.GA;
    case 'HI':
      return UsStateShort.HI;
    case 'IA':
      return UsStateShort.IA;
    case 'ID':
      return UsStateShort.ID;
    case 'IL':
      return UsStateShort.IL;
    case 'IN':
      return UsStateShort.IN;
    case 'KS':
      return UsStateShort.KS;
    case 'KY':
      return UsStateShort.KY;
    case 'LA':
      return UsStateShort.LA;
    case 'MA':
      return UsStateShort.MA;
    case 'MD':
      return UsStateShort.MD;
    case 'ME':
      return UsStateShort.ME;
    case 'MI':
      return UsStateShort.MI;
    case 'MN':
      return UsStateShort.MN;
    case 'MO':
      return UsStateShort.MO;
    case 'MS':
      return UsStateShort.MS;
    case 'MT':
      return UsStateShort.MT;
    case 'NC':
      return UsStateShort.NC;
    case 'ND':
      return UsStateShort.ND;
    case 'NE':
      return UsStateShort.NE;
    case 'NH':
      return UsStateShort.NH;
    case 'NJ':
      return UsStateShort.NJ;
    case 'NM':
      return UsStateShort.NM;
    case 'NV':
      return UsStateShort.NV;
    case 'NY':
      return UsStateShort.NY;
    case 'OH':
      return UsStateShort.OH;
    case 'OK':
      return UsStateShort.OK;
    case 'OR':
      return UsStateShort.OR;
    case 'PA':
      return UsStateShort.PA;
    case 'RI':
      return UsStateShort.RI;
    case 'SC':
      return UsStateShort.SC;
    case 'SD':
      return UsStateShort.SD;
    case 'TN':
      return UsStateShort.TN;
    case 'TX':
      return UsStateShort.TX;
    case 'UT':
      return UsStateShort.UT;
    case 'VA':
      return UsStateShort.VA;
    case 'VT':
      return UsStateShort.VT;
    case 'WA':
      return UsStateShort.WA;
    case 'WI':
      return UsStateShort.WI;
    case 'WV':
      return UsStateShort.WV;
    case 'WY':
      return UsStateShort.WY;
    case 'PR':
      return UsStateShort.PR;
    default:
      return null;
  }
}

function leftPad(base, len, fill = ' ') {
  const fillPattern = fill.length > 0 ? fill.split('') : [' '];
  let result = base;
  let offset = 0;
  while (result.length < len) {
    const idx = fillPattern.length - offset - 1;
    result = fillPattern[idx] + result;
    offset = (offset + 1) % fillPattern.length;
  }
  return result;
}

class TypicalPricing extends TypicalPricing_ {
  format() {
    const self = this;
    return joinString(function* () {
      if (self.maxSpread.isZero()) {
        return;
      }
      if (self.rateIndex === TypicalPricingRateIndex.UNKNOWN) {
        return;
      }
      if (self.rateIndex !== TypicalPricingRateIndex.FIXED) {
        yield self.formatRateIndex();
        yield ' + ';
      }
      yield self.minSpread.format(false /* includeSymbol */);
      yield '\u2013';
      yield self.maxSpread.format();
    });
  }
  formatRateIndex() {
    const rateIndex = this.rateIndex;
    switch (rateIndex) {
      case TypicalPricingRateIndex.LIBOR:
        return 'LIBOR';
      case TypicalPricingRateIndex.LIBOR_SWAP:
        return 'LIBOR Swap';
      case TypicalPricingRateIndex.PRIME:
        return 'Prime';
      case TypicalPricingRateIndex.US_TREASURY:
        return 'UST';
      case TypicalPricingRateIndex.US_TREASURY_SWAP:
        return 'UST Swap';
      case TypicalPricingRateIndex.US_TREASURY_CONSTANT_MATURITY:
        return 'UST Constant Maturity';
      case TypicalPricingRateIndex.ICE:
        return 'ICE';
      case TypicalPricingRateIndex.SOFR:
        return 'SOFR';
      case TypicalPricingRateIndex.CME_TERM_SOFR:
        return 'Term SOFR';
    }
    assert.fail(emsg`Unknown typical pricing rate index: ${rateIndex}`);
    return '';
  }
}
const LoanProgramType = {
  ...LoanProgramType$1,
  format(type, locationLabel) {
    const out = [];
    if (locationLabel && type !== LoanProgramType.LIFE_CO && type !== LoanProgramType.CROWD_FUNDER && type !== LoanProgramType.AGENCY) {
      out.push(locationLabel);
    }
    out.push(formatLoanProgramTypeInner(type, locationLabel));
    return out.join(' ');
  }
};
function formatLoanProgramTypeInner(type, locationLabel) {
  switch (type) {
    case LoanProgramType.OTHER:
      if (locationLabel) {
        return 'Lender';
      }
      return 'Matched Lender';
    case LoanProgramType.BANK:
      return 'Bank';
    case LoanProgramType.CREDIT_UNION:
      return 'Credit Union';
    case LoanProgramType.AGENCY:
      return 'Agency';
    case LoanProgramType.LIFE_CO:
      return 'Life Co';
    case LoanProgramType.CMBS:
      return 'CMBS';
    case LoanProgramType.SBA:
      return 'SBA';
    case LoanProgramType.DEBT_FUND:
      return 'Debt Fund';
    case LoanProgramType.PRIVATE_LENDER:
      return 'Private Lender';
    case LoanProgramType.CROWD_FUNDER:
      return 'Crowd Funder';
    case LoanProgramType.FAMILY_OFFICE:
      return 'Family Office';
    case LoanProgramType.EQUITY:
      return 'Equity';
    case LoanProgramType.PRIVATE_EQUITY:
      return 'Private Equity';
    case LoanProgramType.PRIVATE_DEBT:
      return 'Private Debt';
    case LoanProgramType.REIT:
      return 'REIT';
  }
  assert.fail(emsg`Unknown loan program type: ${type}`);
  return '';
}
class LoanProgram extends LoanProgram_ {
  formatType(locationLabel) {
    return LoanProgramType.format(this.type, locationLabel);
  }
  formatHighlights() {
    return [formatLoanRecourse(this.recourse), formatTypicalAmortization(this.typicalAmortization), formatPrepaymentPenalty(this.prepaymentPenaltyList), formatTypicalPricingFlat(this.typicalPricingFlat), formatLoanTerms(this.loanTermList)].filter(identity);
  }
  formatUnderwritingQualifications() {
    return toArray(extractUnderwritingQualifications(this));
  }
}
function formatLoanRecourse(recourse) {
  switch (recourse) {
    case LoanProgramRecourse.SELECTIVE:
      return 'Non-recourse available';
    case LoanProgramRecourse.ALWAYS_FULL_RECOURSE:
      return 'Full recourse';
    case LoanProgramRecourse.ALWAYS_NON_RECOURSE:
      return 'Non-recourse';
    default:
      return '';
  }
}
const formatTypicalAmortizationYears = years => joinValues(function* () {
  for (const year of years.slice(0, -1)) {
    yield year;
  }
  if (years.length > 0) {
    const last = years[years.length - 1];
    if (last === 1) {
      yield '1 year';
    } else {
      yield `${last} years`;
    }
  }
});
const formatTypicalAmortization = typicalAmortization => joinString(' ', function* () {
  const {
    interestOnly,
    partialInterestOnly,
    yearsList
  } = typicalAmortization;
  const years = [...yearsList];
  if (!interestOnly && years.length === 0) {
    return;
  }
  if (years.length > 0) {
    yield `Amortizes over ${formatTypicalAmortizationYears(years)}.`;
  }
  if (interestOnly) {
    if (partialInterestOnly) {
      yield 'Partial term I/O available.';
    } else {
      yield 'Full term I/O available.';
    }
  } else if (partialInterestOnly) {
    yield 'Partial term I/O available.';
  }
});
function formatPrepaymentPenalty(prepaymentPenaltyList) {
  const penalties = [...prepaymentPenaltyList];
  if (penalties.length === 0) {
    return '';
  }
  if (penalties.length > 1) {
    return 'Multiple prepayment penalty options';
  }
  switch (penalties[0]) {
    case PrepaymentPenaltyType.NONE:
      return 'No prepayment penalty';
    case PrepaymentPenaltyType.LOCKOUT:
      return 'Lockout period before prepayment';
    case PrepaymentPenaltyType.DEFEASANCE:
      return 'Defeasance required to prepay';
    case PrepaymentPenaltyType.YIELD_MAINTENANCE:
      return 'Yield maintenance prepay';
    case PrepaymentPenaltyType.DECLINING:
      return 'Stepdown prepayment penalty';
  }
  assert(false, `Unknown prepayment penalty type (${penalties[0]}).`);
  return '';
}
const formatTypicalPricingFlat = typicalPricing => !typicalPricing.isZero()
// eslint-disable-next-line max-len
? `Current interest rates at ${typicalPricing.formatMaxDecimalPlaces(2, true, '\u00A0\u2013\u00A0')}` : '';
const formatLoanTerms = loanTermList => joinString(function* () {
  const loanTerms = [...loanTermList];
  if (loanTerms.length === 0) {
    return;
  }
  yield joinString(', ', function* () {
    for (const term of loanTerms) {
      ensure(term.unit === TermUnit.MONTH);
      yield term.length / 12;
    }
  });
  yield ' year terms available';
});
function* extractUnderwritingQualifications(loanProgram) {
  const {
    asStabilizedDebtYield,
    asStabilizedLtv,
    assetTypeList,
    equityStake,
    loanSize,
    maxLtc,
    maxLtv,
    minDebtYield,
    minDscr,
    minEquityIrr,
    minGuarantorCreditScore,
    minGuarantorLiquidity,
    minGuarantorLiquidityRatio,
    minGuarantorNetWorth,
    minGuarantorNetWorthRatio,
    sponsorRoleList,
    sponsorStateList,
    targetStateList
  } = loanProgram;
  if (isNationwide(targetStateList, sponsorStateList)) {
    yield 'Nationwide';
  } else if (targetStateList.size >= 45) {
    const targetStateSet = new Set(targetStateList);
    const untargetedStates = [...UsStateShort].filter(state => !targetStateSet.has(state)).filter(noPR).map(state => usStateShortToString(state));
    yield `Nationwide except ${joinValues(untargetedStates)}`;
  } else if (targetStateList.size > 0 && targetStateList.size <= 5) {
    const targetStates = targetStateList.map(state => usStateShortToString(state));
    yield `Properties in ${joinValues(targetStates)}`;
  } else if (sponsorStateList.size > 0 && sponsorStateList.size <= 5) {
    const sponsorStates = sponsorStateList.map(state => usStateShortToString(state));
    yield `Borrowers in ${joinValues(sponsorStates)}`;
  } else if (targetStateList.size > 5 && targetStateList.size < 45) {
    yield 'Regional lender';
  }
  const assetTypes = assetTypeList.filter(assetType => AssetType.OTHER !== assetType);
  if (assetTypes.size > 0 && assetTypes.size <= 5) {
    const assetClasses = assetTypes.map(assetType => assetTypeToCapitalString(assetType));
    yield `Specializes in ${joinValues(assetClasses, 'and')}`;
  }
  const lsLower = loanSize.lowerBound.formatAbbr();
  const lsUpper = loanSize.upperBound.formatAbbr();
  if (lsLower === '$0K' || lsLower === lsUpper) {
    yield `Up to ${lsUpper} check size`;
  } else if (lsUpper.endsWith(lsLower.split('').pop() || 'K')) {
    // same denomination K/M/B
    yield msg`${lsLower.slice(0, lsLower.length - 1)}-${lsUpper.slice(1)}
      check size`;
  } else {
    yield `${lsLower} - ${lsUpper} check size`;
  }
  const loanRequirements = toArray(function* () {
    if (!maxLtv.isZero()) {
      yield `${maxLtv.format()} LTV`;
    }
    if (!maxLtc.isZero()) {
      yield `${maxLtc.format()} LTC`;
    }
    if (!minDscr.isZero()) {
      yield `${minDscr.formatAsMultiple()} DSCR`;
    }
    if (!minDebtYield.isZero()) {
      yield `${minDebtYield.format()} Debt Yield`;
    }
  });
  if (loanRequirements.length > 0) {
    yield `Up to ${joinString(' | ', loanRequirements)}`;
  }
  const stabilizedLoanRequirements = toArray(function* () {
    if (!asStabilizedLtv.isZero()) {
      yield `${asStabilizedLtv.format()} LTV`;
    }
    if (!asStabilizedDebtYield.isZero()) {
      yield `${asStabilizedDebtYield.format()} Debt Yield`;
    }
  });
  if (stabilizedLoanRequirements.length > 0) {
    yield msg`Up to ${joinString(' | ', stabilizedLoanRequirements)} as
      stabilized`;
  }
  if (!equityStake.isZero()) {
    yield `${equityStake.format(true, ' - ')} Equity Stake`;
  }
  if (!minEquityIrr.isZero()) {
    yield `Minimum ${minEquityIrr.format()} IRR`;
  }
  const sponsorRoles = new Set(sponsorRoleList);
  if (sponsorRoles.has(SponsorRole.OWNER_OCCUPIER) && !sponsorRoles.has(SponsorRole.INVESTOR)) {
    yield 'Owner-users only';
  }
  if (minGuarantorCreditScore > 0) {
    const minCreditScoreStr = leftPad(minGuarantorCreditScore.toString(), 3, '0');
    yield `Minimum ${minCreditScoreStr} credit score`;
  }
  const nwlCurrency = toArray(function* () {
    if (!minGuarantorNetWorth.isZero()) {
      yield `${minGuarantorNetWorth.formatAbbr()} guarantor net worth`;
    }
    if (!minGuarantorLiquidity.isZero()) {
      yield `${minGuarantorLiquidity.formatAbbr()} liquidity`;
    }
  });
  if (nwlCurrency.length > 0) {
    yield `Minimum ${joinString(' | ', nwlCurrency)}`;
  }
  const nwlPercent = toArray(function* () {
    if (!minGuarantorNetWorthRatio.isZero()) {
      yield `${minGuarantorNetWorthRatio.format()} guarantor net worth`;
    }
    if (!minGuarantorLiquidityRatio.isZero()) {
      yield `${minGuarantorLiquidityRatio.format()} liquidity`;
    }
  });
  if (nwlPercent.length > 0) {
    yield msg`Minimum ${joinString(' | ', nwlPercent)} compared to loan
      amount`;
  }
}
const noPR = state => state !== UsStateShort.PR;
const ALL_STATES = [...UsStateShort];
const ALL_STATES_NO_PR = ALL_STATES.filter(noPR);
function isNationwide(targetStateList, sponsorStateList) {
  const tsl = [...targetStateList];
  const ssl = [...sponsorStateList];
  const tslNoPR = tsl.filter(noPR);
  const sslNoPR = ssl.filter(noPR);
  return tsl.length === 0 && ssl.length === 0 || tslNoPR.length === ALL_STATES_NO_PR.length || sslNoPR.length === ALL_STATES_NO_PR.length;
}

registerCanonical(LoanProgramSingleTenancy_, LoanProgramSingleTenancy_);
registerCanonical(LoanProgramMultiTenancy_, LoanProgramMultiTenancy_);
registerCanonical(LoanProgramTenancy_, LoanProgramTenancy_);
registerCanonical(LoanProgramRecourse, LoanProgramRecourse);
registerCanonical(LoanProgramType$1, LoanProgramType);
registerCanonical(TypicalAmortization_, TypicalAmortization_);
registerCanonical(TypicalPricingRateIndex, TypicalPricingRateIndex);
registerCanonical(TypicalPricing_, TypicalPricing);
registerCanonical(LoanProgram_, LoanProgram);
registerCanonical(RepeatedLoanProgram_, RepeatedLoanProgram_);

function _defineProperty$_(obj, key, value) { key = _toPropertyKey$_(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$_(arg) { var key = _toPrimitive$_(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$_(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Extension_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 11,
      2: 11,
      3: 11
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Extension field a.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Extension field b.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Extension field c.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Extension$1, 'Extension', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Extension to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get a() {
    return this.getFieldById(1, 0);
  }
  setA(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteA() {
    return this.setValueFieldById(1, null, null);
  }
  get b() {
    return this.getFieldById(2, 0);
  }
  setB(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteB() {
    return this.setValueFieldById(2, null, null);
  }
  get c() {
    return this.getFieldById(3, 0);
  }
  setC(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteC() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$_(Extension_, protoName, DEV_MODE ? 'Extension' : '[Unknown]');
let Extension$1 = Extension_;
(function (_Extension) {})(Extension$1 || (Extension$1 = {}));
Object.defineProperties(Extension$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "a": {
        value: 1
      },
      "b": {
        value: 2
      },
      "c": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "a"
      },
      '2': {
        value: "b"
      },
      '3': {
        value: "c"
      }
    }))
  }
});
Object.defineProperties(Extension$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Extension$1);
  Object.freeze(Extension$1.prototype);
}
class InternationalPhoneNumber_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 8,
      3: Extension$1.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message InternationalPhoneNumber field country_code.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message InternationalPhoneNumber field national_number.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Extension$1);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message InternationalPhoneNumber field extension.
                Expected type Extension.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Extension', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(InternationalPhoneNumber$1, 'InternationalPhoneNumber', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message InternationalPhoneNumber to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get countryCode() {
    return this.getFieldById(1, 0);
  }
  setCountryCode(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteCountryCode() {
    return this.setValueFieldById(1, null, null);
  }
  get nationalNumber() {
    return this.getFieldById(2, 0);
  }
  setNationalNumber(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteNationalNumber() {
    return this.setValueFieldById(2, null, null);
  }
  get extension() {
    return this.getFieldById(3, Extension$1.getDefault());
  }
  setExtension(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteExtension() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$_(InternationalPhoneNumber_, protoName, DEV_MODE ? 'InternationalPhoneNumber' : '[Unknown]');
let InternationalPhoneNumber$1 = InternationalPhoneNumber_;
(function (_InternationalPhoneNumber) {})(InternationalPhoneNumber$1 || (InternationalPhoneNumber$1 = {}));
Object.defineProperties(InternationalPhoneNumber$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "country_code": {
        value: 1
      },
      "countryCode": {
        value: 1
      },
      "national_number": {
        value: 2
      },
      "nationalNumber": {
        value: 2
      },
      "extension": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "country_code"
      },
      '2': {
        value: "national_number"
      },
      '3': {
        value: "extension"
      }
    }))
  }
});
Object.defineProperties(InternationalPhoneNumber$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: Extension$1.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(InternationalPhoneNumber$1);
  Object.freeze(InternationalPhoneNumber$1.prototype);
}

function arrayRepeat(value, length) {
  const out = [];
  for (let i = 0; i < length; i++) {
    out.push(value);
  }
  return out;
}

const BYTE_MASK = 2 ** 8 - 1;
const BASE_12_CHARS = Map$1(function* () {
  const chars = '0123456789#*';
  for (let i = 0; i < chars.length; i++) {
    yield [i, chars[i]];
  }
}());
const BASE_12_DIGITS = Map$1(function* () {
  for (const [i, c] of BASE_12_CHARS.entries()) {
    yield [c, i];
  }
}());
const formatInternational = Symbol('formatInternational');
const formatUnitedStates = Symbol('formatUnitedStates');
class Extension extends Extension_ {
  static encode(decoded) {
    if (decoded.length > 20) {
      throw new Error(`Invalid extension "${decoded}". Must be 20 characters or less.`);
    }
    if (decoded === '') {
      return Extension.getDefault();
    }
    const zeroPadding = countLeftZeroPadding(decoded);
    const decodedSansPadding = decoded.replace(/^0+([0-9ab])/, '$1');
    const base12Value = [];
    for (const c of decodedSansPadding) {
      base12Value.push(base12CharToDigit(c));
    }
    const base256Value = leftPadBytes(convertBase(12, 256, base12Value), 9);
    assert.same(base256Value.length, 9);
    return new Extension().setA(uint32FromBytes([0, 0, zeroPadding, base256Value[0]])).setB(uint32FromBytes(base256Value.slice(1, 5))).setC(uint32FromBytes(base256Value.slice(5)));
  }
  decode() {
    const out = [];

    // All of `a`'s first 18 bits should be 0.
    if (this.a >>> 32 - 18 !== 0) {
      throw new RangeError('Invalid extension. Reserved bits must be set to 0.');
    }

    // The next 6 bits of `a` specify zero-padding.
    const zeroPadding = this.a >>> 8;
    if (zeroPadding > 39) {
      throw new RangeError('Invalid extension. Zero padding cannot be greater than 39.');
    }
    out.push('0'.repeat(zeroPadding));

    // The last 8 bits of `a` in conjunction with `b` and `c` make up a 72 bit
    // number which, when converted to base 12, is the numeric value of the
    // extension (with '#' representing decimal 10 and '*' representing decimal
    // 11).

    // Break the remaining parts into an array of bytes representing the digits
    // of a base 256 number.
    const bytes = [getByteFromUint32(this.a, 3), getByteFromUint32(this.b, 0), getByteFromUint32(this.b, 1), getByteFromUint32(this.b, 2), getByteFromUint32(this.b, 3), getByteFromUint32(this.c, 0), getByteFromUint32(this.c, 1), getByteFromUint32(this.c, 2), getByteFromUint32(this.c, 3)];

    // Convert the base 256 number into a base 12 number.
    const digits = convertBase(256, 12, bytes);
    out.push(digits.map(base12DigitToChar).join(''));
    return out.join('');
  }

  // The left 18 bits of `this.a` must be 0.
  isValid() {
    return this.a >>> 32 - 18 === 0;
  }
  isEmpty() {
    return this.a === 0 && this.b === 0 && this.c === 0;
  }
}
class InternationalPhoneNumber extends InternationalPhoneNumber_ {
  isValid() {
    return this.countryCode > 0 && this.countryCode <= 999 && isValidNationalNumber(this.countryCode, this.nationalNumber) && this.extension.isValid();
  }
  isEmpty() {
    return this.countryCode === 0 && this.nationalNumber === 0 && this.extension.isEmpty();
  }

  // We want to know who we are formatting the phone number for. If they share
  // a country code, we may decide to skip international formatting and display
  // in a local format.
  format(unused_userSettings) {
    if (this.isEmpty()) {
      return '';
    }

    // We want to know who the user is so we can more easily support i18n in
    // the future, but for now we'll just always format for US users.
    return this[formatUnitedStates]();
  }

  // See also `formatInternationalPhoneNumberForUsUser`.
  [formatInternational]() {
    const self = this;
    return joinString(function* () {
      // We currently only support formatting a US number.
      assert.same(self.countryCode, 1);
      yield '+';
      yield self.countryCode;
      yield ' ';
      yield joinString('-', function* () {
        const s = String(self.nationalNumber);
        assert.same(s.length, 10);
        yield s.slice(0, 3);
        yield s.slice(3, 6);
        yield s.slice(6);
      });
      if (!self.extension.isEmpty()) {
        yield ' ext. ';
        yield self.extension.decode();
      }
    });
  }

  // This is intended for displaying phone numbers to US users in a context
  // where phone numbers are reasonably expected to be other US numbers. It
  // hidse the country code if it's "1" and surrounds the area code in
  // parentheses. For contexts where phone numbers should be displayed in a
  // more strict international format, use `internationalPhoneNumberToString`
  // instead.
  [formatUnitedStates]() {
    const self = this;
    return joinString(function* () {
      // We currently only support formatting a US number.
      assert.same(self.countryCode, 1, emsg`
          Invalid country code (${self.countryCode}) with national number
          ${self.nationalNumber}`);
      const nn = String(self.nationalNumber);
      assert.same(nn.length, 10);
      yield '(';
      yield nn.slice(0, 3);
      yield ') ';
      yield nn.slice(3, 6);
      yield '-';
      yield nn.slice(6);
      if (!self.extension.isEmpty()) {
        yield ' ext. ';
        yield self.extension.decode();
      }
    });
  }
  toUrlString() {
    const self = this;
    return joinString(function* () {
      // We currently only support formatting a US number.
      assert.same(self.countryCode, 1);
      yield 'tel:+';
      yield self.countryCode;
      yield self.nationalNumber;
      if (!self.extension.isEmpty()) {
        yield ',';
        yield self.extension.decode();
      }
    });
  }
}
function isValidNationalNumber(countryCode, nationalNumber) {
  if (countryCode === 1) {
    // US phone numbers must be 7 digits long and can't start with 0 or 1.
    return nationalNumber > 1999999;
  }
  return nationalNumber > 0;
}

/**
 * Retrieves a particular byte from a 32 bit integer, dividing the bits from the
 * integer equally into 4 consecutive bytes. For example, the number 239939
 * represented in binary as a 32 bit integer would have the following form:
 *
 *     00000000 00000011 10101001 01000011
 *
 * The byte at index 1 in this sequence would be 0b00000011 or 3.
 */
function getByteFromUint32(value, index) {
  assert(value >= 0 && value < 2 ** 32);
  assert(index >= 0 && index <= 3);
  const offset = 8 * (3 - index);
  return value >>> offset & BYTE_MASK;
}
function base12DigitToChar(d) {
  assert(BASE_12_CHARS.has(d), emsg`Digit out of range: ${d}`);
  return BASE_12_CHARS.get(d);
}
function base12CharToDigit(c) {
  assert(BASE_12_DIGITS.has(c), emsg`Unrecognized character: ${c}`);
  return BASE_12_DIGITS.get(c);
}
function uint32FromBytes(bytes) {
  assert.same(bytes.length, 4);
  let sum = 0;
  for (let i = 0; i < 4; i++) {
    const byte = bytes[3 - i];
    assert(Number.isInteger(byte) && byte >= 0 && byte <= 256);
    sum += byte << i * 8 >>> 0;
  }
  return sum;
}

/**
 * Counts the number of '0's at the beginning of a string. If the string is only
 * '0's, the last '0' is not counted, since the last zero is needed to represent
 * the number. Only the *extra*, non-necessary zeros are counted.
 */
function countLeftZeroPadding(s) {
  const i = s.search(/[^0]/);
  if (i === -1) {
    return s.length - 1;
  }
  return i;
}
function leftPadBytes(r, length) {
  assert(r.length <= length);
  return [...arrayRepeat(0, length - r.length), ...r];
}

registerCanonical(Extension_, Extension);
registerCanonical(InternationalPhoneNumber_, InternationalPhoneNumber);

function _defineProperty$Z(obj, key, value) { key = _toPropertyKey$Z(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$Z(arg) { var key = _toPrimitive$Z(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$Z(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Person_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: 2,
      4: 7,
      5: InternationalPhoneNumber.shape,
      6: 2,
      7: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Person field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Person field display_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Person field image_key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Person field organization_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(InternationalPhoneNumber);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Person field phone_number.
                Expected type InternationalPhoneNumber.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'InternationalPhoneNumber', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Person field linked_in_url.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Person field pipedrive_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Person, 'Person', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Person to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get email() {
    return this.getFieldById(1, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(1, null, null);
  }
  get displayName() {
    return this.getFieldById(2, "");
  }
  setDisplayName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteDisplayName() {
    return this.setValueFieldById(2, null, null);
  }
  get imageKey() {
    return this.getFieldById(3, "");
  }
  setImageKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteImageKey() {
    return this.setValueFieldById(3, null, null);
  }
  get organizationId() {
    return this.getFieldById(4, 0);
  }
  setOrganizationId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteOrganizationId() {
    return this.setValueFieldById(4, null, null);
  }
  get phoneNumber() {
    return this.getFieldById(5, InternationalPhoneNumber.getDefault());
  }
  setPhoneNumber(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deletePhoneNumber() {
    return this.setValueFieldById(5, null, null);
  }
  get linkedInUrl() {
    return this.getFieldById(6, "");
  }
  setLinkedInUrl(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteLinkedInUrl() {
    return this.setValueFieldById(6, null, null);
  }
  get pipedriveId() {
    return this.getFieldById(7, 0);
  }
  setPipedriveId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deletePipedriveId() {
    return this.setValueFieldById(7, null, null);
  }
}
_defineProperty$Z(Person_, protoName, DEV_MODE ? 'Person' : '[Unknown]');
let Person = Person_;
(function (_Person) {})(Person || (Person = {}));
Object.defineProperties(Person, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "email": {
        value: 1
      },
      "display_name": {
        value: 2
      },
      "displayName": {
        value: 2
      },
      "image_key": {
        value: 3
      },
      "imageKey": {
        value: 3
      },
      "organization_id": {
        value: 4
      },
      "organizationId": {
        value: 4
      },
      "phone_number": {
        value: 5
      },
      "phoneNumber": {
        value: 5
      },
      "linked_in_url": {
        value: 6
      },
      "linkedInUrl": {
        value: 6
      },
      "pipedrive_id": {
        value: 7
      },
      "pipedriveId": {
        value: 7
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "email"
      },
      '2': {
        value: "display_name"
      },
      '3': {
        value: "image_key"
      },
      '4': {
        value: "organization_id"
      },
      '5': {
        value: "phone_number"
      },
      '6': {
        value: "linked_in_url"
      },
      '7': {
        value: "pipedrive_id"
      }
    }))
  }
});
Object.defineProperties(Person.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      5: InternationalPhoneNumber.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Person);
  Object.freeze(Person.prototype);
}

// tslint:disable-next-line:no-unused-variable
var UserGender__;
(function (UserGender__) {
  UserGender__[UserGender__["UNSET"] = 0] = "UNSET";
  UserGender__[UserGender__["MALE"] = 1] = "MALE";
  UserGender__[UserGender__["FEMALE"] = 2] = "FEMALE";
})(UserGender__ || (UserGender__ = {}));
const UserGender_ = defineEnumProperties(UserGender__, [0, 1, 2]);
const UserGender = extendEnum('UserGender', [["UNSET", 0], ["MALE", 1], ["FEMALE", 2]])(UserGender_);

const extensions = {
  UserProfile: function (Msg) {
    const _this = this;
    return class UserProfile extends Msg {
      get name() {
        const _this = this;
        return _this.firstName ? _this.lastName ? `${_this.firstName} ${_this.lastName}` : _this.firstName : _this.lastName;
      }
    };
  }
};

function _defineProperty$Y(obj, key, value) { key = _toPropertyKey$Y(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$Y(arg) { var key = _toPrimitive$Y(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$Y(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var ContactMethod__$1;
(function (ContactMethod__) {
  ContactMethod__[ContactMethod__["EMAIL"] = 0] = "EMAIL";
  ContactMethod__[ContactMethod__["PHONE"] = 1] = "PHONE";
})(ContactMethod__$1 || (ContactMethod__$1 = {}));
const ContactMethod_$1 = defineEnumProperties(ContactMethod__$1, [0, 1]);
const ContactMethod$1 = extendEnum('ContactMethod', [["EMAIL", 0], ["PHONE", 1]], extensions.ContactMethod)(ContactMethod_$1);
class UserProfile_$1 extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      21: 2,
      2: 2,
      3: 2,
      4: 2,
      18: DEPRECATED_Date.shape,
      5: 2,
      10: 7,
      22: 2,
      8: StandardUsAddress.shape,
      11: InternationalPhoneNumber.shape,
      9: RichText.shape,
      12: DEPRECATED_Date.shape,
      16: 0,
      17: 0,
      20: 2,
      13: DEPRECATED_Date.shape,
      14: 2,
      15: 1,
      19: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UserProfile field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 21:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field first_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field last_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 18:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field date_of_birth.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field profile_image_key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UserProfile field organization_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 22:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field organization_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field office_address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(InternationalPhoneNumber);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field phone_number.
                Expected type InternationalPhoneNumber.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'InternationalPhoneNumber', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field bio.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field join_date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 16:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message UserProfile field preferred_contact_method.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ContactMethod$1.fromStrictJson_(v, getFieldMap);
          }
          return ContactMethod$1.from(value);
        };
      case 17:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message UserProfile field gender.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return UserGender.fromStrictJson_(v, getFieldMap);
          }
          return UserGender.from(value);
        };
      case 20:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field slack_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field expires.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field permission_role.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message UserProfile field email_verified.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 19:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message UserProfile field active.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UserProfile$2, 'UserProfile', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UserProfile to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(21, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(21, null, null);
  }
  get email() {
    return this.getFieldById(2, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(2, null, null);
  }
  get firstName() {
    return this.getFieldById(3, "");
  }
  setFirstName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteFirstName() {
    return this.setValueFieldById(3, null, null);
  }
  get lastName() {
    return this.getFieldById(4, "");
  }
  setLastName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteLastName() {
    return this.setValueFieldById(4, null, null);
  }
  get dateOfBirth() {
    return this.getFieldById(18, DEPRECATED_Date.getDefault());
  }
  setDateOfBirth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(18, value, null);
  }
  deleteDateOfBirth() {
    return this.setValueFieldById(18, null, null);
  }
  get profileImageKey() {
    return this.getFieldById(5, "");
  }
  setProfileImageKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteProfileImageKey() {
    return this.setValueFieldById(5, null, null);
  }
  get organizationId() {
    return this.getFieldById(10, 0);
  }
  setOrganizationId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteOrganizationId() {
    return this.setValueFieldById(10, null, null);
  }
  get organizationSym() {
    return this.getFieldById(22, "");
  }
  setOrganizationSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteOrganizationSym() {
    return this.setValueFieldById(22, null, null);
  }
  get officeAddress() {
    return this.getFieldById(8, StandardUsAddress.getDefault());
  }
  setOfficeAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteOfficeAddress() {
    return this.setValueFieldById(8, null, null);
  }
  get phoneNumber() {
    return this.getFieldById(11, InternationalPhoneNumber.getDefault());
  }
  setPhoneNumber(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deletePhoneNumber() {
    return this.setValueFieldById(11, null, null);
  }
  get bio() {
    return this.getFieldById(9, RichText.getDefault());
  }
  setBio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteBio() {
    return this.setValueFieldById(9, null, null);
  }
  get joinDate() {
    return this.getFieldById(12, DEPRECATED_Date.getDefault());
  }
  setJoinDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteJoinDate() {
    return this.setValueFieldById(12, null, null);
  }
  get preferredContactMethod() {
    return this.getFieldById(16, 0);
  }
  setPreferredContactMethod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deletePreferredContactMethod() {
    return this.setValueFieldById(16, null, null);
  }
  get gender() {
    return this.getFieldById(17, 0);
  }
  setGender(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteGender() {
    return this.setValueFieldById(17, null, null);
  }
  get slackId() {
    return this.getFieldById(20, "");
  }
  setSlackId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, null);
  }
  deleteSlackId() {
    return this.setValueFieldById(20, null, null);
  }
  get expires() {
    return this.getFieldById(13, DEPRECATED_Date.getDefault());
  }
  setExpires(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteExpires() {
    return this.setValueFieldById(13, null, null);
  }
  get permissionRoleList() {
    return this.getRepeatedFieldById(14);
  }
  hasPermissionRole(value) {
    return this.repeatedFieldByIdHas(14, value);
  }
  addPermissionRole(value) {
    return this.addToRepeatedFieldById(14, value);
  }
  addEachPermissionRole(values) {
    let T = this;
    for (const value of values) {
      T = T.addPermissionRole(value);
    }
    return T;
  }
  setPermissionRole(index, value) {
    return this.setRepeatedFieldById(14, index, value);
  }
  removePermissionRole(index) {
    return this.removeFromRepeatedFieldById(14, index);
  }
  clearPermissionRoleList() {
    return this.clearRepeatedFieldById(14);
  }
  setPermissionRoleList(values) {
    return this.overwriteRepeatedFieldById(14, values);
  }
  get emailVerified() {
    return this.getFieldById(15, false);
  }
  setEmailVerified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteEmailVerified() {
    return this.setValueFieldById(15, null, null);
  }
  get active() {
    return this.getFieldById(19, false);
  }
  setActive(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(19, value, null);
  }
  deleteActive() {
    return this.setValueFieldById(19, null, null);
  }
}
_defineProperty$Y(UserProfile_$1, protoName, DEV_MODE ? 'UserProfile' : '[Unknown]');
let UserProfile$2 = extendMessage('UserProfile', extensions.UserProfile)(UserProfile_$1);
(function (_UserProfile) {})(UserProfile$2 || (UserProfile$2 = {}));
Object.defineProperties(UserProfile$2, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 21
      },
      "email": {
        value: 2
      },
      "first_name": {
        value: 3
      },
      "firstName": {
        value: 3
      },
      "last_name": {
        value: 4
      },
      "lastName": {
        value: 4
      },
      "date_of_birth": {
        value: 18
      },
      "dateOfBirth": {
        value: 18
      },
      "profile_image_key": {
        value: 5
      },
      "profileImageKey": {
        value: 5
      },
      "organization_id": {
        value: 10
      },
      "organizationId": {
        value: 10
      },
      "organization_sym": {
        value: 22
      },
      "organizationSym": {
        value: 22
      },
      "office_address": {
        value: 8
      },
      "officeAddress": {
        value: 8
      },
      "phone_number": {
        value: 11
      },
      "phoneNumber": {
        value: 11
      },
      "bio": {
        value: 9
      },
      "join_date": {
        value: 12
      },
      "joinDate": {
        value: 12
      },
      "preferred_contact_method": {
        value: 16
      },
      "preferredContactMethod": {
        value: 16
      },
      "gender": {
        value: 17
      },
      "slack_id": {
        value: 20
      },
      "slackId": {
        value: 20
      },
      "expires": {
        value: 13
      },
      "permission_role": {
        value: 14
      },
      "permissionRole": {
        value: 14
      },
      "email_verified": {
        value: 15
      },
      "emailVerified": {
        value: 15
      },
      "active": {
        value: 19
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '21': {
        value: "sym"
      },
      '2': {
        value: "email"
      },
      '3': {
        value: "first_name"
      },
      '4': {
        value: "last_name"
      },
      '18': {
        value: "date_of_birth"
      },
      '5': {
        value: "profile_image_key"
      },
      '10': {
        value: "organization_id"
      },
      '22': {
        value: "organization_sym"
      },
      '8': {
        value: "office_address"
      },
      '11': {
        value: "phone_number"
      },
      '9': {
        value: "bio"
      },
      '12': {
        value: "join_date"
      },
      '16': {
        value: "preferred_contact_method"
      },
      '17': {
        value: "gender"
      },
      '20': {
        value: "slack_id"
      },
      '13': {
        value: "expires"
      },
      '14': {
        value: "permission_role"
      },
      '15': {
        value: "email_verified"
      },
      '19': {
        value: "active"
      }
    }))
  }
});
Object.defineProperties(UserProfile$2.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      18: DEPRECATED_Date.prototype,
      8: StandardUsAddress.prototype,
      11: InternationalPhoneNumber.prototype,
      9: RichText.prototype,
      12: DEPRECATED_Date.prototype,
      13: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UserProfile$2);
  Object.freeze(UserProfile$2.prototype);
}

function formatArea(value) {
  return value.format();
}
function formatAreaAmount(value) {
  return value.formatAmount();
}
function areaToNumber(value) {
  return value.toNumber();
}
function formatAreaUnit(unit) {
  return AreaUnit.format(unit);
}

function isValidInternationalPhoneNumber(ipn) {
  return ipn.isValid();
}
function isValidExtension(extension) {
  return extension.isValid();
}
function isEmptyInternationalPhoneNumber(ipn) {
  return ipn.isEmpty();
}

// See also `formatInternationalPhoneNumberForUsUser`.
function internationalPhoneNumberToString(ipn) {
  return ipn[formatInternational]();
}
function internationalPhoneNumberToUrlString(ipn) {
  return ipn.toUrlString();
}
function formatInternationalPhoneNumberForUsUser(ipn) {
  return ipn[formatUnitedStates]();
}
function decodeExtension(extension) {
  return extension.decode();
}
function encodeExtension(decoded) {
  return Extension.encode(decoded);
}

function isRichTextEmpty(rt) {
  return rt.isEmpty();
}
function richTextToHtml(rt) {
  return rt.formatAsHtml();
}
function richTextToPlain(rt) {
  return rt.formatAsPlainText();
}

const TABLE = new Int32Array([0x00000000, 0xf26b8303, 0xe13b70f7, 0x1350f3f4, 0xc79a971f, 0x35f1141c, 0x26a1e7e8, 0xd4ca64eb, 0x8ad958cf, 0x78b2dbcc, 0x6be22838, 0x9989ab3b, 0x4d43cfd0, 0xbf284cd3, 0xac78bf27, 0x5e133c24, 0x105ec76f, 0xe235446c, 0xf165b798, 0x030e349b, 0xd7c45070, 0x25afd373, 0x36ff2087, 0xc494a384, 0x9a879fa0, 0x68ec1ca3, 0x7bbcef57, 0x89d76c54, 0x5d1d08bf, 0xaf768bbc, 0xbc267848, 0x4e4dfb4b, 0x20bd8ede, 0xd2d60ddd, 0xc186fe29, 0x33ed7d2a, 0xe72719c1, 0x154c9ac2, 0x061c6936, 0xf477ea35, 0xaa64d611, 0x580f5512, 0x4b5fa6e6, 0xb93425e5, 0x6dfe410e, 0x9f95c20d, 0x8cc531f9, 0x7eaeb2fa, 0x30e349b1, 0xc288cab2, 0xd1d83946, 0x23b3ba45, 0xf779deae, 0x05125dad, 0x1642ae59, 0xe4292d5a, 0xba3a117e, 0x4851927d, 0x5b016189, 0xa96ae28a, 0x7da08661, 0x8fcb0562, 0x9c9bf696, 0x6ef07595, 0x417b1dbc, 0xb3109ebf, 0xa0406d4b, 0x522bee48, 0x86e18aa3, 0x748a09a0, 0x67dafa54, 0x95b17957, 0xcba24573, 0x39c9c670, 0x2a993584, 0xd8f2b687, 0x0c38d26c, 0xfe53516f, 0xed03a29b, 0x1f682198, 0x5125dad3, 0xa34e59d0, 0xb01eaa24, 0x42752927, 0x96bf4dcc, 0x64d4cecf, 0x77843d3b, 0x85efbe38, 0xdbfc821c, 0x2997011f, 0x3ac7f2eb, 0xc8ac71e8, 0x1c661503, 0xee0d9600, 0xfd5d65f4, 0x0f36e6f7, 0x61c69362, 0x93ad1061, 0x80fde395, 0x72966096, 0xa65c047d, 0x5437877e, 0x4767748a, 0xb50cf789, 0xeb1fcbad, 0x197448ae, 0x0a24bb5a, 0xf84f3859, 0x2c855cb2, 0xdeeedfb1, 0xcdbe2c45, 0x3fd5af46, 0x7198540d, 0x83f3d70e, 0x90a324fa, 0x62c8a7f9, 0xb602c312, 0x44694011, 0x5739b3e5, 0xa55230e6, 0xfb410cc2, 0x092a8fc1, 0x1a7a7c35, 0xe811ff36, 0x3cdb9bdd, 0xceb018de, 0xdde0eb2a, 0x2f8b6829, 0x82f63b78, 0x709db87b, 0x63cd4b8f, 0x91a6c88c, 0x456cac67, 0xb7072f64, 0xa457dc90, 0x563c5f93, 0x082f63b7, 0xfa44e0b4, 0xe9141340, 0x1b7f9043, 0xcfb5f4a8, 0x3dde77ab, 0x2e8e845f, 0xdce5075c, 0x92a8fc17, 0x60c37f14, 0x73938ce0, 0x81f80fe3, 0x55326b08, 0xa759e80b, 0xb4091bff, 0x466298fc, 0x1871a4d8, 0xea1a27db, 0xf94ad42f, 0x0b21572c, 0xdfeb33c7, 0x2d80b0c4, 0x3ed04330, 0xccbbc033, 0xa24bb5a6, 0x502036a5, 0x4370c551, 0xb11b4652, 0x65d122b9, 0x97baa1ba, 0x84ea524e, 0x7681d14d, 0x2892ed69, 0xdaf96e6a, 0xc9a99d9e, 0x3bc21e9d, 0xef087a76, 0x1d63f975, 0x0e330a81, 0xfc588982, 0xb21572c9, 0x407ef1ca, 0x532e023e, 0xa145813d, 0x758fe5d6, 0x87e466d5, 0x94b49521, 0x66df1622, 0x38cc2a06, 0xcaa7a905, 0xd9f75af1, 0x2b9cd9f2, 0xff56bd19, 0x0d3d3e1a, 0x1e6dcdee, 0xec064eed, 0xc38d26c4, 0x31e6a5c7, 0x22b65633, 0xd0ddd530, 0x0417b1db, 0xf67c32d8, 0xe52cc12c, 0x1747422f, 0x49547e0b, 0xbb3ffd08, 0xa86f0efc, 0x5a048dff, 0x8ecee914, 0x7ca56a17, 0x6ff599e3, 0x9d9e1ae0, 0xd3d3e1ab, 0x21b862a8, 0x32e8915c, 0xc083125f, 0x144976b4, 0xe622f5b7, 0xf5720643, 0x07198540, 0x590ab964, 0xab613a67, 0xb831c993, 0x4a5a4a90, 0x9e902e7b, 0x6cfbad78, 0x7fab5e8c, 0x8dc0dd8f, 0xe330a81a, 0x115b2b19, 0x020bd8ed, 0xf0605bee, 0x24aa3f05, 0xd6c1bc06, 0xc5914ff2, 0x37faccf1, 0x69e9f0d5, 0x9b8273d6, 0x88d28022, 0x7ab90321, 0xae7367ca, 0x5c18e4c9, 0x4f48173d, 0xbd23943e, 0xf36e6f75, 0x0105ec76, 0x12551f82, 0xe03e9c81, 0x34f4f86a, 0xc69f7b69, 0xd5cf889d, 0x27a40b9e, 0x79b737ba, 0x8bdcb4b9, 0x988c474d, 0x6ae7c44e, 0xbe2da0a5, 0x4c4623a6, 0x5f16d052, 0xad7d5351]);
function calculateCrc32c(buffer, initial = 0) {
  const input = new Uint8Array(buffer);
  let crc = (initial | 0) ^ -1;
  for (let i = 0; i < input.length; i++) {
    crc = TABLE[(crc ^ input[i]) & 0xff] ^ crc >>> 8;
  }
  return (crc ^ -1) >>> 0;
}

function stringToArrayBuffer(s) {
  const uint16Array = new Uint16Array(s.length);
  let i = 0;
  for (const c of s) {
    uint16Array[i++] = c.codePointAt(0);
  }
  return uint16Array.buffer;
}

const ContentType = {
  [Symbol.iterator]: function () {
    const _this = this;
    return [0, 1, 2][Symbol.iterator]();
  },
  validValues_: new Set([0, 1, 2]),
  has: function (value) {
    return this.validValues_.has(value);
  },
  NONE: 0,
  STRING: 1,
  JSON: 2
};
const ChecksumType = {
  [Symbol.iterator]: function () {
    const _this = this;
    return [0][Symbol.iterator]();
  },
  validValues_: new Set([0]),
  has: function (value) {
    return this.validValues_.has(value);
  },
  CRC32C: 0
};
function getBlob(url, onProgress) {
  if (onProgress === undefined) onProgress = null;
  const xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';
  return new Promise(function (resolve, reject) {
    xhr.addEventListener('load', function () {
      switch (xhr.status) {
        case 200:
          resolve(xhr.response);
          break;
        case 400:
          reject(xhr);
          break;
        default:
          reject(new Error(`Unexpected status ${xhr.status}.`));
          break;
      }
    });
    xhr.addEventListener('error', function () {
      reject();
    });
    if (onProgress) {
      xhr.addEventListener('progress', function (event) {
        let $3388, $3389;
        onProgress(($3388 = event.loaded, $3388 == null ? 0 : $3388), ($3389 = event.total, $3389 == null ? Infinity : $3389));
      });
    }
    xhr.open('GET', url);
    xhr.withCredentials = true;
    xhr.send(null);
  });
}
function post(url, data) {
  return async(function* () {
    const xhr = yield postRaw(url, JSON.stringify(data), new Map([['Content-Type', 'application/json; charset=utf-8']]));
    return getContent(xhr);
  }());
}
function postRaw(url, rawData, headers) {
  if (headers === undefined) headers = null;
  const xhr = new XMLHttpRequest();
  return new Promise(function (resolve, reject) {
    xhr.addEventListener('load', function () {
      switch (xhr.status) {
        case 200:
          resolve(xhr);
          break;
        case 400:
          reject(xhr);
          break;
        default:
          reject(new Error(`Unexpected status ${xhr.status}.`));
          break;
      }
    });
    xhr.addEventListener('error', function () {
      let msg = '';
      try {
        msg = xhr.responseText;
      } catch (err$3385) {
        const e = err$3385;
        {}
      }
      reject(new Error(`Error loading ${JSON.stringify(url)}: ${msg}`));
    });
    xhr.open('POST', url);
    xhr.withCredentials = true;
    if (headers) {
      for (const [header, value] of headers.entries()) {
        xhr.setRequestHeader(header, value);
      }
    }
    xhr.send(rawData);
  });
}
function getContent(xhr) {
  const type = getContentType(xhr.getResponseHeader('Content-Type'));
  switch (type) {
    case ContentType.NONE:
      return null;
      break;
    case ContentType.STRING:
      return xhr.responseText;
      break;
    case ContentType.JSON:
      return xhr.responseText == '' ? null : JSON.parse(xhr.responseText);
      break;
  }
  runtime.checkAsserts() ? runtime.ensure(false, function () {
    return `Unknown content type (${type}).`;
  }) : undefined;
}
function getContentType(type) {
  if (type == null) {
    return ContentType.NONE;
  }
  if (/^text\/plain($|\;)/.test(type)) {
    return ContentType.STRING;
  }
  if (/^application\/json($|\;)/.test(type)) {
    return ContentType.JSON;
  }
  throw new Error(`Unrecognized content type "${type}".`);
}
function postWithChecksum(url, data, checksumType) {
  return async(function* () {
    let $3390;
    runtime.checkAsserts() ? runtime.ensure(checksumType == ChecksumType.CRC32C, null) : undefined;
    const stringData = ($3390 = JSON.stringify(data), $3390 == null ? '' : $3390);
    const checksum = calculateCrc32c(stringToArrayBuffer(stringData));
    let xhr;
    try {
      xhr = yield postRaw(url, stringData, new Map([['Content-Type', 'application/json; charset=utf-8'], ['StackSource-Checksum', checksum]]));
    } catch (err$3386) {
      const e = err$3386;
      if (e instanceof XMLHttpRequest) {
        validateXhrChecksum(e, checksumType);
        throw getContent(e);
      } else throw err$3386;
    }
    validateXhrChecksum(xhr, checksumType);
    return getContent(xhr);
  }());
}
function validateXhrChecksum(xhr, checksumType) {
  runtime.checkAsserts() ? runtime.ensure(checksumType == ChecksumType.CRC32C, null) : undefined;
  let checksumString;
  try {
    checksumString = xhr.getResponseHeader('StackSource-Checksum');
  } catch (err$3387) {
    const e = err$3387;
    {
      return;
    }
  }
  if (!checksumString) {
    return;
  }
  runtime.ensure(typeof checksumString == 'string', function () {
    return 'Checksum expected.';
  });
  const checksum = +checksumString;
  const data = xhr.responseText;
  const expected = calculateCrc32c(stringToArrayBuffer(data));
  runtime.ensure(checksum == expected, function () {
    return `Failed checksum. Expected (${expected}), but found (${checksum}).`;
  });
}

const config$1 = new Configuration(['backend-root', 'upload-root']);
function getBackendRoot() {
  return config$1.get('backend-root');
}
function setBackendRoot(value) {
  config$1.set('backend-root', value);
  setAppRoot(value);
}
function getUploadRoot() {
  return config$1.get('upload-root');
}
function setUploadRoot(value) {
  config$1.set('upload-root', value);
}

function _defineProperty$X(obj, key, value) { key = _toPropertyKey$X(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$X(arg) { var key = _toPrimitive$X(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$X(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class AdwordsConversion_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message AdwordsConversion field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AdwordsConversion field label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(AdwordsConversion, 'AdwordsConversion', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message AdwordsConversion to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get label() {
    return this.getFieldById(2, "");
  }
  setLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteLabel() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$X(AdwordsConversion_, protoName, DEV_MODE ? 'AdwordsConversion' : '[Unknown]');
let AdwordsConversion = AdwordsConversion_;
(function (_AdwordsConversion) {})(AdwordsConversion || (AdwordsConversion = {}));
Object.defineProperties(AdwordsConversion, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "label": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "label"
      }
    }))
  }
});
Object.defineProperties(AdwordsConversion.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(AdwordsConversion);
  Object.freeze(AdwordsConversion.prototype);
}
class YahooConversion_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message YahooConversion field project_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message YahooConversion field pixel_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(YahooConversion, 'YahooConversion', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message YahooConversion to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get projectId() {
    return this.getFieldById(1, "");
  }
  setProjectId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteProjectId() {
    return this.setValueFieldById(1, null, null);
  }
  get pixelId() {
    return this.getFieldById(2, "");
  }
  setPixelId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deletePixelId() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$X(YahooConversion_, protoName, DEV_MODE ? 'YahooConversion' : '[Unknown]');
let YahooConversion = YahooConversion_;
(function (_YahooConversion) {})(YahooConversion || (YahooConversion = {}));
Object.defineProperties(YahooConversion, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "project_id": {
        value: 1
      },
      "projectId": {
        value: 1
      },
      "pixel_id": {
        value: 2
      },
      "pixelId": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "project_id"
      },
      '2': {
        value: "pixel_id"
      }
    }))
  }
});
Object.defineProperties(YahooConversion.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(YahooConversion);
  Object.freeze(YahooConversion.prototype);
}
class FacebookConversionInitialize_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message FacebookConversionInitialize field pixel_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FacebookConversionInitialize, 'FacebookConversionInitialize', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FacebookConversionInitialize to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get pixelId() {
    return this.getFieldById(1, "");
  }
  setPixelId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deletePixelId() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$X(FacebookConversionInitialize_, protoName, DEV_MODE ? 'FacebookConversionInitialize' : '[Unknown]');
let FacebookConversionInitialize = FacebookConversionInitialize_;
(function (_FacebookConversionInitialize) {})(FacebookConversionInitialize || (FacebookConversionInitialize = {}));
Object.defineProperties(FacebookConversionInitialize, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "pixel_id": {
        value: 1
      },
      "pixelId": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "pixel_id"
      }
    }))
  }
});
Object.defineProperties(FacebookConversionInitialize.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FacebookConversionInitialize);
  Object.freeze(FacebookConversionInitialize.prototype);
}
class FacebookConversionPageView_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {};
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {}
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FacebookConversionPageView, 'FacebookConversionPageView', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FacebookConversionPageView to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
}
_defineProperty$X(FacebookConversionPageView_, protoName, DEV_MODE ? 'FacebookConversionPageView' : '[Unknown]');
let FacebookConversionPageView = FacebookConversionPageView_;
(function (_FacebookConversionPageView) {})(FacebookConversionPageView || (FacebookConversionPageView = {}));
Object.defineProperties(FacebookConversionPageView, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {}))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {}))
  }
});
Object.defineProperties(FacebookConversionPageView.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FacebookConversionPageView);
  Object.freeze(FacebookConversionPageView.prototype);
}
class FacebookConversionAddToCart_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {};
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {}
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FacebookConversionAddToCart, 'FacebookConversionAddToCart', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FacebookConversionAddToCart to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
}
_defineProperty$X(FacebookConversionAddToCart_, protoName, DEV_MODE ? 'FacebookConversionAddToCart' : '[Unknown]');
let FacebookConversionAddToCart = FacebookConversionAddToCart_;
(function (_FacebookConversionAddToCart) {})(FacebookConversionAddToCart || (FacebookConversionAddToCart = {}));
Object.defineProperties(FacebookConversionAddToCart, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {}))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {}))
  }
});
Object.defineProperties(FacebookConversionAddToCart.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FacebookConversionAddToCart);
  Object.freeze(FacebookConversionAddToCart.prototype);
}
class FacebookConversionCompleteRegistration_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {};
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {}
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FacebookConversionCompleteRegistration, 'FacebookConversionCompleteRegistration', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FacebookConversionCompleteRegistration to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
}
_defineProperty$X(FacebookConversionCompleteRegistration_, protoName, DEV_MODE ? 'FacebookConversionCompleteRegistration' : '[Unknown]');
let FacebookConversionCompleteRegistration = FacebookConversionCompleteRegistration_;
(function (_FacebookConversionCompleteRegistration) {})(FacebookConversionCompleteRegistration || (FacebookConversionCompleteRegistration = {}));
Object.defineProperties(FacebookConversionCompleteRegistration, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {}))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {}))
  }
});
Object.defineProperties(FacebookConversionCompleteRegistration.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FacebookConversionCompleteRegistration);
  Object.freeze(FacebookConversionCompleteRegistration.prototype);
}
var _FacebookConversion_Type_;
(function (_FacebookConversion_Type_) {
  _FacebookConversion_Type_[_FacebookConversion_Type_["UNSET"] = 0] = "UNSET";
  _FacebookConversion_Type_[_FacebookConversion_Type_["INITIALIZE"] = 1] = "INITIALIZE";
  _FacebookConversion_Type_[_FacebookConversion_Type_["PAGE_VIEW"] = 2] = "PAGE_VIEW";
  _FacebookConversion_Type_[_FacebookConversion_Type_["ADD_TO_CART"] = 3] = "ADD_TO_CART";
  _FacebookConversion_Type_[_FacebookConversion_Type_["COMPLETE_REGISTRATION"] = 4] = "COMPLETE_REGISTRATION";
})(_FacebookConversion_Type_ || (_FacebookConversion_Type_ = {}));
const _FacebookConversion_Type = defineEnumProperties(_FacebookConversion_Type_, [1, 2, 3, 4]);
class FacebookConversion_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: FacebookConversionInitialize.shape,
      2: FacebookConversionPageView.shape,
      3: FacebookConversionAddToCart.shape,
      4: FacebookConversionCompleteRegistration.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FacebookConversionInitialize);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FacebookConversion field initialize.
                Expected type FacebookConversionInitialize.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FacebookConversionInitialize', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FacebookConversionPageView);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FacebookConversion field page_view.
                Expected type FacebookConversionPageView.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FacebookConversionPageView', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FacebookConversionAddToCart);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FacebookConversion field add_to_cart.
                Expected type FacebookConversionAddToCart.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FacebookConversionAddToCart', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FacebookConversionCompleteRegistration);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message FacebookConversion field complete_registration.
                Expected type FacebookConversionCompleteRegistration.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FacebookConversionCompleteRegistration', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FacebookConversion, 'FacebookConversion', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FacebookConversion to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _FacebookConversion_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2, 3, 4]);
  }
  get initialize() {
    return this.getFieldById(1, FacebookConversionInitialize.getDefault());
  }
  setInitialize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteInitialize() {
    return this.setValueFieldById(1, null, "type");
  }
  get pageView() {
    return this.getFieldById(2, FacebookConversionPageView.getDefault());
  }
  setPageView(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deletePageView() {
    return this.setValueFieldById(2, null, "type");
  }
  get addToCart() {
    return this.getFieldById(3, FacebookConversionAddToCart.getDefault());
  }
  setAddToCart(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "type");
  }
  deleteAddToCart() {
    return this.setValueFieldById(3, null, "type");
  }
  get completeRegistration() {
    return this.getFieldById(4, FacebookConversionCompleteRegistration.getDefault());
  }
  setCompleteRegistration(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, "type");
  }
  deleteCompleteRegistration() {
    return this.setValueFieldById(4, null, "type");
  }
}
_defineProperty$X(FacebookConversion_, protoName, DEV_MODE ? 'FacebookConversion' : '[Unknown]');
let FacebookConversion = FacebookConversion_;
(function (_FacebookConversion) {})(FacebookConversion || (FacebookConversion = {}));
Object.defineProperties(FacebookConversion, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "initialize": {
        value: 1
      },
      "page_view": {
        value: 2
      },
      "pageView": {
        value: 2
      },
      "add_to_cart": {
        value: 3
      },
      "addToCart": {
        value: 3
      },
      "complete_registration": {
        value: 4
      },
      "completeRegistration": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "initialize"
      },
      '2': {
        value: "page_view"
      },
      '3': {
        value: "add_to_cart"
      },
      '4': {
        value: "complete_registration"
      }
    }))
  }
});
Object.defineProperties(FacebookConversion.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2, 3, 4]
    }
  },
  prototypes_: {
    value: {
      1: FacebookConversionInitialize.prototype,
      2: FacebookConversionPageView.prototype,
      3: FacebookConversionAddToCart.prototype,
      4: FacebookConversionCompleteRegistration.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FacebookConversion);
  Object.freeze(FacebookConversion.prototype);
}
class BingConversionInitialize_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message BingConversionInitialize field tag_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(BingConversionInitialize, 'BingConversionInitialize', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message BingConversionInitialize to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get tagId() {
    return this.getFieldById(1, "");
  }
  setTagId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteTagId() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$X(BingConversionInitialize_, protoName, DEV_MODE ? 'BingConversionInitialize' : '[Unknown]');
let BingConversionInitialize = BingConversionInitialize_;
(function (_BingConversionInitialize) {})(BingConversionInitialize || (BingConversionInitialize = {}));
Object.defineProperties(BingConversionInitialize, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "tag_id": {
        value: 1
      },
      "tagId": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "tag_id"
      }
    }))
  }
});
Object.defineProperties(BingConversionInitialize.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(BingConversionInitialize);
  Object.freeze(BingConversionInitialize.prototype);
}
var _BingConversion_Type_;
(function (_BingConversion_Type_) {
  _BingConversion_Type_[_BingConversion_Type_["UNSET"] = 0] = "UNSET";
  _BingConversion_Type_[_BingConversion_Type_["INITIALIZE"] = 1] = "INITIALIZE";
})(_BingConversion_Type_ || (_BingConversion_Type_ = {}));
const _BingConversion_Type = defineEnumProperties(_BingConversion_Type_, [1]);
class BingConversion_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: BingConversionInitialize.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(BingConversionInitialize);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message BingConversion field initialize.
                Expected type BingConversionInitialize.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'BingConversionInitialize', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(BingConversion, 'BingConversion', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message BingConversion to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _BingConversion_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1]);
  }
  get initialize() {
    return this.getFieldById(1, BingConversionInitialize.getDefault());
  }
  setInitialize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteInitialize() {
    return this.setValueFieldById(1, null, "type");
  }
}
_defineProperty$X(BingConversion_, protoName, DEV_MODE ? 'BingConversion' : '[Unknown]');
let BingConversion = BingConversion_;
(function (_BingConversion) {})(BingConversion || (BingConversion = {}));
Object.defineProperties(BingConversion, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "initialize": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "initialize"
      }
    }))
  }
});
Object.defineProperties(BingConversion.prototype, {
  oneofs_: {
    value: {
      "type": [1]
    }
  },
  prototypes_: {
    value: {
      1: BingConversionInitialize.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(BingConversion);
  Object.freeze(BingConversion.prototype);
}
class LinkedInConversionInitialize_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message LinkedInConversionInitialize field partner_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LinkedInConversionInitialize, 'LinkedInConversionInitialize', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LinkedInConversionInitialize to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get partnerId() {
    return this.getFieldById(1, "");
  }
  setPartnerId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deletePartnerId() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$X(LinkedInConversionInitialize_, protoName, DEV_MODE ? 'LinkedInConversionInitialize' : '[Unknown]');
let LinkedInConversionInitialize = LinkedInConversionInitialize_;
(function (_LinkedInConversionInitialize) {})(LinkedInConversionInitialize || (LinkedInConversionInitialize = {}));
Object.defineProperties(LinkedInConversionInitialize, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "partner_id": {
        value: 1
      },
      "partnerId": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "partner_id"
      }
    }))
  }
});
Object.defineProperties(LinkedInConversionInitialize.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LinkedInConversionInitialize);
  Object.freeze(LinkedInConversionInitialize.prototype);
}
var _LinkedInConversion_Type_;
(function (_LinkedInConversion_Type_) {
  _LinkedInConversion_Type_[_LinkedInConversion_Type_["UNSET"] = 0] = "UNSET";
  _LinkedInConversion_Type_[_LinkedInConversion_Type_["INITIALIZE"] = 1] = "INITIALIZE";
})(_LinkedInConversion_Type_ || (_LinkedInConversion_Type_ = {}));
const _LinkedInConversion_Type = defineEnumProperties(_LinkedInConversion_Type_, [1]);
class LinkedInConversion_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: LinkedInConversionInitialize.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(LinkedInConversionInitialize);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message LinkedInConversion field initialize.
                Expected type LinkedInConversionInitialize.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'LinkedInConversionInitialize', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(LinkedInConversion, 'LinkedInConversion', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message LinkedInConversion to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _LinkedInConversion_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1]);
  }
  get initialize() {
    return this.getFieldById(1, LinkedInConversionInitialize.getDefault());
  }
  setInitialize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteInitialize() {
    return this.setValueFieldById(1, null, "type");
  }
}
_defineProperty$X(LinkedInConversion_, protoName, DEV_MODE ? 'LinkedInConversion' : '[Unknown]');
let LinkedInConversion = LinkedInConversion_;
(function (_LinkedInConversion) {})(LinkedInConversion || (LinkedInConversion = {}));
Object.defineProperties(LinkedInConversion, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "initialize": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "initialize"
      }
    }))
  }
});
Object.defineProperties(LinkedInConversion.prototype, {
  oneofs_: {
    value: {
      "type": [1]
    }
  },
  prototypes_: {
    value: {
      1: LinkedInConversionInitialize.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(LinkedInConversion);
  Object.freeze(LinkedInConversion.prototype);
}
var _ConversionConfig_Service_;
(function (_ConversionConfig_Service_) {
  _ConversionConfig_Service_[_ConversionConfig_Service_["UNSET"] = 0] = "UNSET";
  _ConversionConfig_Service_[_ConversionConfig_Service_["ADWORDS"] = 1] = "ADWORDS";
  _ConversionConfig_Service_[_ConversionConfig_Service_["YAHOO"] = 2] = "YAHOO";
  _ConversionConfig_Service_[_ConversionConfig_Service_["FACEBOOK"] = 3] = "FACEBOOK";
  _ConversionConfig_Service_[_ConversionConfig_Service_["BING"] = 4] = "BING";
  _ConversionConfig_Service_[_ConversionConfig_Service_["LINKED_IN"] = 5] = "LINKED_IN";
})(_ConversionConfig_Service_ || (_ConversionConfig_Service_ = {}));
const _ConversionConfig_Service = defineEnumProperties(_ConversionConfig_Service_, [1, 2, 3, 4, 5]);
class ConversionConfig_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: AdwordsConversion.shape,
      2: YahooConversion.shape,
      3: FacebookConversion.shape,
      4: BingConversion.shape,
      5: LinkedInConversion.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(AdwordsConversion);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ConversionConfig field adwords.
                Expected type AdwordsConversion.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'AdwordsConversion', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(YahooConversion);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ConversionConfig field yahoo.
                Expected type YahooConversion.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'YahooConversion', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FacebookConversion);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ConversionConfig field facebook.
                Expected type FacebookConversion.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FacebookConversion', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(BingConversion);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ConversionConfig field bing.
                Expected type BingConversion.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'BingConversion', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(LinkedInConversion);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ConversionConfig field linked_in.
                Expected type LinkedInConversion.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'LinkedInConversion', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ConversionConfig, 'ConversionConfig', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ConversionConfig to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ServiceCase() {
    return _ConversionConfig_Service;
  }
  get serviceCase() {
    return this.getCaseFromIds([1, 2, 3, 4, 5]);
  }
  get adwords() {
    return this.getFieldById(1, AdwordsConversion.getDefault());
  }
  setAdwords(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "service");
  }
  deleteAdwords() {
    return this.setValueFieldById(1, null, "service");
  }
  get yahoo() {
    return this.getFieldById(2, YahooConversion.getDefault());
  }
  setYahoo(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "service");
  }
  deleteYahoo() {
    return this.setValueFieldById(2, null, "service");
  }
  get facebook() {
    return this.getFieldById(3, FacebookConversion.getDefault());
  }
  setFacebook(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "service");
  }
  deleteFacebook() {
    return this.setValueFieldById(3, null, "service");
  }
  get bing() {
    return this.getFieldById(4, BingConversion.getDefault());
  }
  setBing(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, "service");
  }
  deleteBing() {
    return this.setValueFieldById(4, null, "service");
  }
  get linkedIn() {
    return this.getFieldById(5, LinkedInConversion.getDefault());
  }
  setLinkedIn(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, "service");
  }
  deleteLinkedIn() {
    return this.setValueFieldById(5, null, "service");
  }
}
_defineProperty$X(ConversionConfig_, protoName, DEV_MODE ? 'ConversionConfig' : '[Unknown]');
let ConversionConfig = ConversionConfig_;
(function (_ConversionConfig) {})(ConversionConfig || (ConversionConfig = {}));
Object.defineProperties(ConversionConfig, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "adwords": {
        value: 1
      },
      "yahoo": {
        value: 2
      },
      "facebook": {
        value: 3
      },
      "bing": {
        value: 4
      },
      "linked_in": {
        value: 5
      },
      "linkedIn": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "adwords"
      },
      '2': {
        value: "yahoo"
      },
      '3': {
        value: "facebook"
      },
      '4': {
        value: "bing"
      },
      '5': {
        value: "linked_in"
      }
    }))
  }
});
Object.defineProperties(ConversionConfig.prototype, {
  oneofs_: {
    value: {
      "service": [1, 2, 3, 4, 5]
    }
  },
  prototypes_: {
    value: {
      1: AdwordsConversion.prototype,
      2: YahooConversion.prototype,
      3: FacebookConversion.prototype,
      4: BingConversion.prototype,
      5: LinkedInConversion.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ConversionConfig);
  Object.freeze(ConversionConfig.prototype);
}

// tslint:disable-next-line:no-unused-variable
var ConversionType__;
(function (ConversionType__) {
  ConversionType__[ConversionType__["PLATFORM_INITIALIZED"] = 0] = "PLATFORM_INITIALIZED";
  ConversionType__[ConversionType__["PAGE_CHANGED"] = 3] = "PAGE_CHANGED";
  ConversionType__[ConversionType__["WIZARD_ADDRESS_COMPLETED"] = 1] = "WIZARD_ADDRESS_COMPLETED";
  ConversionType__[ConversionType__["PROJECT_CREATED"] = 2] = "PROJECT_CREATED";
  ConversionType__[ConversionType__["USER_REGISTERED"] = 4] = "USER_REGISTERED";
})(ConversionType__ || (ConversionType__ = {}));
const ConversionType_ = defineEnumProperties(ConversionType__, [0, 3, 1, 2, 4]);
const ConversionType = extendEnum('ConversionType', [["PLATFORM_INITIALIZED", 0], ["PAGE_CHANGED", 3], ["WIZARD_ADDRESS_COMPLETED", 1], ["PROJECT_CREATED", 2], ["USER_REGISTERED", 4]])(ConversionType_);

const config = new Configuration(['provider-app-root', 'edge-app-root', 'static-root', 'conversions']);
function getProviderAppRoot() {
  return config.get('provider-app-root');
}
function setProviderAppRoot(value) {
  return config.set('provider-app-root', value);
}
function getEdgeAppRoot() {
  return config.get('edge-app-root');
}
function setEdgeAppRoot(value) {
  return config.set('edge-app-root', value);
}
function getStaticRoot() {
  return config.get('static-root');
}
function setStaticRoot(value) {
  return config.set('static-root', value);
}
function getConversions() {
  return config.get('conversions');
}
function setConversions(value) {
  config.set('conversions', new Map(function* () {
    for (const [type, data] of value) {
      runtime.checkAsserts() ? runtime.ensure(ConversionType.has(type), null) : undefined;
      runtime.checkAsserts() ? runtime.ensure(Array.isArray(data), null) : undefined;
      yield [type, new List(function* () {
        for (const d of data) {
          yield ConversionConfig.fromJson(d);
        }
      }())];
    }
  }()));
}
function loadBloomConfig() {
  let test$1720, test$1721, test$1722, test$1723, test$1724;
  const bloom = getBloomConfig();
  setProviderAppRoot(runtime.getComputed(bloom, 'PROVIDER_APP_ROOT') || '');
  setEdgeAppRoot(runtime.getComputed(bloom, 'EDGE_APP_ROOT') || '');
  setStaticRoot((test$1720 = runtime.getComputed(bloom, 'STATIC_ROOT'), runtime.checkAsserts() ? runtime.ensure(test$1720, null) : test$1720));
  setBackendRoot((test$1721 = runtime.getComputed(bloom, 'BACKEND_ROOT'), runtime.checkAsserts() ? runtime.ensure(test$1721, null) : test$1721));
  setUploadRoot((test$1722 = runtime.getComputed(bloom, 'UPLOAD_ROOT'), runtime.checkAsserts() ? runtime.ensure(test$1722, null) : test$1722));
  setUserContentRoot((test$1723 = runtime.getComputed(bloom, 'USER_CONTENT_ROOT'), runtime.checkAsserts() ? runtime.ensure(test$1723, null) : test$1723));
  setConversions((test$1724 = runtime.getComputed(bloom, 'CONVERSIONS'), runtime.checkAsserts() ? runtime.ensure(test$1724, null) : test$1724));
}
function getBloomConfig() {
  let test$1725;
  runtime.checkAsserts() ? runtime.ensure(typeof __bloomConfig != 'undefined', function () {
    return `__bloomConfig is not defined.`;
  }) : undefined;
  return test$1725 = __bloomConfig, runtime.checkAsserts() ? runtime.ensure(test$1725, null) : test$1725;
}
if (typeof __bloomConfig != 'undefined') {
  loadBloomConfig();
}

function resolve(remote, type) {
  if (type === undefined) type = '';
  if (/^\/@r\:/.test(remote)) {
    switch (type) {
      case '':
        return `${getBackendRoot()}${remote}`;
        break;
      case 'upload':
        return `${getUploadRoot()}${remote}`;
        break;
    }
  }
  return remote;
}

function getUserAndProfileImage$1(...args) {
  return postWithChecksum(resolve('/@r:/user.getUserAndProfileImage'), args, ChecksumType.CRC32C);
}
function sendEmailVerification(...args) {
  return postWithChecksum(resolve('/@r:/user.sendEmailVerification'), args, ChecksumType.CRC32C);
}

function _defineProperty$W(obj, key, value) { key = _toPropertyKey$W(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$W(arg) { var key = _toPrimitive$W(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$W(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var UserPermission__;
(function (UserPermission__) {
  UserPermission__[UserPermission__["USERS_CREATE"] = 0] = "USERS_CREATE";
  UserPermission__[UserPermission__["USERS_CREATE_TEMPORARY"] = 1] = "USERS_CREATE_TEMPORARY";
  UserPermission__[UserPermission__["USERS_READ"] = 2] = "USERS_READ";
  UserPermission__[UserPermission__["USERS_EDIT"] = 3] = "USERS_EDIT";
  UserPermission__[UserPermission__["USERS_EDIT_SECURITY_KEYS"] = 38] = "USERS_EDIT_SECURITY_KEYS";
  UserPermission__[UserPermission__["USER_PROFILES_READ"] = 17] = "USER_PROFILES_READ";
  UserPermission__[UserPermission__["USER_PROFILES_EDIT"] = 18] = "USER_PROFILES_EDIT";
  UserPermission__[UserPermission__["USER_PROFILES_LIST"] = 19] = "USER_PROFILES_LIST";
  UserPermission__[UserPermission__["USERS_DEACTIVATE"] = 36] = "USERS_DEACTIVATE";
  UserPermission__[UserPermission__["PROJECTS_CREATE"] = 4] = "PROJECTS_CREATE";
  UserPermission__[UserPermission__["PROJECTS_MAKE_OFFER"] = 5] = "PROJECTS_MAKE_OFFER";
  UserPermission__[UserPermission__["PROJECTS_ADD_OFFER"] = 20] = "PROJECTS_ADD_OFFER";
  UserPermission__[UserPermission__["PROJECTS_APPROVE_OFFER"] = 27] = "PROJECTS_APPROVE_OFFER";
  UserPermission__[UserPermission__["PROJECTS_UNACCEPT_OFFER"] = 29] = "PROJECTS_UNACCEPT_OFFER";
  UserPermission__[UserPermission__["PROJECTS_DELETE_OFFER"] = 33] = "PROJECTS_DELETE_OFFER";
  UserPermission__[UserPermission__["PROJECTS_READ_ALL_DRAFTS"] = 6] = "PROJECTS_READ_ALL_DRAFTS";
  UserPermission__[UserPermission__["PROJECTS_EDIT_ALL"] = 7] = "PROJECTS_EDIT_ALL";
  UserPermission__[UserPermission__["PROJECTS_SHARE"] = 21] = "PROJECTS_SHARE";
  UserPermission__[UserPermission__["PROJECTS_ADD_STACKSOURCE_SOURCED_SHARE"] = 22] = "PROJECTS_ADD_STACKSOURCE_SOURCED_SHARE";
  UserPermission__[UserPermission__["DEPRECATED__PROJECTS_STATUS_READ"] = 31] = "DEPRECATED__PROJECTS_STATUS_READ";
  UserPermission__[UserPermission__["DEPRECATED__PROJECTS_STATUS_EDIT"] = 30] = "DEPRECATED__PROJECTS_STATUS_EDIT";
  UserPermission__[UserPermission__["PROJECTS_STATUS_FULL_VISIBILITY"] = 32] = "PROJECTS_STATUS_FULL_VISIBILITY";
  UserPermission__[UserPermission__["PROJECTS_APPROVE"] = 24] = "PROJECTS_APPROVE";
  UserPermission__[UserPermission__["PROJECTS_REVIEW"] = 39] = "PROJECTS_REVIEW";
  UserPermission__[UserPermission__["PROJECTS_MEDIATE"] = 37] = "PROJECTS_MEDIATE";
  UserPermission__[UserPermission__["PROJECTS_CONCEAL"] = 25] = "PROJECTS_CONCEAL";
  UserPermission__[UserPermission__["PROJECTS_SET_SPONSOR_USER"] = 28] = "PROJECTS_SET_SPONSOR_USER";
  UserPermission__[UserPermission__["PROJECTS_SET_BROKER_USER"] = 35] = "PROJECTS_SET_BROKER_USER";
  UserPermission__[UserPermission__["PROJECTS_CLOSE"] = 34] = "PROJECTS_CLOSE";
  UserPermission__[UserPermission__["LOAN_PROGRAMS_READ"] = 26] = "LOAN_PROGRAMS_READ";
  UserPermission__[UserPermission__["CAPITAL_PROVIDERS_SEARCH"] = 40] = "CAPITAL_PROVIDERS_SEARCH";
  UserPermission__[UserPermission__["ORGANIZATIONS_CREATE"] = 8] = "ORGANIZATIONS_CREATE";
  UserPermission__[UserPermission__["ORGANIZATIONS_READ"] = 9] = "ORGANIZATIONS_READ";
  UserPermission__[UserPermission__["ORGANIZATIONS_EDIT"] = 10] = "ORGANIZATIONS_EDIT";
  UserPermission__[UserPermission__["PEOPLE_CREATE"] = 11] = "PEOPLE_CREATE";
  UserPermission__[UserPermission__["PEOPLE_READ"] = 12] = "PEOPLE_READ";
  UserPermission__[UserPermission__["PEOPLE_EDIT"] = 13] = "PEOPLE_EDIT";
  UserPermission__[UserPermission__["DOMAINS_CREATE"] = 14] = "DOMAINS_CREATE";
  UserPermission__[UserPermission__["DOMAINS_READ"] = 15] = "DOMAINS_READ";
  UserPermission__[UserPermission__["DOMAINS_EDIT"] = 16] = "DOMAINS_EDIT";
  UserPermission__[UserPermission__["REFERRALS_READ"] = 23] = "REFERRALS_READ";
  UserPermission__[UserPermission__["ALPHA_ACCESS"] = 41] = "ALPHA_ACCESS";
})(UserPermission__ || (UserPermission__ = {}));
const UserPermission_ = defineEnumProperties(UserPermission__, [0, 1, 2, 3, 38, 17, 18, 19, 36, 4, 5, 20, 27, 29, 33, 6, 7, 21, 22, 31, 30, 32, 24, 39, 37, 25, 28, 35, 34, 26, 40, 8, 9, 10, 11, 12, 13, 14, 15, 16, 23]);
const UserPermission = extendEnum('UserPermission', [["USERS_CREATE", 0], ["USERS_CREATE_TEMPORARY", 1], ["USERS_READ", 2], ["USERS_EDIT", 3], ["USERS_EDIT_SECURITY_KEYS", 38], ["USER_PROFILES_READ", 17], ["USER_PROFILES_EDIT", 18], ["USER_PROFILES_LIST", 19], ["USERS_DEACTIVATE", 36], ["PROJECTS_CREATE", 4], ["PROJECTS_MAKE_OFFER", 5], ["PROJECTS_ADD_OFFER", 20], ["PROJECTS_APPROVE_OFFER", 27], ["PROJECTS_UNACCEPT_OFFER", 29], ["PROJECTS_DELETE_OFFER", 33], ["PROJECTS_READ_ALL_DRAFTS", 6], ["PROJECTS_EDIT_ALL", 7], ["PROJECTS_SHARE", 21], ["PROJECTS_ADD_STACKSOURCE_SOURCED_SHARE", 22], ["DEPRECATED__PROJECTS_STATUS_READ", 31], ["DEPRECATED__PROJECTS_STATUS_EDIT", 30], ["PROJECTS_STATUS_FULL_VISIBILITY", 32], ["PROJECTS_APPROVE", 24], ["PROJECTS_REVIEW", 39], ["PROJECTS_MEDIATE", 37], ["PROJECTS_CONCEAL", 25], ["PROJECTS_SET_SPONSOR_USER", 28], ["PROJECTS_SET_BROKER_USER", 35], ["PROJECTS_CLOSE", 34], ["LOAN_PROGRAMS_READ", 26], ["CAPITAL_PROVIDERS_SEARCH", 40], ["ORGANIZATIONS_CREATE", 8], ["ORGANIZATIONS_READ", 9], ["ORGANIZATIONS_EDIT", 10], ["PEOPLE_CREATE", 11], ["PEOPLE_READ", 12], ["PEOPLE_EDIT", 13], ["DOMAINS_CREATE", 14], ["DOMAINS_READ", 15], ["DOMAINS_EDIT", 16], ["REFERRALS_READ", 23], ["ALPHA_ACCESS", 41]])(UserPermission_);
class User_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      10: 2,
      8: Uuid.shape,
      2: 1,
      3: 2,
      4: 2,
      9: DEPRECATED_Date.shape,
      5: 2,
      7: 1,
      6: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message User field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message User field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Uuid);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message User field uuid.
                Expected type Uuid.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Uuid', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message User field authenticated.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message User field first_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message User field last_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message User field date_of_birth.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message User field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message User field email_verified.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message User field permission.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UserPermission.fromStrictJson_(v, getFieldMap);
            }
            return UserPermission.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(User$1, 'User', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message User to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(10, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(10, null, null);
  }
  get uuid() {
    return this.getFieldById(8, Uuid.getDefault());
  }
  setUuid(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteUuid() {
    return this.setValueFieldById(8, null, null);
  }
  get authenticated() {
    return this.getFieldById(2, false);
  }
  setAuthenticated(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteAuthenticated() {
    return this.setValueFieldById(2, null, null);
  }
  get firstName() {
    return this.getFieldById(3, "");
  }
  setFirstName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteFirstName() {
    return this.setValueFieldById(3, null, null);
  }
  get lastName() {
    return this.getFieldById(4, "");
  }
  setLastName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteLastName() {
    return this.setValueFieldById(4, null, null);
  }
  get dateOfBirth() {
    return this.getFieldById(9, DEPRECATED_Date.getDefault());
  }
  setDateOfBirth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteDateOfBirth() {
    return this.setValueFieldById(9, null, null);
  }
  get email() {
    return this.getFieldById(5, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(5, null, null);
  }
  get emailVerified() {
    return this.getFieldById(7, false);
  }
  setEmailVerified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteEmailVerified() {
    return this.setValueFieldById(7, null, null);
  }
  get permissionList() {
    return this.getRepeatedFieldById(6);
  }
  hasPermission(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addPermission(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachPermission(values) {
    let T = this;
    for (const value of values) {
      T = T.addPermission(value);
    }
    return T;
  }
  setPermission(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removePermission(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearPermissionList() {
    return this.clearRepeatedFieldById(6);
  }
  setPermissionList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
}
_defineProperty$W(User_, protoName, DEV_MODE ? 'User' : '[Unknown]');
let User$1 = User_;
(function (_User) {})(User$1 || (User$1 = {}));
Object.defineProperties(User$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 10
      },
      "uuid": {
        value: 8
      },
      "authenticated": {
        value: 2
      },
      "first_name": {
        value: 3
      },
      "firstName": {
        value: 3
      },
      "last_name": {
        value: 4
      },
      "lastName": {
        value: 4
      },
      "date_of_birth": {
        value: 9
      },
      "dateOfBirth": {
        value: 9
      },
      "email": {
        value: 5
      },
      "email_verified": {
        value: 7
      },
      "emailVerified": {
        value: 7
      },
      "permission": {
        value: 6
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '10': {
        value: "sym"
      },
      '8': {
        value: "uuid"
      },
      '2': {
        value: "authenticated"
      },
      '3': {
        value: "first_name"
      },
      '4': {
        value: "last_name"
      },
      '9': {
        value: "date_of_birth"
      },
      '5': {
        value: "email"
      },
      '7': {
        value: "email_verified"
      },
      '6': {
        value: "permission"
      }
    }))
  }
});
Object.defineProperties(User$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      8: Uuid.prototype,
      9: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(User$1);
  Object.freeze(User$1.prototype);
}

class User extends User_ {
  get name() {
    return this.firstName ? this.lastName ? `${this.firstName} ${this.lastName}` : this.firstName : this.lastName;
  }
}

registerCanonical(UserPermission, UserPermission);
registerCanonical(User_, User);

function _defineProperty$V(obj, key, value) { key = _toPropertyKey$V(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$V(arg) { var key = _toPrimitive$V(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$V(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class GetUserAndProfileImageResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: User.shape,
      2: 2,
      3: UserProfile$2.shape,
      4: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(User);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message GetUserAndProfileImageResponse field user.
                Expected type User.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'User', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message GetUserAndProfileImageResponse field user_profile_image_key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UserProfile$2);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message GetUserAndProfileImageResponse field user_profile.
                Expected type UserProfile.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UserProfile', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message GetUserAndProfileImageResponse field needs_verification.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(GetUserAndProfileImageResponse, 'GetUserAndProfileImageResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message GetUserAndProfileImageResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get user() {
    return this.getFieldById(1, User.getDefault());
  }
  setUser(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUser() {
    return this.setValueFieldById(1, null, null);
  }
  get userProfileImageKey() {
    return this.getFieldById(2, "");
  }
  setUserProfileImageKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUserProfileImageKey() {
    return this.setValueFieldById(2, null, null);
  }
  get userProfile() {
    return this.getFieldById(3, UserProfile$2.getDefault());
  }
  setUserProfile(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteUserProfile() {
    return this.setValueFieldById(3, null, null);
  }
  get needsVerification() {
    return this.getFieldById(4, false);
  }
  setNeedsVerification(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteNeedsVerification() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$V(GetUserAndProfileImageResponse_, protoName, DEV_MODE ? 'GetUserAndProfileImageResponse' : '[Unknown]');
let GetUserAndProfileImageResponse = GetUserAndProfileImageResponse_;
(function (_GetUserAndProfileImageResponse) {})(GetUserAndProfileImageResponse || (GetUserAndProfileImageResponse = {}));
Object.defineProperties(GetUserAndProfileImageResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "user": {
        value: 1
      },
      "user_profile_image_key": {
        value: 2
      },
      "userProfileImageKey": {
        value: 2
      },
      "user_profile": {
        value: 3
      },
      "userProfile": {
        value: 3
      },
      "needs_verification": {
        value: 4
      },
      "needsVerification": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "user"
      },
      '2': {
        value: "user_profile_image_key"
      },
      '3': {
        value: "user_profile"
      },
      '4': {
        value: "needs_verification"
      }
    }))
  }
});
Object.defineProperties(GetUserAndProfileImageResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: User.prototype,
      3: UserProfile$2.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(GetUserAndProfileImageResponse);
  Object.freeze(GetUserAndProfileImageResponse.prototype);
}

function _defineProperty$U(obj, key, value) { key = _toPropertyKey$U(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$U(arg) { var key = _toPrimitive$U(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$U(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CurrentUser_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      3: 2,
      4: SimpleDate.shape,
      5: 2,
      6: 1,
      7: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CurrentUser field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CurrentUser field first_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CurrentUser field last_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message CurrentUser field date_of_birth.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CurrentUser field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message CurrentUser field email_verified.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 7:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message CurrentUser field permission.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return UserPermission.fromStrictJson_(v, getFieldMap);
            }
            return UserPermission.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CurrentUser$1, 'CurrentUser', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CurrentUser to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get firstName() {
    return this.getFieldById(2, "");
  }
  setFirstName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteFirstName() {
    return this.setValueFieldById(2, null, null);
  }
  get lastName() {
    return this.getFieldById(3, "");
  }
  setLastName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLastName() {
    return this.setValueFieldById(3, null, null);
  }
  get dateOfBirth() {
    return this.getFieldById(4, SimpleDate.getDefault());
  }
  setDateOfBirth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteDateOfBirth() {
    return this.setValueFieldById(4, null, null);
  }
  get email() {
    return this.getFieldById(5, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(5, null, null);
  }
  get emailVerified() {
    return this.getFieldById(6, false);
  }
  setEmailVerified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteEmailVerified() {
    return this.setValueFieldById(6, null, null);
  }
  get permissionList() {
    return this.getRepeatedFieldById(7);
  }
  hasPermission(value) {
    return this.repeatedFieldByIdHas(7, value);
  }
  addPermission(value) {
    return this.addToRepeatedFieldById(7, value);
  }
  addEachPermission(values) {
    let T = this;
    for (const value of values) {
      T = T.addPermission(value);
    }
    return T;
  }
  setPermission(index, value) {
    return this.setRepeatedFieldById(7, index, value);
  }
  removePermission(index) {
    return this.removeFromRepeatedFieldById(7, index);
  }
  clearPermissionList() {
    return this.clearRepeatedFieldById(7);
  }
  setPermissionList(values) {
    return this.overwriteRepeatedFieldById(7, values);
  }
}
_defineProperty$U(CurrentUser_, protoName, DEV_MODE ? 'CurrentUser' : '[Unknown]');
let CurrentUser$1 = CurrentUser_;
(function (_CurrentUser) {})(CurrentUser$1 || (CurrentUser$1 = {}));
Object.defineProperties(CurrentUser$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "first_name": {
        value: 2
      },
      "firstName": {
        value: 2
      },
      "last_name": {
        value: 3
      },
      "lastName": {
        value: 3
      },
      "date_of_birth": {
        value: 4
      },
      "dateOfBirth": {
        value: 4
      },
      "email": {
        value: 5
      },
      "email_verified": {
        value: 6
      },
      "emailVerified": {
        value: 6
      },
      "permission": {
        value: 7
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "first_name"
      },
      '3': {
        value: "last_name"
      },
      '4': {
        value: "date_of_birth"
      },
      '5': {
        value: "email"
      },
      '6': {
        value: "email_verified"
      },
      '7': {
        value: "permission"
      }
    }))
  }
});
Object.defineProperties(CurrentUser$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      4: SimpleDate.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CurrentUser$1);
  Object.freeze(CurrentUser$1.prototype);
}

class CurrentUser extends CurrentUser_ {
  get name() {
    return this.firstName ? this.lastName ? `${this.firstName} ${this.lastName}` : this.firstName : this.lastName;
  }
}

registerCanonical(CurrentUser_, CurrentUser);

function _defineProperty$T(obj, key, value) { key = _toPropertyKey$T(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$T(arg) { var key = _toPrimitive$T(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$T(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class PromiseWithResolver {
  constructor() {
    _defineProperty$T(this, "promise", void 0);
    _defineProperty$T(this, "resolve", void 0);
    _defineProperty$T(this, "reject", void 0);
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}

function sleep(interval) {
  return new Promise(resolve => {
    setTimeout(() => resolve(), interval);
  });
}

const onChangeCallbacks = [];
let polling = false;
function getCookie(name) {
  // Regular expression from
  // https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie
  const re = new RegExp(['(?:^|.*;\\s*)', encodeURIComponent(name).replace(/[.\-+*]/g, '\\$&'), '\\s*\\=\\s*([^;]*).*$'].join(''));
  const m = re.exec(document.cookie);
  if (m) {
    return decodeURIComponent(m[1]);
  }
  return '';
}

// `maxAge` is expected in milliseconds to conform to the common JavaScript
// pattern of specifying times in milliseconds.
// Grandfathered in param count 6
// eslint-disable-next-line max-params
function setCookie(name, value, maxAge = 0, path = '', domain = '', secure = location.protocol === 'https:') {
  document.cookie = joinString(function* () {
    yield encodeURIComponent(name);
    yield '=';
    yield encodeURIComponent(value);
    if (maxAge) {
      yield '; max-age=';
      yield Math.round(maxAge / 1000);
    }
    if (path) {
      yield '; path=';
      yield path;
    }
    if (domain) {
      yield '; domain=';
      yield domain;
    }
    if (secure) {
      yield '; secure';
    }
  });
}
function onCookieChange(name, callback) {
  startPolling();
  let currentValue = getCookie(name);
  onChangeCallbacks.push(() => {
    const newValue = getCookie(name);
    if (currentValue !== newValue) {
      currentValue = newValue;
      callback();
    }
  });
}

// It's regrettable that we have to do this. In this specific case, especially
// related to Bloomlake user management, it seems to be the best choice.
// Don't make this a pattern!
function startPolling() {
  if (polling) {
    return;
  }
  polling = true;
  let currentCookie = document.cookie;
  setInterval(() => {
    if (currentCookie !== document.cookie) {
      currentCookie = document.cookie;
      let hadException = false;
      let firstError = null;
      for (const callback of onChangeCallbacks) {
        try {
          callback();
        } catch (e) {
          hadException = true;
          if (!firstError) {
            firstError = e;
          }
        }
      }
      if (hadException) {
        throw firstError;
      }
    }
  }, 200);
}

;

class EventSubscription {
  constructor(subscriber) {
    const _this = this;
    _this.subscriber = subscriber;
  }
  remove(_this) {
    if (this != null) {
      _this = this;
    }
    if (_this.subscriber) {
      _this.subscriber.removeSubscription(_this);
      _this.subscriber = null;
    }
  }
}

;
class EmitterSubscription extends EventSubscription {
  constructor(subscriber, listener) {
    let _this;
    [super(subscriber), _this = this][0];
    _this.listener = listener;
  }
}

;
class EventSubscriptionVendor {
  constructor() {
    const _this = this;
    _this.subscriptionsForType = new Map();
  }
  addSubscription(_this, eventType, subscription) {
    if (this != null) {
      subscription = eventType;
      eventType = _this;
      _this = this;
    }
    runtime.checkAsserts() ? runtime.ensure(subscription.subscriber == _this, function () {
      return 'The subscriber of the subscription is incorrectly set.';
    }) : undefined;
    if (!_this.subscriptionsForType.has(eventType)) {
      _this.subscriptionsForType.set(eventType, []);
    }
    const subscriptions = _this.subscriptionsForType.get(eventType);
    const key = subscriptions.length;
    subscriptions.push(subscription);
    subscription.eventType = eventType;
    subscription.key = key;
    return subscription;
  }
  removeAllSubscriptions(_this, eventType) {
    if (this != null) {
      eventType = _this;
      _this = this;
    }
    if (eventType == null) {
      _this.subscriptionsForType.clear();
    } else {
      _this.subscriptionsForType.delete(eventType);
    }
  }
  removeSubscription(_this, subscription) {
    if (this != null) {
      subscription = _this;
      _this = this;
    }
    const eventType = subscription.eventType;
    const key = subscription.key;
    const subscriptions = _this.subscriptionsForType.get(eventType);
    if (subscriptions) {
      runtime.deleteComputed(subscriptions, key);
    }
  }
  getSubscriptionsForType(_this, eventType) {
    if (this != null) {
      eventType = _this;
      _this = this;
    }
    return _this.subscriptionsForType.get(eventType);
  }
}

;
class BaseEventEmitter {
  constructor() {
    const _this = this;
    _this.subscriber = new EventSubscriptionVendor();
    _this.currentSubscription = null;
  }
  addListener(_this, eventType, listener) {
    if (this != null) {
      listener = eventType;
      eventType = _this;
      _this = this;
    }
    return _this.subscriber.addSubscription(eventType, new EmitterSubscription(_this.subscriber, listener));
  }
  once(_this, eventType, listener) {
    if (this != null) {
      listener = eventType;
      eventType = _this;
      _this = this;
    }
    return _this.addListener(eventType, function (...args) {
      if (this != null) {
        if (arguments.length >= 0) args.unshift(this);
      }
      _this.removeCurrentListener();
      listener(...args);
    });
  }
  removeAllListeners(_this, eventType) {
    if (this != null) {
      eventType = _this;
      _this = this;
    }
    _this.subscriber.removeAllSubscriptions(eventType);
  }
  removeCurrentListener(_this) {
    if (this != null) {
      _this = this;
    }
    runtime.checkAsserts() ? runtime.ensure(_this.currentSubscription, function () {
      return 'Not in an emitting cycle; there is no current subscription';
    }) : undefined;
    _this.subscriber.removeSubscription(_this.currentSubscription);
  }
  listeners(_this, eventType) {
    if (this != null) {
      eventType = _this;
      _this = this;
    }
    const subscriptions = _this.subscriber.getSubscriptionsForType(eventType);
    return subscriptions ? subscriptions.map(function (subscription) {
      if (this != null) {
        subscription = this;
      }
      return subscription.listener;
    }) : [];
  }
  emit(_this, eventType, ...args) {
    if (this != null) {
      if (arguments.length >= 2) args.unshift(eventType);
      eventType = _this;
      _this = this;
    }
    const subscriptions = _this.subscriber.getSubscriptionsForType(eventType);
    if (subscriptions) {
      const keys = Object.keys(subscriptions);
      for (let from$3287 = 0, to$3288 = keys.length, counter$3289 = from$3287; from$3287 < to$3288 ? counter$3289 < to$3288 : counter$3289 > to$3288; from$3287 < to$3288 ? counter$3289++ : counter$3289--) {
        const ii = counter$3289;
        const key = runtime.getComputed(keys, ii);
        const subscription = runtime.getComputed(subscriptions, key);
        if (subscription) {
          _this.currentSubscription = subscription;
          _this.emitToSubscription(subscription, eventType, ...args);
        }
      }
      _this.currentSubscription = null;
    }
  }
  emitToSubscription(_this, subscription, eventType, ...args) {
    var _context;
    if (this != null) {
      if (arguments.length >= 3) args.unshift(eventType);
      eventType = subscription;
      subscription = _this;
      _this = this;
    }
    (_context = null, subscription.listener).call(_context, ...args);
  }
}

// Derived from:

function _defineProperty$S(obj, key, value) { key = _toPropertyKey$S(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$S(arg) { var key = _toPrimitive$S(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$S(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class AuthenticationMonitor {
  constructor(ava) {
    _defineProperty$S(this, "emitter", new BaseEventEmitter());
    _defineProperty$S(this, "initialized", new PromiseWithResolver());
    _defineProperty$S(this, "ava", void 0);
    _defineProperty$S(this, "currentUser", null);
    _defineProperty$S(this, "emailGuess", '');
    _defineProperty$S(this, "lastUserSym", '');
    _defineProperty$S(this, "lastEmailGuess", '');
    this.ava = ava;

    // TODO(nathan): Instead of tracking T and A cookies, which won't work
    // well across different sub-domains, return the expiration infornmation
    // in the get session API response. There will still need to be a level
    // of V and email cookie tracking, though it's ok if it doesn't work cross-
    // subdomain.
    onCookieChange('T', () => this.updateUser());
    onCookieChange('A', () => {
      if (getCookie('A') !== '1') {
        this.setCurrentUser(null);
      }
    });
    onCookieChange('V', () => {
      // The V cookie changing from '1' to '' indicates security key verifcation
      // may be required again.
      if (!getCookie('V')) {
        this.updateUser();
      }
    });
    onCookieChange('email', () => this.updateUser());
    this.updateUser();
  }
  setCurrentUser(user) {
    this.currentUser = user;
    this.triggerEvents();
    this.initialized.resolve();
  }
  triggerEvents() {
    const newSym = this.currentUser?.sym || '';
    if (this.lastUserSym !== newSym) {
      this.lastUserSym = newSym;
      this.emitter.emit('user-changed');
    }
    if (this.lastEmailGuess !== this.emailGuess) {
      this.lastEmailGuess = this.emailGuess;
      this.emitter.emit('email-guess-changed');
    }
  }
  async getUser() {
    await this.initialized.promise;
    return this.currentUser;
  }
  async getEmailGuess() {
    await this.initialized.promise;
    return this.emailGuess;
  }
  async userUpdating(callback) {
    this.reinit();
    try {
      await callback();
    } finally {
      await this.updateUser();
    }
  }
  onUserChanged(callback) {
    this.emitter.addListener('user-changed', callback);
  }
  onEmailGuessChanged(callback) {
    this.emitter.addListener('email-guess-changed', callback);
  }
  reinit() {
    const oldInit = this.initialized;
    this.initialized = new PromiseWithResolver();
    oldInit.resolve(this.initialized.promise);
  }
  async updateUser() {
    this.reinit();
    this.setCurrentUser(await this.getUpdatedUser());
  }
  async getUpdatedUser() {
    let info = null;
    info = await loadUser();
    if (info.authenticated) {
      if (info.needsVerification && !/^2(\.\d*)?$/.test(getCookie('V'))) {
        await this.refreshVerification();
      }
    }
    let user = null;
    if (info.authenticated) {
      user = info.user;
    }
    let email = user?.email || '';
    if (!email) {
      const emailCookie = getCookie('email');
      if (emailCookie) {
        email = emailCookie;
      }
    }
    // Even if the login is successful, we want to set `emailGuess` in case the
    // user later logs out.
    if (email) {
      this.emailGuess = email;
    }
    return user;
  }
  async refreshVerification() {
    // Most of the time the 'V' cookie is '1' or '', indicating whether the
    // user's security key verification is good or if it is time to reverify.
    // Here we use a number between 2 (inclusive) and 3 (exclusive) to
    // indicate we are currently trying to reverify. This is used as a way
    // to communicate between multiple tabs.
    //
    // If there are two tabs that try to reverify at the same time, the
    // browser will "cancel" one of them, and, with no way to know whether
    // the user was actively trying to reverify in another tab, we would
    // then sign the user out, causing the verification to fail in the other
    // tab.
    //
    // We use a random number here so the tabs can figure out which one has
    // the responsibility to reverify, since there would otherwise be a race
    // condition.
    let v = String(2 + Math.random());
    // Accounting for the impossibly unlikely case that a floating point
    // imprecision causes 2 + Math.random() to result in 3.
    if (!/^2/.test(v)) {
      v = '2';
    }
    setCookie('V', v, 2 * 60 * 1000, '/');
    // It's not perfect, but waiting 2 seconds should be plenty of time to
    // allow any threads that simultaneously passed the first 'V' cookie
    // check and chose a new cookie value to set the new cookie, allowing a
    // winner to be chosen.
    await sleep(2000);
    if (getCookie('V') === v) {
      if (await this.ava.securityKeys.verify()) {
        // The response will set the V cookie.
        await loadUser();
      } else {
        setCookie('V', '', undefined /* maxAge */, '/');
        // If they fail the verification, log them out so they get the
        // experience of being logged out and don't keep looking at an open
        // project in the platform.
        await this.ava.auth.signOut();
      }
    }
  }
}
async function loadUser() {
  const response = await getUserAndProfileImage();
  // Convert the common User to an Ava CurrentUser.
  return {
    user: CurrentUser.from({
      sym: response.user.sym,
      firstName: response.user.firstName,
      lastName: response.user.lastName,
      dateOfBirth: response.user.dateOfBirth.formatIso(),
      email: response.user.email,
      emailVerified: response.user.emailVerified,
      permission: response.user.permissionList
    }),
    needsVerification: response.needsVerification,
    authenticated: response.user.authenticated
  };
}
async function getUserAndProfileImage() {
  return GetUserAndProfileImageResponse.fromJson(await getUserAndProfileImage$1());
}

function signIn(...args) {
  return postWithChecksum(resolve('/@r:/user/login.signIn'), args, ChecksumType.CRC32C);
}

class AuthenticationError extends Error {
  constructor(message = 'Authentication failed') {
    super(message);
  }
}
class NotFoundError$1 extends Error {
  constructor(message = 'Not found') {
    super(message);
  }
}
class ProjectNotFoundError extends NotFoundError$1 {
  constructor() {
    super('Project not found');
  }
}

// tslint:disable-next-line:no-unused-variable
var EndpointErrorCode__;
(function (EndpointErrorCode__) {
  EndpointErrorCode__[EndpointErrorCode__["ENDPOINT_PARAMS_FAILED_CHECKSUM"] = 0] = "ENDPOINT_PARAMS_FAILED_CHECKSUM";
  EndpointErrorCode__[EndpointErrorCode__["NOT_FOUND"] = 1] = "NOT_FOUND";
  EndpointErrorCode__[EndpointErrorCode__["UNAUTHENTICATED"] = 2] = "UNAUTHENTICATED";
  EndpointErrorCode__[EndpointErrorCode__["INVALID_USER_EMAIL_VERIFICATION_TOKEN"] = 4] = "INVALID_USER_EMAIL_VERIFICATION_TOKEN";
  EndpointErrorCode__[EndpointErrorCode__["SIGN_IN_FAILURE_INVALID_PASSWORD"] = 5] = "SIGN_IN_FAILURE_INVALID_PASSWORD";
  EndpointErrorCode__[EndpointErrorCode__["SIGN_IN_FAILURE_INVALID_EMAIL"] = 6] = "SIGN_IN_FAILURE_INVALID_EMAIL";
  EndpointErrorCode__[EndpointErrorCode__["SIGN_IN_FAILURE_ATTEMPTS_EXCEEDED"] = 11] = "SIGN_IN_FAILURE_ATTEMPTS_EXCEEDED";
  EndpointErrorCode__[EndpointErrorCode__["REGISTER_FAILURE_EMAIL_EXISTS"] = 7] = "REGISTER_FAILURE_EMAIL_EXISTS";
  EndpointErrorCode__[EndpointErrorCode__["EMAIL_NOT_INTERNAL"] = 14] = "EMAIL_NOT_INTERNAL";
  EndpointErrorCode__[EndpointErrorCode__["CHANGE_PASSWORD"] = 8] = "CHANGE_PASSWORD";
  EndpointErrorCode__[EndpointErrorCode__["INTERCOM_UNINITIALIZED"] = 9] = "INTERCOM_UNINITIALIZED";
  EndpointErrorCode__[EndpointErrorCode__["UNSUPPORTED_IMAGE"] = 10] = "UNSUPPORTED_IMAGE";
  EndpointErrorCode__[EndpointErrorCode__["LENDER_BANNED"] = 12] = "LENDER_BANNED";
  EndpointErrorCode__[EndpointErrorCode__["SEND_GRID_ERROR"] = 13] = "SEND_GRID_ERROR";
})(EndpointErrorCode__ || (EndpointErrorCode__ = {}));
const EndpointErrorCode_ = defineEnumProperties(EndpointErrorCode__, [0, 1, 2, 4, 5, 6, 11, 7, 14, 8, 9, 10, 12, 13]);
const EndpointErrorCode = extendEnum('EndpointErrorCode', [["ENDPOINT_PARAMS_FAILED_CHECKSUM", 0], ["NOT_FOUND", 1], ["UNAUTHENTICATED", 2], ["INVALID_USER_EMAIL_VERIFICATION_TOKEN", 4], ["SIGN_IN_FAILURE_INVALID_PASSWORD", 5], ["SIGN_IN_FAILURE_INVALID_EMAIL", 6], ["SIGN_IN_FAILURE_ATTEMPTS_EXCEEDED", 11], ["REGISTER_FAILURE_EMAIL_EXISTS", 7], ["EMAIL_NOT_INTERNAL", 14], ["CHANGE_PASSWORD", 8], ["INTERCOM_UNINITIALIZED", 9], ["UNSUPPORTED_IMAGE", 10], ["LENDER_BANNED", 12], ["SEND_GRID_ERROR", 13]])(EndpointErrorCode_);

function _defineProperty$R(obj, key, value) { key = _toPropertyKey$R(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$R(arg) { var key = _toPrimitive$R(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$R(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class EndpointError extends Error {
  static isOfType(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value) {
    return !!value && value.endpointErrorCode === this.endpointErrorCode;
  }
  constructor(message) {
    super(message);
    _defineProperty$R(this, "endpointErrorCode", void 0);
    this.endpointErrorCode = this.constructor.endpointErrorCode;
  }
}
class EndpointCallFailedChecksumError extends EndpointError {
  constructor(expected, found) {
    super(`Failed checksum. Found (${found}) expected (${expected}).`);
  }
}
_defineProperty$R(EndpointCallFailedChecksumError, "endpointErrorCode", EndpointErrorCode.ENDPOINT_PARAMS_FAILED_CHECKSUM);
class UnauthenticatedError extends EndpointError {
  constructor() {
    super('Unauthenticated');
  }
}
_defineProperty$R(UnauthenticatedError, "endpointErrorCode", EndpointErrorCode.UNAUTHENTICATED);
class NotFoundError extends EndpointError {}
_defineProperty$R(NotFoundError, "endpointErrorCode", EndpointErrorCode.NOT_FOUND);
class InvalidUserEmailVerificationTokenError extends EndpointError {
  constructor() {
    super('Invalid email verification token.');
  }
}
_defineProperty$R(InvalidUserEmailVerificationTokenError, "endpointErrorCode", EndpointErrorCode.INVALID_USER_EMAIL_VERIFICATION_TOKEN);
class SignInPasswordError extends EndpointError {
  constructor() {
    super('Invalid password.');
  }
}
_defineProperty$R(SignInPasswordError, "endpointErrorCode", EndpointErrorCode.SIGN_IN_FAILURE_INVALID_PASSWORD);
class SignInEmailError extends EndpointError {
  constructor() {
    super('Invalid Email. Please register to create an account.');
  }
}
_defineProperty$R(SignInEmailError, "endpointErrorCode", EndpointErrorCode.SIGN_IN_FAILURE_INVALID_EMAIL);
class SignInAttemptsExceededError extends EndpointError {
  constructor() {
    super('Exceeded allowed login attempts. Please try again later.');
  }
}
_defineProperty$R(SignInAttemptsExceededError, "endpointErrorCode", EndpointErrorCode.SIGN_IN_FAILURE_ATTEMPTS_EXCEEDED);
class RegisterEmailExistsError extends EndpointError {
  constructor() {
    super('Account already created for this email. Please sign in.');
  }
}
_defineProperty$R(RegisterEmailExistsError, "endpointErrorCode", EndpointErrorCode.REGISTER_FAILURE_EMAIL_EXISTS);
class UserEmailNotInternalError extends Error {
  constructor() {
    super('User email not internal. Internal email required.');
  }
}
_defineProperty$R(UserEmailNotInternalError, "endpointErrorCode", EndpointErrorCode.EMAIL_NOT_INTERNAL);
class ChangePasswordError extends EndpointError {
  constructor() {
    super('Unable to reset password.  Please check the verification code and email ' + 'and try again.');
  }
}
_defineProperty$R(ChangePasswordError, "endpointErrorCode", EndpointErrorCode.CHANGE_PASSWORD);
class IntercomUninitializedError extends EndpointError {
  constructor() {
    super('Uninitialized.');
  }
}
_defineProperty$R(IntercomUninitializedError, "endpointErrorCode", EndpointErrorCode.INTERCOM_UNINITIALIZED);
class UnsupportedImageError extends EndpointError {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(e) {
    super(e && String(e.message) || String(e) || 'Unsupported image');
  }
}
_defineProperty$R(UnsupportedImageError, "endpointErrorCode", EndpointErrorCode.UNSUPPORTED_IMAGE);
class LenderBannedError extends EndpointError {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(e) {
    super(e && String(e.message) || String(e) || 'Lender Banned');
  }
}
_defineProperty$R(LenderBannedError, "endpointErrorCode", EndpointErrorCode.LENDER_BANNED);
class SendGridError extends EndpointError {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor() {
    super('Issue sending email with SendGrid');
  }
}
_defineProperty$R(SendGridError, "endpointErrorCode", EndpointErrorCode.SEND_GRID_ERROR);

async function authenticateWithPassword(ctr, email, password, remember) {
  try {
    await ctr.userUpdating(async () => {
      await signIn(email, password, remember);
    });
  } catch (e) {
    if (SignInEmailError.isOfType(e) || SignInPasswordError.isOfType(e) || SignInAttemptsExceededError.isOfType(e)) {
      throw new AuthenticationError(e.message);
    }
    throw e;
  }
}

function signOut(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/user/logout.signOut'), args, ChecksumType.CRC32C);
}

function _defineProperty$Q(obj, key, value) { key = _toPropertyKey$Q(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$Q(arg) { var key = _toPrimitive$Q(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$Q(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
const $setShareToken = Symbol('$setShareToken');
const $setShareEmail = Symbol('$setShareEmail');
class Auth {
  constructor(ava, ctr) {
    _defineProperty$Q(this, "ava", void 0);
    _defineProperty$Q(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  getCurrentUser() {
    return this.ctr.getUser();
  }
  async signIn(email, password, remember = false) {
    await authenticateWithPassword(this.ctr, email, password, remember);
    return assert(await this.ctr.getUser()).sym;
  }
  async signOut() {
    await this.ctr.userUpdating(async () => {
      setCookie('A', '0');
      await signOut();
    });
  }
  [$setShareToken](token) {
    this.ctr.sharedUser = this.ctr.sharedUser.setToken(token);
  }
  [$setShareEmail](email) {
    this.ctr.sharedUser = this.ctr.sharedUser.setEmail(email);
  }
}

const $symToId = Symbol('$symToId');
function symToId(sym) {
  const id = +sym;
  assert(Number.isSafeInteger(id) && id > 0, emsg`Invalid sym: ${sym}`);
  return id;
}
function idToSym(id) {
  if (id === 0) {
    return '';
  }
  return String(id);
}
function int32OrStringToSymOrString(u) {
  return new SymOrString().setSym(idToSym(u.int32Value)).setString(u.stringValue);
}
function symOrStringToInt32OrString(u) {
  return new Int32OrString().setInt32Value(symToId(u.sym)).setStringValue(u.string);
}

function debounce(func, wait, immediate = false, maxWait = Infinity, _immediateEndCall = false // for use internally by `throttle`
) {
  let tmWait = 0;
  let tmMaxWait = 0;
  let calledAfterImmediate = false;
  function clearTms() {
    clearTimeout(tmWait);
    clearTimeout(tmMaxWait);
    tmWait = 0;
    tmMaxWait = 0;
  }
  let resolves = [];
  return function (...args) {
    return new Promise(resolve => {
      resolves.push(resolve);
      const settle = () => {
        calledAfterImmediate = false;
        const result = func.apply(this, args);
        for (const r of resolves) {
          r(result);
        }
        resolves = [];
      };
      const later = () => {
        clearTms();
        if (!immediate || _immediateEndCall && calledAfterImmediate) {
          settle();
        }
      };
      const callNow = immediate && !tmWait;
      if (immediate && tmWait) {
        calledAfterImmediate = true;
      }
      if (tmWait) {
        clearTimeout(tmWait);
      }
      tmWait = setTimeout(later, wait);
      if (maxWait < Infinity && !tmMaxWait) {
        tmMaxWait = setTimeout(later, maxWait);
      }
      if (callNow) {
        settle();
      }
    });
  };
}

function _defineProperty$P(obj, key, value) { key = _toPropertyKey$P(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$P(arg) { var key = _toPrimitive$P(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$P(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
// If you would like a version of this which accepts a single callback for each
// accumulated loading event, see `BatchAccumlatingLoader`.
class SeparableAccumulatingLoader {
  constructor(getAsync) {
    _defineProperty$P(this, "getAsync", void 0);
    _defineProperty$P(this, "retrieve", void 0);
    _defineProperty$P(this, "requests", void 0);
    _defineProperty$P(this, "resolvers", void 0);
    this.getAsync = getAsync;
    this.requests = new Set();
    this.resolvers = [];
    this.retrieve = debounce(this._retrieve.bind(this), 50, false,
    // immediate
    500);
  }
  load(request) {
    return new Promise(resolve => {
      this.requests.add(request);
      this.resolvers.push({
        request,
        resolve
      });
      this.retrieve();
    });
  }
  _retrieve() {
    const requests = [...this.requests];
    const resolvers = this.resolvers;
    this.requests.clear();
    this.resolvers = [];
    // The whole function isn't async because the lines above need to be
    // executed synchronously in the same turn to prevent a race condition.
    (async () => {
      // `getAsync` must return the results in the same order as the requests.
      const resultsByRequest = new Map();
      for (const [request, result] of await this.getAsync(requests)) {
        resultsByRequest.set(request, result);
      }
      for (const resolver of resolvers) {
        resolver.resolve(resultsByRequest.get(resolver.request));
      }
    })();
  }
}

function _defineProperty$O(obj, key, value) { key = _toPropertyKey$O(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$O(arg) { var key = _toPrimitive$O(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$O(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CacheItem {
  constructor(value, maxAge) {
    _defineProperty$O(this, "value", void 0);
    _defineProperty$O(this, "maxAge", void 0);
    _defineProperty$O(this, "time", void 0);
    this.value = value;
    this.maxAge = maxAge;
    this.time = Date.now();
  }
  getValue() {
    return this.value;
  }
  isFresh() {
    return this.time + this.maxAge >= Date.now();
  }
}
class SimpleCache {
  // There's code here to support old Tempo code, but new code should use the
  // constructor signature above.
  constructor(options, maxAge) {
    _defineProperty$O(this, "maxSize", void 0);
    _defineProperty$O(this, "maxAge", void 0);
    _defineProperty$O(this, "store", void 0);
    if (typeof options === 'number') {
      this.maxSize = options;
      this.maxAge = assert.number(maxAge);
    } else {
      this.maxSize = options.maxSize;
      this.maxAge = options.maxAge;
    }
    this.store = new Map();
  }
  has(key) {
    const item = this.store.get(key);
    return item != null && item.isFresh();
  }
  get(key) {
    const item = this.store.get(key);
    if (item && item.isFresh()) {
      return item.getValue();
    }
    return undefined;
  }

  // The algorithm used here for keeping the cache size down and removing the
  // oldest element when it gets too large relies on JavaScript's `Map` keeping
  // keys ordered by insertion and putting a key at the end of the list if it
  // is deleted and readded. As far as I understand, this is the specified
  // behavior, though that is no guarantee that it is followed by all
  // implementations. There could also be some performance problems from some
  // implementations around some of the method calls, which could be improved
  // upon by taking a more active approach in managing the data structure used
  // to store the cache.
  set(key, value) {
    this.store.delete(key);
    this.store.set(key, new CacheItem(value, this.maxAge));
    if (this.store.size > this.maxSize) {
      this.store.delete(this.getFirstKey());
    }
  }
  delete(key) {
    this.store.delete(key);
  }
  getFirstKey() {
    const info = this.store.keys().next();
    if (!info.done) {
      return info.value;
    }
    throw new Error('Map does not have any keys.');
  }
}

function _defineProperty$N(obj, key, value) { key = _toPropertyKey$N(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$N(arg) { var key = _toPrimitive$N(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$N(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class BatchLoader {
  constructor({
    typeLabel,
    cache,
    load,
    createError,
    initController
  }) {
    _defineProperty$N(this, "typeLabel", void 0);
    _defineProperty$N(this, "cache", void 0);
    _defineProperty$N(this, "_load", void 0);
    _defineProperty$N(this, "createError", void 0);
    _defineProperty$N(this, "initController", void 0);
    _defineProperty$N(this, "loaders", void 0);
    this.typeLabel = typeLabel || '';
    if (cache) {
      this.cache = new SimpleCache({
        maxSize: cache.maxSize,
        maxAge: cache.maxAge
      });
    }
    this._load = load;
    this.createError = createError;
    this.initController = initController;
    this.loaders = new WeakMap();
  }
  getLoader(ctr) {
    let loader = this.loaders.get(ctr);
    if (!loader) {
      loader = new SeparableAccumulatingLoader(this._load.bind(null, ctr));
      this.loaders.set(ctr, loader);
      if (this.initController) {
        this.initController(ctr);
      }
    }
    return loader;
  }
  async load(ctr, key) {
    const cached = this.cache?.get(key);
    if (cached) {
      return cached;
    }
    const value = await this.getLoader(ctr).load(key);
    if (value == null) {
      if (this.createError) {
        throw this.createError(key);
      } else {
        throw new NotFoundError$1(this.typeLabel ? tmsg`Not found: ${this.typeLabel} ${key}` : tmsg`Not found: ${key}`);
      }
    }
    this.cache?.set(key, value);
    return value;
  }
  uncache(ctr, key) {
    this.cache?.delete(key);
  }
}

function _defineProperty$M(obj, key, value) { key = _toPropertyKey$M(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$M(arg) { var key = _toPrimitive$M(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$M(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

// `StringIdGenerator` is guaranteed to always return a unique string.
class StringIdGenerator {
  constructor(chars) {
    _defineProperty$M(this, "nextId", void 0);
    _defineProperty$M(this, "chars", void 0);
    assert(chars.length > 0);
    assert.same(new Set(chars).size, chars.length);
    this.chars = chars;
    this.nextId = [0];
  }
  next() {
    const chars = [];
    for (const char of this.nextId) {
      chars.push(this.chars[char]);
    }
    this.increment();
    return chars.join('');
  }
  increment() {
    for (let i = 0; i < this.nextId.length; i++) {
      const val = ++this.nextId[i];
      if (val >= this.chars.length) {
        this.nextId[i] = 0;
      } else {
        return;
      }
    }
    assert(this.nextId[this.nextId.length - 1] === 0);
    this.nextId.push(0);
  }
}

function _defineProperty$L(obj, key, value) { key = _toPropertyKey$L(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$L(arg) { var key = _toPrimitive$L(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$L(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class BatchUploader {
  constructor({
    typeLabel,
    upload
  }) {
    _defineProperty$L(this, "pendingUploads", void 0);
    _defineProperty$L(this, "uploadIds", void 0);
    _defineProperty$L(this, "uploader", void 0);
    this.pendingUploads = new Map();
    this.uploadIds = new StringIdGenerator('abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890');
    this.uploader = new BatchLoader({
      typeLabel,
      load: this.wrapUpload(upload)
    });
  }
  async upload(ctr, upload) {
    const uploadId = this.uploadIds.next();
    this.pendingUploads.set(uploadId, upload);
    const result = await this.uploader.load(ctr, uploadId);
    if (result instanceof Error) {
      throw result;
    }
    return result;
  }
  wrapUpload(upload) {
    return (ctr, uploadIds) => {
      const uploads = uploadIds.map(uploadId => {
        const upload = assert(this.pendingUploads.get(uploadId));
        this.pendingUploads.delete(uploadId);
        return {
          uploadId,
          upload
        };
      });
      return upload(ctr, uploads);
    };
  }
}

function _defineProperty$K(obj, key, value) { key = _toPropertyKey$K(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$K(arg) { var key = _toPrimitive$K(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$K(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class DealBatchUploader {
  constructor({
    typeLabel,
    upload
  }) {
    _defineProperty$K(this, "uploader", void 0);
    this.uploader = new BatchUploader({
      typeLabel,
      upload: this.wrapUpload(upload)
    });
  }
  upload(ctr, deal, upload) {
    return this.uploader.upload(ctr, {
      deal,
      upload
    });
  }
  wrapUpload(callback) {
    return async (ctr, uploads) => {
      const uploadsByDeal = new Map();
      for (const {
        uploadId,
        upload: {
          deal,
          upload
        }
      } of uploads) {
        let group = uploadsByDeal.get(deal);
        if (!group) {
          group = [];
          uploadsByDeal.set(deal, group);
        }
        group.push({
          uploadId,
          upload
        });
      }
      const all = new Map();
      for (const [deal, uploads] of uploadsByDeal) {
        const responses = await callback(ctr, deal, uploads);
        for (const [uploadId, response] of responses) {
          all.set(uploadId, response);
        }
      }
      return all;
    };
  }
}

function _defineProperty$J(obj, key, value) { key = _toPropertyKey$J(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$J(arg) { var key = _toPrimitive$J(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$J(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class FileData_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message FileData field filename.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message FileData field data.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(FileData, 'FileData', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message FileData to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get filename() {
    return this.getFieldById(1, "");
  }
  setFilename(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteFilename() {
    return this.setValueFieldById(1, null, null);
  }
  get data() {
    return this.getFieldById(2, "");
  }
  setData(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteData() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$J(FileData_, protoName, DEV_MODE ? 'FileData' : '[Unknown]');
let FileData = FileData_;
(function (_FileData) {})(FileData || (FileData = {}));
Object.defineProperties(FileData, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "filename": {
        value: 1
      },
      "data": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "filename"
      },
      '2': {
        value: "data"
      }
    }))
  }
});
Object.defineProperties(FileData.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(FileData);
  Object.freeze(FileData.prototype);
}

function _defineProperty$I(obj, key, value) { key = _toPropertyKey$I(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$I(arg) { var key = _toPrimitive$I(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$I(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class AttachmentFileData_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: FileData.shape,
      3: 0,
      2: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FileData);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message AttachmentFileData field file_data.
                Expected type FileData.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FileData', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message AttachmentFileData field primary_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AttachmentType.fromStrictJson_(v, getFieldMap);
          }
          return AttachmentType.from(value);
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message AttachmentFileData field sub_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return AttachmentType.fromStrictJson_(v, getFieldMap);
            }
            return AttachmentType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(AttachmentFileData, 'AttachmentFileData', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message AttachmentFileData to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get fileData() {
    return this.getFieldById(1, FileData.getDefault());
  }
  setFileData(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteFileData() {
    return this.setValueFieldById(1, null, null);
  }
  get primaryType() {
    return this.getFieldById(3, 0);
  }
  setPrimaryType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deletePrimaryType() {
    return this.setValueFieldById(3, null, null);
  }
  get subTypeList() {
    return this.getRepeatedFieldById(2);
  }
  hasSubType(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addSubType(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachSubType(values) {
    let T = this;
    for (const value of values) {
      T = T.addSubType(value);
    }
    return T;
  }
  setSubType(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeSubType(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearSubTypeList() {
    return this.clearRepeatedFieldById(2);
  }
  setSubTypeList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
}
_defineProperty$I(AttachmentFileData_, protoName, DEV_MODE ? 'AttachmentFileData' : '[Unknown]');
let AttachmentFileData = AttachmentFileData_;
(function (_AttachmentFileData) {})(AttachmentFileData || (AttachmentFileData = {}));
Object.defineProperties(AttachmentFileData, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "file_data": {
        value: 1
      },
      "fileData": {
        value: 1
      },
      "primary_type": {
        value: 3
      },
      "primaryType": {
        value: 3
      },
      "sub_type": {
        value: 2
      },
      "subType": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "file_data"
      },
      '3': {
        value: "primary_type"
      },
      '2': {
        value: "sub_type"
      }
    }))
  }
});
Object.defineProperties(AttachmentFileData.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: FileData.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(AttachmentFileData);
  Object.freeze(AttachmentFileData.prototype);
}

async function uploadAttachmentsRemote(projectIdOrSym, attachments) {
  const formData = new FormData();
  formData.append('projectIdOrSym', String(projectIdOrSym));
  for (const [metadata, file] of attachments) {
    const simpleMetadata = metadata.setFileData(new FileData());
    formData.append('files', file);
    formData.append('fileMetadata', JSON.stringify(simpleMetadata.toJson()));
  }
  const result = await fetch(`${getUploadRoot()}/project/attachments.uploadAttachments`, {
    method: 'post',
    body: formData,
    credentials: 'include'
  });
  const attachmentResults = await result.json();
  return attachmentResults.map(json => Attachment.fromJson(json));
}
async function uploadFilesRemote(files) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const result = await fetch(`${getUploadRoot()}/project/attachments.uploadFiles`, {
    method: 'post',
    body: formData,
    credentials: 'include'
  });
  const uploadResults = await result.json();
  return uploadResults.map(json => Upload.fromJson(json));
}
async function uploadOfferFileRemote(shareToken, file) {
  const formData = new FormData();
  formData.append('shareToken', shareToken);
  formData.append('file', file);
  const result = await fetch(`${getUploadRoot()}/project/attachments.uploadOfferFile`, {
    method: 'post',
    body: formData,
    credentials: 'include'
  });
  const json = await result.json();
  return Upload.fromJson(json);
}

const uploader$1 = new DealBatchUploader({
  typeLabel: 'deal attachment',
  upload: addDealAttachments
});
async function addDealAttachment(ctr, deal, upload) {
  return uploader$1.upload(ctr, deal, upload);
}
async function addDealAttachments(ctr, deal, uploads) {
  const results = new Map();
  const attachments = await addDealAttachmentsInner(ctr, deal, uploads.map(u => u.upload));
  for (const [i, attachment] of attachments.entries()) {
    results.set(uploads[i].uploadId, attachment);
  }
  return results;
}
async function addDealAttachmentsInner(ctr, dealSym, uploads) {
  const attachments = await uploadAttachmentsRemote(dealSym, uploads.map(u => [AttachmentFileData.from({
    fileData: {
      filename: u.file.name
    },
    primaryType: u.primaryType || AttachmentType.UNSET,
    subType: u.subType || []
  }), u.file]));
  ctr.dealChanged(dealSym);
  return attachments;
}

function removeAttachment(...args) {
  return postWithChecksum(resolve('/@r:/project/attachments.removeAttachment'), args, ChecksumType.CRC32C);
}
function setAttachmentTypes(...args) {
  return postWithChecksum(resolve('/@r:/project/attachments.setAttachmentTypes'), args, ChecksumType.CRC32C);
}
function getSomeAttachmentsZip(...args) {
  return postWithChecksum(resolve('/@r:/project/attachments.getSomeAttachmentsZip', 'upload'), args, ChecksumType.CRC32C);
}
function getAllAttachmentsZip(...args) {
  return postWithChecksum(resolve('/@r:/project/attachments.getAllAttachmentsZip', 'upload'), args, ChecksumType.CRC32C);
}

async function removeProjectAttachment(ava, ctr, projectSym, attachmentSym) {
  const id = await ava.deals[$symToId](projectSym);
  await removeAttachment(id, attachmentSym);
  ctr.dealChanged(projectSym);
}

async function downloadProjectAttachments(ava, ctr, projectSym, attachmentSyms) {
  let url = '';
  const syms = [...attachmentSyms];
  ensure(syms.length > 0, 'Cannot download 0 attachments');
  if (syms.length === 1) {
    url = resourceKeyToUrl(assert(syms[0]));
  } else {
    const key = await getSomeAttachmentsZip(ctr.sharedUser, await ava.deals[$symToId](projectSym), syms);
    url = resourceKeyToUrl(key);
  }
  window.location.href = url;
}

// eslint-disable-next-line max-params
async function setProjectAttachmentTypes(ava, ctr, projectSym, attachmentSym, primaryType, subTypes = []) {
  const id = await ava.deals[$symToId](projectSym);
  await setAttachmentTypes(id, attachmentSym, AttachmentType.from(primaryType), [...subTypes].map(u => AttachmentType.from(u)));
  ctr.dealChanged(projectSym);
}

function _defineProperty$H(obj, key, value) { key = _toPropertyKey$H(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$H(arg) { var key = _toPrimitive$H(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$H(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Attachments {
  constructor(ava, ctr) {
    _defineProperty$H(this, "ava", void 0);
    _defineProperty$H(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  add(dealSym, upload) {
    return addDealAttachment(this.ctr, dealSym, upload);
  }
  delete(dealSym, attachmentSym) {
    return removeProjectAttachment(this.ava, this.ctr, dealSym, attachmentSym);
  }
  setTypes(dealSym, attachmentSym, primaryType, subTypes) {
    return setProjectAttachmentTypes(this.ava, this.ctr, dealSym, attachmentSym, primaryType, subTypes);
  }
  download(dealSym, attachmentSyms) {
    return downloadProjectAttachments(this.ava, this.ctr, dealSym, attachmentSyms);
  }
}

function searchProjects$1(...args) {
  return postWithChecksum(resolve('/@r:/project.searchProjects'), args, ChecksumType.CRC32C);
}
function getProjectSummariesByIds(...args) {
  return postWithChecksum(resolve('/@r:/project.getProjectSummariesByIds'), args, ChecksumType.CRC32C);
}
function getProject(...args) {
  return postWithChecksum(resolve('/@r:/project.getProject'), args, ChecksumType.CRC32C);
}
function insertProject(...args) {
  return postWithChecksum(resolve('/@r:/project.insertProject'), args, ChecksumType.CRC32C);
}
function registerAndInsertProject(...args) {
  return postWithChecksum(resolve('/@r:/project.registerAndInsertProject'), args, ChecksumType.CRC32C);
}
function signInAndInsertProject(...args) {
  return postWithChecksum(resolve('/@r:/project.signInAndInsertProject'), args, ChecksumType.CRC32C);
}
function updateProject$1(...args) {
  return postWithChecksum(resolve('/@r:/project.updateProject'), args, ChecksumType.CRC32C);
}
function updateProject_deprecated(...args) {
  return postWithChecksum(resolve('/@r:/project.updateProject_deprecated'), args, ChecksumType.CRC32C);
}
function deleteProject(...args) {
  return postWithChecksum(resolve('/@r:/project.deleteProject'), args, ChecksumType.CRC32C);
}
function acceptOffer(...args) {
  return postWithChecksum(resolve('/@r:/project.acceptOffer'), args, ChecksumType.CRC32C);
}
function approveOffer(...args) {
  return postWithChecksum(resolve('/@r:/project.approveOffer'), args, ChecksumType.CRC32C);
}
function sendOffer(...args) {
  return postWithChecksum(resolve('/@r:/project.sendOffer'), args, ChecksumType.CRC32C);
}
function withdrawOffer(...args) {
  return postWithChecksum(resolve('/@r:/project.withdrawOffer'), args, ChecksumType.CRC32C);
}

// tslint:disable-next-line:no-unused-variable
var ProjectShareNdaStatus__;
(function (ProjectShareNdaStatus__) {
  ProjectShareNdaStatus__[ProjectShareNdaStatus__["UNSIGNED"] = 0] = "UNSIGNED";
  ProjectShareNdaStatus__[ProjectShareNdaStatus__["SIGNED"] = 1] = "SIGNED";
  ProjectShareNdaStatus__[ProjectShareNdaStatus__["EXEMPT"] = 2] = "EXEMPT";
})(ProjectShareNdaStatus__ || (ProjectShareNdaStatus__ = {}));
const ProjectShareNdaStatus_ = defineEnumProperties(ProjectShareNdaStatus__, [0, 1, 2]);
const ProjectShareNdaStatus = extendEnum('ProjectShareNdaStatus', [["UNSIGNED", 0], ["SIGNED", 1], ["EXEMPT", 2]])(ProjectShareNdaStatus_);

function _defineProperty$G(obj, key, value) { key = _toPropertyKey$G(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$G(arg) { var key = _toPrimitive$G(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$G(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectShareInfo_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      8: 7,
      35: 2,
      2: 7,
      36: 2,
      3: Int32OrString.shape,
      37: 2,
      4: 1,
      5: 1,
      6: 1,
      24: 1,
      25: 1,
      7: 7,
      15: 1,
      9: 1,
      10: 7,
      32: 7,
      40: 2,
      11: 0,
      12: 1,
      13: Percent.shape,
      14: 1,
      16: 0,
      17: 2,
      18: 0,
      19: 0,
      20: TypicalAmortization_.shape,
      21: 0,
      22: TypicalPricing.shape,
      27: PercentRange.shape,
      23: Term.shape,
      28: Percent.shape,
      29: Percent.shape,
      30: 2,
      31: 1,
      26: 2,
      39: 2,
      41: 2,
      33: UserOrProjectShare.shape,
      38: 2,
      34: SimpleDate.shape,
      99999: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 8:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareInfo field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 35:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareInfo field project_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 36:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field project_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Int32OrString);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field org_or_label.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 37:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field org_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field active.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field allow_make_offer.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field declined.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 24:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field auto_declined.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 25:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field declined_via_email_link.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareInfo field uses.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field invitation_sent.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 9:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field stacksource_sourced.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareInfo field com_stream_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 32:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareInfo field offer_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 40:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field offer_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareInfo field nda_status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectShareNdaStatus.fromStrictJson_(v, getFieldMap);
          }
          return ProjectShareNdaStatus.from(value);
        };
      case 12:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field concealed.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field lender_fee.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field require_offer_approval.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 16:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareInfo field loan_program_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanProgramType.fromStrictJson_(v, getFieldMap);
          }
          return LoanProgramType.from(value);
        };
      case 17:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field location_label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 18:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareInfo field required_attachment_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return AttachmentType.fromStrictJson_(v, getFieldMap);
            }
            return AttachmentType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 19:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareInfo field recourse.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return LoanProgramRecourse.fromStrictJson_(v, getFieldMap);
          }
          return LoanProgramRecourse.from(value);
        };
      case 20:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TypicalAmortization_);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field typical_amortization.
                Expected type TypicalAmortization.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TypicalAmortization', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 21:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareInfo field prepayment_penalty.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return PrepaymentPenaltyType.fromStrictJson_(v, getFieldMap);
            }
            return PrepaymentPenaltyType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 22:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TypicalPricing);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field typical_pricing.
                Expected type TypicalPricing.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TypicalPricing', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 27:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PercentRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field typical_pricing_flat.
                Expected type PercentRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PercentRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 23:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Term);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field loan_term.
                Expected type Term.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Term', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 28:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field max_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 29:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field min_dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 30:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field automated_quote.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 31:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field include_stacksource_fee.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 26:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field loan_program_notes.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 39:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field marketing_description.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 41:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field loan_program.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 33:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UserOrProjectShare);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field created_by.
                Expected type UserOrProjectShare.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UserOrProjectShare', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 38:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareInfo field created_by_user_sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 34:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareInfo field token_first_created.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 99999:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareInfo field loaded.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectShareInfo, 'ProjectShareInfo', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectShareInfo to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(8, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteId() {
    return this.setValueFieldById(8, null, null);
  }
  get sym() {
    return this.getFieldById(35, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(35, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(35, null, null);
  }
  get projectId() {
    return this.getFieldById(2, 0);
  }
  setProjectId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteProjectId() {
    return this.setValueFieldById(2, null, null);
  }
  get projectSym() {
    return this.getFieldById(36, "");
  }
  setProjectSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(36, value, null);
  }
  deleteProjectSym() {
    return this.setValueFieldById(36, null, null);
  }
  get orgOrLabel() {
    return this.getFieldById(3, Int32OrString.getDefault());
  }
  setOrgOrLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteOrgOrLabel() {
    return this.setValueFieldById(3, null, null);
  }
  get orgSym() {
    return this.getFieldById(37, "");
  }
  setOrgSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(37, value, null);
  }
  deleteOrgSym() {
    return this.setValueFieldById(37, null, null);
  }
  get active() {
    return this.getFieldById(4, false);
  }
  setActive(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteActive() {
    return this.setValueFieldById(4, null, null);
  }
  get allowMakeOffer() {
    return this.getFieldById(5, false);
  }
  setAllowMakeOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteAllowMakeOffer() {
    return this.setValueFieldById(5, null, null);
  }
  get declined() {
    return this.getFieldById(6, false);
  }
  setDeclined(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteDeclined() {
    return this.setValueFieldById(6, null, null);
  }
  get autoDeclined() {
    return this.getFieldById(24, false);
  }
  setAutoDeclined(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(24, value, null);
  }
  deleteAutoDeclined() {
    return this.setValueFieldById(24, null, null);
  }
  get declinedViaEmailLink() {
    return this.getFieldById(25, false);
  }
  setDeclinedViaEmailLink(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(25, value, null);
  }
  deleteDeclinedViaEmailLink() {
    return this.setValueFieldById(25, null, null);
  }
  get uses() {
    return this.getFieldById(7, 0);
  }
  setUses(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteUses() {
    return this.setValueFieldById(7, null, null);
  }
  get invitationSent() {
    return this.getFieldById(15, false);
  }
  setInvitationSent(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteInvitationSent() {
    return this.setValueFieldById(15, null, null);
  }
  get stacksourceSourced() {
    return this.getFieldById(9, false);
  }
  setStacksourceSourced(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteStacksourceSourced() {
    return this.setValueFieldById(9, null, null);
  }
  get comStreamId() {
    return this.getFieldById(10, 0);
  }
  setComStreamId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteComStreamId() {
    return this.setValueFieldById(10, null, null);
  }
  get offerIdList() {
    return this.getRepeatedFieldById(32);
  }
  hasOfferId(value) {
    return this.repeatedFieldByIdHas(32, value);
  }
  addOfferId(value) {
    return this.addToRepeatedFieldById(32, value);
  }
  addEachOfferId(values) {
    let T = this;
    for (const value of values) {
      T = T.addOfferId(value);
    }
    return T;
  }
  setOfferId(index, value) {
    return this.setRepeatedFieldById(32, index, value);
  }
  removeOfferId(index) {
    return this.removeFromRepeatedFieldById(32, index);
  }
  clearOfferIdList() {
    return this.clearRepeatedFieldById(32);
  }
  setOfferIdList(values) {
    return this.overwriteRepeatedFieldById(32, values);
  }
  get offerSymList() {
    return this.getRepeatedFieldById(40);
  }
  hasOfferSym(value) {
    return this.repeatedFieldByIdHas(40, value);
  }
  addOfferSym(value) {
    return this.addToRepeatedFieldById(40, value);
  }
  addEachOfferSym(values) {
    let T = this;
    for (const value of values) {
      T = T.addOfferSym(value);
    }
    return T;
  }
  setOfferSym(index, value) {
    return this.setRepeatedFieldById(40, index, value);
  }
  removeOfferSym(index) {
    return this.removeFromRepeatedFieldById(40, index);
  }
  clearOfferSymList() {
    return this.clearRepeatedFieldById(40);
  }
  setOfferSymList(values) {
    return this.overwriteRepeatedFieldById(40, values);
  }
  get ndaStatus() {
    return this.getFieldById(11, 0);
  }
  setNdaStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteNdaStatus() {
    return this.setValueFieldById(11, null, null);
  }
  get concealed() {
    return this.getFieldById(12, false);
  }
  setConcealed(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteConcealed() {
    return this.setValueFieldById(12, null, null);
  }
  get lenderFee() {
    return this.getFieldById(13, Percent.getDefault());
  }
  setLenderFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteLenderFee() {
    return this.setValueFieldById(13, null, null);
  }
  get requireOfferApproval() {
    return this.getFieldById(14, false);
  }
  setRequireOfferApproval(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteRequireOfferApproval() {
    return this.setValueFieldById(14, null, null);
  }
  get loanProgramType() {
    return this.getFieldById(16, 0);
  }
  setLoanProgramType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deleteLoanProgramType() {
    return this.setValueFieldById(16, null, null);
  }
  get locationLabel() {
    return this.getFieldById(17, "");
  }
  setLocationLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteLocationLabel() {
    return this.setValueFieldById(17, null, null);
  }
  get requiredAttachmentTypeList() {
    return this.getRepeatedFieldById(18);
  }
  hasRequiredAttachmentType(value) {
    return this.repeatedFieldByIdHas(18, value);
  }
  addRequiredAttachmentType(value) {
    return this.addToRepeatedFieldById(18, value);
  }
  addEachRequiredAttachmentType(values) {
    let T = this;
    for (const value of values) {
      T = T.addRequiredAttachmentType(value);
    }
    return T;
  }
  setRequiredAttachmentType(index, value) {
    return this.setRepeatedFieldById(18, index, value);
  }
  removeRequiredAttachmentType(index) {
    return this.removeFromRepeatedFieldById(18, index);
  }
  clearRequiredAttachmentTypeList() {
    return this.clearRepeatedFieldById(18);
  }
  setRequiredAttachmentTypeList(values) {
    return this.overwriteRepeatedFieldById(18, values);
  }
  get recourse() {
    return this.getFieldById(19, 0);
  }
  setRecourse(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(19, value, null);
  }
  deleteRecourse() {
    return this.setValueFieldById(19, null, null);
  }
  get typicalAmortization() {
    return this.getFieldById(20, TypicalAmortization_.getDefault());
  }
  setTypicalAmortization(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, null);
  }
  deleteTypicalAmortization() {
    return this.setValueFieldById(20, null, null);
  }
  get prepaymentPenaltyList() {
    return this.getRepeatedFieldById(21);
  }
  hasPrepaymentPenalty(value) {
    return this.repeatedFieldByIdHas(21, value);
  }
  addPrepaymentPenalty(value) {
    return this.addToRepeatedFieldById(21, value);
  }
  addEachPrepaymentPenalty(values) {
    let T = this;
    for (const value of values) {
      T = T.addPrepaymentPenalty(value);
    }
    return T;
  }
  setPrepaymentPenalty(index, value) {
    return this.setRepeatedFieldById(21, index, value);
  }
  removePrepaymentPenalty(index) {
    return this.removeFromRepeatedFieldById(21, index);
  }
  clearPrepaymentPenaltyList() {
    return this.clearRepeatedFieldById(21);
  }
  setPrepaymentPenaltyList(values) {
    return this.overwriteRepeatedFieldById(21, values);
  }
  get typicalPricing() {
    return this.getFieldById(22, TypicalPricing.getDefault());
  }
  setTypicalPricing(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteTypicalPricing() {
    return this.setValueFieldById(22, null, null);
  }
  get typicalPricingFlat() {
    return this.getFieldById(27, PercentRange.getDefault());
  }
  setTypicalPricingFlat(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(27, value, null);
  }
  deleteTypicalPricingFlat() {
    return this.setValueFieldById(27, null, null);
  }
  get loanTermList() {
    return this.getRepeatedFieldById(23);
  }
  hasLoanTerm(value) {
    return this.repeatedFieldByIdHas(23, value);
  }
  addLoanTerm(value) {
    return this.addToRepeatedFieldById(23, value);
  }
  addEachLoanTerm(values) {
    let T = this;
    for (const value of values) {
      T = T.addLoanTerm(value);
    }
    return T;
  }
  setLoanTerm(index, value) {
    return this.setRepeatedFieldById(23, index, value);
  }
  removeLoanTerm(index) {
    return this.removeFromRepeatedFieldById(23, index);
  }
  clearLoanTermList() {
    return this.clearRepeatedFieldById(23);
  }
  setLoanTermList(values) {
    return this.overwriteRepeatedFieldById(23, values);
  }
  get maxLtv() {
    return this.getFieldById(28, Percent.getDefault());
  }
  setMaxLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(28, value, null);
  }
  deleteMaxLtv() {
    return this.setValueFieldById(28, null, null);
  }
  get minDscr() {
    return this.getFieldById(29, Percent.getDefault());
  }
  setMinDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(29, value, null);
  }
  deleteMinDscr() {
    return this.setValueFieldById(29, null, null);
  }
  get automatedQuote() {
    return this.getFieldById(30, "");
  }
  setAutomatedQuote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(30, value, null);
  }
  deleteAutomatedQuote() {
    return this.setValueFieldById(30, null, null);
  }
  get includeStacksourceFee() {
    return this.getFieldById(31, false);
  }
  setIncludeStacksourceFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(31, value, null);
  }
  deleteIncludeStacksourceFee() {
    return this.setValueFieldById(31, null, null);
  }
  get loanProgramNotes() {
    return this.getFieldById(26, "");
  }
  setLoanProgramNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(26, value, null);
  }
  deleteLoanProgramNotes() {
    return this.setValueFieldById(26, null, null);
  }
  get marketingDescription() {
    return this.getFieldById(39, "");
  }
  setMarketingDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(39, value, null);
  }
  deleteMarketingDescription() {
    return this.setValueFieldById(39, null, null);
  }
  get loanProgram() {
    return this.getFieldById(41, "");
  }
  setLoanProgram(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(41, value, null);
  }
  deleteLoanProgram() {
    return this.setValueFieldById(41, null, null);
  }
  get createdBy() {
    return this.getFieldById(33, UserOrProjectShare.getDefault());
  }
  setCreatedBy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(33, value, null);
  }
  deleteCreatedBy() {
    return this.setValueFieldById(33, null, null);
  }
  get createdByUserSym() {
    return this.getFieldById(38, "");
  }
  setCreatedByUserSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(38, value, null);
  }
  deleteCreatedByUserSym() {
    return this.setValueFieldById(38, null, null);
  }
  get tokenFirstCreated() {
    return this.getFieldById(34, SimpleDate.getDefault());
  }
  setTokenFirstCreated(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(34, value, null);
  }
  deleteTokenFirstCreated() {
    return this.setValueFieldById(34, null, null);
  }
  get loaded() {
    return this.getFieldById(99999, false);
  }
  setLoaded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(99999, value, null);
  }
  deleteLoaded() {
    return this.setValueFieldById(99999, null, null);
  }
}
_defineProperty$G(ProjectShareInfo_, protoName, DEV_MODE ? 'ProjectShareInfo' : '[Unknown]');
let ProjectShareInfo = ProjectShareInfo_;
(function (_ProjectShareInfo) {})(ProjectShareInfo || (ProjectShareInfo = {}));
Object.defineProperties(ProjectShareInfo, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 8
      },
      "sym": {
        value: 35
      },
      "project_id": {
        value: 2
      },
      "projectId": {
        value: 2
      },
      "project_sym": {
        value: 36
      },
      "projectSym": {
        value: 36
      },
      "org_or_label": {
        value: 3
      },
      "orgOrLabel": {
        value: 3
      },
      "org_sym": {
        value: 37
      },
      "orgSym": {
        value: 37
      },
      "active": {
        value: 4
      },
      "allow_make_offer": {
        value: 5
      },
      "allowMakeOffer": {
        value: 5
      },
      "declined": {
        value: 6
      },
      "auto_declined": {
        value: 24
      },
      "autoDeclined": {
        value: 24
      },
      "declined_via_email_link": {
        value: 25
      },
      "declinedViaEmailLink": {
        value: 25
      },
      "uses": {
        value: 7
      },
      "invitation_sent": {
        value: 15
      },
      "invitationSent": {
        value: 15
      },
      "stacksource_sourced": {
        value: 9
      },
      "stacksourceSourced": {
        value: 9
      },
      "com_stream_id": {
        value: 10
      },
      "comStreamId": {
        value: 10
      },
      "offer_id": {
        value: 32
      },
      "offerId": {
        value: 32
      },
      "offer_sym": {
        value: 40
      },
      "offerSym": {
        value: 40
      },
      "nda_status": {
        value: 11
      },
      "ndaStatus": {
        value: 11
      },
      "concealed": {
        value: 12
      },
      "lender_fee": {
        value: 13
      },
      "lenderFee": {
        value: 13
      },
      "require_offer_approval": {
        value: 14
      },
      "requireOfferApproval": {
        value: 14
      },
      "loan_program_type": {
        value: 16
      },
      "loanProgramType": {
        value: 16
      },
      "location_label": {
        value: 17
      },
      "locationLabel": {
        value: 17
      },
      "required_attachment_type": {
        value: 18
      },
      "requiredAttachmentType": {
        value: 18
      },
      "recourse": {
        value: 19
      },
      "typical_amortization": {
        value: 20
      },
      "typicalAmortization": {
        value: 20
      },
      "prepayment_penalty": {
        value: 21
      },
      "prepaymentPenalty": {
        value: 21
      },
      "typical_pricing": {
        value: 22
      },
      "typicalPricing": {
        value: 22
      },
      "typical_pricing_flat": {
        value: 27
      },
      "typicalPricingFlat": {
        value: 27
      },
      "loan_term": {
        value: 23
      },
      "loanTerm": {
        value: 23
      },
      "max_ltv": {
        value: 28
      },
      "maxLtv": {
        value: 28
      },
      "min_dscr": {
        value: 29
      },
      "minDscr": {
        value: 29
      },
      "automated_quote": {
        value: 30
      },
      "automatedQuote": {
        value: 30
      },
      "include_stacksource_fee": {
        value: 31
      },
      "includeStacksourceFee": {
        value: 31
      },
      "loan_program_notes": {
        value: 26
      },
      "loanProgramNotes": {
        value: 26
      },
      "marketing_description": {
        value: 39
      },
      "marketingDescription": {
        value: 39
      },
      "loan_program": {
        value: 41
      },
      "loanProgram": {
        value: 41
      },
      "created_by": {
        value: 33
      },
      "createdBy": {
        value: 33
      },
      "created_by_user_sym": {
        value: 38
      },
      "createdByUserSym": {
        value: 38
      },
      "token_first_created": {
        value: 34
      },
      "tokenFirstCreated": {
        value: 34
      },
      "loaded": {
        value: 99999
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '8': {
        value: "id"
      },
      '35': {
        value: "sym"
      },
      '2': {
        value: "project_id"
      },
      '36': {
        value: "project_sym"
      },
      '3': {
        value: "org_or_label"
      },
      '37': {
        value: "org_sym"
      },
      '4': {
        value: "active"
      },
      '5': {
        value: "allow_make_offer"
      },
      '6': {
        value: "declined"
      },
      '24': {
        value: "auto_declined"
      },
      '25': {
        value: "declined_via_email_link"
      },
      '7': {
        value: "uses"
      },
      '15': {
        value: "invitation_sent"
      },
      '9': {
        value: "stacksource_sourced"
      },
      '10': {
        value: "com_stream_id"
      },
      '32': {
        value: "offer_id"
      },
      '40': {
        value: "offer_sym"
      },
      '11': {
        value: "nda_status"
      },
      '12': {
        value: "concealed"
      },
      '13': {
        value: "lender_fee"
      },
      '14': {
        value: "require_offer_approval"
      },
      '16': {
        value: "loan_program_type"
      },
      '17': {
        value: "location_label"
      },
      '18': {
        value: "required_attachment_type"
      },
      '19': {
        value: "recourse"
      },
      '20': {
        value: "typical_amortization"
      },
      '21': {
        value: "prepayment_penalty"
      },
      '22': {
        value: "typical_pricing"
      },
      '27': {
        value: "typical_pricing_flat"
      },
      '23': {
        value: "loan_term"
      },
      '28': {
        value: "max_ltv"
      },
      '29': {
        value: "min_dscr"
      },
      '30': {
        value: "automated_quote"
      },
      '31': {
        value: "include_stacksource_fee"
      },
      '26': {
        value: "loan_program_notes"
      },
      '39': {
        value: "marketing_description"
      },
      '41': {
        value: "loan_program"
      },
      '33': {
        value: "created_by"
      },
      '38': {
        value: "created_by_user_sym"
      },
      '34': {
        value: "token_first_created"
      },
      '99999': {
        value: "loaded"
      }
    }))
  }
});
Object.defineProperties(ProjectShareInfo.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      3: Int32OrString.prototype,
      13: Percent.prototype,
      20: TypicalAmortization_.prototype,
      22: TypicalPricing.prototype,
      27: PercentRange.prototype,
      23: Term.prototype,
      28: Percent.prototype,
      29: Percent.prototype,
      33: UserOrProjectShare.prototype,
      34: SimpleDate.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectShareInfo);
  Object.freeze(ProjectShareInfo.prototype);
}

function _defineProperty$F(obj, key, value) { key = _toPropertyKey$F(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$F(arg) { var key = _toPrimitive$F(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$F(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class NewProjectInfo_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      4: 2,
      2: ProjectShareInfo.shape,
      3: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message NewProjectInfo field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message NewProjectInfo field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectShareInfo);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message NewProjectInfo field project_share_info.
                Expected type ProjectShareInfo.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectShareInfo', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message NewProjectInfo field creation_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(NewProjectInfo, 'NewProjectInfo', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message NewProjectInfo to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(4, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(4, null, null);
  }
  get projectShareInfoList() {
    return this.getRepeatedFieldById(2);
  }
  hasProjectShareInfo(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addProjectShareInfo(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachProjectShareInfo(values) {
    let T = this;
    for (const value of values) {
      T = T.addProjectShareInfo(value);
    }
    return T;
  }
  setProjectShareInfo(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeProjectShareInfo(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearProjectShareInfoList() {
    return this.clearRepeatedFieldById(2);
  }
  setProjectShareInfoList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
  get creationTime() {
    return this.getFieldById(3, DEPRECATED_Date.getDefault());
  }
  setCreationTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteCreationTime() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$F(NewProjectInfo_, protoName, DEV_MODE ? 'NewProjectInfo' : '[Unknown]');
let NewProjectInfo = NewProjectInfo_;
(function (_NewProjectInfo) {})(NewProjectInfo || (NewProjectInfo = {}));
Object.defineProperties(NewProjectInfo, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 4
      },
      "project_share_info": {
        value: 2
      },
      "projectShareInfo": {
        value: 2
      },
      "creation_time": {
        value: 3
      },
      "creationTime": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '4': {
        value: "sym"
      },
      '2': {
        value: "project_share_info"
      },
      '3': {
        value: "creation_time"
      }
    }))
  }
});
Object.defineProperties(NewProjectInfo.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: ProjectShareInfo.prototype,
      3: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(NewProjectInfo);
  Object.freeze(NewProjectInfo.prototype);
}

function createProject$1(data) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = Object.assign({}, data);
  if (d.hotelFlag && d.hotelFlag.oragnization) {
    d.hotelFlag.organizationId = symToId(d.hotelFlag.organization);
    delete d.hotelFlag.organizationId;
  }
  if (d.tenancy && d.tenancy.tenant && d.tenancy.tenant.sym) {
    d.tenancy.tenant.intValue = symToId(d.tenancy.tenant.sym);
    delete d.tenancy.tenant.sym;
  }
  if (d.solicitedLenders) {
    d.solicitedLenders = d.solicitedLenders.map(symOrStringToInt32OrString);
  }
  return Project.from(d);
}

async function createProject(unused_ctr, projectData) {
  const project = createProject$1(projectData);
  const response = await insertProject(project);
  const newProjectInfo = NewProjectInfo.fromJson(response);
  return newProjectInfo.sym;
}

function getDealSummaryBySym(...args) {
  return postWithChecksum(resolve('/@r:/deal.getDealSummaryBySym'), args, ChecksumType.CRC32C);
}
function getDealsSummariesBySyms(...args) {
  return postWithChecksum(resolve('/@r:/deal.getDealsSummariesBySyms'), args, ChecksumType.CRC32C);
}
function getDealBySym(...args) {
  return postWithChecksum(resolve('/@r:/deal.getDealBySym'), args, ChecksumType.CRC32C);
}
function getDealsBySyms(...args) {
  return postWithChecksum(resolve('/@r:/deal.getDealsBySyms'), args, ChecksumType.CRC32C);
}

function getSymsByIds$1(...args) {
  return postWithChecksum(resolve('/@r:/deal/get_syms.getSymsByIds'), args, ChecksumType.CRC32C);
}
function getIdsBySyms$1(...args) {
  return postWithChecksum(resolve('/@r:/deal/get_syms.getIdsBySyms'), args, ChecksumType.CRC32C);
}

// Get Deal Syms -----------------

const syms = new BatchLoader({
  typeLabel: 'sym',
  load: getSymsByIds,
  cache: {
    maxSize: 1024,
    maxAge: Infinity
  }
});
function getSymById(ctr, id) {
  return syms.load(ctr, id);
}
async function getSymsByIds(ctr, ids) {
  const results = await getSymsByIds$1(ctr.sharedUser, ids);
  return new Map(results);
}

// Get Deals -----------------

const deals = new BatchLoader({
  typeLabel: 'deal',
  load: getDeals,
  cache: {
    maxSize: 16,
    maxAge: 60 * 60 * 1000
  },
  createError: () => new ProjectNotFoundError(),
  initController(ctr) {
    ctr.onDealChanged(sym => deals.uncache(ctr, sym));
  }
});
async function getDeal(ctr, sym) {
  return deals.load(ctr, sym);
}
async function getDeals(ctr, syms) {
  const map = new Map();
  const deals = await getDealsBySyms(syms, ctr.sharedUser);
  for (const data of deals) {
    const deal = Deal.fromJson(data);
    map.set(deal.sym, deal);
  }
  return map;
}

// Get Deal Summaries -----------------

const summaries = new BatchLoader({
  typeLabel: 'deal summary',
  load: getSummaries,
  cache: {
    maxSize: 256,
    maxAge: 60 * 60 * 1000
  },
  createError: () => new ProjectNotFoundError(),
  initController(ctr) {
    ctr.onDealChanged(sym => summaries.uncache(ctr, sym));
  }
});
async function getSummary(ctr, sym) {
  return summaries.load(ctr, sym);
}
async function getSummaries(ctr, syms) {
  const map = new Map();
  const deals = await getDealsSummariesBySyms(syms, ctr.sharedUser);
  for (const data of deals) {
    const deal = Deal.fromJson(data);
    map.set(deal.sym, deal);
  }
  return map;
}

// Get Project ID -----------------

const ids = new BatchLoader({
  typeLabel: 'id',
  load: getIdsBySyms,
  cache: {
    maxSize: 1024,
    maxAge: Infinity
  }
});
async function getIdBySym(ctr, sym) {
  return ids.load(ctr, sym);
}
async function getIdsBySyms(ctr, syms) {
  const results = await getIdsBySyms$1(ctr.sharedUser, syms);
  return new Map(results);
}

function _defineProperty$E(obj, key, value) { key = _toPropertyKey$E(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$E(arg) { var key = _toPrimitive$E(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$E(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var UploadError__;
(function (UploadError__) {
  UploadError__[UploadError__["UNSUPPORTED_IMAGE_TYPE"] = 0] = "UNSUPPORTED_IMAGE_TYPE";
  UploadError__[UploadError__["UNSUPPORTED_IMAGE"] = 1] = "UNSUPPORTED_IMAGE";
  UploadError__[UploadError__["EXISTING_IMAGE"] = 2] = "EXISTING_IMAGE";
})(UploadError__ || (UploadError__ = {}));
const UploadError_ = defineEnumProperties(UploadError__, [0, 1, 2]);
const UploadError = extendEnum('UploadError', [["UNSUPPORTED_IMAGE_TYPE", 0], ["UNSUPPORTED_IMAGE", 1], ["EXISTING_IMAGE", 2]])(UploadError_);
var _UploadResponse_Type_;
(function (_UploadResponse_Type_) {
  _UploadResponse_Type_[_UploadResponse_Type_["UNSET"] = 0] = "UNSET";
  _UploadResponse_Type_[_UploadResponse_Type_["KEY"] = 1] = "KEY";
  _UploadResponse_Type_[_UploadResponse_Type_["ERROR"] = 2] = "ERROR";
})(_UploadResponse_Type_ || (_UploadResponse_Type_ = {}));
const _UploadResponse_Type = defineEnumProperties(_UploadResponse_Type_, [1, 2]);
class UploadResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 0,
      3: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UploadResponse field key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message UploadResponse field error.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return UploadError.fromStrictJson_(v, getFieldMap);
          }
          return UploadError.from(value);
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UploadResponse field filename.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UploadResponse, 'UploadResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UploadResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get TypeCase() {
    return _UploadResponse_Type;
  }
  get typeCase() {
    return this.getCaseFromIds([1, 2]);
  }
  get key() {
    return this.getFieldById(1, "");
  }
  setKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "type");
  }
  deleteKey() {
    return this.setValueFieldById(1, null, "type");
  }
  get error() {
    return this.getFieldById(2, 0);
  }
  setError(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "type");
  }
  deleteError() {
    return this.setValueFieldById(2, null, "type");
  }
  get filename() {
    return this.getFieldById(3, "");
  }
  setFilename(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteFilename() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$E(UploadResponse_, protoName, DEV_MODE ? 'UploadResponse' : '[Unknown]');
let UploadResponse = UploadResponse_;
(function (_UploadResponse) {})(UploadResponse || (UploadResponse = {}));
Object.defineProperties(UploadResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "key": {
        value: 1
      },
      "error": {
        value: 2
      },
      "filename": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "key"
      },
      '2': {
        value: "error"
      },
      '3': {
        value: "filename"
      }
    }))
  }
});
Object.defineProperties(UploadResponse.prototype, {
  oneofs_: {
    value: {
      "type": [1, 2]
    }
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UploadResponse);
  Object.freeze(UploadResponse.prototype);
}

async function uploadImagesRemote(projectIdOrSym, images) {
  const formData = new FormData();
  formData.append('projectIdOrSym', String(projectIdOrSym));
  for (const image of images) {
    formData.append('files', image);
  }
  const result = await fetch(`${getUploadRoot()}/project/images.uploadImages`, {
    method: 'post',
    body: formData,
    credentials: 'include'
  });
  const responses = await result.json();
  return responses.map(r => UploadResponse.fromJson(r));
}

const uploader = new DealBatchUploader({
  typeLabel: 'deal image',
  upload: addDealImages
});
class ImageUploadError extends Error {
  static formatType(type, filename) {
    switch (type) {
      case UploadError.EXISTING_IMAGE:
        return tmsg`Image already exists: ${filename}`;
      case UploadError.UNSUPPORTED_IMAGE_TYPE:
        return tmsg`Image type is unsupported: ${filename}`;
      case UploadError.UNSUPPORTED_IMAGE:
        return tmsg`Image is unsupported: ${filename}`;
    }
    assert.fail(`Unknown image upload error type ${type}: ${filename}`);
    return '';
  }
  constructor({
    type,
    filename
  }) {
    super(ImageUploadError.formatType(type, filename));
  }
}
async function addDealImage(ctr, deal, upload) {
  return uploader.upload(ctr, deal, upload);
}
async function addDealImages(ctr, deal, uploads) {
  const results = new Map();
  const responses = await uploadImagesRemote(deal, uploads.map(u => u.upload));
  for (const [i, response] of responses.entries()) {
    const uploadId = uploads[i].uploadId;
    switch (response.typeCase) {
      case UploadResponse.TypeCase.KEY:
        results.set(uploadId, DealImage.from({
          key: response.key
        }));
        break;
      case UploadResponse.TypeCase.ERROR:
        results.set(uploadId, new ImageUploadError({
          type: response.error,
          filename: response.filename
        }));
        break;
    }
  }
  ctr.dealChanged(deal);
  return results;
}

function removeImage(...args) {
  return postWithChecksum(resolve('/@r:/project/images.removeImage'), args, ChecksumType.CRC32C);
}

async function removeProjectImage(ava, ctr, projectSym, imageSym) {
  const id = await ava.deals[$symToId](projectSym);
  await removeImage(id, imageSym);
  ctr.dealChanged(projectSym);
}

function _defineProperty$D(obj, key, value) { key = _toPropertyKey$D(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$D(arg) { var key = _toPrimitive$D(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$D(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Images {
  constructor(ava, ctr) {
    _defineProperty$D(this, "ava", void 0);
    _defineProperty$D(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  add(projectSym, upload) {
    return addDealImage(this.ctr, projectSym, upload);
  }
  delete(projectSym, imageSym) {
    return removeProjectImage(this.ava, this.ctr, projectSym, imageSym);
  }
}

function _defineProperty$C(obj, key, value) { key = _toPropertyKey$C(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$C(arg) { var key = _toPrimitive$C(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$C(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class ProjectSummary_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      25: 2,
      13: 7,
      19: Int32OrString.shape,
      24: Int32OrString.shape,
      2: 1,
      3: 1,
      22: 1,
      71: 0,
      4: 0,
      5: 0,
      6: 2,
      7: StandardUsAddress.shape,
      9: 7,
      15: CurrencyValueRange.shape,
      14: RichText.shape,
      10: 2,
      11: 7,
      12: 7,
      20: DEPRECATED_Date.shape,
      23: 7,
      16: 7,
      17: DEPRECATED_Date.shape,
      18: 7,
      21: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 25:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectSummary field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 13:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field owner.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 19:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field sponsor_user.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 24:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Int32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field broker_user.
                Expected type Int32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Int32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectSummary field user_has_write_permission.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectSummary field draft.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 22:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectSummary field approved.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 71:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectSummary field status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectStatus.fromStrictJson_(v, getFieldMap);
          }
          return ProjectStatus.from(value);
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectSummary field financing_purpose.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FinancingPurpose.fromStrictJson_(v, getFieldMap);
          }
          return FinancingPurpose.from(value);
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectSummary field asset_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AssetType.fromStrictJson_(v, getFieldMap);
          }
          return AssetType.from(value);
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectSummary field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field number_of_units.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 15:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValueRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field principal_needed.
                Expected type CurrencyValueRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValueRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field note.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectSummary field tile_image.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 11:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 12:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field accepted_offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 20:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field offer_accepted_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 23:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field offer_underwriting_requested.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 16:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field leading_offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 17:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectSummary field creation_time.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 18:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectSummary field capital_advisor.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 21:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectSummary field highlight.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectSummary, 'ProjectSummary', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectSummary to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(25, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(25, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(25, null, null);
  }
  get owner() {
    return this.getFieldById(13, 0);
  }
  setOwner(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteOwner() {
    return this.setValueFieldById(13, null, null);
  }
  get sponsorUserList() {
    return this.getRepeatedFieldById(19);
  }
  hasSponsorUser(value) {
    return this.repeatedFieldByIdHas(19, value);
  }
  addSponsorUser(value) {
    return this.addToRepeatedFieldById(19, value);
  }
  addEachSponsorUser(values) {
    let T = this;
    for (const value of values) {
      T = T.addSponsorUser(value);
    }
    return T;
  }
  setSponsorUser(index, value) {
    return this.setRepeatedFieldById(19, index, value);
  }
  removeSponsorUser(index) {
    return this.removeFromRepeatedFieldById(19, index);
  }
  clearSponsorUserList() {
    return this.clearRepeatedFieldById(19);
  }
  setSponsorUserList(values) {
    return this.overwriteRepeatedFieldById(19, values);
  }
  get brokerUserList() {
    return this.getRepeatedFieldById(24);
  }
  hasBrokerUser(value) {
    return this.repeatedFieldByIdHas(24, value);
  }
  addBrokerUser(value) {
    return this.addToRepeatedFieldById(24, value);
  }
  addEachBrokerUser(values) {
    let T = this;
    for (const value of values) {
      T = T.addBrokerUser(value);
    }
    return T;
  }
  setBrokerUser(index, value) {
    return this.setRepeatedFieldById(24, index, value);
  }
  removeBrokerUser(index) {
    return this.removeFromRepeatedFieldById(24, index);
  }
  clearBrokerUserList() {
    return this.clearRepeatedFieldById(24);
  }
  setBrokerUserList(values) {
    return this.overwriteRepeatedFieldById(24, values);
  }
  get userHasWritePermission() {
    return this.getFieldById(2, false);
  }
  setUserHasWritePermission(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUserHasWritePermission() {
    return this.setValueFieldById(2, null, null);
  }
  get draft() {
    return this.getFieldById(3, false);
  }
  setDraft(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteDraft() {
    return this.setValueFieldById(3, null, null);
  }
  get approved() {
    return this.getFieldById(22, false);
  }
  setApproved(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, null);
  }
  deleteApproved() {
    return this.setValueFieldById(22, null, null);
  }
  get status() {
    return this.getFieldById(71, 0);
  }
  setStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(71, value, null);
  }
  deleteStatus() {
    return this.setValueFieldById(71, null, null);
  }
  get financingPurpose() {
    return this.getFieldById(4, 0);
  }
  setFinancingPurpose(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteFinancingPurpose() {
    return this.setValueFieldById(4, null, null);
  }
  get assetType() {
    return this.getFieldById(5, 0);
  }
  setAssetType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteAssetType() {
    return this.setValueFieldById(5, null, null);
  }
  get name() {
    return this.getFieldById(6, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteName() {
    return this.setValueFieldById(6, null, null);
  }
  get address() {
    return this.getFieldById(7, StandardUsAddress.getDefault());
  }
  setAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteAddress() {
    return this.setValueFieldById(7, null, null);
  }
  get numberOfUnits() {
    return this.getFieldById(9, 0);
  }
  setNumberOfUnits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteNumberOfUnits() {
    return this.setValueFieldById(9, null, null);
  }
  get principalNeeded() {
    return this.getFieldById(15, CurrencyValueRange.getDefault());
  }
  setPrincipalNeeded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deletePrincipalNeeded() {
    return this.setValueFieldById(15, null, null);
  }
  get note() {
    return this.getFieldById(14, RichText.getDefault());
  }
  setNote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteNote() {
    return this.setValueFieldById(14, null, null);
  }
  get tileImageList() {
    return this.getRepeatedFieldById(10);
  }
  hasTileImage(value) {
    return this.repeatedFieldByIdHas(10, value);
  }
  addTileImage(value) {
    return this.addToRepeatedFieldById(10, value);
  }
  addEachTileImage(values) {
    let T = this;
    for (const value of values) {
      T = T.addTileImage(value);
    }
    return T;
  }
  setTileImage(index, value) {
    return this.setRepeatedFieldById(10, index, value);
  }
  removeTileImage(index) {
    return this.removeFromRepeatedFieldById(10, index);
  }
  clearTileImageList() {
    return this.clearRepeatedFieldById(10);
  }
  setTileImageList(values) {
    return this.overwriteRepeatedFieldById(10, values);
  }
  get offerList() {
    return this.getRepeatedFieldById(11);
  }
  hasOffer(value) {
    return this.repeatedFieldByIdHas(11, value);
  }
  addOffer(value) {
    return this.addToRepeatedFieldById(11, value);
  }
  addEachOffer(values) {
    let T = this;
    for (const value of values) {
      T = T.addOffer(value);
    }
    return T;
  }
  setOffer(index, value) {
    return this.setRepeatedFieldById(11, index, value);
  }
  removeOffer(index) {
    return this.removeFromRepeatedFieldById(11, index);
  }
  clearOfferList() {
    return this.clearRepeatedFieldById(11);
  }
  setOfferList(values) {
    return this.overwriteRepeatedFieldById(11, values);
  }
  get acceptedOffer() {
    return this.getFieldById(12, 0);
  }
  setAcceptedOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteAcceptedOffer() {
    return this.setValueFieldById(12, null, null);
  }
  get offerAcceptedTime() {
    return this.getFieldById(20, DEPRECATED_Date.getDefault());
  }
  setOfferAcceptedTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, null);
  }
  deleteOfferAcceptedTime() {
    return this.setValueFieldById(20, null, null);
  }
  get offerUnderwritingRequested() {
    return this.getFieldById(23, 0);
  }
  setOfferUnderwritingRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(23, value, null);
  }
  deleteOfferUnderwritingRequested() {
    return this.setValueFieldById(23, null, null);
  }
  get leadingOffer() {
    return this.getFieldById(16, 0);
  }
  setLeadingOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, null);
  }
  deleteLeadingOffer() {
    return this.setValueFieldById(16, null, null);
  }
  get creationTime() {
    return this.getFieldById(17, DEPRECATED_Date.getDefault());
  }
  setCreationTime(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, null);
  }
  deleteCreationTime() {
    return this.setValueFieldById(17, null, null);
  }
  get capitalAdvisorList() {
    return this.getRepeatedFieldById(18);
  }
  hasCapitalAdvisor(value) {
    return this.repeatedFieldByIdHas(18, value);
  }
  addCapitalAdvisor(value) {
    return this.addToRepeatedFieldById(18, value);
  }
  addEachCapitalAdvisor(values) {
    let T = this;
    for (const value of values) {
      T = T.addCapitalAdvisor(value);
    }
    return T;
  }
  setCapitalAdvisor(index, value) {
    return this.setRepeatedFieldById(18, index, value);
  }
  removeCapitalAdvisor(index) {
    return this.removeFromRepeatedFieldById(18, index);
  }
  clearCapitalAdvisorList() {
    return this.clearRepeatedFieldById(18);
  }
  setCapitalAdvisorList(values) {
    return this.overwriteRepeatedFieldById(18, values);
  }
  get highlight() {
    return this.getFieldById(21, false);
  }
  setHighlight(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, null);
  }
  deleteHighlight() {
    return this.setValueFieldById(21, null, null);
  }
}
_defineProperty$C(ProjectSummary_, protoName, DEV_MODE ? 'ProjectSummary' : '[Unknown]');
let ProjectSummary = ProjectSummary_;
(function (_ProjectSummary) {})(ProjectSummary || (ProjectSummary = {}));
Object.defineProperties(ProjectSummary, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 25
      },
      "owner": {
        value: 13
      },
      "sponsor_user": {
        value: 19
      },
      "sponsorUser": {
        value: 19
      },
      "broker_user": {
        value: 24
      },
      "brokerUser": {
        value: 24
      },
      "user_has_write_permission": {
        value: 2
      },
      "userHasWritePermission": {
        value: 2
      },
      "draft": {
        value: 3
      },
      "approved": {
        value: 22
      },
      "status": {
        value: 71
      },
      "financing_purpose": {
        value: 4
      },
      "financingPurpose": {
        value: 4
      },
      "asset_type": {
        value: 5
      },
      "assetType": {
        value: 5
      },
      "name": {
        value: 6
      },
      "address": {
        value: 7
      },
      "number_of_units": {
        value: 9
      },
      "numberOfUnits": {
        value: 9
      },
      "principal_needed": {
        value: 15
      },
      "principalNeeded": {
        value: 15
      },
      "note": {
        value: 14
      },
      "tile_image": {
        value: 10
      },
      "tileImage": {
        value: 10
      },
      "offer": {
        value: 11
      },
      "accepted_offer": {
        value: 12
      },
      "acceptedOffer": {
        value: 12
      },
      "offer_accepted_time": {
        value: 20
      },
      "offerAcceptedTime": {
        value: 20
      },
      "offer_underwriting_requested": {
        value: 23
      },
      "offerUnderwritingRequested": {
        value: 23
      },
      "leading_offer": {
        value: 16
      },
      "leadingOffer": {
        value: 16
      },
      "creation_time": {
        value: 17
      },
      "creationTime": {
        value: 17
      },
      "capital_advisor": {
        value: 18
      },
      "capitalAdvisor": {
        value: 18
      },
      "highlight": {
        value: 21
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '25': {
        value: "sym"
      },
      '13': {
        value: "owner"
      },
      '19': {
        value: "sponsor_user"
      },
      '24': {
        value: "broker_user"
      },
      '2': {
        value: "user_has_write_permission"
      },
      '3': {
        value: "draft"
      },
      '22': {
        value: "approved"
      },
      '71': {
        value: "status"
      },
      '4': {
        value: "financing_purpose"
      },
      '5': {
        value: "asset_type"
      },
      '6': {
        value: "name"
      },
      '7': {
        value: "address"
      },
      '9': {
        value: "number_of_units"
      },
      '15': {
        value: "principal_needed"
      },
      '14': {
        value: "note"
      },
      '10': {
        value: "tile_image"
      },
      '11': {
        value: "offer"
      },
      '12': {
        value: "accepted_offer"
      },
      '20': {
        value: "offer_accepted_time"
      },
      '23': {
        value: "offer_underwriting_requested"
      },
      '16': {
        value: "leading_offer"
      },
      '17': {
        value: "creation_time"
      },
      '18': {
        value: "capital_advisor"
      },
      '21': {
        value: "highlight"
      }
    }))
  }
});
Object.defineProperties(ProjectSummary.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      19: Int32OrString.prototype,
      24: Int32OrString.prototype,
      7: StandardUsAddress.prototype,
      15: CurrencyValueRange.prototype,
      14: RichText.prototype,
      20: DEPRECATED_Date.prototype,
      17: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectSummary);
  Object.freeze(ProjectSummary.prototype);
}

function _defineProperty$B(obj, key, value) { key = _toPropertyKey$B(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$B(arg) { var key = _toPrimitive$B(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$B(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class PaginatedProjectIds_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      3: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message PaginatedProjectIds field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message PaginatedProjectIds field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message PaginatedProjectIds field continuation_token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PaginatedProjectIds, 'PaginatedProjectIds', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PaginatedProjectIds to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get idList() {
    return this.getRepeatedFieldById(1);
  }
  hasId(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addId(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachId(values) {
    let T = this;
    for (const value of values) {
      T = T.addId(value);
    }
    return T;
  }
  setId(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeId(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearIdList() {
    return this.clearRepeatedFieldById(1);
  }
  setIdList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get symList() {
    return this.getRepeatedFieldById(3);
  }
  hasSym(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addSym(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachSym(values) {
    let T = this;
    for (const value of values) {
      T = T.addSym(value);
    }
    return T;
  }
  setSym(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeSym(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearSymList() {
    return this.clearRepeatedFieldById(3);
  }
  setSymList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
  get continuationToken() {
    return this.getFieldById(2, "");
  }
  setContinuationToken(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteContinuationToken() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$B(PaginatedProjectIds_, protoName, DEV_MODE ? 'PaginatedProjectIds' : '[Unknown]');
let PaginatedProjectIds = PaginatedProjectIds_;
(function (_PaginatedProjectIds) {})(PaginatedProjectIds || (PaginatedProjectIds = {}));
Object.defineProperties(PaginatedProjectIds, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 3
      },
      "continuation_token": {
        value: 2
      },
      "continuationToken": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '3': {
        value: "sym"
      },
      '2': {
        value: "continuation_token"
      }
    }))
  }
});
Object.defineProperties(PaginatedProjectIds.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PaginatedProjectIds);
  Object.freeze(PaginatedProjectIds.prototype);
}

function _defineProperty$A(obj, key, value) { key = _toPropertyKey$A(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$A(arg) { var key = _toPrimitive$A(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$A(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class SearchProjectsResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: PaginatedProjectIds.shape,
      2: ProjectSummary.shape,
      3: UserProfile$2.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PaginatedProjectIds);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message SearchProjectsResponse field projects_page.
                Expected type PaginatedProjectIds.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PaginatedProjectIds', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectSummary);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message SearchProjectsResponse field project_summary.
                Expected type ProjectSummary.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectSummary', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(UserProfile$2);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message SearchProjectsResponse field user_profile.
                Expected type UserProfile.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'UserProfile', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SearchProjectsResponse, 'SearchProjectsResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SearchProjectsResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get projectsPage() {
    return this.getFieldById(1, PaginatedProjectIds.getDefault());
  }
  setProjectsPage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteProjectsPage() {
    return this.setValueFieldById(1, null, null);
  }
  get projectSummaryList() {
    return this.getRepeatedFieldById(2);
  }
  hasProjectSummary(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addProjectSummary(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachProjectSummary(values) {
    let T = this;
    for (const value of values) {
      T = T.addProjectSummary(value);
    }
    return T;
  }
  setProjectSummary(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeProjectSummary(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearProjectSummaryList() {
    return this.clearRepeatedFieldById(2);
  }
  setProjectSummaryList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
  get userProfileList() {
    return this.getRepeatedFieldById(3);
  }
  hasUserProfile(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addUserProfile(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachUserProfile(values) {
    let T = this;
    for (const value of values) {
      T = T.addUserProfile(value);
    }
    return T;
  }
  setUserProfile(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeUserProfile(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearUserProfileList() {
    return this.clearRepeatedFieldById(3);
  }
  setUserProfileList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
}
_defineProperty$A(SearchProjectsResponse_, protoName, DEV_MODE ? 'SearchProjectsResponse' : '[Unknown]');
let SearchProjectsResponse = SearchProjectsResponse_;
(function (_SearchProjectsResponse) {})(SearchProjectsResponse || (SearchProjectsResponse = {}));
Object.defineProperties(SearchProjectsResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "projects_page": {
        value: 1
      },
      "projectsPage": {
        value: 1
      },
      "project_summary": {
        value: 2
      },
      "projectSummary": {
        value: 2
      },
      "user_profile": {
        value: 3
      },
      "userProfile": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "projects_page"
      },
      '2': {
        value: "project_summary"
      },
      '3': {
        value: "user_profile"
      }
    }))
  }
});
Object.defineProperties(SearchProjectsResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: PaginatedProjectIds.prototype,
      2: ProjectSummary.prototype,
      3: UserProfile$2.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SearchProjectsResponse);
  Object.freeze(SearchProjectsResponse.prototype);
}

async function searchProjects(ctr, filters = {}, sorts = [], continuationToken = '') {
  if (sorts.length === 0) {
    // Sort by creation time by default.
    sorts = [{
      field: 'CREATION_TIME',
      direction: 'DESC'
    }];
  }
  const response = SearchProjectsResponse.fromJson(await searchProjects$1(ctr.sharedUser, ProjectFilters.from({
    search: filters.search,
    projectStatus: filters.status,
    projectStage: filters.stage,
    financingPurpose: filters.financingPurpose,
    capitalAdvisorSym: filters.capitalAdvisor
  }), sorts.map(u => ProjectSort.from({
    field: u.field,
    direction: u.direction ?? 'ASC'
  })), ProjectView.from({
    continuationToken: continuationToken,
    limit: 20
  })));
  const page = response.projectsPage;
  const syms = [...page.symList];
  const out = {
    syms
  };
  if (page.continuationToken) {
    out.next = () => searchProjects(ctr, filters, sorts, page.continuationToken);
  }
  return out;
}

function _defineProperty$z(obj, key, value) { key = _toPropertyKey$z(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$z(arg) { var key = _toPrimitive$z(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$z(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class RepeatedUint32_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message RepeatedUint32 field value.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedUint32, 'RepeatedUint32', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedUint32 to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$z(RepeatedUint32_, protoName, DEV_MODE ? 'RepeatedUint32' : '[Unknown]');
let RepeatedUint32 = RepeatedUint32_;
(function (_RepeatedUint) {})(RepeatedUint32 || (RepeatedUint32 = {}));
Object.defineProperties(RepeatedUint32, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedUint32.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedUint32);
  Object.freeze(RepeatedUint32.prototype);
}
class RepeatedString_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message RepeatedString field value.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedString, 'RepeatedString', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedString to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$z(RepeatedString_, protoName, DEV_MODE ? 'RepeatedString' : '[Unknown]');
let RepeatedString = RepeatedString_;
(function (_RepeatedString) {})(RepeatedString || (RepeatedString = {}));
Object.defineProperties(RepeatedString, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedString.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedString);
  Object.freeze(RepeatedString.prototype);
}

function _defineProperty$y(obj, key, value) { key = _toPropertyKey$y(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$y(arg) { var key = _toPrimitive$y(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$y(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _ProjectUpdate_SponsorUsersUpdate_;
(function (_ProjectUpdate_SponsorUsersUpdate_) {
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["ADD_SPONSOR_USER_ID"] = 65] = "ADD_SPONSOR_USER_ID";
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["ADD_SPONSOR_USER_SYM"] = 100] = "ADD_SPONSOR_USER_SYM";
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["INVITE_SPONSOR_USER_EMAIL"] = 66] = "INVITE_SPONSOR_USER_EMAIL";
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["REMOVE_SPONSOR_USER_ID"] = 67] = "REMOVE_SPONSOR_USER_ID";
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["REMOVE_SPONSOR_USER_SYM"] = 101] = "REMOVE_SPONSOR_USER_SYM";
  _ProjectUpdate_SponsorUsersUpdate_[_ProjectUpdate_SponsorUsersUpdate_["REMOVE_SPONSOR_USER_EMAIL"] = 68] = "REMOVE_SPONSOR_USER_EMAIL";
})(_ProjectUpdate_SponsorUsersUpdate_ || (_ProjectUpdate_SponsorUsersUpdate_ = {}));
const _ProjectUpdate_SponsorUsersUpdate = defineEnumProperties(_ProjectUpdate_SponsorUsersUpdate_, [65, 100, 66, 67, 101, 68]);
var _ProjectUpdate_BrokerUsersUpdate_;
(function (_ProjectUpdate_BrokerUsersUpdate_) {
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["ADD_BROKER_USER_ID"] = 92] = "ADD_BROKER_USER_ID";
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["ADD_BROKER_USER_SYM"] = 102] = "ADD_BROKER_USER_SYM";
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["INVITE_BROKER_USER_EMAIL"] = 93] = "INVITE_BROKER_USER_EMAIL";
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["REMOVE_BROKER_USER_ID"] = 94] = "REMOVE_BROKER_USER_ID";
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["REMOVE_BROKER_USER_SYM"] = 103] = "REMOVE_BROKER_USER_SYM";
  _ProjectUpdate_BrokerUsersUpdate_[_ProjectUpdate_BrokerUsersUpdate_["REMOVE_BROKER_USER_EMAIL"] = 95] = "REMOVE_BROKER_USER_EMAIL";
})(_ProjectUpdate_BrokerUsersUpdate_ || (_ProjectUpdate_BrokerUsersUpdate_ = {}));
const _ProjectUpdate_BrokerUsersUpdate = defineEnumProperties(_ProjectUpdate_BrokerUsersUpdate_, [92, 102, 93, 94, 103, 95]);
var _ProjectUpdate_StatusUpdate_;
(function (_ProjectUpdate_StatusUpdate_) {
  _ProjectUpdate_StatusUpdate_[_ProjectUpdate_StatusUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_StatusUpdate_[_ProjectUpdate_StatusUpdate_["STATUS"] = 70] = "STATUS";
})(_ProjectUpdate_StatusUpdate_ || (_ProjectUpdate_StatusUpdate_ = {}));
const _ProjectUpdate_StatusUpdate = defineEnumProperties(_ProjectUpdate_StatusUpdate_, [70]);
var _ProjectUpdate_LostReasonUpdate_;
(function (_ProjectUpdate_LostReasonUpdate_) {
  _ProjectUpdate_LostReasonUpdate_[_ProjectUpdate_LostReasonUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LostReasonUpdate_[_ProjectUpdate_LostReasonUpdate_["LOST_REASON"] = 71] = "LOST_REASON";
})(_ProjectUpdate_LostReasonUpdate_ || (_ProjectUpdate_LostReasonUpdate_ = {}));
const _ProjectUpdate_LostReasonUpdate = defineEnumProperties(_ProjectUpdate_LostReasonUpdate_, [71]);
var _ProjectUpdate_ApprovedUpdate_;
(function (_ProjectUpdate_ApprovedUpdate_) {
  _ProjectUpdate_ApprovedUpdate_[_ProjectUpdate_ApprovedUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ApprovedUpdate_[_ProjectUpdate_ApprovedUpdate_["APPROVED"] = 56] = "APPROVED";
})(_ProjectUpdate_ApprovedUpdate_ || (_ProjectUpdate_ApprovedUpdate_ = {}));
const _ProjectUpdate_ApprovedUpdate = defineEnumProperties(_ProjectUpdate_ApprovedUpdate_, [56]);
var _ProjectUpdate_ApprovalRequestedUpdate_;
(function (_ProjectUpdate_ApprovalRequestedUpdate_) {
  _ProjectUpdate_ApprovalRequestedUpdate_[_ProjectUpdate_ApprovalRequestedUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ApprovalRequestedUpdate_[_ProjectUpdate_ApprovalRequestedUpdate_["APPROVAL_REQUESTED"] = 96] = "APPROVAL_REQUESTED";
})(_ProjectUpdate_ApprovalRequestedUpdate_ || (_ProjectUpdate_ApprovalRequestedUpdate_ = {}));
const _ProjectUpdate_ApprovalRequestedUpdate = defineEnumProperties(_ProjectUpdate_ApprovalRequestedUpdate_, [96]);
var _ProjectUpdate_NdaRequiredUpdate_;
(function (_ProjectUpdate_NdaRequiredUpdate_) {
  _ProjectUpdate_NdaRequiredUpdate_[_ProjectUpdate_NdaRequiredUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_NdaRequiredUpdate_[_ProjectUpdate_NdaRequiredUpdate_["NDA_REQUIRED"] = 57] = "NDA_REQUIRED";
})(_ProjectUpdate_NdaRequiredUpdate_ || (_ProjectUpdate_NdaRequiredUpdate_ = {}));
const _ProjectUpdate_NdaRequiredUpdate = defineEnumProperties(_ProjectUpdate_NdaRequiredUpdate_, [57]);
var _ProjectUpdate_StacksourceAgreementSignedUpdate_;
(function (_ProjectUpdate_StacksourceAgreementSignedUpdate_) {
  _ProjectUpdate_StacksourceAgreementSignedUpdate_[_ProjectUpdate_StacksourceAgreementSignedUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_StacksourceAgreementSignedUpdate_[_ProjectUpdate_StacksourceAgreementSignedUpdate_["STACKSOURCE_AGREEMENT_SIGNED"] = 63] = "STACKSOURCE_AGREEMENT_SIGNED";
})(_ProjectUpdate_StacksourceAgreementSignedUpdate_ || (_ProjectUpdate_StacksourceAgreementSignedUpdate_ = {}));
const _ProjectUpdate_StacksourceAgreementSignedUpdate = defineEnumProperties(_ProjectUpdate_StacksourceAgreementSignedUpdate_, [63]);
var _ProjectUpdate_FinancingPurposeUpdate_;
(function (_ProjectUpdate_FinancingPurposeUpdate_) {
  _ProjectUpdate_FinancingPurposeUpdate_[_ProjectUpdate_FinancingPurposeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FinancingPurposeUpdate_[_ProjectUpdate_FinancingPurposeUpdate_["FINANCING_PURPOSE"] = 5] = "FINANCING_PURPOSE";
})(_ProjectUpdate_FinancingPurposeUpdate_ || (_ProjectUpdate_FinancingPurposeUpdate_ = {}));
const _ProjectUpdate_FinancingPurposeUpdate = defineEnumProperties(_ProjectUpdate_FinancingPurposeUpdate_, [5]);
var _ProjectUpdate_AssetTypeUpdate_;
(function (_ProjectUpdate_AssetTypeUpdate_) {
  _ProjectUpdate_AssetTypeUpdate_[_ProjectUpdate_AssetTypeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AssetTypeUpdate_[_ProjectUpdate_AssetTypeUpdate_["ASSET_TYPE"] = 6] = "ASSET_TYPE";
})(_ProjectUpdate_AssetTypeUpdate_ || (_ProjectUpdate_AssetTypeUpdate_ = {}));
const _ProjectUpdate_AssetTypeUpdate = defineEnumProperties(_ProjectUpdate_AssetTypeUpdate_, [6]);
var _ProjectUpdate_SponsorRoleUpdate_;
(function (_ProjectUpdate_SponsorRoleUpdate_) {
  _ProjectUpdate_SponsorRoleUpdate_[_ProjectUpdate_SponsorRoleUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_SponsorRoleUpdate_[_ProjectUpdate_SponsorRoleUpdate_["SPONSOR_ROLE"] = 49] = "SPONSOR_ROLE";
})(_ProjectUpdate_SponsorRoleUpdate_ || (_ProjectUpdate_SponsorRoleUpdate_ = {}));
const _ProjectUpdate_SponsorRoleUpdate = defineEnumProperties(_ProjectUpdate_SponsorRoleUpdate_, [49]);
var _ProjectUpdate_HotelFlagUpdate_;
(function (_ProjectUpdate_HotelFlagUpdate_) {
  _ProjectUpdate_HotelFlagUpdate_[_ProjectUpdate_HotelFlagUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_HotelFlagUpdate_[_ProjectUpdate_HotelFlagUpdate_["HOTEL_FLAG"] = 89] = "HOTEL_FLAG";
})(_ProjectUpdate_HotelFlagUpdate_ || (_ProjectUpdate_HotelFlagUpdate_ = {}));
const _ProjectUpdate_HotelFlagUpdate = defineEnumProperties(_ProjectUpdate_HotelFlagUpdate_, [89]);
var _ProjectUpdate_HotelAverageDailyRateUpdate_;
(function (_ProjectUpdate_HotelAverageDailyRateUpdate_) {
  _ProjectUpdate_HotelAverageDailyRateUpdate_[_ProjectUpdate_HotelAverageDailyRateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_HotelAverageDailyRateUpdate_[_ProjectUpdate_HotelAverageDailyRateUpdate_["HOTEL_AVERAGE_DAILY_RATE"] = 90] = "HOTEL_AVERAGE_DAILY_RATE";
})(_ProjectUpdate_HotelAverageDailyRateUpdate_ || (_ProjectUpdate_HotelAverageDailyRateUpdate_ = {}));
const _ProjectUpdate_HotelAverageDailyRateUpdate = defineEnumProperties(_ProjectUpdate_HotelAverageDailyRateUpdate_, [90]);
var _ProjectUpdate_HotelAverageOccupancyRateUpdate_;
(function (_ProjectUpdate_HotelAverageOccupancyRateUpdate_) {
  _ProjectUpdate_HotelAverageOccupancyRateUpdate_[_ProjectUpdate_HotelAverageOccupancyRateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_HotelAverageOccupancyRateUpdate_[_ProjectUpdate_HotelAverageOccupancyRateUpdate_["HOTEL_AVERAGE_OCCUPANCY_RATE"] = 91] = "HOTEL_AVERAGE_OCCUPANCY_RATE";
})(_ProjectUpdate_HotelAverageOccupancyRateUpdate_ || (_ProjectUpdate_HotelAverageOccupancyRateUpdate_ = {}));
const _ProjectUpdate_HotelAverageOccupancyRateUpdate = defineEnumProperties(_ProjectUpdate_HotelAverageOccupancyRateUpdate_, [91]);
var _ProjectUpdate_TenancyUpdate_;
(function (_ProjectUpdate_TenancyUpdate_) {
  _ProjectUpdate_TenancyUpdate_[_ProjectUpdate_TenancyUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_TenancyUpdate_[_ProjectUpdate_TenancyUpdate_["TENANCY"] = 61] = "TENANCY";
})(_ProjectUpdate_TenancyUpdate_ || (_ProjectUpdate_TenancyUpdate_ = {}));
const _ProjectUpdate_TenancyUpdate = defineEnumProperties(_ProjectUpdate_TenancyUpdate_, [61]);
var _ProjectUpdate_NameUpdate_;
(function (_ProjectUpdate_NameUpdate_) {
  _ProjectUpdate_NameUpdate_[_ProjectUpdate_NameUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_NameUpdate_[_ProjectUpdate_NameUpdate_["NAME"] = 7] = "NAME";
})(_ProjectUpdate_NameUpdate_ || (_ProjectUpdate_NameUpdate_ = {}));
const _ProjectUpdate_NameUpdate = defineEnumProperties(_ProjectUpdate_NameUpdate_, [7]);
var _ProjectUpdate_AddressUpdate_;
(function (_ProjectUpdate_AddressUpdate_) {
  _ProjectUpdate_AddressUpdate_[_ProjectUpdate_AddressUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AddressUpdate_[_ProjectUpdate_AddressUpdate_["ADDRESS"] = 8] = "ADDRESS";
})(_ProjectUpdate_AddressUpdate_ || (_ProjectUpdate_AddressUpdate_ = {}));
const _ProjectUpdate_AddressUpdate = defineEnumProperties(_ProjectUpdate_AddressUpdate_, [8]);
var _ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_;
(function (_ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_) {
  _ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_[_ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_[_ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_["DEPRECATED_TOTAL_PROJECT_BUDGET"] = 9] = "DEPRECATED_TOTAL_PROJECT_BUDGET";
})(_ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_ || (_ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_ = {}));
const _ProjectUpdate_DeprecatedTotalProjectBudgetUpdate = defineEnumProperties(_ProjectUpdate_DeprecatedTotalProjectBudgetUpdate_, [9]);
var _ProjectUpdate_NumberOfUnitsUpdate_;
(function (_ProjectUpdate_NumberOfUnitsUpdate_) {
  _ProjectUpdate_NumberOfUnitsUpdate_[_ProjectUpdate_NumberOfUnitsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_NumberOfUnitsUpdate_[_ProjectUpdate_NumberOfUnitsUpdate_["NUMBER_OF_UNITS"] = 10] = "NUMBER_OF_UNITS";
})(_ProjectUpdate_NumberOfUnitsUpdate_ || (_ProjectUpdate_NumberOfUnitsUpdate_ = {}));
const _ProjectUpdate_NumberOfUnitsUpdate = defineEnumProperties(_ProjectUpdate_NumberOfUnitsUpdate_, [10]);
var _ProjectUpdate_ProjectSummaryUpdate_;
(function (_ProjectUpdate_ProjectSummaryUpdate_) {
  _ProjectUpdate_ProjectSummaryUpdate_[_ProjectUpdate_ProjectSummaryUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ProjectSummaryUpdate_[_ProjectUpdate_ProjectSummaryUpdate_["PROJECT_SUMMARY"] = 11] = "PROJECT_SUMMARY";
})(_ProjectUpdate_ProjectSummaryUpdate_ || (_ProjectUpdate_ProjectSummaryUpdate_ = {}));
const _ProjectUpdate_ProjectSummaryUpdate = defineEnumProperties(_ProjectUpdate_ProjectSummaryUpdate_, [11]);
var _ProjectUpdate_TotalSizeUpdate_;
(function (_ProjectUpdate_TotalSizeUpdate_) {
  _ProjectUpdate_TotalSizeUpdate_[_ProjectUpdate_TotalSizeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_TotalSizeUpdate_[_ProjectUpdate_TotalSizeUpdate_["TOTAL_SIZE"] = 12] = "TOTAL_SIZE";
})(_ProjectUpdate_TotalSizeUpdate_ || (_ProjectUpdate_TotalSizeUpdate_ = {}));
const _ProjectUpdate_TotalSizeUpdate = defineEnumProperties(_ProjectUpdate_TotalSizeUpdate_, [12]);
var _ProjectUpdate_FootprintSizeUpdate_;
(function (_ProjectUpdate_FootprintSizeUpdate_) {
  _ProjectUpdate_FootprintSizeUpdate_[_ProjectUpdate_FootprintSizeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FootprintSizeUpdate_[_ProjectUpdate_FootprintSizeUpdate_["FOOTPRINT_SIZE"] = 13] = "FOOTPRINT_SIZE";
})(_ProjectUpdate_FootprintSizeUpdate_ || (_ProjectUpdate_FootprintSizeUpdate_ = {}));
const _ProjectUpdate_FootprintSizeUpdate = defineEnumProperties(_ProjectUpdate_FootprintSizeUpdate_, [13]);
var _ProjectUpdate_LotSizeUpdate_;
(function (_ProjectUpdate_LotSizeUpdate_) {
  _ProjectUpdate_LotSizeUpdate_[_ProjectUpdate_LotSizeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LotSizeUpdate_[_ProjectUpdate_LotSizeUpdate_["LOT_SIZE"] = 14] = "LOT_SIZE";
})(_ProjectUpdate_LotSizeUpdate_ || (_ProjectUpdate_LotSizeUpdate_ = {}));
const _ProjectUpdate_LotSizeUpdate = defineEnumProperties(_ProjectUpdate_LotSizeUpdate_, [14]);
var _ProjectUpdate_ArchitectUpdate_;
(function (_ProjectUpdate_ArchitectUpdate_) {
  _ProjectUpdate_ArchitectUpdate_[_ProjectUpdate_ArchitectUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ArchitectUpdate_[_ProjectUpdate_ArchitectUpdate_["ARCHITECT"] = 15] = "ARCHITECT";
})(_ProjectUpdate_ArchitectUpdate_ || (_ProjectUpdate_ArchitectUpdate_ = {}));
const _ProjectUpdate_ArchitectUpdate = defineEnumProperties(_ProjectUpdate_ArchitectUpdate_, [15]);
var _ProjectUpdate_StructuralEngineerUpdate_;
(function (_ProjectUpdate_StructuralEngineerUpdate_) {
  _ProjectUpdate_StructuralEngineerUpdate_[_ProjectUpdate_StructuralEngineerUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_StructuralEngineerUpdate_[_ProjectUpdate_StructuralEngineerUpdate_["STRUCTURAL_ENGINEER"] = 42] = "STRUCTURAL_ENGINEER";
})(_ProjectUpdate_StructuralEngineerUpdate_ || (_ProjectUpdate_StructuralEngineerUpdate_ = {}));
const _ProjectUpdate_StructuralEngineerUpdate = defineEnumProperties(_ProjectUpdate_StructuralEngineerUpdate_, [42]);
var _ProjectUpdate_ConstructionManagerUpdate_;
(function (_ProjectUpdate_ConstructionManagerUpdate_) {
  _ProjectUpdate_ConstructionManagerUpdate_[_ProjectUpdate_ConstructionManagerUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ConstructionManagerUpdate_[_ProjectUpdate_ConstructionManagerUpdate_["CONSTRUCTION_MANAGER"] = 43] = "CONSTRUCTION_MANAGER";
})(_ProjectUpdate_ConstructionManagerUpdate_ || (_ProjectUpdate_ConstructionManagerUpdate_ = {}));
const _ProjectUpdate_ConstructionManagerUpdate = defineEnumProperties(_ProjectUpdate_ConstructionManagerUpdate_, [43]);
var _ProjectUpdate_GeneralContractorUpdate_;
(function (_ProjectUpdate_GeneralContractorUpdate_) {
  _ProjectUpdate_GeneralContractorUpdate_[_ProjectUpdate_GeneralContractorUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_GeneralContractorUpdate_[_ProjectUpdate_GeneralContractorUpdate_["GENERAL_CONTRACTOR"] = 37] = "GENERAL_CONTRACTOR";
})(_ProjectUpdate_GeneralContractorUpdate_ || (_ProjectUpdate_GeneralContractorUpdate_ = {}));
const _ProjectUpdate_GeneralContractorUpdate = defineEnumProperties(_ProjectUpdate_GeneralContractorUpdate_, [37]);
var _ProjectUpdate_LeasingRepresentativeUpdate_;
(function (_ProjectUpdate_LeasingRepresentativeUpdate_) {
  _ProjectUpdate_LeasingRepresentativeUpdate_[_ProjectUpdate_LeasingRepresentativeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LeasingRepresentativeUpdate_[_ProjectUpdate_LeasingRepresentativeUpdate_["LEASING_REPRESENTATIVE"] = 44] = "LEASING_REPRESENTATIVE";
})(_ProjectUpdate_LeasingRepresentativeUpdate_ || (_ProjectUpdate_LeasingRepresentativeUpdate_ = {}));
const _ProjectUpdate_LeasingRepresentativeUpdate = defineEnumProperties(_ProjectUpdate_LeasingRepresentativeUpdate_, [44]);
var _ProjectUpdate_PropertyManagerUpdate_;
(function (_ProjectUpdate_PropertyManagerUpdate_) {
  _ProjectUpdate_PropertyManagerUpdate_[_ProjectUpdate_PropertyManagerUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_PropertyManagerUpdate_[_ProjectUpdate_PropertyManagerUpdate_["PROPERTY_MANAGER"] = 45] = "PROPERTY_MANAGER";
})(_ProjectUpdate_PropertyManagerUpdate_ || (_ProjectUpdate_PropertyManagerUpdate_ = {}));
const _ProjectUpdate_PropertyManagerUpdate = defineEnumProperties(_ProjectUpdate_PropertyManagerUpdate_, [45]);
var _ProjectUpdate_CompletedYearUpdate_;
(function (_ProjectUpdate_CompletedYearUpdate_) {
  _ProjectUpdate_CompletedYearUpdate_[_ProjectUpdate_CompletedYearUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_CompletedYearUpdate_[_ProjectUpdate_CompletedYearUpdate_["COMPLETED_YEAR"] = 16] = "COMPLETED_YEAR";
})(_ProjectUpdate_CompletedYearUpdate_ || (_ProjectUpdate_CompletedYearUpdate_ = {}));
const _ProjectUpdate_CompletedYearUpdate = defineEnumProperties(_ProjectUpdate_CompletedYearUpdate_, [16]);
var _ProjectUpdate_RenovatedYearUpdate_;
(function (_ProjectUpdate_RenovatedYearUpdate_) {
  _ProjectUpdate_RenovatedYearUpdate_[_ProjectUpdate_RenovatedYearUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_RenovatedYearUpdate_[_ProjectUpdate_RenovatedYearUpdate_["RENOVATED_YEAR"] = 51] = "RENOVATED_YEAR";
})(_ProjectUpdate_RenovatedYearUpdate_ || (_ProjectUpdate_RenovatedYearUpdate_ = {}));
const _ProjectUpdate_RenovatedYearUpdate = defineEnumProperties(_ProjectUpdate_RenovatedYearUpdate_, [51]);
var _ProjectUpdate_FloorsAboveGroundUpdate_;
(function (_ProjectUpdate_FloorsAboveGroundUpdate_) {
  _ProjectUpdate_FloorsAboveGroundUpdate_[_ProjectUpdate_FloorsAboveGroundUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FloorsAboveGroundUpdate_[_ProjectUpdate_FloorsAboveGroundUpdate_["FLOORS_ABOVE_GROUND"] = 17] = "FLOORS_ABOVE_GROUND";
})(_ProjectUpdate_FloorsAboveGroundUpdate_ || (_ProjectUpdate_FloorsAboveGroundUpdate_ = {}));
const _ProjectUpdate_FloorsAboveGroundUpdate = defineEnumProperties(_ProjectUpdate_FloorsAboveGroundUpdate_, [17]);
var _ProjectUpdate_FloorsBelowGroundUpdate_;
(function (_ProjectUpdate_FloorsBelowGroundUpdate_) {
  _ProjectUpdate_FloorsBelowGroundUpdate_[_ProjectUpdate_FloorsBelowGroundUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FloorsBelowGroundUpdate_[_ProjectUpdate_FloorsBelowGroundUpdate_["FLOORS_BELOW_GROUND"] = 18] = "FLOORS_BELOW_GROUND";
})(_ProjectUpdate_FloorsBelowGroundUpdate_ || (_ProjectUpdate_FloorsBelowGroundUpdate_ = {}));
const _ProjectUpdate_FloorsBelowGroundUpdate = defineEnumProperties(_ProjectUpdate_FloorsBelowGroundUpdate_, [18]);
var _ProjectUpdate_ParkingSpotsUpdate_;
(function (_ProjectUpdate_ParkingSpotsUpdate_) {
  _ProjectUpdate_ParkingSpotsUpdate_[_ProjectUpdate_ParkingSpotsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ParkingSpotsUpdate_[_ProjectUpdate_ParkingSpotsUpdate_["PARKING_SPOTS"] = 98] = "PARKING_SPOTS";
})(_ProjectUpdate_ParkingSpotsUpdate_ || (_ProjectUpdate_ParkingSpotsUpdate_ = {}));
const _ProjectUpdate_ParkingSpotsUpdate = defineEnumProperties(_ProjectUpdate_ParkingSpotsUpdate_, [98]);
var _ProjectUpdate_DesignInfoUpdate_;
(function (_ProjectUpdate_DesignInfoUpdate_) {
  _ProjectUpdate_DesignInfoUpdate_[_ProjectUpdate_DesignInfoUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_DesignInfoUpdate_[_ProjectUpdate_DesignInfoUpdate_["DESIGN_INFO"] = 19] = "DESIGN_INFO";
})(_ProjectUpdate_DesignInfoUpdate_ || (_ProjectUpdate_DesignInfoUpdate_ = {}));
const _ProjectUpdate_DesignInfoUpdate = defineEnumProperties(_ProjectUpdate_DesignInfoUpdate_, [19]);
var _ProjectUpdate_PropertyTitleUpdate_;
(function (_ProjectUpdate_PropertyTitleUpdate_) {
  _ProjectUpdate_PropertyTitleUpdate_[_ProjectUpdate_PropertyTitleUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_PropertyTitleUpdate_[_ProjectUpdate_PropertyTitleUpdate_["PROPERTY_TITLE"] = 20] = "PROPERTY_TITLE";
})(_ProjectUpdate_PropertyTitleUpdate_ || (_ProjectUpdate_PropertyTitleUpdate_ = {}));
const _ProjectUpdate_PropertyTitleUpdate = defineEnumProperties(_ProjectUpdate_PropertyTitleUpdate_, [20]);
var _ProjectUpdate_AssetNotesUpdate_;
(function (_ProjectUpdate_AssetNotesUpdate_) {
  _ProjectUpdate_AssetNotesUpdate_[_ProjectUpdate_AssetNotesUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AssetNotesUpdate_[_ProjectUpdate_AssetNotesUpdate_["ASSET_NOTES"] = 21] = "ASSET_NOTES";
})(_ProjectUpdate_AssetNotesUpdate_ || (_ProjectUpdate_AssetNotesUpdate_ = {}));
const _ProjectUpdate_AssetNotesUpdate = defineEnumProperties(_ProjectUpdate_AssetNotesUpdate_, [21]);
var _ProjectUpdate_LatestAppraisalValueUpdate_;
(function (_ProjectUpdate_LatestAppraisalValueUpdate_) {
  _ProjectUpdate_LatestAppraisalValueUpdate_[_ProjectUpdate_LatestAppraisalValueUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LatestAppraisalValueUpdate_[_ProjectUpdate_LatestAppraisalValueUpdate_["LATEST_APPRAISAL_VALUE"] = 22] = "LATEST_APPRAISAL_VALUE";
})(_ProjectUpdate_LatestAppraisalValueUpdate_ || (_ProjectUpdate_LatestAppraisalValueUpdate_ = {}));
const _ProjectUpdate_LatestAppraisalValueUpdate = defineEnumProperties(_ProjectUpdate_LatestAppraisalValueUpdate_, [22]);
var _ProjectUpdate_LatestAppraisalDateUpdate_;
(function (_ProjectUpdate_LatestAppraisalDateUpdate_) {
  _ProjectUpdate_LatestAppraisalDateUpdate_[_ProjectUpdate_LatestAppraisalDateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LatestAppraisalDateUpdate_[_ProjectUpdate_LatestAppraisalDateUpdate_["LATEST_APPRAISAL_DATE"] = 38] = "LATEST_APPRAISAL_DATE";
})(_ProjectUpdate_LatestAppraisalDateUpdate_ || (_ProjectUpdate_LatestAppraisalDateUpdate_ = {}));
const _ProjectUpdate_LatestAppraisalDateUpdate = defineEnumProperties(_ProjectUpdate_LatestAppraisalDateUpdate_, [38]);
var _ProjectUpdate_TargetCloseDateUpdate_;
(function (_ProjectUpdate_TargetCloseDateUpdate_) {
  _ProjectUpdate_TargetCloseDateUpdate_[_ProjectUpdate_TargetCloseDateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_TargetCloseDateUpdate_[_ProjectUpdate_TargetCloseDateUpdate_["TARGET_CLOSE_DATE"] = 97] = "TARGET_CLOSE_DATE";
})(_ProjectUpdate_TargetCloseDateUpdate_ || (_ProjectUpdate_TargetCloseDateUpdate_ = {}));
const _ProjectUpdate_TargetCloseDateUpdate = defineEnumProperties(_ProjectUpdate_TargetCloseDateUpdate_, [97]);
var _ProjectUpdate_InvestmentCategoryUpdate_;
(function (_ProjectUpdate_InvestmentCategoryUpdate_) {
  _ProjectUpdate_InvestmentCategoryUpdate_[_ProjectUpdate_InvestmentCategoryUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_InvestmentCategoryUpdate_[_ProjectUpdate_InvestmentCategoryUpdate_["INVESTMENT_CATEGORY"] = 76] = "INVESTMENT_CATEGORY";
})(_ProjectUpdate_InvestmentCategoryUpdate_ || (_ProjectUpdate_InvestmentCategoryUpdate_ = {}));
const _ProjectUpdate_InvestmentCategoryUpdate = defineEnumProperties(_ProjectUpdate_InvestmentCategoryUpdate_, [76]);
var _ProjectUpdate_CurrentNetOperatingIncomeUpdate_;
(function (_ProjectUpdate_CurrentNetOperatingIncomeUpdate_) {
  _ProjectUpdate_CurrentNetOperatingIncomeUpdate_[_ProjectUpdate_CurrentNetOperatingIncomeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_CurrentNetOperatingIncomeUpdate_[_ProjectUpdate_CurrentNetOperatingIncomeUpdate_["CURRENT_NET_OPERATING_INCOME"] = 39] = "CURRENT_NET_OPERATING_INCOME";
})(_ProjectUpdate_CurrentNetOperatingIncomeUpdate_ || (_ProjectUpdate_CurrentNetOperatingIncomeUpdate_ = {}));
const _ProjectUpdate_CurrentNetOperatingIncomeUpdate = defineEnumProperties(_ProjectUpdate_CurrentNetOperatingIncomeUpdate_, [39]);
var _ProjectUpdate_ProFormaNetOperatingIncomeUpdate_;
(function (_ProjectUpdate_ProFormaNetOperatingIncomeUpdate_) {
  _ProjectUpdate_ProFormaNetOperatingIncomeUpdate_[_ProjectUpdate_ProFormaNetOperatingIncomeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ProFormaNetOperatingIncomeUpdate_[_ProjectUpdate_ProFormaNetOperatingIncomeUpdate_["PRO_FORMA_NET_OPERATING_INCOME"] = 75] = "PRO_FORMA_NET_OPERATING_INCOME";
})(_ProjectUpdate_ProFormaNetOperatingIncomeUpdate_ || (_ProjectUpdate_ProFormaNetOperatingIncomeUpdate_ = {}));
const _ProjectUpdate_ProFormaNetOperatingIncomeUpdate = defineEnumProperties(_ProjectUpdate_ProFormaNetOperatingIncomeUpdate_, [75]);
var _ProjectUpdate_CapRateUpdate_;
(function (_ProjectUpdate_CapRateUpdate_) {
  _ProjectUpdate_CapRateUpdate_[_ProjectUpdate_CapRateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_CapRateUpdate_[_ProjectUpdate_CapRateUpdate_["CAP_RATE"] = 40] = "CAP_RATE";
})(_ProjectUpdate_CapRateUpdate_ || (_ProjectUpdate_CapRateUpdate_ = {}));
const _ProjectUpdate_CapRateUpdate = defineEnumProperties(_ProjectUpdate_CapRateUpdate_, [40]);
var _ProjectUpdate_ProFormaCapRateUpdate_;
(function (_ProjectUpdate_ProFormaCapRateUpdate_) {
  _ProjectUpdate_ProFormaCapRateUpdate_[_ProjectUpdate_ProFormaCapRateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ProFormaCapRateUpdate_[_ProjectUpdate_ProFormaCapRateUpdate_["PRO_FORMA_CAP_RATE"] = 74] = "PRO_FORMA_CAP_RATE";
})(_ProjectUpdate_ProFormaCapRateUpdate_ || (_ProjectUpdate_ProFormaCapRateUpdate_ = {}));
const _ProjectUpdate_ProFormaCapRateUpdate = defineEnumProperties(_ProjectUpdate_ProFormaCapRateUpdate_, [74]);
var _ProjectUpdate_FinancialNotesUpdate_;
(function (_ProjectUpdate_FinancialNotesUpdate_) {
  _ProjectUpdate_FinancialNotesUpdate_[_ProjectUpdate_FinancialNotesUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FinancialNotesUpdate_[_ProjectUpdate_FinancialNotesUpdate_["FINANCIAL_NOTES"] = 23] = "FINANCIAL_NOTES";
})(_ProjectUpdate_FinancialNotesUpdate_ || (_ProjectUpdate_FinancialNotesUpdate_ = {}));
const _ProjectUpdate_FinancialNotesUpdate = defineEnumProperties(_ProjectUpdate_FinancialNotesUpdate_, [23]);
var _ProjectUpdate_AcquisitionPriceUpdate_;
(function (_ProjectUpdate_AcquisitionPriceUpdate_) {
  _ProjectUpdate_AcquisitionPriceUpdate_[_ProjectUpdate_AcquisitionPriceUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AcquisitionPriceUpdate_[_ProjectUpdate_AcquisitionPriceUpdate_["ACQUISITION_PRICE"] = 24] = "ACQUISITION_PRICE";
})(_ProjectUpdate_AcquisitionPriceUpdate_ || (_ProjectUpdate_AcquisitionPriceUpdate_ = {}));
const _ProjectUpdate_AcquisitionPriceUpdate = defineEnumProperties(_ProjectUpdate_AcquisitionPriceUpdate_, [24]);
var _ProjectUpdate_AcquisitionDateUpdate_;
(function (_ProjectUpdate_AcquisitionDateUpdate_) {
  _ProjectUpdate_AcquisitionDateUpdate_[_ProjectUpdate_AcquisitionDateUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AcquisitionDateUpdate_[_ProjectUpdate_AcquisitionDateUpdate_["ACQUISITION_DATE"] = 25] = "ACQUISITION_DATE";
})(_ProjectUpdate_AcquisitionDateUpdate_ || (_ProjectUpdate_AcquisitionDateUpdate_ = {}));
const _ProjectUpdate_AcquisitionDateUpdate = defineEnumProperties(_ProjectUpdate_AcquisitionDateUpdate_, [25]);
var _ProjectUpdate_AcquisitionStatusUpdate_;
(function (_ProjectUpdate_AcquisitionStatusUpdate_) {
  _ProjectUpdate_AcquisitionStatusUpdate_[_ProjectUpdate_AcquisitionStatusUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AcquisitionStatusUpdate_[_ProjectUpdate_AcquisitionStatusUpdate_["ACQUISITION_STATUS"] = 48] = "ACQUISITION_STATUS";
})(_ProjectUpdate_AcquisitionStatusUpdate_ || (_ProjectUpdate_AcquisitionStatusUpdate_ = {}));
const _ProjectUpdate_AcquisitionStatusUpdate = defineEnumProperties(_ProjectUpdate_AcquisitionStatusUpdate_, [48]);
var _ProjectUpdate_PrincipalNeededUpdate_;
(function (_ProjectUpdate_PrincipalNeededUpdate_) {
  _ProjectUpdate_PrincipalNeededUpdate_[_ProjectUpdate_PrincipalNeededUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_PrincipalNeededUpdate_[_ProjectUpdate_PrincipalNeededUpdate_["PRINCIPAL_NEEDED"] = 26] = "PRINCIPAL_NEEDED";
})(_ProjectUpdate_PrincipalNeededUpdate_ || (_ProjectUpdate_PrincipalNeededUpdate_ = {}));
const _ProjectUpdate_PrincipalNeededUpdate = defineEnumProperties(_ProjectUpdate_PrincipalNeededUpdate_, [26]);
var _ProjectUpdate_LienPositionUpdate_;
(function (_ProjectUpdate_LienPositionUpdate_) {
  _ProjectUpdate_LienPositionUpdate_[_ProjectUpdate_LienPositionUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LienPositionUpdate_[_ProjectUpdate_LienPositionUpdate_["LIEN_POSITION"] = 27] = "LIEN_POSITION";
})(_ProjectUpdate_LienPositionUpdate_ || (_ProjectUpdate_LienPositionUpdate_ = {}));
const _ProjectUpdate_LienPositionUpdate = defineEnumProperties(_ProjectUpdate_LienPositionUpdate_, [27]);
var _ProjectUpdate_FinancialInstrumentsUpdate_;
(function (_ProjectUpdate_FinancialInstrumentsUpdate_) {
  _ProjectUpdate_FinancialInstrumentsUpdate_[_ProjectUpdate_FinancialInstrumentsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FinancialInstrumentsUpdate_[_ProjectUpdate_FinancialInstrumentsUpdate_["FINANCIAL_INSTRUMENTS"] = 28] = "FINANCIAL_INSTRUMENTS";
})(_ProjectUpdate_FinancialInstrumentsUpdate_ || (_ProjectUpdate_FinancialInstrumentsUpdate_ = {}));
const _ProjectUpdate_FinancialInstrumentsUpdate = defineEnumProperties(_ProjectUpdate_FinancialInstrumentsUpdate_, [28]);
var _ProjectUpdate_AffordabilityUpdate_;
(function (_ProjectUpdate_AffordabilityUpdate_) {
  _ProjectUpdate_AffordabilityUpdate_[_ProjectUpdate_AffordabilityUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AffordabilityUpdate_[_ProjectUpdate_AffordabilityUpdate_["AFFORDABILITY"] = 77] = "AFFORDABILITY";
})(_ProjectUpdate_AffordabilityUpdate_ || (_ProjectUpdate_AffordabilityUpdate_ = {}));
const _ProjectUpdate_AffordabilityUpdate = defineEnumProperties(_ProjectUpdate_AffordabilityUpdate_, [77]);
var _ProjectUpdate_HasElevatorUpdate_;
(function (_ProjectUpdate_HasElevatorUpdate_) {
  _ProjectUpdate_HasElevatorUpdate_[_ProjectUpdate_HasElevatorUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_HasElevatorUpdate_[_ProjectUpdate_HasElevatorUpdate_["HAS_ELEVATOR"] = 78] = "HAS_ELEVATOR";
})(_ProjectUpdate_HasElevatorUpdate_ || (_ProjectUpdate_HasElevatorUpdate_ = {}));
const _ProjectUpdate_HasElevatorUpdate = defineEnumProperties(_ProjectUpdate_HasElevatorUpdate_, [78]);
var _ProjectUpdate_NumberOfFloorsUpdate_;
(function (_ProjectUpdate_NumberOfFloorsUpdate_) {
  _ProjectUpdate_NumberOfFloorsUpdate_[_ProjectUpdate_NumberOfFloorsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_NumberOfFloorsUpdate_[_ProjectUpdate_NumberOfFloorsUpdate_["NUMBER_OF_FLOORS"] = 79] = "NUMBER_OF_FLOORS";
})(_ProjectUpdate_NumberOfFloorsUpdate_ || (_ProjectUpdate_NumberOfFloorsUpdate_ = {}));
const _ProjectUpdate_NumberOfFloorsUpdate = defineEnumProperties(_ProjectUpdate_NumberOfFloorsUpdate_, [79]);
var _ProjectUpdate_UnitInformationUpdate_;
(function (_ProjectUpdate_UnitInformationUpdate_) {
  _ProjectUpdate_UnitInformationUpdate_[_ProjectUpdate_UnitInformationUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_UnitInformationUpdate_[_ProjectUpdate_UnitInformationUpdate_["UNIT_INFORMATION"] = 80] = "UNIT_INFORMATION";
})(_ProjectUpdate_UnitInformationUpdate_ || (_ProjectUpdate_UnitInformationUpdate_ = {}));
const _ProjectUpdate_UnitInformationUpdate = defineEnumProperties(_ProjectUpdate_UnitInformationUpdate_, [80]);
var _ProjectUpdate_LihtcRatioUpdate_;
(function (_ProjectUpdate_LihtcRatioUpdate_) {
  _ProjectUpdate_LihtcRatioUpdate_[_ProjectUpdate_LihtcRatioUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LihtcRatioUpdate_[_ProjectUpdate_LihtcRatioUpdate_["LIHTC_RATIO"] = 81] = "LIHTC_RATIO";
})(_ProjectUpdate_LihtcRatioUpdate_ || (_ProjectUpdate_LihtcRatioUpdate_ = {}));
const _ProjectUpdate_LihtcRatioUpdate = defineEnumProperties(_ProjectUpdate_LihtcRatioUpdate_, [81]);
var _ProjectUpdate_HudSelectedLtvUpdate_;
(function (_ProjectUpdate_HudSelectedLtvUpdate_) {
  _ProjectUpdate_HudSelectedLtvUpdate_[_ProjectUpdate_HudSelectedLtvUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_HudSelectedLtvUpdate_[_ProjectUpdate_HudSelectedLtvUpdate_["HUD_SELECTED_LTV"] = 82] = "HUD_SELECTED_LTV";
})(_ProjectUpdate_HudSelectedLtvUpdate_ || (_ProjectUpdate_HudSelectedLtvUpdate_ = {}));
const _ProjectUpdate_HudSelectedLtvUpdate = defineEnumProperties(_ProjectUpdate_HudSelectedLtvUpdate_, [82]);
var _ProjectUpdate_FreddieSblSelectedLtvUpdate_;
(function (_ProjectUpdate_FreddieSblSelectedLtvUpdate_) {
  _ProjectUpdate_FreddieSblSelectedLtvUpdate_[_ProjectUpdate_FreddieSblSelectedLtvUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FreddieSblSelectedLtvUpdate_[_ProjectUpdate_FreddieSblSelectedLtvUpdate_["FREDDIE_SBL_SELECTED_LTV"] = 83] = "FREDDIE_SBL_SELECTED_LTV";
})(_ProjectUpdate_FreddieSblSelectedLtvUpdate_ || (_ProjectUpdate_FreddieSblSelectedLtvUpdate_ = {}));
const _ProjectUpdate_FreddieSblSelectedLtvUpdate = defineEnumProperties(_ProjectUpdate_FreddieSblSelectedLtvUpdate_, [83]);
var _ProjectUpdate_FreddieSblSelectedDscrUpdate_;
(function (_ProjectUpdate_FreddieSblSelectedDscrUpdate_) {
  _ProjectUpdate_FreddieSblSelectedDscrUpdate_[_ProjectUpdate_FreddieSblSelectedDscrUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FreddieSblSelectedDscrUpdate_[_ProjectUpdate_FreddieSblSelectedDscrUpdate_["FREDDIE_SBL_SELECTED_DSCR"] = 84] = "FREDDIE_SBL_SELECTED_DSCR";
})(_ProjectUpdate_FreddieSblSelectedDscrUpdate_ || (_ProjectUpdate_FreddieSblSelectedDscrUpdate_ = {}));
const _ProjectUpdate_FreddieSblSelectedDscrUpdate = defineEnumProperties(_ProjectUpdate_FreddieSblSelectedDscrUpdate_, [84]);
var _ProjectUpdate_FreddieSblInterestOnlyTermUpdate_;
(function (_ProjectUpdate_FreddieSblInterestOnlyTermUpdate_) {
  _ProjectUpdate_FreddieSblInterestOnlyTermUpdate_[_ProjectUpdate_FreddieSblInterestOnlyTermUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FreddieSblInterestOnlyTermUpdate_[_ProjectUpdate_FreddieSblInterestOnlyTermUpdate_["FREDDIE_SBL_INTEREST_ONLY_TERM"] = 85] = "FREDDIE_SBL_INTEREST_ONLY_TERM";
})(_ProjectUpdate_FreddieSblInterestOnlyTermUpdate_ || (_ProjectUpdate_FreddieSblInterestOnlyTermUpdate_ = {}));
const _ProjectUpdate_FreddieSblInterestOnlyTermUpdate = defineEnumProperties(_ProjectUpdate_FreddieSblInterestOnlyTermUpdate_, [85]);
var _ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_;
(function (_ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_) {
  _ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_[_ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_[_ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_["FREDDIE_SBL_PREPAYMENT_PENALTY"] = 86] = "FREDDIE_SBL_PREPAYMENT_PENALTY";
})(_ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_ || (_ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_ = {}));
const _ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate = defineEnumProperties(_ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate_, [86]);
var _ProjectUpdate_OfferExpectationsUpdate_;
(function (_ProjectUpdate_OfferExpectationsUpdate_) {
  _ProjectUpdate_OfferExpectationsUpdate_[_ProjectUpdate_OfferExpectationsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_OfferExpectationsUpdate_[_ProjectUpdate_OfferExpectationsUpdate_["OFFER_EXPECTATIONS"] = 46] = "OFFER_EXPECTATIONS";
})(_ProjectUpdate_OfferExpectationsUpdate_ || (_ProjectUpdate_OfferExpectationsUpdate_ = {}));
const _ProjectUpdate_OfferExpectationsUpdate = defineEnumProperties(_ProjectUpdate_OfferExpectationsUpdate_, [46]);
var _ProjectUpdate_CapStackItemsUpdate_;
(function (_ProjectUpdate_CapStackItemsUpdate_) {
  _ProjectUpdate_CapStackItemsUpdate_[_ProjectUpdate_CapStackItemsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_CapStackItemsUpdate_[_ProjectUpdate_CapStackItemsUpdate_["CAP_STACK_ITEMS"] = 30] = "CAP_STACK_ITEMS";
})(_ProjectUpdate_CapStackItemsUpdate_ || (_ProjectUpdate_CapStackItemsUpdate_ = {}));
const _ProjectUpdate_CapStackItemsUpdate = defineEnumProperties(_ProjectUpdate_CapStackItemsUpdate_, [30]);
var _ProjectUpdate_CapStackTargetsUpdate_;
(function (_ProjectUpdate_CapStackTargetsUpdate_) {
  _ProjectUpdate_CapStackTargetsUpdate_[_ProjectUpdate_CapStackTargetsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_CapStackTargetsUpdate_[_ProjectUpdate_CapStackTargetsUpdate_["CAP_STACK_TARGETS"] = 73] = "CAP_STACK_TARGETS";
})(_ProjectUpdate_CapStackTargetsUpdate_ || (_ProjectUpdate_CapStackTargetsUpdate_ = {}));
const _ProjectUpdate_CapStackTargetsUpdate = defineEnumProperties(_ProjectUpdate_CapStackTargetsUpdate_, [73]);
var _ProjectUpdate_GuarantorsUpdate_;
(function (_ProjectUpdate_GuarantorsUpdate_) {
  _ProjectUpdate_GuarantorsUpdate_[_ProjectUpdate_GuarantorsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_GuarantorsUpdate_[_ProjectUpdate_GuarantorsUpdate_["GUARANTORS"] = 72] = "GUARANTORS";
})(_ProjectUpdate_GuarantorsUpdate_ || (_ProjectUpdate_GuarantorsUpdate_ = {}));
const _ProjectUpdate_GuarantorsUpdate = defineEnumProperties(_ProjectUpdate_GuarantorsUpdate_, [72]);
var _ProjectUpdate_SolicitedLendersUpdate_;
(function (_ProjectUpdate_SolicitedLendersUpdate_) {
  _ProjectUpdate_SolicitedLendersUpdate_[_ProjectUpdate_SolicitedLendersUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_SolicitedLendersUpdate_[_ProjectUpdate_SolicitedLendersUpdate_["SOLICITED_LENDERS"] = 88] = "SOLICITED_LENDERS";
})(_ProjectUpdate_SolicitedLendersUpdate_ || (_ProjectUpdate_SolicitedLendersUpdate_ = {}));
const _ProjectUpdate_SolicitedLendersUpdate = defineEnumProperties(_ProjectUpdate_SolicitedLendersUpdate_, [88]);
var _ProjectUpdate_SponsorProfileUpdate_;
(function (_ProjectUpdate_SponsorProfileUpdate_) {
  _ProjectUpdate_SponsorProfileUpdate_[_ProjectUpdate_SponsorProfileUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_SponsorProfileUpdate_[_ProjectUpdate_SponsorProfileUpdate_["SPONSOR_PROFILE"] = 33] = "SPONSOR_PROFILE";
})(_ProjectUpdate_SponsorProfileUpdate_ || (_ProjectUpdate_SponsorProfileUpdate_ = {}));
const _ProjectUpdate_SponsorProfileUpdate = defineEnumProperties(_ProjectUpdate_SponsorProfileUpdate_, [33]);
var _ProjectUpdate_NoteUpdate_;
(function (_ProjectUpdate_NoteUpdate_) {
  _ProjectUpdate_NoteUpdate_[_ProjectUpdate_NoteUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_NoteUpdate_[_ProjectUpdate_NoteUpdate_["NOTE"] = 41] = "NOTE";
})(_ProjectUpdate_NoteUpdate_ || (_ProjectUpdate_NoteUpdate_ = {}));
const _ProjectUpdate_NoteUpdate = defineEnumProperties(_ProjectUpdate_NoteUpdate_, [41]);
var _ProjectUpdate_AcceptedOfferUpdate_;
(function (_ProjectUpdate_AcceptedOfferUpdate_) {
  _ProjectUpdate_AcceptedOfferUpdate_[_ProjectUpdate_AcceptedOfferUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_AcceptedOfferUpdate_[_ProjectUpdate_AcceptedOfferUpdate_["ACCEPTED_OFFER"] = 36] = "ACCEPTED_OFFER";
})(_ProjectUpdate_AcceptedOfferUpdate_ || (_ProjectUpdate_AcceptedOfferUpdate_ = {}));
const _ProjectUpdate_AcceptedOfferUpdate = defineEnumProperties(_ProjectUpdate_AcceptedOfferUpdate_, [36]);
var _ProjectUpdate_OfferUnderwritingRequestedUpdate_;
(function (_ProjectUpdate_OfferUnderwritingRequestedUpdate_) {
  _ProjectUpdate_OfferUnderwritingRequestedUpdate_[_ProjectUpdate_OfferUnderwritingRequestedUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_OfferUnderwritingRequestedUpdate_[_ProjectUpdate_OfferUnderwritingRequestedUpdate_["OFFER_UNDERWRITING_REQUESTED"] = 87] = "OFFER_UNDERWRITING_REQUESTED";
})(_ProjectUpdate_OfferUnderwritingRequestedUpdate_ || (_ProjectUpdate_OfferUnderwritingRequestedUpdate_ = {}));
const _ProjectUpdate_OfferUnderwritingRequestedUpdate = defineEnumProperties(_ProjectUpdate_OfferUnderwritingRequestedUpdate_, [87]);
var _ProjectUpdate_LeadingOfferUpdate_;
(function (_ProjectUpdate_LeadingOfferUpdate_) {
  _ProjectUpdate_LeadingOfferUpdate_[_ProjectUpdate_LeadingOfferUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_LeadingOfferUpdate_[_ProjectUpdate_LeadingOfferUpdate_["LEADING_OFFER"] = 47] = "LEADING_OFFER";
})(_ProjectUpdate_LeadingOfferUpdate_ || (_ProjectUpdate_LeadingOfferUpdate_ = {}));
const _ProjectUpdate_LeadingOfferUpdate = defineEnumProperties(_ProjectUpdate_LeadingOfferUpdate_, [47]);
var _ProjectUpdate_OccupancyUpdate_;
(function (_ProjectUpdate_OccupancyUpdate_) {
  _ProjectUpdate_OccupancyUpdate_[_ProjectUpdate_OccupancyUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_OccupancyUpdate_[_ProjectUpdate_OccupancyUpdate_["OCCUPANCY"] = 52] = "OCCUPANCY";
})(_ProjectUpdate_OccupancyUpdate_ || (_ProjectUpdate_OccupancyUpdate_ = {}));
const _ProjectUpdate_OccupancyUpdate = defineEnumProperties(_ProjectUpdate_OccupancyUpdate_, [52]);
var _ProjectUpdate_UsesUpdate_;
(function (_ProjectUpdate_UsesUpdate_) {
  _ProjectUpdate_UsesUpdate_[_ProjectUpdate_UsesUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_UsesUpdate_[_ProjectUpdate_UsesUpdate_["USES"] = 53] = "USES";
})(_ProjectUpdate_UsesUpdate_ || (_ProjectUpdate_UsesUpdate_ = {}));
const _ProjectUpdate_UsesUpdate = defineEnumProperties(_ProjectUpdate_UsesUpdate_, [53]);
var _ProjectUpdate_DisclaimerUpdate_;
(function (_ProjectUpdate_DisclaimerUpdate_) {
  _ProjectUpdate_DisclaimerUpdate_[_ProjectUpdate_DisclaimerUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_DisclaimerUpdate_[_ProjectUpdate_DisclaimerUpdate_["DISCLAIMER"] = 54] = "DISCLAIMER";
})(_ProjectUpdate_DisclaimerUpdate_ || (_ProjectUpdate_DisclaimerUpdate_ = {}));
const _ProjectUpdate_DisclaimerUpdate = defineEnumProperties(_ProjectUpdate_DisclaimerUpdate_, [54]);
var _ProjectUpdate_DemographicsUpdate_;
(function (_ProjectUpdate_DemographicsUpdate_) {
  _ProjectUpdate_DemographicsUpdate_[_ProjectUpdate_DemographicsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_DemographicsUpdate_[_ProjectUpdate_DemographicsUpdate_["DEMOGRAPHICS"] = 55] = "DEMOGRAPHICS";
})(_ProjectUpdate_DemographicsUpdate_ || (_ProjectUpdate_DemographicsUpdate_ = {}));
const _ProjectUpdate_DemographicsUpdate = defineEnumProperties(_ProjectUpdate_DemographicsUpdate_, [55]);
var _ProjectUpdate_ShowConcealedSharesUpdate_;
(function (_ProjectUpdate_ShowConcealedSharesUpdate_) {
  _ProjectUpdate_ShowConcealedSharesUpdate_[_ProjectUpdate_ShowConcealedSharesUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ShowConcealedSharesUpdate_[_ProjectUpdate_ShowConcealedSharesUpdate_["SHOW_CONCEALED_SHARES"] = 58] = "SHOW_CONCEALED_SHARES";
})(_ProjectUpdate_ShowConcealedSharesUpdate_ || (_ProjectUpdate_ShowConcealedSharesUpdate_ = {}));
const _ProjectUpdate_ShowConcealedSharesUpdate = defineEnumProperties(_ProjectUpdate_ShowConcealedSharesUpdate_, [58]);
var _ProjectUpdate_ProjectFeeUpdate_;
(function (_ProjectUpdate_ProjectFeeUpdate_) {
  _ProjectUpdate_ProjectFeeUpdate_[_ProjectUpdate_ProjectFeeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_ProjectFeeUpdate_[_ProjectUpdate_ProjectFeeUpdate_["PROJECT_FEE"] = 59] = "PROJECT_FEE";
})(_ProjectUpdate_ProjectFeeUpdate_ || (_ProjectUpdate_ProjectFeeUpdate_ = {}));
const _ProjectUpdate_ProjectFeeUpdate = defineEnumProperties(_ProjectUpdate_ProjectFeeUpdate_, [59]);
var _ProjectUpdate_CapitalAdvisorsUpdate_;
(function (_ProjectUpdate_CapitalAdvisorsUpdate_) {
  _ProjectUpdate_CapitalAdvisorsUpdate_[_ProjectUpdate_CapitalAdvisorsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_CapitalAdvisorsUpdate_[_ProjectUpdate_CapitalAdvisorsUpdate_["CAPITAL_ADVISORS"] = 60] = "CAPITAL_ADVISORS";
  _ProjectUpdate_CapitalAdvisorsUpdate_[_ProjectUpdate_CapitalAdvisorsUpdate_["CAPITAL_ADVISOR_SYMS"] = 104] = "CAPITAL_ADVISOR_SYMS";
})(_ProjectUpdate_CapitalAdvisorsUpdate_ || (_ProjectUpdate_CapitalAdvisorsUpdate_ = {}));
const _ProjectUpdate_CapitalAdvisorsUpdate = defineEnumProperties(_ProjectUpdate_CapitalAdvisorsUpdate_, [60, 104]);
var _ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_;
(function (_ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_) {
  _ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_[_ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_[_ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_["UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS"] = 64] = "UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS";
})(_ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_ || (_ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_ = {}));
const _ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate = defineEnumProperties(_ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate_, [64]);
class ProjectUpdate_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      99: 2,
      69: 7,
      65: 7,
      100: 7,
      66: 2,
      67: 7,
      101: 7,
      68: 2,
      92: 7,
      102: 7,
      93: 2,
      94: 7,
      103: 7,
      95: 2,
      70: 0,
      71: ProjectLostReason.shape,
      56: 1,
      96: 1,
      57: 1,
      63: 1,
      5: 0,
      6: 0,
      49: 0,
      89: HotelFlag.shape,
      90: CurrencyValue.shape,
      91: Percent.shape,
      61: ProjectTenancy.shape,
      7: 2,
      8: StandardUsAddress.shape,
      9: CurrencyValue.shape,
      10: 7,
      11: RichText.shape,
      12: Area.shape,
      13: Area.shape,
      14: Area.shape,
      15: 2,
      42: 2,
      43: 2,
      37: 2,
      44: 2,
      45: 2,
      16: 7,
      51: 7,
      17: 7,
      18: 7,
      98: 7,
      19: RichText.shape,
      20: 0,
      21: RichText.shape,
      22: CurrencyValue.shape,
      38: SimpleDate.shape,
      97: SimpleDate.shape,
      76: 2,
      39: CurrencyValue.shape,
      75: CurrencyValue.shape,
      40: Percent.shape,
      74: Percent.shape,
      23: RichText.shape,
      24: CurrencyValue.shape,
      25: SimpleDate.shape,
      48: 0,
      26: CurrencyValueRange.shape,
      27: 7,
      28: FinancialInstruments.shape,
      77: 0,
      78: 1,
      79: 7,
      80: UnitInformation.shape,
      81: Percent.shape,
      82: Percent.shape,
      83: Percent.shape,
      84: Percent.shape,
      85: 0,
      86: 0,
      46: OfferExpectations.shape,
      30: RepeatedCapStackItem.shape,
      73: RepeatedCapStackTarget.shape,
      72: RepeatedProjectGuarantor.shape,
      88: RepeatedInt32OrString.shape,
      33: SponsorProfile.shape,
      41: RichText.shape,
      36: 7,
      87: 7,
      47: 7,
      52: Occupancy.shape,
      53: RepeatedCapStackItem.shape,
      54: PropertyDisclaimer.shape,
      55: PropertyDemographics.shape,
      58: 1,
      59: ProjectFee.shape,
      60: RepeatedUint32.shape,
      104: RepeatedString.shape,
      64: RepeatedUnderwritingApprovalRequirement.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 99:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 69:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 65:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field add_sponsor_user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 100:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field add_sponsor_user_sym.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 66:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field invite_sponsor_user_email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 67:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field remove_sponsor_user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 101:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field remove_sponsor_user_sym.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 68:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field remove_sponsor_user_email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 92:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field add_broker_user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 102:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field add_broker_user_sym.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 93:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field invite_broker_user_email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 94:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field remove_broker_user_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 103:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field remove_broker_user_sym.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 95:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field remove_broker_user_email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 70:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectStatus.fromStrictJson_(v, getFieldMap);
          }
          return ProjectStatus.from(value);
        };
      case 71:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectLostReason);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field lost_reason.
                Expected type ProjectLostReason.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectLostReason', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 56:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectUpdate field approved.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 96:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectUpdate field approval_requested.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 57:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectUpdate field nda_required.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 63:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectUpdate field stacksource_agreement_signed.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field financing_purpose.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FinancingPurpose.fromStrictJson_(v, getFieldMap);
          }
          return FinancingPurpose.from(value);
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field asset_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AssetType.fromStrictJson_(v, getFieldMap);
          }
          return AssetType.from(value);
        };
      case 49:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field sponsor_role.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return SponsorRole.fromStrictJson_(v, getFieldMap);
          }
          return SponsorRole.from(value);
        };
      case 89:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(HotelFlag);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field hotel_flag.
                Expected type HotelFlag.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'HotelFlag', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 90:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field hotel_average_daily_rate.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 91:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field hotel_average_occupancy_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 61:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectTenancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field tenancy.
                Expected type ProjectTenancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectTenancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field deprecated_total_project_budget.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field number_of_units.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field project_summary.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field total_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field footprint_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Area);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field lot_size.
                Expected type Area.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Area', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field architect.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 42:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field structural_engineer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 43:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field construction_manager.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 37:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field general_contractor.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 44:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field leasing_representative.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 45:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field property_manager.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 16:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field completed_year.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 51:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field renovated_year.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 17:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field floors_above_ground.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 18:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field floors_below_ground.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 98:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field parking_spots.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 19:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field design_info.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 20:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field property_title.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return PropertyTitle.fromStrictJson_(v, getFieldMap);
          }
          return PropertyTitle.from(value);
        };
      case 21:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field asset_notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 22:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field latest_appraisal_value.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 38:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field latest_appraisal_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 97:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field target_close_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 76:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectUpdate field investment_category.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 39:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field current_net_operating_income.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 75:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field pro_forma_net_operating_income.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 40:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 74:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field pro_forma_cap_rate.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 23:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field financial_notes.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 24:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field acquisition_price.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 25:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SimpleDate);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field acquisition_date.
                Expected type SimpleDate.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SimpleDate', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 48:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field acquisition_status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AcquisitionStatus.fromStrictJson_(v, getFieldMap);
          }
          return AcquisitionStatus.from(value);
        };
      case 26:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValueRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field principal_needed.
                Expected type CurrencyValueRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValueRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 27:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field lien_position.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 28:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(FinancialInstruments);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field financial_instruments.
                Expected type FinancialInstruments.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'FinancialInstruments', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 77:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field affordability.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return AffordabilityType.fromStrictJson_(v, getFieldMap);
          }
          return AffordabilityType.from(value);
        };
      case 78:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectUpdate field has_elevator.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 79:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field number_of_floors.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 80:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UnitInformation);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field unit_information.
                Expected type UnitInformation.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UnitInformation', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 81:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field lihtc_ratio.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 82:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field hud_selected_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 83:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field freddie_sbl_selected_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 84:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field freddie_sbl_selected_dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 85:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field freddie_sbl_interest_only_term.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblInterestOnlyTerm.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblInterestOnlyTerm.from(value);
        };
      case 86:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectUpdate field freddie_sbl_prepayment_penalty.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return FreddieSblPrepaymentPenalty.fromStrictJson_(v, getFieldMap);
          }
          return FreddieSblPrepaymentPenalty.from(value);
        };
      case 46:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OfferExpectations);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field offer_expectations.
                Expected type OfferExpectations.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OfferExpectations', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 30:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedCapStackItem);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field cap_stack_items.
                Expected type RepeatedCapStackItem.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedCapStackItem', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 73:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedCapStackTarget);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field cap_stack_targets.
                Expected type RepeatedCapStackTarget.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedCapStackTarget', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 72:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedProjectGuarantor);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field guarantors.
                Expected type RepeatedProjectGuarantor.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedProjectGuarantor', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 88:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedInt32OrString);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field solicited_lenders.
                Expected type RepeatedInt32OrString.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedInt32OrString', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 33:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(SponsorProfile);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field sponsor_profile.
                Expected type SponsorProfile.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'SponsorProfile', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 41:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field note.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 36:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field accepted_offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 87:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field offer_underwriting_requested.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 47:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectUpdate field leading_offer.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 52:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Occupancy);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field occupancy.
                Expected type Occupancy.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Occupancy', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 53:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedCapStackItem);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field uses.
                Expected type RepeatedCapStackItem.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedCapStackItem', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 54:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PropertyDisclaimer);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field disclaimer.
                Expected type PropertyDisclaimer.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PropertyDisclaimer', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 55:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(PropertyDemographics);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field demographics.
                Expected type PropertyDemographics.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'PropertyDemographics', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 58:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectUpdate field show_concealed_shares.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 59:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(ProjectFee);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field project_fee.
                Expected type ProjectFee.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'ProjectFee', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 60:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedUint32);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field capital_advisors.
                Expected type RepeatedUint32.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedUint32', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 104:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedString);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field capital_advisor_syms.
                Expected type RepeatedString.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedString', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 64:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedUnderwritingApprovalRequirement);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectUpdate field underwriting_approval_requirement_exceptions.
                Expected type RepeatedUnderwritingApprovalRequirement.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedUnderwritingApprovalRequirement', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectUpdate, 'ProjectUpdate', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectUpdate to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get SponsorUsersUpdateCase() {
    return _ProjectUpdate_SponsorUsersUpdate;
  }
  get sponsorUsersUpdateCase() {
    return this.getCaseFromIds([65, 100, 66, 67, 101, 68]);
  }
  static get BrokerUsersUpdateCase() {
    return _ProjectUpdate_BrokerUsersUpdate;
  }
  get brokerUsersUpdateCase() {
    return this.getCaseFromIds([92, 102, 93, 94, 103, 95]);
  }
  static get StatusUpdateCase() {
    return _ProjectUpdate_StatusUpdate;
  }
  get statusUpdateCase() {
    return this.getCaseFromIds([70]);
  }
  static get LostReasonUpdateCase() {
    return _ProjectUpdate_LostReasonUpdate;
  }
  get lostReasonUpdateCase() {
    return this.getCaseFromIds([71]);
  }
  static get ApprovedUpdateCase() {
    return _ProjectUpdate_ApprovedUpdate;
  }
  get approvedUpdateCase() {
    return this.getCaseFromIds([56]);
  }
  static get ApprovalRequestedUpdateCase() {
    return _ProjectUpdate_ApprovalRequestedUpdate;
  }
  get approvalRequestedUpdateCase() {
    return this.getCaseFromIds([96]);
  }
  static get NdaRequiredUpdateCase() {
    return _ProjectUpdate_NdaRequiredUpdate;
  }
  get ndaRequiredUpdateCase() {
    return this.getCaseFromIds([57]);
  }
  static get StacksourceAgreementSignedUpdateCase() {
    return _ProjectUpdate_StacksourceAgreementSignedUpdate;
  }
  get stacksourceAgreementSignedUpdateCase() {
    return this.getCaseFromIds([63]);
  }
  static get FinancingPurposeUpdateCase() {
    return _ProjectUpdate_FinancingPurposeUpdate;
  }
  get financingPurposeUpdateCase() {
    return this.getCaseFromIds([5]);
  }
  static get AssetTypeUpdateCase() {
    return _ProjectUpdate_AssetTypeUpdate;
  }
  get assetTypeUpdateCase() {
    return this.getCaseFromIds([6]);
  }
  static get SponsorRoleUpdateCase() {
    return _ProjectUpdate_SponsorRoleUpdate;
  }
  get sponsorRoleUpdateCase() {
    return this.getCaseFromIds([49]);
  }
  static get HotelFlagUpdateCase() {
    return _ProjectUpdate_HotelFlagUpdate;
  }
  get hotelFlagUpdateCase() {
    return this.getCaseFromIds([89]);
  }
  static get HotelAverageDailyRateUpdateCase() {
    return _ProjectUpdate_HotelAverageDailyRateUpdate;
  }
  get hotelAverageDailyRateUpdateCase() {
    return this.getCaseFromIds([90]);
  }
  static get HotelAverageOccupancyRateUpdateCase() {
    return _ProjectUpdate_HotelAverageOccupancyRateUpdate;
  }
  get hotelAverageOccupancyRateUpdateCase() {
    return this.getCaseFromIds([91]);
  }
  static get TenancyUpdateCase() {
    return _ProjectUpdate_TenancyUpdate;
  }
  get tenancyUpdateCase() {
    return this.getCaseFromIds([61]);
  }
  static get NameUpdateCase() {
    return _ProjectUpdate_NameUpdate;
  }
  get nameUpdateCase() {
    return this.getCaseFromIds([7]);
  }
  static get AddressUpdateCase() {
    return _ProjectUpdate_AddressUpdate;
  }
  get addressUpdateCase() {
    return this.getCaseFromIds([8]);
  }
  static get DeprecatedTotalProjectBudgetUpdateCase() {
    return _ProjectUpdate_DeprecatedTotalProjectBudgetUpdate;
  }
  get deprecatedTotalProjectBudgetUpdateCase() {
    return this.getCaseFromIds([9]);
  }
  static get NumberOfUnitsUpdateCase() {
    return _ProjectUpdate_NumberOfUnitsUpdate;
  }
  get numberOfUnitsUpdateCase() {
    return this.getCaseFromIds([10]);
  }
  static get ProjectSummaryUpdateCase() {
    return _ProjectUpdate_ProjectSummaryUpdate;
  }
  get projectSummaryUpdateCase() {
    return this.getCaseFromIds([11]);
  }
  static get TotalSizeUpdateCase() {
    return _ProjectUpdate_TotalSizeUpdate;
  }
  get totalSizeUpdateCase() {
    return this.getCaseFromIds([12]);
  }
  static get FootprintSizeUpdateCase() {
    return _ProjectUpdate_FootprintSizeUpdate;
  }
  get footprintSizeUpdateCase() {
    return this.getCaseFromIds([13]);
  }
  static get LotSizeUpdateCase() {
    return _ProjectUpdate_LotSizeUpdate;
  }
  get lotSizeUpdateCase() {
    return this.getCaseFromIds([14]);
  }
  static get ArchitectUpdateCase() {
    return _ProjectUpdate_ArchitectUpdate;
  }
  get architectUpdateCase() {
    return this.getCaseFromIds([15]);
  }
  static get StructuralEngineerUpdateCase() {
    return _ProjectUpdate_StructuralEngineerUpdate;
  }
  get structuralEngineerUpdateCase() {
    return this.getCaseFromIds([42]);
  }
  static get ConstructionManagerUpdateCase() {
    return _ProjectUpdate_ConstructionManagerUpdate;
  }
  get constructionManagerUpdateCase() {
    return this.getCaseFromIds([43]);
  }
  static get GeneralContractorUpdateCase() {
    return _ProjectUpdate_GeneralContractorUpdate;
  }
  get generalContractorUpdateCase() {
    return this.getCaseFromIds([37]);
  }
  static get LeasingRepresentativeUpdateCase() {
    return _ProjectUpdate_LeasingRepresentativeUpdate;
  }
  get leasingRepresentativeUpdateCase() {
    return this.getCaseFromIds([44]);
  }
  static get PropertyManagerUpdateCase() {
    return _ProjectUpdate_PropertyManagerUpdate;
  }
  get propertyManagerUpdateCase() {
    return this.getCaseFromIds([45]);
  }
  static get CompletedYearUpdateCase() {
    return _ProjectUpdate_CompletedYearUpdate;
  }
  get completedYearUpdateCase() {
    return this.getCaseFromIds([16]);
  }
  static get RenovatedYearUpdateCase() {
    return _ProjectUpdate_RenovatedYearUpdate;
  }
  get renovatedYearUpdateCase() {
    return this.getCaseFromIds([51]);
  }
  static get FloorsAboveGroundUpdateCase() {
    return _ProjectUpdate_FloorsAboveGroundUpdate;
  }
  get floorsAboveGroundUpdateCase() {
    return this.getCaseFromIds([17]);
  }
  static get FloorsBelowGroundUpdateCase() {
    return _ProjectUpdate_FloorsBelowGroundUpdate;
  }
  get floorsBelowGroundUpdateCase() {
    return this.getCaseFromIds([18]);
  }
  static get ParkingSpotsUpdateCase() {
    return _ProjectUpdate_ParkingSpotsUpdate;
  }
  get parkingSpotsUpdateCase() {
    return this.getCaseFromIds([98]);
  }
  static get DesignInfoUpdateCase() {
    return _ProjectUpdate_DesignInfoUpdate;
  }
  get designInfoUpdateCase() {
    return this.getCaseFromIds([19]);
  }
  static get PropertyTitleUpdateCase() {
    return _ProjectUpdate_PropertyTitleUpdate;
  }
  get propertyTitleUpdateCase() {
    return this.getCaseFromIds([20]);
  }
  static get AssetNotesUpdateCase() {
    return _ProjectUpdate_AssetNotesUpdate;
  }
  get assetNotesUpdateCase() {
    return this.getCaseFromIds([21]);
  }
  static get LatestAppraisalValueUpdateCase() {
    return _ProjectUpdate_LatestAppraisalValueUpdate;
  }
  get latestAppraisalValueUpdateCase() {
    return this.getCaseFromIds([22]);
  }
  static get LatestAppraisalDateUpdateCase() {
    return _ProjectUpdate_LatestAppraisalDateUpdate;
  }
  get latestAppraisalDateUpdateCase() {
    return this.getCaseFromIds([38]);
  }
  static get TargetCloseDateUpdateCase() {
    return _ProjectUpdate_TargetCloseDateUpdate;
  }
  get targetCloseDateUpdateCase() {
    return this.getCaseFromIds([97]);
  }
  static get InvestmentCategoryUpdateCase() {
    return _ProjectUpdate_InvestmentCategoryUpdate;
  }
  get investmentCategoryUpdateCase() {
    return this.getCaseFromIds([76]);
  }
  static get CurrentNetOperatingIncomeUpdateCase() {
    return _ProjectUpdate_CurrentNetOperatingIncomeUpdate;
  }
  get currentNetOperatingIncomeUpdateCase() {
    return this.getCaseFromIds([39]);
  }
  static get ProFormaNetOperatingIncomeUpdateCase() {
    return _ProjectUpdate_ProFormaNetOperatingIncomeUpdate;
  }
  get proFormaNetOperatingIncomeUpdateCase() {
    return this.getCaseFromIds([75]);
  }
  static get CapRateUpdateCase() {
    return _ProjectUpdate_CapRateUpdate;
  }
  get capRateUpdateCase() {
    return this.getCaseFromIds([40]);
  }
  static get ProFormaCapRateUpdateCase() {
    return _ProjectUpdate_ProFormaCapRateUpdate;
  }
  get proFormaCapRateUpdateCase() {
    return this.getCaseFromIds([74]);
  }
  static get FinancialNotesUpdateCase() {
    return _ProjectUpdate_FinancialNotesUpdate;
  }
  get financialNotesUpdateCase() {
    return this.getCaseFromIds([23]);
  }
  static get AcquisitionPriceUpdateCase() {
    return _ProjectUpdate_AcquisitionPriceUpdate;
  }
  get acquisitionPriceUpdateCase() {
    return this.getCaseFromIds([24]);
  }
  static get AcquisitionDateUpdateCase() {
    return _ProjectUpdate_AcquisitionDateUpdate;
  }
  get acquisitionDateUpdateCase() {
    return this.getCaseFromIds([25]);
  }
  static get AcquisitionStatusUpdateCase() {
    return _ProjectUpdate_AcquisitionStatusUpdate;
  }
  get acquisitionStatusUpdateCase() {
    return this.getCaseFromIds([48]);
  }
  static get PrincipalNeededUpdateCase() {
    return _ProjectUpdate_PrincipalNeededUpdate;
  }
  get principalNeededUpdateCase() {
    return this.getCaseFromIds([26]);
  }
  static get LienPositionUpdateCase() {
    return _ProjectUpdate_LienPositionUpdate;
  }
  get lienPositionUpdateCase() {
    return this.getCaseFromIds([27]);
  }
  static get FinancialInstrumentsUpdateCase() {
    return _ProjectUpdate_FinancialInstrumentsUpdate;
  }
  get financialInstrumentsUpdateCase() {
    return this.getCaseFromIds([28]);
  }
  static get AffordabilityUpdateCase() {
    return _ProjectUpdate_AffordabilityUpdate;
  }
  get affordabilityUpdateCase() {
    return this.getCaseFromIds([77]);
  }
  static get HasElevatorUpdateCase() {
    return _ProjectUpdate_HasElevatorUpdate;
  }
  get hasElevatorUpdateCase() {
    return this.getCaseFromIds([78]);
  }
  static get NumberOfFloorsUpdateCase() {
    return _ProjectUpdate_NumberOfFloorsUpdate;
  }
  get numberOfFloorsUpdateCase() {
    return this.getCaseFromIds([79]);
  }
  static get UnitInformationUpdateCase() {
    return _ProjectUpdate_UnitInformationUpdate;
  }
  get unitInformationUpdateCase() {
    return this.getCaseFromIds([80]);
  }
  static get LihtcRatioUpdateCase() {
    return _ProjectUpdate_LihtcRatioUpdate;
  }
  get lihtcRatioUpdateCase() {
    return this.getCaseFromIds([81]);
  }
  static get HudSelectedLtvUpdateCase() {
    return _ProjectUpdate_HudSelectedLtvUpdate;
  }
  get hudSelectedLtvUpdateCase() {
    return this.getCaseFromIds([82]);
  }
  static get FreddieSblSelectedLtvUpdateCase() {
    return _ProjectUpdate_FreddieSblSelectedLtvUpdate;
  }
  get freddieSblSelectedLtvUpdateCase() {
    return this.getCaseFromIds([83]);
  }
  static get FreddieSblSelectedDscrUpdateCase() {
    return _ProjectUpdate_FreddieSblSelectedDscrUpdate;
  }
  get freddieSblSelectedDscrUpdateCase() {
    return this.getCaseFromIds([84]);
  }
  static get FreddieSblInterestOnlyTermUpdateCase() {
    return _ProjectUpdate_FreddieSblInterestOnlyTermUpdate;
  }
  get freddieSblInterestOnlyTermUpdateCase() {
    return this.getCaseFromIds([85]);
  }
  static get FreddieSblPrepaymentPenaltyUpdateCase() {
    return _ProjectUpdate_FreddieSblPrepaymentPenaltyUpdate;
  }
  get freddieSblPrepaymentPenaltyUpdateCase() {
    return this.getCaseFromIds([86]);
  }
  static get OfferExpectationsUpdateCase() {
    return _ProjectUpdate_OfferExpectationsUpdate;
  }
  get offerExpectationsUpdateCase() {
    return this.getCaseFromIds([46]);
  }
  static get CapStackItemsUpdateCase() {
    return _ProjectUpdate_CapStackItemsUpdate;
  }
  get capStackItemsUpdateCase() {
    return this.getCaseFromIds([30]);
  }
  static get CapStackTargetsUpdateCase() {
    return _ProjectUpdate_CapStackTargetsUpdate;
  }
  get capStackTargetsUpdateCase() {
    return this.getCaseFromIds([73]);
  }
  static get GuarantorsUpdateCase() {
    return _ProjectUpdate_GuarantorsUpdate;
  }
  get guarantorsUpdateCase() {
    return this.getCaseFromIds([72]);
  }
  static get SolicitedLendersUpdateCase() {
    return _ProjectUpdate_SolicitedLendersUpdate;
  }
  get solicitedLendersUpdateCase() {
    return this.getCaseFromIds([88]);
  }
  static get SponsorProfileUpdateCase() {
    return _ProjectUpdate_SponsorProfileUpdate;
  }
  get sponsorProfileUpdateCase() {
    return this.getCaseFromIds([33]);
  }
  static get NoteUpdateCase() {
    return _ProjectUpdate_NoteUpdate;
  }
  get noteUpdateCase() {
    return this.getCaseFromIds([41]);
  }
  static get AcceptedOfferUpdateCase() {
    return _ProjectUpdate_AcceptedOfferUpdate;
  }
  get acceptedOfferUpdateCase() {
    return this.getCaseFromIds([36]);
  }
  static get OfferUnderwritingRequestedUpdateCase() {
    return _ProjectUpdate_OfferUnderwritingRequestedUpdate;
  }
  get offerUnderwritingRequestedUpdateCase() {
    return this.getCaseFromIds([87]);
  }
  static get LeadingOfferUpdateCase() {
    return _ProjectUpdate_LeadingOfferUpdate;
  }
  get leadingOfferUpdateCase() {
    return this.getCaseFromIds([47]);
  }
  static get OccupancyUpdateCase() {
    return _ProjectUpdate_OccupancyUpdate;
  }
  get occupancyUpdateCase() {
    return this.getCaseFromIds([52]);
  }
  static get UsesUpdateCase() {
    return _ProjectUpdate_UsesUpdate;
  }
  get usesUpdateCase() {
    return this.getCaseFromIds([53]);
  }
  static get DisclaimerUpdateCase() {
    return _ProjectUpdate_DisclaimerUpdate;
  }
  get disclaimerUpdateCase() {
    return this.getCaseFromIds([54]);
  }
  static get DemographicsUpdateCase() {
    return _ProjectUpdate_DemographicsUpdate;
  }
  get demographicsUpdateCase() {
    return this.getCaseFromIds([55]);
  }
  static get ShowConcealedSharesUpdateCase() {
    return _ProjectUpdate_ShowConcealedSharesUpdate;
  }
  get showConcealedSharesUpdateCase() {
    return this.getCaseFromIds([58]);
  }
  static get ProjectFeeUpdateCase() {
    return _ProjectUpdate_ProjectFeeUpdate;
  }
  get projectFeeUpdateCase() {
    return this.getCaseFromIds([59]);
  }
  static get CapitalAdvisorsUpdateCase() {
    return _ProjectUpdate_CapitalAdvisorsUpdate;
  }
  get capitalAdvisorsUpdateCase() {
    return this.getCaseFromIds([60, 104]);
  }
  static get UnderwritingApprovalRequirementExceptionsUpdateCase() {
    return _ProjectUpdate_UnderwritingApprovalRequirementExceptionsUpdate;
  }
  get underwritingApprovalRequirementExceptionsUpdateCase() {
    return this.getCaseFromIds([64]);
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(99, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(99, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(99, null, null);
  }
  get userId() {
    return this.getFieldById(69, 0);
  }
  setUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(69, value, null);
  }
  deleteUserId() {
    return this.setValueFieldById(69, null, null);
  }
  get addSponsorUserId() {
    return this.getFieldById(65, 0);
  }
  setAddSponsorUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(65, value, "sponsor_users_update");
  }
  deleteAddSponsorUserId() {
    return this.setValueFieldById(65, null, "sponsor_users_update");
  }
  get addSponsorUserSym() {
    return this.getFieldById(100, 0);
  }
  setAddSponsorUserSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(100, value, "sponsor_users_update");
  }
  deleteAddSponsorUserSym() {
    return this.setValueFieldById(100, null, "sponsor_users_update");
  }
  get inviteSponsorUserEmail() {
    return this.getFieldById(66, "");
  }
  setInviteSponsorUserEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(66, value, "sponsor_users_update");
  }
  deleteInviteSponsorUserEmail() {
    return this.setValueFieldById(66, null, "sponsor_users_update");
  }
  get removeSponsorUserId() {
    return this.getFieldById(67, 0);
  }
  setRemoveSponsorUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(67, value, "sponsor_users_update");
  }
  deleteRemoveSponsorUserId() {
    return this.setValueFieldById(67, null, "sponsor_users_update");
  }
  get removeSponsorUserSym() {
    return this.getFieldById(101, 0);
  }
  setRemoveSponsorUserSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(101, value, "sponsor_users_update");
  }
  deleteRemoveSponsorUserSym() {
    return this.setValueFieldById(101, null, "sponsor_users_update");
  }
  get removeSponsorUserEmail() {
    return this.getFieldById(68, "");
  }
  setRemoveSponsorUserEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(68, value, "sponsor_users_update");
  }
  deleteRemoveSponsorUserEmail() {
    return this.setValueFieldById(68, null, "sponsor_users_update");
  }
  get addBrokerUserId() {
    return this.getFieldById(92, 0);
  }
  setAddBrokerUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(92, value, "broker_users_update");
  }
  deleteAddBrokerUserId() {
    return this.setValueFieldById(92, null, "broker_users_update");
  }
  get addBrokerUserSym() {
    return this.getFieldById(102, 0);
  }
  setAddBrokerUserSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(102, value, "broker_users_update");
  }
  deleteAddBrokerUserSym() {
    return this.setValueFieldById(102, null, "broker_users_update");
  }
  get inviteBrokerUserEmail() {
    return this.getFieldById(93, "");
  }
  setInviteBrokerUserEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(93, value, "broker_users_update");
  }
  deleteInviteBrokerUserEmail() {
    return this.setValueFieldById(93, null, "broker_users_update");
  }
  get removeBrokerUserId() {
    return this.getFieldById(94, 0);
  }
  setRemoveBrokerUserId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(94, value, "broker_users_update");
  }
  deleteRemoveBrokerUserId() {
    return this.setValueFieldById(94, null, "broker_users_update");
  }
  get removeBrokerUserSym() {
    return this.getFieldById(103, 0);
  }
  setRemoveBrokerUserSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(103, value, "broker_users_update");
  }
  deleteRemoveBrokerUserSym() {
    return this.setValueFieldById(103, null, "broker_users_update");
  }
  get removeBrokerUserEmail() {
    return this.getFieldById(95, "");
  }
  setRemoveBrokerUserEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(95, value, "broker_users_update");
  }
  deleteRemoveBrokerUserEmail() {
    return this.setValueFieldById(95, null, "broker_users_update");
  }
  get status() {
    return this.getFieldById(70, 0);
  }
  setStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(70, value, "status_update");
  }
  deleteStatus() {
    return this.setValueFieldById(70, null, "status_update");
  }
  get lostReason() {
    return this.getFieldById(71, ProjectLostReason.getDefault());
  }
  setLostReason(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(71, value, "lost_reason_update");
  }
  deleteLostReason() {
    return this.setValueFieldById(71, null, "lost_reason_update");
  }
  get approved() {
    return this.getFieldById(56, false);
  }
  setApproved(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(56, value, "approved_update");
  }
  deleteApproved() {
    return this.setValueFieldById(56, null, "approved_update");
  }
  get approvalRequested() {
    return this.getFieldById(96, false);
  }
  setApprovalRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(96, value, "approval_requested_update");
  }
  deleteApprovalRequested() {
    return this.setValueFieldById(96, null, "approval_requested_update");
  }
  get ndaRequired() {
    return this.getFieldById(57, false);
  }
  setNdaRequired(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(57, value, "nda_required_update");
  }
  deleteNdaRequired() {
    return this.setValueFieldById(57, null, "nda_required_update");
  }
  get stacksourceAgreementSigned() {
    return this.getFieldById(63, false);
  }
  setStacksourceAgreementSigned(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(63, value, "stacksource_agreement_signed_update");
  }
  deleteStacksourceAgreementSigned() {
    return this.setValueFieldById(63, null, "stacksource_agreement_signed_update");
  }
  get financingPurpose() {
    return this.getFieldById(5, 0);
  }
  setFinancingPurpose(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, "financing_purpose_update");
  }
  deleteFinancingPurpose() {
    return this.setValueFieldById(5, null, "financing_purpose_update");
  }
  get assetType() {
    return this.getFieldById(6, 0);
  }
  setAssetType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, "asset_type_update");
  }
  deleteAssetType() {
    return this.setValueFieldById(6, null, "asset_type_update");
  }
  get sponsorRole() {
    return this.getFieldById(49, 0);
  }
  setSponsorRole(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(49, value, "sponsor_role_update");
  }
  deleteSponsorRole() {
    return this.setValueFieldById(49, null, "sponsor_role_update");
  }
  get hotelFlag() {
    return this.getFieldById(89, HotelFlag.getDefault());
  }
  setHotelFlag(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(89, value, "hotel_flag_update");
  }
  deleteHotelFlag() {
    return this.setValueFieldById(89, null, "hotel_flag_update");
  }
  get hotelAverageDailyRate() {
    return this.getFieldById(90, CurrencyValue.getDefault());
  }
  setHotelAverageDailyRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(90, value, "hotel_average_daily_rate_update");
  }
  deleteHotelAverageDailyRate() {
    return this.setValueFieldById(90, null, "hotel_average_daily_rate_update");
  }
  get hotelAverageOccupancyRate() {
    return this.getFieldById(91, Percent.getDefault());
  }
  setHotelAverageOccupancyRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(91, value, "hotel_average_occupancy_rate_update");
  }
  deleteHotelAverageOccupancyRate() {
    return this.setValueFieldById(91, null, "hotel_average_occupancy_rate_update");
  }
  get tenancy() {
    return this.getFieldById(61, ProjectTenancy.getDefault());
  }
  setTenancy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(61, value, "tenancy_update");
  }
  deleteTenancy() {
    return this.setValueFieldById(61, null, "tenancy_update");
  }
  get name() {
    return this.getFieldById(7, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, "name_update");
  }
  deleteName() {
    return this.setValueFieldById(7, null, "name_update");
  }
  get address() {
    return this.getFieldById(8, StandardUsAddress.getDefault());
  }
  setAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, "address_update");
  }
  deleteAddress() {
    return this.setValueFieldById(8, null, "address_update");
  }
  get deprecatedTotalProjectBudget() {
    return this.getFieldById(9, CurrencyValue.getDefault());
  }
  setDeprecatedTotalProjectBudget(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, "deprecated_total_project_budget_update");
  }
  deleteDeprecatedTotalProjectBudget() {
    return this.setValueFieldById(9, null, "deprecated_total_project_budget_update");
  }
  get numberOfUnits() {
    return this.getFieldById(10, 0);
  }
  setNumberOfUnits(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, "number_of_units_update");
  }
  deleteNumberOfUnits() {
    return this.setValueFieldById(10, null, "number_of_units_update");
  }
  get projectSummary() {
    return this.getFieldById(11, RichText.getDefault());
  }
  setProjectSummary(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, "project_summary_update");
  }
  deleteProjectSummary() {
    return this.setValueFieldById(11, null, "project_summary_update");
  }
  get totalSize() {
    return this.getFieldById(12, Area.getDefault());
  }
  setTotalSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, "total_size_update");
  }
  deleteTotalSize() {
    return this.setValueFieldById(12, null, "total_size_update");
  }
  get footprintSize() {
    return this.getFieldById(13, Area.getDefault());
  }
  setFootprintSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, "footprint_size_update");
  }
  deleteFootprintSize() {
    return this.setValueFieldById(13, null, "footprint_size_update");
  }
  get lotSize() {
    return this.getFieldById(14, Area.getDefault());
  }
  setLotSize(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, "lot_size_update");
  }
  deleteLotSize() {
    return this.setValueFieldById(14, null, "lot_size_update");
  }
  get architect() {
    return this.getFieldById(15, "");
  }
  setArchitect(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, "architect_update");
  }
  deleteArchitect() {
    return this.setValueFieldById(15, null, "architect_update");
  }
  get structuralEngineer() {
    return this.getFieldById(42, "");
  }
  setStructuralEngineer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(42, value, "structural_engineer_update");
  }
  deleteStructuralEngineer() {
    return this.setValueFieldById(42, null, "structural_engineer_update");
  }
  get constructionManager() {
    return this.getFieldById(43, "");
  }
  setConstructionManager(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(43, value, "construction_manager_update");
  }
  deleteConstructionManager() {
    return this.setValueFieldById(43, null, "construction_manager_update");
  }
  get generalContractor() {
    return this.getFieldById(37, "");
  }
  setGeneralContractor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(37, value, "general_contractor_update");
  }
  deleteGeneralContractor() {
    return this.setValueFieldById(37, null, "general_contractor_update");
  }
  get leasingRepresentative() {
    return this.getFieldById(44, "");
  }
  setLeasingRepresentative(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(44, value, "leasing_representative_update");
  }
  deleteLeasingRepresentative() {
    return this.setValueFieldById(44, null, "leasing_representative_update");
  }
  get propertyManager() {
    return this.getFieldById(45, "");
  }
  setPropertyManager(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(45, value, "property_manager_update");
  }
  deletePropertyManager() {
    return this.setValueFieldById(45, null, "property_manager_update");
  }
  get completedYear() {
    return this.getFieldById(16, 0);
  }
  setCompletedYear(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(16, value, "completed_year_update");
  }
  deleteCompletedYear() {
    return this.setValueFieldById(16, null, "completed_year_update");
  }
  get renovatedYear() {
    return this.getFieldById(51, 0);
  }
  setRenovatedYear(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(51, value, "renovated_year_update");
  }
  deleteRenovatedYear() {
    return this.setValueFieldById(51, null, "renovated_year_update");
  }
  get floorsAboveGround() {
    return this.getFieldById(17, 0);
  }
  setFloorsAboveGround(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(17, value, "floors_above_ground_update");
  }
  deleteFloorsAboveGround() {
    return this.setValueFieldById(17, null, "floors_above_ground_update");
  }
  get floorsBelowGround() {
    return this.getFieldById(18, 0);
  }
  setFloorsBelowGround(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(18, value, "floors_below_ground_update");
  }
  deleteFloorsBelowGround() {
    return this.setValueFieldById(18, null, "floors_below_ground_update");
  }
  get parkingSpots() {
    return this.getFieldById(98, 0);
  }
  setParkingSpots(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(98, value, "parking_spots_update");
  }
  deleteParkingSpots() {
    return this.setValueFieldById(98, null, "parking_spots_update");
  }
  get designInfo() {
    return this.getFieldById(19, RichText.getDefault());
  }
  setDesignInfo(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(19, value, "design_info_update");
  }
  deleteDesignInfo() {
    return this.setValueFieldById(19, null, "design_info_update");
  }
  get propertyTitle() {
    return this.getFieldById(20, 0);
  }
  setPropertyTitle(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(20, value, "property_title_update");
  }
  deletePropertyTitle() {
    return this.setValueFieldById(20, null, "property_title_update");
  }
  get assetNotes() {
    return this.getFieldById(21, RichText.getDefault());
  }
  setAssetNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(21, value, "asset_notes_update");
  }
  deleteAssetNotes() {
    return this.setValueFieldById(21, null, "asset_notes_update");
  }
  get latestAppraisalValue() {
    return this.getFieldById(22, CurrencyValue.getDefault());
  }
  setLatestAppraisalValue(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(22, value, "latest_appraisal_value_update");
  }
  deleteLatestAppraisalValue() {
    return this.setValueFieldById(22, null, "latest_appraisal_value_update");
  }
  get latestAppraisalDate() {
    return this.getFieldById(38, SimpleDate.getDefault());
  }
  setLatestAppraisalDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(38, value, "latest_appraisal_date_update");
  }
  deleteLatestAppraisalDate() {
    return this.setValueFieldById(38, null, "latest_appraisal_date_update");
  }
  get targetCloseDate() {
    return this.getFieldById(97, SimpleDate.getDefault());
  }
  setTargetCloseDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(97, value, "target_close_date_update");
  }
  deleteTargetCloseDate() {
    return this.setValueFieldById(97, null, "target_close_date_update");
  }
  get investmentCategory() {
    return this.getFieldById(76, "");
  }
  setInvestmentCategory(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(76, value, "investment_category_update");
  }
  deleteInvestmentCategory() {
    return this.setValueFieldById(76, null, "investment_category_update");
  }
  get currentNetOperatingIncome() {
    return this.getFieldById(39, CurrencyValue.getDefault());
  }
  setCurrentNetOperatingIncome(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(39, value, "current_net_operating_income_update");
  }
  deleteCurrentNetOperatingIncome() {
    return this.setValueFieldById(39, null, "current_net_operating_income_update");
  }
  get proFormaNetOperatingIncome() {
    return this.getFieldById(75, CurrencyValue.getDefault());
  }
  setProFormaNetOperatingIncome(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(75, value, "pro_forma_net_operating_income_update");
  }
  deleteProFormaNetOperatingIncome() {
    return this.setValueFieldById(75, null, "pro_forma_net_operating_income_update");
  }
  get capRate() {
    return this.getFieldById(40, Percent.getDefault());
  }
  setCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(40, value, "cap_rate_update");
  }
  deleteCapRate() {
    return this.setValueFieldById(40, null, "cap_rate_update");
  }
  get proFormaCapRate() {
    return this.getFieldById(74, Percent.getDefault());
  }
  setProFormaCapRate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(74, value, "pro_forma_cap_rate_update");
  }
  deleteProFormaCapRate() {
    return this.setValueFieldById(74, null, "pro_forma_cap_rate_update");
  }
  get financialNotes() {
    return this.getFieldById(23, RichText.getDefault());
  }
  setFinancialNotes(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(23, value, "financial_notes_update");
  }
  deleteFinancialNotes() {
    return this.setValueFieldById(23, null, "financial_notes_update");
  }
  get acquisitionPrice() {
    return this.getFieldById(24, CurrencyValue.getDefault());
  }
  setAcquisitionPrice(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(24, value, "acquisition_price_update");
  }
  deleteAcquisitionPrice() {
    return this.setValueFieldById(24, null, "acquisition_price_update");
  }
  get acquisitionDate() {
    return this.getFieldById(25, SimpleDate.getDefault());
  }
  setAcquisitionDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(25, value, "acquisition_date_update");
  }
  deleteAcquisitionDate() {
    return this.setValueFieldById(25, null, "acquisition_date_update");
  }
  get acquisitionStatus() {
    return this.getFieldById(48, 0);
  }
  setAcquisitionStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(48, value, "acquisition_status_update");
  }
  deleteAcquisitionStatus() {
    return this.setValueFieldById(48, null, "acquisition_status_update");
  }
  get principalNeeded() {
    return this.getFieldById(26, CurrencyValueRange.getDefault());
  }
  setPrincipalNeeded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(26, value, "principal_needed_update");
  }
  deletePrincipalNeeded() {
    return this.setValueFieldById(26, null, "principal_needed_update");
  }
  get lienPosition() {
    return this.getFieldById(27, 0);
  }
  setLienPosition(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(27, value, "lien_position_update");
  }
  deleteLienPosition() {
    return this.setValueFieldById(27, null, "lien_position_update");
  }
  get financialInstruments() {
    return this.getFieldById(28, FinancialInstruments.getDefault());
  }
  setFinancialInstruments(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(28, value, "financial_instruments_update");
  }
  deleteFinancialInstruments() {
    return this.setValueFieldById(28, null, "financial_instruments_update");
  }
  get affordability() {
    return this.getFieldById(77, 0);
  }
  setAffordability(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(77, value, "affordability_update");
  }
  deleteAffordability() {
    return this.setValueFieldById(77, null, "affordability_update");
  }
  get hasElevator() {
    return this.getFieldById(78, false);
  }
  setHasElevator(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(78, value, "has_elevator_update");
  }
  deleteHasElevator() {
    return this.setValueFieldById(78, null, "has_elevator_update");
  }
  get numberOfFloors() {
    return this.getFieldById(79, 0);
  }
  setNumberOfFloors(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(79, value, "number_of_floors_update");
  }
  deleteNumberOfFloors() {
    return this.setValueFieldById(79, null, "number_of_floors_update");
  }
  get unitInformation() {
    return this.getFieldById(80, UnitInformation.getDefault());
  }
  setUnitInformation(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(80, value, "unit_information_update");
  }
  deleteUnitInformation() {
    return this.setValueFieldById(80, null, "unit_information_update");
  }
  get lihtcRatio() {
    return this.getFieldById(81, Percent.getDefault());
  }
  setLihtcRatio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(81, value, "lihtc_ratio_update");
  }
  deleteLihtcRatio() {
    return this.setValueFieldById(81, null, "lihtc_ratio_update");
  }
  get hudSelectedLtv() {
    return this.getFieldById(82, Percent.getDefault());
  }
  setHudSelectedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(82, value, "hud_selected_ltv_update");
  }
  deleteHudSelectedLtv() {
    return this.setValueFieldById(82, null, "hud_selected_ltv_update");
  }
  get freddieSblSelectedLtv() {
    return this.getFieldById(83, Percent.getDefault());
  }
  setFreddieSblSelectedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(83, value, "freddie_sbl_selected_ltv_update");
  }
  deleteFreddieSblSelectedLtv() {
    return this.setValueFieldById(83, null, "freddie_sbl_selected_ltv_update");
  }
  get freddieSblSelectedDscr() {
    return this.getFieldById(84, Percent.getDefault());
  }
  setFreddieSblSelectedDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(84, value, "freddie_sbl_selected_dscr_update");
  }
  deleteFreddieSblSelectedDscr() {
    return this.setValueFieldById(84, null, "freddie_sbl_selected_dscr_update");
  }
  get freddieSblInterestOnlyTerm() {
    return this.getFieldById(85, 0);
  }
  setFreddieSblInterestOnlyTerm(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(85, value, "freddie_sbl_interest_only_term_update");
  }
  deleteFreddieSblInterestOnlyTerm() {
    return this.setValueFieldById(85, null, "freddie_sbl_interest_only_term_update");
  }
  get freddieSblPrepaymentPenalty() {
    return this.getFieldById(86, 0);
  }
  setFreddieSblPrepaymentPenalty(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(86, value, "freddie_sbl_prepayment_penalty_update");
  }
  deleteFreddieSblPrepaymentPenalty() {
    return this.setValueFieldById(86, null, "freddie_sbl_prepayment_penalty_update");
  }
  get offerExpectations() {
    return this.getFieldById(46, OfferExpectations.getDefault());
  }
  setOfferExpectations(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(46, value, "offer_expectations_update");
  }
  deleteOfferExpectations() {
    return this.setValueFieldById(46, null, "offer_expectations_update");
  }
  get capStackItems() {
    return this.getFieldById(30, RepeatedCapStackItem.getDefault());
  }
  setCapStackItems(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(30, value, "cap_stack_items_update");
  }
  deleteCapStackItems() {
    return this.setValueFieldById(30, null, "cap_stack_items_update");
  }
  get capStackTargets() {
    return this.getFieldById(73, RepeatedCapStackTarget.getDefault());
  }
  setCapStackTargets(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(73, value, "cap_stack_targets_update");
  }
  deleteCapStackTargets() {
    return this.setValueFieldById(73, null, "cap_stack_targets_update");
  }
  get guarantors() {
    return this.getFieldById(72, RepeatedProjectGuarantor.getDefault());
  }
  setGuarantors(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(72, value, "guarantors_update");
  }
  deleteGuarantors() {
    return this.setValueFieldById(72, null, "guarantors_update");
  }
  get solicitedLenders() {
    return this.getFieldById(88, RepeatedInt32OrString.getDefault());
  }
  setSolicitedLenders(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(88, value, "solicited_lenders_update");
  }
  deleteSolicitedLenders() {
    return this.setValueFieldById(88, null, "solicited_lenders_update");
  }
  get sponsorProfile() {
    return this.getFieldById(33, SponsorProfile.getDefault());
  }
  setSponsorProfile(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(33, value, "sponsor_profile_update");
  }
  deleteSponsorProfile() {
    return this.setValueFieldById(33, null, "sponsor_profile_update");
  }
  get note() {
    return this.getFieldById(41, RichText.getDefault());
  }
  setNote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(41, value, "note_update");
  }
  deleteNote() {
    return this.setValueFieldById(41, null, "note_update");
  }
  get acceptedOffer() {
    return this.getFieldById(36, 0);
  }
  setAcceptedOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(36, value, "accepted_offer_update");
  }
  deleteAcceptedOffer() {
    return this.setValueFieldById(36, null, "accepted_offer_update");
  }
  get offerUnderwritingRequested() {
    return this.getFieldById(87, 0);
  }
  setOfferUnderwritingRequested(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(87, value, "offer_underwriting_requested_update");
  }
  deleteOfferUnderwritingRequested() {
    return this.setValueFieldById(87, null, "offer_underwriting_requested_update");
  }
  get leadingOffer() {
    return this.getFieldById(47, 0);
  }
  setLeadingOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(47, value, "leading_offer_update");
  }
  deleteLeadingOffer() {
    return this.setValueFieldById(47, null, "leading_offer_update");
  }
  get occupancy() {
    return this.getFieldById(52, Occupancy.getDefault());
  }
  setOccupancy(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(52, value, "occupancy_update");
  }
  deleteOccupancy() {
    return this.setValueFieldById(52, null, "occupancy_update");
  }
  get uses() {
    return this.getFieldById(53, RepeatedCapStackItem.getDefault());
  }
  setUses(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(53, value, "uses_update");
  }
  deleteUses() {
    return this.setValueFieldById(53, null, "uses_update");
  }
  get disclaimer() {
    return this.getFieldById(54, PropertyDisclaimer.getDefault());
  }
  setDisclaimer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(54, value, "disclaimer_update");
  }
  deleteDisclaimer() {
    return this.setValueFieldById(54, null, "disclaimer_update");
  }
  get demographics() {
    return this.getFieldById(55, PropertyDemographics.getDefault());
  }
  setDemographics(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(55, value, "demographics_update");
  }
  deleteDemographics() {
    return this.setValueFieldById(55, null, "demographics_update");
  }
  get showConcealedShares() {
    return this.getFieldById(58, false);
  }
  setShowConcealedShares(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(58, value, "show_concealed_shares_update");
  }
  deleteShowConcealedShares() {
    return this.setValueFieldById(58, null, "show_concealed_shares_update");
  }
  get projectFee() {
    return this.getFieldById(59, ProjectFee.getDefault());
  }
  setProjectFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(59, value, "project_fee_update");
  }
  deleteProjectFee() {
    return this.setValueFieldById(59, null, "project_fee_update");
  }
  get capitalAdvisors() {
    return this.getFieldById(60, RepeatedUint32.getDefault());
  }
  setCapitalAdvisors(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(60, value, "capital_advisors_update");
  }
  deleteCapitalAdvisors() {
    return this.setValueFieldById(60, null, "capital_advisors_update");
  }
  get capitalAdvisorSyms() {
    return this.getFieldById(104, RepeatedString.getDefault());
  }
  setCapitalAdvisorSyms(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(104, value, "capital_advisors_update");
  }
  deleteCapitalAdvisorSyms() {
    return this.setValueFieldById(104, null, "capital_advisors_update");
  }
  get underwritingApprovalRequirementExceptions() {
    return this.getFieldById(64, RepeatedUnderwritingApprovalRequirement.getDefault());
  }
  setUnderwritingApprovalRequirementExceptions(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(64, value, "underwriting_approval_requirement_exceptions_update");
  }
  deleteUnderwritingApprovalRequirementExceptions() {
    return this.setValueFieldById(64, null, "underwriting_approval_requirement_exceptions_update");
  }
}
_defineProperty$y(ProjectUpdate_, protoName, DEV_MODE ? 'ProjectUpdate' : '[Unknown]');
let ProjectUpdate = ProjectUpdate_;
(function (_ProjectUpdate) {})(ProjectUpdate || (ProjectUpdate = {}));
Object.defineProperties(ProjectUpdate, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 99
      },
      "user_id": {
        value: 69
      },
      "userId": {
        value: 69
      },
      "add_sponsor_user_id": {
        value: 65
      },
      "addSponsorUserId": {
        value: 65
      },
      "add_sponsor_user_sym": {
        value: 100
      },
      "addSponsorUserSym": {
        value: 100
      },
      "invite_sponsor_user_email": {
        value: 66
      },
      "inviteSponsorUserEmail": {
        value: 66
      },
      "remove_sponsor_user_id": {
        value: 67
      },
      "removeSponsorUserId": {
        value: 67
      },
      "remove_sponsor_user_sym": {
        value: 101
      },
      "removeSponsorUserSym": {
        value: 101
      },
      "remove_sponsor_user_email": {
        value: 68
      },
      "removeSponsorUserEmail": {
        value: 68
      },
      "add_broker_user_id": {
        value: 92
      },
      "addBrokerUserId": {
        value: 92
      },
      "add_broker_user_sym": {
        value: 102
      },
      "addBrokerUserSym": {
        value: 102
      },
      "invite_broker_user_email": {
        value: 93
      },
      "inviteBrokerUserEmail": {
        value: 93
      },
      "remove_broker_user_id": {
        value: 94
      },
      "removeBrokerUserId": {
        value: 94
      },
      "remove_broker_user_sym": {
        value: 103
      },
      "removeBrokerUserSym": {
        value: 103
      },
      "remove_broker_user_email": {
        value: 95
      },
      "removeBrokerUserEmail": {
        value: 95
      },
      "status": {
        value: 70
      },
      "lost_reason": {
        value: 71
      },
      "lostReason": {
        value: 71
      },
      "approved": {
        value: 56
      },
      "approval_requested": {
        value: 96
      },
      "approvalRequested": {
        value: 96
      },
      "nda_required": {
        value: 57
      },
      "ndaRequired": {
        value: 57
      },
      "stacksource_agreement_signed": {
        value: 63
      },
      "stacksourceAgreementSigned": {
        value: 63
      },
      "financing_purpose": {
        value: 5
      },
      "financingPurpose": {
        value: 5
      },
      "asset_type": {
        value: 6
      },
      "assetType": {
        value: 6
      },
      "sponsor_role": {
        value: 49
      },
      "sponsorRole": {
        value: 49
      },
      "hotel_flag": {
        value: 89
      },
      "hotelFlag": {
        value: 89
      },
      "hotel_average_daily_rate": {
        value: 90
      },
      "hotelAverageDailyRate": {
        value: 90
      },
      "hotel_average_occupancy_rate": {
        value: 91
      },
      "hotelAverageOccupancyRate": {
        value: 91
      },
      "tenancy": {
        value: 61
      },
      "name": {
        value: 7
      },
      "address": {
        value: 8
      },
      "deprecated_total_project_budget": {
        value: 9
      },
      "deprecatedTotalProjectBudget": {
        value: 9
      },
      "number_of_units": {
        value: 10
      },
      "numberOfUnits": {
        value: 10
      },
      "project_summary": {
        value: 11
      },
      "projectSummary": {
        value: 11
      },
      "total_size": {
        value: 12
      },
      "totalSize": {
        value: 12
      },
      "footprint_size": {
        value: 13
      },
      "footprintSize": {
        value: 13
      },
      "lot_size": {
        value: 14
      },
      "lotSize": {
        value: 14
      },
      "architect": {
        value: 15
      },
      "structural_engineer": {
        value: 42
      },
      "structuralEngineer": {
        value: 42
      },
      "construction_manager": {
        value: 43
      },
      "constructionManager": {
        value: 43
      },
      "general_contractor": {
        value: 37
      },
      "generalContractor": {
        value: 37
      },
      "leasing_representative": {
        value: 44
      },
      "leasingRepresentative": {
        value: 44
      },
      "property_manager": {
        value: 45
      },
      "propertyManager": {
        value: 45
      },
      "completed_year": {
        value: 16
      },
      "completedYear": {
        value: 16
      },
      "renovated_year": {
        value: 51
      },
      "renovatedYear": {
        value: 51
      },
      "floors_above_ground": {
        value: 17
      },
      "floorsAboveGround": {
        value: 17
      },
      "floors_below_ground": {
        value: 18
      },
      "floorsBelowGround": {
        value: 18
      },
      "parking_spots": {
        value: 98
      },
      "parkingSpots": {
        value: 98
      },
      "design_info": {
        value: 19
      },
      "designInfo": {
        value: 19
      },
      "property_title": {
        value: 20
      },
      "propertyTitle": {
        value: 20
      },
      "asset_notes": {
        value: 21
      },
      "assetNotes": {
        value: 21
      },
      "latest_appraisal_value": {
        value: 22
      },
      "latestAppraisalValue": {
        value: 22
      },
      "latest_appraisal_date": {
        value: 38
      },
      "latestAppraisalDate": {
        value: 38
      },
      "target_close_date": {
        value: 97
      },
      "targetCloseDate": {
        value: 97
      },
      "investment_category": {
        value: 76
      },
      "investmentCategory": {
        value: 76
      },
      "current_net_operating_income": {
        value: 39
      },
      "currentNetOperatingIncome": {
        value: 39
      },
      "pro_forma_net_operating_income": {
        value: 75
      },
      "proFormaNetOperatingIncome": {
        value: 75
      },
      "cap_rate": {
        value: 40
      },
      "capRate": {
        value: 40
      },
      "pro_forma_cap_rate": {
        value: 74
      },
      "proFormaCapRate": {
        value: 74
      },
      "financial_notes": {
        value: 23
      },
      "financialNotes": {
        value: 23
      },
      "acquisition_price": {
        value: 24
      },
      "acquisitionPrice": {
        value: 24
      },
      "acquisition_date": {
        value: 25
      },
      "acquisitionDate": {
        value: 25
      },
      "acquisition_status": {
        value: 48
      },
      "acquisitionStatus": {
        value: 48
      },
      "principal_needed": {
        value: 26
      },
      "principalNeeded": {
        value: 26
      },
      "lien_position": {
        value: 27
      },
      "lienPosition": {
        value: 27
      },
      "financial_instruments": {
        value: 28
      },
      "financialInstruments": {
        value: 28
      },
      "affordability": {
        value: 77
      },
      "has_elevator": {
        value: 78
      },
      "hasElevator": {
        value: 78
      },
      "number_of_floors": {
        value: 79
      },
      "numberOfFloors": {
        value: 79
      },
      "unit_information": {
        value: 80
      },
      "unitInformation": {
        value: 80
      },
      "lihtc_ratio": {
        value: 81
      },
      "lihtcRatio": {
        value: 81
      },
      "hud_selected_ltv": {
        value: 82
      },
      "hudSelectedLtv": {
        value: 82
      },
      "freddie_sbl_selected_ltv": {
        value: 83
      },
      "freddieSblSelectedLtv": {
        value: 83
      },
      "freddie_sbl_selected_dscr": {
        value: 84
      },
      "freddieSblSelectedDscr": {
        value: 84
      },
      "freddie_sbl_interest_only_term": {
        value: 85
      },
      "freddieSblInterestOnlyTerm": {
        value: 85
      },
      "freddie_sbl_prepayment_penalty": {
        value: 86
      },
      "freddieSblPrepaymentPenalty": {
        value: 86
      },
      "offer_expectations": {
        value: 46
      },
      "offerExpectations": {
        value: 46
      },
      "cap_stack_items": {
        value: 30
      },
      "capStackItems": {
        value: 30
      },
      "cap_stack_targets": {
        value: 73
      },
      "capStackTargets": {
        value: 73
      },
      "guarantors": {
        value: 72
      },
      "solicited_lenders": {
        value: 88
      },
      "solicitedLenders": {
        value: 88
      },
      "sponsor_profile": {
        value: 33
      },
      "sponsorProfile": {
        value: 33
      },
      "note": {
        value: 41
      },
      "accepted_offer": {
        value: 36
      },
      "acceptedOffer": {
        value: 36
      },
      "offer_underwriting_requested": {
        value: 87
      },
      "offerUnderwritingRequested": {
        value: 87
      },
      "leading_offer": {
        value: 47
      },
      "leadingOffer": {
        value: 47
      },
      "occupancy": {
        value: 52
      },
      "uses": {
        value: 53
      },
      "disclaimer": {
        value: 54
      },
      "demographics": {
        value: 55
      },
      "show_concealed_shares": {
        value: 58
      },
      "showConcealedShares": {
        value: 58
      },
      "project_fee": {
        value: 59
      },
      "projectFee": {
        value: 59
      },
      "capital_advisors": {
        value: 60
      },
      "capitalAdvisors": {
        value: 60
      },
      "capital_advisor_syms": {
        value: 104
      },
      "capitalAdvisorSyms": {
        value: 104
      },
      "underwriting_approval_requirement_exceptions": {
        value: 64
      },
      "underwritingApprovalRequirementExceptions": {
        value: 64
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '99': {
        value: "sym"
      },
      '69': {
        value: "user_id"
      },
      '65': {
        value: "add_sponsor_user_id"
      },
      '100': {
        value: "add_sponsor_user_sym"
      },
      '66': {
        value: "invite_sponsor_user_email"
      },
      '67': {
        value: "remove_sponsor_user_id"
      },
      '101': {
        value: "remove_sponsor_user_sym"
      },
      '68': {
        value: "remove_sponsor_user_email"
      },
      '92': {
        value: "add_broker_user_id"
      },
      '102': {
        value: "add_broker_user_sym"
      },
      '93': {
        value: "invite_broker_user_email"
      },
      '94': {
        value: "remove_broker_user_id"
      },
      '103': {
        value: "remove_broker_user_sym"
      },
      '95': {
        value: "remove_broker_user_email"
      },
      '70': {
        value: "status"
      },
      '71': {
        value: "lost_reason"
      },
      '56': {
        value: "approved"
      },
      '96': {
        value: "approval_requested"
      },
      '57': {
        value: "nda_required"
      },
      '63': {
        value: "stacksource_agreement_signed"
      },
      '5': {
        value: "financing_purpose"
      },
      '6': {
        value: "asset_type"
      },
      '49': {
        value: "sponsor_role"
      },
      '89': {
        value: "hotel_flag"
      },
      '90': {
        value: "hotel_average_daily_rate"
      },
      '91': {
        value: "hotel_average_occupancy_rate"
      },
      '61': {
        value: "tenancy"
      },
      '7': {
        value: "name"
      },
      '8': {
        value: "address"
      },
      '9': {
        value: "deprecated_total_project_budget"
      },
      '10': {
        value: "number_of_units"
      },
      '11': {
        value: "project_summary"
      },
      '12': {
        value: "total_size"
      },
      '13': {
        value: "footprint_size"
      },
      '14': {
        value: "lot_size"
      },
      '15': {
        value: "architect"
      },
      '42': {
        value: "structural_engineer"
      },
      '43': {
        value: "construction_manager"
      },
      '37': {
        value: "general_contractor"
      },
      '44': {
        value: "leasing_representative"
      },
      '45': {
        value: "property_manager"
      },
      '16': {
        value: "completed_year"
      },
      '51': {
        value: "renovated_year"
      },
      '17': {
        value: "floors_above_ground"
      },
      '18': {
        value: "floors_below_ground"
      },
      '98': {
        value: "parking_spots"
      },
      '19': {
        value: "design_info"
      },
      '20': {
        value: "property_title"
      },
      '21': {
        value: "asset_notes"
      },
      '22': {
        value: "latest_appraisal_value"
      },
      '38': {
        value: "latest_appraisal_date"
      },
      '97': {
        value: "target_close_date"
      },
      '76': {
        value: "investment_category"
      },
      '39': {
        value: "current_net_operating_income"
      },
      '75': {
        value: "pro_forma_net_operating_income"
      },
      '40': {
        value: "cap_rate"
      },
      '74': {
        value: "pro_forma_cap_rate"
      },
      '23': {
        value: "financial_notes"
      },
      '24': {
        value: "acquisition_price"
      },
      '25': {
        value: "acquisition_date"
      },
      '48': {
        value: "acquisition_status"
      },
      '26': {
        value: "principal_needed"
      },
      '27': {
        value: "lien_position"
      },
      '28': {
        value: "financial_instruments"
      },
      '77': {
        value: "affordability"
      },
      '78': {
        value: "has_elevator"
      },
      '79': {
        value: "number_of_floors"
      },
      '80': {
        value: "unit_information"
      },
      '81': {
        value: "lihtc_ratio"
      },
      '82': {
        value: "hud_selected_ltv"
      },
      '83': {
        value: "freddie_sbl_selected_ltv"
      },
      '84': {
        value: "freddie_sbl_selected_dscr"
      },
      '85': {
        value: "freddie_sbl_interest_only_term"
      },
      '86': {
        value: "freddie_sbl_prepayment_penalty"
      },
      '46': {
        value: "offer_expectations"
      },
      '30': {
        value: "cap_stack_items"
      },
      '73': {
        value: "cap_stack_targets"
      },
      '72': {
        value: "guarantors"
      },
      '88': {
        value: "solicited_lenders"
      },
      '33': {
        value: "sponsor_profile"
      },
      '41': {
        value: "note"
      },
      '36': {
        value: "accepted_offer"
      },
      '87': {
        value: "offer_underwriting_requested"
      },
      '47': {
        value: "leading_offer"
      },
      '52': {
        value: "occupancy"
      },
      '53': {
        value: "uses"
      },
      '54': {
        value: "disclaimer"
      },
      '55': {
        value: "demographics"
      },
      '58': {
        value: "show_concealed_shares"
      },
      '59': {
        value: "project_fee"
      },
      '60': {
        value: "capital_advisors"
      },
      '104': {
        value: "capital_advisor_syms"
      },
      '64': {
        value: "underwriting_approval_requirement_exceptions"
      }
    }))
  }
});
Object.defineProperties(ProjectUpdate.prototype, {
  oneofs_: {
    value: {
      "sponsor_users_update": [65, 100, 66, 67, 101, 68],
      "broker_users_update": [92, 102, 93, 94, 103, 95],
      "status_update": [70],
      "lost_reason_update": [71],
      "approved_update": [56],
      "approval_requested_update": [96],
      "nda_required_update": [57],
      "stacksource_agreement_signed_update": [63],
      "financing_purpose_update": [5],
      "asset_type_update": [6],
      "sponsor_role_update": [49],
      "hotel_flag_update": [89],
      "hotel_average_daily_rate_update": [90],
      "hotel_average_occupancy_rate_update": [91],
      "tenancy_update": [61],
      "name_update": [7],
      "address_update": [8],
      "deprecated_total_project_budget_update": [9],
      "number_of_units_update": [10],
      "project_summary_update": [11],
      "total_size_update": [12],
      "footprint_size_update": [13],
      "lot_size_update": [14],
      "architect_update": [15],
      "structural_engineer_update": [42],
      "construction_manager_update": [43],
      "general_contractor_update": [37],
      "leasing_representative_update": [44],
      "property_manager_update": [45],
      "completed_year_update": [16],
      "renovated_year_update": [51],
      "floors_above_ground_update": [17],
      "floors_below_ground_update": [18],
      "parking_spots_update": [98],
      "design_info_update": [19],
      "property_title_update": [20],
      "asset_notes_update": [21],
      "latest_appraisal_value_update": [22],
      "latest_appraisal_date_update": [38],
      "target_close_date_update": [97],
      "investment_category_update": [76],
      "current_net_operating_income_update": [39],
      "pro_forma_net_operating_income_update": [75],
      "cap_rate_update": [40],
      "pro_forma_cap_rate_update": [74],
      "financial_notes_update": [23],
      "acquisition_price_update": [24],
      "acquisition_date_update": [25],
      "acquisition_status_update": [48],
      "principal_needed_update": [26],
      "lien_position_update": [27],
      "financial_instruments_update": [28],
      "affordability_update": [77],
      "has_elevator_update": [78],
      "number_of_floors_update": [79],
      "unit_information_update": [80],
      "lihtc_ratio_update": [81],
      "hud_selected_ltv_update": [82],
      "freddie_sbl_selected_ltv_update": [83],
      "freddie_sbl_selected_dscr_update": [84],
      "freddie_sbl_interest_only_term_update": [85],
      "freddie_sbl_prepayment_penalty_update": [86],
      "offer_expectations_update": [46],
      "cap_stack_items_update": [30],
      "cap_stack_targets_update": [73],
      "guarantors_update": [72],
      "solicited_lenders_update": [88],
      "sponsor_profile_update": [33],
      "note_update": [41],
      "accepted_offer_update": [36],
      "offer_underwriting_requested_update": [87],
      "leading_offer_update": [47],
      "occupancy_update": [52],
      "uses_update": [53],
      "disclaimer_update": [54],
      "demographics_update": [55],
      "show_concealed_shares_update": [58],
      "project_fee_update": [59],
      "capital_advisors_update": [60, 104],
      "underwriting_approval_requirement_exceptions_update": [64]
    }
  },
  prototypes_: {
    value: {
      71: ProjectLostReason.prototype,
      89: HotelFlag.prototype,
      90: CurrencyValue.prototype,
      91: Percent.prototype,
      61: ProjectTenancy.prototype,
      8: StandardUsAddress.prototype,
      9: CurrencyValue.prototype,
      11: RichText.prototype,
      12: Area.prototype,
      13: Area.prototype,
      14: Area.prototype,
      19: RichText.prototype,
      21: RichText.prototype,
      22: CurrencyValue.prototype,
      38: SimpleDate.prototype,
      97: SimpleDate.prototype,
      39: CurrencyValue.prototype,
      75: CurrencyValue.prototype,
      40: Percent.prototype,
      74: Percent.prototype,
      23: RichText.prototype,
      24: CurrencyValue.prototype,
      25: SimpleDate.prototype,
      26: CurrencyValueRange.prototype,
      28: FinancialInstruments.prototype,
      80: UnitInformation.prototype,
      81: Percent.prototype,
      82: Percent.prototype,
      83: Percent.prototype,
      84: Percent.prototype,
      46: OfferExpectations.prototype,
      30: RepeatedCapStackItem.prototype,
      73: RepeatedCapStackTarget.prototype,
      72: RepeatedProjectGuarantor.prototype,
      88: RepeatedInt32OrString.prototype,
      33: SponsorProfile.prototype,
      41: RichText.prototype,
      52: Occupancy.prototype,
      53: RepeatedCapStackItem.prototype,
      54: PropertyDisclaimer.prototype,
      55: PropertyDemographics.prototype,
      59: ProjectFee.prototype,
      60: RepeatedUint32.prototype,
      104: RepeatedString.prototype,
      64: RepeatedUnderwritingApprovalRequirement.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectUpdate);
  Object.freeze(ProjectUpdate.prototype);
}

function createProjectUpdate(data) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const update = Object.assign({}, data);
  if (update.hotelFlag && update.hotelFlag.oragnization) {
    update.hotelFlag.organizationId = symToId(update.hotelFlag.organization);
    delete update.hotelFlag.organizationId;
  }
  if (update.tenancy && update.tenancy.tenant && update.tenancy.tenant.sym) {
    update.tenancy.tenant.intValue = symToId(update.tenancy.tenant.sym);
    delete update.tenancy.tenant.sym;
  }
  if (update.solicitedLenders) {
    update.solicitedLenders.value = update.solicitedLenders.value.map(symOrStringToInt32OrString);
  }
  if ('acceptedOffer' in update) {
    if (!update.acceptedOffer) {
      update.acceptedOffer = 0;
    } else {
      update.acceptedOffer = symToId(update.acceptedOffer);
    }
  }
  if ('investmentCategory' in update) {
    if (typeof update.investmentCategory === 'string') {
      update.investmentCategory = InvestmentCategory.fromString(update.investmentCategory);
    }
    if (typeof update.investmentCategory === 'number') {
      switch (update.investmentCategory) {
        case InvestmentCategory.UNSET:
          update.investmentCategory = '';
          break;
        case InvestmentCategory.STABILIZED:
          update.investmentCategory = 'Stabilized';
          break;
        case InvestmentCategory.VALUE_ADD:
          update.investmentCategory = 'Value Add';
          break;
        case InvestmentCategory.VACANT:
          update.investmentCategory = 'Vacant';
          break;
        default:
          throw new Error(tmsg`Unknown investment category: ${update.investmentCategory}`);
      }
    }
  }
  if ('capStack' in update) {
    const capStack = update.capStack;
    if ('existingItem' in capStack) {
      update.uses = {
        value: capStack.existingItem
      };
    }
    if ('source' in capStack) {
      update.capStackItems = {
        value: capStack.source
      };
    }
    if ('use' in capStack) {
      update.capStackTargets = {
        value: capStack.use
      };
    }
    delete update.capStack;
  }
  if ('capitalAdvisorSyms' in update) {
    // Convert to RepeatedString
    const capitalAdvisorSyms = update.capitalAdvisorSyms;
    update.capitalAdvisorSyms = {
      value: capitalAdvisorSyms
    };
  }
  return ProjectUpdate.from(update);
}

function _defineProperty$x(obj, key, value) { key = _toPropertyKey$x(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$x(arg) { var key = _toPrimitive$x(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$x(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class RemoteUpdateManager {
  constructor(queueManager, updateRemote, timeoutLength = 10000) {
    _defineProperty$x(this, "update", void 0);
    _defineProperty$x(this, "updateRemote", void 0);
    _defineProperty$x(this, "timeoutLength", void 0);
    _defineProperty$x(this, "queueManager", void 0);
    _defineProperty$x(this, "emitter", void 0);
    _defineProperty$x(this, "promiseResolvers", void 0);
    this.queueManager = queueManager;
    this.updateRemote = updateRemote;
    this.timeoutLength = timeoutLength;
    this.update = debounce(this._update, 1500);
    this.emitter = new BaseEventEmitter();
    this.promiseResolvers = [];
  }
  addUpdate(update) {
    this.queueManager.dispatch(update);
    this.update();
    return new Promise((resolve, reject) => {
      this.promiseResolvers.push({
        resolve,
        reject
      });
    });
  }
  _update() {
    if (this.queueManager.isPending) {
      this.update();
      return;
    }
    this.sendUpdate();
  }
  async sendUpdate() {
    const {
      update,
      callback
    } = this.queueManager.getUpdate();
    if (update == null) {
      return;
    }
    try {
      await this.updateRemote(update);
    } catch (e) {
      assert(callback)(false /* removeUpdates */);
      const resolvers = this.promiseResolvers;
      this.promiseResolvers = [];
      for (const {
        reject
      } of resolvers) {
        reject(e);
      }
      this.emitter.emit('save', true /* error */);
      throw e;
    }
    assert(callback)(true /* removeUpdates */);
    if (this.queueManager.isQueueEmpty()) {
      const resolvers = this.promiseResolvers;
      this.promiseResolvers = [];
      for (const {
        resolve
      } of resolvers) {
        resolve();
      }
      this.emitter.emit('save', false /* error */);
    }
  }

  addSaveListener(callback) {
    return this.emitter.addListener('save', callback);
  }
}

function _defineProperty$w(obj, key, value) { key = _toPropertyKey$w(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$w(arg) { var key = _toPrimitive$w(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$w(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class UpdateQueueManager {
  constructor(mergeUpdate) {
    _defineProperty$w(this, "mergeUpdate", void 0);
    _defineProperty$w(this, "updates", void 0);
    _defineProperty$w(this, "_isPending", void 0);
    this.mergeUpdate = mergeUpdate;
    this.updates = new Array();
    this._isPending = false;
  }
  get isPending() {
    return this._isPending;
  }
  dispatch(update) {
    this.updates.push(update);
  }
  getUpdate() {
    assert(!this._isPending);
    const updateCount = this.updates.length;
    if (updateCount === 0) {
      return {
        update: null,
        callback: null
      };
    }
    this._isPending = true;
    const update = this.updates.reduce(this.mergeUpdate);
    const callback = removeUpdates => {
      if (removeUpdates) {
        this.updates.splice(0, updateCount);
      }
      this._isPending = false;
    };
    return {
      update,
      callback
    };
  }
  isQueueEmpty() {
    return this.updates.length === 0;
  }
}

function _defineProperty$v(obj, key, value) { key = _toPropertyKey$v(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$v(arg) { var key = _toPrimitive$v(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$v(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class DbValueFormatter {
  // It would make more sense for this to be declared as `abstract static`, but
  // that's not supported by TypeScript.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static format(_) {
    throw new Error('DbValueFormatter.format must be implemented by a subclass');
  }
  constructor(value) {
    _defineProperty$v(this, 'rawType', true);
    _defineProperty$v(this, "value", void 0);
    this.value = value;
    this.rawType = true;
  }
  toPostgres() {
    return this.constructor.format(this.value);
  }
}

function _defineProperty$u(obj, key, value) { key = _toPropertyKey$u(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$u(arg) { var key = _toPrimitive$u(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$u(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class IterableFormatter {
  static format(values, formatItem, castArrayType) {
    const formatted = formatItems(values, formatItem);
    if (castArrayType) {
      return `array[${formatted}]::${castArrayType}[]`;
    }
    return `array[${formatted}]`;
  }
  constructor(values, formatItem, castArrayType) {
    _defineProperty$u(this, 'rawType', true);
    _defineProperty$u(this, "values", void 0);
    _defineProperty$u(this, "formatItem", void 0);
    _defineProperty$u(this, "castArrayType", void 0);
    this.values = values;
    this.formatItem = formatItem;
    this.castArrayType = castArrayType;
  }
  toPostgres() {
    return IterableFormatter.format(this.values, this.formatItem, this.castArrayType);
  }
}
function formatItems(values, formatItem) {
  const out = [];
  for (const value of values) {
    out.push(formatItem(value));
  }
  return out.join(', ');
}

// TODO(andrew.bowers) figure out solutions for all of these anys
const splicers = [];
function createFormatter(kind, value) {
  for (const splice of splicers) {
    const result = splice(kind, value);
    if (result) {
      return result;
    }
  }
  return null;
}
function registerFormatter(test, constructor) {
  splicers.push((kind, value) => test(kind, value) ? new constructor(value) : null);
}

// Kind formatters must be spliced first so that enum formatters are
// discovered before `NumberFormatter`.
// TODO(nathan): Come up with a less fragile way to manage which formatters
// take precedence over others.

function registerKindFormatter(test, constructor) {
  splicers.unshift((kind, value) => test(kind, value) ? new constructor(value) : null);
}
function registerValueFormatter(test, constructor) {
  registerFormatter((kind, value) => test(value), constructor);
}
function registerTypeFormatter(type, constructor) {
  registerValueFormatter(value => typeof value === type, constructor);
}
function registerInstanceFormatter(instanceConstructor, formatterConstructor) {
  registerValueFormatter(value => value instanceof instanceConstructor, formatterConstructor);
}
function registerIterableInstanceFormatter(instanceConstructor, formatterConstructor) {
  registerKindFormatter(kind => kind && kind.iterable && kind.constructor === instanceConstructor, formatterConstructor);
}
function registerEnumFormatter(enumType, constructor) {
  registerKindFormatter((kind, value) => {
    if (kind === enumType) {
      if (!enumType.has(value)) {
        throw new Error(`Expected value ${JSON.stringify(value)} could not be found in ` + 'enum.');
      }
      return true;
    }
    return false;
  }, constructor);
}
function registerRepeatedFormatter(repeatedConstructor, instanceFormatter, castArrayType) {
  registerInstanceFormatter(repeatedConstructor, createRepeatedFormatter(instanceFormatter.format, castArrayType));
}
function createRepeatedFormatter(formatItem, castArrayType) {
  return class RepeatedFormatter extends DbValueFormatter {
    static format(value) {
      return IterableFormatter.format(value.valueList, formatItem, castArrayType);
    }
  };
}

function _defineProperty$t(obj, key, value) { key = _toPropertyKey$t(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$t(arg) { var key = _toPrimitive$t(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$t(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class FieldUpdater {
  // eslint-disable-next-line max-params
  constructor(columnName, test, toDbValue, updateDb, applyUpdate, mergeUpdate) {
    _defineProperty$t(this, "columnName", void 0);
    _defineProperty$t(this, "test", void 0);
    _defineProperty$t(this, "toDbValue", void 0);
    _defineProperty$t(this, "updateDb", void 0);
    _defineProperty$t(this, "applyUpdate", void 0);
    _defineProperty$t(this, "mergeUpdate", void 0);
    this.columnName = columnName;
    this.test = test;
    this.toDbValue = toDbValue;
    this.updateDb = updateDb;
    this.applyUpdate = applyUpdate;
    this.mergeUpdate = mergeUpdate;
  }
}

class UpdateManager {
  static applyUpdates(dataProto, update) {
    const _this = this;
    let newDataProto = dataProto;
    for (const fieldUpdater of _this.fieldUpdaters) {
      if (fieldUpdater.test(update) && fieldUpdater.applyUpdate) {
        newDataProto = fieldUpdater.applyUpdate(newDataProto, update);
      }
    }
    return newDataProto;
  }
  static mergeUpdate(previousUpdate, newUpdate) {
    const _this = this;
    let mergedUpdate = previousUpdate;
    for (const fieldUpdater of _this.fieldUpdaters) {
      if (fieldUpdater.test(newUpdate) && fieldUpdater.mergeUpdate) {
        mergedUpdate = fieldUpdater.mergeUpdate(mergedUpdate, newUpdate);
      }
    }
    return mergedUpdate;
  }
  constructor(fieldUpdaters, update) {
    const _this = this;
    _this.setters_ = new Map();
    _this.dbUpdaters_ = new Array();
    for (const fieldUpdater of fieldUpdaters) {
      if (fieldUpdater.test(update)) {
        if (fieldUpdater.columnName) {
          var _context;
          let $2, $1, $3;
          runtime.checkAsserts() ? runtime.ensure(fieldUpdater.toDbValue, function () {
            return `Missing value converter for field updater on column ${JSON.stringify(fieldUpdater.columnName)}.`;
          }) : undefined;
          _this.setters_.set(fieldUpdater.columnName.toLowerCase().trim(), (($2 = ($3 = fieldUpdater, (_context = $3, $3.toDbValue).bind(_context)), $1 = update), function ($0) {
            return $2($0, $1);
          }));
        } else {
          var _context2;
          let $6, $5, $7;
          runtime.checkAsserts() ? runtime.ensure(!fieldUpdater.toDbValue, function () {
            return `Missing column name for field updater with defined value converter.`;
          }) : undefined;
          runtime.checkAsserts() ? runtime.ensure(fieldUpdater.updateDb, function () {
            return `Missing configuration on field updater.`;
          }) : undefined;
          _this.dbUpdaters_.push((($6 = ($7 = fieldUpdater, (_context2 = $7, $7.updateDb).bind(_context2)), $5 = update), function ($4) {
            return $6($4, $5);
          }));
        }
      }
    }
  }
  setField(name, value) {
    const _this = this;
    _this.setters_.set(name.toLowerCase().trim(), function () {
      return value;
    });
  }
  update(tx, table, whereClause) {
    const _this = this;
    return async(function* () {
      const [setFragment] = yield Promise.all([_this.injectSetters(tx), _this.updateConfiguredFields(tx)]);
      if (setFragment) {
        yield tx.none`
        UPDATE $_unsafe_inject_${table}
            SET ${setFragment}
            WHERE ${whereClause}`;
      }
    }());
  }
  injectSetters(tx) {
    const _this = this;
    return async(function* () {
      if (_this.setters_.size == 0) {
        return null;
      }
      return tx.joinFragments(', ', yield Promise.all(function* () {
        for (const [columnName, getValue] of _this.setters_.entries()) {
          yield _this.injectSetter(tx, columnName, getValue);
        }
      }()));
    }());
  }
  injectSetter(tx, columnName, getValue) {
    const _this = this;
    return async(function* () {
      return tx.fragment`
      $_unsafe_inject_${columnName} = ${yield getValue(tx)}`;
    }());
  }
  updateConfiguredFields(tx) {
    const _this = this;
    return Promise.all(function* () {
      for (const updateDb of _this.dbUpdaters_) {
        yield updateDb(tx);
      }
    }());
  }
}

function applyGuarantorsUpdate(dataProto, updateProto) {
  runtime.checkAsserts() ? runtime.ensure(updateProto.guarantorsUpdateCase == ProjectUpdate.GuarantorsUpdateCase.GUARANTORS, null) : undefined;
  return dataProto.setGuarantorList(updateProto.guarantors.valueList);
}
function applySponsorUsersUpdate(dataProto, updateProto) {
  const Case = ProjectUpdate.SponsorUsersUpdateCase;
  switch (updateProto.sponsorUsersUpdateCase) {
    case Case.ADD_SPONSOR_USER_ID:
      runtime.checkAsserts() ? runtime.ensure(!dataProto.sponsorUserList.some(function (u) {
        return u.int32Value == updateProto.addSponsorUserId;
      }), null) : undefined;
      return dataProto.addSponsorUser(new Int32OrString().setInt32Value(updateProto.addSponsorUserId));
      break;
    case Case.INVITE_SPONSOR_USER_EMAIL:
      runtime.checkAsserts() ? runtime.ensure(!dataProto.sponsorUserList.some(function (u) {
        return u.stringValue == updateProto.inviteSponsorUserEmail;
      }), null) : undefined;
      return dataProto.addSponsorUser(new Int32OrString().setStringValue(updateProto.inviteSponsorUserEmail));
      break;
    case Case.REMOVE_SPONSOR_USER_ID:
      runtime.checkAsserts() ? runtime.ensure(dataProto.sponsorUserList.some(function (u) {
        return u.int32Value == updateProto.removeSponsorUserId;
      }), null) : undefined;
      return dataProto.setSponsorUserList(dataProto.sponsorUserList.filter(function (u) {
        return u.int32Value != updateProto.removeSponsorUserId;
      }));
      break;
    case Case.REMOVE_SPONSOR_USER_EMAIL:
      runtime.checkAsserts() ? runtime.ensure(dataProto.sponsorUserList.some(function (u) {
        return u.stringValue == updateProto.removeSponsorUserEmail;
      }), null) : undefined;
      return dataProto.setSponsorUserList(dataProto.sponsorUserList.filter(function (u) {
        return u.stringValue != updateProto.removeSponsorUserEmail;
      }));
      break;
    case Case.UNSET:
      break;
    default:
      runtime.checkAsserts() ? runtime.ensure(false, function () {
        return `Unknown sponsor user update case (${updateProto.sponsorUsersUpdateCase}).`;
      }) : undefined;
      break;
  }
}
function applyBrokerUsersUpdate(dataProto, updateProto) {
  const Case = ProjectUpdate.BrokerUsersUpdateCase;
  switch (updateProto.brokerUsersUpdateCase) {
    case Case.ADD_BROKER_USER_ID:
      runtime.checkAsserts() ? runtime.ensure(!dataProto.brokerUserList.some(function (u) {
        return u.int32Value == updateProto.addBrokerUserId;
      }), null) : undefined;
      return dataProto.addBrokerUser(new Int32OrString().setInt32Value(updateProto.addBrokerUserId));
      break;
    case Case.INVITE_BROKER_USER_EMAIL:
      runtime.checkAsserts() ? runtime.ensure(!dataProto.brokerUserList.some(function (u) {
        return u.stringValue == updateProto.inviteBrokerUserEmail;
      }), null) : undefined;
      return dataProto.addBrokerUser(new Int32OrString().setStringValue(updateProto.inviteBrokerUserEmail));
      break;
    case Case.REMOVE_BROKER_USER_ID:
      runtime.checkAsserts() ? runtime.ensure(dataProto.brokerUserList.some(function (u) {
        return u.int32Value == updateProto.removeBrokerUserId;
      }), null) : undefined;
      return dataProto.setBrokerUserList(dataProto.brokerUserList.filter(function (u) {
        return u.int32Value != updateProto.removeBrokerUserId;
      }));
      break;
    case Case.REMOVE_BROKER_USER_EMAIL:
      runtime.checkAsserts() ? runtime.ensure(dataProto.brokerUserList.some(function (u) {
        return u.stringValue == updateProto.removeBrokerUserEmail;
      }), null) : undefined;
      return dataProto.setBrokerUserList(dataProto.brokerUserList.filter(function (u) {
        return u.stringValue != updateProto.removeBrokerUserEmail;
      }));
      break;
    case Case.UNSET:
      break;
    default:
      runtime.checkAsserts() ? runtime.ensure(false, function () {
        return `Unknown broker user update case (${updateProto.brokerUsersUpdateCase}).`;
      }) : undefined;
      break;
  }
}
function applyCapStackItemsUpdate(dataProto, updateProto) {
  if (updateProto.capStackItemsUpdateCase == ProjectUpdate.CapStackItemsUpdateCase.CAP_STACK_ITEMS) {
    return dataProto.setCapStackItemList(updateProto.capStackItems.valueList);
  }
}
function applyCapStackTargetsUpdate(dataProto, updateProto) {
  if (updateProto.capStackTargetsUpdateCase == ProjectUpdate.CapStackTargetsUpdateCase.CAP_STACK_TARGETS) {
    return dataProto.setCapStackTargetList(updateProto.capStackTargets.valueList);
  }
}
function applyUsesUpdate(dataProto, updateProto) {
  if (updateProto.usesUpdateCase == ProjectUpdate.UsesUpdateCase.USES) {
    return dataProto.setUseList(updateProto.uses.valueList);
  }
}
function applySolicitedLendersUpdate(dataProto, updateProto) {
  if (updateProto.solicitedLendersUpdateCase == ProjectUpdate.SolicitedLendersUpdateCase.SOLICITED_LENDERS) {
    return dataProto.setSolicitedLenderList(updateProto.solicitedLenders.valueList);
  }
}
function applyCapitalAdvisorsUpdate(dataProto, updateProto) {
  if (updateProto.capitalAdvisorsUpdateCase == ProjectUpdate.CapitalAdvisorsUpdateCase.CAPITAL_ADVISORS) {
    return dataProto.setCapitalAdvisorList(updateProto.capitalAdvisors.valueList);
  }
}
function applyUnderwritingApprovalRequirementExceptionsUpdate(dataProto, updateProto) {
  if (updateProto.underwritingApprovalRequirementExceptionsUpdateCase == ProjectUpdate.UnderwritingApprovalRequirementExceptionsUpdateCase.UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS) {
    return dataProto.setUnderwritingApprovalRequirementExceptionList(updateProto.underwritingApprovalRequirementExceptions.valueList);
  }
}
function applyAcceptedOfferUpdate(dataProto, updateProto) {
  if (updateProto.acceptedOfferUpdateCase == ProjectUpdate.AcceptedOfferUpdateCase.ACCEPTED_OFFER) {
    return dataProto.setAcceptedOffer(updateProto.acceptedOffer).setOfferAcceptedTime(getCurrentDate());
  }
}
function applyApprovedUpdate(dataProto, updateProto) {
  if (updateProto.approvedUpdateCase == ProjectUpdate.ApprovedUpdateCase.APPROVED) {
    if (updateProto.approved) {
      return dataProto.setApproved(updateProto.approved);
    }
    return dataProto.setApproved(updateProto.approved).setApprovalRequested(updateProto.approvalRequested);
  }
}
function applyApprovalRequestedUpdate(dataProto, updateProto) {
  if (updateProto.approvalRequestedUpdateCase == ProjectUpdate.ApprovalRequestedUpdateCase.APPROVAL_REQUESTED) {
    return dataProto.setApprovalRequested(updateProto.approvalRequested).setApprovalRequestedBy(updateProto.approvalRequested ? updateProto.userId : undefined);
  }
}

const FIELD_UPDATERS = [new FieldUpdater(null, function (updateProto) {
  return updateProto.sponsorUsersUpdateCase != ProjectUpdate.SponsorUsersUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applySponsorUsersUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.sponsorUsersUpdateCase == ProjectUpdate.SponsorUsersUpdateCase.ADD_SPONSOR_USER_ID) {
    return currentProto.setAddSponsorUserId(updateProto.addSponsorUserId);
  }
  if (updateProto.sponsorUsersUpdateCase == ProjectUpdate.SponsorUsersUpdateCase.ADD_SPONSOR_USER_SYM) {
    return currentProto.setAddSponsorUserSym(updateProto.addSponsorUserSym);
  }
  if (updateProto.sponsorUsersUpdateCase == ProjectUpdate.SponsorUsersUpdateCase.INVITE_SPONSOR_USER_EMAIL) {
    return currentProto.setInviteSponsorUserEmail(updateProto.inviteSponsorUserEmail);
  }
  if (updateProto.sponsorUsersUpdateCase == ProjectUpdate.SponsorUsersUpdateCase.REMOVE_SPONSOR_USER_ID) {
    return currentProto.setRemoveSponsorUserId(updateProto.removeSponsorUserId);
  }
  if (updateProto.sponsorUsersUpdateCase == ProjectUpdate.SponsorUsersUpdateCase.REMOVE_SPONSOR_USER_SYM) {
    return currentProto.setRemoveSponsorUserSym(updateProto.removeSponsorUserSym);
  }
  if (updateProto.sponsorUsersUpdateCase == ProjectUpdate.SponsorUsersUpdateCase.REMOVE_SPONSOR_USER_EMAIL) {
    return currentProto.setRemoveSponsorUserEmail(updateProto.removeSponsorUserEmail);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.brokerUsersUpdateCase != ProjectUpdate.BrokerUsersUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyBrokerUsersUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.brokerUsersUpdateCase == ProjectUpdate.BrokerUsersUpdateCase.ADD_BROKER_USER_ID) {
    return currentProto.setAddBrokerUserId(updateProto.addBrokerUserId);
  }
  if (updateProto.brokerUsersUpdateCase == ProjectUpdate.BrokerUsersUpdateCase.ADD_BROKER_USER_SYM) {
    return currentProto.setAddBrokerUserSym(updateProto.addBrokerUserSym);
  }
  if (updateProto.brokerUsersUpdateCase == ProjectUpdate.BrokerUsersUpdateCase.INVITE_BROKER_USER_EMAIL) {
    return currentProto.setInviteBrokerUserEmail(updateProto.inviteBrokerUserEmail);
  }
  if (updateProto.brokerUsersUpdateCase == ProjectUpdate.BrokerUsersUpdateCase.REMOVE_BROKER_USER_ID) {
    return currentProto.setRemoveBrokerUserId(updateProto.removeBrokerUserId);
  }
  if (updateProto.brokerUsersUpdateCase == ProjectUpdate.BrokerUsersUpdateCase.REMOVE_BROKER_USER_SYM) {
    return currentProto.setRemoveBrokerUserSym(updateProto.removeBrokerUserSym);
  }
  if (updateProto.brokerUsersUpdateCase == ProjectUpdate.BrokerUsersUpdateCase.REMOVE_BROKER_USER_EMAIL) {
    return currentProto.setRemoveBrokerUserEmail(updateProto.removeBrokerUserEmail);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.statusUpdateCase != ProjectUpdate.StatusUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.statusUpdateCase == ProjectUpdate.StatusUpdateCase.STATUS) {
    return dataProto.setStatus(updateProto.status);
  }
}, function (currentProto, updateProto) {
  if (updateProto.statusUpdateCase == ProjectUpdate.StatusUpdateCase.STATUS) {
    return currentProto.setStatus(updateProto.status);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.lostReasonUpdateCase != ProjectUpdate.LostReasonUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.lostReasonUpdateCase == ProjectUpdate.LostReasonUpdateCase.LOST_REASON) {
    return dataProto.setLostReason(updateProto.lostReason);
  }
}, function (currentProto, updateProto) {
  if (updateProto.lostReasonUpdateCase == ProjectUpdate.LostReasonUpdateCase.LOST_REASON) {
    return currentProto.setLostReason(updateProto.lostReason);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.approvedUpdateCase != ProjectUpdate.ApprovedUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyApprovedUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.approvedUpdateCase == ProjectUpdate.ApprovedUpdateCase.APPROVED) {
    return currentProto.setApproved(updateProto.approved);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.approvalRequestedUpdateCase != ProjectUpdate.ApprovalRequestedUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyApprovalRequestedUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.approvalRequestedUpdateCase == ProjectUpdate.ApprovalRequestedUpdateCase.APPROVAL_REQUESTED) {
    return currentProto.setApprovalRequested(updateProto.approvalRequested);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.ndaRequiredUpdateCase != ProjectUpdate.NdaRequiredUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.ndaRequiredUpdateCase == ProjectUpdate.NdaRequiredUpdateCase.NDA_REQUIRED) {
    return dataProto.setNdaRequired(updateProto.ndaRequired);
  }
}, function (currentProto, updateProto) {
  if (updateProto.ndaRequiredUpdateCase == ProjectUpdate.NdaRequiredUpdateCase.NDA_REQUIRED) {
    return currentProto.setNdaRequired(updateProto.ndaRequired);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.stacksourceAgreementSignedUpdateCase != ProjectUpdate.StacksourceAgreementSignedUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.stacksourceAgreementSignedUpdateCase == ProjectUpdate.StacksourceAgreementSignedUpdateCase.STACKSOURCE_AGREEMENT_SIGNED) {
    return dataProto.setStacksourceAgreementSigned(updateProto.stacksourceAgreementSigned);
  }
}, function (currentProto, updateProto) {
  if (updateProto.stacksourceAgreementSignedUpdateCase == ProjectUpdate.StacksourceAgreementSignedUpdateCase.STACKSOURCE_AGREEMENT_SIGNED) {
    return currentProto.setStacksourceAgreementSigned(updateProto.stacksourceAgreementSigned);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.financingPurposeUpdateCase != ProjectUpdate.FinancingPurposeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.financingPurposeUpdateCase == ProjectUpdate.FinancingPurposeUpdateCase.FINANCING_PURPOSE) {
    return dataProto.setFinancingPurpose(updateProto.financingPurpose);
  }
}, function (currentProto, updateProto) {
  if (updateProto.financingPurposeUpdateCase == ProjectUpdate.FinancingPurposeUpdateCase.FINANCING_PURPOSE) {
    return currentProto.setFinancingPurpose(updateProto.financingPurpose);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.assetTypeUpdateCase != ProjectUpdate.AssetTypeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.assetTypeUpdateCase == ProjectUpdate.AssetTypeUpdateCase.ASSET_TYPE) {
    return dataProto.setAssetType(updateProto.assetType);
  }
}, function (currentProto, updateProto) {
  if (updateProto.assetTypeUpdateCase == ProjectUpdate.AssetTypeUpdateCase.ASSET_TYPE) {
    return currentProto.setAssetType(updateProto.assetType);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.sponsorRoleUpdateCase != ProjectUpdate.SponsorRoleUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.sponsorRoleUpdateCase == ProjectUpdate.SponsorRoleUpdateCase.SPONSOR_ROLE) {
    return dataProto.setSponsorRole(updateProto.sponsorRole);
  }
}, function (currentProto, updateProto) {
  if (updateProto.sponsorRoleUpdateCase == ProjectUpdate.SponsorRoleUpdateCase.SPONSOR_ROLE) {
    return currentProto.setSponsorRole(updateProto.sponsorRole);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.hotelFlagUpdateCase != ProjectUpdate.HotelFlagUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.hotelFlagUpdateCase == ProjectUpdate.HotelFlagUpdateCase.HOTEL_FLAG) {
    return dataProto.setHotelFlag(updateProto.hotelFlag);
  }
}, function (currentProto, updateProto) {
  if (updateProto.hotelFlagUpdateCase == ProjectUpdate.HotelFlagUpdateCase.HOTEL_FLAG) {
    return currentProto.setHotelFlag(updateProto.hotelFlag);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.hotelAverageDailyRateUpdateCase != ProjectUpdate.HotelAverageDailyRateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.hotelAverageDailyRateUpdateCase == ProjectUpdate.HotelAverageDailyRateUpdateCase.HOTEL_AVERAGE_DAILY_RATE) {
    return dataProto.setHotelAverageDailyRate(updateProto.hotelAverageDailyRate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.hotelAverageDailyRateUpdateCase == ProjectUpdate.HotelAverageDailyRateUpdateCase.HOTEL_AVERAGE_DAILY_RATE) {
    return currentProto.setHotelAverageDailyRate(updateProto.hotelAverageDailyRate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.hotelAverageOccupancyRateUpdateCase != ProjectUpdate.HotelAverageOccupancyRateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.hotelAverageOccupancyRateUpdateCase == ProjectUpdate.HotelAverageOccupancyRateUpdateCase.HOTEL_AVERAGE_OCCUPANCY_RATE) {
    return dataProto.setHotelAverageOccupancyRate(updateProto.hotelAverageOccupancyRate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.hotelAverageOccupancyRateUpdateCase == ProjectUpdate.HotelAverageOccupancyRateUpdateCase.HOTEL_AVERAGE_OCCUPANCY_RATE) {
    return currentProto.setHotelAverageOccupancyRate(updateProto.hotelAverageOccupancyRate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.tenancyUpdateCase != ProjectUpdate.TenancyUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.tenancyUpdateCase == ProjectUpdate.TenancyUpdateCase.TENANCY) {
    return dataProto.setTenancy(updateProto.tenancy);
  }
}, function (currentProto, updateProto) {
  if (updateProto.tenancyUpdateCase == ProjectUpdate.TenancyUpdateCase.TENANCY) {
    return currentProto.setTenancy(updateProto.tenancy);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.nameUpdateCase != ProjectUpdate.NameUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.nameUpdateCase == ProjectUpdate.NameUpdateCase.NAME) {
    return dataProto.setName(updateProto.name);
  }
}, function (currentProto, updateProto) {
  if (updateProto.nameUpdateCase == ProjectUpdate.NameUpdateCase.NAME) {
    return currentProto.setName(updateProto.name);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.addressUpdateCase != ProjectUpdate.AddressUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.addressUpdateCase == ProjectUpdate.AddressUpdateCase.ADDRESS) {
    return dataProto.setAddress(updateProto.address);
  }
}, function (currentProto, updateProto) {
  if (updateProto.addressUpdateCase == ProjectUpdate.AddressUpdateCase.ADDRESS) {
    return currentProto.setAddress(updateProto.address);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.deprecatedTotalProjectBudgetUpdateCase != ProjectUpdate.DeprecatedTotalProjectBudgetUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.deprecatedTotalProjectBudgetUpdateCase == ProjectUpdate.DeprecatedTotalProjectBudgetUpdateCase.DEPRECATED_TOTAL_PROJECT_BUDGET) {
    return dataProto.setDeprecatedTotalProjectBudget(updateProto.deprecatedTotalProjectBudget);
  }
}, function (currentProto, updateProto) {
  if (updateProto.deprecatedTotalProjectBudgetUpdateCase == ProjectUpdate.DeprecatedTotalProjectBudgetUpdateCase.DEPRECATED_TOTAL_PROJECT_BUDGET) {
    return currentProto.setDeprecatedTotalProjectBudget(updateProto.deprecatedTotalProjectBudget);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.numberOfUnitsUpdateCase != ProjectUpdate.NumberOfUnitsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.numberOfUnitsUpdateCase == ProjectUpdate.NumberOfUnitsUpdateCase.NUMBER_OF_UNITS) {
    return dataProto.setNumberOfUnits(updateProto.numberOfUnits);
  }
}, function (currentProto, updateProto) {
  if (updateProto.numberOfUnitsUpdateCase == ProjectUpdate.NumberOfUnitsUpdateCase.NUMBER_OF_UNITS) {
    return currentProto.setNumberOfUnits(updateProto.numberOfUnits);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.projectSummaryUpdateCase != ProjectUpdate.ProjectSummaryUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.projectSummaryUpdateCase == ProjectUpdate.ProjectSummaryUpdateCase.PROJECT_SUMMARY) {
    return dataProto.setProjectSummary(updateProto.projectSummary);
  }
}, function (currentProto, updateProto) {
  if (updateProto.projectSummaryUpdateCase == ProjectUpdate.ProjectSummaryUpdateCase.PROJECT_SUMMARY) {
    return currentProto.setProjectSummary(updateProto.projectSummary);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.totalSizeUpdateCase != ProjectUpdate.TotalSizeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.totalSizeUpdateCase == ProjectUpdate.TotalSizeUpdateCase.TOTAL_SIZE) {
    return dataProto.setTotalSize(updateProto.totalSize);
  }
}, function (currentProto, updateProto) {
  if (updateProto.totalSizeUpdateCase == ProjectUpdate.TotalSizeUpdateCase.TOTAL_SIZE) {
    return currentProto.setTotalSize(updateProto.totalSize);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.footprintSizeUpdateCase != ProjectUpdate.FootprintSizeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.footprintSizeUpdateCase == ProjectUpdate.FootprintSizeUpdateCase.FOOTPRINT_SIZE) {
    return dataProto.setFootprintSize(updateProto.footprintSize);
  }
}, function (currentProto, updateProto) {
  if (updateProto.footprintSizeUpdateCase == ProjectUpdate.FootprintSizeUpdateCase.FOOTPRINT_SIZE) {
    return currentProto.setFootprintSize(updateProto.footprintSize);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.lotSizeUpdateCase != ProjectUpdate.LotSizeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.lotSizeUpdateCase == ProjectUpdate.LotSizeUpdateCase.LOT_SIZE) {
    return dataProto.setLotSize(updateProto.lotSize);
  }
}, function (currentProto, updateProto) {
  if (updateProto.lotSizeUpdateCase == ProjectUpdate.LotSizeUpdateCase.LOT_SIZE) {
    return currentProto.setLotSize(updateProto.lotSize);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.architectUpdateCase != ProjectUpdate.ArchitectUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.architectUpdateCase == ProjectUpdate.ArchitectUpdateCase.ARCHITECT) {
    return dataProto.setArchitect(updateProto.architect);
  }
}, function (currentProto, updateProto) {
  if (updateProto.architectUpdateCase == ProjectUpdate.ArchitectUpdateCase.ARCHITECT) {
    return currentProto.setArchitect(updateProto.architect);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.structuralEngineerUpdateCase != ProjectUpdate.StructuralEngineerUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.structuralEngineerUpdateCase == ProjectUpdate.StructuralEngineerUpdateCase.STRUCTURAL_ENGINEER) {
    return dataProto.setStructuralEngineer(updateProto.structuralEngineer);
  }
}, function (currentProto, updateProto) {
  if (updateProto.structuralEngineerUpdateCase == ProjectUpdate.StructuralEngineerUpdateCase.STRUCTURAL_ENGINEER) {
    return currentProto.setStructuralEngineer(updateProto.structuralEngineer);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.constructionManagerUpdateCase != ProjectUpdate.ConstructionManagerUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.constructionManagerUpdateCase == ProjectUpdate.ConstructionManagerUpdateCase.CONSTRUCTION_MANAGER) {
    return dataProto.setConstructionManager(updateProto.constructionManager);
  }
}, function (currentProto, updateProto) {
  if (updateProto.constructionManagerUpdateCase == ProjectUpdate.ConstructionManagerUpdateCase.CONSTRUCTION_MANAGER) {
    return currentProto.setConstructionManager(updateProto.constructionManager);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.generalContractorUpdateCase != ProjectUpdate.GeneralContractorUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.generalContractorUpdateCase == ProjectUpdate.GeneralContractorUpdateCase.GENERAL_CONTRACTOR) {
    return dataProto.setGeneralContractor(updateProto.generalContractor);
  }
}, function (currentProto, updateProto) {
  if (updateProto.generalContractorUpdateCase == ProjectUpdate.GeneralContractorUpdateCase.GENERAL_CONTRACTOR) {
    return currentProto.setGeneralContractor(updateProto.generalContractor);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.leasingRepresentativeUpdateCase != ProjectUpdate.LeasingRepresentativeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.leasingRepresentativeUpdateCase == ProjectUpdate.LeasingRepresentativeUpdateCase.LEASING_REPRESENTATIVE) {
    return dataProto.setLeasingRepresentative(updateProto.leasingRepresentative);
  }
}, function (currentProto, updateProto) {
  if (updateProto.leasingRepresentativeUpdateCase == ProjectUpdate.LeasingRepresentativeUpdateCase.LEASING_REPRESENTATIVE) {
    return currentProto.setLeasingRepresentative(updateProto.leasingRepresentative);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.propertyManagerUpdateCase != ProjectUpdate.PropertyManagerUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.propertyManagerUpdateCase == ProjectUpdate.PropertyManagerUpdateCase.PROPERTY_MANAGER) {
    return dataProto.setPropertyManager(updateProto.propertyManager);
  }
}, function (currentProto, updateProto) {
  if (updateProto.propertyManagerUpdateCase == ProjectUpdate.PropertyManagerUpdateCase.PROPERTY_MANAGER) {
    return currentProto.setPropertyManager(updateProto.propertyManager);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.completedYearUpdateCase != ProjectUpdate.CompletedYearUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.completedYearUpdateCase == ProjectUpdate.CompletedYearUpdateCase.COMPLETED_YEAR) {
    return dataProto.setCompletedYear(updateProto.completedYear);
  }
}, function (currentProto, updateProto) {
  if (updateProto.completedYearUpdateCase == ProjectUpdate.CompletedYearUpdateCase.COMPLETED_YEAR) {
    return currentProto.setCompletedYear(updateProto.completedYear);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.renovatedYearUpdateCase != ProjectUpdate.RenovatedYearUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.renovatedYearUpdateCase == ProjectUpdate.RenovatedYearUpdateCase.RENOVATED_YEAR) {
    return dataProto.setRenovatedYear(updateProto.renovatedYear);
  }
}, function (currentProto, updateProto) {
  if (updateProto.renovatedYearUpdateCase == ProjectUpdate.RenovatedYearUpdateCase.RENOVATED_YEAR) {
    return currentProto.setRenovatedYear(updateProto.renovatedYear);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.floorsAboveGroundUpdateCase != ProjectUpdate.FloorsAboveGroundUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.floorsAboveGroundUpdateCase == ProjectUpdate.FloorsAboveGroundUpdateCase.FLOORS_ABOVE_GROUND) {
    return dataProto.setFloorsAboveGround(updateProto.floorsAboveGround);
  }
}, function (currentProto, updateProto) {
  if (updateProto.floorsAboveGroundUpdateCase == ProjectUpdate.FloorsAboveGroundUpdateCase.FLOORS_ABOVE_GROUND) {
    return currentProto.setFloorsAboveGround(updateProto.floorsAboveGround);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.floorsBelowGroundUpdateCase != ProjectUpdate.FloorsBelowGroundUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.floorsBelowGroundUpdateCase == ProjectUpdate.FloorsBelowGroundUpdateCase.FLOORS_BELOW_GROUND) {
    return dataProto.setFloorsBelowGround(updateProto.floorsBelowGround);
  }
}, function (currentProto, updateProto) {
  if (updateProto.floorsBelowGroundUpdateCase == ProjectUpdate.FloorsBelowGroundUpdateCase.FLOORS_BELOW_GROUND) {
    return currentProto.setFloorsBelowGround(updateProto.floorsBelowGround);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.parkingSpotsUpdateCase != ProjectUpdate.ParkingSpotsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.parkingSpotsUpdateCase == ProjectUpdate.ParkingSpotsUpdateCase.PARKING_SPOTS) {
    return dataProto.setParkingSpots(updateProto.parkingSpots);
  }
}, function (currentProto, updateProto) {
  if (updateProto.parkingSpotsUpdateCase == ProjectUpdate.ParkingSpotsUpdateCase.PARKING_SPOTS) {
    return currentProto.setParkingSpots(updateProto.parkingSpots);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.designInfoUpdateCase != ProjectUpdate.DesignInfoUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.designInfoUpdateCase == ProjectUpdate.DesignInfoUpdateCase.DESIGN_INFO) {
    return dataProto.setDesignInfo(updateProto.designInfo);
  }
}, function (currentProto, updateProto) {
  if (updateProto.designInfoUpdateCase == ProjectUpdate.DesignInfoUpdateCase.DESIGN_INFO) {
    return currentProto.setDesignInfo(updateProto.designInfo);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.propertyTitleUpdateCase != ProjectUpdate.PropertyTitleUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.propertyTitleUpdateCase == ProjectUpdate.PropertyTitleUpdateCase.PROPERTY_TITLE) {
    return dataProto.setPropertyTitle(updateProto.propertyTitle);
  }
}, function (currentProto, updateProto) {
  if (updateProto.propertyTitleUpdateCase == ProjectUpdate.PropertyTitleUpdateCase.PROPERTY_TITLE) {
    return currentProto.setPropertyTitle(updateProto.propertyTitle);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.assetNotesUpdateCase != ProjectUpdate.AssetNotesUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.assetNotesUpdateCase == ProjectUpdate.AssetNotesUpdateCase.ASSET_NOTES) {
    return dataProto.setAssetNotes(updateProto.assetNotes);
  }
}, function (currentProto, updateProto) {
  if (updateProto.assetNotesUpdateCase == ProjectUpdate.AssetNotesUpdateCase.ASSET_NOTES) {
    return currentProto.setAssetNotes(updateProto.assetNotes);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.latestAppraisalValueUpdateCase != ProjectUpdate.LatestAppraisalValueUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.latestAppraisalValueUpdateCase == ProjectUpdate.LatestAppraisalValueUpdateCase.LATEST_APPRAISAL_VALUE) {
    return dataProto.setLatestAppraisalValue(updateProto.latestAppraisalValue);
  }
}, function (currentProto, updateProto) {
  if (updateProto.latestAppraisalValueUpdateCase == ProjectUpdate.LatestAppraisalValueUpdateCase.LATEST_APPRAISAL_VALUE) {
    return currentProto.setLatestAppraisalValue(updateProto.latestAppraisalValue);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.latestAppraisalDateUpdateCase != ProjectUpdate.LatestAppraisalDateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.latestAppraisalDateUpdateCase == ProjectUpdate.LatestAppraisalDateUpdateCase.LATEST_APPRAISAL_DATE) {
    return dataProto.setLatestAppraisalDate(updateProto.latestAppraisalDate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.latestAppraisalDateUpdateCase == ProjectUpdate.LatestAppraisalDateUpdateCase.LATEST_APPRAISAL_DATE) {
    return currentProto.setLatestAppraisalDate(updateProto.latestAppraisalDate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.targetCloseDateUpdateCase != ProjectUpdate.TargetCloseDateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.targetCloseDateUpdateCase == ProjectUpdate.TargetCloseDateUpdateCase.TARGET_CLOSE_DATE) {
    return dataProto.setTargetCloseDate(updateProto.targetCloseDate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.targetCloseDateUpdateCase == ProjectUpdate.TargetCloseDateUpdateCase.TARGET_CLOSE_DATE) {
    return currentProto.setTargetCloseDate(updateProto.targetCloseDate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.investmentCategoryUpdateCase != ProjectUpdate.InvestmentCategoryUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.investmentCategoryUpdateCase == ProjectUpdate.InvestmentCategoryUpdateCase.INVESTMENT_CATEGORY) {
    return dataProto.setInvestmentCategory(updateProto.investmentCategory);
  }
}, function (currentProto, updateProto) {
  if (updateProto.investmentCategoryUpdateCase == ProjectUpdate.InvestmentCategoryUpdateCase.INVESTMENT_CATEGORY) {
    return currentProto.setInvestmentCategory(updateProto.investmentCategory);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.currentNetOperatingIncomeUpdateCase != ProjectUpdate.CurrentNetOperatingIncomeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.currentNetOperatingIncomeUpdateCase == ProjectUpdate.CurrentNetOperatingIncomeUpdateCase.CURRENT_NET_OPERATING_INCOME) {
    return dataProto.setCurrentNetOperatingIncome(updateProto.currentNetOperatingIncome);
  }
}, function (currentProto, updateProto) {
  if (updateProto.currentNetOperatingIncomeUpdateCase == ProjectUpdate.CurrentNetOperatingIncomeUpdateCase.CURRENT_NET_OPERATING_INCOME) {
    return currentProto.setCurrentNetOperatingIncome(updateProto.currentNetOperatingIncome);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.proFormaNetOperatingIncomeUpdateCase != ProjectUpdate.ProFormaNetOperatingIncomeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.proFormaNetOperatingIncomeUpdateCase == ProjectUpdate.ProFormaNetOperatingIncomeUpdateCase.PRO_FORMA_NET_OPERATING_INCOME) {
    return dataProto.setProFormaNetOperatingIncome(updateProto.proFormaNetOperatingIncome);
  }
}, function (currentProto, updateProto) {
  if (updateProto.proFormaNetOperatingIncomeUpdateCase == ProjectUpdate.ProFormaNetOperatingIncomeUpdateCase.PRO_FORMA_NET_OPERATING_INCOME) {
    return currentProto.setProFormaNetOperatingIncome(updateProto.proFormaNetOperatingIncome);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.capRateUpdateCase != ProjectUpdate.CapRateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.capRateUpdateCase == ProjectUpdate.CapRateUpdateCase.CAP_RATE) {
    return dataProto.setCapRate(updateProto.capRate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.capRateUpdateCase == ProjectUpdate.CapRateUpdateCase.CAP_RATE) {
    return currentProto.setCapRate(updateProto.capRate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.proFormaCapRateUpdateCase != ProjectUpdate.ProFormaCapRateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.proFormaCapRateUpdateCase == ProjectUpdate.ProFormaCapRateUpdateCase.PRO_FORMA_CAP_RATE) {
    return dataProto.setProFormaCapRate(updateProto.proFormaCapRate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.proFormaCapRateUpdateCase == ProjectUpdate.ProFormaCapRateUpdateCase.PRO_FORMA_CAP_RATE) {
    return currentProto.setProFormaCapRate(updateProto.proFormaCapRate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.financialNotesUpdateCase != ProjectUpdate.FinancialNotesUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.financialNotesUpdateCase == ProjectUpdate.FinancialNotesUpdateCase.FINANCIAL_NOTES) {
    return dataProto.setFinancialNotes(updateProto.financialNotes);
  }
}, function (currentProto, updateProto) {
  if (updateProto.financialNotesUpdateCase == ProjectUpdate.FinancialNotesUpdateCase.FINANCIAL_NOTES) {
    return currentProto.setFinancialNotes(updateProto.financialNotes);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.acquisitionPriceUpdateCase != ProjectUpdate.AcquisitionPriceUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.acquisitionPriceUpdateCase == ProjectUpdate.AcquisitionPriceUpdateCase.ACQUISITION_PRICE) {
    return dataProto.setAcquisitionPrice(updateProto.acquisitionPrice);
  }
}, function (currentProto, updateProto) {
  if (updateProto.acquisitionPriceUpdateCase == ProjectUpdate.AcquisitionPriceUpdateCase.ACQUISITION_PRICE) {
    return currentProto.setAcquisitionPrice(updateProto.acquisitionPrice);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.acquisitionDateUpdateCase != ProjectUpdate.AcquisitionDateUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.acquisitionDateUpdateCase == ProjectUpdate.AcquisitionDateUpdateCase.ACQUISITION_DATE) {
    return dataProto.setAcquisitionDate(updateProto.acquisitionDate);
  }
}, function (currentProto, updateProto) {
  if (updateProto.acquisitionDateUpdateCase == ProjectUpdate.AcquisitionDateUpdateCase.ACQUISITION_DATE) {
    return currentProto.setAcquisitionDate(updateProto.acquisitionDate);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.acquisitionStatusUpdateCase != ProjectUpdate.AcquisitionStatusUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.acquisitionStatusUpdateCase == ProjectUpdate.AcquisitionStatusUpdateCase.ACQUISITION_STATUS) {
    return dataProto.setAcquisitionStatus(updateProto.acquisitionStatus);
  }
}, function (currentProto, updateProto) {
  if (updateProto.acquisitionStatusUpdateCase == ProjectUpdate.AcquisitionStatusUpdateCase.ACQUISITION_STATUS) {
    return currentProto.setAcquisitionStatus(updateProto.acquisitionStatus);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.principalNeededUpdateCase != ProjectUpdate.PrincipalNeededUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.principalNeededUpdateCase == ProjectUpdate.PrincipalNeededUpdateCase.PRINCIPAL_NEEDED) {
    return dataProto.setPrincipalNeeded(updateProto.principalNeeded);
  }
}, function (currentProto, updateProto) {
  if (updateProto.principalNeededUpdateCase == ProjectUpdate.PrincipalNeededUpdateCase.PRINCIPAL_NEEDED) {
    return currentProto.setPrincipalNeeded(updateProto.principalNeeded);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.lienPositionUpdateCase != ProjectUpdate.LienPositionUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.lienPositionUpdateCase == ProjectUpdate.LienPositionUpdateCase.LIEN_POSITION) {
    return dataProto.setLienPosition(updateProto.lienPosition);
  }
}, function (currentProto, updateProto) {
  if (updateProto.lienPositionUpdateCase == ProjectUpdate.LienPositionUpdateCase.LIEN_POSITION) {
    return currentProto.setLienPosition(updateProto.lienPosition);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.financialInstrumentsUpdateCase != ProjectUpdate.FinancialInstrumentsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.financialInstrumentsUpdateCase == ProjectUpdate.FinancialInstrumentsUpdateCase.FINANCIAL_INSTRUMENTS) {
    return dataProto.setFinancialInstruments(updateProto.financialInstruments);
  }
}, function (currentProto, updateProto) {
  if (updateProto.financialInstrumentsUpdateCase == ProjectUpdate.FinancialInstrumentsUpdateCase.FINANCIAL_INSTRUMENTS) {
    return currentProto.setFinancialInstruments(updateProto.financialInstruments);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.affordabilityUpdateCase != ProjectUpdate.AffordabilityUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.affordabilityUpdateCase == ProjectUpdate.AffordabilityUpdateCase.AFFORDABILITY) {
    return dataProto.setAffordability(updateProto.affordability);
  }
}, function (currentProto, updateProto) {
  if (updateProto.affordabilityUpdateCase == ProjectUpdate.AffordabilityUpdateCase.AFFORDABILITY) {
    return currentProto.setAffordability(updateProto.affordability);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.hasElevatorUpdateCase != ProjectUpdate.HasElevatorUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.hasElevatorUpdateCase == ProjectUpdate.HasElevatorUpdateCase.HAS_ELEVATOR) {
    return dataProto.setHasElevator(updateProto.hasElevator);
  }
}, function (currentProto, updateProto) {
  if (updateProto.hasElevatorUpdateCase == ProjectUpdate.HasElevatorUpdateCase.HAS_ELEVATOR) {
    return currentProto.setHasElevator(updateProto.hasElevator);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.numberOfFloorsUpdateCase != ProjectUpdate.NumberOfFloorsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.numberOfFloorsUpdateCase == ProjectUpdate.NumberOfFloorsUpdateCase.NUMBER_OF_FLOORS) {
    return dataProto.setNumberOfFloors(updateProto.numberOfFloors);
  }
}, function (currentProto, updateProto) {
  if (updateProto.numberOfFloorsUpdateCase == ProjectUpdate.NumberOfFloorsUpdateCase.NUMBER_OF_FLOORS) {
    return currentProto.setNumberOfFloors(updateProto.numberOfFloors);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.unitInformationUpdateCase != ProjectUpdate.UnitInformationUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.unitInformationUpdateCase == ProjectUpdate.UnitInformationUpdateCase.UNIT_INFORMATION) {
    return dataProto.setUnitInformation(updateProto.unitInformation);
  }
}, function (currentProto, updateProto) {
  if (updateProto.unitInformationUpdateCase == ProjectUpdate.UnitInformationUpdateCase.UNIT_INFORMATION) {
    return currentProto.setUnitInformation(updateProto.unitInformation);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.lihtcRatioUpdateCase != ProjectUpdate.LihtcRatioUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.lihtcRatioUpdateCase == ProjectUpdate.LihtcRatioUpdateCase.LIHTC_RATIO) {
    return dataProto.setLihtcRatio(updateProto.lihtcRatio);
  }
}, function (currentProto, updateProto) {
  if (updateProto.lihtcRatioUpdateCase == ProjectUpdate.LihtcRatioUpdateCase.LIHTC_RATIO) {
    return currentProto.setLihtcRatio(updateProto.lihtcRatio);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.hudSelectedLtvUpdateCase != ProjectUpdate.HudSelectedLtvUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.hudSelectedLtvUpdateCase == ProjectUpdate.HudSelectedLtvUpdateCase.HUD_SELECTED_LTV) {
    return dataProto.setHudSelectedLtv(updateProto.hudSelectedLtv);
  }
}, function (currentProto, updateProto) {
  if (updateProto.hudSelectedLtvUpdateCase == ProjectUpdate.HudSelectedLtvUpdateCase.HUD_SELECTED_LTV) {
    return currentProto.setHudSelectedLtv(updateProto.hudSelectedLtv);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.freddieSblSelectedLtvUpdateCase != ProjectUpdate.FreddieSblSelectedLtvUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.freddieSblSelectedLtvUpdateCase == ProjectUpdate.FreddieSblSelectedLtvUpdateCase.FREDDIE_SBL_SELECTED_LTV) {
    return dataProto.setFreddieSblSelectedLtv(updateProto.freddieSblSelectedLtv);
  }
}, function (currentProto, updateProto) {
  if (updateProto.freddieSblSelectedLtvUpdateCase == ProjectUpdate.FreddieSblSelectedLtvUpdateCase.FREDDIE_SBL_SELECTED_LTV) {
    return currentProto.setFreddieSblSelectedLtv(updateProto.freddieSblSelectedLtv);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.freddieSblSelectedDscrUpdateCase != ProjectUpdate.FreddieSblSelectedDscrUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.freddieSblSelectedDscrUpdateCase == ProjectUpdate.FreddieSblSelectedDscrUpdateCase.FREDDIE_SBL_SELECTED_DSCR) {
    return dataProto.setFreddieSblSelectedDscr(updateProto.freddieSblSelectedDscr);
  }
}, function (currentProto, updateProto) {
  if (updateProto.freddieSblSelectedDscrUpdateCase == ProjectUpdate.FreddieSblSelectedDscrUpdateCase.FREDDIE_SBL_SELECTED_DSCR) {
    return currentProto.setFreddieSblSelectedDscr(updateProto.freddieSblSelectedDscr);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.freddieSblInterestOnlyTermUpdateCase != ProjectUpdate.FreddieSblInterestOnlyTermUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.freddieSblInterestOnlyTermUpdateCase == ProjectUpdate.FreddieSblInterestOnlyTermUpdateCase.FREDDIE_SBL_INTEREST_ONLY_TERM) {
    return dataProto.setFreddieSblInterestOnlyTerm(updateProto.freddieSblInterestOnlyTerm);
  }
}, function (currentProto, updateProto) {
  if (updateProto.freddieSblInterestOnlyTermUpdateCase == ProjectUpdate.FreddieSblInterestOnlyTermUpdateCase.FREDDIE_SBL_INTEREST_ONLY_TERM) {
    return currentProto.setFreddieSblInterestOnlyTerm(updateProto.freddieSblInterestOnlyTerm);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.freddieSblPrepaymentPenaltyUpdateCase != ProjectUpdate.FreddieSblPrepaymentPenaltyUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.freddieSblPrepaymentPenaltyUpdateCase == ProjectUpdate.FreddieSblPrepaymentPenaltyUpdateCase.FREDDIE_SBL_PREPAYMENT_PENALTY) {
    return dataProto.setFreddieSblPrepaymentPenalty(updateProto.freddieSblPrepaymentPenalty);
  }
}, function (currentProto, updateProto) {
  if (updateProto.freddieSblPrepaymentPenaltyUpdateCase == ProjectUpdate.FreddieSblPrepaymentPenaltyUpdateCase.FREDDIE_SBL_PREPAYMENT_PENALTY) {
    return currentProto.setFreddieSblPrepaymentPenalty(updateProto.freddieSblPrepaymentPenalty);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.offerExpectationsUpdateCase != ProjectUpdate.OfferExpectationsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.offerExpectationsUpdateCase == ProjectUpdate.OfferExpectationsUpdateCase.OFFER_EXPECTATIONS) {
    return dataProto.setOfferExpectations(updateProto.offerExpectations);
  }
}, function (currentProto, updateProto) {
  if (updateProto.offerExpectationsUpdateCase == ProjectUpdate.OfferExpectationsUpdateCase.OFFER_EXPECTATIONS) {
    return currentProto.setOfferExpectations(updateProto.offerExpectations);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.capStackItemsUpdateCase != ProjectUpdate.CapStackItemsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyCapStackItemsUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.capStackItemsUpdateCase == ProjectUpdate.CapStackItemsUpdateCase.CAP_STACK_ITEMS) {
    return currentProto.setCapStackItems(updateProto.capStackItems);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.capStackTargetsUpdateCase != ProjectUpdate.CapStackTargetsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyCapStackTargetsUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.capStackTargetsUpdateCase == ProjectUpdate.CapStackTargetsUpdateCase.CAP_STACK_TARGETS) {
    return currentProto.setCapStackTargets(updateProto.capStackTargets);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.guarantorsUpdateCase != ProjectUpdate.GuarantorsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyGuarantorsUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.guarantorsUpdateCase == ProjectUpdate.GuarantorsUpdateCase.GUARANTORS) {
    return currentProto.setGuarantors(updateProto.guarantors);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.solicitedLendersUpdateCase != ProjectUpdate.SolicitedLendersUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applySolicitedLendersUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.solicitedLendersUpdateCase == ProjectUpdate.SolicitedLendersUpdateCase.SOLICITED_LENDERS) {
    return currentProto.setSolicitedLenders(updateProto.solicitedLenders);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.sponsorProfileUpdateCase != ProjectUpdate.SponsorProfileUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.sponsorProfileUpdateCase == ProjectUpdate.SponsorProfileUpdateCase.SPONSOR_PROFILE) {
    return dataProto.setSponsorProfile(updateProto.sponsorProfile);
  }
}, function (currentProto, updateProto) {
  if (updateProto.sponsorProfileUpdateCase == ProjectUpdate.SponsorProfileUpdateCase.SPONSOR_PROFILE) {
    return currentProto.setSponsorProfile(updateProto.sponsorProfile);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.noteUpdateCase != ProjectUpdate.NoteUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.noteUpdateCase == ProjectUpdate.NoteUpdateCase.NOTE) {
    return dataProto.setNote(updateProto.note);
  }
}, function (currentProto, updateProto) {
  if (updateProto.noteUpdateCase == ProjectUpdate.NoteUpdateCase.NOTE) {
    return currentProto.setNote(updateProto.note);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.acceptedOfferUpdateCase != ProjectUpdate.AcceptedOfferUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyAcceptedOfferUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.acceptedOfferUpdateCase == ProjectUpdate.AcceptedOfferUpdateCase.ACCEPTED_OFFER) {
    return currentProto.setAcceptedOffer(updateProto.acceptedOffer);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.offerUnderwritingRequestedUpdateCase != ProjectUpdate.OfferUnderwritingRequestedUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.offerUnderwritingRequestedUpdateCase == ProjectUpdate.OfferUnderwritingRequestedUpdateCase.OFFER_UNDERWRITING_REQUESTED) {
    return dataProto.setOfferUnderwritingRequested(updateProto.offerUnderwritingRequested);
  }
}, function (currentProto, updateProto) {
  if (updateProto.offerUnderwritingRequestedUpdateCase == ProjectUpdate.OfferUnderwritingRequestedUpdateCase.OFFER_UNDERWRITING_REQUESTED) {
    return currentProto.setOfferUnderwritingRequested(updateProto.offerUnderwritingRequested);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.leadingOfferUpdateCase != ProjectUpdate.LeadingOfferUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.leadingOfferUpdateCase == ProjectUpdate.LeadingOfferUpdateCase.LEADING_OFFER) {
    return dataProto.setLeadingOffer(updateProto.leadingOffer);
  }
}, function (currentProto, updateProto) {
  if (updateProto.leadingOfferUpdateCase == ProjectUpdate.LeadingOfferUpdateCase.LEADING_OFFER) {
    return currentProto.setLeadingOffer(updateProto.leadingOffer);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.occupancyUpdateCase != ProjectUpdate.OccupancyUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.occupancyUpdateCase == ProjectUpdate.OccupancyUpdateCase.OCCUPANCY) {
    return dataProto.setOccupancy(updateProto.occupancy);
  }
}, function (currentProto, updateProto) {
  if (updateProto.occupancyUpdateCase == ProjectUpdate.OccupancyUpdateCase.OCCUPANCY) {
    return currentProto.setOccupancy(updateProto.occupancy);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.usesUpdateCase != ProjectUpdate.UsesUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyUsesUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.usesUpdateCase == ProjectUpdate.UsesUpdateCase.USES) {
    return currentProto.setUses(updateProto.uses);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.disclaimerUpdateCase != ProjectUpdate.DisclaimerUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.disclaimerUpdateCase == ProjectUpdate.DisclaimerUpdateCase.DISCLAIMER) {
    return dataProto.setDisclaimer(updateProto.disclaimer);
  }
}, function (currentProto, updateProto) {
  if (updateProto.disclaimerUpdateCase == ProjectUpdate.DisclaimerUpdateCase.DISCLAIMER) {
    return currentProto.setDisclaimer(updateProto.disclaimer);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.demographicsUpdateCase != ProjectUpdate.DemographicsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.demographicsUpdateCase == ProjectUpdate.DemographicsUpdateCase.DEMOGRAPHICS) {
    return dataProto.setDemographics(updateProto.demographics);
  }
}, function (currentProto, updateProto) {
  if (updateProto.demographicsUpdateCase == ProjectUpdate.DemographicsUpdateCase.DEMOGRAPHICS) {
    return currentProto.setDemographics(updateProto.demographics);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.showConcealedSharesUpdateCase != ProjectUpdate.ShowConcealedSharesUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.showConcealedSharesUpdateCase == ProjectUpdate.ShowConcealedSharesUpdateCase.SHOW_CONCEALED_SHARES) {
    return dataProto.setShowConcealedShares(updateProto.showConcealedShares);
  }
}, function (currentProto, updateProto) {
  if (updateProto.showConcealedSharesUpdateCase == ProjectUpdate.ShowConcealedSharesUpdateCase.SHOW_CONCEALED_SHARES) {
    return currentProto.setShowConcealedShares(updateProto.showConcealedShares);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.projectFeeUpdateCase != ProjectUpdate.ProjectFeeUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  if (updateProto.projectFeeUpdateCase == ProjectUpdate.ProjectFeeUpdateCase.PROJECT_FEE) {
    return dataProto.setProjectFee(updateProto.projectFee);
  }
}, function (currentProto, updateProto) {
  if (updateProto.projectFeeUpdateCase == ProjectUpdate.ProjectFeeUpdateCase.PROJECT_FEE) {
    return currentProto.setProjectFee(updateProto.projectFee);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.capitalAdvisorsUpdateCase != ProjectUpdate.CapitalAdvisorsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyCapitalAdvisorsUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.capitalAdvisorsUpdateCase == ProjectUpdate.CapitalAdvisorsUpdateCase.CAPITAL_ADVISORS) {
    return currentProto.setCapitalAdvisors(updateProto.capitalAdvisors);
  }
  if (updateProto.capitalAdvisorsUpdateCase == ProjectUpdate.CapitalAdvisorsUpdateCase.CAPITAL_ADVISOR_SYMS) {
    return currentProto.setCapitalAdvisorSyms(updateProto.capitalAdvisorSyms);
  }
}), new FieldUpdater(null, function (updateProto) {
  return updateProto.underwritingApprovalRequirementExceptionsUpdateCase != ProjectUpdate.UnderwritingApprovalRequirementExceptionsUpdateCase.UNSET;
}, null, null, function (dataProto, updateProto) {
  return applyUnderwritingApprovalRequirementExceptionsUpdate(dataProto, updateProto);
}, function (currentProto, updateProto) {
  if (updateProto.underwritingApprovalRequirementExceptionsUpdateCase == ProjectUpdate.UnderwritingApprovalRequirementExceptionsUpdateCase.UNDERWRITING_APPROVAL_REQUIREMENT_EXCEPTIONS) {
    return currentProto.setUnderwritingApprovalRequirementExceptions(updateProto.underwritingApprovalRequirementExceptions);
  }
})];
class ProjectUpdateManager extends UpdateManager {
  constructor(update, ...args) {
    let _this;
    [super(FIELD_UPDATERS, update), _this = this][0];
    _this.args = args;
  }
  static get fieldUpdaters() {
    const _this = this;
    return FIELD_UPDATERS;
  }
}

const queueManager = new UpdateQueueManager((a, b) => ProjectUpdateManager.mergeUpdate(a, b));
const updateManager = new RemoteUpdateManager(queueManager, updateProject$1);
async function updateProject(ctr, updateData) {
  const update = createProjectUpdate(updateData);
  ctr.dealChanged(update.sym);
  await updateManager.addUpdate(update);
}

function _defineProperty$s(obj, key, value) { key = _toPropertyKey$s(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$s(arg) { var key = _toPrimitive$s(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$s(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Deals {
  constructor(ava, ctr) {
    _defineProperty$s(this, "ava", void 0);
    _defineProperty$s(this, "ctr", void 0);
    _defineProperty$s(this, "attachments", void 0);
    _defineProperty$s(this, "images", void 0);
    this.ava = ava;
    this.ctr = ctr;
    this.attachments = new Attachments(ava, ctr);
    this.images = new Images(ava, ctr);
  }
  search(filters, sorts) {
    return searchProjects(this.ctr, filters, sorts);
  }
  getSummary(sym) {
    return getSummary(this.ctr, sym);
  }
  get(sym) {
    return getDeal(this.ctr, sym);
  }
  getSymById(id) {
    return getSymById(this.ctr, id);
  }
  [$symToId](sym) {
    return getIdBySym(this.ctr, sym);
  }
  create(data) {
    return createProject(this.ctr, data);
  }
  async update(update) {
    await updateProject(this.ctr, update);
  }
}

async function createOffer(unused_ctr, unused_data) {
  throw new Error('Unimplemented');
}

function getOffers(...args) {
  return postWithChecksum(resolve('/@r:/offers/get.getOffers'), args, ChecksumType.CRC32C);
}

async function getOffer(ctr, sym) {
  let offer = ctr.state.getOffer(sym);
  if (!offer) {
    offer = await ctr.loadBatchListWithSharedUser(sym, (sharedUser, syms) => getOffers(sharedUser, syms), data => Offer.fromJson(data), (state, sym, value) => state.setOffer(sym, value));
    if (!offer) {
      throw new NotFoundError$1();
    }
  }
  return offer;
}

async function updateOffer(unused_ctr, unused_data) {
  throw new Error('Unimplemented');
}

function _defineProperty$r(obj, key, value) { key = _toPropertyKey$r(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$r(arg) { var key = _toPrimitive$r(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$r(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Offers {
  constructor(ava, ctr) {
    _defineProperty$r(this, "ava", void 0);
    _defineProperty$r(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  get(sym) {
    return getOffer(this.ctr, sym);
  }
  create(data) {
    return createOffer(this.ctr, data);
  }
  async update(data) {
    await updateOffer(this.ctr, data);
  }
  async delete(sym) {
    assert(this.ctr.state.getOffer(sym), emsg`Offer doesn't exist with sym ${sym}`);
    // TODO(nathan): Delete offer in backend.
    this.ctr.setState(this.ctr.state.deleteOffer(sym));
  }
}

function getOrganizationsByIds(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.getOrganizationsByIds'), args, ChecksumType.CRC32C);
}
function getOrganizationDescriptionsByIds(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.getOrganizationDescriptionsByIds'), args, ChecksumType.CRC32C);
}
function getAllOrganizations(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.getAllOrganizations'), args, ChecksumType.CRC32C);
}
function addOrganization(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.addOrganization'), args, ChecksumType.CRC32C);
}
function updateOrganization(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.updateOrganization'), args, ChecksumType.CRC32C);
}
function getOwnOrganization(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.getOwnOrganization'), args, ChecksumType.CRC32C);
}
function updateOwnOrganization(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.updateOwnOrganization'), args, ChecksumType.CRC32C);
}
function searchOrganizations(...args) {
  if (this != null) {
    if (arguments.length >= 0) args.unshift(this);
  }
  return postWithChecksum(resolve('/@r:/organizations.searchOrganizations'), args, ChecksumType.CRC32C);
}

function toAvaOrganization(value) {
  return new Org().setSym(value.sym).setName(value.name).setLogo(new OrgImage().setKey(value.logoKey)).setWebsite(value.website).setBondCreditRating(value.bondCreditRating).setBriefDescription(value.briefDescription).setDescription(new OrgDescription().setQuote(value.description.quote).setManual(value.description.manual));
}

function _defineProperty$q(obj, key, value) { key = _toPropertyKey$q(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$q(arg) { var key = _toPrimitive$q(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$q(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class OrganizationDescription_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: Quotation.shape,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Quotation);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message OrganizationDescription field quote.
                Expected type Quotation.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Quotation', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message OrganizationDescription field manual.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(OrganizationDescription, 'OrganizationDescription', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message OrganizationDescription to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get quote() {
    return this.getFieldById(1, Quotation.getDefault());
  }
  setQuote(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteQuote() {
    return this.setValueFieldById(1, null, null);
  }
  get manual() {
    return this.getFieldById(2, "");
  }
  setManual(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteManual() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$q(OrganizationDescription_, protoName, DEV_MODE ? 'OrganizationDescription' : '[Unknown]');
let OrganizationDescription = OrganizationDescription_;
(function (_OrganizationDescription) {})(OrganizationDescription || (OrganizationDescription = {}));
Object.defineProperties(OrganizationDescription, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "quote": {
        value: 1
      },
      "manual": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "quote"
      },
      '2': {
        value: "manual"
      }
    }))
  }
});
Object.defineProperties(OrganizationDescription.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: Quotation.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(OrganizationDescription);
  Object.freeze(OrganizationDescription.prototype);
}
class Organization_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      15: 2,
      2: 2,
      3: 2,
      4: Url.shape,
      5: BondCreditRating.shape,
      6: 2,
      7: OrganizationDescription.shape,
      10: 2,
      11: 7,
      12: 2,
      13: 2,
      14: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Organization field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field logo_key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Url);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Organization field website.
                Expected type Url.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Url', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(BondCreditRating);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Organization field bond_credit_rating.
                Expected type BondCreditRating.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'BondCreditRating', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field brief_description.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(OrganizationDescription);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message Organization field description.
                Expected type OrganizationDescription.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'OrganizationDescription', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field location_label.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 11:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Organization field moody_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 12:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field lei.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 13:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field ticker_symbol.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 14:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Organization field tag.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Organization, 'Organization', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Organization to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get sym() {
    return this.getFieldById(15, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(15, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get logoKey() {
    return this.getFieldById(3, "");
  }
  setLogoKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteLogoKey() {
    return this.setValueFieldById(3, null, null);
  }
  get website() {
    return this.getFieldById(4, Url.getDefault());
  }
  setWebsite(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteWebsite() {
    return this.setValueFieldById(4, null, null);
  }
  get bondCreditRating() {
    return this.getFieldById(5, BondCreditRating.getDefault());
  }
  setBondCreditRating(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteBondCreditRating() {
    return this.setValueFieldById(5, null, null);
  }
  get briefDescription() {
    return this.getFieldById(6, "");
  }
  setBriefDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteBriefDescription() {
    return this.setValueFieldById(6, null, null);
  }
  get description() {
    return this.getFieldById(7, OrganizationDescription.getDefault());
  }
  setDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteDescription() {
    return this.setValueFieldById(7, null, null);
  }
  get locationLabel() {
    return this.getFieldById(10, "");
  }
  setLocationLabel(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteLocationLabel() {
    return this.setValueFieldById(10, null, null);
  }
  get moodyId() {
    return this.getFieldById(11, 0);
  }
  setMoodyId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteMoodyId() {
    return this.setValueFieldById(11, null, null);
  }
  get lei() {
    return this.getFieldById(12, "");
  }
  setLei(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteLei() {
    return this.setValueFieldById(12, null, null);
  }
  get tickerSymbol() {
    return this.getFieldById(13, "");
  }
  setTickerSymbol(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteTickerSymbol() {
    return this.setValueFieldById(13, null, null);
  }
  get tagList() {
    return this.getRepeatedFieldById(14);
  }
  hasTag(value) {
    return this.repeatedFieldByIdHas(14, value);
  }
  addTag(value) {
    return this.addToRepeatedFieldById(14, value);
  }
  addEachTag(values) {
    let T = this;
    for (const value of values) {
      T = T.addTag(value);
    }
    return T;
  }
  setTag(index, value) {
    return this.setRepeatedFieldById(14, index, value);
  }
  removeTag(index) {
    return this.removeFromRepeatedFieldById(14, index);
  }
  clearTagList() {
    return this.clearRepeatedFieldById(14);
  }
  setTagList(values) {
    return this.overwriteRepeatedFieldById(14, values);
  }
}
_defineProperty$q(Organization_, protoName, DEV_MODE ? 'Organization' : '[Unknown]');
let Organization = Organization_;
(function (_Organization) {})(Organization || (Organization = {}));
Object.defineProperties(Organization, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 15
      },
      "name": {
        value: 2
      },
      "logo_key": {
        value: 3
      },
      "logoKey": {
        value: 3
      },
      "website": {
        value: 4
      },
      "bond_credit_rating": {
        value: 5
      },
      "bondCreditRating": {
        value: 5
      },
      "brief_description": {
        value: 6
      },
      "briefDescription": {
        value: 6
      },
      "description": {
        value: 7
      },
      "location_label": {
        value: 10
      },
      "locationLabel": {
        value: 10
      },
      "moody_id": {
        value: 11
      },
      "moodyId": {
        value: 11
      },
      "lei": {
        value: 12
      },
      "ticker_symbol": {
        value: 13
      },
      "tickerSymbol": {
        value: 13
      },
      "tag": {
        value: 14
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '15': {
        value: "sym"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "logo_key"
      },
      '4': {
        value: "website"
      },
      '5': {
        value: "bond_credit_rating"
      },
      '6': {
        value: "brief_description"
      },
      '7': {
        value: "description"
      },
      '10': {
        value: "location_label"
      },
      '11': {
        value: "moody_id"
      },
      '12': {
        value: "lei"
      },
      '13': {
        value: "ticker_symbol"
      },
      '14': {
        value: "tag"
      }
    }))
  }
});
Object.defineProperties(Organization.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      4: Url.prototype,
      5: BondCreditRating.prototype,
      7: OrganizationDescription.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Organization);
  Object.freeze(Organization.prototype);
}

// Accepts either a sym or a SymOrString. If a SymOrString is passed in, it
// may resolve to an Org with only a name field set.
async function getOrganization(ctr, sym) {
  if (sym instanceof SymOrString) {
    if (sym.sym) {
      return getOrganization(ctr, sym.sym);
    }
    return new Org().setName(sym.string);
  }
  const org = ctr.state.getOrganization(sym) || (await ctr.batchLoad('Org', getOrganizations, sym));
  return toAvaOrganization(org);
}
async function getOrganizations(ctr, syms) {
  const orgs = new Map();
  const neededSyms = new Set();
  for (const sym of syms) {
    const org = ctr.state.getOrganization(sym);
    if (org) {
      orgs.set(sym, org);
    } else {
      neededSyms.add(sym);
    }
  }
  if (neededSyms.size > 0) {
    const data = await getOrganizationsByIds(ctr.sharedUser, [...neededSyms]);
    let state = ctr.state;
    for (const d of data) {
      const org = Organization.fromJson(d);
      orgs.set(org.sym, org);
      state = state.setOrganization(org.sym, org);
    }
    ctr.setState(state);
  }
  return orgs;
}

// TODO(nathan): Is this even needed or used?
// Accepts either a sym or a SymOrString. If a SymOrString is passed in, it
// may resolve to an Org with only a name field set.
async function getOrganizationDetails(ctr, sym) {
  if (sym instanceof SymOrString) {
    if (sym.sym) {
      return getOrganizationDetails(ctr, sym.sym);
    }
    return new Org().setName(sym.string);
  }
  const org = ctr.state.getOrganization(sym) || (await ctr.batchLoad('Org', getAllOrganizationDetails, sym));
  return toAvaOrganization(org);
}
async function getAllOrganizationDetails(ctr, syms) {
  const orgs = new Map();
  const neededSyms = new Set();
  for (const sym of syms) {
    const org = ctr.state.getOrganization(sym);
    if (org) {
      orgs.set(sym, org);
    } else {
      neededSyms.add(sym);
    }
  }
  if (neededSyms.size > 0) {
    const data = await getOrganizationDescriptionsByIds(ctr.sharedUser, [...neededSyms]);
    let state = ctr.state;
    for (const d of data) {
      const org = Organization.fromJson(d);
      orgs.set(org.sym, org);
      state = state.setOrganization(org.sym, org);
    }
    ctr.setState(state);
  }
  return orgs;
}

function _defineProperty$p(obj, key, value) { key = _toPropertyKey$p(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$p(arg) { var key = _toPrimitive$p(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$p(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Orgs {
  constructor(ava, ctr) {
    _defineProperty$p(this, "ava", void 0);
    _defineProperty$p(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  get(sym) {
    return getOrganization(this.ctr, sym);
  }
  getDetails(sym) {
    return getOrganizationDetails(this.ctr, sym);
  }
}

function getPeopleByEmails$1(...args) {
  return postWithChecksum(resolve('/@r:/people.getPeopleByEmails'), args, ChecksumType.CRC32C);
}
function getAllPeople(...args) {
  return postWithChecksum(resolve('/@r:/people.getAllPeople'), args, ChecksumType.CRC32C);
}
function addPerson(...args) {
  return postWithChecksum(resolve('/@r:/people.addPerson'), args, ChecksumType.CRC32C);
}
function updatePerson(...args) {
  return postWithChecksum(resolve('/@r:/people.updatePerson'), args, ChecksumType.CRC32C);
}

const batch = new BatchLoader({
  typeLabel: 'person',
  load: getPeopleByEmails,
  cache: {
    maxSize: 1024,
    maxAge: 60 * 60 * 1000
  }
});
function getPersonByEmail(ctr, email) {
  return batch.load(ctr, email);
}
async function getPeopleByEmails(ctr, emails) {
  const map = new Map();
  for (const u of await getPeopleByEmails$1(ctr.sharedUser, emails)) {
    // Removing the organization ID so that it won't be relied upon in Buble.
    // If we want organization info, we need to add it using a sym.
    const person = Person.fromJson(u).deleteOrganizationId();
    map.set(person.email, person);
  }
  return map;
}

function _defineProperty$o(obj, key, value) { key = _toPropertyKey$o(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$o(arg) { var key = _toPrimitive$o(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$o(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class People {
  constructor(ava, ctr) {
    _defineProperty$o(this, "ava", void 0);
    _defineProperty$o(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  getByEmail(email) {
    return getPersonByEmail(this.ctr, email);
  }
}

function getLoanPrograms(...args) {
  return postWithChecksum(resolve('/@r:/loan_programs.getLoanPrograms'), args, ChecksumType.CRC32C);
}
function getLoanProgramsBySyms(...args) {
  return postWithChecksum(resolve('/@r:/program.getProgramsBySyms'), args, ChecksumType.CRC32C);
}
function getProgramsBySyms(...args) {
  return postWithChecksum(resolve('/@r:/program.getProgramsBySyms'), args, ChecksumType.CRC32C);
}
function countMatchingLoanPrograms(...args) {
  return postWithChecksum(resolve('/@r:/loan_programs.countMatchingLoanPrograms'), args, ChecksumType.CRC32C);
}
function getLoanProgramMatches(...args) {
  return postWithChecksum(resolve('/@r:/loan_programs.getLoanProgramMatches'), args, ChecksumType.CRC32C);
}
function getLoanProgramMatchIds(...args) {
  return postWithChecksum(resolve('/@r:/loan_programs.getLoanProgramMatchIds'), args, ChecksumType.CRC32C);
}

function _defineProperty$n(obj, key, value) { key = _toPropertyKey$n(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$n(arg) { var key = _toPrimitive$n(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$n(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class GetLoanProgramsResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: LoanProgram.shape,
      2: Organization.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(LoanProgram);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetLoanProgramsResponse field loan_program.
                Expected type LoanProgram.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'LoanProgram', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Organization);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetLoanProgramsResponse field organization.
                Expected type Organization.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Organization', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(GetLoanProgramsResponse, 'GetLoanProgramsResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message GetLoanProgramsResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get loanProgramList() {
    return this.getRepeatedFieldById(1);
  }
  hasLoanProgram(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addLoanProgram(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachLoanProgram(values) {
    let T = this;
    for (const value of values) {
      T = T.addLoanProgram(value);
    }
    return T;
  }
  setLoanProgram(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeLoanProgram(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearLoanProgramList() {
    return this.clearRepeatedFieldById(1);
  }
  setLoanProgramList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get organizationList() {
    return this.getRepeatedFieldById(2);
  }
  hasOrganization(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addOrganization(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachOrganization(values) {
    let T = this;
    for (const value of values) {
      T = T.addOrganization(value);
    }
    return T;
  }
  setOrganization(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeOrganization(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearOrganizationList() {
    return this.clearRepeatedFieldById(2);
  }
  setOrganizationList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
}
_defineProperty$n(GetLoanProgramsResponse_, protoName, DEV_MODE ? 'GetLoanProgramsResponse' : '[Unknown]');
let GetLoanProgramsResponse = GetLoanProgramsResponse_;
(function (_GetLoanProgramsResponse) {})(GetLoanProgramsResponse || (GetLoanProgramsResponse = {}));
Object.defineProperties(GetLoanProgramsResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "loan_program": {
        value: 1
      },
      "loanProgram": {
        value: 1
      },
      "organization": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "loan_program"
      },
      '2': {
        value: "organization"
      }
    }))
  }
});
Object.defineProperties(GetLoanProgramsResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: LoanProgram.prototype,
      2: Organization.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(GetLoanProgramsResponse);
  Object.freeze(GetLoanProgramsResponse.prototype);
}
class GetLoanProgramMatchIdsResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: [7, RepeatedUint32.shape],
      2: LoanProgram.shape,
      3: Organization.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message GetLoanProgramMatchIdsResponse field matching_loan_program_ids.
          Expected type number.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RepeatedUint32);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetLoanProgramMatchIdsResponse field matching_loan_program_ids.
                Expected type RepeatedUint32.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RepeatedUint32', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(LoanProgram);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetLoanProgramMatchIdsResponse field loan_program.
                Expected type LoanProgram.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'LoanProgram', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Organization);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetLoanProgramMatchIdsResponse field organization.
                Expected type Organization.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Organization', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(GetLoanProgramMatchIdsResponse, 'GetLoanProgramMatchIdsResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message GetLoanProgramMatchIdsResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get matchingLoanProgramIdsKeys() {
    return this.getMapIteratorById(1);
  }
  get matchingLoanProgramIdsValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(1)) {
        yield self.getMapValueById(1, key);
      }
    }();
  }
  get matchingLoanProgramIdsSize() {
    return this.getMapSizeById(1);
  }
  hasMatchingLoanProgramIds(key) {
    return this.hasMapKeyById(1, key);
  }
  getMatchingLoanProgramIds(key) {
    return this.getMapValueById(1, key);
  }
  setMatchingLoanProgramIds(key, value) {
    return this.setMapValue(1, key, value);
  }
  get matchingLoanProgramIdsMap() {
    return this.getMapById(1);
  }
  setMatchingLoanProgramIdsMap(map) {
    if (map.size === this.getMapSizeById(1)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(1, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(1);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(1, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeMatchingLoanProgramIds(key) {
    return deprecatedMember('GetLoanProgramMatchIdsResponse', 'removeMatchingLoanProgramIds', 'deleteMatchingLoanProgramIds', this.deleteMatchingLoanProgramIds(key));
  }
  deleteMatchingLoanProgramIds(key) {
    return this.removeFromMapById(1, key);
  }
  clearMatchingLoanProgramIdsMap() {
    return this.clearMap(1);
  }
  get loanProgramList() {
    return this.getRepeatedFieldById(2);
  }
  hasLoanProgram(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addLoanProgram(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachLoanProgram(values) {
    let T = this;
    for (const value of values) {
      T = T.addLoanProgram(value);
    }
    return T;
  }
  setLoanProgram(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeLoanProgram(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearLoanProgramList() {
    return this.clearRepeatedFieldById(2);
  }
  setLoanProgramList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
  get organizationList() {
    return this.getRepeatedFieldById(3);
  }
  hasOrganization(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addOrganization(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachOrganization(values) {
    let T = this;
    for (const value of values) {
      T = T.addOrganization(value);
    }
    return T;
  }
  setOrganization(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeOrganization(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearOrganizationList() {
    return this.clearRepeatedFieldById(3);
  }
  setOrganizationList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
}
_defineProperty$n(GetLoanProgramMatchIdsResponse_, protoName, DEV_MODE ? 'GetLoanProgramMatchIdsResponse' : '[Unknown]');
let GetLoanProgramMatchIdsResponse = GetLoanProgramMatchIdsResponse_;
(function (_GetLoanProgramMatchIdsResponse) {})(GetLoanProgramMatchIdsResponse || (GetLoanProgramMatchIdsResponse = {}));
Object.defineProperties(GetLoanProgramMatchIdsResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "matching_loan_program_ids": {
        value: 1
      },
      "matchingLoanProgramIds": {
        value: 1
      },
      "loan_program": {
        value: 2
      },
      "loanProgram": {
        value: 2
      },
      "organization": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "matching_loan_program_ids"
      },
      '2': {
        value: "loan_program"
      },
      '3': {
        value: "organization"
      }
    }))
  }
});
Object.defineProperties(GetLoanProgramMatchIdsResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: [null, RepeatedUint32.prototype],
      2: LoanProgram.prototype,
      3: Organization.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(GetLoanProgramMatchIdsResponse);
  Object.freeze(GetLoanProgramMatchIdsResponse.prototype);
}
class GetLoanProgramMatchesResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: [7, RepeatedLoanProgram_.shape]
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message GetLoanProgramMatchesResponse field matching_loan_programs.
          Expected type number.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RepeatedLoanProgram_);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetLoanProgramMatchesResponse field matching_loan_programs.
                Expected type RepeatedLoanProgram.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RepeatedLoanProgram', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(GetLoanProgramMatchesResponse, 'GetLoanProgramMatchesResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message GetLoanProgramMatchesResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get matchingLoanProgramsKeys() {
    return this.getMapIteratorById(1);
  }
  get matchingLoanProgramsValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(1)) {
        yield self.getMapValueById(1, key);
      }
    }();
  }
  get matchingLoanProgramsSize() {
    return this.getMapSizeById(1);
  }
  hasMatchingLoanPrograms(key) {
    return this.hasMapKeyById(1, key);
  }
  getMatchingLoanPrograms(key) {
    return this.getMapValueById(1, key);
  }
  setMatchingLoanPrograms(key, value) {
    return this.setMapValue(1, key, value);
  }
  get matchingLoanProgramsMap() {
    return this.getMapById(1);
  }
  setMatchingLoanProgramsMap(map) {
    if (map.size === this.getMapSizeById(1)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(1, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(1);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(1, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeMatchingLoanPrograms(key) {
    return deprecatedMember('GetLoanProgramMatchesResponse', 'removeMatchingLoanPrograms', 'deleteMatchingLoanPrograms', this.deleteMatchingLoanPrograms(key));
  }
  deleteMatchingLoanPrograms(key) {
    return this.removeFromMapById(1, key);
  }
  clearMatchingLoanProgramsMap() {
    return this.clearMap(1);
  }
}
_defineProperty$n(GetLoanProgramMatchesResponse_, protoName, DEV_MODE ? 'GetLoanProgramMatchesResponse' : '[Unknown]');
let GetLoanProgramMatchesResponse = GetLoanProgramMatchesResponse_;
(function (_GetLoanProgramMatchesResponse) {})(GetLoanProgramMatchesResponse || (GetLoanProgramMatchesResponse = {}));
Object.defineProperties(GetLoanProgramMatchesResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "matching_loan_programs": {
        value: 1
      },
      "matchingLoanPrograms": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "matching_loan_programs"
      }
    }))
  }
});
Object.defineProperties(GetLoanProgramMatchesResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: [null, RepeatedLoanProgram_.prototype]
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(GetLoanProgramMatchesResponse);
  Object.freeze(GetLoanProgramMatchesResponse.prototype);
}

async function getCapitalProvider(ctr, sym) {
  return ctr.state.getLoanProgram(sym) || (await ctr.batchLoad('Provider', getPrograms, sym));
}
async function getProviders(ctr, syms) {
  const map = new Map();
  const neededSyms = new Set();
  for (const sym of syms) {
    const value = ctr.state.getLoanProgram(sym);
    if (value) {
      map.set(sym, value);
    } else {
      neededSyms.add(sym);
    }
  }
  if (neededSyms.size > 0) {
    const response = GetLoanProgramsResponse.fromJson(await getLoanProgramsBySyms(ctr.sharedUser, [...neededSyms]));
    let state = ctr.state;
    for (const provider of response.loanProgramList) {
      map.set(provider.sym, provider);
      state = state.setLoanProgram(provider.sym, provider);
    }
    for (const org of response.organizationList) {
      state = state.setOrganization(org.sym, org);
    }
    ctr.setState(state);
  }
  return map;
}

async function getPrograms(
  ctr,
  syms,
) {
  const map = new Map();

  const neededSyms = new Set();
  for (const sym of syms) {
    const value = ctr.state.getLoanProgram(sym);
    if (value) {
      map.set(sym, value);
    } else {
      neededSyms.add(sym);
    }
  }

  if (neededSyms.size > 0) {
    const programsData =
      await getProgramsBySyms(ctr.sharedUser, [...neededSyms]);
    const programs = new Map(Object.entries(programsData));
    let state = ctr.state;
    for (const [sym, pData] of programs) {
      const program = LoanProgram.from(pData);
      map.set(sym, program);
      state = state.setLoanProgram(sym, program);
    }
    ctr.setState(state);
  }

  return map;
}

function searchCapitalProviders$1(...args) {
  return postWithChecksum(resolve('/@r:/capital_provider/search.searchCapitalProviders'), args, ChecksumType.CRC32C);
}

function _defineProperty$m(obj, key, value) { key = _toPropertyKey$m(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$m(arg) { var key = _toPrimitive$m(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$m(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var RecourseOption__;
(function (RecourseOption__) {
  RecourseOption__[RecourseOption__["NON_RECOURSE"] = 0] = "NON_RECOURSE";
  RecourseOption__[RecourseOption__["PARTIAL_RECOURSE"] = 1] = "PARTIAL_RECOURSE";
})(RecourseOption__ || (RecourseOption__ = {}));
const RecourseOption_ = defineEnumProperties(RecourseOption__, [0, 1]);
const RecourseOption = extendEnum('RecourseOption', [["NON_RECOURSE", 0], ["PARTIAL_RECOURSE", 1]])(RecourseOption_);
var AmortizationType__;
(function (AmortizationType__) {
  AmortizationType__[AmortizationType__["INTEREST_ONLY"] = 0] = "INTEREST_ONLY";
  AmortizationType__[AmortizationType__["PARTIAL_INTEREST_ONLY"] = 1] = "PARTIAL_INTEREST_ONLY";
})(AmortizationType__ || (AmortizationType__ = {}));
const AmortizationType_ = defineEnumProperties(AmortizationType__, [0, 1]);
const AmortizationType = extendEnum('AmortizationType', [["INTEREST_ONLY", 0], ["PARTIAL_INTEREST_ONLY", 1]])(AmortizationType_);
var SubEquityType__;
(function (SubEquityType__) {
  SubEquityType__[SubEquityType__["PREFERRED_EQUITY"] = 0] = "PREFERRED_EQUITY";
  SubEquityType__[SubEquityType__["LP_EQUITY"] = 1] = "LP_EQUITY";
  SubEquityType__[SubEquityType__["COGP_EQUITY"] = 2] = "COGP_EQUITY";
})(SubEquityType__ || (SubEquityType__ = {}));
const SubEquityType_ = defineEnumProperties(SubEquityType__, [0, 1, 2]);
const SubEquityType = extendEnum('SubEquityType', [["PREFERRED_EQUITY", 0], ["LP_EQUITY", 1], ["COGP_EQUITY", 2]])(SubEquityType_);
var PreferredReturnType__;
(function (PreferredReturnType__) {
  PreferredReturnType__[PreferredReturnType__["CURRENT_PAY"] = 0] = "CURRENT_PAY";
  PreferredReturnType__[PreferredReturnType__["ACCRUED"] = 1] = "ACCRUED";
})(PreferredReturnType__ || (PreferredReturnType__ = {}));
const PreferredReturnType_ = defineEnumProperties(PreferredReturnType__, [0, 1]);
const PreferredReturnType = extendEnum('PreferredReturnType', [["CURRENT_PAY", 0], ["ACCRUED", 1]])(PreferredReturnType_);
var Instrument__;
(function (Instrument__) {
  Instrument__[Instrument__["SENIOR"] = 0] = "SENIOR";
  Instrument__[Instrument__["SUBORDINATE"] = 1] = "SUBORDINATE";
  Instrument__[Instrument__["EQUITY"] = 2] = "EQUITY";
  Instrument__[Instrument__["CPACE"] = 3] = "CPACE";
  Instrument__[Instrument__["GROUND_LEASE"] = 4] = "GROUND_LEASE";
  Instrument__[Instrument__["LINE_OF_CREDIT"] = 5] = "LINE_OF_CREDIT";
})(Instrument__ || (Instrument__ = {}));
const Instrument_ = defineEnumProperties(Instrument__, [0, 1, 2, 3, 4, 5]);
const Instrument = extendEnum('Instrument', [["SENIOR", 0], ["SUBORDINATE", 1], ["EQUITY", 2], ["CPACE", 3], ["GROUND_LEASE", 4], ["LINE_OF_CREDIT", 5]])(Instrument_);
class ProviderFilters_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: 0,
      3: 0,
      4: TermRange.shape,
      5: TermRange.shape,
      6: 0,
      7: CurrencyValue.shape,
      8: Percent.shape,
      9: Percent.shape,
      10: Percent.shape,
      11: Percent.shape,
      12: Percent.shape,
      13: Percent.shape,
      14: 1,
      15: 1,
      16: 0,
      17: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProviderFilters field instrument.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return Instrument.fromStrictJson_(v, getFieldMap);
            }
            return Instrument.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProviderFilters field program_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return LoanProgramType.fromStrictJson_(v, getFieldMap);
            }
            return LoanProgramType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 3:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProviderFilters field recourse.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return RecourseOption.fromStrictJson_(v, getFieldMap);
            }
            return RecourseOption.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TermRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field term.
                Expected type TermRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TermRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(TermRange);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field amortization.
                Expected type TermRange.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'TermRange', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProviderFilters field amortization_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return AmortizationType.fromStrictJson_(v, getFieldMap);
            }
            return AmortizationType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CurrencyValue);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field loan_amount.
                Expected type CurrencyValue.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CurrencyValue', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field ltc.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field debt_yield.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field dscr.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field as_stabilized_ltv.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProviderFilters field as_stabilized_debt_yield.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProviderFilters field allows_subordinate_financing.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProviderFilters field allows_c_pace.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 16:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProviderFilters field sub_equity_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return SubEquityType.fromStrictJson_(v, getFieldMap);
            }
            return SubEquityType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 17:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            if (strictJson) {
              assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProviderFilters field preferred_return_type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
              const v = value;
              return PreferredReturnType.fromStrictJson_(v, getFieldMap);
            }
            return PreferredReturnType.from(value);
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProviderFilters, 'ProviderFilters', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProviderFilters to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get instrumentList() {
    return this.getRepeatedFieldById(1);
  }
  hasInstrument(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addInstrument(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachInstrument(values) {
    let T = this;
    for (const value of values) {
      T = T.addInstrument(value);
    }
    return T;
  }
  setInstrument(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeInstrument(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearInstrumentList() {
    return this.clearRepeatedFieldById(1);
  }
  setInstrumentList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get programTypeList() {
    return this.getRepeatedFieldById(2);
  }
  hasProgramType(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addProgramType(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachProgramType(values) {
    let T = this;
    for (const value of values) {
      T = T.addProgramType(value);
    }
    return T;
  }
  setProgramType(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeProgramType(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearProgramTypeList() {
    return this.clearRepeatedFieldById(2);
  }
  setProgramTypeList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
  get recourseList() {
    return this.getRepeatedFieldById(3);
  }
  hasRecourse(value) {
    return this.repeatedFieldByIdHas(3, value);
  }
  addRecourse(value) {
    return this.addToRepeatedFieldById(3, value);
  }
  addEachRecourse(values) {
    let T = this;
    for (const value of values) {
      T = T.addRecourse(value);
    }
    return T;
  }
  setRecourse(index, value) {
    return this.setRepeatedFieldById(3, index, value);
  }
  removeRecourse(index) {
    return this.removeFromRepeatedFieldById(3, index);
  }
  clearRecourseList() {
    return this.clearRepeatedFieldById(3);
  }
  setRecourseList(values) {
    return this.overwriteRepeatedFieldById(3, values);
  }
  get term() {
    return this.getFieldById(4, TermRange.getDefault());
  }
  setTerm(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteTerm() {
    return this.setValueFieldById(4, null, null);
  }
  get amortization() {
    return this.getFieldById(5, TermRange.getDefault());
  }
  setAmortization(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteAmortization() {
    return this.setValueFieldById(5, null, null);
  }
  get amortizationTypeList() {
    return this.getRepeatedFieldById(6);
  }
  hasAmortizationType(value) {
    return this.repeatedFieldByIdHas(6, value);
  }
  addAmortizationType(value) {
    return this.addToRepeatedFieldById(6, value);
  }
  addEachAmortizationType(values) {
    let T = this;
    for (const value of values) {
      T = T.addAmortizationType(value);
    }
    return T;
  }
  setAmortizationType(index, value) {
    return this.setRepeatedFieldById(6, index, value);
  }
  removeAmortizationType(index) {
    return this.removeFromRepeatedFieldById(6, index);
  }
  clearAmortizationTypeList() {
    return this.clearRepeatedFieldById(6);
  }
  setAmortizationTypeList(values) {
    return this.overwriteRepeatedFieldById(6, values);
  }
  get loanAmount() {
    return this.getFieldById(7, CurrencyValue.getDefault());
  }
  setLoanAmount(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteLoanAmount() {
    return this.setValueFieldById(7, null, null);
  }
  get ltc() {
    return this.getFieldById(8, Percent.getDefault());
  }
  setLtc(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteLtc() {
    return this.setValueFieldById(8, null, null);
  }
  get ltv() {
    return this.getFieldById(9, Percent.getDefault());
  }
  setLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deleteLtv() {
    return this.setValueFieldById(9, null, null);
  }
  get debtYield() {
    return this.getFieldById(10, Percent.getDefault());
  }
  setDebtYield(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteDebtYield() {
    return this.setValueFieldById(10, null, null);
  }
  get dscr() {
    return this.getFieldById(11, Percent.getDefault());
  }
  setDscr(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteDscr() {
    return this.setValueFieldById(11, null, null);
  }
  get asStabilizedLtv() {
    return this.getFieldById(12, Percent.getDefault());
  }
  setAsStabilizedLtv(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deleteAsStabilizedLtv() {
    return this.setValueFieldById(12, null, null);
  }
  get asStabilizedDebtYield() {
    return this.getFieldById(13, Percent.getDefault());
  }
  setAsStabilizedDebtYield(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteAsStabilizedDebtYield() {
    return this.setValueFieldById(13, null, null);
  }
  get allowsSubordinateFinancing() {
    return this.getFieldById(14, false);
  }
  setAllowsSubordinateFinancing(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteAllowsSubordinateFinancing() {
    return this.setValueFieldById(14, null, null);
  }
  get allowsCPace() {
    return this.getFieldById(15, false);
  }
  setAllowsCPace(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, null);
  }
  deleteAllowsCPace() {
    return this.setValueFieldById(15, null, null);
  }
  get subEquityTypeList() {
    return this.getRepeatedFieldById(16);
  }
  hasSubEquityType(value) {
    return this.repeatedFieldByIdHas(16, value);
  }
  addSubEquityType(value) {
    return this.addToRepeatedFieldById(16, value);
  }
  addEachSubEquityType(values) {
    let T = this;
    for (const value of values) {
      T = T.addSubEquityType(value);
    }
    return T;
  }
  setSubEquityType(index, value) {
    return this.setRepeatedFieldById(16, index, value);
  }
  removeSubEquityType(index) {
    return this.removeFromRepeatedFieldById(16, index);
  }
  clearSubEquityTypeList() {
    return this.clearRepeatedFieldById(16);
  }
  setSubEquityTypeList(values) {
    return this.overwriteRepeatedFieldById(16, values);
  }
  get preferredReturnTypeList() {
    return this.getRepeatedFieldById(17);
  }
  hasPreferredReturnType(value) {
    return this.repeatedFieldByIdHas(17, value);
  }
  addPreferredReturnType(value) {
    return this.addToRepeatedFieldById(17, value);
  }
  addEachPreferredReturnType(values) {
    let T = this;
    for (const value of values) {
      T = T.addPreferredReturnType(value);
    }
    return T;
  }
  setPreferredReturnType(index, value) {
    return this.setRepeatedFieldById(17, index, value);
  }
  removePreferredReturnType(index) {
    return this.removeFromRepeatedFieldById(17, index);
  }
  clearPreferredReturnTypeList() {
    return this.clearRepeatedFieldById(17);
  }
  setPreferredReturnTypeList(values) {
    return this.overwriteRepeatedFieldById(17, values);
  }
}
_defineProperty$m(ProviderFilters_, protoName, DEV_MODE ? 'ProviderFilters' : '[Unknown]');
let ProviderFilters = ProviderFilters_;
(function (_ProviderFilters) {})(ProviderFilters || (ProviderFilters = {}));
Object.defineProperties(ProviderFilters, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "instrument": {
        value: 1
      },
      "program_type": {
        value: 2
      },
      "programType": {
        value: 2
      },
      "recourse": {
        value: 3
      },
      "term": {
        value: 4
      },
      "amortization": {
        value: 5
      },
      "amortization_type": {
        value: 6
      },
      "amortizationType": {
        value: 6
      },
      "loan_amount": {
        value: 7
      },
      "loanAmount": {
        value: 7
      },
      "ltc": {
        value: 8
      },
      "ltv": {
        value: 9
      },
      "debt_yield": {
        value: 10
      },
      "debtYield": {
        value: 10
      },
      "dscr": {
        value: 11
      },
      "as_stabilized_ltv": {
        value: 12
      },
      "asStabilizedLtv": {
        value: 12
      },
      "as_stabilized_debt_yield": {
        value: 13
      },
      "asStabilizedDebtYield": {
        value: 13
      },
      "allows_subordinate_financing": {
        value: 14
      },
      "allowsSubordinateFinancing": {
        value: 14
      },
      "allows_c_pace": {
        value: 15
      },
      "allowsCPace": {
        value: 15
      },
      "sub_equity_type": {
        value: 16
      },
      "subEquityType": {
        value: 16
      },
      "preferred_return_type": {
        value: 17
      },
      "preferredReturnType": {
        value: 17
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "instrument"
      },
      '2': {
        value: "program_type"
      },
      '3': {
        value: "recourse"
      },
      '4': {
        value: "term"
      },
      '5': {
        value: "amortization"
      },
      '6': {
        value: "amortization_type"
      },
      '7': {
        value: "loan_amount"
      },
      '8': {
        value: "ltc"
      },
      '9': {
        value: "ltv"
      },
      '10': {
        value: "debt_yield"
      },
      '11': {
        value: "dscr"
      },
      '12': {
        value: "as_stabilized_ltv"
      },
      '13': {
        value: "as_stabilized_debt_yield"
      },
      '14': {
        value: "allows_subordinate_financing"
      },
      '15': {
        value: "allows_c_pace"
      },
      '16': {
        value: "sub_equity_type"
      },
      '17': {
        value: "preferred_return_type"
      }
    }))
  }
});
Object.defineProperties(ProviderFilters.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      4: TermRange.prototype,
      5: TermRange.prototype,
      7: CurrencyValue.prototype,
      8: Percent.prototype,
      9: Percent.prototype,
      10: Percent.prototype,
      11: Percent.prototype,
      12: Percent.prototype,
      13: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProviderFilters);
  Object.freeze(ProviderFilters.prototype);
}

function _defineProperty$l(obj, key, value) { key = _toPropertyKey$l(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$l(arg) { var key = _toPrimitive$l(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$l(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class CapitalProvidersPage_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CapitalProvidersPage field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message CapitalProvidersPage field continuation_token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(CapitalProvidersPage, 'CapitalProvidersPage', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message CapitalProvidersPage to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get symList() {
    return this.getRepeatedFieldById(1);
  }
  hasSym(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addSym(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachSym(values) {
    let T = this;
    for (const value of values) {
      T = T.addSym(value);
    }
    return T;
  }
  setSym(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeSym(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearSymList() {
    return this.clearRepeatedFieldById(1);
  }
  setSymList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get continuationToken() {
    return this.getFieldById(2, "");
  }
  setContinuationToken(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteContinuationToken() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$l(CapitalProvidersPage_, protoName, DEV_MODE ? 'CapitalProvidersPage' : '[Unknown]');
let CapitalProvidersPage = CapitalProvidersPage_;
(function (_CapitalProvidersPage) {})(CapitalProvidersPage || (CapitalProvidersPage = {}));
Object.defineProperties(CapitalProvidersPage, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "continuation_token": {
        value: 2
      },
      "continuationToken": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "continuation_token"
      }
    }))
  }
});
Object.defineProperties(CapitalProvidersPage.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(CapitalProvidersPage);
  Object.freeze(CapitalProvidersPage.prototype);
}
class SearchCapitalProvidersResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: CapitalProvidersPage.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(CapitalProvidersPage);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message SearchCapitalProvidersResponse field page.
                Expected type CapitalProvidersPage.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'CapitalProvidersPage', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SearchCapitalProvidersResponse, 'SearchCapitalProvidersResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SearchCapitalProvidersResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get page() {
    return this.getFieldById(1, CapitalProvidersPage.getDefault());
  }
  setPage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deletePage() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$l(SearchCapitalProvidersResponse_, protoName, DEV_MODE ? 'SearchCapitalProvidersResponse' : '[Unknown]');
let SearchCapitalProvidersResponse = SearchCapitalProvidersResponse_;
(function (_SearchCapitalProvidersResponse) {})(SearchCapitalProvidersResponse || (SearchCapitalProvidersResponse = {}));
Object.defineProperties(SearchCapitalProvidersResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "page": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "page"
      }
    }))
  }
});
Object.defineProperties(SearchCapitalProvidersResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: CapitalProvidersPage.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SearchCapitalProvidersResponse);
  Object.freeze(SearchCapitalProvidersResponse.prototype);
}

async function searchCapitalProviders(ctr, projectSym, filters, continuationToken = '') {
  const response = SearchCapitalProvidersResponse.fromJson(await searchCapitalProviders$1(ctr.sharedUser, projectSym, ProviderFilters.from(filters || {}), continuationToken));
  const page = response.page;
  const out = {
    syms: page.symList
  };
  if (page.continuationToken) {
    out.next = () => searchCapitalProviders(ctr, projectSym, filters, page.continuationToken);
  }
  return out;
}

function _defineProperty$k(obj, key, value) { key = _toPropertyKey$k(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$k(arg) { var key = _toPrimitive$k(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$k(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Providers {
  constructor(ava, ctr) {
    _defineProperty$k(this, "ava", void 0);
    _defineProperty$k(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  search(projectSym, filters) {
    return searchCapitalProviders(this.ctr, projectSym, filters);
  }
  get(sym) {
    return getCapitalProvider(this.ctr, sym);
  }
}

function deleteSecurityKey(...args) {
  return postWithChecksum(resolve('/@r:/user/security_keys.deleteSecurityKey'), args, ChecksumType.CRC32C);
}
function getSecurityKeys(...args) {
  return postWithChecksum(resolve('/@r:/user/security_keys.getSecurityKeys'), args, ChecksumType.CRC32C);
}
function getSecurityKeyChallenge$1(...args) {
  return postWithChecksum(resolve('/@r:/user/security_keys.getSecurityKeyChallenge'), args, ChecksumType.CRC32C);
}
function registerSecurityKey$1(...args) {
  return postWithChecksum(resolve('/@r:/user/security_keys.registerSecurityKey'), args, ChecksumType.CRC32C);
}
function verifySecurityKey$1(...args) {
  return postWithChecksum(resolve('/@r:/user/security_keys.verifySecurityKey'), args, ChecksumType.CRC32C);
}

function _defineProperty$j(obj, key, value) { key = _toPropertyKey$j(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$j(arg) { var key = _toPrimitive$j(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$j(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class WebauthnCredential_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message WebauthnCredential field id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message WebauthnCredential field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(WebauthnCredential, 'WebauthnCredential', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message WebauthnCredential to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, "");
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$j(WebauthnCredential_, protoName, DEV_MODE ? 'WebauthnCredential' : '[Unknown]');
let WebauthnCredential = WebauthnCredential_;
(function (_WebauthnCredential) {})(WebauthnCredential || (WebauthnCredential = {}));
Object.defineProperties(WebauthnCredential, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "name": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "name"
      }
    }))
  }
});
Object.defineProperties(WebauthnCredential.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(WebauthnCredential);
  Object.freeze(WebauthnCredential.prototype);
}

function _defineProperty$i(obj, key, value) { key = _toPropertyKey$i(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$i(arg) { var key = _toPrimitive$i(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$i(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class SecurityKey_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: WebauthnCredential.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message SecurityKey field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(WebauthnCredential);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message SecurityKey field credential.
                Expected type WebauthnCredential.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'WebauthnCredential', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(SecurityKey, 'SecurityKey', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message SecurityKey to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get credential() {
    return this.getFieldById(2, WebauthnCredential.getDefault());
  }
  setCredential(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteCredential() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$i(SecurityKey_, protoName, DEV_MODE ? 'SecurityKey' : '[Unknown]');
let SecurityKey = SecurityKey_;
(function (_SecurityKey) {})(SecurityKey || (SecurityKey = {}));
Object.defineProperties(SecurityKey, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "credential": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "credential"
      }
    }))
  }
});
Object.defineProperties(SecurityKey.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: WebauthnCredential.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(SecurityKey);
  Object.freeze(SecurityKey.prototype);
}

function encodeDataUrlBase64(data, type = 'application/octet-stream') {
  assert(/^[\w-]+\/[\w-]+$/.test(type), emsg`Invalid data URL type: ${type}`);
  return `data:${type};base64,${toBase64(data)}`;
}
function toBase64(data) {
  const out = [];
  for (const u of new Uint8Array(data)) {
    out.push(String.fromCharCode(u));
  }
  return btoa(out.join(''));
}

function base64ToBase64Url(s) {
  return s.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}
function base64UrlToBase64(s) {
  return padBase64(s.replace(/-/g, '+').replace(/_/g, '/'));
}
function base64ToArrayBuffer(s) {
  return Uint8Array.from(atob(s), u => u.charCodeAt(0)).buffer;
}
function base64UrlToArrayBuffer(s) {
  return base64ToArrayBuffer(base64UrlToBase64(s));
}
function padBase64(s) {
  return s + '='.repeat((4 - s.length % 4) % 4);
}

function cborToDataUrl(data) {
  return encodeDataUrlBase64(data, 'application/cbor');
}

const DATA_URL = /^data:([\w.\-+]+\/[\w\-.+]+)?(?:;charset=([\w-]+))?(;base64)?,(.*)$/;
const DEFAULT_CHARSET = 'utf8';
function decodeDataUrl(url, ensureType) {
  const m = DATA_URL.exec(url);
  let charset;
  let type;
  let isBase64;
  let dataStr;
  if (!m) {
    ensure.same(url, 'data:', emsg`Invalid upload starting with: ${url.slice(0, 40)}`);
    type = 'application/octet-stream';
    charset = DEFAULT_CHARSET;
    isBase64 = true;
    dataStr = '';
  } else {
    type = m[1] || 'text/plain';
    charset = m[2] || DEFAULT_CHARSET;
    isBase64 = m[3] === ';base64';
    dataStr = m[4];
  }
  const data = isBase64 ? base64UrlToArrayBuffer(dataStr) : decodeText(dataStr, charset);
  if (ensureType === undefined) {
    return {
      type,
      data
    };
  }
  ensure.same(type, ensureType);
  return data;
}
function decodeText(text, charset) {
  ensure(/^utf-?8$/i.test(charset), emsg`Unsupported charset: ${charset}`);
  return new TextEncoder().encode(decodeURIComponent(text));
}

async function getSecurityKeyChallenge() {
  return decodeDataUrl(await getSecurityKeyChallenge$1(), 'application/octet-stream');
}

function _defineProperty$h(obj, key, value) { key = _toPropertyKey$h(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$h(arg) { var key = _toPrimitive$h(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$h(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
async function registerSecurityKey(forUserProfile, keyName) {
  const excludeCredentials = [];
  let credential;
  const [keysData, challenge] = await Promise.all([getSecurityKeys(forUserProfile.sym), getSecurityKeyChallenge()]);
  const existingKeys = keysData.map(u => SecurityKey.fromJson(u));
  for (const key of existingKeys) {
    excludeCredentials.push({
      id: base64UrlToArrayBuffer(key.credential.id),
      type: 'public-key'
    });
  }
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        attestation: 'direct',
        authenticatorSelection: {
          authenticatorAttachment: 'cross-platform'
        },
        challenge,
        excludeCredentials,
        pubKeyCredParams: [{
          type: 'public-key',
          alg: -7 // ES256
        }],

        rp: {
          name: 'StackSource'
        },
        timeout: 30000,
        user: {
          displayName: forUserProfile.name,
          name: forUserProfile.email,
          id: new TextEncoder().encode(forUserProfile.sym)
        }
      }
    });
  } catch (e) {
    if (e instanceof DOMException && e.code === 11) {
      throw new SecurityKeyAlreadyRegisteredError(e);
    }
    throw e;
  }
  if (credential == null) {
    throw new CannotCreateSecurityKeyError();
  }
  const attestation = cborToDataUrl(credential.response.attestationObject);
  const clientData = encodeDataUrlBase64(credential.response.clientDataJSON);
  const sym = await registerSecurityKey$1(forUserProfile.sym, credential.id, keyName, clientData, attestation);
  return new SecurityKey().setSym(sym).setCredential(new WebauthnCredential().setId(credential.id).setName(keyName));
}
const securityKeyAlreadyRegisteredErrorTag = Symbol('SecurityKeyAlreadyRegisteredError');
class SecurityKeyAlreadyRegisteredError extends CustomError {
  static isInstance(value) {
    return !!value && securityKeyAlreadyRegisteredErrorTag in value;
  }
  constructor(cause) {
    super('That security key has already been registered', cause);
    _defineProperty$h(this, securityKeyAlreadyRegisteredErrorTag, true);
  }
}
const cannotCreateSecurityKeyErrorTag = Symbol('CannotCreateSecurityKeyError');
class CannotCreateSecurityKeyError extends CustomError {
  static isInstance(value) {
    return !!value && cannotCreateSecurityKeyErrorTag in value;
  }
  constructor() {
    super('Cannot create security key');
    _defineProperty$h(this, cannotCreateSecurityKeyErrorTag, true);
  }
}

async function verifySecurityKey(userSym = '') {
  const [keysData, challenge] = await Promise.all([getSecurityKeys(userSym), getSecurityKeyChallenge()]);
  const keys = keysData.map(u => SecurityKey.fromJson(u));
  let credential;
  try {
    credential = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: keys.map(u => ({
          id: base64UrlToArrayBuffer(u.credential.id),
          type: 'public-key',
          transports: ['usb', 'nfc']
        })),
        userVerification: 'discouraged',
        timeout: 60000
      }
    });
  } catch (e) {
    if (e && e instanceof DOMException && e.code === 0) {
      // This seems to be what is indicated by the trapped exception above,
      // but I'm not sure the test is sufficient, so I'm adding this log here
      // to help in debugging and prevent this exception from occuring
      // silently in other cases.
      console.log('Security key signature cancelled by user');
      return undefined;
    }
    throw e;
  }
  if (!credential) {
    return undefined;
  }
  const authData = cborToDataUrl(credential.response.authenticatorData);
  const clientData = encodeDataUrlBase64(credential.response.clientDataJSON);
  const signature = encodeDataUrlBase64(credential.response.signature);
  const verifiedSym = await verifySecurityKey$1(userSym, encodeDataUrlBase64(credential.rawId), authData, clientData, signature);
  if (!verifiedSym) {
    return undefined;
  }
  for (const key of keys) {
    if (key.sym === verifiedSym) {
      return key;
    }
  }
  assert.fail(emsg`Expected to find security key: ${verifiedSym}`);
  return undefined;
}

function _defineProperty$g(obj, key, value) { key = _toPropertyKey$g(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$g(arg) { var key = _toPrimitive$g(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$g(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

// Adding some properties missing from DOM Credential in the current version
// of TypeScript we're using.
// TODO(nathan): Remove this interface when TypeScript is updated to support
// more Credential properties.

class SecurityKeys {
  constructor(ava, ctr) {
    _defineProperty$g(this, "ava", void 0);
    _defineProperty$g(this, "ctr", void 0);
    _defineProperty$g(this, "verify", verifySecurityKey);
    this.ava = ava;
    this.ctr = ctr;
  }
  async register(forUser, keyName) {
    const profile = await this.ava.userProfiles.get(forUser);
    return registerSecurityKey(profile, keyName);
  }
}

function _defineProperty$f(obj, key, value) { key = _toPropertyKey$f(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$f(arg) { var key = _toPrimitive$f(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$f(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Sessions {
  constructor(ava, ctr) {
    _defineProperty$f(this, "ava", void 0);
    _defineProperty$f(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  deactivateCurrent() {
    return deprecatedMember('Ava', 'sessions.deactivateCurrent', 'auth.signOut', this.ava.auth.signOut());
  }
}

function addLenders(...args) {
  return postWithChecksum(resolve('/@r:/user/share.addLenders'), args, ChecksumType.CRC32C);
}
function addLendersToOrganizationShare(...args) {
  return postWithChecksum(resolve('/@r:/user/share.addLendersToOrganizationShare'), args, ChecksumType.CRC32C);
}
function addLendersToLoanProgramShare(...args) {
  return postWithChecksum(resolve('/@r:/user/share.addLendersToLoanProgramShare'), args, ChecksumType.CRC32C);
}
function addLendersToProjectShare(...args) {
  return postWithChecksum(resolve('/@r:/user/share.addLendersToProjectShare'), args, ChecksumType.CRC32C);
}
function inviteLendersInProjectShares(...args) {
  return postWithChecksum(resolve('/@r:/user/share.inviteLendersInProjectShares'), args, ChecksumType.CRC32C);
}
function getNewShareToken(...args) {
  return postWithChecksum(resolve('/@r:/user/share.getNewShareToken'), args, ChecksumType.CRC32C);
}
function getShares$1(...args) {
  return postWithChecksum(resolve('/@r:/user/share.getShares'), args, ChecksumType.CRC32C);
}
function setShareDeclinedByLender(...args) {
  return postWithChecksum(resolve('/@r:/user/share.setShareDeclinedByLender'), args, ChecksumType.CRC32C);
}
function setShareDeclinedBySponsor(...args) {
  return postWithChecksum(resolve('/@r:/user/share.setShareDeclinedBySponsor'), args, ChecksumType.CRC32C);
}
function deleteShare(...args) {
  return postWithChecksum(resolve('/@r:/user/share.deleteShare'), args, ChecksumType.CRC32C);
}

function getLoanProgramTypeLabel(loanProgramOrType, orgOrLocationLabel) {
  if (orgOrLocationLabel === undefined) orgOrLocationLabel = '';
  if (loanProgramOrType instanceof LoanProgram) {
    const locationLabel = typeof orgOrLocationLabel == 'string' ? orgOrLocationLabel : orgOrLocationLabel ? orgOrLocationLabel.locationLabel : '';
    return LoanProgramType.format(loanProgramOrType.type, locationLabel);
  }
  runtime.checkAsserts() ? runtime.ensure(typeof orgOrLocationLabel == 'string', null) : undefined;
  return LoanProgramType.format(loanProgramOrType, orgOrLocationLabel);
}
function formatTypicalPricing(typicalPricing) {
  return typicalPricing.format();
}
function typicalPricingRateIndexToString(typicalPricing) {
  return typicalPricing.formatRateIndex();
}

function _defineProperty$e(obj, key, value) { key = _toPropertyKey$e(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$e(arg) { var key = _toPrimitive$e(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$e(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _ProjectShareUpdate_ConcealedUpdate_;
(function (_ProjectShareUpdate_ConcealedUpdate_) {
  _ProjectShareUpdate_ConcealedUpdate_[_ProjectShareUpdate_ConcealedUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectShareUpdate_ConcealedUpdate_[_ProjectShareUpdate_ConcealedUpdate_["CONCEALED"] = 4] = "CONCEALED";
})(_ProjectShareUpdate_ConcealedUpdate_ || (_ProjectShareUpdate_ConcealedUpdate_ = {}));
const _ProjectShareUpdate_ConcealedUpdate = defineEnumProperties(_ProjectShareUpdate_ConcealedUpdate_, [4]);
var _ProjectShareUpdate_ActiveUpdate_;
(function (_ProjectShareUpdate_ActiveUpdate_) {
  _ProjectShareUpdate_ActiveUpdate_[_ProjectShareUpdate_ActiveUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectShareUpdate_ActiveUpdate_[_ProjectShareUpdate_ActiveUpdate_["ACTIVE"] = 2] = "ACTIVE";
})(_ProjectShareUpdate_ActiveUpdate_ || (_ProjectShareUpdate_ActiveUpdate_ = {}));
const _ProjectShareUpdate_ActiveUpdate = defineEnumProperties(_ProjectShareUpdate_ActiveUpdate_, [2]);
var _ProjectShareUpdate_NdaStatusUpdate_;
(function (_ProjectShareUpdate_NdaStatusUpdate_) {
  _ProjectShareUpdate_NdaStatusUpdate_[_ProjectShareUpdate_NdaStatusUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectShareUpdate_NdaStatusUpdate_[_ProjectShareUpdate_NdaStatusUpdate_["NDA_STATUS"] = 3] = "NDA_STATUS";
})(_ProjectShareUpdate_NdaStatusUpdate_ || (_ProjectShareUpdate_NdaStatusUpdate_ = {}));
const _ProjectShareUpdate_NdaStatusUpdate = defineEnumProperties(_ProjectShareUpdate_NdaStatusUpdate_, [3]);
var _ProjectShareUpdate_StacksourceSourcedUpdate_;
(function (_ProjectShareUpdate_StacksourceSourcedUpdate_) {
  _ProjectShareUpdate_StacksourceSourcedUpdate_[_ProjectShareUpdate_StacksourceSourcedUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectShareUpdate_StacksourceSourcedUpdate_[_ProjectShareUpdate_StacksourceSourcedUpdate_["STACKSOURCE_SOURCED"] = 5] = "STACKSOURCE_SOURCED";
})(_ProjectShareUpdate_StacksourceSourcedUpdate_ || (_ProjectShareUpdate_StacksourceSourcedUpdate_ = {}));
const _ProjectShareUpdate_StacksourceSourcedUpdate = defineEnumProperties(_ProjectShareUpdate_StacksourceSourcedUpdate_, [5]);
var _ProjectShareUpdate_LenderFeeUpdate_;
(function (_ProjectShareUpdate_LenderFeeUpdate_) {
  _ProjectShareUpdate_LenderFeeUpdate_[_ProjectShareUpdate_LenderFeeUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectShareUpdate_LenderFeeUpdate_[_ProjectShareUpdate_LenderFeeUpdate_["LENDER_FEE"] = 6] = "LENDER_FEE";
})(_ProjectShareUpdate_LenderFeeUpdate_ || (_ProjectShareUpdate_LenderFeeUpdate_ = {}));
const _ProjectShareUpdate_LenderFeeUpdate = defineEnumProperties(_ProjectShareUpdate_LenderFeeUpdate_, [6]);
var _ProjectShareUpdate_RequireOfferApprovalUpdate_;
(function (_ProjectShareUpdate_RequireOfferApprovalUpdate_) {
  _ProjectShareUpdate_RequireOfferApprovalUpdate_[_ProjectShareUpdate_RequireOfferApprovalUpdate_["UNSET"] = 0] = "UNSET";
  _ProjectShareUpdate_RequireOfferApprovalUpdate_[_ProjectShareUpdate_RequireOfferApprovalUpdate_["REQUIRE_OFFER_APPROVAL"] = 7] = "REQUIRE_OFFER_APPROVAL";
})(_ProjectShareUpdate_RequireOfferApprovalUpdate_ || (_ProjectShareUpdate_RequireOfferApprovalUpdate_ = {}));
const _ProjectShareUpdate_RequireOfferApprovalUpdate = defineEnumProperties(_ProjectShareUpdate_RequireOfferApprovalUpdate_, [7]);
class ProjectShareUpdate_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      4: 1,
      2: 1,
      3: 0,
      5: 1,
      6: Percent.shape,
      7: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareUpdate field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareUpdate field concealed.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareUpdate field active.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareUpdate field nda_status.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectShareNdaStatus.fromStrictJson_(v, getFieldMap);
          }
          return ProjectShareNdaStatus.from(value);
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareUpdate field stacksource_sourced.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Percent);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareUpdate field lender_fee.
                Expected type Percent.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Percent', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectShareUpdate field require_offer_approval.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectShareUpdate, 'ProjectShareUpdate', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectShareUpdate to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get ConcealedUpdateCase() {
    return _ProjectShareUpdate_ConcealedUpdate;
  }
  get concealedUpdateCase() {
    return this.getCaseFromIds([4]);
  }
  static get ActiveUpdateCase() {
    return _ProjectShareUpdate_ActiveUpdate;
  }
  get activeUpdateCase() {
    return this.getCaseFromIds([2]);
  }
  static get NdaStatusUpdateCase() {
    return _ProjectShareUpdate_NdaStatusUpdate;
  }
  get ndaStatusUpdateCase() {
    return this.getCaseFromIds([3]);
  }
  static get StacksourceSourcedUpdateCase() {
    return _ProjectShareUpdate_StacksourceSourcedUpdate;
  }
  get stacksourceSourcedUpdateCase() {
    return this.getCaseFromIds([5]);
  }
  static get LenderFeeUpdateCase() {
    return _ProjectShareUpdate_LenderFeeUpdate;
  }
  get lenderFeeUpdateCase() {
    return this.getCaseFromIds([6]);
  }
  static get RequireOfferApprovalUpdateCase() {
    return _ProjectShareUpdate_RequireOfferApprovalUpdate;
  }
  get requireOfferApprovalUpdateCase() {
    return this.getCaseFromIds([7]);
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get concealed() {
    return this.getFieldById(4, false);
  }
  setConcealed(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, "concealed_update");
  }
  deleteConcealed() {
    return this.setValueFieldById(4, null, "concealed_update");
  }
  get active() {
    return this.getFieldById(2, false);
  }
  setActive(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "active_update");
  }
  deleteActive() {
    return this.setValueFieldById(2, null, "active_update");
  }
  get ndaStatus() {
    return this.getFieldById(3, 0);
  }
  setNdaStatus(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "nda_status_update");
  }
  deleteNdaStatus() {
    return this.setValueFieldById(3, null, "nda_status_update");
  }
  get stacksourceSourced() {
    return this.getFieldById(5, false);
  }
  setStacksourceSourced(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, "stacksource_sourced_update");
  }
  deleteStacksourceSourced() {
    return this.setValueFieldById(5, null, "stacksource_sourced_update");
  }
  get lenderFee() {
    return this.getFieldById(6, Percent.getDefault());
  }
  setLenderFee(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, "lender_fee_update");
  }
  deleteLenderFee() {
    return this.setValueFieldById(6, null, "lender_fee_update");
  }
  get requireOfferApproval() {
    return this.getFieldById(7, false);
  }
  setRequireOfferApproval(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, "require_offer_approval_update");
  }
  deleteRequireOfferApproval() {
    return this.setValueFieldById(7, null, "require_offer_approval_update");
  }
}
_defineProperty$e(ProjectShareUpdate_, protoName, DEV_MODE ? 'ProjectShareUpdate' : '[Unknown]');
let ProjectShareUpdate = ProjectShareUpdate_;
(function (_ProjectShareUpdate) {})(ProjectShareUpdate || (ProjectShareUpdate = {}));
Object.defineProperties(ProjectShareUpdate, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "concealed": {
        value: 4
      },
      "active": {
        value: 2
      },
      "nda_status": {
        value: 3
      },
      "ndaStatus": {
        value: 3
      },
      "stacksource_sourced": {
        value: 5
      },
      "stacksourceSourced": {
        value: 5
      },
      "lender_fee": {
        value: 6
      },
      "lenderFee": {
        value: 6
      },
      "require_offer_approval": {
        value: 7
      },
      "requireOfferApproval": {
        value: 7
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '4': {
        value: "concealed"
      },
      '2': {
        value: "active"
      },
      '3': {
        value: "nda_status"
      },
      '5': {
        value: "stacksource_sourced"
      },
      '6': {
        value: "lender_fee"
      },
      '7': {
        value: "require_offer_approval"
      }
    }))
  }
});
Object.defineProperties(ProjectShareUpdate.prototype, {
  oneofs_: {
    value: {
      "concealed_update": [4],
      "active_update": [2],
      "nda_status_update": [3],
      "stacksource_sourced_update": [5],
      "lender_fee_update": [6],
      "require_offer_approval_update": [7]
    }
  },
  prototypes_: {
    value: {
      6: Percent.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectShareUpdate);
  Object.freeze(ProjectShareUpdate.prototype);
}

function getProjectShareBlindName(projectShare) {
  const loanProgramType = projectShare.loanProgramType;
  const locationLabel = projectShare.locationLabel;
  return getLoanProgramTypeLabel(loanProgramType, locationLabel);
}

async function createFromProvider(ava, ctr, projectSym, providerSym) {
  const provider = await ava.providers.get(providerSym);
  const responses = await addLendersToLoanProgramShare(projectSym, providerSym, provider.lenderEmailList.toArray(), false,
  // sendEmail
  !provider.preferredLender,
  // concealed
  provider.lenderFee, provider.requireOfferApproval);
  const shares = responses.map(u => ProjectShareInfo.fromJson(u));
  const shareSym = ensure(shares.length === 1 && shares[0].sym, emsg`
      There was a problem creating a share for project ${projectSym}
      from provider ${providerSym}. It may conflict with an existing share.`);
  ctr.dealChanged(projectSym);
  return shareSym;
}

async function createInviteUrl(ava, unused_ctr, shareSym) {
  const [share, token] = await Promise.all([ava.shares.get(shareSym), getNewShareToken(shareSym)]);
  return `${getAppRoot()}/deal:${share.deal}/view#s=${token}`;
}

async function setShareDeclined(ava, ctr, sym, declined) {
  const user = await ava.auth.getCurrentUser();
  // If the user is signed in, decline as a sponsor/capital advisor.
  // Note: This will need to be adjusted if lenders get user accounts.
  if (user?.sym) {
    await setShareDeclinedBySponsor(sym, declined, false // undeclineByEmail
    );
  } else {
    await setShareDeclinedByLender(ctr.sharedUser, declined);
  }
  const share = ctr.state.getShare(sym);
  if (share) {
    ctr.setState(ctr.state.setShare(sym, share.setDeclined(declined ? ShareDeclined.MANUAL : ShareDeclined.UNSET)));
  }
}

async function removeShare(ava, ctr, sym) {
  const share = await ava.shares.get(sym);
  await deleteShare(sym);
  ctr.dealChanged(share.project);
}

function _defineProperty$d(obj, key, value) { key = _toPropertyKey$d(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$d(arg) { var key = _toPrimitive$d(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$d(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class GetSharesResponse_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: ProjectShareInfo.shape,
      2: Organization.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectShareInfo);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetSharesResponse field project_share_info.
                Expected type ProjectShareInfo.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectShareInfo', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 2:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Organization);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message GetSharesResponse field organization.
                Expected type Organization.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Organization', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(GetSharesResponse, 'GetSharesResponse', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message GetSharesResponse to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get projectShareInfoList() {
    return this.getRepeatedFieldById(1);
  }
  hasProjectShareInfo(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addProjectShareInfo(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachProjectShareInfo(values) {
    let T = this;
    for (const value of values) {
      T = T.addProjectShareInfo(value);
    }
    return T;
  }
  setProjectShareInfo(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeProjectShareInfo(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearProjectShareInfoList() {
    return this.clearRepeatedFieldById(1);
  }
  setProjectShareInfoList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
  get organizationList() {
    return this.getRepeatedFieldById(2);
  }
  hasOrganization(value) {
    return this.repeatedFieldByIdHas(2, value);
  }
  addOrganization(value) {
    return this.addToRepeatedFieldById(2, value);
  }
  addEachOrganization(values) {
    let T = this;
    for (const value of values) {
      T = T.addOrganization(value);
    }
    return T;
  }
  setOrganization(index, value) {
    return this.setRepeatedFieldById(2, index, value);
  }
  removeOrganization(index) {
    return this.removeFromRepeatedFieldById(2, index);
  }
  clearOrganizationList() {
    return this.clearRepeatedFieldById(2);
  }
  setOrganizationList(values) {
    return this.overwriteRepeatedFieldById(2, values);
  }
}
_defineProperty$d(GetSharesResponse_, protoName, DEV_MODE ? 'GetSharesResponse' : '[Unknown]');
let GetSharesResponse = GetSharesResponse_;
(function (_GetSharesResponse) {})(GetSharesResponse || (GetSharesResponse = {}));
Object.defineProperties(GetSharesResponse, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "project_share_info": {
        value: 1
      },
      "projectShareInfo": {
        value: 1
      },
      "organization": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "project_share_info"
      },
      '2': {
        value: "organization"
      }
    }))
  }
});
Object.defineProperties(GetSharesResponse.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: ProjectShareInfo.prototype,
      2: Organization.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(GetSharesResponse);
  Object.freeze(GetSharesResponse.prototype);
}

async function getShare(ctr, sym) {
  const share = ctr.state.getShare(sym);
  if (share) {
    return share;
  }
  return ctr.batchLoad('Share', getShares, sym);
}
async function getShares(ctr, syms) {
  const shares = new Map();
  const neededSyms = new Set();
  for (const sym of syms) {
    const share = ctr.state.getShare(sym);
    if (share) {
      shares.set(sym, share);
    } else {
      neededSyms.add(sym);
    }
  }
  if (neededSyms.size > 0) {
    const response = GetSharesResponse.fromJson(await getShares$1([...neededSyms]));
    let state = ctr.state;
    for (const ps of response.projectShareInfoList) {
      // Delete the IDs so they're not accidentally used in Buble.
      const share = projectShareToShare(ps);
      shares.set(share.sym, share);
      state = state.setShare(share.sym, share);
    }
    for (const org of response.organizationList) {
      state = state.setOrganization(org.sym, org);
    }
    ctr.setState(state);
  }
  return shares;
}
function projectShareToShare(ps) {
  return new Share().setSym(ps.sym).setDeal(ps.projectSym).setOrg(ps.orgSym).setLabel(ps.orgOrLabel.stringValue).setLocationLabel(ps.locationLabel).setActive(ps.active).setDeclined(getDeclined(ps)).setViews(ps.uses).setInvitationSentByPlatform(ps.invitationSent).setTokenFirstCreated(ps.tokenFirstCreated).setConcealed(ps.concealed).setStacksourceSourced(ps.stacksourceSourced).setOfferList(ps.offerSymList).setProvider(ps.loanProgram);
}
function getDeclined(ps) {
  switch (true) {
    // Note: We have to check `autoDeclined` before checking `declined` because
    // `declined` will always be true if `autoDeclined` is true for
    // `ProjectShareInfo`.
    case ps.autoDeclined:
      return ShareDeclined.AUTO;
    case ps.declined:
      return ShareDeclined.MANUAL;
    case ps.declinedViaEmailLink:
      return ShareDeclined.EMAIL;
    default:
      return ShareDeclined.UNSET;
  }
}

function getProjectShareUsers(...args) {
  return postWithChecksum(resolve('/@r:/project/share.getProjectShareUsers'), args, ChecksumType.CRC32C);
}
function updateProjectShare(...args) {
  return postWithChecksum(resolve('/@r:/project/share.updateProjectShare'), args, ChecksumType.CRC32C);
}

function _defineProperty$c(obj, key, value) { key = _toPropertyKey$c(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$c(arg) { var key = _toPrimitive$c(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$c(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var ProjectShareUserActivityType__;
(function (ProjectShareUserActivityType__) {
  ProjectShareUserActivityType__[ProjectShareUserActivityType__["INVITE"] = 0] = "INVITE";
  ProjectShareUserActivityType__[ProjectShareUserActivityType__["VIEW"] = 1] = "VIEW";
})(ProjectShareUserActivityType__ || (ProjectShareUserActivityType__ = {}));
const ProjectShareUserActivityType_ = defineEnumProperties(ProjectShareUserActivityType__, [0, 1]);
const ProjectShareUserActivityType = extendEnum('ProjectShareUserActivityType', [["INVITE", 0], ["VIEW", 1]])(ProjectShareUserActivityType_);
class ProjectShareUserActivity_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 0,
      2: DEPRECATED_Date.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectShareUserActivity field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectShareUserActivityType.fromStrictJson_(v, getFieldMap);
          }
          return ProjectShareUserActivityType.from(value);
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareUserActivity field date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectShareUserActivity, 'ProjectShareUserActivity', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectShareUserActivity to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get type() {
    return this.getFieldById(1, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteType() {
    return this.setValueFieldById(1, null, null);
  }
  get date() {
    return this.getFieldById(2, DEPRECATED_Date.getDefault());
  }
  setDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteDate() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$c(ProjectShareUserActivity_, protoName, DEV_MODE ? 'ProjectShareUserActivity' : '[Unknown]');
let ProjectShareUserActivity = ProjectShareUserActivity_;
(function (_ProjectShareUserActivity) {})(ProjectShareUserActivity || (ProjectShareUserActivity = {}));
Object.defineProperties(ProjectShareUserActivity, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type": {
        value: 1
      },
      "date": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type"
      },
      '2': {
        value: "date"
      }
    }))
  }
});
Object.defineProperties(ProjectShareUserActivity.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      2: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectShareUserActivity);
  Object.freeze(ProjectShareUserActivity.prototype);
}
class ProjectShareUser_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 7,
      3: 2,
      4: DEPRECATED_Date.shape,
      5: ProjectShareUserActivity.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareUser field share.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectShareUser field share_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message ProjectShareUser field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareUser field date_added.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 5:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectShareUserActivity);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message ProjectShareUser field activity.
                Expected type ProjectShareUserActivity.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectShareUserActivity', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectShareUser, 'ProjectShareUser', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectShareUser to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get share() {
    return this.getFieldById(1, "");
  }
  setShare(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteShare() {
    return this.setValueFieldById(1, null, null);
  }
  get shareId() {
    return this.getFieldById(2, 0);
  }
  setShareId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteShareId() {
    return this.setValueFieldById(2, null, null);
  }
  get email() {
    return this.getFieldById(3, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(3, null, null);
  }
  get dateAdded() {
    return this.getFieldById(4, DEPRECATED_Date.getDefault());
  }
  setDateAdded(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteDateAdded() {
    return this.setValueFieldById(4, null, null);
  }
  get activityList() {
    return this.getRepeatedFieldById(5);
  }
  hasActivity(value) {
    return this.repeatedFieldByIdHas(5, value);
  }
  addActivity(value) {
    return this.addToRepeatedFieldById(5, value);
  }
  addEachActivity(values) {
    let T = this;
    for (const value of values) {
      T = T.addActivity(value);
    }
    return T;
  }
  setActivity(index, value) {
    return this.setRepeatedFieldById(5, index, value);
  }
  removeActivity(index) {
    return this.removeFromRepeatedFieldById(5, index);
  }
  clearActivityList() {
    return this.clearRepeatedFieldById(5);
  }
  setActivityList(values) {
    return this.overwriteRepeatedFieldById(5, values);
  }
}
_defineProperty$c(ProjectShareUser_, protoName, DEV_MODE ? 'ProjectShareUser' : '[Unknown]');
let ProjectShareUser = ProjectShareUser_;
(function (_ProjectShareUser) {})(ProjectShareUser || (ProjectShareUser = {}));
Object.defineProperties(ProjectShareUser, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "share": {
        value: 1
      },
      "share_id": {
        value: 2
      },
      "shareId": {
        value: 2
      },
      "email": {
        value: 3
      },
      "date_added": {
        value: 4
      },
      "dateAdded": {
        value: 4
      },
      "activity": {
        value: 5
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "share"
      },
      '2': {
        value: "share_id"
      },
      '3': {
        value: "email"
      },
      '4': {
        value: "date_added"
      },
      '5': {
        value: "activity"
      }
    }))
  }
});
Object.defineProperties(ProjectShareUser.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      4: DEPRECATED_Date.prototype,
      5: ProjectShareUserActivity.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectShareUser);
  Object.freeze(ProjectShareUser.prototype);
}
class RepeatedProjectShareUser_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: ProjectShareUser.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectShareUser);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedProjectShareUser field value.
                Expected type ProjectShareUser.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectShareUser', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedProjectShareUser, 'RepeatedProjectShareUser', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedProjectShareUser to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$c(RepeatedProjectShareUser_, protoName, DEV_MODE ? 'RepeatedProjectShareUser' : '[Unknown]');
let RepeatedProjectShareUser = RepeatedProjectShareUser_;
(function (_RepeatedProjectShareUser) {})(RepeatedProjectShareUser || (RepeatedProjectShareUser = {}));
Object.defineProperties(RepeatedProjectShareUser, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedProjectShareUser.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: ProjectShareUser.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedProjectShareUser);
  Object.freeze(RepeatedProjectShareUser.prototype);
}

function getContacts(ctr, sym) {
  return ctr.batchLoad('Share contact', batchGetContacts, sym);
}
async function batchGetContacts(ctr, syms) {
  const contacts = new Map();
  for (const data of await getProjectShareUsers(ctr.sharedUser, syms)) {
    const shareUser = ProjectShareUser.fromJson(data);
    let shareUsers = contacts.get(shareUser.share);
    if (!shareUsers) {
      shareUsers = [];
      contacts.set(shareUser.share, shareUsers);
    }
    shareUsers.push(shareUser);
  }
  return contacts;
}

async function sendInvites(unused_ava, unused_ctr, shareSym) {
  await inviteLendersInProjectShares([shareSym]);
}

function _defineProperty$b(obj, key, value) { key = _toPropertyKey$b(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$b(arg) { var key = _toPrimitive$b(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$b(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Shares {
  constructor(ava, ctr) {
    _defineProperty$b(this, "ava", void 0);
    _defineProperty$b(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  createFromProvider(projectSym, providerSym) {
    return createFromProvider(this.ava, this.ctr, projectSym, providerSym);
  }
  get(sym) {
    return getShare(this.ctr, sym);
  }
  getContacts(sym) {
    return getContacts(this.ctr, sym);
  }
  sendInvites(sym) {
    return sendInvites(this.ava, this.ctr, sym);
  }
  createInviteUrl(sym) {
    return createInviteUrl(this.ava, this.ctr, sym);
  }
  delete(sym) {
    return removeShare(this.ava, this.ctr, sym);
  }
  async decline(sym) {
    await setShareDeclined(this.ava, this.ctr, sym, true);
  }
  async undecline(sym) {
    await setShareDeclined(this.ava, this.ctr, sym, false);
  }
}

function getUserProfileBySym(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.getUserProfileBySym'), args, ChecksumType.CRC32C);
}
function getUserProfilesBySyms(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.getUserProfilesBySyms'), args, ChecksumType.CRC32C);
}
function getUserProfilesByIds(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.getUserProfilesByIds'), args, ChecksumType.CRC32C);
}
function getUserProfilesByEmails(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.getUserProfilesByEmails'), args, ChecksumType.CRC32C);
}
function getUserProfilesByRole(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.getUserProfilesByRole'), args, ChecksumType.CRC32C);
}
function getAllUserProfiles(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.getAllUserProfiles'), args, ChecksumType.CRC32C);
}
function updateUserProfile$1(...args) {
  return postWithChecksum(resolve('/@r:/user/profile.updateUserProfile'), args, ChecksumType.CRC32C);
}

function _defineProperty$a(obj, key, value) { key = _toPropertyKey$a(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$a(arg) { var key = _toPrimitive$a(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$a(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class UserProfileImage_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfileImage field key.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UserProfileImage$1, 'UserProfileImage', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UserProfileImage to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get key() {
    return this.getFieldById(1, "");
  }
  setKey(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteKey() {
    return this.setValueFieldById(1, null, null);
  }
}
_defineProperty$a(UserProfileImage_, protoName, DEV_MODE ? 'UserProfileImage' : '[Unknown]');
let UserProfileImage$1 = UserProfileImage_;
(function (_UserProfileImage) {})(UserProfileImage$1 || (UserProfileImage$1 = {}));
Object.defineProperties(UserProfileImage$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "key": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "key"
      }
    }))
  }
});
Object.defineProperties(UserProfileImage$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UserProfileImage$1);
  Object.freeze(UserProfileImage$1.prototype);
}

class UserProfileImage extends UserProfileImage_ {
  get url() {
    return resourceKeyToUrl(this.key);
  }
  get thumbnailUrl() {
    const url = resourceKeyToUrl(this.key);
    if (!url) {
      return '';
    }
    return imagePathToUserProfileThumbnail(url);
  }
  get tinyThumbnailUrl() {
    const url = resourceKeyToUrl(this.key);
    if (!url) {
      return '';
    }
    return imagePathToUserProfileTinyThumbnail(url);
  }
}

registerCanonical(UserProfileImage_, UserProfileImage);

function _defineProperty$9(obj, key, value) { key = _toPropertyKey$9(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$9(arg) { var key = _toPrimitive$9(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$9(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var ContactMethod__;
(function (ContactMethod__) {
  ContactMethod__[ContactMethod__["EMAIL"] = 0] = "EMAIL";
  ContactMethod__[ContactMethod__["PHONE"] = 1] = "PHONE";
})(ContactMethod__ || (ContactMethod__ = {}));
const ContactMethod_ = defineEnumProperties(ContactMethod__, [0, 1]);
const ContactMethod = extendEnum('ContactMethod', [["EMAIL", 0], ["PHONE", 1]])(ContactMethod_);
class UserProfile_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 2,
      2: 2,
      14: 1,
      3: 2,
      4: 2,
      5: DEPRECATED_Date.shape,
      6: UserProfileImage.shape,
      7: 2,
      8: StandardUsAddress.shape,
      9: InternationalPhoneNumber.shape,
      10: RichText.shape,
      11: DEPRECATED_Date.shape,
      12: 0,
      13: 0
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 14:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message UserProfile field email_verified.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field first_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field last_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 5:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field date_of_birth.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UserProfileImage);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field profile_image.
                Expected type UserProfileImage.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UserProfileImage', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfile field organization.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field office_address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(InternationalPhoneNumber);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field phone_number.
                Expected type InternationalPhoneNumber.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'InternationalPhoneNumber', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field bio.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfile field join_date.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message UserProfile field preferred_contact_method.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ContactMethod.fromStrictJson_(v, getFieldMap);
          }
          return ContactMethod.from(value);
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message UserProfile field gender.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return UserGender.fromStrictJson_(v, getFieldMap);
          }
          return UserGender.from(value);
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UserProfile$1, 'UserProfile', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UserProfile to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get sym() {
    return this.getFieldById(1, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteSym() {
    return this.setValueFieldById(1, null, null);
  }
  get email() {
    return this.getFieldById(2, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(2, null, null);
  }
  get emailVerified() {
    return this.getFieldById(14, false);
  }
  setEmailVerified(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, null);
  }
  deleteEmailVerified() {
    return this.setValueFieldById(14, null, null);
  }
  get firstName() {
    return this.getFieldById(3, "");
  }
  setFirstName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteFirstName() {
    return this.setValueFieldById(3, null, null);
  }
  get lastName() {
    return this.getFieldById(4, "");
  }
  setLastName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteLastName() {
    return this.setValueFieldById(4, null, null);
  }
  get dateOfBirth() {
    return this.getFieldById(5, DEPRECATED_Date.getDefault());
  }
  setDateOfBirth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteDateOfBirth() {
    return this.setValueFieldById(5, null, null);
  }
  get profileImage() {
    return this.getFieldById(6, UserProfileImage.getDefault());
  }
  setProfileImage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteProfileImage() {
    return this.setValueFieldById(6, null, null);
  }
  get organization() {
    return this.getFieldById(7, "");
  }
  setOrganization(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteOrganization() {
    return this.setValueFieldById(7, null, null);
  }
  get officeAddress() {
    return this.getFieldById(8, StandardUsAddress.getDefault());
  }
  setOfficeAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, null);
  }
  deleteOfficeAddress() {
    return this.setValueFieldById(8, null, null);
  }
  get phoneNumber() {
    return this.getFieldById(9, InternationalPhoneNumber.getDefault());
  }
  setPhoneNumber(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, null);
  }
  deletePhoneNumber() {
    return this.setValueFieldById(9, null, null);
  }
  get bio() {
    return this.getFieldById(10, RichText.getDefault());
  }
  setBio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, null);
  }
  deleteBio() {
    return this.setValueFieldById(10, null, null);
  }
  get joinDate() {
    return this.getFieldById(11, DEPRECATED_Date.getDefault());
  }
  setJoinDate(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, null);
  }
  deleteJoinDate() {
    return this.setValueFieldById(11, null, null);
  }
  get preferredContactMethod() {
    return this.getFieldById(12, 0);
  }
  setPreferredContactMethod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, null);
  }
  deletePreferredContactMethod() {
    return this.setValueFieldById(12, null, null);
  }
  get gender() {
    return this.getFieldById(13, 0);
  }
  setGender(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, null);
  }
  deleteGender() {
    return this.setValueFieldById(13, null, null);
  }
}
_defineProperty$9(UserProfile_, protoName, DEV_MODE ? 'UserProfile' : '[Unknown]');
let UserProfile$1 = UserProfile_;
(function (_UserProfile) {})(UserProfile$1 || (UserProfile$1 = {}));
Object.defineProperties(UserProfile$1, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "sym": {
        value: 1
      },
      "email": {
        value: 2
      },
      "email_verified": {
        value: 14
      },
      "emailVerified": {
        value: 14
      },
      "first_name": {
        value: 3
      },
      "firstName": {
        value: 3
      },
      "last_name": {
        value: 4
      },
      "lastName": {
        value: 4
      },
      "date_of_birth": {
        value: 5
      },
      "dateOfBirth": {
        value: 5
      },
      "profile_image": {
        value: 6
      },
      "profileImage": {
        value: 6
      },
      "organization": {
        value: 7
      },
      "office_address": {
        value: 8
      },
      "officeAddress": {
        value: 8
      },
      "phone_number": {
        value: 9
      },
      "phoneNumber": {
        value: 9
      },
      "bio": {
        value: 10
      },
      "join_date": {
        value: 11
      },
      "joinDate": {
        value: 11
      },
      "preferred_contact_method": {
        value: 12
      },
      "preferredContactMethod": {
        value: 12
      },
      "gender": {
        value: 13
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "sym"
      },
      '2': {
        value: "email"
      },
      '14': {
        value: "email_verified"
      },
      '3': {
        value: "first_name"
      },
      '4': {
        value: "last_name"
      },
      '5': {
        value: "date_of_birth"
      },
      '6': {
        value: "profile_image"
      },
      '7': {
        value: "organization"
      },
      '8': {
        value: "office_address"
      },
      '9': {
        value: "phone_number"
      },
      '10': {
        value: "bio"
      },
      '11': {
        value: "join_date"
      },
      '12': {
        value: "preferred_contact_method"
      },
      '13': {
        value: "gender"
      }
    }))
  }
});
Object.defineProperties(UserProfile$1.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      5: DEPRECATED_Date.prototype,
      6: UserProfileImage.prototype,
      8: StandardUsAddress.prototype,
      9: InternationalPhoneNumber.prototype,
      10: RichText.prototype,
      11: DEPRECATED_Date.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UserProfile$1);
  Object.freeze(UserProfile$1.prototype);
}

class UserProfile extends UserProfile_ {
  get name() {
    return this.firstName ? this.lastName ? `${this.firstName} ${this.lastName}` : this.firstName : this.lastName;
  }
}

registerCanonical(ContactMethod, ContactMethod);
registerCanonical(UserProfile_, UserProfile);

function toAvaUserProfile(u) {
  if (!u) {
    return null;
  }
  return new UserProfile().setSym(u.sym).setEmail(u.email).setEmailVerified(u.emailVerified).setFirstName(u.firstName).setLastName(u.lastName).setProfileImage(resourceKeyToUserProfileImage(u.profileImageKey)).setOrganization(u.organizationSym).setOfficeAddress(u.officeAddress).setPhoneNumber(u.phoneNumber).setBio(u.bio).setJoinDate(u.joinDate).setPreferredContactMethod(u.preferredContactMethod).setGender(u.gender);
}
function resourceKeyToUserProfileImage(key) {
  return new UserProfileImage().setKey(key);
}

const profiles = new BatchLoader({
  typeLabel: 'user profile',
  load: getUserProfiles,
  cache: {
    maxSize: 256,
    maxAge: 60 * 60 * 1000
  },
  initController(ctr) {
    ctr.onUserProfileChanged(sym => profiles.uncache(ctr, sym));
  }
});
function getUserProfile(ctr, sym) {
  return profiles.load(ctr, sym);
}
async function getUserProfiles(ctr, syms) {
  const map = new Map();
  const results = await getUserProfilesBySyms(ctr.sharedUser, syms);
  for (const [sym, data] of Object.entries(results)) {
    const profile = toAvaUserProfile(UserProfile$2.fromJson(data));
    // Setting both `sym` and `profile.sym` here in case the sym has changed.
    map.set(sym, profile);
    map.set(profile.sym, profile);
  }
  return map;
}

async function getAllAdvisors(ctr) {
  return (await getUserProfilesByRole(ctr.sharedUser, 'stack_source_capital_advisor')).map(u => toAvaUserProfile(UserProfile$2.fromJson(u)));
}

function _defineProperty$8(obj, key, value) { key = _toPropertyKey$8(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$8(arg) { var key = _toPrimitive$8(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$8(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var _UserProfileUpdate_Identifier_;
(function (_UserProfileUpdate_Identifier_) {
  _UserProfileUpdate_Identifier_[_UserProfileUpdate_Identifier_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_Identifier_[_UserProfileUpdate_Identifier_["ID"] = 1] = "ID";
  _UserProfileUpdate_Identifier_[_UserProfileUpdate_Identifier_["SYM"] = 15] = "SYM";
})(_UserProfileUpdate_Identifier_ || (_UserProfileUpdate_Identifier_ = {}));
const _UserProfileUpdate_Identifier = defineEnumProperties(_UserProfileUpdate_Identifier_, [1, 15]);
var _UserProfileUpdate_FirstNameUpdate_;
(function (_UserProfileUpdate_FirstNameUpdate_) {
  _UserProfileUpdate_FirstNameUpdate_[_UserProfileUpdate_FirstNameUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_FirstNameUpdate_[_UserProfileUpdate_FirstNameUpdate_["FIRST_NAME"] = 2] = "FIRST_NAME";
})(_UserProfileUpdate_FirstNameUpdate_ || (_UserProfileUpdate_FirstNameUpdate_ = {}));
const _UserProfileUpdate_FirstNameUpdate = defineEnumProperties(_UserProfileUpdate_FirstNameUpdate_, [2]);
var _UserProfileUpdate_LastNameUpdate_;
(function (_UserProfileUpdate_LastNameUpdate_) {
  _UserProfileUpdate_LastNameUpdate_[_UserProfileUpdate_LastNameUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_LastNameUpdate_[_UserProfileUpdate_LastNameUpdate_["LAST_NAME"] = 3] = "LAST_NAME";
})(_UserProfileUpdate_LastNameUpdate_ || (_UserProfileUpdate_LastNameUpdate_ = {}));
const _UserProfileUpdate_LastNameUpdate = defineEnumProperties(_UserProfileUpdate_LastNameUpdate_, [3]);
var _UserProfileUpdate_ProfileImageUpdate_;
(function (_UserProfileUpdate_ProfileImageUpdate_) {
  _UserProfileUpdate_ProfileImageUpdate_[_UserProfileUpdate_ProfileImageUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_ProfileImageUpdate_[_UserProfileUpdate_ProfileImageUpdate_["PROFILE_IMAGE_URL"] = 4] = "PROFILE_IMAGE_URL";
})(_UserProfileUpdate_ProfileImageUpdate_ || (_UserProfileUpdate_ProfileImageUpdate_ = {}));
const _UserProfileUpdate_ProfileImageUpdate = defineEnumProperties(_UserProfileUpdate_ProfileImageUpdate_, [4]);
var _UserProfileUpdate_OfficeAddressUpdate_;
(function (_UserProfileUpdate_OfficeAddressUpdate_) {
  _UserProfileUpdate_OfficeAddressUpdate_[_UserProfileUpdate_OfficeAddressUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_OfficeAddressUpdate_[_UserProfileUpdate_OfficeAddressUpdate_["OFFICE_ADDRESS"] = 7] = "OFFICE_ADDRESS";
})(_UserProfileUpdate_OfficeAddressUpdate_ || (_UserProfileUpdate_OfficeAddressUpdate_ = {}));
const _UserProfileUpdate_OfficeAddressUpdate = defineEnumProperties(_UserProfileUpdate_OfficeAddressUpdate_, [7]);
var _UserProfileUpdate_PreferredContactMethodUpdate_;
(function (_UserProfileUpdate_PreferredContactMethodUpdate_) {
  _UserProfileUpdate_PreferredContactMethodUpdate_[_UserProfileUpdate_PreferredContactMethodUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_PreferredContactMethodUpdate_[_UserProfileUpdate_PreferredContactMethodUpdate_["PREFERRED_CONTACT_METHOD"] = 10] = "PREFERRED_CONTACT_METHOD";
})(_UserProfileUpdate_PreferredContactMethodUpdate_ || (_UserProfileUpdate_PreferredContactMethodUpdate_ = {}));
const _UserProfileUpdate_PreferredContactMethodUpdate = defineEnumProperties(_UserProfileUpdate_PreferredContactMethodUpdate_, [10]);
var _UserProfileUpdate_PhoneNumberUpdate_;
(function (_UserProfileUpdate_PhoneNumberUpdate_) {
  _UserProfileUpdate_PhoneNumberUpdate_[_UserProfileUpdate_PhoneNumberUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_PhoneNumberUpdate_[_UserProfileUpdate_PhoneNumberUpdate_["PHONE_NUMBER"] = 9] = "PHONE_NUMBER";
})(_UserProfileUpdate_PhoneNumberUpdate_ || (_UserProfileUpdate_PhoneNumberUpdate_ = {}));
const _UserProfileUpdate_PhoneNumberUpdate = defineEnumProperties(_UserProfileUpdate_PhoneNumberUpdate_, [9]);
var _UserProfileUpdate_BioUpdate_;
(function (_UserProfileUpdate_BioUpdate_) {
  _UserProfileUpdate_BioUpdate_[_UserProfileUpdate_BioUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_BioUpdate_[_UserProfileUpdate_BioUpdate_["BIO"] = 8] = "BIO";
})(_UserProfileUpdate_BioUpdate_ || (_UserProfileUpdate_BioUpdate_ = {}));
const _UserProfileUpdate_BioUpdate = defineEnumProperties(_UserProfileUpdate_BioUpdate_, [8]);
var _UserProfileUpdate_DateOfBirthUpdate_;
(function (_UserProfileUpdate_DateOfBirthUpdate_) {
  _UserProfileUpdate_DateOfBirthUpdate_[_UserProfileUpdate_DateOfBirthUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_DateOfBirthUpdate_[_UserProfileUpdate_DateOfBirthUpdate_["DATE_OF_BIRTH"] = 11] = "DATE_OF_BIRTH";
})(_UserProfileUpdate_DateOfBirthUpdate_ || (_UserProfileUpdate_DateOfBirthUpdate_ = {}));
const _UserProfileUpdate_DateOfBirthUpdate = defineEnumProperties(_UserProfileUpdate_DateOfBirthUpdate_, [11]);
var _UserProfileUpdate_ActiveUpdate_;
(function (_UserProfileUpdate_ActiveUpdate_) {
  _UserProfileUpdate_ActiveUpdate_[_UserProfileUpdate_ActiveUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_ActiveUpdate_[_UserProfileUpdate_ActiveUpdate_["ACTIVE"] = 12] = "ACTIVE";
})(_UserProfileUpdate_ActiveUpdate_ || (_UserProfileUpdate_ActiveUpdate_ = {}));
const _UserProfileUpdate_ActiveUpdate = defineEnumProperties(_UserProfileUpdate_ActiveUpdate_, [12]);
var _UserProfileUpdate_PermissionRolesUpdate_;
(function (_UserProfileUpdate_PermissionRolesUpdate_) {
  _UserProfileUpdate_PermissionRolesUpdate_[_UserProfileUpdate_PermissionRolesUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_PermissionRolesUpdate_[_UserProfileUpdate_PermissionRolesUpdate_["PERMISSION_ROLES"] = 13] = "PERMISSION_ROLES";
})(_UserProfileUpdate_PermissionRolesUpdate_ || (_UserProfileUpdate_PermissionRolesUpdate_ = {}));
const _UserProfileUpdate_PermissionRolesUpdate = defineEnumProperties(_UserProfileUpdate_PermissionRolesUpdate_, [13]);
var _UserProfileUpdate_SlackIdUpdate_;
(function (_UserProfileUpdate_SlackIdUpdate_) {
  _UserProfileUpdate_SlackIdUpdate_[_UserProfileUpdate_SlackIdUpdate_["UNSET"] = 0] = "UNSET";
  _UserProfileUpdate_SlackIdUpdate_[_UserProfileUpdate_SlackIdUpdate_["SLACK_ID"] = 14] = "SLACK_ID";
})(_UserProfileUpdate_SlackIdUpdate_ || (_UserProfileUpdate_SlackIdUpdate_ = {}));
const _UserProfileUpdate_SlackIdUpdate = defineEnumProperties(_UserProfileUpdate_SlackIdUpdate_, [14]);
class UserProfileUpdate_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      15: 2,
      2: 2,
      3: 2,
      4: Url.shape,
      7: StandardUsAddress.shape,
      10: 0,
      9: InternationalPhoneNumber.shape,
      8: RichText.shape,
      11: DEPRECATED_Date.shape,
      12: 1,
      13: RepeatedString.shape,
      14: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message UserProfileUpdate field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 15:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfileUpdate field sym.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfileUpdate field first_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfileUpdate field last_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(Url);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfileUpdate field profile_image_url.
                Expected type Url.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'Url', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 7:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(StandardUsAddress);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfileUpdate field office_address.
                Expected type StandardUsAddress.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'StandardUsAddress', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message UserProfileUpdate field preferred_contact_method.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ContactMethod$1.fromStrictJson_(v, getFieldMap);
          }
          return ContactMethod$1.from(value);
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(InternationalPhoneNumber);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfileUpdate field phone_number.
                Expected type InternationalPhoneNumber.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'InternationalPhoneNumber', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 8:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RichText);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfileUpdate field bio.
                Expected type RichText.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RichText', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(DEPRECATED_Date);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfileUpdate field date_of_birth.
                Expected type DEPRECATED_Date.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'DEPRECATED_Date', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 12:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message UserProfileUpdate field active.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RepeatedString);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message UserProfileUpdate field permission_roles.
                Expected type RepeatedString.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RepeatedString', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 14:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message UserProfileUpdate field slack_id.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(UserProfileUpdate, 'UserProfileUpdate', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message UserProfileUpdate to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  static get IdentifierCase() {
    return _UserProfileUpdate_Identifier;
  }
  get identifierCase() {
    return this.getCaseFromIds([1, 15]);
  }
  static get FirstNameUpdateCase() {
    return _UserProfileUpdate_FirstNameUpdate;
  }
  get firstNameUpdateCase() {
    return this.getCaseFromIds([2]);
  }
  static get LastNameUpdateCase() {
    return _UserProfileUpdate_LastNameUpdate;
  }
  get lastNameUpdateCase() {
    return this.getCaseFromIds([3]);
  }
  static get ProfileImageUpdateCase() {
    return _UserProfileUpdate_ProfileImageUpdate;
  }
  get profileImageUpdateCase() {
    return this.getCaseFromIds([4]);
  }
  static get OfficeAddressUpdateCase() {
    return _UserProfileUpdate_OfficeAddressUpdate;
  }
  get officeAddressUpdateCase() {
    return this.getCaseFromIds([7]);
  }
  static get PreferredContactMethodUpdateCase() {
    return _UserProfileUpdate_PreferredContactMethodUpdate;
  }
  get preferredContactMethodUpdateCase() {
    return this.getCaseFromIds([10]);
  }
  static get PhoneNumberUpdateCase() {
    return _UserProfileUpdate_PhoneNumberUpdate;
  }
  get phoneNumberUpdateCase() {
    return this.getCaseFromIds([9]);
  }
  static get BioUpdateCase() {
    return _UserProfileUpdate_BioUpdate;
  }
  get bioUpdateCase() {
    return this.getCaseFromIds([8]);
  }
  static get DateOfBirthUpdateCase() {
    return _UserProfileUpdate_DateOfBirthUpdate;
  }
  get dateOfBirthUpdateCase() {
    return this.getCaseFromIds([11]);
  }
  static get ActiveUpdateCase() {
    return _UserProfileUpdate_ActiveUpdate;
  }
  get activeUpdateCase() {
    return this.getCaseFromIds([12]);
  }
  static get PermissionRolesUpdateCase() {
    return _UserProfileUpdate_PermissionRolesUpdate;
  }
  get permissionRolesUpdateCase() {
    return this.getCaseFromIds([13]);
  }
  static get SlackIdUpdateCase() {
    return _UserProfileUpdate_SlackIdUpdate;
  }
  get slackIdUpdateCase() {
    return this.getCaseFromIds([14]);
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, "identifier");
  }
  deleteId() {
    return this.setValueFieldById(1, null, "identifier");
  }
  get sym() {
    return this.getFieldById(15, "");
  }
  setSym(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(15, value, "identifier");
  }
  deleteSym() {
    return this.setValueFieldById(15, null, "identifier");
  }
  get firstName() {
    return this.getFieldById(2, "");
  }
  setFirstName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, "first_name_update");
  }
  deleteFirstName() {
    return this.setValueFieldById(2, null, "first_name_update");
  }
  get lastName() {
    return this.getFieldById(3, "");
  }
  setLastName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, "last_name_update");
  }
  deleteLastName() {
    return this.setValueFieldById(3, null, "last_name_update");
  }
  get profileImageUrl() {
    return this.getFieldById(4, Url.getDefault());
  }
  setProfileImageUrl(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, "profile_image_update");
  }
  deleteProfileImageUrl() {
    return this.setValueFieldById(4, null, "profile_image_update");
  }
  get officeAddress() {
    return this.getFieldById(7, StandardUsAddress.getDefault());
  }
  setOfficeAddress(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, "office_address_update");
  }
  deleteOfficeAddress() {
    return this.setValueFieldById(7, null, "office_address_update");
  }
  get preferredContactMethod() {
    return this.getFieldById(10, 0);
  }
  setPreferredContactMethod(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(10, value, "preferred_contact_method_update");
  }
  deletePreferredContactMethod() {
    return this.setValueFieldById(10, null, "preferred_contact_method_update");
  }
  get phoneNumber() {
    return this.getFieldById(9, InternationalPhoneNumber.getDefault());
  }
  setPhoneNumber(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(9, value, "phone_number_update");
  }
  deletePhoneNumber() {
    return this.setValueFieldById(9, null, "phone_number_update");
  }
  get bio() {
    return this.getFieldById(8, RichText.getDefault());
  }
  setBio(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(8, value, "bio_update");
  }
  deleteBio() {
    return this.setValueFieldById(8, null, "bio_update");
  }
  get dateOfBirth() {
    return this.getFieldById(11, DEPRECATED_Date.getDefault());
  }
  setDateOfBirth(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(11, value, "date_of_birth_update");
  }
  deleteDateOfBirth() {
    return this.setValueFieldById(11, null, "date_of_birth_update");
  }
  get active() {
    return this.getFieldById(12, false);
  }
  setActive(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(12, value, "active_update");
  }
  deleteActive() {
    return this.setValueFieldById(12, null, "active_update");
  }
  get permissionRoles() {
    return this.getFieldById(13, RepeatedString.getDefault());
  }
  setPermissionRoles(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(13, value, "permission_roles_update");
  }
  deletePermissionRoles() {
    return this.setValueFieldById(13, null, "permission_roles_update");
  }
  get slackId() {
    return this.getFieldById(14, "");
  }
  setSlackId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(14, value, "slack_id_update");
  }
  deleteSlackId() {
    return this.setValueFieldById(14, null, "slack_id_update");
  }
}
_defineProperty$8(UserProfileUpdate_, protoName, DEV_MODE ? 'UserProfileUpdate' : '[Unknown]');
let UserProfileUpdate = UserProfileUpdate_;
(function (_UserProfileUpdate) {})(UserProfileUpdate || (UserProfileUpdate = {}));
Object.defineProperties(UserProfileUpdate, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "sym": {
        value: 15
      },
      "first_name": {
        value: 2
      },
      "firstName": {
        value: 2
      },
      "last_name": {
        value: 3
      },
      "lastName": {
        value: 3
      },
      "profile_image_url": {
        value: 4
      },
      "profileImageUrl": {
        value: 4
      },
      "office_address": {
        value: 7
      },
      "officeAddress": {
        value: 7
      },
      "preferred_contact_method": {
        value: 10
      },
      "preferredContactMethod": {
        value: 10
      },
      "phone_number": {
        value: 9
      },
      "phoneNumber": {
        value: 9
      },
      "bio": {
        value: 8
      },
      "date_of_birth": {
        value: 11
      },
      "dateOfBirth": {
        value: 11
      },
      "active": {
        value: 12
      },
      "permission_roles": {
        value: 13
      },
      "permissionRoles": {
        value: 13
      },
      "slack_id": {
        value: 14
      },
      "slackId": {
        value: 14
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '15': {
        value: "sym"
      },
      '2': {
        value: "first_name"
      },
      '3': {
        value: "last_name"
      },
      '4': {
        value: "profile_image_url"
      },
      '7': {
        value: "office_address"
      },
      '10': {
        value: "preferred_contact_method"
      },
      '9': {
        value: "phone_number"
      },
      '8': {
        value: "bio"
      },
      '11': {
        value: "date_of_birth"
      },
      '12': {
        value: "active"
      },
      '13': {
        value: "permission_roles"
      },
      '14': {
        value: "slack_id"
      }
    }))
  }
});
Object.defineProperties(UserProfileUpdate.prototype, {
  oneofs_: {
    value: {
      "identifier": [1, 15],
      "first_name_update": [2],
      "last_name_update": [3],
      "profile_image_update": [4],
      "office_address_update": [7],
      "preferred_contact_method_update": [10],
      "phone_number_update": [9],
      "bio_update": [8],
      "date_of_birth_update": [11],
      "active_update": [12],
      "permission_roles_update": [13],
      "slack_id_update": [14]
    }
  },
  prototypes_: {
    value: {
      4: Url.prototype,
      7: StandardUsAddress.prototype,
      9: InternationalPhoneNumber.prototype,
      8: RichText.prototype,
      11: DEPRECATED_Date.prototype,
      13: RepeatedString.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(UserProfileUpdate);
  Object.freeze(UserProfileUpdate.prototype);
}

// TODO(nathan): Automated this and allow access to the json via something like
// UserProfileUpdate.Json

async function updateUserProfile(ctr, update) {
  if (!(update instanceof UserProfileUpdate)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const u = Object.assign({}, update);
    if (u.dateOfBirth && !(u.dateOfBirth instanceof DEPRECATED_Date)) {
      const simpleDate = SimpleDate.from(u.dateOfBirth);
      u.dateOfBirth = DEPRECATED_Date.from(simpleDate.year, simpleDate.month, simpleDate.dayOfMonth);
    }
    if (u.phoneNumber && !(u.phoneNumber instanceof InternationalPhoneNumber) && typeof u.phoneNumber.extension === 'string') {
      u.phoneNumber.extension = Extension.encode(u.phoneNumber.extension);
    }
    update = u;
  }
  await updateUserProfile$1(UserProfileUpdate.from(update));
  ctr.userProfileChanged(update.sym);
}

function _defineProperty$7(obj, key, value) { key = _toPropertyKey$7(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$7(arg) { var key = _toPrimitive$7(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$7(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class UserProfiles {
  constructor(ava, ctr) {
    _defineProperty$7(this, "ava", void 0);
    _defineProperty$7(this, "ctr", void 0);
    this.ava = ava;
    this.ctr = ctr;
  }
  get(sym) {
    return getUserProfile(this.ctr, sym);
  }
  update(update) {
    return updateUserProfile(this.ctr, update);
  }
  getAllAdvisors() {
    return getAllAdvisors(this.ctr);
  }
}

function _defineProperty$6(obj, key, value) { key = _toPropertyKey$6(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$6(arg) { var key = _toPrimitive$6(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$6(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

// This weird renaming seems necessary to export from the namespace.

// eslint-disable-next-line @typescript-eslint/no-namespace
let ClientNamespaces;
(function (_ClientNamespaces) {})(ClientNamespaces || (ClientNamespaces = {}));
class AvaClient {
  constructor(ctr) {
    _defineProperty$6(this, "ctr", void 0);
    _defineProperty$6(this, "auth", void 0);
    _defineProperty$6(this, "deals", void 0);
    _defineProperty$6(this, "offers", void 0);
    _defineProperty$6(this, "orgs", void 0);
    _defineProperty$6(this, "people", void 0);
    _defineProperty$6(this, "providers", void 0);
    _defineProperty$6(this, "securityKeys", void 0);
    _defineProperty$6(this, "sessions", void 0);
    _defineProperty$6(this, "shares", void 0);
    _defineProperty$6(this, "userProfiles", void 0);
    this.auth = new Auth(this, ctr);
    this.deals = new Deals(this, ctr);
    this.offers = new Offers(this, ctr);
    this.orgs = new Orgs(this, ctr);
    this.people = new People(this, ctr);
    this.providers = new Providers(this, ctr);
    this.securityKeys = new SecurityKeys(this, ctr);
    this.sessions = new Sessions(this, ctr);
    this.shares = new Shares(this, ctr);
    this.userProfiles = new UserProfiles(this, ctr);
  }
  async deactivateCurrentSession() {
    await deprecatedMember('Ava', 'deactivateCurrentSession', 'sessions.deactivateCurrent', this.sessions.deactivateCurrent());
  }
  async getUserSettings() {
    return this.ctr.state.userSettings;
  }
  searchProjects(filters, sorts) {
    return deprecatedMember('Ava', 'searchProjects', 'deals.search', this.deals.search(filters, sorts));
  }
  getProjectSummary(sym) {
    return deprecatedMember('Ava', 'getProjectSummary', 'deals.getSummary', this.deals.getSummary(sym));
  }
  getProject(sym) {
    return deprecatedMember('Ava', 'getProject', 'deals.get', this.deals.get(sym));
  }
  createProject(data) {
    return deprecatedMember('Ava', 'createProject', 'deals.create', this.deals.create(data));
  }
  async updateProject(update) {
    await deprecatedMember('Ava', 'updateProject', 'deals.update', this.deals.update(update));
  }
  searchCapitalProviders(projectSym, filters) {
    return deprecatedMember('Ava', 'searchCapitalProviders', 'providers.search', this.providers.search(projectSym, filters));
  }
  getCapitalProvider(sym) {
    return deprecatedMember('Ava', 'getCapitalProvider', 'providers.get', this.providers.get(sym));
  }
  getOffer(sym) {
    return deprecatedMember('Ava', 'getOffer', 'offers.get', this.offers.get(sym));
  }
  createOffer(data) {
    return deprecatedMember('Ava', 'createOffer', 'offers.create', this.offers.create(data));
  }
  async updateOffer(data) {
    await deprecatedMember('Ava', 'updateOffer', 'offers.update', this.offers.update(data));
  }
  async deleteOffer(sym) {
    await deprecatedMember('Ava', 'deleteOffer', 'offers.delete', this.offers.delete(sym));
  }
  async declineProjectShare() {
    await deprecatedMember('Ava', 'declineProjectShare', 'shares.decline', this.shares.decline(''));
  }
  async undeclineProjectShare() {
    await deprecatedMember('Ava', 'undeclineProjectShare', 'shares.undecline', this.shares.undecline(''));
  }
  getUserProfile(sym) {
    return deprecatedMember('Ava', 'getUserProfile', 'userProfiles.get', this.userProfiles.get(sym));
  }
  updateUserProfile(update) {
    return deprecatedMember('Ava', 'updateUserProfile', 'userProfiles.update', this.userProfiles.update(update));
  }
  getOrganizationSummary(sym) {
    return deprecatedMember('Ava', 'getOrganizationSummary', 'getOrganization', this.getOrganization(sym));
  }
  getOrganization(sym) {
    return deprecatedMember('Ava', 'getOrganization', 'orgs.get', this.orgs.get(sym));
  }
  getOrganizationDetails(sym) {
    return deprecatedMember('Ava', 'getOrganizationDetails', 'orgs.getDetails', this.orgs.getDetails(sym));
  }
  downloadAttachments(attachments, projectSym) {
    return deprecatedMember('Ava', 'downloadAttachments', 'projects.attachments.download', this.deals.attachments.download(projectSym, attachments.map(u => u.sym)));
  }
}

function _defineProperty$5(obj, key, value) { key = _toPropertyKey$5(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$5(arg) { var key = _toPrimitive$5(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$5(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class PlatformInvitation_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 2,
      3: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message PlatformInvitation field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message PlatformInvitation field email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message PlatformInvitation field token_hex.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(PlatformInvitation, 'PlatformInvitation', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message PlatformInvitation to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get email() {
    return this.getFieldById(2, "");
  }
  setEmail(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteEmail() {
    return this.setValueFieldById(2, null, null);
  }
  get tokenHex() {
    return this.getFieldById(3, "");
  }
  setTokenHex(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteTokenHex() {
    return this.setValueFieldById(3, null, null);
  }
}
_defineProperty$5(PlatformInvitation_, protoName, DEV_MODE ? 'PlatformInvitation' : '[Unknown]');
let PlatformInvitation = PlatformInvitation_;
(function (_PlatformInvitation) {})(PlatformInvitation || (PlatformInvitation = {}));
Object.defineProperties(PlatformInvitation, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "email": {
        value: 2
      },
      "token_hex": {
        value: 3
      },
      "tokenHex": {
        value: 3
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "email"
      },
      '3': {
        value: "token_hex"
      }
    }))
  }
});
Object.defineProperties(PlatformInvitation.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(PlatformInvitation);
  Object.freeze(PlatformInvitation.prototype);
}

function _defineProperty$4(obj, key, value) { key = _toPropertyKey$4(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$4(arg) { var key = _toPrimitive$4(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$4(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var ProjectPermissionsType__;
(function (ProjectPermissionsType__) {
  ProjectPermissionsType__[ProjectPermissionsType__["USER"] = 0] = "USER";
  ProjectPermissionsType__[ProjectPermissionsType__["ORGANIZATION"] = 1] = "ORGANIZATION";
  ProjectPermissionsType__[ProjectPermissionsType__["SHARE"] = 2] = "SHARE";
  ProjectPermissionsType__[ProjectPermissionsType__["INVITATION"] = 3] = "INVITATION";
})(ProjectPermissionsType__ || (ProjectPermissionsType__ = {}));
const ProjectPermissionsType_ = defineEnumProperties(ProjectPermissionsType__, [0, 1, 2, 3]);
const ProjectPermissionsType = extendEnum('ProjectPermissionsType', [["USER", 0], ["ORGANIZATION", 1], ["SHARE", 2], ["INVITATION", 3]])(ProjectPermissionsType_);
class ProjectPermissions_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 0,
      3: 7,
      4: 1,
      7: 1,
      5: 1,
      6: 1
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectPermissions field project_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, strictJson, getFieldMap) => {
          if (strictJson) {
            assert(typeof value === 'string' || typeof value === 'number', () => emsg`
                Error setting message ProjectPermissions field type.
                Expected number or string.
                Found type ${typeof value}: ${value}
              `);
            const v = value;
            return ProjectPermissionsType.fromStrictJson_(v, getFieldMap);
          }
          return ProjectPermissionsType.from(value);
        };
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message ProjectPermissions field reference_id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectPermissions field read.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 7:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectPermissions field read_shares.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 5:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectPermissions field manage.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
      case 6:
        return (value, unused_strictJson, unused_getFieldMap) => {
          assert(typeof value === 'boolean' || value === 1 || value === 0, () => emsg`
              Error setting message ProjectPermissions field make_offer.
              Expected boolean, 1, or 0.
              Found type ${typeof value}: ${value}
            `);
          return !!value;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(ProjectPermissions, 'ProjectPermissions', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message ProjectPermissions to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get projectId() {
    return this.getFieldById(1, 0);
  }
  setProjectId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteProjectId() {
    return this.setValueFieldById(1, null, null);
  }
  get type() {
    return this.getFieldById(2, 0);
  }
  setType(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteType() {
    return this.setValueFieldById(2, null, null);
  }
  get referenceId() {
    return this.getFieldById(3, 0);
  }
  setReferenceId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteReferenceId() {
    return this.setValueFieldById(3, null, null);
  }
  get read() {
    return this.getFieldById(4, false);
  }
  setRead(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteRead() {
    return this.setValueFieldById(4, null, null);
  }
  get readShares() {
    return this.getFieldById(7, false);
  }
  setReadShares(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(7, value, null);
  }
  deleteReadShares() {
    return this.setValueFieldById(7, null, null);
  }
  get manage() {
    return this.getFieldById(5, false);
  }
  setManage(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(5, value, null);
  }
  deleteManage() {
    return this.setValueFieldById(5, null, null);
  }
  get makeOffer() {
    return this.getFieldById(6, false);
  }
  setMakeOffer(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(6, value, null);
  }
  deleteMakeOffer() {
    return this.setValueFieldById(6, null, null);
  }
}
_defineProperty$4(ProjectPermissions_, protoName, DEV_MODE ? 'ProjectPermissions' : '[Unknown]');
let ProjectPermissions = ProjectPermissions_;
(function (_ProjectPermissions) {})(ProjectPermissions || (ProjectPermissions = {}));
Object.defineProperties(ProjectPermissions, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "project_id": {
        value: 1
      },
      "projectId": {
        value: 1
      },
      "type": {
        value: 2
      },
      "reference_id": {
        value: 3
      },
      "referenceId": {
        value: 3
      },
      "read": {
        value: 4
      },
      "read_shares": {
        value: 7
      },
      "readShares": {
        value: 7
      },
      "manage": {
        value: 5
      },
      "make_offer": {
        value: 6
      },
      "makeOffer": {
        value: 6
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "project_id"
      },
      '2': {
        value: "type"
      },
      '3': {
        value: "reference_id"
      },
      '4': {
        value: "read"
      },
      '7': {
        value: "read_shares"
      },
      '5': {
        value: "manage"
      },
      '6': {
        value: "make_offer"
      }
    }))
  }
});
Object.defineProperties(ProjectPermissions.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(ProjectPermissions);
  Object.freeze(ProjectPermissions.prototype);
}
class RepeatedProjectPermissions_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: ProjectPermissions.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(ProjectPermissions);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message RepeatedProjectPermissions field value.
                Expected type ProjectPermissions.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'ProjectPermissions', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RepeatedProjectPermissions, 'RepeatedProjectPermissions', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RepeatedProjectPermissions to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get valueList() {
    return this.getRepeatedFieldById(1);
  }
  hasValue(value) {
    return this.repeatedFieldByIdHas(1, value);
  }
  addValue(value) {
    return this.addToRepeatedFieldById(1, value);
  }
  addEachValue(values) {
    let T = this;
    for (const value of values) {
      T = T.addValue(value);
    }
    return T;
  }
  setValue(index, value) {
    return this.setRepeatedFieldById(1, index, value);
  }
  removeValue(index) {
    return this.removeFromRepeatedFieldById(1, index);
  }
  clearValueList() {
    return this.clearRepeatedFieldById(1);
  }
  setValueList(values) {
    return this.overwriteRepeatedFieldById(1, values);
  }
}
_defineProperty$4(RepeatedProjectPermissions_, protoName, DEV_MODE ? 'RepeatedProjectPermissions' : '[Unknown]');
let RepeatedProjectPermissions = RepeatedProjectPermissions_;
(function (_RepeatedProjectPermissions) {})(RepeatedProjectPermissions || (RepeatedProjectPermissions = {}));
Object.defineProperties(RepeatedProjectPermissions, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "value": {
        value: 1
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "value"
      }
    }))
  }
});
Object.defineProperties(RepeatedProjectPermissions.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: ProjectPermissions.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RepeatedProjectPermissions);
  Object.freeze(RepeatedProjectPermissions.prototype);
}

function _defineProperty$3(obj, key, value) { key = _toPropertyKey$3(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$3(arg) { var key = _toPrimitive$3(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$3(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Place_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: 7,
      2: 2,
      3: 2,
      4: 2
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message Place field id.
          Expected type number.
          Found type ${typeof value}: ${value}
        `);
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Place field name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Place field image_url.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 4:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message Place field brief_description.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(Place, 'Place', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message Place to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get id() {
    return this.getFieldById(1, 0);
  }
  setId(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteId() {
    return this.setValueFieldById(1, null, null);
  }
  get name() {
    return this.getFieldById(2, "");
  }
  setName(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteName() {
    return this.setValueFieldById(2, null, null);
  }
  get imageUrl() {
    return this.getFieldById(3, "");
  }
  setImageUrl(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteImageUrl() {
    return this.setValueFieldById(3, null, null);
  }
  get briefDescription() {
    return this.getFieldById(4, "");
  }
  setBriefDescription(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(4, value, null);
  }
  deleteBriefDescription() {
    return this.setValueFieldById(4, null, null);
  }
}
_defineProperty$3(Place_, protoName, DEV_MODE ? 'Place' : '[Unknown]');
let Place = Place_;
(function (_Place) {})(Place || (Place = {}));
Object.defineProperties(Place, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "id": {
        value: 1
      },
      "name": {
        value: 2
      },
      "image_url": {
        value: 3
      },
      "imageUrl": {
        value: 3
      },
      "brief_description": {
        value: 4
      },
      "briefDescription": {
        value: 4
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "id"
      },
      '2': {
        value: "name"
      },
      '3': {
        value: "image_url"
      },
      '4': {
        value: "brief_description"
      }
    }))
  }
});
Object.defineProperties(Place.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {}
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(Place);
  Object.freeze(Place.prototype);
}

function _defineProperty$2(obj, key, value) { key = _toPropertyKey$2(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$2(arg) { var key = _toPrimitive$2(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$2(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class RateIndexMapEntry_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: RateIndexTypeAndTenor.shape,
      2: RateIndex.shape
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RateIndexTypeAndTenor);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message RateIndexMapEntry field type_and_tenor.
                Expected type RateIndexTypeAndTenor.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RateIndexTypeAndTenor', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(RateIndex);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message RateIndexMapEntry field index.
                Expected type RateIndex.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'RateIndex', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(RateIndexMapEntry, 'RateIndexMapEntry', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message RateIndexMapEntry to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get typeAndTenor() {
    return this.getFieldById(1, RateIndexTypeAndTenor.getDefault());
  }
  setTypeAndTenor(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteTypeAndTenor() {
    return this.setValueFieldById(1, null, null);
  }
  get index() {
    return this.getFieldById(2, RateIndex.getDefault());
  }
  setIndex(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteIndex() {
    return this.setValueFieldById(2, null, null);
  }
}
_defineProperty$2(RateIndexMapEntry_, protoName, DEV_MODE ? 'RateIndexMapEntry' : '[Unknown]');
let RateIndexMapEntry = RateIndexMapEntry_;
(function (_RateIndexMapEntry) {})(RateIndexMapEntry || (RateIndexMapEntry = {}));
Object.defineProperties(RateIndexMapEntry, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "type_and_tenor": {
        value: 1
      },
      "typeAndTenor": {
        value: 1
      },
      "index": {
        value: 2
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "type_and_tenor"
      },
      '2': {
        value: "index"
      }
    }))
  }
});
Object.defineProperties(RateIndexMapEntry.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: RateIndexTypeAndTenor.prototype,
      2: RateIndex.prototype
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(RateIndexMapEntry);
  Object.freeze(RateIndexMapEntry.prototype);
}
class AppState_ extends Message {
  // TODO(nathan): It'd be nice to generate a unique tag to use instead of
  // '[Unknown]' in prod that could be referenced to determine the actual
  // message.

  // A message's shape briefly indicates a message's schema. It can be used
  // to detect when a message's schema has been updated. This is helpful for
  // applications that may be storing serialized messages, such as a cache,
  // and needs to be able to detect if the stored message schema is out of
  // date. The shape indicates what properties are defined on the message,
  // including what properties are defined on sub-messages.
  //
  // For example, given the following messages, Foo will have the shape
  // {1:7,2:2} while Bar has the shape {1:1,2:2,3:{1:7,2:2},4:0}.
  //
  //     message Foo {
  //       uint32 first = 1;
  //       string second = 2;
  //     }
  //
  //     enum Color {
  //       RED = 1;
  //       GREEN = 2;
  //       BLUE = 3;
  //     }
  //
  //     message Bar {
  //       bool a_bool = 1;
  //       string a_string = 2;
  //       Foo a_foo = 3;
  //       Color a_color = 4;
  //     }
  //
  static get shape() {
    return {
      1: User.shape,
      2: 2,
      3: UserSettings.shape,
      6: [2, Offer.shape],
      9: [2, Share.shape],
      10: [2, RepeatedProjectShareUser.shape],
      11: [2, RepeatedProjectPermissions.shape],
      12: [2, Person.shape],
      13: [2, Organization.shape],
      14: [7, Organization.shape],
      15: [2, 2],
      16: RateIndexMapEntry.shape,
      17: [2, PlatformInvitation.shape],
      18: [2, LoanProgram.shape],
      19: [2, RepeatedUint32.shape],
      20: [2, Place.shape],
      21: [2, RepeatedInt32OrString.shape]
    };
  }

  // This can be used to validate data if necessary from an untrusted
  // source, where we explicitly want an exception if the data is invalid
  // (even in production).
  //
  // Note: We don't want to have to validate in production by default
  // because it could be inefficient.
  static validateJson(data) {
    ensure(typeof data === 'object' && data != null);
    // TODO(nathan): Add validations for each of the fields.
    return data;
  }
  static getFieldMapper_(id) {
    switch (id) {
      case 1:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(User);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message AppState field user.
                Expected type User.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'User', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 2:
        return (value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field user_email_guess.
          Expected type string.
          Found type ${typeof value}: ${value}
        `);
      case 3:
        return (value, strictJson, getFieldMap) => {
          const CanonicalMessage = getCanonical(UserSettings);
          if (strictJson) {
            assert(typeof value === 'object', () => emsg`
                Error setting message AppState field user_settings.
                Expected type UserSettings.
                Found type ${typeof value}: ${value}
              `);
            return Message.mapFields_(CanonicalMessage, 'UserSettings', value, strictJson, getFieldMap);
          }
          const V = CanonicalMessage.from(value);
          return V instanceof Message ? Message.unwrapData(V) : V;
        };
      case 6:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field offer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Offer);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field offer.
                Expected type Offer.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Offer', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 9:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field share.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Share);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field share.
                Expected type Share.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Share', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 10:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field share_users.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RepeatedProjectShareUser);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field share_users.
                Expected type RepeatedProjectShareUser.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RepeatedProjectShareUser', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 11:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field project_permissions.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RepeatedProjectPermissions);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field project_permissions.
                Expected type RepeatedProjectPermissions.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RepeatedProjectPermissions', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 12:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field person_by_email.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Person);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field person_by_email.
                Expected type Person.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Person', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 13:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field organization.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Organization);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field organization.
                Expected type Organization.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Organization', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 14:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.number(value, () => emsg`
          Error setting message AppState field organization_description.
          Expected type number.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Organization);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field organization_description.
                Expected type Organization.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Organization', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 15:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field share_sym_by_share_token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field share_sym_by_share_token.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 16:
        return (values, strictJson, getFieldMap) => {
          const items = assert.iterable(values);
          const out = [];
          const convert = (value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RateIndexMapEntry);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field rate_index_map_entry.
                Expected type RateIndexMapEntry.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RateIndexMapEntry', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          };
          for (const value of items) {
            out.push(convert(value, strictJson, getFieldMap));
          }
          return out;
        };
      case 17:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field platform_invitation.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(PlatformInvitation);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field platform_invitation.
                Expected type PlatformInvitation.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'PlatformInvitation', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 18:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field loan_program.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(LoanProgram);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field loan_program.
                Expected type LoanProgram.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'LoanProgram', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 19:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field project_matching_loan_programs.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RepeatedUint32);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field project_matching_loan_programs.
                Expected type RepeatedUint32.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RepeatedUint32', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 20:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field place_by_name.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(Place);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field place_by_name.
                Expected type Place.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'Place', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
      case 21:
        return (value, strictJson, getFieldMap) => {
          const entries = assert.array(value).map(u => assert.array(u));
          const out = [];
          const convertKey = ((value, unused_strictJson, unused_getFieldMap) => assert.string(value, () => emsg`
          Error setting message AppState field lender_contacts_by_offer.
          Expected type string.
          Found type ${typeof value}: ${value}
        `)) ?? ((u, v, w) => u);
          const convertValue = ((value, strictJson, getFieldMap) => {
            const CanonicalMessage = getCanonical(RepeatedInt32OrString);
            if (strictJson) {
              assert(typeof value === 'object', () => emsg`
                Error setting message AppState field lender_contacts_by_offer.
                Expected type RepeatedInt32OrString.
                Found type ${typeof value}: ${value}
              `);
              return Message.mapFields_(CanonicalMessage, 'RepeatedInt32OrString', value, strictJson, getFieldMap);
            }
            const V = CanonicalMessage.from(value);
            return V instanceof Message ? Message.unwrapData(V) : V;
          }) ?? ((u, v, w) => u);
          for (const [key, value] of entries) {
            out.push([convertKey(key, strictJson, getFieldMap), convertValue(value, strictJson, getFieldMap)]);
          }
          return out;
        };
    }
    assert.fail(`Field not found (${id})`);
    return null;
  }
  toJson() {
    // We ignore the useCompactJson setting in prod but allow overriding
    // it in dev.
    if (DEV_MODE && !Message.useCompactJson) {
      try {
        return Message.mapFields_(AppState, 'AppState', super.toJson(), true /* strictJson */, Msg => Msg.namesById_);
      } catch (e) {
        throw new CustomError(`Error converting message AppState to JSON.\n` + (e ? String(e.message) + '\n' : '') + `Message: ${JSON.stringify(this.data_)}\n`, e);
      }
    }
    return super.toJson();
  }
  get user() {
    return this.getFieldById(1, User.getDefault());
  }
  setUser(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(1, value, null);
  }
  deleteUser() {
    return this.setValueFieldById(1, null, null);
  }
  get userEmailGuess() {
    return this.getFieldById(2, "");
  }
  setUserEmailGuess(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(2, value, null);
  }
  deleteUserEmailGuess() {
    return this.setValueFieldById(2, null, null);
  }
  get userSettings() {
    return this.getFieldById(3, UserSettings.getDefault());
  }
  setUserSettings(value) {
    // TODO(nathan): Add asserts for numeric types to ensure proper
    // range in dev mode.
    // TODO(nathan): Add instanceof assert for message types.
    // TODO(nathan): Add assert for enum type.
    return this.setValueFieldById(3, value, null);
  }
  deleteUserSettings() {
    return this.setValueFieldById(3, null, null);
  }
  get offerKeys() {
    return this.getMapIteratorById(6);
  }
  get offerValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(6)) {
        yield self.getMapValueById(6, key);
      }
    }();
  }
  get offerSize() {
    return this.getMapSizeById(6);
  }
  hasOffer(key) {
    return this.hasMapKeyById(6, key);
  }
  getOffer(key) {
    return this.getMapValueById(6, key);
  }
  setOffer(key, value) {
    return this.setMapValue(6, key, value);
  }
  get offerMap() {
    return this.getMapById(6);
  }
  setOfferMap(map) {
    if (map.size === this.getMapSizeById(6)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(6, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(6);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(6, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeOffer(key) {
    return deprecatedMember('AppState', 'removeOffer', 'deleteOffer', this.deleteOffer(key));
  }
  deleteOffer(key) {
    return this.removeFromMapById(6, key);
  }
  clearOfferMap() {
    return this.clearMap(6);
  }
  get shareKeys() {
    return this.getMapIteratorById(9);
  }
  get shareValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(9)) {
        yield self.getMapValueById(9, key);
      }
    }();
  }
  get shareSize() {
    return this.getMapSizeById(9);
  }
  hasShare(key) {
    return this.hasMapKeyById(9, key);
  }
  getShare(key) {
    return this.getMapValueById(9, key);
  }
  setShare(key, value) {
    return this.setMapValue(9, key, value);
  }
  get shareMap() {
    return this.getMapById(9);
  }
  setShareMap(map) {
    if (map.size === this.getMapSizeById(9)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(9, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(9);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(9, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeShare(key) {
    return deprecatedMember('AppState', 'removeShare', 'deleteShare', this.deleteShare(key));
  }
  deleteShare(key) {
    return this.removeFromMapById(9, key);
  }
  clearShareMap() {
    return this.clearMap(9);
  }
  get shareUsersKeys() {
    return this.getMapIteratorById(10);
  }
  get shareUsersValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(10)) {
        yield self.getMapValueById(10, key);
      }
    }();
  }
  get shareUsersSize() {
    return this.getMapSizeById(10);
  }
  hasShareUsers(key) {
    return this.hasMapKeyById(10, key);
  }
  getShareUsers(key) {
    return this.getMapValueById(10, key);
  }
  setShareUsers(key, value) {
    return this.setMapValue(10, key, value);
  }
  get shareUsersMap() {
    return this.getMapById(10);
  }
  setShareUsersMap(map) {
    if (map.size === this.getMapSizeById(10)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(10, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(10);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(10, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeShareUsers(key) {
    return deprecatedMember('AppState', 'removeShareUsers', 'deleteShareUsers', this.deleteShareUsers(key));
  }
  deleteShareUsers(key) {
    return this.removeFromMapById(10, key);
  }
  clearShareUsersMap() {
    return this.clearMap(10);
  }
  get projectPermissionsKeys() {
    return this.getMapIteratorById(11);
  }
  get projectPermissionsValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(11)) {
        yield self.getMapValueById(11, key);
      }
    }();
  }
  get projectPermissionsSize() {
    return this.getMapSizeById(11);
  }
  hasProjectPermissions(key) {
    return this.hasMapKeyById(11, key);
  }
  getProjectPermissions(key) {
    return this.getMapValueById(11, key);
  }
  setProjectPermissions(key, value) {
    return this.setMapValue(11, key, value);
  }
  get projectPermissionsMap() {
    return this.getMapById(11);
  }
  setProjectPermissionsMap(map) {
    if (map.size === this.getMapSizeById(11)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(11, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(11);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(11, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeProjectPermissions(key) {
    return deprecatedMember('AppState', 'removeProjectPermissions', 'deleteProjectPermissions', this.deleteProjectPermissions(key));
  }
  deleteProjectPermissions(key) {
    return this.removeFromMapById(11, key);
  }
  clearProjectPermissionsMap() {
    return this.clearMap(11);
  }
  get personByEmailKeys() {
    return this.getMapIteratorById(12);
  }
  get personByEmailValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(12)) {
        yield self.getMapValueById(12, key);
      }
    }();
  }
  get personByEmailSize() {
    return this.getMapSizeById(12);
  }
  hasPersonByEmail(key) {
    return this.hasMapKeyById(12, key);
  }
  getPersonByEmail(key) {
    return this.getMapValueById(12, key);
  }
  setPersonByEmail(key, value) {
    return this.setMapValue(12, key, value);
  }
  get personByEmailMap() {
    return this.getMapById(12);
  }
  setPersonByEmailMap(map) {
    if (map.size === this.getMapSizeById(12)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(12, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(12);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(12, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removePersonByEmail(key) {
    return deprecatedMember('AppState', 'removePersonByEmail', 'deletePersonByEmail', this.deletePersonByEmail(key));
  }
  deletePersonByEmail(key) {
    return this.removeFromMapById(12, key);
  }
  clearPersonByEmailMap() {
    return this.clearMap(12);
  }
  get organizationKeys() {
    return this.getMapIteratorById(13);
  }
  get organizationValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(13)) {
        yield self.getMapValueById(13, key);
      }
    }();
  }
  get organizationSize() {
    return this.getMapSizeById(13);
  }
  hasOrganization(key) {
    return this.hasMapKeyById(13, key);
  }
  getOrganization(key) {
    return this.getMapValueById(13, key);
  }
  setOrganization(key, value) {
    return this.setMapValue(13, key, value);
  }
  get organizationMap() {
    return this.getMapById(13);
  }
  setOrganizationMap(map) {
    if (map.size === this.getMapSizeById(13)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(13, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(13);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(13, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeOrganization(key) {
    return deprecatedMember('AppState', 'removeOrganization', 'deleteOrganization', this.deleteOrganization(key));
  }
  deleteOrganization(key) {
    return this.removeFromMapById(13, key);
  }
  clearOrganizationMap() {
    return this.clearMap(13);
  }
  get organizationDescriptionKeys() {
    return this.getMapIteratorById(14);
  }
  get organizationDescriptionValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(14)) {
        yield self.getMapValueById(14, key);
      }
    }();
  }
  get organizationDescriptionSize() {
    return this.getMapSizeById(14);
  }
  hasOrganizationDescription(key) {
    return this.hasMapKeyById(14, key);
  }
  getOrganizationDescription(key) {
    return this.getMapValueById(14, key);
  }
  setOrganizationDescription(key, value) {
    return this.setMapValue(14, key, value);
  }
  get organizationDescriptionMap() {
    return this.getMapById(14);
  }
  setOrganizationDescriptionMap(map) {
    if (map.size === this.getMapSizeById(14)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(14, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(14);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(14, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeOrganizationDescription(key) {
    return deprecatedMember('AppState', 'removeOrganizationDescription', 'deleteOrganizationDescription', this.deleteOrganizationDescription(key));
  }
  deleteOrganizationDescription(key) {
    return this.removeFromMapById(14, key);
  }
  clearOrganizationDescriptionMap() {
    return this.clearMap(14);
  }
  get shareSymByShareTokenKeys() {
    return this.getMapIteratorById(15);
  }
  get shareSymByShareTokenValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(15)) {
        yield self.getMapValueById(15, key);
      }
    }();
  }
  get shareSymByShareTokenSize() {
    return this.getMapSizeById(15);
  }
  hasShareSymByShareToken(key) {
    return this.hasMapKeyById(15, key);
  }
  getShareSymByShareToken(key) {
    return this.getMapValueById(15, key);
  }
  setShareSymByShareToken(key, value) {
    return this.setMapValue(15, key, value);
  }
  get shareSymByShareTokenMap() {
    return this.getMapById(15);
  }
  setShareSymByShareTokenMap(map) {
    if (map.size === this.getMapSizeById(15)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(15, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(15);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(15, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeShareSymByShareToken(key) {
    return deprecatedMember('AppState', 'removeShareSymByShareToken', 'deleteShareSymByShareToken', this.deleteShareSymByShareToken(key));
  }
  deleteShareSymByShareToken(key) {
    return this.removeFromMapById(15, key);
  }
  clearShareSymByShareTokenMap() {
    return this.clearMap(15);
  }
  get rateIndexMapEntryList() {
    return this.getRepeatedFieldById(16);
  }
  hasRateIndexMapEntry(value) {
    return this.repeatedFieldByIdHas(16, value);
  }
  addRateIndexMapEntry(value) {
    return this.addToRepeatedFieldById(16, value);
  }
  addEachRateIndexMapEntry(values) {
    let T = this;
    for (const value of values) {
      T = T.addRateIndexMapEntry(value);
    }
    return T;
  }
  setRateIndexMapEntry(index, value) {
    return this.setRepeatedFieldById(16, index, value);
  }
  removeRateIndexMapEntry(index) {
    return this.removeFromRepeatedFieldById(16, index);
  }
  clearRateIndexMapEntryList() {
    return this.clearRepeatedFieldById(16);
  }
  setRateIndexMapEntryList(values) {
    return this.overwriteRepeatedFieldById(16, values);
  }
  get platformInvitationKeys() {
    return this.getMapIteratorById(17);
  }
  get platformInvitationValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(17)) {
        yield self.getMapValueById(17, key);
      }
    }();
  }
  get platformInvitationSize() {
    return this.getMapSizeById(17);
  }
  hasPlatformInvitation(key) {
    return this.hasMapKeyById(17, key);
  }
  getPlatformInvitation(key) {
    return this.getMapValueById(17, key);
  }
  setPlatformInvitation(key, value) {
    return this.setMapValue(17, key, value);
  }
  get platformInvitationMap() {
    return this.getMapById(17);
  }
  setPlatformInvitationMap(map) {
    if (map.size === this.getMapSizeById(17)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(17, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(17);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(17, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removePlatformInvitation(key) {
    return deprecatedMember('AppState', 'removePlatformInvitation', 'deletePlatformInvitation', this.deletePlatformInvitation(key));
  }
  deletePlatformInvitation(key) {
    return this.removeFromMapById(17, key);
  }
  clearPlatformInvitationMap() {
    return this.clearMap(17);
  }
  get loanProgramKeys() {
    return this.getMapIteratorById(18);
  }
  get loanProgramValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(18)) {
        yield self.getMapValueById(18, key);
      }
    }();
  }
  get loanProgramSize() {
    return this.getMapSizeById(18);
  }
  hasLoanProgram(key) {
    return this.hasMapKeyById(18, key);
  }
  getLoanProgram(key) {
    return this.getMapValueById(18, key);
  }
  setLoanProgram(key, value) {
    return this.setMapValue(18, key, value);
  }
  get loanProgramMap() {
    return this.getMapById(18);
  }
  setLoanProgramMap(map) {
    if (map.size === this.getMapSizeById(18)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(18, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(18);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(18, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeLoanProgram(key) {
    return deprecatedMember('AppState', 'removeLoanProgram', 'deleteLoanProgram', this.deleteLoanProgram(key));
  }
  deleteLoanProgram(key) {
    return this.removeFromMapById(18, key);
  }
  clearLoanProgramMap() {
    return this.clearMap(18);
  }
  get projectMatchingLoanProgramsKeys() {
    return this.getMapIteratorById(19);
  }
  get projectMatchingLoanProgramsValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(19)) {
        yield self.getMapValueById(19, key);
      }
    }();
  }
  get projectMatchingLoanProgramsSize() {
    return this.getMapSizeById(19);
  }
  hasProjectMatchingLoanPrograms(key) {
    return this.hasMapKeyById(19, key);
  }
  getProjectMatchingLoanPrograms(key) {
    return this.getMapValueById(19, key);
  }
  setProjectMatchingLoanPrograms(key, value) {
    return this.setMapValue(19, key, value);
  }
  get projectMatchingLoanProgramsMap() {
    return this.getMapById(19);
  }
  setProjectMatchingLoanProgramsMap(map) {
    if (map.size === this.getMapSizeById(19)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(19, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(19);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(19, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeProjectMatchingLoanPrograms(key) {
    return deprecatedMember('AppState', 'removeProjectMatchingLoanPrograms', 'deleteProjectMatchingLoanPrograms', this.deleteProjectMatchingLoanPrograms(key));
  }
  deleteProjectMatchingLoanPrograms(key) {
    return this.removeFromMapById(19, key);
  }
  clearProjectMatchingLoanProgramsMap() {
    return this.clearMap(19);
  }
  get placeByNameKeys() {
    return this.getMapIteratorById(20);
  }
  get placeByNameValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(20)) {
        yield self.getMapValueById(20, key);
      }
    }();
  }
  get placeByNameSize() {
    return this.getMapSizeById(20);
  }
  hasPlaceByName(key) {
    return this.hasMapKeyById(20, key);
  }
  getPlaceByName(key) {
    return this.getMapValueById(20, key);
  }
  setPlaceByName(key, value) {
    return this.setMapValue(20, key, value);
  }
  get placeByNameMap() {
    return this.getMapById(20);
  }
  setPlaceByNameMap(map) {
    if (map.size === this.getMapSizeById(20)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(20, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(20);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(20, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removePlaceByName(key) {
    return deprecatedMember('AppState', 'removePlaceByName', 'deletePlaceByName', this.deletePlaceByName(key));
  }
  deletePlaceByName(key) {
    return this.removeFromMapById(20, key);
  }
  clearPlaceByNameMap() {
    return this.clearMap(20);
  }
  get lenderContactsByOfferKeys() {
    return this.getMapIteratorById(21);
  }
  get lenderContactsByOfferValues() {
    const self = this;
    return function* () {
      for (const key of self.getMapIteratorById(21)) {
        yield self.getMapValueById(21, key);
      }
    }();
  }
  get lenderContactsByOfferSize() {
    return this.getMapSizeById(21);
  }
  hasLenderContactsByOffer(key) {
    return this.hasMapKeyById(21, key);
  }
  getLenderContactsByOffer(key) {
    return this.getMapValueById(21, key);
  }
  setLenderContactsByOffer(key, value) {
    return this.setMapValue(21, key, value);
  }
  get lenderContactsByOfferMap() {
    return this.getMapById(21);
  }
  setLenderContactsByOfferMap(map) {
    if (map.size === this.getMapSizeById(21)) {
      let same = true;
      for (const key of map.keys()) {
        if (this.getMapValueById(21, key) !== map.get(key)) {
          same = false;
          break;
        }
      }
      if (same) {
        return this;
      }
    }
    let newValue = this.clearMap(21);
    for (const key of map.keys()) {
      newValue = newValue.setMapValue(21, key, map.get(key));
    }
    return newValue;
  }

  // DEPRECATED
  removeLenderContactsByOffer(key) {
    return deprecatedMember('AppState', 'removeLenderContactsByOffer', 'deleteLenderContactsByOffer', this.deleteLenderContactsByOffer(key));
  }
  deleteLenderContactsByOffer(key) {
    return this.removeFromMapById(21, key);
  }
  clearLenderContactsByOfferMap() {
    return this.clearMap(21);
  }
}
_defineProperty$2(AppState_, protoName, DEV_MODE ? 'AppState' : '[Unknown]');
let AppState = AppState_;
(function (_AppState) {})(AppState || (AppState = {}));
Object.defineProperties(AppState, {
  idsByName_: {
    value: Object.freeze(Object.create(null, {
      "user": {
        value: 1
      },
      "user_email_guess": {
        value: 2
      },
      "userEmailGuess": {
        value: 2
      },
      "user_settings": {
        value: 3
      },
      "userSettings": {
        value: 3
      },
      "offer": {
        value: 6
      },
      "share": {
        value: 9
      },
      "share_users": {
        value: 10
      },
      "shareUsers": {
        value: 10
      },
      "project_permissions": {
        value: 11
      },
      "projectPermissions": {
        value: 11
      },
      "person_by_email": {
        value: 12
      },
      "personByEmail": {
        value: 12
      },
      "organization": {
        value: 13
      },
      "organization_description": {
        value: 14
      },
      "organizationDescription": {
        value: 14
      },
      "share_sym_by_share_token": {
        value: 15
      },
      "shareSymByShareToken": {
        value: 15
      },
      "rate_index_map_entry": {
        value: 16
      },
      "rateIndexMapEntry": {
        value: 16
      },
      "platform_invitation": {
        value: 17
      },
      "platformInvitation": {
        value: 17
      },
      "loan_program": {
        value: 18
      },
      "loanProgram": {
        value: 18
      },
      "project_matching_loan_programs": {
        value: 19
      },
      "projectMatchingLoanPrograms": {
        value: 19
      },
      "place_by_name": {
        value: 20
      },
      "placeByName": {
        value: 20
      },
      "lender_contacts_by_offer": {
        value: 21
      },
      "lenderContactsByOffer": {
        value: 21
      }
    }))
  },
  namesById_: {
    value: Object.freeze(Object.create(null, {
      '1': {
        value: "user"
      },
      '2': {
        value: "user_email_guess"
      },
      '3': {
        value: "user_settings"
      },
      '6': {
        value: "offer"
      },
      '9': {
        value: "share"
      },
      '10': {
        value: "share_users"
      },
      '11': {
        value: "project_permissions"
      },
      '12': {
        value: "person_by_email"
      },
      '13': {
        value: "organization"
      },
      '14': {
        value: "organization_description"
      },
      '15': {
        value: "share_sym_by_share_token"
      },
      '16': {
        value: "rate_index_map_entry"
      },
      '17': {
        value: "platform_invitation"
      },
      '18': {
        value: "loan_program"
      },
      '19': {
        value: "project_matching_loan_programs"
      },
      '20': {
        value: "place_by_name"
      },
      '21': {
        value: "lender_contacts_by_offer"
      }
    }))
  }
});
Object.defineProperties(AppState.prototype, {
  oneofs_: {
    value: {}
  },
  prototypes_: {
    value: {
      1: User.prototype,
      3: UserSettings.prototype,
      6: [null, Offer.prototype],
      9: [null, Share.prototype],
      10: [null, RepeatedProjectShareUser.prototype],
      11: [null, RepeatedProjectPermissions.prototype],
      12: [null, Person.prototype],
      13: [null, Organization.prototype],
      14: [null, Organization.prototype],
      15: [null, null],
      16: RateIndexMapEntry.prototype,
      17: [null, PlatformInvitation.prototype],
      18: [null, LoanProgram.prototype],
      19: [null, RepeatedUint32.prototype],
      20: [null, Place.prototype],
      21: [null, RepeatedInt32OrString.prototype]
    }
  }
});

// We can't freeze the constructor prototype whether we want to or not in
// prod because of the way Closure Compiler rewrites class inheritance.
if (DEV_MODE) {
  Object.freeze(AppState);
  Object.freeze(AppState.prototype);
}

function _defineProperty$1(obj, key, value) { key = _toPropertyKey$1(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey$1(arg) { var key = _toPrimitive$1(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive$1(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Controller {
  constructor() {
    _defineProperty$1(this, "sharedUser", void 0);
    _defineProperty$1(this, "_state", void 0);
    _defineProperty$1(this, "_loaders", void 0);
    _defineProperty$1(this, "emitter", void 0);
    _defineProperty$1(this, "authMon", new PromiseWithResolver());
    this._state = new AppState();
    this._loaders = new Map();
    this.sharedUser = SharedUser.getDefault();
    this.emitter = new BaseEventEmitter();
  }
  setAuthenticationMonitor(authMon) {
    this.authMon.resolve(authMon);
  }
  async getUser() {
    return (await this.authMon.promise).getUser();
  }
  async userUpdating(callback) {
    return (await this.authMon.promise).userUpdating(callback);
  }
  async getEmailGuess() {
    return (await this.authMon.promise).getEmailGuess();
  }
  get state() {
    return this._state;
  }
  setState(state) {
    this._state = state;
  }

  // DEPRECATED. Use `BatchLoader`.
  async batchLoad(
  // A resource type label to use in the error message.
  // The label will be used at the beginning of a sentence, so capitalization
  // is recommended.
  // Example: 'Project'
  typeLabel,
  // IMPORTANT: `getAsync` argument needs to be the same function instance
  // each time it is called, so an inline function won't work.
  getAsync, request, createError) {
    let loader = this._loaders.get(getAsync);
    if (!loader) {
      loader = new SeparableAccumulatingLoader(getAsync.bind(null, this));
      this._loaders.set(getAsync, loader);
    }
    const value = await loader.load(request);
    if (value == null) {
      if (createError) {
        throw createError(request);
      } else {
        throw new NotFoundError$1(tmsg`${typeLabel} not found: ${request}`);
      }
    }
    return value;
  }
  async loadBatchListWithSharedUser(sym, getRemote, msgFromJson, update) {
    const map = mapFromArray(await getRemote(this.sharedUser, [sym]), value => value.sym, json => msgFromJson(json));
    const value = map.get(sym);
    if (value) {
      this.setState(update(this.state, sym, value));
    }
    return value;
  }

  // DERPECATED. This version works on IDs instead of syms.
  async _loadBatchListWithSharedUser(id, getRemote, msgFromJson, update) {
    const map = _mapFromArray(await getRemote(this.sharedUser, [id]), value => value.id, json => msgFromJson(json));
    const value = map.get(id);
    if (value) {
      this.setState(update(this.state, id, value));
    }
    return value;
  }
  onDealChanged(callback) {
    this.emitter.addListener('deal-changed', callback);
  }
  dealChanged(sym) {
    this.emitter.emit('deal-changed', sym);
  }
  onUserProfileChanged(callback) {
    this.emitter.addListener('user-profile-changed', callback);
  }
  userProfileChanged(sym) {
    this.emitter.emit('user-profile-changed', sym);
  }
}
function mapFromArray(values, getKey, valueFromJson) {
  const map = new Map();
  for (const valueData of values) {
    const value = valueFromJson(valueData);
    const key = getKey(value);
    map.set(key, value);
  }
  return map;
}

// DERPECATED. This version works on IDs instead of syms.
function _mapFromArray(values, getKey, valueFromJson) {
  const map = new Map();
  for (const valueData of values) {
    const value = valueFromJson(valueData);
    const key = getKey(value);
    map.set(key, value);
  }
  return map;
}

function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
class Ava extends AvaClient {
  static calculateLoanSize(project, constraints, interestRate, amortization) {
    return deprecated('Ava.calculateLoanSize', 'LoanSize.calculate', calculateLoanSize(project, constraints, interestRate, amortization));
  }
  static configureRemote(host) {
    return deprecated('Ava.configureRemote(host)', 'new Ava({remote: host})', configureRemote(host));
  }
  constructor({
    remote
  }) {
    if (remote) {
      configureRemote(remote);
    }
    const ctr = new Controller();
    super(ctr);
    _defineProperty(this, "ctr", void 0);
    ctr.setAuthenticationMonitor(new AuthenticationMonitor(this));
    this.ctr = ctr;
  }
  async getCurrentUser() {
    const user = await this.ctr.getUser();
    return deprecated('Ava.getCurrentUser', 'ava.auth.getCurrentUser', {
      sym: user?.sym || ''
    });
  }
}

// Helper function for common configurations.
let configuredRemote = '';
function configureRemote(host) {
  // DERPECATED. This is here temporarily to allow old versions of Buble to pass
  // the host without https:. The protocol should now be specified.
  if (!/^https?:/.test(host)) {
    configureRemote(`https://${host}`);
    return;
  }
  if (configuredRemote) {
    ensure.same(configuredRemote, host, emsg`
      Cannot change remote to ${host}; it has already been set to
      ${configuredRemote}
    `);
    // Allow calling multiple times with the same host to allow
    // `new Ava({remote})` to be called more than once on the same host.
    // (In theory it should support multiple hosts, but that's not possible
    // right now.)
    return;
  }
  configuredRemote = host;
  switch (host) {
    case 'https://platform.stacksource.com':
      setBackendRoot(host);
      setUploadRoot(host);
      setUserContentRoot('https://static.stacksource.com/user-uploads');
      break;
    case 'https://buble.dev.stacksource.co':
      setBackendRoot(host);
      setUploadRoot(host);
      setUserContentRoot('https://static.stacksource.com/user-uploads-dev');
      break;
    default:
      // Handle common devbox setup via baslake
      if (/(\.dev\.stacksource\.co|localhost):8000$/.test(host)) {
        const withoutPort = host.slice(0, -5);
        setBackendRoot(host);
        setUploadRoot(host);
        setUserContentRoot(`${withoutPort}:8002`);
        break;
      }
      assert.fail(emsg`Unknown host ${host}`);
  }
}

// DEPRECATED. Do not add any new static re-exports. Instead export directly,
// as is done above.
for (const [key, value] of Object.entries({
  AcquisitionStatus,
  AssetType,
  Attachment,
  CreditScore,
  CurrencyUnit,
  CurrencyValue,
  CurrencyValueRange,
  DisclaimerItem,
  ExpectedInterestRate,
  ExpectedRecourse,
  ExpectedRepaymentSchedule,
  FinancingPurpose,
  InvestmentCategory,
  LoanSize,
  LoanSizeConstraint,
  LoanSizeConstraints,
  Occupancy,
  Offer,
  DEPRECATED_OfferInfo: OfferInfo,
  Organization: Org,
  Percent,
  Deal,
  PropertyDisclaimer,
  PropertyTitle,
  RichText,
  RichTextType,
  SymOrString,
  Term,
  TermRange,
  TermUnit
})) {
  Object.defineProperty(Ava, key, {
    get: () => deprecated(`Ava.${key}`, key, value)
  });
}

export { AcquisitionStatus, Area, AreaUnit, AssetType, Attachment, AttachmentType, AuthenticationError, Ava, CapStackDetails, CapStackItem, CapStackTarget, LoanProgram as CapitalProvider, ClosingCost, ContactMethod$1 as ContactMethod, CreditScore, CurrencyUnit, CurrencyValue, CurrencyValueRange, CurrentUser, DEPRECATED_Date, OfferInfo as DEPRECATED_OfferInfo, Deal, ProjectGuarantor as DealGuarantor, DealImage, DealTenancy, DealTenancyType, DisclaimerItem, EstimatedValuation, EstimatedValuationType, ExpectedInterestRate, ExpectedRecourse, ExpectedRepaymentSchedule, FinancingPurpose, ImageUploadError, InternationalPhoneNumber, InvestmentCategory, LoanSize, LoanSizeConstraint, LoanSizeConstraints, NotFoundError$1 as NotFoundError, Occupancy, Offer, Org, Org as Organization, Percent, Person, Deal as Project, Attachment as ProjectAttachment, DealImage as ProjectImage, ProjectNotFoundError, DealTenancy as ProjectTenancy, DealTenancyType as ProjectTenancyType, PropertyDisclaimer, PropertyTitle, LoanProgram as Provider, RichText, RichTextType, Share, ShareDeclined, SimpleDate, SponsorRole, StandardUsAddress, SymOrString, Term, TermRange, TermUnit, UnderwritingApprovalRequirement, UsCitizenshipStatus, UserProfile, UserProfileImage, UserSettings };
